import { lazy, Suspense, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import type * as React from "react";
import { createPortal } from "react-dom";
import { useVirtualizer } from "@tanstack/react-virtual";
import toast from "react-hot-toast";
import { sanitizeUploadFilename } from "../../utils/sanitizeUploadFilename";
import {
  getOutboundDeliveryFeedback,
  highestOutboundDeliveryStatus,
  isOutboundDeliveryUncertain,
  shouldReconcileOutboundRequestError,
} from "../../utils/supportOutboundDelivery";
import {
  Activity,
  AlertTriangle,
  ArrowLeft,
  Bell,
  Brain,
  BrainCircuit,
  Check,
  CheckCheck,
  CheckCircle2,
  Calendar,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Clipboard,
  CalendarClock,
  Columns3,
  ExternalLink,
  Filter,
  Download,
  FileText,
  Film,
  Gauge,
  GraduationCap,
  Image as ImageIcon,
  Info,
  Inbox,
  ListChecks,
  Loader2,
  MessageCircle,
  MessagesSquare,
  Mic,
  MoreHorizontal,
  MoreVertical,
  PauseCircle,
  Pencil,
  PlayCircle,
  Plug,
  Plus,
  RefreshCw,
  RotateCcw,
  Save,
  Search,
  Send,
  ShoppingBag,
  Sparkles,
  Settings,
  Settings2,
  StickyNote,
  Tag,
  Trash2,
  UploadCloud,
  UserRound,
  Video,
  Workflow,
  Paperclip,
  X,
  XCircle,
} from "lucide-react";
import { RiWhatsappLine } from "react-icons/ri";
import { useNavigate } from "react-router-dom";
import DropiLogo from "../../assets/images/dropi-logo.png";
import GuardianAvatar from "../../assets/images/guardian-avatar.webp";
import FluxiLogo from "../../assets/svg/FluxiLogo.svg";
import { Tooltip } from "../../components/utils/Tooltip/Tooltip";
import { useFacebookSDK } from "../../hooks/useFacebookSDK";
import { completeWhatsAppOAuth } from "../../components/integrations/hooks/channelOAuthFlow";
import {
  useSupportConversationsInfinite,
  type HumanHelpFilter,
} from "../../hooks/useSupportConversationsInfinite";
import { uploadImagesToS3 } from "../../services/s3/s3Service";
import { toBase64 } from "../../utils/utils";
import {
  WHATSAPP_TEMPLATE_PRESETS,
  WHATSAPP_TEMPLATE_PRESET_KEYS,
} from "../../utils/whatsappTemplatePresets";
import {
  ALL_PRODUCTS_OPTION,
  ProductPickerSelect,
  productPickerChipContent,
} from "./components/ProductPickerSelect";
import { AppointmentSchedulingSettings } from "./components/AppointmentSchedulingSettings";
import { getGoogleOAuthCompletionResult } from "./googleOAuthCompletion";
import { HotmartAccessCasesPanel } from "../../components/chatbot/HotmartAccessCasesPanel";
import { HotmartWorkspace } from "../../components/chatbot/HotmartWorkspace";
import {
  buildHotmartClubMappingPatch,
  clubPublicUrlFromMapping,
  HOTMART_MEMBERS_IMPORT_URL,
  parseClubSubdomainFromUrl,
} from "../../utils/hotmartClubUtils";

import {
  chatbotV2Service,
  type ChatbotDeliveryLog,
  type ChatbotEvent,
  type ChatbotRealtimeEvent,
  type CommerceChatbotImagesConfig,
  type CommerceChatbotSelectedImage,
  type CommerceConfirmationImage,
  type CommerceFlowApiRow,
  type CommerceFlowDraft,
  type CommerceFlowProductContext,
  type CommerceLogisticsPolicy,
  type CommerceOrderMode,
  type ConfirmationSyncResult,
  type ConfirmationsOverview,
  type ContactStateItem,
  type ConversationPriority,
  type ConversationStatus,
  type GuardianConversation,
  type GuardianMemoryItem,
  type GuardianProposal,
  type GuardianRun,
  type GuardianSettings,
  type GuardianState,
  type HotmartProductMapping,
  type HotmartAccessCase,
  type IntegrationConnection,
  type IntegrationProvider,
  type IntegrationTool,
  type KnowledgeSource,
  type LearningEntry,
  type NotificationsSummary,
  type OperationsHealth,
  type ReplyApproval,
  type RetentionPreview,
  type SupportContact,
  type SupportContactNote,
  type SupportContactUpdate,
  type SupportAutomation,
  type SupportAutomationRun,
  type PersonView,
  type SupportConfirmation,
  type SupportConversation,
  type SupportConversationUpdate,
  type SupportMessage,
  type SupportNotification,
  type SupportOverview,
  type SupportSettings,
  type SupportSettingsPayload,
  type SupportTestChatHistoryItem,
  type SupportTestChatResponse,
  type StateBoard,
  type WhatsAppConnectionStatus,
  type WhatsAppInboundEvent,
  type WhatsAppTemplate,
} from "../../services/chatbotV2/chatbotV2Service";
import { getFunnelChatbotImages } from "../../services/generativeAI/generativeAIAxios/generateCopysAxios";
import { getProductsPage } from "../../services/products/productAxios";
import { handleApi } from "../../utils/handleApi";
import { getDropiIntegrations } from "../../services/integrations/integrationsService";
import { axiosInstanceWebsite } from "../../services/instance";
import type { Product } from "../../components/products/ProductInterface";
import type { Website } from "../../interfaces/websites";
import { ChannelConnectionDetail } from "../../components/integrations/ChannelConnectionDetail";
import { ChannelAvatarBadge } from "../../components/integrations/ChannelAvatarBadge";
import { useChatChannelConnections } from "../../components/integrations/hooks/useChatChannelConnections";
import { useWebsitesStore } from "../../store/websitesStore";
import { isLocalHostname } from "../../utils/localHost";

const AppointmentSchedulingWorkspace = lazy(async () => {
  const module = await import("./components/AppointmentSchedulingWorkspace");
  return { default: module.AppointmentSchedulingWorkspace };
});

type SettingsPanelTab =
  | "knowledge"
  | "templates"
  | "integrations"
  | "actions";
type ActionsConfigSubTab =
  | "ventas"
  | "confirmaciones"
  | "logistica"
  | "agendamientos"
  | "recuperacion"
  | "hotmart"
  | "otras";
type IntegrationDetail = "whatsapp" | "messenger" | "instagram" | "hotmart" | "dropi" | "shopify";
type WhatsAppSetupTab = "quick" | "manual";

const getLocalChatbotDebugParams = () => {
  if (typeof window === "undefined") return null;
  if (!import.meta.env.DEV && !isLocalHostname(window.location.hostname)) return null;
  return new URLSearchParams(window.location.search);
};

const CHATBOT_SHOW_AI_SUMMARIES_STORAGE_KEY = "fluxi.chatbot.showAiSummaries";

const getStoredBoolean = (key: string, fallback = false) => {
  if (typeof window === "undefined") return fallback;
  const value = window.localStorage.getItem(key);
  if (value === "true") return true;
  if (value === "false") return false;
  return fallback;
};

const inferWhatsAppMediaTypeFromFile = (file: File): MediaResourceDraft["files"][number]["media_type"] => {
  const type = `${file.type} ${file.name}`.toLowerCase();
  if (type.includes("webp") || /\.webp$/i.test(file.name)) return "sticker";
  if (type.includes("video") || /\.(mp4|3gp)$/i.test(file.name)) return "video";
  if (type.includes("audio") || /\.(mp3|ogg|m4a|aac|amr)$/i.test(file.name)) return "audio";
  if (type.includes("image") || /\.(jpg|jpeg|png)$/i.test(file.name)) return "image";
  return "document";
};

const WHATSAPP_MEDIA_RESOURCE_ACCEPT =
  "image/jpeg,image/png,image/webp,video/mp4,video/3gpp,audio/mpeg,audio/mp4,audio/aac,audio/amr,audio/ogg,application/pdf,text/plain,text/csv,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation,.jpg,.jpeg,.png,.webp,.mp4,.3gp,.mp3,.m4a,.aac,.amr,.ogg,.pdf,.txt,.csv,.doc,.docx,.xls,.xlsx,.ppt,.pptx";

const mediaFileHasSendableReference = (file: MediaResourceDraft["files"][number]) =>
  Boolean((file.url || "").trim());

const mediaFileWhatsAppStatus = (
  kind: MediaResourceDraft["kind"],
  file: MediaResourceDraft["files"][number]
): { sendable: boolean; mediaType: string; blockedReason: string | null } => {
  const mediaType = (file.media_type || inferWhatsAppMediaTypeFromName(file.name, file.mime_type || file.type)).toLowerCase();
  const mimeType = `${file.mime_type || ""}`.toLowerCase();
  const name = `${file.name || ""}`.toLowerCase();
  const hasReference = mediaFileHasSendableReference(file);
  let formatAllowed = false;
  let blockedReason: string | null = null;

  if (mediaType === "image") {
    formatAllowed = Boolean(mimeType === "image/jpeg" || mimeType === "image/png" || /\.(jpe?g|png)$/i.test(name));
    blockedReason = formatAllowed ? null : "unsupported_whatsapp_image_format";
    if (mimeType === "image/webp" || /\.webp$/i.test(name)) {
      formatAllowed = false;
      blockedReason = "webp_not_supported_as_whatsapp_image";
    }
  } else if (mediaType === "video") {
    formatAllowed = Boolean(["video/mp4", "video/3gpp"].includes(mimeType) || /\.(mp4|3gp)$/i.test(name));
    blockedReason = formatAllowed ? null : "unsupported_whatsapp_video_format";
  } else if (mediaType === "audio") {
    formatAllowed = Boolean(["audio/aac", "audio/mp4", "audio/mpeg", "audio/amr", "audio/ogg"].includes(mimeType) || /\.(aac|m4a|mp3|amr|ogg)$/i.test(name));
    blockedReason = formatAllowed ? null : "unsupported_whatsapp_audio_format";
  } else if (mediaType === "sticker") {
    const declaresSticker = kind === "sticker" || file.media_type === "sticker";
    formatAllowed = Boolean(declaresSticker && (mimeType === "image/webp" || /\.webp$/i.test(name)));
    blockedReason = formatAllowed ? null : "webp_requires_sticker_resource";
  } else {
    formatAllowed = mimeType !== "image/webp" && !/\.webp$/i.test(name);
    blockedReason = formatAllowed ? null : "unsupported_whatsapp_document_format";
  }

  if (!hasReference) {
    return { sendable: false, mediaType, blockedReason: "missing_url_or_media_id" };
  }
  return { sendable: formatAllowed, mediaType, blockedReason };
};

const inferWhatsAppMediaTypeFromName = (
  name: string,
  mimeType?: string
): NonNullable<MediaResourceDraft["files"][number]["media_type"]> => {
  const text = `${mimeType || ""} ${name || ""}`.toLowerCase();
  if (text.includes("webp") || /\.webp$/i.test(name)) return "sticker";
  if (text.includes("video") || /\.(mp4|3gp)$/i.test(name)) return "video";
  if (text.includes("audio") || /\.(mp3|ogg|m4a|aac|amr)$/i.test(name)) return "audio";
  if (text.includes("image") || /\.(jpg|jpeg|png)$/i.test(name)) return "image";
  return "document";
};

const mediaBlockedReasonLabel = (reason?: string | null) => {
  const labels: Record<string, string> = {
    missing_url_or_media_id: "sin URL",
    webp_not_supported_as_whatsapp_image: "WebP debe ser sticker",
    webp_requires_sticker_resource: "marca el recurso como sticker",
    unsupported_whatsapp_image_format: "imagen no compatible",
    unsupported_whatsapp_video_format: "video no compatible",
    unsupported_whatsapp_audio_format: "audio no compatible",
    unsupported_whatsapp_document_format: "documento no compatible",
    unsupported_whatsapp_media_type: "tipo no compatible",
  };
  return labels[String(reason || "")] || "no compatible";
};

const CHAT_THREAD_PATTERN_IMAGE = `url("data:image/svg+xml,${encodeURIComponent(
  `<svg id="patternId" width="100%" height="100%" xmlns="http://www.w3.org/2000/svg"><defs><pattern id="a" patternUnits="userSpaceOnUse" width="40" height="59.428" patternTransform="scale(1) rotate(35)"><rect x="0" y="0" width="100%" height="100%" fill="#f7f8faff"/><path d="M0 70.975V47.881m20-1.692L8.535 52.808v13.239L20 72.667l11.465-6.62V52.808zm0-32.95l11.465-6.62V-6.619L20-13.24 8.535-6.619V6.619L20 13.24m8.535 4.927v13.238L40 38.024l11.465-6.62V18.166L40 11.546zM20 36.333L0 47.88m0 0v23.094m0 0l20 11.548 20-11.548V47.88m0 0L20 36.333m0 0l20 11.549M0 11.547l-11.465 6.619v13.239L0 38.025l11.465-6.62v-13.24L0 11.548v-23.094l20-11.547 20 11.547v23.094M20 36.333V13.24" stroke-linecap="square" stroke-width="1" stroke="#e9eef7ff" fill="none"/></pattern></defs><rect width="800%" height="800%" transform="translate(0,-0.856)" fill="url(#a)"/></svg>`
)}")`;

const CHAT_THREAD_BACKGROUND_STYLE: React.CSSProperties = {
  backgroundColor: "#F7F8FA",
  backgroundImage: CHAT_THREAD_PATTERN_IMAGE,
};

const GUARDIAN_WORKSPACE_BACKGROUND_STYLE: React.CSSProperties = {
  backgroundColor: "#F4F8FF",
  backgroundImage:
    "radial-gradient(circle at 16% 8%, rgba(86, 156, 255, 0.22), transparent 30%), radial-gradient(circle at 88% 0%, rgba(37, 99, 235, 0.14), transparent 34%), linear-gradient(145deg, rgba(250, 253, 255, 0.98) 0%, rgba(239, 247, 255, 0.94) 46%, rgba(225, 239, 255, 0.9) 100%)",
};

const CHAT_THREAD_TOPBAR_STYLE: React.CSSProperties = {
  backgroundColor: "#F7F8FA",
  backgroundImage: `linear-gradient(rgba(255,255,255,0.70), rgba(255,255,255,0.70)), ${CHAT_THREAD_PATTERN_IMAGE}`,
};

const CHAT_COMPOSER_MAX_HEIGHT = 384;
const KNOWLEDGE_ACTIVE_CHAR_LIMIT = 80_000;

const resolveKnowledgeCharCount = (source: Pick<KnowledgeSource, "char_count" | "content">) => {
  const stored = Number(source.char_count);
  const contentLength = (source.content || "").length;
  if (Number.isFinite(stored) && stored > 0) return stored;
  return contentLength;
};

const knowledgeSourceAuthorLabel = (source: Pick<KnowledgeSource, "created_by" | "source_type">) => {
  const marker = `${source.created_by || ""} ${source.source_type || ""}`.toLowerCase();
  if (marker.includes("guardian")) return "Guardian";
  return "Humano";
};

type CartRecoveryFlowDraft = {
  id: string;
  title: string;
  enabled: boolean;
  templateName: string;
  templateLanguage: string;
  instructions: string;
  productScope: "all" | "selected";
  productIds: string[];
  productLabels?: Record<string, string>;
  productImages?: Record<string, string>;
  delayValue: number;
  delayUnit: "minutes" | "hours";
  ephemeral?: boolean;
};

type SalesActionsSettings = {
  futureContactFollowupEnabled: boolean;
  futureContactTemplateName: string;
  futureContactInstructions: string;
};

type DynamicFollowupSettings = {
  enabled: boolean;
  delayHours: number;
  delayMinutes: number | null;
  flows: {
    sale: boolean;
    confirmation: boolean;
    support: boolean;
  };
};

const defaultSalesActionsSettings = (): SalesActionsSettings => ({
  futureContactFollowupEnabled: false,
  futureContactTemplateName: "",
  futureContactInstructions: "",
});

const clampDynamicFollowupDelay = (value: unknown) => {
  const parsed = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(parsed)) return 6;
  return Math.min(6, Math.max(1, Math.round(parsed)));
};

// Delay opcional en MINUTOS (2..360) para poder probar los seguimientos de verdad;
// si es null se usa delayHours como siempre.
const clampDynamicFollowupDelayMinutes = (value: unknown): number | null => {
  if (value === null || value === undefined || value === "") return null;
  const parsed = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) return null;
  return Math.min(360, Math.max(2, Math.round(parsed)));
};

const dynamicFollowupSettingsFromMetadata = (metadata: Record<string, unknown> | undefined): DynamicFollowupSettings => {
  const raw = isRecord(metadata?.dynamic_followup_settings) ? metadata.dynamic_followup_settings : {};
  const rawFlows = isRecord(raw.flows) ? raw.flows : {};
  return {
    enabled: raw.enabled === true,
    delayHours: clampDynamicFollowupDelay(raw.delay_hours ?? raw.delayHours),
    delayMinutes: clampDynamicFollowupDelayMinutes(raw.delay_minutes ?? raw.delayMinutes),
    flows: {
      sale: rawFlows.sale !== false,
      confirmation: rawFlows.confirmation !== false,
      support: rawFlows.support !== false,
    },
  };
};

const dynamicFollowupSettingsToMetadata = (settings: DynamicFollowupSettings) => ({
  enabled: settings.enabled,
  delay_hours: clampDynamicFollowupDelay(settings.delayHours),
  delay_minutes: clampDynamicFollowupDelayMinutes(settings.delayMinutes),
  flows: {
    sale: settings.flows.sale,
    confirmation: settings.flows.confirmation,
    support: settings.flows.support,
  },
});

const salesActionsSettingsFromMetadata = (metadata: Record<string, unknown> | undefined): SalesActionsSettings => {
  const raw = isRecord(metadata?.sales_actions_settings) ? metadata.sales_actions_settings : {};
  return {
    futureContactFollowupEnabled: raw.future_contact_followup_enabled === true,
    futureContactTemplateName:
      typeof raw.future_contact_template_name === "string" ? raw.future_contact_template_name : "",
    futureContactInstructions:
      typeof raw.future_contact_instructions === "string" ? raw.future_contact_instructions : "",
  };
};

const salesActionsSettingsToMetadata = (settings: SalesActionsSettings) => ({
  future_contact_followup_enabled: settings.futureContactFollowupEnabled,
  future_contact_template_name: settings.futureContactTemplateName,
  future_contact_instructions: settings.futureContactInstructions,
});

const normalizeCartRecoveryFlowDrafts = (rows: unknown): CartRecoveryFlowDraft[] => {
  if (!Array.isArray(rows)) return [];
  return rows
    .map((row) => {
      if (!isRecord(row)) return null;
      const id = String(row.id || "").trim();
      if (!id) return null;
      return {
        id,
        title: typeof row.title === "string" ? row.title : "",
        enabled: row.enabled !== false,
        templateName: typeof row.templateName === "string" ? row.templateName : typeof row.template_name === "string" ? row.template_name : "",
        templateLanguage: typeof row.templateLanguage === "string" ? row.templateLanguage : typeof row.template_language === "string" ? row.template_language : "es",
        instructions: typeof row.instructions === "string" ? row.instructions : "",
        productScope: row.productScope === "all" || row.product_scope === "all" ? "all" : "selected",
        productIds: Array.isArray(row.productIds)
          ? row.productIds.map((value) => String(value)).filter(Boolean)
          : Array.isArray(row.product_ids)
            ? row.product_ids.map((value) => String(value)).filter(Boolean)
            : [],
        productLabels: isRecord(row.productLabels) ? (row.productLabels as Record<string, string>) : {},
        delayValue:
          typeof row.delayValue === "number"
            ? row.delayValue
            : typeof row.delay_value === "number"
              ? row.delay_value
              : 30,
        delayUnit:
          row.delayUnit === "hours" || row.delay_unit === "hours" ? "hours" : "minutes",
        ephemeral: row.ephemeral === true,
      } satisfies CartRecoveryFlowDraft;
    })
    .filter(Boolean) as CartRecoveryFlowDraft[];
};

const cartRecoveryFlowDraftsFromMetadata = (metadata: Record<string, unknown> | undefined) =>
  normalizeCartRecoveryFlowDrafts(metadata?.cart_recovery_flows);

const serializeCartRecoveryFlowDrafts = (rows: CartRecoveryFlowDraft[]) =>
  JSON.stringify(
    rows.map((row) => ({
      id: row.id,
      title: row.title,
      enabled: row.enabled,
      templateName: row.templateName,
      templateLanguage: row.templateLanguage,
      instructions: row.instructions,
      productScope: row.productScope,
      productIds: row.productIds,
      productLabels: row.productLabels || {},
      delayValue: row.delayValue,
      delayUnit: row.delayUnit,
    }))
  );

const isCartRecoveryFlowDraftCommitted = (flow: CartRecoveryFlowDraft) =>
  Boolean(flow.title.trim() && flow.templateName.trim() && (flow.productScope === "all" || flow.productIds.length > 0));

const committedCartRecoveryFlowDrafts = (rows: CartRecoveryFlowDraft[]) =>
  rows.filter((row) => !row.ephemeral || isCartRecoveryFlowDraftCommitted(row));

const validateCartRecoveryFlowDrafts = (drafts: CartRecoveryFlowDraft[]) => {
  const errors: string[] = [];
  committedCartRecoveryFlowDrafts(drafts).forEach((flow, index) => {
    const label = flow.title.trim() || `Recuperación ${index + 1}`;
    if (!flow.title.trim()) errors.push(`${label}: agrega un nombre.`);
    if (flow.productScope !== "all" && !flow.productIds.length) {
      errors.push(`${label}: elige productos o selecciona Todos.`);
    }
    if (!flow.templateName.trim()) errors.push(`${label}: elige una plantilla.`);
    if (!Number.isFinite(flow.delayValue) || flow.delayValue <= 0) {
      errors.push(`${label}: define cuánto tiempo esperar antes de enviar.`);
    }
  });
  return errors;
};

// Nombre secuencial "Base N" con N = max existente + 1 (spec Julian).
const nextSequentialFlowTitle = (baseLabel: string, existingTitles: string[]) => {
  const usedNumbers = existingTitles
    .map((title) => {
      const match = title.trim().match(new RegExp(`^${baseLabel}\\s+(\\d+)$`, "i"));
      return match ? Number(match[1]) : null;
    })
    .filter((value): value is number => Number.isFinite(value));
  const next = usedNumbers.length ? Math.max(...usedNumbers) + 1 : 1;
  return `${baseLabel} ${next}`;
};

// Displays claros por nombre exacto para el paquete oficial del chatbot. Tiene prioridad
// sobre los displayName de los presets para que toda la UI hable el mismo idioma.
const TEMPLATE_DISPLAY_NAME_OVERRIDES: Record<string, string> = {
  chatbot_confirmacion_inicio_v1: "Confirmación de pedido (inicia conversación)",
  chatbot_confirmacion_inicio_foto_v1: "Confirmación de pedido + foto (inicia conversación)",
  chatbot_confirmacion_recordatorio_v1: "Recordatorio de confirmación (si el cliente no respondió)",
  chatbot_pedido_confirmado_v1: "Pedido confirmado (aviso final tras confirmar)",
  chatbot_carrito_recuperacion_v1: "Recuperación de carrito",
  chatbot_guia_generada_v1: "Envío: guía generada",
  chatbot_recogido_v1: "Envío: recogido por transportadora",
  chatbot_en_camino_v1: "Envío: en camino",
  chatbot_en_oficina_v1: "Envío: en oficina o punto de entrega",
  chatbot_entregado_v1: "Envío: entregado",
  chatbot_novedad_v1: "Envío: con novedad",
  chatbot_devuelto_v1: "Envío: devuelto",
  chatbot_envio_cancelado_v1: "Envío: cancelado",
  chatbot_contacto_programado_v1: "Contacto programado (seguimiento acordado)",
  chatbot_guardian_resumen_v1: "Resumen del Guardián (para ti)",
  chatbot_guardian_alerta_v1: "Alerta del Guardián (para ti)",
};

// Purpose de cada plantilla del paquete recomendado (fallback local cuando el backend
// aún no devuelve `purpose` en el listado de templates).
const TEMPLATE_PURPOSE_BY_NAME: Record<string, string> = {
  chatbot_confirmacion_inicio_v1: "confirmation_flow",
  chatbot_confirmacion_inicio_foto_v1: "confirmation_flow_photo",
  chatbot_confirmacion_recordatorio_v1: "reminder",
  chatbot_pedido_confirmado_v1: "order_confirmed",
  chatbot_carrito_recuperacion_v1: "cart_recovery",
  chatbot_guia_generada_v1: "dropi_guia_generada",
  chatbot_recogido_v1: "dropi_recogido",
  chatbot_en_camino_v1: "dropi_en_camino",
  chatbot_en_oficina_v1: "dropi_en_oficina",
  chatbot_entregado_v1: "dropi_entregado",
  chatbot_novedad_v1: "dropi_novedad",
  chatbot_devuelto_v1: "dropi_devuelto",
  chatbot_envio_cancelado_v1: "dropi_cancelado",
  chatbot_contacto_programado_v1: "future_contact",
  chatbot_guardian_resumen_v1: "guardian_summary",
  chatbot_guardian_alerta_v1: "guardian_alert",
};

// Las 13 plantillas del paquete recomendado de Fluxi (POST provision-package).
const RECOMMENDED_TEMPLATE_PACKAGE_NAMES = Object.keys(TEMPLATE_PURPOSE_BY_NAME);

// Orden fijo dentro del tab Logística (flujo cronológico de una orden).
const LOGISTICS_PURPOSE_ORDER = [
  "confirmation_flow",
  "confirmation_flow_photo",
  "reminder",
  "order_confirmed",
  "dropi_guia_generada",
  "dropi_recogido",
  "dropi_en_camino",
  "dropi_en_oficina",
  "dropi_entregado",
  "dropi_novedad",
  "dropi_devuelto",
  "dropi_cancelado",
  "future_contact",
];

type TemplateCategoryTabId = "all" | "logistics" | "marketing" | "reopen" | "guardian" | "other";

const templateEffectivePurpose = (template: WhatsAppTemplate): string | null =>
  template.purpose || TEMPLATE_PURPOSE_BY_NAME[template.name] || null;

const templateCategoryTab = (template: WhatsAppTemplate): Exclude<TemplateCategoryTabId, "all"> => {
  const purpose = templateEffectivePurpose(template);
  if (purpose) {
    if (purpose === "guardian_summary" || purpose === "guardian_alert") return "guardian";
    if (purpose === "cart_recovery") return "marketing";
    // "Seguimiento acordado" es un contacto comercial futuro, no un evento logístico (Julián).
    if (purpose === "future_contact") return "marketing";
    if (purpose.startsWith("conversation_reopen")) return "reopen";
    if (LOGISTICS_PURPOSE_ORDER.includes(purpose) || purpose.startsWith("dropi_")) return "logistics";
    return "other";
  }
  return String(template.category || "").toUpperCase() === "MARKETING" ? "marketing" : "other";
};

const templateDisplayTitle = (template: WhatsAppTemplate) => {
  const override = TEMPLATE_DISPLAY_NAME_OVERRIDES[template.name];
  if (override) return override;
  const preset = Object.values(WHATSAPP_TEMPLATE_PRESETS).find((item) => item.suggestedName === template.name);
  return preset?.displayName || humanize(template.name);
};

// La plantilla sale con foto: tiene un componente HEADER con formato IMAGE en Meta.
const templateHasImageHeader = (template?: WhatsAppTemplate | null): boolean =>
  Boolean(
    template?.components?.some(
      (component) =>
        isRecord(component) &&
        String(component.type || "").toUpperCase() === "HEADER" &&
        String(component.format || "").toUpperCase() === "IMAGE"
    )
  );

// Placeholder de la foto del header: deja claro que el mensaje sale con imagen.
const TemplateImageHeaderPlaceholder = () => (
  <div className="mb-2 flex h-24 items-center justify-center rounded-lg bg-gray-100">
    <ImageIcon className="size-6 text-gray-400" />
  </div>
);

// Estados de plantilla en pickers (spec Julian 2026-07-15): PENDING se puede elegir
// (la acción queda configurada y arranca sola al aprobarse Meta); REJECTED/PAUSED/
// no-disponible se VEN pero deshabilitadas — el estado se entiende sin adivinar.
const TEMPLATE_STATE_BADGES: Record<string, string> = {
  PENDING: "En aprobación por Meta",
  REJECTED: "Rechazada por Meta",
  PAUSED: "Pausada por Meta",
  MISSING: "Ya no existe en tu WhatsApp",
};

const templateStateBadge = (template: WhatsAppTemplate): string => {
  const status = String(template.status || "").toUpperCase();
  if (status === "APPROVED") return "";
  return TEMPLATE_STATE_BADGES[status] || "No disponible";
};

const templateSelectableInPicker = (template: WhatsAppTemplate): boolean => {
  const status = String(template.status || "").toUpperCase();
  return status === "APPROVED" || status === "PENDING";
};

// Picker de plantillas (Julian): sin opción "sin asignar" (no hace sentido guardar sin plantilla).
const buildTemplatePickerOptions = (templates: WhatsAppTemplate[]) =>
  templates.map((template) => {
    const badge = templateStateBadge(template);
    return {
      value: template.name,
      label: badge ? `${templateDisplayTitle(template)} — ${badge}` : templateDisplayTitle(template),
      description: `${template.name} · ${template.language}`,
      preview: template.body_text || undefined,
      disabled: !templateSelectableInPicker(template),
    };
  });

const buildApprovedTemplateOptions = (templates: WhatsAppTemplate[]) => [
  { value: "", label: "Sin asignar", description: "Elige una plantilla aprobada" },
  ...templates.map((template) => ({
    value: template.name,
    label: templateDisplayTitle(template),
    description: `${template.name} · ${template.language}`,
    preview: template.body_text || undefined,
  })),
];

// El guardado de Confirmaciones muestra su alcance REAL (spec Julian, incidente
// berrylove): qué embudos quedaron confirmando, o el error claro — nunca un
// "guardado" con el gate apagado por debajo.
const notifyConfirmationSyncResult = (
  sync: ConfirmationSyncResult | null,
  flowsWereSaved: boolean
) => {
  if (!sync || !flowsWereSaved) return;
  if (sync.mode === "all_products") {
    if (sync.owner_all_enabled) {
      toast.success("Confirmaciones activas para TODOS tus embudos (incluye los que crees después)");
    } else {
      toast.error("No se pudo activar la confirmación para todos tus embudos — vuelve a guardar");
    }
    return;
  }
  if (sync.mode === "selected_products") {
    const enabledCount = sync.websites_enabled?.length ?? 0;
    if (enabledCount > 0) {
      toast.success(`Confirmaciones activas en ${enabledCount} embudo${enabledCount === 1 ? "" : "s"}`);
    } else {
      toast.error("Ningún embudo quedó habilitado para confirmaciones — revisa los productos elegidos");
    }
    return;
  }
  if (sync.mode === "error") {
    toast.error("No se pudo sincronizar el alcance de las confirmaciones — vuelve a guardar");
  }
};

type MetricsSurface = "dashboard" | "kanban";
type MetricsRangeMode = "preset" | "custom";

const METRICS_RANGE_PRESETS = [
  { id: "7d", label: "7 días", days: 7 },
  { id: "30d", label: "30 días", days: 30 },
  { id: "90d", label: "90 días", days: 90 },
] as const;

const metricsSinceIso = (days: number) => new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString();
const metricsSinceIsoFromDate = (date: string) => new Date(`${date}T00:00:00`).toISOString();
const metricsUntilIsoFromDate = (date: string) => new Date(`${date}T23:59:59.999`).toISOString();

const submitTextareaOnEnter = (
  event: React.KeyboardEvent<HTMLTextAreaElement>,
  submit: () => void
) => {
  if (event.key !== "Enter" || event.shiftKey) return;
  event.preventDefault();
  submit();
};

type AccountFormState = {
  phone_number_id: string;
  waba_id: string;
  display_name: string;
  access_token: string;
  app_secret: string;
  is_active: boolean;
  rotate_verify_token: boolean;
};

type KnowledgeFormState = {
  name: string;
  category: string;
  content: string;
  source_type: string;
  is_active: boolean;
};

type ContactFormState = {
  name: string;
  email: string;
  alias: string;
  phone: string;
  tags: string;
  custom_attributes: string;
};

type GuardianSuggestionCardData = {
  id: string;
  number?: number | null;
  title: string;
  affectedUsers: number;
  problem: string;
  solution: string;
  detectedAt?: string | null;
  needsUserInput?: boolean;
  inputRequest?: string;
  proposal?: GuardianProposal;
  example?: boolean;
  // Sugerencias creadas por el chatbot (kind chatbot_info_gap) pueden traer el producto asociado.
  kind?: string | null;
  productName?: string | null;
  // Sugerencias de aprendizaje (kind learning): el texto queda tal cual como el dueño lo apruebe.
  learningEntryText?: string | null;
  learningExampleSummary?: string | null;
  learningCategory?: string | null;
};

type ProcessedGuardianSuggestionCardData = GuardianSuggestionCardData & {
  processedStatus: "approved" | "rejected";
  actionResult?: {
    label: string;
    tone: "success" | "warning" | "danger" | "neutral";
  } | null;
};

type GuardianChatMessage = {
  id: string;
  sender: "guardian" | "owner";
  content: string;
  time: string;
  pending?: boolean;
  failed?: boolean;
  retryContent?: string;
};

type GuardianChatHistoryItem = {
  id: string;
  title: string;
  preview: string;
  time: string;
  messages: GuardianChatMessage[];
};

type SupportTestChatMessage = {
  id: string;
  sender: "customer" | "assistant";
  content: string;
  time: string;
  pending?: boolean;
  failed?: boolean;
  retryContent?: string;
  usageModel?: string | null;
  action?: string | null;
};

type StateView = "kanban" | "table";
type StateDomain = "orders" | "contacts";
type ConversationBucket = "open" | "resolved";
type MainWorkspace = "chat" | "guardian" | "states" | "appointments";
type ConversationActionLoading = { action: string; conversationId: number } | null;

const isConversationActionLoading = (
  loading: ConversationActionLoading,
  action: string,
  conversationId: number
) => loading?.action === action && loading.conversationId === conversationId;

const getInitialStatesDomain = (): StateDomain => {
  if (typeof window === "undefined") return "contacts";
  return window.localStorage.getItem("fluxi.chatbot.statesDomain") === "orders" ? "orders" : "contacts";
};

type AutomationDraft = {
  id: string;
  title: string;
  // Caja de avisos logísticos a la que pertenece la regla (persisten en el frontend_payload,
  // hermanos de title). Vacíos en automatizaciones generales o reglas viejas sin caja.
  groupKey?: string;
  groupTitle?: string;
  trigger: string;
  triggerStateTarget: string;
  triggerAiStateTarget: "active" | "paused";
  logisticsStatusTarget: string;
  action: string;
  templateName: string;
  stateTarget: string;
  aiStateTarget: "active" | "paused";
  productScope: "all" | "selected";
  productIds: string[];
  enabled: boolean;
  requestedEnabled?: boolean;
  configured?: boolean;
  ephemeral?: boolean;
  followupEnabled: boolean;
  followupDelayValue: number;
  followupDelayUnit: "minutes" | "hours";
  followupAction: "" | "send_template" | "change_state" | "set_ai_responses";
  followupTemplateName: string;
  followupStateTarget: string;
  followupAiStateTarget: "active" | "paused";
  // Salud visible (solo lectura, viene del backend): fallos de envío recientes.
  recentFailures?: {
    count: number;
    last_error: string | null;
    last_failed_at: string | null;
  };
};

type MediaResourceDraft = {
  id: string;
  title: string;
  kind: "image" | "gif" | "video" | "document" | "audio" | "sticker" | "mixed";
  summary: string;
  instructions: string;
  files: Array<{
    id: string;
    name: string;
    type: string;
    size: number;
    url?: string;
    mime_type?: string;
    media_type?: "image" | "video" | "document" | "audio" | "sticker";
    whatsapp_sendable?: boolean;
    whatsapp_blocked_reason?: string | null;
  }>;
  enabled: boolean;
};

type HotmartFormState = {
  display_name: string;
  client_id: string;
  client_secret: string;
  basic: string;
  hottok: string;
};

type DropiFormState = {
  display_name: string;
  country_code: string;
  base_url: string;
  api_token: string;
  webhook_secret: string;
};

const EMPTY_ACCOUNT_FORM: AccountFormState = {
  phone_number_id: "",
  waba_id: "",
  display_name: "",
  access_token: "",
  app_secret: "",
  is_active: true,
  rotate_verify_token: false,
};

const EMPTY_KNOWLEDGE_FORM: KnowledgeFormState = {
  name: "",
  category: "",
  content: "",
  source_type: "manual",
  is_active: true,
};

const EMPTY_CONTACT_FORM: ContactFormState = {
  name: "",
  email: "",
  alias: "",
  phone: "",
  tags: "",
  custom_attributes: "{}",
};

const GUARDIAN_SUGGESTION_EXAMPLES: GuardianSuggestionCardData[] = [
  {
    id: "example-access-email",
    title: "Documentar recuperación de acceso con correo incorrecto",
    affectedUsers: 9,
    problem:
      "Varios usuarios dicen que compraron el curso pero no reciben el acceso. En las conversaciones se repite el mismo patrón: compraron con un correo distinto, escriben desde WhatsApp sin recordar el correo exacto, y la IA no tiene una ruta clara para pedir los datos mínimos sin hacer demasiadas preguntas.",
    solution:
      "Agregar a la base de conocimiento un procedimiento de recuperación de acceso: pedir correo usado en compra, correo alterno, nombre completo, producto comprado y captura opcional del comprobante. Esto permite que la IA haga una validación ordenada, reduzca vueltas con el cliente y escale solo cuando realmente falte una activación manual.",
    example: true,
  },
  {
    id: "example-guarantee-policy",
    title: "Aclarar política de garantía y devolución",
    affectedUsers: 6,
    problem:
      "La IA está respondiendo con frases muy generales cuando los clientes preguntan por garantía, devolución o reembolso. Esto pasa porque no hay una política única cargada con tiempos, condiciones, excepciones y pasos concretos. El resultado es que algunas conversaciones terminan en humano aunque podrían resolverse de forma automática.",
    solution:
      "Crear una fuente de conocimiento específica para garantía: días disponibles para solicitarla, requisitos, qué casos no aplican, cómo enviar evidencia y cuál es el tiempo esperado de respuesta. Una política clara permite que la IA responda con seguridad y mantiene consistencia entre todos los agentes.",
    example: true,
  },
  {
    id: "example-payment-proof",
    title: "Estandarizar manejo de pagos pendientes",
    affectedUsers: 11,
    problem:
      "Hay clientes que envían comprobantes de pago, dicen que ya pagaron o preguntan por qué su orden sigue pendiente. La IA actualmente no distingue bien entre pago en revisión, pago rechazado, comprobante incompleto y pago aprobado pero no reflejado en el sistema.",
    solution:
      "Agregar un flujo de conocimiento para pagos: cómo identificar el estado de la orden, qué datos pedir del comprobante, cuándo informar que el pago está en validación y cuándo escalar a revisión manual. Esto evita respuestas ambiguas y ayuda a que soporte priorice solo los casos donde hay dinero o acceso en riesgo.",
    example: true,
  },
  {
    id: "example-course-start",
    title: "Crear guía de primeros pasos para nuevos alumnos",
    affectedUsers: 14,
    problem:
      "Después de recibir acceso, varios usuarios preguntan dónde empezar, qué módulo ver primero, si hay grupo de soporte, cómo descargar materiales o cuánto tiempo tienen para completar el curso. La IA responde distinto según la pregunta porque esa información está dispersa o no está cargada.",
    solution:
      "Crear una guía de bienvenida en la base de conocimiento con pasos concretos: cómo entrar, módulo recomendado para empezar, recursos incluidos, reglas del grupo, soporte disponible y tiempos de acceso. Esto mejora la experiencia del alumno nuevo y reduce preguntas repetidas durante las primeras horas después de la compra.",
    example: true,
  },
  {
    id: "example-escalation-criteria",
    title: "Definir cuándo escalar casos a humano",
    affectedUsers: 5,
    problem:
      "La IA escala algunos casos demasiado rápido y en otros espera demasiado antes de pedir ayuda. Los patrones más frecuentes son: cliente molesto por acceso, cobro duplicado, solicitud de reembolso, error técnico con capturas y cambios de datos sensibles como correo o teléfono.",
    solution:
      "Agregar criterios de escalamiento a la base de conocimiento: qué casos puede resolver la IA, qué casos requieren aprobación humana y qué información debe recopilar antes de escalar. Esto mejora la calidad del traspaso a soporte y evita que el equipo reciba conversaciones sin contexto suficiente.",
    example: true,
  },
];

const GUARDIAN_CHAT_HISTORY_EXAMPLES: GuardianChatHistoryItem[] = [
  {
    id: "current",
    title: "Revisión de soporte",
    preview: "Accesos, pagos y garantías concentraron la mayoría de dudas.",
    time: "Ahora",
    messages: [
      {
        id: "current-owner-1",
        sender: "owner",
        content: "Guardian, ¿qué viste hoy en soporte?",
        time: "9:18 a. m.",
      },
      {
        id: "current-guardian-1",
        sender: "guardian",
        content:
          "Detecté tres patrones repetidos: usuarios sin acceso por correo incorrecto, dudas sobre garantía y pagos enviados con comprobantes incompletos. Las sugerencias de la izquierda priorizan esos huecos para fortalecer la base de conocimiento.",
        time: "9:19 a. m.",
      },
      {
        id: "current-owner-2",
        sender: "owner",
        content: "Prioriza lo que más quite carga manual.",
        time: "9:20 a. m.",
      },
      {
        id: "current-guardian-2",
        sender: "guardian",
        content:
          "Empezaría por recuperación de acceso y pagos pendientes. Son flujos con alto volumen, datos verificables y bastante margen para que la IA resuelva sin escalar.",
        time: "9:20 a. m.",
      },
    ],
  },
  {
    id: "knowledge",
    title: "Base de conocimiento",
    preview: "Faltan políticas claras para reembolso, comprobantes y onboarding.",
    time: "Hoy",
    messages: [
      {
        id: "knowledge-owner-1",
        sender: "owner",
        content: "¿Qué conocimiento deberíamos cargar primero?",
        time: "8:42 a. m.",
      },
      {
        id: "knowledge-guardian-1",
        sender: "guardian",
        content:
          "Cargaría tres documentos cortos: política de garantía, pasos para validar pagos y guía de primeros pasos para alumnos nuevos. Hoy la IA responde esas preguntas con suficiente cortesía, pero sin precisión operativa.",
        time: "8:43 a. m.",
      },
      {
        id: "knowledge-guardian-2",
        sender: "guardian",
        content:
          "La mejora esperada es reducir escalaciones repetitivas y hacer que cada respuesta tenga criterios concretos: qué pedir, cuándo esperar y cuándo pasar a humano.",
        time: "8:44 a. m.",
      },
    ],
  },
  {
    id: "weekly",
    title: "Resumen operativo",
    preview: "La semana dejó señales claras para ordenar soporte.",
    time: "Ayer",
    messages: [
      {
        id: "weekly-owner-1",
        sender: "owner",
        content: "Dame el resumen de la semana en soporte.",
        time: "Ayer",
      },
      {
        id: "weekly-guardian-1",
        sender: "guardian",
        content:
          "El volumen se concentró en accesos después de compra, confirmaciones de pago y dudas de garantía. También vi que algunos casos llegan a humano sin datos mínimos, especialmente cuando el cliente está molesto o manda capturas sin contexto.",
        time: "Ayer",
      },
      {
        id: "weekly-guardian-2",
        sender: "guardian",
        content:
          "Mi recomendación es definir criterios de escalamiento y una checklist previa. Así el equipo recibe casos más claros y la IA conserva más conversaciones simples.",
        time: "Ayer",
      },
    ],
  },
];

const metadataString = (metadata: Record<string, unknown>, keys: string[]) => {
  for (const key of keys) {
    const value = metadata[key];
    if (typeof value === "string" && value.trim()) return value.trim();
  }
  return "";
};

const metadataNumber = (metadata: Record<string, unknown>, keys: string[], fallback = 1) => {
  for (const key of keys) {
    const value = metadata[key];
    if (typeof value === "number" && Number.isFinite(value)) return Math.max(0, Math.round(value));
    if (typeof value === "string" && value.trim()) {
      const parsed = Number(value);
      if (Number.isFinite(parsed)) return Math.max(0, Math.round(parsed));
    }
  }
  return fallback;
};

// Cita exacta de lo que la sugerencia aplicaría al aprobarse (regla de Julian: el dueño
// aprueba EXACTAMENTE lo que se va a escribir, sin adivinar).
const guardianProposedQuote = (
  proposal?: GuardianProposal | null
): { target: string; content: string } | null => {
  const metadata = (proposal?.metadata_json || {}) as Record<string, unknown>;
  const changes = metadata.proposed_changes as Record<string, unknown> | undefined;
  if (!changes || typeof changes !== "object") return null;
  const text = (value: unknown) => String(value ?? "").trim();
  const content =
    text(changes.text) || text(changes.content) || text(changes.description) || text(changes.body_text);
  if (!content) return null;
  const actionType = text(metadata.action_type);
  const targetByAction: Record<string, string> = {
    edit_instructions: `Agregar a instrucciones (${text(changes.field) || "generales"})`,
    create_knowledge_source: `Nueva fuente de conocimiento: ${text(changes.name)}`,
    update_knowledge_source: `Actualizar fuente: ${text(changes.name) || text(changes.id)}`,
    update_product_description: `Descripción del producto: ${text(changes.product_name) || text(changes.product_id)}`,
    create_template: `Plantilla WhatsApp: ${text(changes.name)}`,
  };
  return { target: targetByAction[actionType] || "Contenido exacto propuesto", content };
};

const GuardianProposedQuoteBlock = ({ proposal }: { proposal?: GuardianProposal | null }) => {
  const quote = guardianProposedQuote(proposal);
  if (!quote) return null;
  return (
    <div>
      <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">{quote.target}</p>
      <div className="mt-1 whitespace-pre-wrap rounded-xl border border-[#3151C9]/20 bg-[#3151C9]/5 px-3 py-2 italic leading-5 text-gray-800">
        {quote.content}
      </div>
    </div>
  );
};

// Categorías del aprendizaje del modo aprendiz: se muestran capitalizadas y con tilde donde aplique.
const LEARNING_CATEGORY_LABELS: Record<string, string> = {
  tono: "Tono",
  politica: "Política",
  acciones: "Acciones",
  informacion: "Información",
  otro: "Otro",
};

const learningCategoryLabel = (category?: string | null) => {
  const normalized = String(category || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
  if (!normalized) return "";
  return LEARNING_CATEGORY_LABELS[normalized] || humanize(category);
};

const proposalToSuggestionCard = (proposal: GuardianProposal): GuardianSuggestionCardData => {
  const metadata = isRecord(proposal.metadata_json) ? proposal.metadata_json : {};
  const proposedChanges = isRecord(metadata.proposed_changes) ? metadata.proposed_changes : {};
  const problem = metadataString(metadata, ["problem", "problema", "detected_problem"]) || proposal.description || "Sin problema detallado.";
  const solution =
    metadataString(metadata, ["solution", "solucion", "suggested_solution", "recommended_solution"]) ||
    proposal.impact ||
    "Revisar la sugerencia y aplicar el ajuste propuesto si hace sentido para la operación.";

  const needsUserInput = Boolean(metadata.needs_user_input ?? metadata.requiere_info_usuario);
  const inputRequest =
    metadataString(metadata, ["input_request", "solicitud_info"]) ||
    (needsUserInput ? "Completa la información que falta para poder aplicar esta sugerencia." : "");

  return {
    id: proposal.id,
    number: proposal.number ?? proposal.suggestion_number ?? null,
    title: proposal.title || proposal.kind || "Sugerencia",
    affectedUsers: metadataNumber(metadata, ["affected_users", "affectedUsers", "usuarios_afectados", "users_affected"]),
    problem,
    solution,
    detectedAt: proposal.created_at || null,
    needsUserInput,
    inputRequest,
    proposal,
    kind: proposal.kind || null,
    productName: metadataString(metadata, ["producto_nombre", "product_name"]) || null,
    learningEntryText: metadataString(proposedChanges, ["entry_text"]) || null,
    learningExampleSummary: metadataString(proposedChanges, ["example_summary"]) || null,
    learningCategory: metadataString(proposedChanges, ["category"]) || null,
  };
};

const guardianProposalActionResult = (proposal?: GuardianProposal): ProcessedGuardianSuggestionCardData["actionResult"] => {
  const metadata = isRecord(proposal?.metadata_json) ? proposal?.metadata_json : {};
  const action = isRecord(metadata.applied_action) ? metadata.applied_action : null;
  const status = String(action?.status || "").trim();
  if (!action || !status) return null;
  if (status === "applied") return { label: "Aplicada", tone: "success" };
  if (status === "requires_manual") return { label: "Requiere acción manual", tone: "warning" };
  if (status === "blocked") return { label: "Bloqueada", tone: "danger" };
  if (status === "no_action") return { label: "Sin acción automática", tone: "neutral" };
  return { label: humanize(status), tone: "neutral" };
};

const EMPTY_HOTMART_FORM: HotmartFormState = {
  display_name: "Hotmart",
  client_id: "",
  client_secret: "",
  basic: "",
  hottok: "",
};

const EMPTY_DROPI_FORM: DropiFormState = {
  display_name: "Dropi",
  country_code: "co",
  base_url: "",
  api_token: "",
  webhook_secret: "",
};

const statusOptions: ConversationStatus[] = ["open", "resolved"];
const priorityOptions: ConversationPriority[] = ["normal", "medium", "critical"];
const customerStateOptions = [
  "customer_state:interested",
  "customer_state:purchased",
  "support_state:active",
  "support_state:resolved",
];
const customerStateTagSet = new Set(customerStateOptions);

const settingsTabs: {
  id: SettingsPanelTab;
  label: string;
  icon: typeof Inbox;
}[] = [
  { id: "knowledge", label: "Asistente IA", icon: Brain },
  { id: "actions", label: "Acciones", icon: ListChecks },
  { id: "integrations", label: "Integraciones", icon: Plug },
  { id: "templates", label: "Plantillas", icon: FileText },
];

const automationTriggers = [
  {
    value: "purchase_created",
    label: "Usuario compra",
    description: "Cuando una orden queda creada o aprobada y aplica enviar seguimiento por WhatsApp.",
  },
  {
    value: "state_changed",
    label: "Cambia estado",
    description: "Cuando el contacto o la orden llega al estado elegido.",
  },
  {
    value: "logistics_event",
    label: "Evento logístico",
    description: "Cuando Dropi reporta un avance de guía, despacho, oficina, entrega o novedad.",
  },
  {
    value: "ai_responses_active",
    label: "IA se activa",
    description: "Cuando las respuestas IA quedan activas para nuevas conversaciones.",
  },
  {
    value: "ai_responses_paused",
    label: "IA se pausa",
    description: "Cuando las respuestas IA quedan pausadas para nuevas conversaciones.",
  },
];

const automationActions = [
  {
    value: "send_template",
    label: "Se envía plantilla",
    description: "Fluxi envía un mensaje de WhatsApp al contacto usando una plantilla disponible.",
  },
  {
    value: "change_state",
    label: "Cambiar estado",
    description: "Fluxi cambia el estado del contacto u orden según la regla.",
  },
  {
    value: "activate_ai_responses",
    label: "IA se activa",
    description: "Fluxi activa respuestas IA para nuevas conversaciones.",
  },
  {
    value: "pause_ai_responses",
    label: "IA se pausa",
    description: "Fluxi pausa respuestas IA para nuevas conversaciones.",
  },
];

const supportToolActions = [
  {
    key: "escalar_a_humano",
    title: "Escalar a humano",
    description: "Fluxi · Permite que la IA solicite ayuda humana sin pausar la conversación automática.",
    placeholder: "Cuándo debe pedir ayuda humana, qué casos son urgentes y cómo debe seguir acompañando al cliente...",
  },
  {
    key: "cambiar_prioridad_conversacion",
    title: "Cambiar prioridad",
    description: "Fluxi · Permite que la IA marque conversaciones normales, medias o críticas según el contexto.",
    placeholder: "Qué señales hacen que un caso sea medio o crítico, y cuándo volverlo a normal...",
  },
  {
    key: "cambiar_estado_cliente",
    title: "Cambiar estado del cliente",
    description: "Fluxi · Permite que la IA actualice si el contacto está interesado, compró o está en soporte.",
    placeholder: "Cuándo mover un cliente a interesado, compró, soporte activo o soporte resuelto...",
  },
  {
    key: "cambiar_estado_orden",
    title: "Cambiar estado de confirmación",
    description:
      "Fluxi · Permite que la IA mueva una confirmación existente a pendiente, confirmada, rechazada o expirada. No crea pedidos, no confirma compras reales y no cancela en Dropi.",
    placeholder:
      "Cuándo mover una confirmación interna; para confirmar o cancelar pedidos reales usa las herramientas comerciales...",
  },
  {
    key: "cerrar_conversacion",
    title: "Cerrar conversación",
    description: "Fluxi · Permite que la IA resuelva una conversación cuando el cliente ya quedó atendido.",
    placeholder: "Cuándo cerrar, cuándo dejar abierta y qué confirmación debe existir antes de resolver...",
  },
  {
    key: "guardar_nota_contacto",
    title: "Guardar nota",
    description: "Fluxi · Permite que la IA guarde notas internas útiles sobre preferencias o contexto del cliente.",
    placeholder: "Qué datos vale la pena guardar como nota y qué cosas no deberían quedar registradas...",
  },
  {
    key: "enviar_recurso_multimedia",
    title: "Enviar recurso multimedia",
    description: "Fluxi · Permite que la IA envíe imágenes, videos, gifs o documentos configurados en Asistente IA.",
    placeholder: "Cuándo enviar videos, imágenes, ebooks u otros recursos y cuándo solo mencionarlos...",
  },
];

const guardianToolActions = [
  {
    key: "crear_sugerencia_guardian",
    title: "Crear sugerencia",
    description: "Guardian · Permite que Guardian cree sugerencias accionables cuando detecta patrones o mejoras claras.",
    placeholder: "Cuándo vale la pena crear una sugerencia, qué evidencia mínima debe existir y qué tono usar...",
  },
  {
    key: "listar_sugerencias_guardian",
    title: "Listar sugerencias",
    description: "Guardian · Permite que Guardian consulte sugerencias pendientes, aprobadas o rechazadas para discutirlas.",
    placeholder: "Cuándo listar solo pendientes, cuándo incluir procesadas y cómo resumirlas al usuario...",
  },
  {
    key: "aprobar_sugerencia_guardian",
    title: "Aprobar sugerencia",
    description: "Guardian · Permite que Guardian apruebe una sugerencia cuando el usuario lo pida por chat.",
    placeholder: "Qué confirmación necesita antes de aprobar una sugerencia y cuándo debe pedir claridad...",
  },
  {
    key: "rechazar_sugerencia_guardian",
    title: "Rechazar sugerencia",
    description: "Guardian · Permite que Guardian rechace una sugerencia cuando el usuario lo pida por chat.",
    placeholder: "Cuándo rechazar, cuándo pedir confirmación y qué nota debería dejar al procesarla...",
  },
];

const automationStateTargets = [
  { value: "customer_state:interested", label: "Contacto interesado" },
  { value: "customer_state:purchased", label: "Contacto compró" },
  { value: "support_state:active", label: "Soporte activo" },
  { value: "support_state:resolved", label: "Soporte resuelto" },
  { value: "order_state:pending", label: "Orden en espera" },
  { value: "order_state:confirmed", label: "Orden confirmada" },
  { value: "order_state:rejected", label: "Orden rechazada" },
];

const automationAiStateTargets = [
  { value: "active", label: "IA activa" },
  { value: "paused", label: "IA pausada" },
];

// Iconos por estado (Julian): identifican el avance tanto en el dropdown como elegido.
const automationLogisticsStatusTargets = [
  { value: "guide_generated", label: "🧾 Guía generada" },
  { value: "dispatched", label: "📦 Pedido recogido" },
  { value: "in_transit", label: "🚚 Pedido en ruta" },
  { value: "at_office", label: "🏤 En oficina/punto de entrega" },
  { value: "delivered", label: "✅ Entregado" },
  { value: "incident", label: "⚠️ Con novedad" },
  { value: "returned", label: "↩️ Devuelto o rechazado" },
  { value: "cancelled", label: "❌ Cancelado" },
];

// Plantilla recomendada del paquete de Fluxi para cada estado logístico canónico.
// Todos los estados canónicos tienen su plantilla del paquete (incl. recogido, en oficina, novedad).
const LOGISTICS_STATE_RECOMMENDED_TEMPLATE: Record<string, string> = {
  guide_generated: "chatbot_guia_generada_v1",
  dispatched: "chatbot_recogido_v1",
  in_transit: "chatbot_en_camino_v1",
  at_office: "chatbot_en_oficina_v1",
  delivered: "chatbot_entregado_v1",
  incident: "chatbot_novedad_v1",
  returned: "chatbot_devuelto_v1",
  cancelled: "chatbot_envio_cancelado_v1",
};

const defaultAutomationDrafts: AutomationDraft[] = [];

const defaultMediaResourceDrafts: MediaResourceDraft[] = [];

const normalizeAutomationDrafts = (value: unknown): AutomationDraft[] => {
  if (!Array.isArray(value)) return defaultAutomationDrafts;
  const rows = value
    .map((item, index) => {
      if (!isRecord(item)) return null;
      const rawTrigger = typeof item.trigger === "string" ? item.trigger : "";
      const triggerAiStateTarget = item.triggerAiStateTarget === "paused" ? "paused" : "active";
      const trigger =
        rawTrigger === "ai_responses_changed"
          ? triggerAiStateTarget === "paused"
            ? "ai_responses_paused"
            : "ai_responses_active"
          : rawTrigger;
      const rawAction = typeof item.action === "string" ? item.action : "";
      const action =
        rawAction === "activate_ai_responses" || rawAction === "pause_ai_responses" ? "set_ai_responses" : rawAction;
      const followupDelayValue =
        typeof item.followupDelayValue === "number" && Number.isFinite(item.followupDelayValue) && item.followupDelayValue > 0
          ? Math.round(item.followupDelayValue)
          : 30;
      const rawTriggerStateTarget = typeof item.triggerStateTarget === "string" ? item.triggerStateTarget : undefined;
      const rawLogisticsStatusTarget =
        typeof item.logisticsStatusTarget === "string" ? item.logisticsStatusTarget : undefined;
      const rawStateTarget = typeof item.stateTarget === "string" ? item.stateTarget : undefined;
      const rawFollowupStateTarget = typeof item.followupStateTarget === "string" ? item.followupStateTarget : undefined;
      const aiStateTarget = item.aiStateTarget === "paused" ? "paused" : "active";
      const followupAiStateTarget = item.followupAiStateTarget === "paused" ? "paused" : "active";
      const rawFollowupAction = typeof item.followupAction === "string" ? item.followupAction : "";
      const followupAction =
        rawFollowupAction === "" ||
        rawFollowupAction === "change_state" ||
        rawFollowupAction === "set_ai_responses" ||
        rawFollowupAction === "send_template"
          ? rawFollowupAction
          : rawFollowupAction === "activate_ai_responses" || rawFollowupAction === "pause_ai_responses"
            ? "set_ai_responses"
            : "send_template";
      return {
        id: typeof item.id === "string" && item.id ? item.id : `automation-${index}`,
        title: typeof item.title === "string" ? item.title : "",
        groupKey: typeof item.groupKey === "string" ? item.groupKey : "",
        groupTitle: typeof item.groupTitle === "string" ? item.groupTitle : "",
        trigger: trigger === "" || automationTriggers.some((option) => option.value === trigger) ? trigger : "",
        triggerStateTarget:
          rawTriggerStateTarget === "" ||
          (rawTriggerStateTarget && automationStateTargets.some((option) => option.value === rawTriggerStateTarget))
            ? rawTriggerStateTarget
            : "",
        triggerAiStateTarget,
        logisticsStatusTarget:
          rawLogisticsStatusTarget === "" ||
          (rawLogisticsStatusTarget &&
            automationLogisticsStatusTargets.some((option) => option.value === rawLogisticsStatusTarget))
            ? rawLogisticsStatusTarget
            : "",
        action:
          action === "" || action === "set_ai_responses" || automationActions.some((option) => option.value === action)
            ? action
            : "",
        templateName: typeof item.templateName === "string" ? item.templateName : "",
        stateTarget:
          rawStateTarget === "" || (rawStateTarget && automationStateTargets.some((option) => option.value === rawStateTarget))
            ? rawStateTarget
            : "",
        aiStateTarget:
          rawAction === "pause_ai_responses" ? "paused" : rawAction === "activate_ai_responses" ? "active" : aiStateTarget,
        productScope: item.productScope === "all" ? "all" : "selected",
        productIds: Array.isArray(item.productIds) ? item.productIds.map(String).filter(Boolean) : [],
        enabled: typeof item.enabled === "boolean" ? item.enabled : true,
        requestedEnabled: typeof item.requestedEnabled === "boolean" ? item.requestedEnabled : undefined,
        configured: typeof item.configured === "boolean" ? item.configured : undefined,
        ephemeral: item.ephemeral === true,
        followupEnabled: typeof item.followupEnabled === "boolean" ? item.followupEnabled : false,
        followupDelayValue,
        followupDelayUnit: item.followupDelayUnit === "hours" ? "hours" : "minutes",
        followupAction,
        followupTemplateName: typeof item.followupTemplateName === "string" ? item.followupTemplateName : "",
        followupStateTarget:
          rawFollowupStateTarget === "" ||
          (rawFollowupStateTarget && automationStateTargets.some((option) => option.value === rawFollowupStateTarget))
            ? rawFollowupStateTarget
            : "",
        followupAiStateTarget:
          rawFollowupAction === "pause_ai_responses"
            ? "paused"
            : rawFollowupAction === "activate_ai_responses"
              ? "active"
              : followupAiStateTarget,
      } as AutomationDraft;
    })
    .filter(Boolean) as AutomationDraft[];
  return rows.length ? rows : defaultAutomationDrafts;
};

const automationDraftsFromSettings = (settings: SupportSettings): AutomationDraft[] =>
  normalizeAutomationDrafts(settings.metadata_json?.support_automations);

const isHiddenSupportAutomationRow = (row: SupportAutomation) =>
  row.automation_key.startsWith("__sales_") || row.metadata_json?.hidden_system_automation === true;

// M18: las automatizaciones ocultas/sistema (ej. __sales_future_contact__, con triggers backend
// como manual_followup que el UI no reconoce) NO deben entrar a la validacion del formulario humano,
// porque la bloquean entera y entonces el guardado nunca se ejecuta. Se conservan al guardar.
const isHiddenSystemAutomationDraft = (automation: AutomationDraft) =>
  String(automation.id || "").startsWith("__");

const shouldDropIncompleteSupportAutomationPayload = (payload: Record<string, unknown>) => {
  const configured = payload.configured === false;
  const title = typeof payload.title === "string" ? payload.title.trim() : "";
  const action = typeof payload.action === "string" ? payload.action.trim() : "";
  return configured && !title && (!action || action === "pending");
};

const automationDraftsFromRows = (
  rows: SupportAutomation[],
  fallbackSettings?: SupportSettings | null
): AutomationDraft[] => {
  const payloads = rows
    .filter((row) => !isHiddenSupportAutomationRow(row))
    .map((row): { payload: Record<string, unknown>; fromFrontendPayload: boolean } | null => {
      if (isRecord(row.frontend_payload)) {
        return {
          payload: {
            ...row.frontend_payload,
            enabled: row.enabled,
            requestedEnabled: row.requested_enabled,
            configured: row.configured,
          },
          fromFrontendPayload: true,
        };
      }
      const metadataPayload = isRecord(row.metadata_json?.frontend_payload)
        ? row.metadata_json.frontend_payload
        : null;
      if (metadataPayload) {
        return {
          payload: {
            ...metadataPayload,
            enabled: row.enabled,
            requestedEnabled: row.requested_enabled,
            configured: row.configured,
          },
          fromFrontendPayload: true,
        };
      }
      const triggerAiState = row.trigger_config?.ai_state === "paused" ? "paused" : "active";
      const trigger =
        row.trigger_type === "ai_responses_changed"
          ? triggerAiState === "paused"
            ? "ai_responses_paused"
            : "ai_responses_active"
          : row.trigger_type;
      return {
        payload: {
          id: row.automation_key,
          title: row.title || "",
          trigger,
          triggerStateTarget:
            typeof row.trigger_config?.state_target === "string" ? row.trigger_config.state_target : "",
          triggerAiStateTarget: triggerAiState,
          logisticsStatusTarget:
            typeof row.trigger_config?.status_target === "string" ? row.trigger_config.status_target : "",
          action: row.action_type,
          templateName:
            typeof row.action_config?.template_name === "string" ? row.action_config.template_name : "",
          stateTarget:
            typeof row.action_config?.state_target === "string" ? row.action_config.state_target : "",
          aiStateTarget: row.action_config?.ai_state === "paused" ? "paused" : "active",
          productScope: row.product_scope === "all" ? "all" : "selected",
          productIds: row.product_ids || [],
          enabled: row.enabled,
          requestedEnabled: row.requested_enabled,
          configured: row.configured,
        },
        fromFrontendPayload: false,
      };
    })
    .filter((item): item is { payload: Record<string, unknown>; fromFrontendPayload: boolean } =>
      item !== null && (item.fromFrontendPayload || !shouldDropIncompleteSupportAutomationPayload(item.payload))
    )
    .map((item) => item.payload);
  if (payloads.length) {
    const drafts = normalizeAutomationDrafts(payloads);
    // Salud visible por acción (solo lectura): se re-adjunta tras el normalize (que
    // whitelistea campos) usando el automation_key como id del draft.
    const healthByKey = new Map(
      rows
        .filter((row) => row.recent_failures && row.recent_failures.count > 0)
        .map((row) => [row.automation_key, row.recent_failures] as const)
    );
    return drafts.map((draft) =>
      healthByKey.has(draft.id) ? { ...draft, recentFailures: healthByKey.get(draft.id) } : draft
    );
  }
  return fallbackSettings ? automationDraftsFromSettings(fallbackSettings) : defaultAutomationDrafts;
};

const automationRequiresProducts = (automation: AutomationDraft) =>
  Boolean(automation.trigger) && !["state_changed", "ai_responses_active", "ai_responses_paused"].includes(automation.trigger);

const isAutomationDraftCommitted = (automation: AutomationDraft) =>
  Boolean(
    automation.title.trim() &&
      automation.trigger &&
      automation.action &&
      (automation.action !== "send_template" || automation.templateName.trim()) &&
      (automation.action !== "change_state" || automation.stateTarget) &&
      (automation.trigger !== "state_changed" || automation.triggerStateTarget) &&
      (automation.trigger !== "logistics_event" || automation.logisticsStatusTarget) &&
      (!automationRequiresProducts(automation) ||
        automation.productScope === "all" ||
        (automation.productIds || []).length > 0)
  );

const committedAutomationDrafts = (drafts: AutomationDraft[]) =>
  normalizeAutomationDrafts(drafts);

const pruneEphemeralAutomations = (drafts: AutomationDraft[]) =>
  normalizeAutomationDrafts(drafts);

const validateAutomationDrafts = (drafts: AutomationDraft[]) => {
  const errors: string[] = [];
  committedAutomationDrafts(drafts).forEach((automation, index) => {
    const label = automation.title.trim() || `Automatización ${index + 1}`;
    if (isHiddenSystemAutomationDraft(automation)) return;
    if (!automation.enabled) return;
    if (!automation.title.trim()) errors.push(`${label}: agrega un título.`);
    if (!automation.trigger) errors.push(`${label}: elige qué evento la activa.`);
    if (automation.trigger === "state_changed" && !automation.triggerStateTarget) {
      errors.push(`${label}: elige el estado que activa la regla.`);
    }
    if (automation.trigger === "logistics_event" && !automation.logisticsStatusTarget) {
      errors.push(`${label}: elige el estado logístico que activa la regla.`);
    }
    if (automationRequiresProducts(automation) && automation.productScope !== "all" && !automation.productIds.length) {
      errors.push(`${label}: elige productos o selecciona Todos.`);
    }
    if (!automation.action) errors.push(`${label}: elige qué pasa.`);
    if (automation.action === "send_template" && !automation.templateName.trim()) {
      errors.push(`${label}: elige la plantilla que se envía.`);
    }
    if (automation.action === "change_state" && !automation.stateTarget) {
      errors.push(`${label}: elige el estado destino.`);
    }
    if (automation.followupEnabled) {
      if (!Number.isFinite(automation.followupDelayValue) || automation.followupDelayValue <= 0) {
        errors.push(`${label}: define un tiempo válido para el seguimiento.`);
      }
      if (!automation.followupAction) errors.push(`${label}: elige qué pasa en el seguimiento.`);
      if (automation.followupAction === "send_template" && !automation.followupTemplateName.trim()) {
        errors.push(`${label}: elige la plantilla del seguimiento.`);
      }
      if (automation.followupAction === "change_state" && !automation.followupStateTarget) {
        errors.push(`${label}: elige el estado destino del seguimiento.`);
      }
    }
  });
  return errors;
};

const LEGACY_ORDER_REQUIRED_FIELDS = ["nombre", "telefono", "direccion", "ciudad", "departamento"];
const DEFAULT_ORDER_REQUIRED_FIELDS = [
  "full_name",
  "email",
  "phone",
  "national_id",
  "address",
  "address_detail",
  "city",
  "department",
  "neighborhood",
];

const ORDER_REQUIRED_FIELD_OPTIONS = [
  { value: "full_name", label: "Nombre completo" },
  { value: "phone", label: "Teléfono" },
  { value: "email", label: "Email" },
  { value: "national_id", label: "Documento / cédula" },
  { value: "address", label: "Dirección" },
  { value: "address_detail", label: "Detalle de entrega" },
  { value: "city", label: "Ciudad" },
  { value: "department", label: "Departamento" },
  { value: "neighborhood", label: "Barrio" },
  { value: "notas", label: "Notas de entrega" },
];

const ORDER_REQUIRED_FIELD_ALIASES: Record<string, string> = {
  nombre: "full_name",
  nombre_completo: "full_name",
  fullName: "full_name",
  telefono: "phone",
  celular: "phone",
  whatsapp: "phone",
  correo: "email",
  correo_electronico: "email",
  cedula: "national_id",
  documento: "national_id",
  document: "national_id",
  identification: "national_id",
  nationalId: "national_id",
  direccion: "address",
  direccion_exacta: "address",
  direccion_completa: "address",
  shipping_address: "address",
  detalle_direccion: "address_detail",
  notas_de_entrega: "address_detail",
  notas_entrega: "address_detail",
  delivery_notes: "address_detail",
  ciudad: "city",
  municipio: "city",
  pueblo: "city",
  departamento: "department",
  estado: "department",
  barrio: "neighborhood",
};

const canonicalOrderRequiredField = (field: string) => {
  const value = String(field || "").trim();
  return ORDER_REQUIRED_FIELD_ALIASES[value] || value;
};

const orderRequiredFieldLabel = (field: string) => {
  const value = canonicalOrderRequiredField(field);
  return ORDER_REQUIRED_FIELD_OPTIONS.find((item) => item.value === value)?.label || value;
};

const normalizeOrderRequiredFields = (fields: string[], flowType?: CommerceFlowDraft["flowType"]) => {
  const rawCleaned = fields.map((field) => String(field || "").trim()).filter(Boolean);
  const legacy = JSON.stringify(rawCleaned) === JSON.stringify(LEGACY_ORDER_REQUIRED_FIELDS);
  const cleaned = Array.from(new Set(rawCleaned.map(canonicalOrderRequiredField).filter(Boolean)));
  if (flowType !== "order_capture") return cleaned;
  return legacy ? [...DEFAULT_ORDER_REQUIRED_FIELDS] : cleaned;
};

const ORDER_CAPTURE_MODES: { value: CommerceOrderMode; label: string; description: string }[] = [
  {
    value: "chat_capture_cod",
    label: "Capturar datos en el chat",
    description: "La IA pide los datos requeridos, vende contraentrega y usa un embudo fuente para precios y opciones.",
  },
  {
    value: "fluxi_checkout_link",
    label: "Enviar checkout de Fluxi",
    description: "La IA envía el link exacto del embudo para que el cliente complete la compra.",
  },
];

const defaultCommerceLogisticsPolicy = (): CommerceLogisticsPolicy => ({
  dropiGuideNotifyEnabled: true,
  dropiGuideTemplateName: "dropi_guia_generada",
  dropiGuideInstructions: "",
  smallMunicipalityOffice: false,
  smallMunicipalityOfficeMode: "suggest",
  smallMunicipalityOfficeInstructions: "",
  askPreferredCarrier: false,
  preferredCarrierMode: "ask_customer",
  preferredCarrierRules: "",
  shippingSurchargeEnabled: false,
  shippingSurchargeThreshold: 20000,
  shippingSurchargePercent: 50,
  shippingSurchargeMode: "percent",
  shippingSurchargeFixedAmount: 5000,
  mapsValidationEnabled: false,
  mapsRejectOrderIfInvalid: true,
  dropiConfirmationEnabled: true,
  autoExpireEnabled: false,
  autoExpireHours: 48,
  confirmedInstructions: "",
  rejectedInstructions: "",
});

const LEGACY_DROPI_LOGISTICS_POLICY_KEYS = [
  "dropiAutoConfirmEnabled",
  "dropiClassificationEnabled",
  "greenOnly",
  "prepayRedOrange",
];

const normalizeCommerceLogisticsPolicy = (raw?: unknown): CommerceLogisticsPolicy => {
  const merged: Record<string, unknown> = {
    ...defaultCommerceLogisticsPolicy(),
    ...(isRecord(raw) ? raw : {}),
  };
  LEGACY_DROPI_LOGISTICS_POLICY_KEYS.forEach((key) => {
    delete merged[key];
  });
  return merged as CommerceLogisticsPolicy;
};

// ——— Imágenes del embudo para el chatbot (ventas) y foto de confirmación ———
const MAX_CHATBOT_IMAGES_SELECTED = 6;

const normalizeChatbotSelectedImage = (value: unknown): CommerceChatbotSelectedImage | null => {
  if (!isRecord(value)) return null;
  const key = String(value.key || "").trim();
  const url = String(value.url || "").trim();
  if (!key && !url) return null;
  return {
    key,
    url,
    description: String(value.description || "").trim(),
    source: String(value.source || "").trim(),
  };
};

const normalizeChatbotImagesConfig = (value: unknown): CommerceChatbotImagesConfig | null => {
  if (!isRecord(value)) return null;
  const selected = (Array.isArray(value.selected) ? value.selected : [])
    .map(normalizeChatbotSelectedImage)
    .filter(Boolean)
    .slice(0, MAX_CHATBOT_IMAGES_SELECTED) as CommerceChatbotSelectedImage[];
  return {
    selected,
    instructions: String(value.instructions || "").trim(),
  };
};

const normalizeChatbotImagesByProductId = (value: unknown): Record<string, CommerceChatbotImagesConfig> => {
  if (!isRecord(value)) return {};
  return Object.entries(value).reduce<Record<string, CommerceChatbotImagesConfig>>((result, [productId, config]) => {
    const key = String(productId).trim();
    const normalized = normalizeChatbotImagesConfig(config);
    if (key && normalized) result[key] = normalized;
    return result;
  }, {});
};

const normalizeConfirmationImage = (value: unknown): CommerceConfirmationImage | null => {
  if (!isRecord(value)) return null;
  const key = String(value.key || "").trim();
  const url = String(value.url || "").trim();
  if (!key && !url) return null;
  return {
    key,
    url,
    description: String(value.description || "").trim(),
  };
};

const normalizeConfirmationImageByProductId = (value: unknown): Record<string, CommerceConfirmationImage> => {
  if (!isRecord(value)) return {};
  return Object.entries(value).reduce<Record<string, CommerceConfirmationImage>>((result, [productId, image]) => {
    const key = String(productId).trim();
    const normalized = normalizeConfirmationImage(image);
    if (key && normalized) result[key] = normalized;
    return result;
  }, {});
};

const checkoutConfigProductMappings = (checkoutConfig: unknown): Record<string, unknown>[] => {
  if (!isRecord(checkoutConfig)) return [];
  const mappings = Array.isArray(checkoutConfig.product_mappings)
    ? checkoutConfig.product_mappings
    : Array.isArray(checkoutConfig.productMappings)
      ? checkoutConfig.productMappings
      : [];
  return mappings.filter(isRecord);
};

const chatbotImagesByProductIdFromCheckoutConfig = (checkoutConfig: unknown) =>
  checkoutConfigProductMappings(checkoutConfig).reduce<Record<string, CommerceChatbotImagesConfig>>(
    (result, mapping) => {
      const productRef = String(mapping.product_ref || "").trim();
      const config = normalizeChatbotImagesConfig(mapping.chatbot_images);
      if (productRef && config) result[productRef] = config;
      return result;
    },
    {}
  );

const confirmationImageByProductIdFromCheckoutConfig = (checkoutConfig: unknown) =>
  checkoutConfigProductMappings(checkoutConfig).reduce<Record<string, CommerceConfirmationImage>>(
    (result, mapping) => {
      const productRef = String(mapping.product_ref || "").trim();
      const image = normalizeConfirmationImage(mapping.confirmation_image);
      if (productRef && image) result[productRef] = image;
      return result;
    },
    {}
  );

// Units de ofertas por cantidad apagados para el chat: únicos, enteros positivos y
// ordenados (orden canónico para que la comparación dirty por JSON sea estable).
const normalizeDisabledOfferUnits = (value: unknown): number[] =>
  Array.isArray(value)
    ? Array.from(
        new Set(value.map((units) => Number(units)).filter((units) => Number.isInteger(units) && units > 0))
      ).sort((a, b) => a - b)
    : [];

const normalizeDisabledOfferUnitsByProductId = (value: unknown): Record<string, number[]> => {
  if (!isRecord(value)) return {};
  return Object.entries(value).reduce<Record<string, number[]>>((result, [productId, units]) => {
    const key = String(productId).trim();
    const normalized = normalizeDisabledOfferUnits(units);
    if (key && normalized.length) result[key] = normalized;
    return result;
  }, {});
};

const disabledOfferUnitsByProductIdFromCheckoutConfig = (checkoutConfig: unknown) =>
  checkoutConfigProductMappings(checkoutConfig).reduce<Record<string, number[]>>((result, mapping) => {
    const productRef = String(mapping.product_ref || "").trim();
    const units = normalizeDisabledOfferUnits(mapping.disabled_offer_units);
    if (productRef && units.length) result[productRef] = units;
    return result;
  }, {});

const commerceFlowFromApi = (row: CommerceFlowApiRow): CommerceFlowDraft => {
  const productIds = (row.products || [])
    .map((product) => String(product.product_ref || "").trim())
    .filter(Boolean);
  const productLabels = (row.products || []).reduce<Record<string, string>>((labels, product) => {
    const productRef = String(product.product_ref || "").trim();
    const label = String(product.label_snapshot || "").trim();
    if (productRef && label) labels[productRef] = label;
    return labels;
  }, {});
  const productImages = (row.products || []).reduce<Record<string, string>>((images, product) => {
    const productRef = String(product.product_ref || "").trim();
    const image = String(product.image_snapshot || "").trim();
    if (productRef && image) images[productRef] = image;
    return images;
  }, {});
  const productDescriptions = (
    Array.isArray(row.checkout_config?.product_mappings) ? row.checkout_config.product_mappings : []
  ).reduce<Record<string, string>>((descriptions, mapping) => {
    if (!isRecord(mapping)) return descriptions;
    const productRef = String(mapping.product_ref || "").trim();
    const description = String(mapping.description_snapshot || "").trim();
    if (productRef && description) descriptions[productRef] = description;
    return descriptions;
  }, {});
  const rawLogisticsPolicy = isRecord(row.logistics_policy) ? row.logistics_policy : {};
  const logisticsPolicy = normalizeCommerceLogisticsPolicy(rawLogisticsPolicy);
  const productScope =
    rawLogisticsPolicy.productScope === "all" || (row as { product_scope?: string }).product_scope === "all"
      ? "all"
      : productIds.length
        ? "selected"
        : "selected";
  delete logisticsPolicy.productScope;
  const checkoutWebsiteIds = (row.checkout_config?.website_ids || [])
    .map((websiteId) => String(websiteId).trim())
    .filter(Boolean);
  const checkoutWebsiteByProductId = normalizeCheckoutWebsiteByProductId(row.checkout_config, productIds, checkoutWebsiteIds);
  const chatbotImagesByProductId = chatbotImagesByProductIdFromCheckoutConfig(row.checkout_config);
  const disabledOfferUnitsByProductId = disabledOfferUnitsByProductIdFromCheckoutConfig(row.checkout_config);
  const confirmationImageByProductId = confirmationImageByProductIdFromCheckoutConfig(row.checkout_config);
  const confirmationPhotosEnabled = row.checkout_config?.confirmation_photos_enabled === true;
  return {
    id: String(row.id || row.flow_key || ""),
    title: String(row.name || ""),
    enabled: Boolean(row.enabled),
    flowType: row.flow_type === "order_capture" ? "order_capture" : "confirmation",
    mode:
      row.mode === "chat_capture_cod" || row.mode === "fluxi_checkout_link"
        ? row.mode
        : row.flow_type === "order_capture"
          ? "chat_capture_cod"
          : null,
    templateName: String(row.template_name || ""),
    templateLanguage: String(row.template_language || "es"),
    instructions: String(row.instructions || ""),
    productScope,
    productIds,
    productLabels,
    productImages,
    productDescriptions,
    requiredFields: normalizeOrderRequiredFields(
      Array.isArray(row.required_fields)
        ? row.required_fields.map((field) => String(field).trim()).filter(Boolean)
        : [],
      row.flow_type === "order_capture" ? "order_capture" : "confirmation"
    ),
    checkoutWebsiteIds,
    checkoutWebsiteByProductId,
    chatbotImagesByProductId,
    disabledOfferUnitsByProductId,
    confirmationImageByProductId,
    confirmationPhotosEnabled,
    logisticsPolicy,
    ephemeral: false,
  };
};

const checkoutProductKey = (productId: string) => `fluxi_product:${String(productId || "").trim()}`;

const normalizeCheckoutWebsiteByProductId = (
  checkoutConfig: unknown,
  productIds: string[] = [],
  legacyWebsiteIds: string[] = []
) => {
  const result: Record<string, string> = {};
  const config = checkoutConfig && typeof checkoutConfig === "object" && !Array.isArray(checkoutConfig)
    ? (checkoutConfig as Record<string, unknown>)
    : {};
  const mappings =
    (Array.isArray(config.product_mappings) && config.product_mappings) ||
    (Array.isArray(config.productMappings) && config.productMappings) ||
    [];
  mappings.forEach((item) => {
    if (!item || typeof item !== "object" || Array.isArray(item)) return;
    const record = item as Record<string, unknown>;
    const productRef = String(
      record.product_ref ||
        record.product_id ||
        record.productId ||
        record.product_key ||
        record.productKey ||
        ""
    )
      .split(":")
      .pop()
      ?.trim();
    const websiteId = String(record.website_id || record.websiteId || "").trim();
    if (productRef && websiteId) result[productRef] = websiteId;
  });
  if (!Object.keys(result).length && productIds.length && legacyWebsiteIds.length === 1) {
    productIds.forEach((productId) => {
      result[productId] = legacyWebsiteIds[0];
    });
  }
  if (!Object.keys(result).length && productIds.length === legacyWebsiteIds.length) {
    productIds.forEach((productId, index) => {
      const websiteId = legacyWebsiteIds[index];
      if (websiteId) result[productId] = websiteId;
    });
  }
  return result;
};

const uniqueCheckoutWebsiteIds = (checkoutWebsiteByProductId: Record<string, string> = {}) =>
  Array.from(new Set(Object.values(checkoutWebsiteByProductId).map((websiteId) => String(websiteId).trim()).filter(Boolean)));

const isCommerceFlowApiRow = (item: Record<string, unknown>) =>
  typeof item.flow_type === "string" ||
  typeof item.name === "string" ||
  Array.isArray(item.products) ||
  (typeof item.flow_key === "string" && !("flowType" in item));

const normalizeCommerceFlowDraftItem = (item: Record<string, unknown>, index = 0): CommerceFlowDraft | null => {
  if (!isCommerceFlowApiRow(item)) {
    const rawLogisticsPolicy = {
      ...(isRecord(item.logisticsPolicy) ? item.logisticsPolicy : {}),
      ...(isRecord(item.logistics_policy) ? item.logistics_policy : {}),
    };
    const logisticsPolicy = normalizeCommerceLogisticsPolicy(rawLogisticsPolicy);
    if ("productScope" in logisticsPolicy) delete logisticsPolicy.productScope;
    const productScope =
      item.productScope === "all"
        ? "all"
        : Array.isArray(item.productIds) && item.productIds.length
          ? "selected"
          : "selected";
    const flowType =
      item.flowType === "order_capture" || item.flow_type === "order_capture" ? "order_capture" : "confirmation";
    const rawRequiredFields = Array.isArray(item.requiredFields)
      ? item.requiredFields.map((field) => String(field).trim()).filter(Boolean)
      : Array.isArray(item.required_fields)
        ? item.required_fields.map((field) => String(field).trim()).filter(Boolean)
        : [];
    const requiredFields = normalizeOrderRequiredFields(rawRequiredFields, flowType);
    const checkoutWebsiteIds = Array.isArray(item.checkoutWebsiteIds)
      ? item.checkoutWebsiteIds.map((websiteId) => String(websiteId).trim()).filter(Boolean)
      : Array.isArray((item.checkout_config as { website_ids?: unknown[] } | undefined)?.website_ids)
        ? ((item.checkout_config as { website_ids?: unknown[] }).website_ids || [])
            .map((websiteId) => String(websiteId).trim())
            .filter(Boolean)
        : [];
    const productIds = Array.isArray(item.productIds)
      ? item.productIds.map((productId) => String(productId).trim()).filter(Boolean)
      : [];
    const checkoutWebsiteByProductId =
      isRecord(item.checkoutWebsiteByProductId)
        ? Object.fromEntries(
            Object.entries(item.checkoutWebsiteByProductId)
              .map(([productId, websiteId]) => [String(productId).trim(), String(websiteId || "").trim()] as const)
              .filter(([productId, websiteId]) => productId && websiteId)
          )
        : normalizeCheckoutWebsiteByProductId(item.checkout_config, productIds, checkoutWebsiteIds);
    const mode =
      item.mode === "chat_capture_cod" || item.mode === "fluxi_checkout_link"
        ? item.mode
        : flowType === "order_capture"
          ? "chat_capture_cod"
          : null;
    return {
      id: typeof item.id === "string" && item.id ? item.id : `commerce-flow-${index}`,
      title: typeof item.title === "string" ? item.title : typeof item.name === "string" ? item.name : "",
      enabled: typeof item.enabled === "boolean" ? item.enabled : true,
      flowType,
      mode,
      templateName:
        typeof item.templateName === "string"
          ? item.templateName
          : typeof item.template_name === "string"
            ? item.template_name
            : "",
      templateLanguage:
        typeof item.templateLanguage === "string"
          ? item.templateLanguage
          : typeof item.template_language === "string"
            ? item.template_language
            : "es",
      instructions: typeof item.instructions === "string" ? item.instructions : "",
      productScope,
      productIds,
      productLabels: isRecord(item.productLabels)
        ? Object.fromEntries(
            Object.entries(item.productLabels)
              .map(([key, value]) => [String(key).trim(), String(value || "").trim()] as const)
              .filter(([key, value]) => key && value)
          )
        : {},
      productDescriptions: isRecord(item.productDescriptions)
        ? Object.fromEntries(
            Object.entries(item.productDescriptions)
              .map(([key, value]) => [String(key).trim(), String(value || "").trim()] as const)
              .filter(([key, value]) => key && value)
          )
        : {},
      requiredFields,
      checkoutWebsiteIds,
      checkoutWebsiteByProductId,
      chatbotImagesByProductId: isRecord(item.chatbotImagesByProductId)
        ? normalizeChatbotImagesByProductId(item.chatbotImagesByProductId)
        : chatbotImagesByProductIdFromCheckoutConfig(item.checkout_config),
      disabledOfferUnitsByProductId: isRecord(item.disabledOfferUnitsByProductId)
        ? normalizeDisabledOfferUnitsByProductId(item.disabledOfferUnitsByProductId)
        : disabledOfferUnitsByProductIdFromCheckoutConfig(item.checkout_config),
      confirmationImageByProductId: isRecord(item.confirmationImageByProductId)
        ? normalizeConfirmationImageByProductId(item.confirmationImageByProductId)
        : confirmationImageByProductIdFromCheckoutConfig(item.checkout_config),
      confirmationPhotosEnabled:
        item.confirmationPhotosEnabled === true ||
        (isRecord(item.checkout_config) && item.checkout_config.confirmation_photos_enabled === true),
      logisticsPolicy,
      ephemeral: item.ephemeral === true,
    };
  }
  return commerceFlowFromApi(item as CommerceFlowApiRow);
};

const normalizeCommerceFlowDrafts = (value: unknown): CommerceFlowDraft[] => {
  if (!Array.isArray(value)) return [];
  return value
    .map((item, index) => {
      if (!isRecord(item)) return null;
      return normalizeCommerceFlowDraftItem(item, index);
    })
    .filter(Boolean) as CommerceFlowDraft[];
};

const serializeCommerceFlowDrafts = (drafts: CommerceFlowDraft[]) =>
  JSON.stringify(
    normalizeCommerceFlowDrafts(drafts).map(({ ephemeral: _ephemeral, ...flow }) => flow)
  );

const commerceFlowProductLabelSnapshot = (product: Product) => {
  const chip = productPickerChipContent(product);
  if (!chip.name.trim()) return "";
  return chip.shortCode ? `${chip.name} (${chip.shortCode})` : chip.name;
};

// Precio de una oferta por cantidad en formato humano: "$139.900 COP".
const formatQuantityOfferPrice = (precio: number, moneda: string | null): string =>
  `$${new Intl.NumberFormat("es-CO", { maximumFractionDigits: 0 }).format(precio)} ${moneda || "COP"}`;

// El backend entrega la oferta en el formato crudo que lee el agente
// ("precio embudo=145000.0 COP (fuente=FUNNEL)"); para el dueño se muestra humano.
const formatFunnelOfferForHumans = (raw: string): string => {
  const match = raw.match(/precio embudo=([\d.]+)\s*([A-Z]{3})?/);
  if (!match) return raw;
  const amount = Number.parseFloat(match[1]);
  if (!Number.isFinite(amount)) return raw;
  const formatted = new Intl.NumberFormat("es-CO", { maximumFractionDigits: 0 }).format(amount);
  const currency = match[2] || "COP";
  return `$${formatted} ${currency} — precio del embudo`;
};

type CheckoutWebsiteOption = Pick<
  Website,
  | "websiteId"
  | "url"
  | "name"
  | "productName"
  | "primaryHost"
  | "idSequence"
  | "sales_angle"
  | "sales_angle_description"
  | "sales_angle_complementary_angles"
  | "products"
  | "automaticOrderShopify"
  | "automaticOrderDropi"
  | "shopifyIntegrationStatus"
  | "dropiIntegrationStatus"
  | "configurations"
>;

const checkoutWebsiteHasPublicUrl = (website: CheckoutWebsiteOption | null | undefined) =>
  Boolean(String(website?.url || "").trim());

const checkoutWebsiteProductRef = (product: unknown) => {
  if (!product || typeof product !== "object" || Array.isArray(product)) return "";
  const record = product as Record<string, unknown>;
  return String(record.product_id || record.productId || record.product_ref || record.productRef || record.id || "").trim();
};

const productCatalogReferenceIds = (product: Product | null | undefined, fallbackProductId: string) =>
  Array.from(
    new Set(
      [
        fallbackProductId,
        product?.id,
        ...(Array.isArray(product?.child_products) ? product.child_products.map((child) => child.id) : []),
      ]
        .map((item) => String(item || "").trim())
        .filter(Boolean)
    )
  );

const productLinkedFunnelIds = (product: Product | null | undefined) => {
  const record = product as (Product & { linkedFunnels?: Product["linked_funnels"] }) | null | undefined;
  const directWebsiteId = String(product?.website_id || "").trim();
  const funnelRefs = [
    ...(Array.isArray(product?.linked_funnels) ? product.linked_funnels : []),
    ...(Array.isArray(record?.linkedFunnels) ? record.linkedFunnels : []),
    ...(Array.isArray(product?.child_products)
      ? product.child_products.flatMap((child) => [
          ...(child.website_id ? [{ website_id: child.website_id }] : []),
          ...(child.linked_funnels || []),
        ])
      : []),
  ];
  return new Set([
    ...(directWebsiteId ? [directWebsiteId] : []),
    ...funnelRefs.map((funnel) => String(funnel.website_id || "").trim()).filter(Boolean),
  ]);
};

const checkoutWebsiteContainsProduct = (
  website: CheckoutWebsiteOption | null | undefined,
  productId: string,
  product?: Product | null
) => {
  const expectedProductId = String(productId || "").trim();
  if (!expectedProductId || !website) return false;
  if (productLinkedFunnelIds(product).has(String(website.websiteId || "").trim())) return true;
  if (!Array.isArray(website.products)) return false;
  const expectedIds = new Set(productCatalogReferenceIds(product, expectedProductId));
  return website.products.some((websiteProduct) => expectedIds.has(checkoutWebsiteProductRef(websiteProduct)));
};

const checkoutWebsitesForProduct = (
  productId: string,
  checkoutWebsites: CheckoutWebsiteOption[] = [],
  product?: Product | null
) =>
  checkoutWebsites.filter(
    (website) => checkoutWebsiteHasPublicUrl(website) && checkoutWebsiteContainsProduct(website, productId, product)
  );

const checkoutWebsiteOpenUrl = (website: CheckoutWebsiteOption | null | undefined) => {
  const rawUrl = String(website?.url || "").trim();
  if (!rawUrl) return "";
  try {
    const url = new URL(rawUrl);
    url.searchParams.set("fluxi_checkout", "open");
    return url.toString();
  } catch {
    return `${rawUrl}${rawUrl.includes("?") ? "&" : "?"}fluxi_checkout=open`;
  }
};

const checkoutWebsiteSequenceLabel = (website: CheckoutWebsiteOption | null | undefined) => {
  const sequence = Number(website?.idSequence || 0);
  if (Number.isFinite(sequence) && sequence > 0) return `#${sequence}`;
  const fallback = String(website?.websiteId || "").trim();
  return fallback ? `#${fallback.slice(0, 6)}` : "";
};

const checkoutWebsiteName = (website: CheckoutWebsiteOption | null | undefined) =>
  String(website?.name || website?.productName || `Embudo ${website?.idSequence || website?.websiteId || ""}`).trim();

const checkoutWebsiteSubtitle = (website: CheckoutWebsiteOption | null | undefined) =>
  String(website?.sales_angle || website?.productName || website?.primaryHost || website?.url || "").trim();

const CHECKOUT_ONLINE_PAYMENT_PROVIDERS = new Set(["stripe", "mercadopago", "confio", "shopify"]);

const normalizeCheckoutPaymentMethods = (
  rawMethods: unknown,
  rawProvider: unknown,
) => {
  const provider = String(rawProvider || "").trim().toLowerCase();
  const validProvider = CHECKOUT_ONLINE_PAYMENT_PROVIDERS.has(provider) ? provider : null;
  const methods = Array.isArray(rawMethods)
    ? rawMethods.map((method) => String(method || "").trim().toLowerCase()).filter(Boolean)
    : [];
  const normalizedMethods: string[] = [];

  methods.forEach((method) => {
    if (method === "cod" && !normalizedMethods.includes("cod")) {
      normalizedMethods.push("cod");
    }
    if (method === "online" && validProvider && !normalizedMethods.includes("online")) {
      normalizedMethods.push("online");
    }
  });

  return {
    methods: normalizedMethods.length ? normalizedMethods : ["cod"],
    provider: normalizedMethods.includes("online") ? validProvider : null,
    hadInvalidOnline:
      methods.includes("online") && !validProvider,
  };
};

const checkoutWebsitePaymentContext = (
  website: CheckoutWebsiteOption | null | undefined,
  mode: CommerceOrderMode | null
) => {
  const configurations = website?.configurations || { enabled: false, name_funnel: "" };
  const normalized = normalizeCheckoutPaymentMethods(
    configurations.allowed_payment_methods,
    configurations.online_payment_provider,
  );
  if (mode === "chat_capture_cod") {
    return {
      order_mode: mode || null,
      charge_mode: "contraentrega",
      allowed_payment_methods: ["cod"],
      online_payment_provider: null,
      checkout_provider: configurations.checkout_provider || null,
      shopify_checkout_enabled: Boolean(configurations.shopify_checkout_enabled),
    };
  }
  return {
    order_mode: mode || null,
    charge_mode: "checkout_link",
    allowed_payment_methods: normalized.methods,
    online_payment_provider: normalized.provider,
    checkout_provider: configurations.checkout_provider || null,
    shopify_checkout_enabled: Boolean(configurations.shopify_checkout_enabled),
    payment_configuration_warning: normalized.hadInvalidOnline
      ? "online_payment_without_explicit_provider_normalized_to_cod"
      : null,
  };
};

const checkoutWebsiteFulfillmentContext = (website: CheckoutWebsiteOption | null | undefined) => {
  const configurations = website?.configurations || { enabled: false, name_funnel: "" };
  const shopifyAutomatic = Boolean(website?.automaticOrderShopify || configurations.automatic_order_shopify);
  const dropiAutomatic = Boolean(website?.automaticOrderDropi || configurations.automatic_order_dropi);
  return {
    fulfillment_destination: dropiAutomatic ? "dropi" : shopifyAutomatic ? "shopify" : "none",
    dropi_automatic_order: dropiAutomatic,
    shopify_automatic_order: shopifyAutomatic,
    shopify_checkout_enabled: Boolean(configurations.shopify_checkout_enabled),
    shopify_integration_state: website?.shopifyIntegrationStatus || configurations.shopify_integration_state || null,
    dropi_integration_state: website?.dropiIntegrationStatus || configurations.dropi_integration_state || null,
  };
};

const checkoutWebsiteFulfillmentLabel = (website: CheckoutWebsiteOption | null | undefined) => {
  const context = checkoutWebsiteFulfillmentContext(website);
  if (context.dropi_automatic_order) return "Sincroniza con Dropi";
  if (context.shopify_automatic_order) return "Sincroniza con Shopify";
  return "Sin sincronización automática";
};

type FunnelFulfillmentDestination = "none" | "shopify" | "dropi";

type FunnelDropiIntegrationResponse = {
  automatic_order_dropi?: boolean;
  products?: Array<{ id: string; dropi_id?: string; dropiId?: string }>;
  compatible_with_funnel?: boolean;
  compatibility_reason?: string | null;
};

type DropiProductLookupResponse = {
  id: string;
  name: string;
  image?: string | null;
  price?: number | string | null;
  suggested_price?: number | string | null;
  sku?: string | null;
  variants?: Array<{
    id: string;
    name?: string | null;
    sku?: string | null;
    price?: number | string | null;
    stock?: number | null;
  }>;
};

type FunnelShopifyVariantMapping = {
  variant_id?: string;
  variantId?: string;
  sku?: string;
  id_shopify_variant?: string;
  idShopifyVariant?: string;
};

type FunnelShopifyProductMapping = {
  product_id?: string;
  productId?: string;
  sku?: string;
  id_shopify_product?: string;
  idShopifyProduct?: string;
  variants?: FunnelShopifyVariantMapping[];
};

type FunnelShopifyIntegrationResponse = {
  token?: string | null;
  url_store?: string | null;
  automatic_order_shopify?: boolean;
  currency_mode?: string | null;
  checkout_provider?: string | null;
  storefront_access_token?: string | null;
  client_id?: string | null;
  products?: FunnelShopifyProductMapping[];
};

const getFunnelDropiIntegrationState = async (websiteId: string) => {
  const response = await axiosInstanceWebsite.get<FunnelDropiIntegrationResponse>(
    `/websites/${encodeURIComponent(websiteId)}/dropi-integration`
  );
  return {
    ...response.data,
    products: response.data.products || [],
  };
};

const lookupDropiProduct = async (productId: string) => {
  const response = await axiosInstanceWebsite.get<DropiProductLookupResponse>(
    `/order/dropi/products/${encodeURIComponent(productId)}`
  );
  return response.data;
};

const updateFunnelDropiAutomatic = async (websiteId: string, enabled: boolean) => {
  const current = await getFunnelDropiIntegrationState(websiteId);
  if (enabled) {
    if (current.compatible_with_funnel === false) {
      throw new Error(current.compatibility_reason || "Este embudo no es compatible con Dropi automático.");
    }
    if (!current.products.length) {
      throw new Error("Configura el mapeo de productos Dropi en este embudo antes de activar Dropi.");
    }
  }
  const products = current.products.map((product) => ({
    id: product.id,
    dropi_id: product.dropi_id || product.dropiId || "",
  }));
  await axiosInstanceWebsite.put(`/websites/${encodeURIComponent(websiteId)}/dropi-integration`, {
    automatic_order_dropi: enabled,
    products,
  });
};

const updateFunnelDropiIntegration = async (
  websiteId: string,
  payload: { automatic_order_dropi?: boolean; products: Array<{ id: string; dropi_id: string }> }
) => {
  const response = await axiosInstanceWebsite.put<FunnelDropiIntegrationResponse>(
    `/websites/${encodeURIComponent(websiteId)}/dropi-integration`,
    payload
  );
  return response.data;
};

const getFunnelShopifyIntegrationState = async (websiteId: string) => {
  try {
    const response = await axiosInstanceWebsite.get<FunnelShopifyIntegrationResponse | null>(
      `/websites/${encodeURIComponent(websiteId)}/shopify-integration`
    );
    return response.data || null;
  } catch (error) {
    const status = (error as { response?: { status?: number } })?.response?.status;
    if (status === 404) return null;
    throw error;
  }
};

const updateFunnelShopifyAutomatic = async (websiteId: string, enabled: boolean) => {
  const current = await getFunnelShopifyIntegrationState(websiteId);
  if (!current) {
    throw new Error("Conecta Shopify en este embudo antes de activar la sincronización.");
  }
  if (enabled) {
    if (String(current.checkout_provider || "").trim().toLowerCase() === "shopify") {
      throw new Error("Este embudo usa checkout Shopify. Eso es distinto a sincronizar órdenes automáticamente a Shopify.");
    }
    if (!String(current.token || "").trim()) {
      throw new Error("Agrega el token Admin de Shopify en este embudo antes de activar la sincronización.");
    }
    if (!String(current.url_store || "").trim()) {
      throw new Error("Agrega la URL de la tienda Shopify en este embudo antes de activar la sincronización.");
    }
    if (!Array.isArray(current.products) || !current.products.length) {
      throw new Error("Mapea productos Shopify en este embudo antes de activar la sincronización.");
    }
  }
  await axiosInstanceWebsite.put(`/websites/${encodeURIComponent(websiteId)}/shopify-integration`, {
    token: current.token || null,
    url_store: current.url_store || null,
    automatic_order_shopify: enabled,
    currency_mode: current.currency_mode || undefined,
    checkout_provider: current.checkout_provider || undefined,
    storefront_access_token: current.storefront_access_token || null,
    client_id: current.client_id || null,
    products: current.products || [],
  });
};

const updateFunnelShopifyIntegration = async (
  websiteId: string,
  payload: {
    token?: string | null;
    url_store?: string | null;
    automatic_order_shopify?: boolean;
    currency_mode?: string | null;
    checkout_provider?: string | null;
    storefront_access_token?: string | null;
    client_id?: string | null;
    products?: FunnelShopifyProductMapping[];
  }
) => {
  const response = await axiosInstanceWebsite.put<FunnelShopifyIntegrationResponse>(
    `/websites/${encodeURIComponent(websiteId)}/shopify-integration`,
    payload
  );
  return response.data;
};

const disableFunnelFulfillmentDestination = async (websiteId: string) => {
  const [dropiState, shopifyState] = await Promise.all([
    getFunnelDropiIntegrationState(websiteId).catch(() => null),
    getFunnelShopifyIntegrationState(websiteId).catch(() => null),
  ]);
  if (dropiState?.automatic_order_dropi) {
    await updateFunnelDropiIntegration(websiteId, {
      automatic_order_dropi: false,
      products: (dropiState.products || []).map((product) => ({
        id: product.id,
        dropi_id: product.dropi_id || product.dropiId || "",
      })),
    });
  }
  if (shopifyState?.automatic_order_shopify) {
    await updateFunnelShopifyIntegration(websiteId, {
      ...shopifyState,
      automatic_order_shopify: false,
      products: shopifyState.products || [],
    });
  }
};

const saveWebsiteSyncDraft = async (draft: WebsiteSyncDraftState) => {
  const websiteId = draft.websiteId.trim();
  if (!websiteId) return;
  if (draft.provider === "none" || !draft.provider) {
    await disableFunnelFulfillmentDestination(websiteId);
    return;
  }
  if (draft.provider === "dropi") {
    const shopifyState = await getFunnelShopifyIntegrationState(websiteId).catch(() => null);
    await updateFunnelDropiIntegration(websiteId, {
      automatic_order_dropi: true,
      products: draft.rows.map((row) => ({ id: row.id, dropi_id: row.dropi_id.trim() })),
    });
    if (shopifyState?.automatic_order_shopify) {
      await updateFunnelShopifyIntegration(websiteId, {
        ...shopifyState,
        automatic_order_shopify: false,
        products: shopifyState.products || [],
      });
    }
    return;
  }
  const dropiState = await getFunnelDropiIntegrationState(websiteId).catch(() => null);
  await updateFunnelShopifyIntegration(websiteId, {
    token: draft.shopifyToken?.trim() || null,
    url_store: draft.shopifyUrlStore?.trim() || null,
    automatic_order_shopify: true,
    currency_mode: draft.shopifyCurrencyMode || "FUNNEL_CURRENCY",
    checkout_provider: draft.shopifyCheckoutProvider || "fluxi",
    storefront_access_token: draft.shopifyStorefrontToken?.trim() || null,
    client_id: draft.shopifyClientId?.trim() || null,
    products: draft.rows.map((row) => ({
      product_id: row.id,
      sku: row.sku.trim(),
      id_shopify_product: row.id_shopify_product.trim(),
      variants: row.variants || [],
    })),
  });
  if (dropiState?.automatic_order_dropi) {
    await updateFunnelDropiIntegration(websiteId, {
      automatic_order_dropi: false,
      products: (dropiState.products || []).map((product) => ({
        id: product.id,
        dropi_id: product.dropi_id || product.dropiId || "",
      })),
    });
  }
};

const validateWebsiteSyncDrafts = (draftsByWebsiteId: Record<string, WebsiteSyncDraftState>) => {
  const errors: string[] = [];
  Object.values(draftsByWebsiteId).forEach((draft) => {
    if (!draft.dirty) return;
    if (draft.loading || !draft.loaded) {
      errors.push("Espera a que cargue la configuración de sincronización del embudo.");
      return;
    }
    if (draft.error) {
      errors.push(draft.error);
      return;
    }
    if (draft.provider === "none" || !draft.provider) return;
    if (!draft.rows.length) {
      errors.push("El embudo no tiene productos para sincronizar.");
      return;
    }
    if (draft.provider === "dropi") {
      if (draft.dropiCompatible === false) {
        errors.push(draft.dropiCompatibilityReason || "Este embudo no es compatible con Dropi automático.");
        return;
      }
      const pendingRow = draft.rows.find((row) => {
        const dropiId = row.dropi_id.trim();
        return (
          !row.id.trim() ||
          !dropiId ||
          row.dropi_needs_verification ||
          Boolean(row.dropi_verification_error)
        );
      });
      if (pendingRow) {
        errors.push(`Verifica el ID de Dropi de ${pendingRow.name || "cada producto"} antes de guardar.`);
      }
      return;
    }
    const shopifyIncomplete = !draft.shopifyUrlStore?.trim() ||
      !draft.shopifyToken?.trim() ||
      draft.rows.some((row) => !row.id.trim() || !row.id_shopify_product.trim());
    if (shopifyIncomplete) {
      errors.push("Completa tienda, token y producto Shopify antes de guardar la sincronización.");
    }
  });
  return errors;
};

const commerceFlowToApiPayload = (flow: CommerceFlowDraft, checkoutWebsites: CheckoutWebsiteOption[] = []) => {
  const checkoutWebsiteByProductId = flow.checkoutWebsiteByProductId || {};
  const salesProductMappings =
    flow.flowType === "order_capture" && flow.productScope !== "all"
      ? flow.productIds
          .map((productId) => {
            const websiteId = String(checkoutWebsiteByProductId[productId] || "").trim();
            if (!websiteId) return null;
            const website = checkoutWebsites.find((item) => String(item.websiteId).trim() === websiteId);
            const label = flow.productLabels?.[productId] || "";
            const chatbotImages = normalizeChatbotImagesConfig(flow.chatbotImagesByProductId?.[productId]);
            const disabledOfferUnits = normalizeDisabledOfferUnits(flow.disabledOfferUnitsByProductId?.[productId]);
            return {
              product_source: "fluxi_product",
              product_ref: productId,
              product_key: checkoutProductKey(productId),
              website_id: websiteId,
              checkout_url: checkoutWebsiteOpenUrl(website),
              checkout_url_snapshot: checkoutWebsiteOpenUrl(website),
              funnel_number: website?.idSequence || websiteId,
              website_name: checkoutWebsiteName(website),
              sales_angle: website?.sales_angle || "",
              sales_angle_description: website?.sales_angle_description || "",
              sales_angle_complementary_angles: website?.sales_angle_complementary_angles || [],
              payment_context: checkoutWebsitePaymentContext(website, flow.mode),
              fulfillment_context: checkoutWebsiteFulfillmentContext(website),
              ...(label ? { product_name: label } : {}),
              ...(flow.productDescriptions?.[productId]
                ? { description_snapshot: flow.productDescriptions[productId].slice(0, 600) }
                : {}),
              ...(chatbotImages ? { chatbot_images: chatbotImages } : {}),
              ...(disabledOfferUnits.length ? { disabled_offer_units: disabledOfferUnits } : {}),
            };
          })
          .filter(Boolean)
      : [];
  // Confirmación: solo materializamos mapping cuando hay foto elegida; sin foto el backend
  // usa por defecto la imagen principal del producto. Con scope "todos" no hay productIds:
  // las fotos elegidas viven por producto del catálogo (keys del mapa de imágenes).
  const confirmationImageProductIds =
    flow.productScope === "all"
      ? Object.keys(flow.confirmationImageByProductId || {})
      : flow.productIds;
  const confirmationProductMappings =
    flow.flowType === "confirmation"
      ? confirmationImageProductIds
          .map((productId) => {
            const confirmationImage = normalizeConfirmationImage(flow.confirmationImageByProductId?.[productId]);
            if (!confirmationImage) return null;
            const websiteId = String(checkoutWebsiteByProductId[productId] || "").trim();
            const label = flow.productLabels?.[productId] || "";
            return {
              product_source: "fluxi_product",
              product_ref: productId,
              product_key: checkoutProductKey(productId),
              website_id: websiteId,
              ...(label ? { product_name: label } : {}),
              confirmation_image: confirmationImage,
            };
          })
          .filter(Boolean)
      : [];
  const productMappings: Array<Record<string, unknown> | null> =
    flow.flowType === "order_capture" ? salesProductMappings : confirmationProductMappings;
  const websiteIds = productMappings.length
    ? uniqueCheckoutWebsiteIds(
        Object.fromEntries(
          productMappings.map((mapping) => [
            (mapping as { product_ref: string }).product_ref,
            (mapping as { website_id: string }).website_id,
          ])
        )
      )
    : flow.checkoutWebsiteIds;
  return {
    id: flow.id,
    flow_key: flow.id,
    flow_type: flow.flowType,
    name: flow.title,
    enabled: flow.enabled,
    mode: flow.flowType === "order_capture" ? flow.mode : null,
    template_name: flow.templateName || null,
    template_language: flow.templateLanguage || "es",
    instructions: flow.instructions || null,
    required_fields: normalizeOrderRequiredFields(flow.requiredFields, flow.flowType),
    checkout_config: {
      website_ids: websiteIds,
      product_mappings: productMappings,
      ...(flow.flowType === "confirmation"
        ? { confirmation_photos_enabled: flow.confirmationPhotosEnabled === true }
        : {}),
    },
    logistics_policy: normalizeCommerceLogisticsPolicy({
      ...flow.logisticsPolicy,
      productScope: flow.productScope,
    }),
    products:
      flow.productScope === "all"
        ? []
        : flow.productIds.map((productId) => {
            const label = flow.productLabels?.[productId] || "";
            return {
              product_source: "fluxi_product",
              product_ref: productId,
              ...(label ? { label_snapshot: label } : {}),
            };
          }),
  };
};

const flowHasCheckoutWebsiteWithPublicUrl = (
  flow: CommerceFlowDraft,
  checkoutWebsites: CheckoutWebsiteOption[] = []
) => {
  if (!checkoutWebsites.length) return true;
  if (flow.productScope === "all" || !flow.productIds.length) return false;
  const byProduct = flow.checkoutWebsiteByProductId || {};
  return flow.productIds.every((productId) => {
    const websiteId = String(byProduct[productId] || "").trim();
    if (!websiteId) return false;
    const website = checkoutWebsites.find((item) => String(item.websiteId).trim() === websiteId);
    return checkoutWebsiteHasPublicUrl(website) && checkoutWebsiteContainsProduct(website, productId);
  });
};

const isCommerceFlowDraftCommitted = (flow: CommerceFlowDraft, checkoutWebsites: CheckoutWebsiteOption[] = []) => {
  if (!flow.title.trim()) return false;
  if (flow.productScope !== "all" && !flow.productIds.length) return false;
  if (flow.flowType === "confirmation" && !flow.templateName.trim()) return false;
  if (flow.flowType === "order_capture") {
    if (!flow.mode) return false;
    if (flow.productScope === "all") return false;
    if (!flow.productIds.length) return false;
    if (
      flow.productIds.some((productId) => !String(flow.checkoutWebsiteByProductId?.[productId] || "").trim())
    ) return false;
    if (!flowHasCheckoutWebsiteWithPublicUrl(flow, checkoutWebsites)) return false;
    if (flow.mode === "chat_capture_cod" && !flow.requiredFields.length) return false;
  }
  return true;
};

const commerceFlowActivationIssues = (
  flow: CommerceFlowDraft,
  checkoutWebsites: CheckoutWebsiteOption[] = []
) => {
  const label = flow.flowType === "confirmation" ? "confirmación" : "toma de orden";
  const issues: string[] = [];
  if (!flow.title.trim()) issues.push(`Agrega un nombre para activar esta ${label}.`);
  if (flow.flowType === "confirmation") {
    if (flow.productScope !== "all" && !flow.productIds.length) {
      issues.push("Elige productos o selecciona Todos.");
    }
    if (!flow.templateName.trim()) issues.push("Elige la plantilla de confirmación.");
    return issues;
  }
  if (!flow.mode) issues.push("Elige el modo de cierre de venta.");
  if (flow.productScope === "all" || !flow.productIds.length) {
    issues.push("Elige productos específicos para asignarles embudo.");
  }
  const byProduct = flow.checkoutWebsiteByProductId || {};
  const missingFunnel = flow.productIds.some((productId) => !String(byProduct[productId] || "").trim());
  if (missingFunnel) issues.push("Asigna un embudo fuente a cada producto.");
  if (flow.productIds.length && !flowHasCheckoutWebsiteWithPublicUrl(flow, checkoutWebsites)) {
    issues.push("Cada producto debe tener un embudo público vinculado.");
  }
  if (flow.mode === "chat_capture_cod" && !flow.requiredFields.length) {
    issues.push("Agrega al menos un campo requerido para capturar la orden en el chat.");
  }
  return issues;
};

const cartRecoveryActivationIssues = (flow: CartRecoveryFlowDraft) => {
  const issues: string[] = [];
  if (!flow.title.trim()) issues.push("Agrega un nombre para activar esta recuperación.");
  if (flow.productScope !== "all" && !flow.productIds.length) issues.push("Elige productos o selecciona Todos.");
  if (!flow.templateName.trim()) issues.push("Elige la plantilla de recuperación.");
  if (!Number.isFinite(flow.delayValue) || flow.delayValue <= 0) {
    issues.push("Define cuánto tiempo esperar antes de enviar.");
  }
  return issues;
};

const automationActivationIssues = (automation: AutomationDraft) => {
  const issues: string[] = [];
  if (!automation.title.trim()) issues.push("Agrega un título para activar esta automatización.");
  if (!automation.trigger) issues.push("Elige qué evento la activa.");
  if (automation.trigger === "state_changed" && !automation.triggerStateTarget) {
    issues.push("Elige el estado que activa la regla.");
  }
  if (automation.trigger === "logistics_event" && !automation.logisticsStatusTarget) {
    issues.push("Elige el estado logístico que activa la regla.");
  }
  if (automationRequiresProducts(automation) && automation.productScope !== "all" && !(automation.productIds || []).length) {
    issues.push("Elige productos o selecciona Todos.");
  }
  if (!automation.action) issues.push("Elige qué pasa.");
  if (automation.action === "send_template" && !automation.templateName.trim()) {
    issues.push("Elige la plantilla que se envía.");
  }
  if (automation.action === "change_state" && !automation.stateTarget) {
    issues.push("Elige el estado destino.");
  }
  return issues;
};

const committedCommerceFlowDrafts = (drafts: CommerceFlowDraft[]) =>
  normalizeCommerceFlowDrafts(drafts);

const pruneEphemeralCommerceFlows = (drafts: CommerceFlowDraft[]) =>
  normalizeCommerceFlowDrafts(drafts);

// Duplicados por tipo (Julian): un producto no puede estar en dos acciones del MISMO tipo.
// Mapa productId → nombre de la otra acción habilitada que ya lo usa, para mostrar la opción
// gray-out con "Ya está en '<nombre>'" en vez de ocultarla.
const collectActiveCommerceProductUsage = (
  drafts: CommerceFlowDraft[],
  flowType: CommerceFlowDraft["flowType"],
  excludeFlowId?: string
): Record<string, string> => {
  const usage: Record<string, string> = {};
  committedCommerceFlowDrafts(drafts).forEach((flow) => {
    if (!flow.enabled || flow.id === excludeFlowId || flow.flowType !== flowType) return;
    if (flow.productScope === "all") return;
    const label =
      flow.title.trim() || (flowType === "confirmation" ? "otra confirmación" : "otra toma de orden");
    flow.productIds.forEach((productId) => {
      if (!usage[productId]) usage[productId] = label;
    });
  });
  return usage;
};

const collectActiveRecoveryProductUsage = (
  drafts: CartRecoveryFlowDraft[],
  excludeFlowId?: string
): Record<string, string> => {
  const usage: Record<string, string> = {};
  drafts.forEach((flow) => {
    if (!flow.enabled || flow.id === excludeFlowId) return;
    if (flow.productScope === "all") return;
    const label = flow.title.trim() || "otra recuperación";
    (flow.productIds || []).forEach((productId) => {
      if (!usage[productId]) usage[productId] = label;
    });
  });
  return usage;
};

const validateCommerceFlowDrafts = (
  drafts: CommerceFlowDraft[],
  flowType: CommerceFlowDraft["flowType"],
  checkoutWebsites: CheckoutWebsiteOption[] = []
) => {
  const errors: string[] = [];
  const activeProducts = new Map<string, string>();
  committedCommerceFlowDrafts(drafts)
    .filter((flow) => flow.flowType === flowType)
    .forEach((flow, index) => {
      const label = flow.title.trim() || (flowType === "confirmation" ? `Confirmación ${index + 1}` : `Toma de orden ${index + 1}`);
      if (!flow.enabled) return;
      if (!flow.title.trim()) errors.push(`${label}: agrega un nombre.`);
      if (flow.productScope !== "all" && !flow.productIds.length) {
        errors.push(
          flow.flowType === "order_capture"
            ? `${label}: elige los productos que la IA puede vender.`
            : `${label}: elige productos o selecciona Todos.`
        );
      }
      if (flow.flowType === "confirmation" && !flow.templateName.trim()) {
        errors.push(`${label}: elige la plantilla de confirmación.`);
      }
      if (flow.flowType === "order_capture") {
        if (!flow.mode) errors.push(`${label}: elige cómo se cierra la venta.`);
        if (flow.productScope === "all") {
          errors.push(`${label}: la toma de orden necesita productos específicos y un embudo fuente por producto.`);
        }
        flow.productIds.forEach((productId) => {
          const websiteId = String(flow.checkoutWebsiteByProductId?.[productId] || "").trim();
          const productLabel = flow.productLabels?.[productId] || productId;
          if (!websiteId) {
            errors.push(`${label}: elige un embudo fuente para ${productLabel}.`);
            return;
          }
          const website = checkoutWebsites.find((item) => String(item.websiteId).trim() === websiteId);
          if (!checkoutWebsites.length) return;
          if (!checkoutWebsiteHasPublicUrl(website)) {
            errors.push(`${label}: el embudo de ${productLabel} necesita URL pública.`);
            return;
          }
          if (!checkoutWebsiteContainsProduct(website, productId)) {
            errors.push(`${label}: el embudo de ${productLabel} debe estar vinculado a ese producto.`);
          }
        });
        if (flow.mode === "chat_capture_cod" && !flow.requiredFields.length) {
          errors.push(`${label}: define al menos un campo requerido.`);
        }
      }
      if (flow.productScope === "all") return;
      flow.productIds.forEach((productId) => {
        const previousFlow = activeProducts.get(productId);
        if (previousFlow) {
          errors.push(`${label}: el producto ${productId} ya está en "${previousFlow}".`);
        } else {
          activeProducts.set(productId, flow.title.trim() || label);
        }
      });
    });
  return errors;
};

const committedMediaResourceDrafts = (rows: MediaResourceDraft[], savedRows: MediaResourceDraft[]) => {
  const savedIds = new Set(savedRows.map((row) => row.id));
  return rows.filter(
    (row) => savedIds.has(row.id) || Boolean(row.summary.trim()) || row.files.length > 0 || row.instructions.trim()
  );
};

const pruneEphemeralMediaResourceDrafts = (rows: MediaResourceDraft[], savedRows: MediaResourceDraft[]) => {
  const savedIds = new Set(savedRows.map((row) => row.id));
  return rows.filter(
    (row) =>
      savedIds.has(row.id) ||
      Boolean(row.summary.trim()) ||
      row.files.length > 0 ||
      Boolean(row.instructions.trim())
  );
};

const normalizeMediaResourceDrafts = (value: unknown): MediaResourceDraft[] => {
  if (!Array.isArray(value)) return defaultMediaResourceDrafts;
  const rows = value
    .map((item, index): MediaResourceDraft | null => {
      if (!isRecord(item)) return null;
      const kind = typeof item.kind === "string" ? item.kind : "mixed";
      const normalizedKind = ["image", "gif", "video", "document", "audio", "sticker", "mixed"].includes(kind)
        ? (kind as MediaResourceDraft["kind"])
        : "mixed";
      const files = Array.isArray(item.files)
        ? item.files
            .map((file, fileIndex): MediaResourceDraft["files"][number] | null => {
              if (!isRecord(file)) return null;
              return {
                id: typeof file.id === "string" && file.id ? file.id : `media-file-${index}-${fileIndex}`,
                name: typeof file.name === "string" ? file.name : "archivo",
                type: typeof file.type === "string" ? file.type : "FILE",
                size: typeof file.size === "number" ? file.size : 0,
                url: typeof file.url === "string" ? file.url : undefined,
                mime_type: typeof file.mime_type === "string" ? file.mime_type : undefined,
                media_type:
                  typeof file.media_type === "string" && ["image", "video", "document", "audio", "sticker"].includes(file.media_type)
                    ? (file.media_type as MediaResourceDraft["files"][number]["media_type"])
                    : undefined,
                whatsapp_sendable: typeof file.whatsapp_sendable === "boolean" ? file.whatsapp_sendable : undefined,
                whatsapp_blocked_reason:
                  typeof file.whatsapp_blocked_reason === "string" ? file.whatsapp_blocked_reason : null,
              };
            })
            .filter((file): file is MediaResourceDraft["files"][number] => Boolean(file))
        : [];
      const summary = typeof item.summary === "string" ? item.summary.trim() : "";
      const hasSendableFile = files.some((file) => mediaFileWhatsAppStatus(normalizedKind, file).sendable);
      return {
        id:
          typeof item.id === "string" && item.id
            ? item.id
            : typeof item.resource_key === "string" && item.resource_key
              ? item.resource_key
              : `media-resource-${index}`,
        title: typeof item.title === "string" && item.title.trim() ? item.title : "Recurso multimedia",
        kind: normalizedKind,
        summary,
        instructions: typeof item.instructions === "string" ? item.instructions : "",
        files,
        enabled: (typeof item.enabled === "boolean" ? item.enabled : true) && Boolean(summary) && hasSendableFile,
      } satisfies MediaResourceDraft;
    })
    .filter((item): item is MediaResourceDraft => Boolean(item));
  return rows.length ? rows : defaultMediaResourceDrafts;
};

const validateMediaResourceDrafts = (resources: MediaResourceDraft[]) => {
  const errors: string[] = [];
  resources.forEach((resource, index) => {
    const label = resource.title.trim() || `Recurso multimedia ${index + 1}`;
    if (!resource.title.trim()) {
      errors.push(`Recurso multimedia ${index + 1}: agrega un nombre.`);
    }
    if (!resource.summary.trim()) {
      errors.push(`${label}: agrega el resumen obligatorio.`);
    }
    if (resource.enabled && !resource.files.length) {
      errors.push(`${label}: sube al menos un archivo antes de activarlo.`);
    }
    if (resource.enabled && resource.files.length) {
      const hasSendableFile = resource.files.some((file) => mediaFileWhatsAppStatus(resource.kind, file).sendable);
      if (!hasSendableFile) {
        const firstReason = mediaFileWhatsAppStatus(resource.kind, resource.files[0]).blockedReason;
        errors.push(`${label}: no tiene archivos enviables por WhatsApp (${mediaBlockedReasonLabel(firstReason)}).`);
      }
    }
  });
  return errors;
};

const normalizeIntegrationToolDrafts = (tools: IntegrationTool[]) =>
  tools
    .map((tool) => ({
      tool_name: tool.tool_name,
      enabled: Boolean(tool.enabled),
      policy: tool.policy || null,
      metadata_json: isRecord(tool.metadata_json) ? tool.metadata_json : {},
    }))
    .sort((a, b) => a.tool_name.localeCompare(b.tool_name));

const normalizeIntegrationToolDraftMap = (toolsByConnectionId: Record<number, IntegrationTool[]>) =>
  Object.entries(toolsByConnectionId)
    .map(([connectionId, tools]) => ({
      connection_id: Number(connectionId),
      tools: normalizeIntegrationToolDrafts(tools),
    }))
    .sort((a, b) => a.connection_id - b.connection_id);

const mediaResourceDraftsFromSettings = (settings: SupportSettings): MediaResourceDraft[] =>
  normalizeMediaResourceDrafts(settings.metadata_json?.media_resources);

const mediaResourceDraftsFromApi = (
  resources: unknown,
  fallbackSettings?: SupportSettings | null
): MediaResourceDraft[] => {
  if (resources !== null && resources !== undefined) return normalizeMediaResourceDrafts(resources);
  return fallbackSettings ? mediaResourceDraftsFromSettings(fallbackSettings) : defaultMediaResourceDrafts;
};

const defaultTemplateSlots: {
  key:
    | "confirmation_template_name"
    | "reminder_template_name"
    | "fallback_template_name"
    | "cart_recovery_template_name"
    | "order_confirmed_template_name";
  label: string;
  description: string;
}[] = [
  {
    key: "confirmation_template_name",
    label: "Confirmación",
    description: "Primer mensaje proactivo para confirmar una orden cuando todavía no hay ventana abierta.",
  },
  {
    key: "reminder_template_name",
    label: "Recordatorio",
    description: "Seguimiento corto si el cliente no responde a la confirmación inicial.",
  },
  {
    key: "fallback_template_name",
    label: "Fallback",
    description: "Mensaje seguro cuando no hay una plantilla específica para el caso.",
  },
  {
    key: "cart_recovery_template_name",
    label: "Carrito",
    description: "Recuperación de carrito o pago pendiente, según el flujo conectado.",
  },
  {
    key: "order_confirmed_template_name",
    label: "Orden confirmada",
    description: "Aviso final cuando la orden ya quedó confirmada o validada.",
  },
];

const formatWhatsAppStatus = (status?: string | null) => {
  const normalized = String(status || "not_connected");
  const labels: Record<string, string> = {
    connected: "Conectado",
    not_connected: "Sin conectar",
    inactive: "Pausado",
    disabled: "Deshabilitado",
    error: "Con error",
    credential_error: "Error de credenciales",
    invalid_credentials: "Credenciales inválidas",
  };
  return labels[normalized] || normalized.replace(/_/g, " ");
};

const formatWhatsAppQuality = (quality?: string | null) => {
  const normalized = String(quality || "").toLowerCase();
  const labels: Record<string, string> = {
    green: "Alta",
    high: "Alta",
    yellow: "Media",
    medium: "Media",
    red: "Baja",
    low: "Baja",
    na: "Sin determinar",
    none: "Sin determinar",
    unknown: "Sin determinar",
  };
  return labels[normalized] || (quality ? humanize(quality) : "Sin dato");
};

const isWhatsAppQualityHealthy = (quality?: string | null) => {
  const normalized = String(quality || "").toLowerCase();
  return ["green", "high", "yellow", "medium"].includes(normalized);
};

const formatWhatsAppTokenStatus = (status?: string | null, configured?: boolean) => {
  if (!configured) return "Pendiente";
  const normalized = String(status || "").toLowerCase();
  const labels: Record<string, string> = {
    active: "Activo",
    valid: "Activo",
    configured: "Configurado",
    ok: "Activo",
    expired: "Expirado",
    invalid: "Inválido",
    unknown: "Sin verificar",
  };
  return labels[normalized] || (status ? humanize(status) : "Configurado");
};

const isWhatsAppTokenHealthy = (status?: string | null, configured?: boolean) => {
  if (!configured) return false;
  const normalized = String(status || "").toLowerCase();
  return !["expired", "invalid", "unknown"].includes(normalized);
};

const whatsappCredentialStatus = (account?: WhatsAppConnectionStatus | null) => {
  if (!account?.exists || !account.access_token_configured || !account.app_secret_configured) {
    return { ready: false, value: "Pendiente" };
  }
  if (account.credentials_readable === false || account.credential_error) {
    return { ready: false, value: "No legibles" };
  }
  if (account.credentials_encrypted) {
    return { ready: true, value: "Cifradas" };
  }
  return { ready: false, value: "Sin cifrar" };
};

const formatWhatsAppMessagingLimit = (limit?: string | null) => {
  if (!limit) return "Sin dato";
  return String(limit)
    .replace(/^WHATS_APP_MESSAGE_LIMIT_TIER_/i, "")
    .replace(/^TIER_/i, "")
    .replace(/_/g, " ");
};

const whatsappApiStatus = (
  account: WhatsAppConnectionStatus | null,
  health: OperationsHealth | null
): { label: string; tone: "success" | "warning" | "danger"; icon: typeof Inbox } => {
  if (
    !account?.exists ||
    !account.is_active ||
    !account.phone_number_id ||
    !account.waba_id ||
    !account.access_token_configured ||
    !account.app_secret_configured
  ) {
    return { label: "API Desconectada", tone: "danger", icon: AlertTriangle };
  }

  const credentials = whatsappCredentialStatus(account);
  const tokenStatus = String(account.health?.token_status || "").toLowerCase();
  const quality = String(account.health?.quality_rating || "").toLowerCase();
  const operationalErrors = Number(health?.events.errors || 0);
  const hasAlert =
    !credentials.ready ||
    ["expired", "invalid", "unknown"].includes(tokenStatus) ||
    ["red", "yellow"].includes(quality) ||
    operationalErrors > 0;

  if (hasAlert) {
    return { label: "API Con Alertas", tone: "warning", icon: AlertTriangle };
  }

  return { label: "API Conectada", tone: "success", icon: CheckCircle2 };
};

const formatConversationStatus = (status?: string | null) => {
  const normalized = String(status || "");
  const labels: Record<string, string> = {
    open: "Abierta",
    resolved: "Resuelta",
  };
  return labels[normalized] || normalized.replace(/_/g, " ");
};

const primaryConversationStatus = (status?: string | null): ConversationStatus =>
  status === "resolved" ? "resolved" : "open";

const formatConversationBucket = (bucket?: string | null) => {
  const normalized = String(bucket || "open");
  const labels: Record<string, string> = {
    open: "Abiertas",
    resolved: "Resueltas",
  };
  return labels[normalized] || humanize(bucket);
};

const formatConversationPriority = (priority?: string | null) => {
  const normalized = normalizeConversationPriority(priority);
  const labels: Record<string, string> = {
    normal: "Normal",
    medium: "Media",
    critical: "Crítica",
  };
  return labels[normalized] || normalized.replace(/_/g, " ");
};

const normalizeConversationPriority = (priority?: string | null): ConversationPriority => {
  const normalized = String(priority || "normal").trim().toLowerCase();
  if (normalized === "critical" || normalized === "critica" || normalized === "crítica") return "critical";
  if (normalized === "medium" || normalized === "media" || normalized === "high" || normalized === "alta") return "medium";
  return "normal";
};

const priorityAvatarTextClass = (priority?: string | null) => {
  const normalized = normalizeConversationPriority(priority);
  if (normalized === "critical") return "text-red-500";
  if (normalized === "medium") return "text-amber-500";
  return "text-gray-500";
};

const PriorityAvatarMark = ({ priority }: { priority?: string | null }) => (
  <span className="inline-flex size-6 shrink-0 items-center justify-center rounded-full bg-gray-50 ring-1 ring-gray-200">
    <UserRound className={`size-3.5 ${priorityAvatarTextClass(priority)}`} />
  </span>
);

const formatSenderType = (sender?: string | null) => {
  const normalized = String(sender || "");
  const labels: Record<string, string> = {
    customer: "Cliente",
    ai: "IA",
    bot: "IA",
    human: "Humano",
    agent: "Humano",
    owner: "Humano",
    system: "Sistema",
  };
  return labels[normalized] || normalized.replace(/_/g, " ");
};

// Las notas internas que deja la IA (p. ej. al crear una sugerencia con dejar_sugerencia_negocio)
// viajan con sender_name="IA" sobre sender_type "agent"; el pie debe decir IA, no Humano.
const noteSenderLabel = (note: { sender_type?: string | null; sender_name?: string | null }) =>
  note.sender_name === "IA" ? "IA" : formatSenderType(note.sender_type);

const isHumanSender = (sender?: string | null) => {
  const normalized = String(sender || "");
  return normalized === "human" || normalized === "agent" || normalized === "owner";
};

const formatDeliveryStatus = (status?: string | null) => {
  const normalized = String(status || "");
  const labels: Record<string, string> = {
    accepted: "Aceptado",
    sent: "Enviado",
    delivered: "Entregado",
    read: "Leído",
    failed: "Falló",
    recorded: "Registrado",
    pending: "Pendiente",
    sending: "Enviando",
    unknown: "Por confirmar",
  };
  return labels[normalized] || normalized.replace(/_/g, " ");
};

const getChannelLabel = (channel?: string | null) => {
  const normalized = String(channel || "whatsapp").toLowerCase();
  if (normalized === "instagram") return "Instagram";
  if (normalized === "messenger") return "Messenger";
  return "WhatsApp";
};

const getDeliveryStatusTitle = (status?: string | null, channel?: string | null) => {
  const normalized = String(status || "").toLowerCase();
  const channelLabel = getChannelLabel(channel);
  if (normalized === "read") return "Leído por el contacto";
  if (normalized === "delivered") return `Entregado por ${channelLabel}`;
  if (normalized === "sent" || normalized === "accepted") return `Enviado por ${channelLabel}`;
  if (normalized === "recorded") return "Registrado en Fluxi";
  if (normalized === "pending") return "Pendiente de confirmación";
  if (normalized === "sending") return "Envío en proceso";
  if (normalized === "unknown") return "Envío en verificación; no lo envíes de nuevo";
  return formatDeliveryStatus(status);
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const classifyMetaDeliveryError = (code?: string | null, message?: string | null) => {
  const normalizedCode = String(code || "").trim();
  const text = String(message || "").toLowerCase();
  const labels: Record<string, string> = {
    "130472": "Experimento Meta",
    "131000": "Error Meta",
    "131026": "No entregable",
    "131031": "Cuenta bloqueada",
    "131042": "Falta pagar en Meta",
    "131047": "Fuera de 24h",
    "131049": "Límite Meta",
    "131050": "Opt-out marketing",
    "131051": "Tipo no soportado",
    "131052": "Media no descargó",
    "131053": "Media no subió",
    "132000": "Plantilla inválida",
    "132001": "Plantilla no existe",
    "132015": "Plantilla pausada",
    "132016": "Plantilla bloqueada",
  };
  if (labels[normalizedCode]) return labels[normalizedCode];
  if (text.includes("payment") || text.includes("billing") || text.includes("presupuesto")) return "Falta pagar en Meta";
  if (text.includes("session_window_expired") || text.includes("outside 24") || text.includes("fuera de 24")) return "Fuera de 24h";
  if (text.includes("24 hour") || text.includes("24 horas") || text.includes("re-engagement")) return "Fuera de 24h";
  if (text.includes("opted_out") || text.includes("opt-out")) return "Opt-out";
  if (text.includes("contact_phone_missing") || text.includes("phone_missing")) return "Sin teléfono";
  if (text.includes("credential") || text.includes("token")) return "Credenciales";
  if (text.includes("network error") || text.includes("timeout")) return "Sin conexión";
  if (text.includes("forbidden") || text.includes("unauthorized") || text.includes("permission")) return "Sin permisos";
  if (text.includes("not found")) return "No encontrado";
  if (text.includes("manual_send_only_supports_whatsapp")) return "Solo WhatsApp";
  if (text.includes("policy") || text.includes("política") || text.includes("commerce")) return "Política Meta";
  if (text.includes("undeliverable") || text.includes("incapable")) return "No entregable";
  if (text.includes("locked") || text.includes("restricted")) return "Cuenta restringida";
  return normalizedCode ? "Error Meta" : "Error de envío";
};

const stringFromUnknown = (value: unknown) =>
  typeof value === "string" || typeof value === "number" ? String(value).trim() : "";

const formatOperationalErrorSummary = (error?: string | null) => {
  const raw = String(error || "").trim();
  if (!raw) return "";
  const lower = raw.toLowerCase();
  const metaCode = raw.match(/\b(13\d{4})\b/)?.[1] || "";
  if (metaCode) return classifyMetaDeliveryError(metaCode, raw);
  if (lower.includes("403") || lower.includes("forbidden")) return "Meta sin permisos — revisa Integraciones";
  if (lower.includes("400") || lower.includes("bad request")) return "Meta rechazó el envío";
  if (lower.includes("graph.facebook.com")) return classifyMetaDeliveryError(null, raw);
  return classifyMetaDeliveryError(null, raw);
};

const getMessageClientMessageId = (message?: SupportMessage | null) => {
  const metadata = isRecord(message?.metadata_json) ? message?.metadata_json : {};
  return stringFromUnknown(metadata.client_message_id);
};

const getMessageStableKey = (message: SupportMessage) => getMessageClientMessageId(message) || String(message.id);

const getMessageUiOrder = (message: SupportMessage) => {
  const metadata = isRecord(message.metadata_json) ? message.metadata_json : {};
  const uiOrder = Number(metadata.ui_order);
  return Number.isFinite(uiOrder) && uiOrder > 0 ? uiOrder : timestampMs(message.created_at);
};

const getMessageDeliveryFailure = (message: SupportMessage, log?: ChatbotDeliveryLog | null) => {
  if (message.delivery_status !== "failed" && log?.status !== "failed") return null;

  const metadata = isRecord(message.metadata_json) ? message.metadata_json : {};
  const deliveryError = isRecord(metadata.delivery_error) ? metadata.delivery_error : {};
  const errors = Array.isArray(metadata.errors) && isRecord(metadata.errors[0]) ? metadata.errors[0] : {};
  const code =
    stringFromUnknown(log?.meta_error_code) ||
    stringFromUnknown(metadata.meta_error_code) ||
    stringFromUnknown(deliveryError.meta_error_code) ||
    stringFromUnknown(deliveryError.code) ||
    stringFromUnknown(errors.code);
  const detail =
    stringFromUnknown(log?.meta_error_message_sanitized) ||
    stringFromUnknown(metadata.meta_error_message) ||
    stringFromUnknown(deliveryError.meta_error_message) ||
    stringFromUnknown(deliveryError.error) ||
    stringFromUnknown(errors.message) ||
    stringFromUnknown(errors.title);
  const label = classifyMetaDeliveryError(code, detail);
  const visibleCode = code && code !== "local_send_failed" ? `Meta ${code}` : "";
  const title = [label, visibleCode, detail].filter(Boolean).join(" · ");
  return {
    label,
    title: title || label,
  };
};

const getMessageAiContext = (message: SupportMessage) => {
  const metadata = isRecord(message.metadata_json) ? message.metadata_json : {};
  const ai = isRecord(metadata.ai) ? metadata.ai : {};
  const context = isRecord(ai.context) ? ai.context : null;
  return context;
};

const getMessageMemorySummaryUsed = (message: SupportMessage) => {
  const context = getMessageAiContext(message);
  if (!context) return "";
  const value = stringFromUnknown(context.memory_summary_used);
  return value.trim();
};

type ConversationAiRuntime = {
  status?: string;
  updated_at?: string;
  expires_at?: string;
  started_at?: string;
  sent_at?: string;
  failed_at?: string;
  inbound_wa_message_id?: string;
  trace_id?: string;
  error_class?: string;
  error?: string;
  retryable?: boolean;
};

type ActiveAiFailure = {
  title: string;
  detail: string;
  shortReason: string;
  created_at: string | null;
  trace_id: string | null;
  retryable?: boolean | null;
  recommended_action?: string | null;
};

type ActionNotice = {
  title: string;
  detail: string;
  tone: "warning" | "danger" | "neutral";
  created_at: string | null;
};

type ChatTimelineItem =
  | { type: "date_separator"; key: string; createdAt: string | null; label: string }
  | { type: "message"; key: string; createdAt: string | null; message: SupportMessage }
  | { type: "ai_failure"; key: string; createdAt: string | null; failure: ActiveAiFailure }
  | { type: "action_notice"; key: string; createdAt: string | null; notice: ActionNotice }
  | { type: "reply_approval"; key: string; createdAt: string | null; approval: ReplyApproval }
  | { type: "reply_approval_expired"; key: string; createdAt: string | null; approval: ReplyApproval }
  | { type: "reply_approval_rejected"; key: string; createdAt: string | null; approval: ReplyApproval };

const getTimelineItemUiOrder = (item: ChatTimelineItem) =>
  item.type === "message" ? getMessageUiOrder(item.message) : timestampMs(item.createdAt);

const getConversationAiRuntime = (conversation?: SupportConversation | null): ConversationAiRuntime | null => {
  const metadata = isRecord(conversation?.metadata_json) ? conversation?.metadata_json : null;
  const runtime = isRecord(metadata?.ai_runtime) ? metadata?.ai_runtime : null;
  return runtime ? (runtime as ConversationAiRuntime) : null;
};

const shortAiFailureReason = (value?: string | null) => {
  const text = String(value || "").toLowerCase();
  if (text.includes("timeout") || text.includes("timed out")) return "Timeout";
  if (text.includes("billing") || text.includes("delinquent") || text.includes("credits")) return "Créditos";
  if (text.includes("api key") || text.includes("credential")) return "Credenciales";
  if (text.includes("quota") || text.includes("rate limit") || text.includes("429")) return "Límite IA";
  if (text.includes("safety") || text.includes("blocked")) return "Bloqueado";
  if (text.includes("network") || text.includes("connection")) return "Conexión";
  return "Falló IA";
};

const getActiveAiFailure = (
  conversation: SupportConversation | null,
  events: ChatbotEvent[],
): ActiveAiFailure | null => {
  if (!conversation || !hasPendingCustomerReply(conversation)) return null;
  const runtime = getConversationAiRuntime(conversation);
  if (runtime?.status !== "failed") return null;
  const matchingEvent = events.find((event) => (
    event.event_type === "support.ai.response_failed" &&
    (!runtime.inbound_wa_message_id || event.wa_message_id === runtime.inbound_wa_message_id)
  ));
  const eventDetails = isRecord(matchingEvent?.details) ? matchingEvent?.details : {};
  const envelope = isRecord(eventDetails.error_envelope) ? eventDetails.error_envelope : {};
  const detail =
    stringFromUnknown(envelope.message) ||
    runtime.error ||
    stringFromUnknown(eventDetails.error) ||
    "El request de IA falló antes de enviar respuesta.";
  return {
    title: "La IA no pudo responder",
    detail,
    shortReason: stringFromUnknown(envelope.short_label) || shortAiFailureReason(`${runtime.error_class || ""} ${detail}`),
    created_at: runtime.failed_at || matchingEvent?.created_at || runtime.updated_at || null,
    trace_id: runtime.trace_id || matchingEvent?.trace_id || null,
    retryable: typeof envelope.retryable === "boolean" ? envelope.retryable : runtime.retryable,
    recommended_action: stringFromUnknown(envelope.recommended_action),
  };
};

const getDeliveryFailureTimelineItem = (log: ChatbotDeliveryLog): ActiveAiFailure | null => {
  if (log.status !== "failed" || log.direction !== "outbound" || log.message_id !== null) return null;

  const detail =
    log.owner_message ||
    log.meta_error_message_sanitized ||
    log.technical_message_sanitized ||
    "No pudimos completar el envío. Revisa la configuración y vuelve a intentar.";
  const shortReason =
    log.short_label ||
    classifyMetaDeliveryError(log.meta_error_code, detail) ||
    shortAiFailureReason(`${log.error_family || ""} ${detail}`);
  const whatsappFailure = log.error_family
    ? log.error_family === "whatsapp_delivery"
    : log.channel === "whatsapp";
  return {
    title: whatsappFailure ? "No pudimos enviar por WhatsApp" : "La IA no pudo responder",
    detail,
    shortReason,
    created_at: log.created_at || log.updated_at || null,
    trace_id: log.trace_id || null,
    retryable: log.retryable,
    recommended_action: log.recommended_action,
  };
};

const getActionNotice = (event: ChatbotEvent): ActionNotice | null => {
  const details = isRecord(event.details) ? event.details : {};
  if (event.event_type === "support.escalation.created") {
    const reason = stringFromUnknown(details.reason) || "La IA pasó esta conversación a atención humana.";
    return {
      title: "Atención humana",
      detail: reason,
      tone: "warning",
      created_at: event.created_at,
    };
  }
  if (event.event_type === "support.conversation.priority_changed_by_ai") {
    const priority = stringFromUnknown(details.priority) || stringFromUnknown(details.new_priority);
    const priorityLabel =
      priority === "critical"
        ? "crítica"
        : priority === "medium"
          ? "media"
          : priority === "normal"
            ? "normal"
            : priority;
    return {
      title: priorityLabel ? `Prioridad ${priorityLabel}` : "Prioridad actualizada",
      detail: stringFromUnknown(details.reason) || "La IA ajustó la prioridad de la conversación.",
      tone: priority === "critical" ? "danger" : "warning",
      created_at: event.created_at,
    };
  }
  if (event.event_type === "support.conversation.ai_paused_by_ai") {
    return {
      title: "IA pausada",
      detail: stringFromUnknown(details.reason) || "La IA pausó respuestas automáticas para que un humano continúe.",
      tone: "neutral",
      created_at: event.created_at,
    };
  }
  return null;
};

const makeLocalFailedMessage = ({
  conversation,
  content,
  error,
  clientMessageId,
}: {
  conversation: SupportConversation;
  content: string;
  error: string;
  clientMessageId: string;
}): { message: SupportMessage; deliveryLog: ChatbotDeliveryLog } => {
  const nowMs = Date.now();
  const nowIso = new Date(nowMs).toISOString();
  const tempId = -nowMs;
  const errorLabel = classifyMetaDeliveryError(null, error);
  const metadataError = {
    code: "local_send_failed",
    title: errorLabel,
    message: error,
    error_data: { details: error },
  };
  const message: SupportMessage = {
    id: tempId,
    owner_id: conversation.owner_id,
    conversation_id: conversation.id,
    contact_id: conversation.contact_id,
    sender_type: "agent",
    sender_id: null,
    sender_name: "Equipo",
    direction: "outbound",
    channel: "whatsapp",
    content,
    content_type: "text",
    private: false,
    turn_id: null,
    wa_message_id: null,
    template_name: null,
    delivery_status: "failed",
    metadata_json: {
      local: true,
      client_message_id: clientMessageId,
      ui_order: nowMs,
      errors: [metadataError],
    },
    created_at: nowIso,
  };
  const deliveryLog: ChatbotDeliveryLog = {
    id: tempId,
    owner_id: conversation.owner_id,
    conversation_id: conversation.id,
    message_id: tempId,
    wa_message_id: null,
    direction: "outbound",
    channel: "whatsapp",
    message_type: "text",
    template_name: null,
    status: "failed",
    meta_error_code: "local_send_failed",
    meta_error_subcode: null,
    meta_error_message_sanitized: error,
    provider_payload_hash: null,
    created_at: nowIso,
    updated_at: nowIso,
  };
  return { message, deliveryLog };
};

const humanize = (value?: string | null) => {
  const normalized = String(value || "").trim();
  if (!normalized) return "Sin dato";
  return normalized
    .replace(/[._-]+/g, " ")
    .replace(/\s+/g, " ")
    .replace(/^\w/, (letter) => letter.toUpperCase());
};

const formatKnowledgeSourceDisplayName = (name?: string | null) => {
  const normalized = String(name || "").trim();
  if (!normalized) return "Fuente sin título";
  const looksTechnical = /^[a-z0-9_-]+$/i.test(normalized) && /[_-]/.test(normalized);
  return looksTechnical ? humanize(normalized) : normalized;
};

const knowledgeSourceTechnicalName = (name?: string | null) => {
  const normalized = String(name || "").trim();
  if (!normalized) return null;
  const display = formatKnowledgeSourceDisplayName(normalized);
  return display !== normalized ? normalized : null;
};

const formatRecommendedAction = (value?: string | null) => {
  switch (value) {
    case "reconnect_whatsapp":
      return "Reconectar WhatsApp";
    case "reconnect_instagram":
      return "Reconectar Instagram";
    case "reconnect_messenger":
      return "Reconectar Messenger";
    case "use_template":
      return "Usar plantilla";
    case "review_whatsapp_billing":
      return "Revisar pago";
    case "review_whatsapp_error":
      return "Revisar WhatsApp";
    case "review_instagram_error":
      return "Revisar Instagram";
    case "review_messenger_error":
      return "Revisar Messenger";
    case "review_request":
      return "Revisar datos";
    case "sync_templates":
      return "Sincronizar plantillas";
    case "retry":
      return "Reintentar";
    default:
      return humanize(value);
  }
};

const isRetryInstructionMessage = (message: SupportMessage) => message.content_type === "retry_instruction";
const INBOUND_MEDIA_BUBBLE_TYPES = new Set(["image", "video", "audio", "document"]);
const MEDIA_PLACEHOLDER_RE = /^\[(image|video|audio|document|sticker)\]$/i;

const isInboundMediaMessage = (message: SupportMessage) =>
  message.direction === "inbound" && INBOUND_MEDIA_BUBBLE_TYPES.has(String(message.content_type || "").toLowerCase());

const getInboundMediaVisual = (contentType: string) => {
  const normalized = String(contentType || "").toLowerCase();
  if (normalized === "image") return { icon: ImageIcon, label: "Imagen" };
  if (normalized === "video") return { icon: Video, label: "Video" };
  if (normalized === "audio") return { icon: Mic, label: "Audio" };
  return { icon: FileText, label: "Documento" };
};

const getInboundMediaExtractedText = (message: SupportMessage) => {
  const inboundMedia = message.metadata_json?.inbound_media;
  if (!inboundMedia || typeof inboundMedia !== "object") return "";
  const extracted = (inboundMedia as Record<string, unknown>).extracted_text;
  return typeof extracted === "string" && extracted.trim() ? extracted.trim() : "";
};

const getInboundMediaExtractionStatus = (message: SupportMessage) => {
  const inboundMedia = message.metadata_json?.inbound_media;
  if (!inboundMedia || typeof inboundMedia !== "object") return null;
  const status = (inboundMedia as Record<string, unknown>).extraction_status;
  return typeof status === "string" && status.trim() ? status.trim().toLowerCase() : null;
};

const INBOUND_MEDIA_EXTRACTION_SLA_MS = 320_000;

const getInboundMediaExtractionStartedAt = (message: SupportMessage) => {
  const inboundMedia = message.metadata_json?.inbound_media;
  if (!inboundMedia || typeof inboundMedia !== "object") return null;
  const startedAt = (inboundMedia as Record<string, unknown>).extraction_started_at;
  return typeof startedAt === "string" && startedAt.trim() ? startedAt.trim() : null;
};

const isInboundMediaExtractionWithinSla = (message: SupportMessage) => {
  const startedAt = getInboundMediaExtractionStartedAt(message);
  if (!startedAt) return true;
  const startedMs = timestampMs(startedAt);
  if (!startedMs) return true;
  return Date.now() - startedMs <= INBOUND_MEDIA_EXTRACTION_SLA_MS;
};

const getInboundMediaExtractionRawError = (message: SupportMessage) => {
  if (getInboundMediaExtractionStatus(message) !== "failed") return "";
  const inboundMedia = message.metadata_json?.inbound_media;
  if (!inboundMedia || typeof inboundMedia !== "object") return "";
  const error = (inboundMedia as Record<string, unknown>).extraction_error;
  return typeof error === "string" ? error.trim() : "";
};

const getInboundMediaExtractionError = (message: SupportMessage) => {
  if (getInboundMediaExtractionStatus(message) !== "failed") return "";
  const error = getInboundMediaExtractionRawError(message);
  if (error === "invalid_or_encrypted_pdf") {
    return "No pudimos leer el PDF. Puede estar dañado o protegido con contraseña; pide una copia válida sin contraseña.";
  }
  if (error.startsWith("media_size_limit_exceeded:")) {
    return "El archivo supera el límite de 20 MB. Pide una versión más liviana.";
  }
  if (error.startsWith("pdf_page_limit_exceeded:")) {
    return "El PDF supera el límite de 100 páginas. Pide que lo dividan en archivos más pequeños.";
  }
  return "No se pudo procesar el archivo. Puedes reintentar; si vuelve a fallar, pide una copia nueva.";
};

const isInboundMediaExtractionRetryable = (message: SupportMessage) => {
  const status = getInboundMediaExtractionStatus(message);
  if ((status === "pending" || status === "processing") && !isInboundMediaExtractionWithinSla(message)) return true;
  if (status !== "failed") return false;
  const error = getInboundMediaExtractionRawError(message);
  return !(
    error === "invalid_or_encrypted_pdf" ||
    error.startsWith("media_size_limit_exceeded:") ||
    error.startsWith("pdf_page_limit_exceeded:")
  );
};

const isInboundMediaExtractionPending = (message: SupportMessage) => {
  if (!getInboundMediaId(message)) return false;
  if (getInboundMediaExtractedText(message)) return false;
  const status = getInboundMediaExtractionStatus(message);
  if (status !== "pending" && status !== "processing") return false;
  return isInboundMediaExtractionWithinSla(message);
};

const getInboundMediaChipLabel = (contentType: string, hasExtractedText: boolean) => {
  const visual = getInboundMediaVisual(contentType);
  const normalized = String(contentType || "").toLowerCase();
  if (hasExtractedText && normalized === "audio") {
    return { ...visual, label: "Transcripción audio" };
  }
  if (hasExtractedText && normalized === "image") {
    return { ...visual, label: "Descripción imagen" };
  }
  if (hasExtractedText && normalized === "video") {
    return { ...visual, label: "Descripción video" };
  }
  return visual;
};

const isOutboundVoiceReplyMessage = (message: SupportMessage) =>
  message.direction === "outbound" && String(message.content_type || "").toLowerCase() === "audio";

const messageVoiceReplyFailed = (message: SupportMessage) => {
  const ai = message.metadata_json?.ai;
  if (!ai || typeof ai !== "object") return false;
  return Boolean((ai as Record<string, unknown>).voice_reply_failed);
};

const getInboundMediaCaption = (message: SupportMessage) => {
  const inboundMedia = message.metadata_json?.inbound_media;
  const captionValue =
    typeof inboundMedia === "object" && inboundMedia !== null
      ? (inboundMedia as Record<string, unknown>).caption
      : null;
  if (typeof captionValue === "string" && captionValue.trim()) return captionValue.trim();
  const extracted = getInboundMediaExtractedText(message);
  const content = String(message.content || "").trim();
  if (!content || MEDIA_PLACEHOLDER_RE.test(content)) return "";
  if (extracted && content === extracted) return "";
  return content;
};

const getInboundMediaId = (message: SupportMessage) => {
  const inboundMedia = message.metadata_json?.inbound_media;
  if (!inboundMedia || typeof inboundMedia !== "object") return null;
  const mediaId = (inboundMedia as Record<string, unknown>).media_id;
  return typeof mediaId === "string" && mediaId.trim() ? mediaId.trim() : null;
};

const InboundWhatsAppImagePreview = ({ mediaId }: { mediaId: string }) => {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let active = true;
    let objectUrl: string | null = null;
    setFailed(false);
    setPreviewUrl(null);
    void chatbotV2Service
      .fetchInboundWhatsAppMediaBlob(mediaId)
      .then((blob) => {
        if (!active) return;
        objectUrl = URL.createObjectURL(blob);
        setPreviewUrl(objectUrl);
      })
      .catch(() => {
        if (active) setFailed(true);
      });
    return () => {
      active = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [mediaId]);

  if (previewUrl) {
    return (
      <img
        src={previewUrl}
        alt="Imagen de WhatsApp"
        className="max-h-64 max-w-full rounded-xl border border-gray-200 object-contain"
      />
    );
  }
  if (failed) return null;
  return (
    <div className="flex items-center gap-2 rounded-xl border border-dashed border-gray-200 px-3 py-2 text-xs text-gray-500">
      <Loader2 className="size-3.5 animate-spin" />
      Cargando imagen
    </div>
  );
};

type InboundMediaUrlInfo = {
  url: string;
  mime_type?: string | null;
  media_type?: string | null;
  filename?: string | null;
  size_bytes?: number | null;
  expires_in_seconds?: number;
};

const ChatMediaContent = ({
  url,
  mediaType,
  filename,
}: {
  url: string;
  mediaType: string;
  filename?: string | null;
}) => {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  useEffect(() => {
    if (!lightboxOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setLightboxOpen(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [lightboxOpen]);
  const normalized = String(mediaType || "").toLowerCase();

  if (normalized === "image" || normalized === "sticker") {
    return (
      <>
        <button type="button" onClick={() => setLightboxOpen(true)} className="block cursor-zoom-in" title="Ver en grande">
          <img
            src={url}
            alt={filename || "Imagen"}
            className="max-h-64 max-w-full rounded-xl border border-gray-200 object-contain"
          />
        </button>
        {lightboxOpen
          ? createPortal(
              <div
                className="fixed inset-0 z-[120] flex items-center justify-center bg-black/85 p-6"
                onClick={() => setLightboxOpen(false)}
                role="dialog"
                aria-modal="true"
              >
                <img src={url} alt={filename || "Imagen"} className="max-h-full max-w-full rounded-lg object-contain" />
                <button
                  type="button"
                  onClick={() => setLightboxOpen(false)}
                  className="absolute right-4 top-4 inline-flex size-9 items-center justify-center rounded-full bg-white/10 text-white transition hover:bg-white/20"
                  aria-label="Cerrar"
                >
                  <X className="size-5" />
                </button>
              </div>,
              document.body
            )
          : null}
      </>
    );
  }
  if (normalized === "audio") {
    return <audio controls preload="metadata" src={url} className="h-10 w-72 max-w-full" />;
  }
  if (normalized === "video") {
    return (
      <video
        controls
        preload="metadata"
        src={url}
        className="max-h-64 max-w-full rounded-xl border border-gray-200 bg-black"
      />
    );
  }
  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      className="inline-flex max-w-full items-center gap-2 rounded-xl border border-gray-200 bg-white px-3 py-2 text-xs font-medium text-gray-700 transition hover:bg-gray-50"
      title="Abrir / descargar"
    >
      <FileText className="size-4 shrink-0 text-gray-500" />
      <span className="truncate">{filename || "Documento"}</span>
      <Download className="size-3.5 shrink-0 text-gray-400" />
    </a>
  );
};

const InboundMediaViewer = ({ message }: { message: SupportMessage }) => {
  const [media, setMedia] = useState<InboundMediaUrlInfo | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let active = true;
    setMedia(null);
    setFailed(false);
    void chatbotV2Service
      .fetchInboundMediaUrl(message.id)
      .then((info) => {
        if (!active) return;
        if (info?.url) setMedia(info);
        else setFailed(true);
      })
      .catch(() => {
        if (active) setFailed(true);
      });
    return () => {
      active = false;
    };
  }, [message.id]);

  if (media?.url) {
    return (
      <ChatMediaContent
        url={media.url}
        mediaType={String(media.media_type || message.content_type || "")}
        filename={media.filename || null}
      />
    );
  }
  if (failed) {
    // Mensajes previos a la persistencia S3: la imagen intenta el proxy directo de Meta;
    // el resto conserva el chip + transcripcion de siempre.
    const mediaId = getInboundMediaId(message);
    if (String(message.content_type || "").toLowerCase() === "image" && mediaId) {
      return <InboundWhatsAppImagePreview mediaId={mediaId} />;
    }
    return null;
  }
  return (
    <div className="flex items-center gap-2 rounded-xl border border-dashed border-gray-200 px-3 py-2 text-xs text-gray-500">
      <Loader2 className="size-3.5 animate-spin" />
      Cargando archivo
    </div>
  );
};

type OutboundMediaResourceMeta = {
  resource_key?: string;
  title?: string;
  kind?: string;
  file_name?: string;
  mime_type?: string;
  media_type?: string;
  link?: string;
  image_url?: string;
  source?: string;
  product_ref?: string;
};

const getOutboundMediaResource = (message: SupportMessage): OutboundMediaResourceMeta | null => {
  const meta = message.metadata_json?.media_resource;
  if (!meta || typeof meta !== "object") return null;
  return meta as OutboundMediaResourceMeta;
};

const formatTemplateStatus = (status?: string | null) => {
  const normalized = String(status || "").toUpperCase();
  const labels: Record<string, string> = {
    APPROVED: "Aprobada",
    PENDING: "En revisión",
    REJECTED: "Rechazada",
    PAUSED: "Pausada",
    DISABLED: "Deshabilitada",
    IN_APPEAL: "En apelación",
    PENDING_DELETION: "Pendiente de borrar",
    DELETED: "Eliminada",
    DRAFT: "Borrador",
    UNKNOWN: "Sin estado",
    NONE: "Sin estado",
  };
  return labels[normalized] || humanize(status);
};

const formatTemplateDisplayName = (name?: string | null) => {
  const normalized = String(name || "").trim();
  if (!normalized) return "Plantilla sin nombre";
  if (TEMPLATE_DISPLAY_NAME_OVERRIDES[normalized]) return TEMPLATE_DISPLAY_NAME_OVERRIDES[normalized];
  const presetKey = WHATSAPP_TEMPLATE_PRESET_KEYS.find(
    (key) => WHATSAPP_TEMPLATE_PRESETS[key].suggestedName === normalized
  );
  if (presetKey) return WHATSAPP_TEMPLATE_PRESETS[presetKey].displayName;
  return humanize(normalized);
};

const templateVisibleIssue = (template: WhatsAppTemplate) => {
  const reason = String(template.rejection_reason || "").trim();
  if (!reason || reason.toUpperCase() === "NONE" || template.status === "APPROVED") return null;
  return reason;
};

const formatConfirmationStatus = (status?: string | null) => {
  const normalized = String(status || "");
  const labels: Record<string, string> = {
    pending: "Pendiente",
    awaiting_response: "Esperando respuesta",
    confirmed: "Confirmada",
    rejected: "Rechazada",
    expired: "Expirada",
    canceled: "Cancelada",
    send_failed: "Error al enviar",
  };
  return labels[normalized] || humanize(status);
};

const formatOrderState = (state?: string | null) => {
  const normalized = String(state || "");
  const labels: Record<string, string> = {
    pending: "Pendiente",
    awaiting_response: "Esperando respuesta",
    confirmed: "Confirmada",
    rejected: "Rechazada",
    expired: "Expirada",
    send_failed: "Error al enviar",
    canceled: "Cancelada",
  };
  return labels[normalized] || formatConfirmationStatus(state);
};

const formatContactState = (state?: string | null) => {
  const normalized = String(state || "");
  const labels: Record<string, string> = {
    interested: "Interesado",
    purchased: "Compró",
    support_active: "Soporte activo",
    support_resolved: "Soporte resuelto",
    "customer_state:interested": "Interesado",
    "customer_state:purchased": "Compró",
    "support_state:active": "Soporte activo",
    "support_state:resolved": "Soporte resuelto",
  };
  return labels[normalized] || humanize(state);
};

const visibleBusinessTags = (tags?: string[] | null) =>
  (tags || []).filter((tag) => !customerStateTagSet.has(tag));

const HUMAN_HELP_REQUESTED_LABEL = "human_help_requested";

const formatConversationLabel = (label: string) => {
  const normalized = String(label || "");
  const labels: Record<string, string> = {
    [HUMAN_HELP_REQUESTED_LABEL]: "Ayuda humana solicitada",
    knowledge_gap: "Falta conocimiento",
  };
  return labels[normalized] || normalized;
};

const formatOrderTotal = (value?: number | string | null) => {
  if (value === null || value === undefined || value === "") return "Sin total";
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric <= 0) return "Sin total";
  return new Intl.NumberFormat("es-CO", {
    style: "currency",
    currency: "COP",
    maximumFractionDigits: 0,
  }).format(numeric);
};

const formatOrderDisplayReference = (
  order: Pick<SupportConfirmation, "display_code" | "order_id" | "id">
) => {
  const code = order.display_code || order.order_id;
  return code ? `#${code}` : `#${order.id}`;
};

const orderCardStateLabel = (state?: string | null) => {
  const normalized = String(state || "").toLowerCase();
  const labels: Record<string, string> = {
    pending: "Por enviar",
    awaiting_response: "Esperando al cliente",
    waiting: "Esperando al cliente",
    confirmed: "Confirmada",
    rejected: "Rechazada",
    expired: "Expirada",
    canceled: "Cancelada",
    cancelled: "Cancelada",
    send_failed: "Error al enviar",
    // Estados de órdenes de la tienda (filas sin flujo de confirmación del chatbot).
    in_progress: "En proceso",
    completed: "Completada",
    delivered: "Entregada",
    returned: "Devuelta",
    ecommerce: "Orden de la tienda",
    ecommerce_order: "Orden de la tienda",
  };
  return labels[normalized] || formatOrderState(state);
};

const orderCardStateTooltip = (state?: string | null) => {
  const normalized = String(state || "").toLowerCase();
  const tooltips: Record<string, string> = {
    pending: "Orden creada. El mensaje de confirmación automática aún no se ha enviado.",
    awaiting_response: "Se envió el mensaje de confirmación por WhatsApp y estamos esperando la respuesta del cliente.",
    waiting: "Se envió el mensaje de confirmación por WhatsApp y estamos esperando la respuesta del cliente.",
    confirmed: "El cliente confirmó esta orden.",
    rejected: "El cliente rechazó esta orden.",
    expired: "La orden expiró sin respuesta del cliente.",
    canceled: "La orden fue cancelada.",
    cancelled: "La orden fue cancelada.",
    send_failed: "No se pudo enviar el mensaje de confirmación. Revisa tu cuenta de WhatsApp o la plantilla asignada.",
    in_progress: "La orden está en proceso en la tienda.",
    completed: "La orden se completó.",
    delivered: "La orden fue entregada al cliente.",
    returned: "La orden fue devuelta.",
    ecommerce: "Orden creada en tu tienda (sin flujo de confirmación del chatbot).",
    ecommerce_order: "Orden creada en tu tienda (sin flujo de confirmación del chatbot).",
  };
  return tooltips[normalized] || orderCardStateLabel(state);
};

// Estado logístico "más avanzado" de la fila ecommerce del mismo pedido: cuando la
// logística ya avanzó, el chip único progresa (Confirmada -> Entregada) porque el
// estado logístico implica la confirmación.
const orderLogisticsAdvanceLabel = (state?: string | null) => {
  const normalized = String(state || "").toLowerCase();
  const labels: Record<string, string> = {
    delivered: "Entregada",
    completed: "Completada",
    cancelled: "Cancelada",
    canceled: "Cancelada",
    returned: "Devuelta",
  };
  return labels[normalized] || null;
};

const formatGuardianRunType = (type?: string | null) => {
  const normalized = String(type || "");
  const labels: Record<string, string> = {
    reactive_question: "Pregunta del owner",
    daily_summary: "Resumen diario",
    weekly_summary: "Resumen semanal",
    critical_alert: "Alerta crítica",
  };
  return labels[normalized] || humanize(type);
};

const formatEventSeverity = (severity?: string | null) => {
  const normalized = String(severity || "");
  const labels: Record<string, string> = {
    info: "Info",
    warning: "Revisar",
    error: "Error",
    critical: "Crítico",
  };
  return labels[normalized] || humanize(severity);
};

const formatChannel = (channel?: string | null) => {
  const normalized = String(channel || "whatsapp");
  const labels: Record<string, string> = {
    whatsapp: "WhatsApp",
    messenger: "Messenger",
    instagram: "Instagram",
    web: "Web",
    slack: "Slack",
  };
  return labels[normalized] || humanize(channel);
};

const channelBadgeClass = (channel?: string | null) => {
  const normalized = String(channel || "whatsapp");
  const styles: Record<string, string> = {
    whatsapp: "bg-emerald-50 text-emerald-700 ring-emerald-100",
    messenger: "bg-blue-50 text-blue-700 ring-blue-100",
    instagram: "bg-pink-50 text-pink-700 ring-pink-100",
  };
  return styles[normalized] || "bg-gray-50 text-gray-600 ring-gray-100";
};

const manualReplyPlaceholder = (channel?: string | null, optedOut?: boolean) => {
  if (optedOut) return "Este contacto pidió no recibir mensajes";
  switch (String(channel || "whatsapp")) {
    case "messenger":
      return "Escribe respuesta manual por Messenger";
    case "instagram":
      return "Escribe respuesta manual por Instagram";
    default:
      return "Escribe respuesta manual por WhatsApp";
  }
};

const formatLogStatus = (status?: string | null) => {
  const normalized = String(status || "");
  const labels: Record<string, string> = {
    accepted: "Aceptado",
    sent: "Enviado",
    delivered: "Entregado",
    read: "Leído",
    recorded: "Registrado",
    received: "Recibido",
    processing: "Procesando",
    buffered: "En buffer",
    processed: "Procesado",
    skipped: "Omitido",
    failed: "Falló",
    pending: "Pendiente",
    error: "Error",
    info: "Info",
    warning: "Revisar",
    critical: "Crítico",
  };
  return labels[normalized] || humanize(status);
};

const formatResponseLength = (value?: string | null) => {
  const normalized = String(value || "");
  const labels: Record<string, string> = {
    short: "Corto",
    medium: "Medio",
    long: "Largo",
  };
  return labels[normalized] || humanize(value);
};

const parseBackendDate = (value?: string | null) => {
  if (!value) return null;
  const normalized = /(?:z|[+-]\d{2}:?\d{2})$/i.test(value) ? value : `${value}Z`;
  const date = new Date(normalized);
  return Number.isNaN(date.getTime()) ? null : date;
};

// Fecha corta dd/mm/aaaa para las cajitas de "Sugerencias aprobadas".
const formatLearningDate = (value?: string | null) => {
  const date = parseBackendDate(value);
  if (!date) return "";
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  return `${day}/${month}/${date.getFullYear()}`;
};

const learningEntriesSortedDesc = (entries?: LearningEntry[]) =>
  [...(entries ?? [])].sort(
    (a, b) => (parseBackendDate(b.date)?.getTime() ?? 0) - (parseBackendDate(a.date)?.getTime() ?? 0)
  );

const learningLogLatestText = (source: Pick<KnowledgeSource, "entries">) => {
  const latest = learningEntriesSortedDesc(source.entries)[0];
  return latest?.text?.trim() || "";
};

const formatDate = (value?: string | null) => {
  if (!value) return "Sin fecha";
  const date = parseBackendDate(value);
  if (!date) return value;
  return new Intl.DateTimeFormat("es-CO", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
};

const validDateFrom = (value?: string | null) => {
  return parseBackendDate(value);
};

const formatMessageTime = (value?: string | null) => {
  const date = validDateFrom(value);
  if (!date) return "";
  return new Intl.DateTimeFormat("es-CO", {
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
};

const timelineDateKey = (value?: string | null) => {
  const date = validDateFrom(value);
  if (!date) return "sin-fecha";
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
};

const startOfLocalDay = (date: Date) => new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();

const formatTimelineDateLabel = (value?: string | null) => {
  const date = validDateFrom(value);
  if (!date) return "Sin fecha";

  const today = new Date();
  const diffDays = Math.round((startOfLocalDay(date) - startOfLocalDay(today)) / 86_400_000);
  if (diffDays === 0) return "Hoy";
  if (diffDays === -1) return "Ayer";

  const month = new Intl.DateTimeFormat("es-CO", { month: "short" })
    .format(date)
    .replace(".", "");
  return `${date.getDate()} ${month} ${date.getFullYear()}`;
};

const timestampMs = (value?: string | null) => {
  return parseBackendDate(value)?.getTime() || 0;
};

const hasPendingCustomerReply = (conversation: SupportConversation) => {
  const lastCustomer = timestampMs(conversation.last_customer_message_at);
  if (!lastCustomer) return false;
  const lastTeamReply = Math.max(
    timestampMs(conversation.last_bot_message_at),
    timestampMs(conversation.last_human_message_at)
  );
  return lastCustomer > lastTeamReply;
};

type ConversationWorkState = "idle" | "working" | "needs_action" | "failed" | "human_help" | "resolved_ping";
type AiUiPhase = "preparing" | "writing" | null;

const isLiveAiRuntimeStatus = (status?: string) => status === "processing" || status === "queued";

const isTerminalAiRuntimeStatus = (status?: string) =>
  status === "sent" || status === "failed" || status === "cancelled";

const shouldClearRetryingTraceForConversation = (conversation: SupportConversation) => {
  const runtime = getConversationAiRuntime(conversation);
  const status = getEffectiveAiRuntimeStatus(runtime);
  if (!status) return true;
  return isTerminalAiRuntimeStatus(status);
};

const isAiGenerationCancellable = (conversation: SupportConversation) =>
  isLiveAiRuntimeStatus(getEffectiveAiRuntimeStatus(getConversationAiRuntime(conversation)));

const isAiRuntimeExpired = (runtime: ConversationAiRuntime | null) => {
  if (!runtime?.expires_at) return false;
  const expiresMs = timestampMs(runtime.expires_at);
  return expiresMs > 0 && Date.now() >= expiresMs;
};

const getEffectiveAiRuntimeStatus = (runtime: ConversationAiRuntime | null) => {
  const status = runtime?.status;
  if (!status) return undefined;
  if (isAiRuntimeExpired(runtime) && (status === "queued" || status === "processing")) {
    return undefined;
  }
  return status;
};

const isReopenedFromResolved = (conversation: SupportConversation) => {
  const metadata = isRecord(conversation.metadata_json) ? conversation.metadata_json : null;
  return Boolean(metadata?.reopened_from_resolved);
};

const hasHumanHelpRequested = (conversation: SupportConversation) =>
  Boolean(conversation.human_help_requested) ||
  Boolean(conversation.labels?.includes(HUMAN_HELP_REQUESTED_LABEL));

const isRetryingActiveForConversation = (
  conversation: SupportConversation,
  retryingTraceId: string | null,
  retryingConversationId: number | null,
  conversationActionLoading: ConversationActionLoading
) => {
  if (isConversationActionLoading(conversationActionLoading, "retry-ai", conversation.id)) return true;
  const aiRuntime = getConversationAiRuntime(conversation);
  const runtimeStatus = getEffectiveAiRuntimeStatus(aiRuntime);
  if (!retryingTraceId || !aiRuntime?.trace_id || aiRuntime.trace_id !== retryingTraceId) return false;
  return isLiveAiRuntimeStatus(runtimeStatus);
};

const getConversationSimulationMeta = (conversation: SupportConversation) => {
  const simulation = conversation.metadata_json?.simulation;
  if (!simulation || typeof simulation !== "object") return null;
  const record = simulation as Record<string, unknown>;
  if (record.transport !== "simulation") return null;
  return record;
};

const isAiPipelineActive = (
  conversation: SupportConversation,
  retryingTraceId: string | null,
  retryingConversationId: number | null,
  conversationActionLoading: ConversationActionLoading,
  supportAiEnabled: boolean
) => {
  if (!supportAiEnabled || !conversation.ai_enabled) return false;
  if (isRetryingActiveForConversation(conversation, retryingTraceId, retryingConversationId, conversationActionLoading)) {
    return true;
  }
  const runtimeStatus = getEffectiveAiRuntimeStatus(getConversationAiRuntime(conversation));
  return isLiveAiRuntimeStatus(runtimeStatus);
};

const getConversationAiUiPhase = (
  conversation: SupportConversation | null,
  messages: SupportMessage[],
  events: ChatbotEvent[],
  retryingTraceId: string | null,
  retryingConversationId: number | null,
  conversationActionLoading: ConversationActionLoading,
  supportAiEnabled: boolean
): AiUiPhase => {
  if (!conversation) return null;
  if (!isAiPipelineActive(conversation, retryingTraceId, retryingConversationId, conversationActionLoading, supportAiEnabled)) {
    return null;
  }
  const latestStartedAt = Math.max(
    ...events
      .filter((event) => event.event_type === "support.ai.response_started")
      .map((event) => timestampMs(event.created_at)),
    0
  );
  const latestPartSentAt = Math.max(
    ...events
      .filter((event) => event.event_type === "support.ai.response_part_sent")
      .map((event) => timestampMs(event.created_at)),
    0
  );
  const latestBotMessageAt = Math.max(
    ...messages
      .filter((message) => message.direction === "outbound" && !isHumanSender(message.sender_type))
      .map((message) => timestampMs(message.created_at)),
    0
  );
  if (latestStartedAt > Math.max(latestPartSentAt, latestBotMessageAt)) return "preparing";
  return "writing";
};

const getConversationWorkState = (
  conversation: SupportConversation,
  supportAiEnabled: boolean,
  retryingTraceId: string | null,
  retryingConversationId: number | null,
  conversationActionLoading: ConversationActionLoading
): ConversationWorkState => {
  const pending = hasPendingCustomerReply(conversation);
  const runtimeStatus = getEffectiveAiRuntimeStatus(getConversationAiRuntime(conversation));
  const retrying = isRetryingActiveForConversation(
    conversation,
    retryingTraceId,
    retryingConversationId,
    conversationActionLoading
  );
  const aiActive = supportAiEnabled && conversation.ai_enabled;
  // Modo aprendiz: una respuesta esperando aprobación pide acción del owner (badge negro),
  // no debe pintarse como IA-trabajando (azul). Se resuelve antes de calcular aiWorking.
  if (getConversationAiRuntime(conversation)?.status === "pending_approval") return "needs_action";
  const aiWorking = isLiveAiRuntimeStatus(runtimeStatus) || retrying;

  // Prioridad: rojo > verde > negro > azul > gris > idle
  if (pending && runtimeStatus === "failed" && !retrying) return "failed";
  // Puntico rojo también por ERROR DE ENVÍO (Julián): la nota universal de fallo
  // siempre deja el preview "❌ …" — se limpia solo cuando llega un mensaje nuevo.
  if (String(conversation.last_message_preview || "").trim().startsWith("❌")) return "failed";
  if (hasHumanHelpRequested(conversation)) return "human_help";
  if (pending && !aiActive) {
    if (isReopenedFromResolved(conversation)) return "resolved_ping";
    return "needs_action";
  }
  if (aiWorking && aiActive) return "working";
  if (isReopenedFromResolved(conversation) && !aiActive) return "resolved_ping";
  return "idle";
};

// Modo aprendiz: extrae el motivo del 409 al aprobar una respuesta
// (customer_replied | window_closed | invalid_status | version_not_found).
const getReplyApprovalConflict = (error: unknown): string | null => {
  const response = (error as { response?: { status?: number; data?: unknown } }).response;
  if (!response || response.status !== 409) return null;
  const detail = isRecord(response.data) ? response.data.detail : null;
  if (typeof detail === "string") return detail;
  if (isRecord(detail) && typeof detail.status === "string") return detail.status;
  return null;
};

const getPendingCustomerMessageCount = (conversation: SupportConversation) => {
  if (!hasPendingCustomerReply(conversation)) return 0;
  return Math.max(1, conversation.unread_count || 0);
};

const formatCustomerReplyAge = (value?: string | null) => {
  if (!value) return "";
  const date = parseBackendDate(value);
  if (!date) return "";
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(date);
  target.setHours(0, 0, 0, 0);
  const diffDays = Math.round((today.getTime() - target.getTime()) / 86400000);
  const time = new Intl.DateTimeFormat("es-CO", {
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
  if (diffDays <= 0) return time;
  if (diffDays === 1) return `Ayer · ${time}`;
  const shortDate = new Intl.DateTimeFormat("es-CO", {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
  }).format(date);
  return `${shortDate} · ${time}`;
};

const getErrorMessage = (error: unknown) => {
  const err = error as {
    response?: { data?: { detail?: unknown; message?: string }; status?: number };
    message?: string;
  };
  const detail = err.response?.data?.detail;
  if (detail === "shared_waba_requires_isolated_account") {
    return "Esa WABA tiene más de un número. Crea o usa una WABA separada para este WhatsApp antes de suscribir webhooks.";
  }
  if (detail === "configured_phone_number_not_in_waba") {
    return "El phone_number_id guardado no pertenece a esa WABA. Revisa ambos IDs en Meta antes de continuar.";
  }
  if (detail === "knowledge_budget_exceeded") {
    return "La base 'Sugerencias aprobadas' alcanzó el límite de conocimiento activo — borra cajitas viejas o desactiva otras fuentes.";
  }
  if (typeof detail === "string") return detail;
  if (detail && typeof detail === "object") {
    const detailObj = detail as { message?: string; fields?: string[]; errors?: string[] };
    if (detailObj.message === "Automation is incomplete") {
      return "Completa la automatización antes de guardar.";
    }
    if (detailObj.message && detailObj.fields?.length) {
      return `${detailObj.message}: ${detailObj.fields.join(", ")}`;
    }
    if (detailObj.message) return detailObj.message;
  }
  return err.response?.data?.message || err.message || "Error inesperado";
};

const stringifySmall = (value: unknown) => {
  if (!value || (typeof value === "object" && Object.keys(value as Record<string, unknown>).length === 0)) {
    return "Sin detalles";
  }
  return JSON.stringify(value, null, 2);
};

const detailText = (details: Record<string, unknown>, key: string) => {
  const value = details[key];
  if (typeof value === "string") return value;
  if (typeof value === "number") return String(value);
  return "";
};

const eventDisplayTitle = (event: ChatbotEvent) => {
  if (event.event_type.startsWith("integration.tool.")) {
    const toolName = detailText(event.details, "tool_name") || "Tool";
    return `${toolName} · ${event.event_type.replace("integration.tool.", "")}`;
  }
  if (event.event_type.startsWith("hotmart.")) {
    return `Hotmart · ${event.event_type.replace("hotmart.", "")}`;
  }
  return event.event_type;
};

const eventDisplaySubtitle = (event: ChatbotEvent) => {
  const provider = detailText(event.details, "provider_key");
  const status = detailText(event.details, "status");
  const parts = [provider, status ? formatLogStatus(status) : "", formatDate(event.created_at)].filter(Boolean);
  return parts.join(" · ");
};

const contactName = (conversation: SupportConversation | null) => {
  if (!conversation) return "Selecciona una conversación";
  const channel = String(conversation.channel || "").toLowerCase();
  const externalIdFallback =
    channel === "instagram"
      ? "Usuario de Instagram"
      : channel === "messenger"
        ? "Usuario de Messenger"
        : formatPhoneForDisplay(conversation.contact?.external_id);
  return (
    conversation.contact?.alias ||
    conversation.contact?.name ||
    formatPhoneForDisplay(conversation.contact?.phone) ||
    externalIdFallback ||
    `Conversación #${conversation.id}`
  );
};

const conversationIdentityLabel = (conversation: SupportConversation | null) => {
  if (!conversation) return "";
  const name = conversation.contact?.alias || conversation.contact?.name;
  if (name) return name;
  const phone = formatPhoneForDisplay(conversation.contact?.phone);
  if (phone) return phone;
  const channel = String(conversation.channel || "").toLowerCase();
  if (channel === "instagram") return "Usuario de Instagram";
  if (channel === "messenger") return "Usuario de Messenger";
  const externalId = formatPhoneForDisplay(conversation.contact?.external_id);
  if (externalId) return externalId;
  return `Conversación #${conversation.id}`;
};

const formatPhoneForDisplay = (phone?: string | null) => {
  if (!phone) return "";
  const raw = phone.trim();
  const digits = raw.replace(/\D/g, "");
  if (!digits) return raw;
  const countryLength =
    digits.startsWith("57") && digits.length === 12
      ? 2
      : digits.length > 10
        ? Math.max(1, digits.length - 10)
        : 0;
  if (!countryLength) return formatNationalPhone(digits);
  const country = digits.slice(0, countryLength);
  const national = digits.slice(countryLength);
  return `+${country} ${formatNationalPhone(national)}`;
};

const formatNationalPhone = (digits: string) => {
  if (digits.length === 10) return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`;
  if (digits.length === 9) return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`;
  if (digits.length <= 4) return digits;
  return digits.replace(/(.{3})(?=.)/g, "$1 ").trim();
};

const compactPhone = (phone?: string | null) => {
  return formatPhoneForDisplay(phone) || "Sin teléfono";
};

const contactToForm = (contact?: SupportContact | null): ContactFormState => ({
  name: contact?.name || "",
  email: contact?.email || "",
  alias: contact?.alias || "",
  phone: contact?.phone || "",
  tags: (contact?.tags || []).join(", "),
  custom_attributes: JSON.stringify(contact?.custom_attributes || {}, null, 2),
});

const pickNewerAiRuntime = (
  base: ConversationAiRuntime | null | undefined,
  updated: ConversationAiRuntime | null | undefined
): ConversationAiRuntime | null | undefined => {
  if (!updated) return base;
  if (!base) return updated;
  const baseTs = timestampMs(base.updated_at);
  const updatedTs = timestampMs(updated.updated_at);
  return updatedTs >= baseTs ? updated : base;
};

const mergeConversationRecord = (
  base: SupportConversation | null | undefined,
  updated: SupportConversation
): SupportConversation => {
  const mergedMetadata = {
    ...(base?.metadata_json || {}),
    ...(updated.metadata_json || {}),
  };
  const mergedRuntime = pickNewerAiRuntime(
    isRecord(base?.metadata_json?.ai_runtime) ? (base?.metadata_json?.ai_runtime as ConversationAiRuntime) : null,
    isRecord(updated.metadata_json?.ai_runtime) ? (updated.metadata_json?.ai_runtime as ConversationAiRuntime) : null
  );
  if (mergedRuntime) {
    mergedMetadata.ai_runtime = mergedRuntime;
  }
  return {
    ...(base || {}),
    ...updated,
    contact: updated.contact ?? base?.contact ?? null,
    metadata_json: mergedMetadata,
  };
};

const sortConversationsByUpdatedAt = (rows: SupportConversation[]) =>
  [...rows].sort((a, b) => timestampMs(b.updated_at) - timestampMs(a.updated_at));

const mergeConversationLists = (lists: SupportConversation[][], limit = 50) => {
  const byId = new Map<number, SupportConversation>();
  lists.flat().forEach((conversation) => {
    const current = byId.get(conversation.id);
    byId.set(conversation.id, current ? mergeConversationRecord(current, conversation) : conversation);
  });
  return sortConversationsByUpdatedAt(Array.from(byId.values())).slice(0, limit);
};

const sortMessagesByCreatedDesc = (rows: SupportMessage[]) =>
  [...rows].sort((a, b) => {
    const diff = getMessageUiOrder(b) - getMessageUiOrder(a);
    if (diff !== 0) return diff;
    return Number(b.id || 0) - Number(a.id || 0);
  });

const mergeSupportMessageRecord = (
  base: SupportMessage | undefined,
  updated: SupportMessage
): SupportMessage => {
  const deliveryStatus = highestOutboundDeliveryStatus(
    base?.delivery_status,
    updated.delivery_status
  );
  const baseMetadata = base?.metadata_json || {};
  const updatedMetadata = updated.metadata_json || {};
  const mergedMetadata: Record<string, unknown> = {
    ...baseMetadata,
    ...updatedMetadata,
  };
  const baseDelivery = isRecord(baseMetadata.delivery) ? baseMetadata.delivery : {};
  const updatedDelivery = isRecord(updatedMetadata.delivery) ? updatedMetadata.delivery : {};
  if (Object.keys(baseDelivery).length || Object.keys(updatedDelivery).length) {
    const deliveryState = highestOutboundDeliveryStatus(
      String(baseDelivery.state || base?.delivery_status || ""),
      String(updatedDelivery.state || updated.delivery_status || "")
    );
    mergedMetadata.delivery = {
      ...baseDelivery,
      ...updatedDelivery,
      ...(deliveryState ? { state: deliveryState } : {}),
    };
  }
  return {
    ...(base || {}),
    ...updated,
    delivery_status: deliveryStatus || updated.delivery_status || base?.delivery_status || null,
    metadata_json: mergedMetadata,
  };
};

const mergeMessageRows = (current: SupportMessage[], incoming: SupportMessage[]) => {
  const byId = new Map<number, SupportMessage>();
  const clientIdToMessageId = new Map<string, number>();
  const upsert = (message: SupportMessage) => {
    const clientMessageId = getMessageClientMessageId(message);
    const existingId = clientMessageId ? clientIdToMessageId.get(clientMessageId) : undefined;
    const previous = existingId !== undefined ? byId.get(existingId) : byId.get(message.id);
    if (existingId !== undefined && existingId !== message.id) {
      byId.delete(existingId);
    }
    const merged = mergeSupportMessageRecord(previous, message);
    byId.set(message.id, merged);
    if (clientMessageId) clientIdToMessageId.set(clientMessageId, message.id);
  };
  current.forEach(upsert);
  incoming.forEach(upsert);
  return sortMessagesByCreatedDesc(Array.from(byId.values()));
};

const mergeConversationMessageRows = (
  current: SupportMessage[],
  incoming: SupportMessage[],
  conversationId: number
) => mergeMessageRows(
  current.filter((message) => message.conversation_id === conversationId),
  incoming
);

const rowsEqualByJson = <T,>(current: T[], next: T[]) => JSON.stringify(current) === JSON.stringify(next);

const setRowsIfChanged = <T,>(setter: React.Dispatch<React.SetStateAction<T[]>>, next: T[]) => {
  setter((current) => (rowsEqualByJson(current, next) ? current : next));
};

const ignoredRealtimeEvents = new Set(["realtime.connected", "realtime.unavailable"]);
const inboxRealtimeEvents = new Set([
  "conversation.created",
  "conversation.updated",
  "message.created",
  "message.received",
  "ai.response_started",
  "ai.response_part_sent",
  "ai.response_failed",
  "ai.response_cancelled",
  "ai.response_pending_approval",
  "ai.reply_approval.updated",
  "ai.reply_approval.resolved",
  "delivery.updated",
]);
const conversationDetailRealtimeEvents = new Set([
  "conversation.updated",
  "message.created",
  "message.received",
  "ai.response_started",
  "ai.response_part_sent",
  "ai.response_failed",
  "ai.response_cancelled",
  "ai.response_pending_approval",
  "ai.reply_approval.updated",
  "ai.reply_approval.resolved",
  "delivery.updated",
]);
const statesRealtimeEvents = new Set(["states.updated"]);
const notificationsRealtimeEvents = new Set(["notifications.updated"]);
const guardianRealtimeEvents = new Set([
  "guardian.chat.updated",
  "guardian.proposals.updated",
  "guardian.review.completed",
  "guardian.report.completed",
]);
let activeChatbotRealtimeAbortController: AbortController | null = null;

const realtimeEventKey = (event: ChatbotRealtimeEvent) =>
  [
    event.type,
    event.conversation_id ?? "all",
    event.message_id ?? "no-message",
    event.wa_message_id ?? "no-wa",
    event.trace_id ?? "no-trace",
  ].join(":");

const shouldIgnoreRealtimeEvent = (
  event: ChatbotRealtimeEvent,
  seenEvents: Map<string, number>
) => {
  if (ignoredRealtimeEvents.has(event.type)) return true;
  const now = Date.now();
  for (const [key, lastSeenAt] of seenEvents.entries()) {
    if (now - lastSeenAt > 5000) seenEvents.delete(key);
  }
  const key = realtimeEventKey(event);
  const lastSeenAt = seenEvents.get(key);
  if (lastSeenAt && now - lastSeenAt < 800) return true;
  seenEvents.set(key, now);
  return false;
};

const parseCsvTags = (value: string) =>
  value
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);

const normalizeLabels = (labels: string[]) =>
  Array.from(new Set(labels.map((label) => label.trim()).filter(Boolean)));

const labelsEqual = (a: string[], b: string[]) => {
  const first = normalizeLabels(a).sort();
  const second = normalizeLabels(b).sort();
  return first.length === second.length && first.every((label, index) => label === second[index]);
};

const getCustomerStateTag = (tags?: string[] | null) =>
  (tags || []).find((tag) => customerStateTagSet.has(tag)) || "customer_state:interested";

const mergeCustomerStateTag = (tagsCsv: string, stateTag: string) =>
  normalizeLabels([
    ...parseCsvTags(tagsCsv).filter((tag) => !customerStateTagSet.has(tag)),
    stateTag,
  ]).join(", ");

const conversationToContactStateItem = (conversation: SupportConversation): ContactStateItem => {
  const contact = conversation.contact;
  const tags = contact?.tags || [];
  const state = getCustomerStateTag(tags);
  return {
    id: contact?.id || conversation.contact_id || conversation.id,
    contact: contact || {
      id: conversation.contact_id || conversation.id,
      owner_id: conversation.owner_id,
      channel: conversation.channel,
      external_id: null,
      phone: null,
      name: null,
      email: null,
      alias: null,
      tags: [],
      notes: [],
      custom_attributes: {},
      opted_out_at: null,
      last_seen_at: null,
    },
    state,
    state_label: formatContactState(state),
    tags,
    confirmed_orders: 0,
    active_support: conversation.status !== "resolved",
    resolved_support: conversation.status === "resolved",
    latest_order_at: null,
    latest_conversation_id: conversation.id,
  };
};

const loadContactSidebarPayload = async (item: ContactStateItem) => {
  const conversationId = item.latest_conversation_id || null;
  const [conversation, orders, messageRows] = await Promise.all([
    conversationId ? chatbotV2Service.getConversation(conversationId) : Promise.resolve(null),
    chatbotV2Service.listContactOrders(item.id, 12),
    conversationId ? chatbotV2Service.listMessages(conversationId, { limit: 30 }) : Promise.resolve([]),
  ]);
  const notes = messageRows.filter(
    (message) => (message.private || message.direction === "internal") && !isRetryInstructionMessage(message)
  );
  return { conversation, orders, notes };
};

// Caché del catálogo (Julian): si lo acabo de ver y vuelvo a abrir, no debe volver a cargar
// ni "desaparecerse". Módulo-level para sobrevivir cierres del modal/cambios de tab; TTL corto
// para no servir datos viejos; el refresh corre en segundo plano SIN vaciar lo ya mostrado.
const PRODUCTS_CATALOG_CACHE_TTL_MS = 120_000;
let productsCatalogCache: { items: Product[]; fetchedAt: number } | null = null;

const useFluxiProductsCatalog = () => {
  const [catalogProducts, setCatalogProducts] = useState<Product[]>(
    () => productsCatalogCache?.items || []
  );
  const [catalogLoading, setCatalogLoading] = useState(false);
  useEffect(() => {
    let cancelled = false;
    const cached = productsCatalogCache;
    const fresh = cached && Date.now() - cached.fetchedAt < PRODUCTS_CATALOG_CACHE_TTL_MS;
    if (cached && !fresh) setCatalogProducts(cached.items);
    if (fresh) return;
    // Solo muestra esqueleto si no hay NADA que mostrar (primera vez).
    if (!cached) setCatalogLoading(true);
    void getProductsPage({ size: 100, page: 0 })
      .then((response) => {
        const items = response.items || [];
        productsCatalogCache = { items, fetchedAt: Date.now() };
        if (!cancelled) setCatalogProducts(items);
      })
      .catch((error) => {
        console.warn("[ChatbotPage] products catalog load failed", error);
      })
      .finally(() => {
        if (!cancelled) setCatalogLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);
  return { catalogProducts, catalogLoading };
};

const safeParseJson = (value: string): Record<string, unknown> => {
  const trimmed = value.trim();
  if (!trimmed) return {};
  const parsed = JSON.parse(trimmed);
  if (!parsed || Array.isArray(parsed) || typeof parsed !== "object") {
    throw new Error("custom_attributes debe ser un objeto JSON");
  }
  return parsed as Record<string, unknown>;
};

const noteFromUnknown = (value: unknown): SupportContactNote | null => {
  if (!value || typeof value !== "object") return null;
  const note = value as Partial<SupportContactNote> & { content?: unknown };
  const noteText = note.note || (typeof note.content === "string" ? note.content : "");
  if (!noteText) return null;
  return {
    note: String(noteText),
    category: String(note.category || "general"),
    source: String(note.source || "unknown"),
    conversation_id: typeof note.conversation_id === "number" ? note.conversation_id : null,
    wa_message_id: note.wa_message_id ? String(note.wa_message_id) : null,
    private: Boolean(note.private),
    created_at: note.created_at ? String(note.created_at) : "",
  };
};

const makeOptimisticOutboundMessage = ({
  conversation,
  content,
  clientMessageId,
}: {
  conversation: SupportConversation;
  content: string;
  clientMessageId: string;
}): SupportMessage => {
  const nowMs = Date.now();
  return {
    id: -nowMs,
    owner_id: conversation.owner_id,
    conversation_id: conversation.id,
    contact_id: conversation.contact_id,
    sender_type: "agent",
    sender_id: null,
    sender_name: "Equipo",
    direction: "outbound",
    channel: "whatsapp",
    content,
    content_type: "text",
    private: false,
    turn_id: null,
    wa_message_id: null,
    template_name: null,
    delivery_status: "pending",
    metadata_json: {
      local: true,
      optimistic: true,
      client_message_id: clientMessageId,
      ui_order: nowMs,
    },
    created_at: new Date(nowMs).toISOString(),
  };
};

const makeLocalUnknownOutboundMessage = (message: SupportMessage): SupportMessage => ({
  ...message,
  delivery_status: "unknown",
  metadata_json: {
    ...(message.metadata_json || {}),
    local: true,
    optimistic: true,
    delivery: {
      state: "unknown",
      reason: "client_reconciliation_pending",
    },
  },
});

const createClientMessageId = (conversationId: number) => {
  const randomPart =
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : Math.random().toString(36).slice(2);
  return `manual-${conversationId}-${Date.now()}-${randomPart}`;
};

const parseRealtimeBlock = (block: string): ChatbotRealtimeEvent | null => {
  const data = block
    .split("\n")
    .filter((line) => line.startsWith("data:"))
    .map((line) => line.slice(5).trimStart())
    .join("\n")
    .trim();
  if (!data) return null;
  try {
    return JSON.parse(data) as ChatbotRealtimeEvent;
  } catch {
    return null;
  }
};

const connectChatbotRealtime = async (
  signal: AbortSignal,
  onEvent: (event: ChatbotRealtimeEvent) => void
) => {
  const token = chatbotV2Service.getRealtimeAuthToken();
  if (!token) throw new Error("missing_realtime_token");

  const response = await fetch(chatbotV2Service.getRealtimeStreamUrl(), {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: "text/event-stream",
    },
    signal,
  });
  if (!response.ok || !response.body) {
    throw new Error(`realtime_stream_failed:${response.status}`);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  try {
    while (!signal.aborted) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const blocks = buffer.split("\n\n");
      buffer = blocks.pop() || "";
      for (const block of blocks) {
        const event = parseRealtimeBlock(block);
        if (event?.type === "realtime.unavailable") {
          throw new Error("realtime_unavailable");
        }
        if (event) onEvent(event);
      }
    }
  } finally {
    try {
      await reader.cancel();
    } catch {
      // The fetch may already be aborted; either way the stream is no longer usable.
    }
  }
  if (!signal.aborted) {
    throw new Error("realtime_stream_closed");
  }
};

const workspaceTabs: { value: MainWorkspace; label: string }[] = [
  { value: "chat", label: "Chat" },
  { value: "guardian", label: "Guardian" },
  { value: "states", label: "Métricas" },
  { value: "appointments", label: "Agendamientos" },
];

const MainWorkspaceTabs = ({
  value,
  onChange,
}: {
  value: MainWorkspace;
  onChange: (value: MainWorkspace) => void;
}) => (
  <div className="inline-flex h-10 max-w-[calc(100vw-160px)] items-center gap-1 overflow-x-auto rounded-full bg-gray-100 p-1 md:max-w-none">
    {workspaceTabs.map((tab) => {
      const active = tab.value === value;
      return (
        <button
          key={tab.value}
          type="button"
          onClick={() => onChange(tab.value)}
          className={`inline-flex h-8 shrink-0 items-center rounded-full px-4 text-sm font-semibold transition ${
            active ? "bg-white text-gray-950 shadow-sm" : "text-gray-500 hover:text-gray-900"
          }`}
        >
          {tab.label}
        </button>
      );
    })}
  </div>
);

const ChatbotPage = () => {
  const navigate = useNavigate();
  const checkoutWebsitesForValidation = useWebsitesStore((state) => state.websites);
  const refreshCheckoutWebsites = useWebsitesStore((state) => state.initialize);
  const googleOAuthCompletionResult = typeof window === "undefined"
    ? null
    : getGoogleOAuthCompletionResult(window.location.search);
  const [settingsModalOpen, setSettingsModalOpen] = useState(
    googleOAuthCompletionResult !== null
  );
  const [settingsPanelTab, setSettingsPanelTab] = useState<SettingsPanelTab>(
    googleOAuthCompletionResult ? "actions" : "knowledge"
  );
  const [actionsConfigSubTab, setActionsConfigSubTab] = useState<ActionsConfigSubTab>(
    googleOAuthCompletionResult ? "agendamientos" : "ventas"
  );
  const [knowledgePanelView, setKnowledgePanelView] = useState<"main" | "test" | "editor" | "source" | "media">("main");
  const [integrationDetail, setIntegrationDetail] = useState<IntegrationDetail | null>(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [coreLoading, setCoreLoading] = useState(false);
  const [pageError, setPageError] = useState<string | null>(null);
  const coreLoadInFlightRef = useRef(false);

  const [account, setAccount] = useState<WhatsAppConnectionStatus | null>(null);
  const [settings, setSettings] = useState<SupportSettings | null>(null);
  const [overview, setOverview] = useState<SupportOverview | null>(null);
  const [health, setHealth] = useState<OperationsHealth | null>(null);
  const [notifications, setNotifications] = useState<SupportNotification[]>([]);
  // Counts por estado REAL (sugerencias pendientes / ayuda humana activa / sin responder),
  // independientes de si las notificaciones están leídas o no.
  const [notificationsSummary, setNotificationsSummary] = useState<NotificationsSummary | null>(null);
  const [notificationsOpen, setNotificationsOpen] = useState(false);

  const [conversationBucketFilters, setConversationBucketFilters] = useState<ConversationBucket[]>(["open"]);
  const [conversationSearch, setConversationSearch] = useState("");
  const [conversationPriorityFilter, setConversationPriorityFilter] = useState("");
  const [conversationCustomerStateFilter, setConversationCustomerStateFilter] = useState("");
  const [conversationLabelFilters, setConversationLabelFilters] = useState<string[]>([]);
  const [conversationHumanHelpFilter, setConversationHumanHelpFilter] = useState<HumanHelpFilter>("all");
  const [conversationChannelFilter, setConversationChannelFilter] = useState("");
  const [conversationUnansweredFilter, setConversationUnansweredFilter] = useState(false);
  const [conversationPendingApprovalFilter, setConversationPendingApprovalFilter] = useState(false);
  const [conversationDeliveryErrorFilter, setConversationDeliveryErrorFilter] = useState(false);
  const {
    conversations,
    isLoading: conversationsLoading,
    isFetching: conversationsFetching,
    isSearchPending: conversationsSearchPending,
    hasNextPage: conversationsHasNextPage,
    fetchNextPage: fetchNextConversationsPage,
    invalidate: invalidateConversations,
    patchConversation,
    refreshOverview: refreshConversationsOverview,
  } = useSupportConversationsInfinite({
    buckets: conversationBucketFilters,
    search: conversationSearch,
    priority: conversationPriorityFilter,
    customerState: conversationCustomerStateFilter,
    labels: conversationLabelFilters,
    humanHelp: conversationHumanHelpFilter,
    channel: conversationChannelFilter,
    unanswered: conversationUnansweredFilter,
    pendingApproval: conversationPendingApprovalFilter,
    deliveryError: conversationDeliveryErrorFilter,
  });
  const [selectedConversationId, setSelectedConversationId] = useState<number | null>(null);
  const [selectedConversation, setSelectedConversation] = useState<SupportConversation | null>(null);
  const [messages, setMessages] = useState<SupportMessage[]>([]);
  const [messagesHasMore, setMessagesHasMore] = useState(false);
  const [messagesLoadingOlder, setMessagesLoadingOlder] = useState(false);
  const [conversationEvents, setConversationEvents] = useState<ChatbotEvent[]>([]);
  const [conversationDeliveryLogs, setConversationDeliveryLogs] = useState<ChatbotDeliveryLog[]>([]);
  const [contactOrders, setContactOrders] = useState<SupportConfirmation[]>([]);
  const [contactOrdersLoading, setContactOrdersLoading] = useState(false);
  const [personView, setPersonView] = useState<PersonView | null>(null);
  // Se incrementa para re-fetchear el person view sin cambiar de contacto (p. ej. tras separar un chat).
  const [personViewRefreshKey, setPersonViewRefreshKey] = useState(0);
  const [conversationLoading, setConversationLoading] = useState(false);
  const [composerValue, setComposerValue] = useState("");
  const [sendingMessage, setSendingMessage] = useState(false);
  const [forceScrollToBottomSignal, setForceScrollToBottomSignal] = useState(0);
  const [conversationActionLoading, setConversationActionLoading] = useState<ConversationActionLoading>(null);
  const [contactForm, setContactForm] = useState<ContactFormState>(EMPTY_CONTACT_FORM);
  const [contactSaving, setContactSaving] = useState(false);
  const [contactNote, setContactNote] = useState("");
  const [privateNote, setPrivateNote] = useState("");
  const [retryInstruction, setRetryInstruction] = useState("");
  const [retryingTraceId, setRetryingTraceId] = useState<string | null>(null);
  const [retryingConversationId, setRetryingConversationId] = useState<number | null>(null);
  const [pendingIntegrationSave, setPendingIntegrationSave] = useState<"whatsapp" | "hotmart" | null>(null);
  const selectedConversationIdRef = useRef<number | null>(null);
  const selectedConversationRef = useRef<SupportConversation | null>(null);
  const messagesRef = useRef<SupportMessage[]>([]);
  const coreLoadedRef = useRef(false);
  const initialStatesDomain = useMemo(getInitialStatesDomain, []);
  const realtimeListTimerRef = useRef<number | null>(null);
  const realtimeDetailsTimerRef = useRef<number | null>(null);
  const realtimeStatesTimerRef = useRef<number | null>(null);
  const realtimeNotificationsTimerRef = useRef<number | null>(null);
  const realtimeGuardianTimerRef = useRef<number | null>(null);
  const realtimeSeenEventsRef = useRef<Map<string, number>>(new Map());
  const activeWorkspaceRef = useRef<MainWorkspace>("chat");
  const statesDomainRef = useRef<StateDomain>(initialStatesDomain);
  const metricsSurfaceRef = useRef<MetricsSurface>("dashboard");

  const [accountForm, setAccountForm] = useState<AccountFormState>(EMPTY_ACCOUNT_FORM);
  const [accountSaving, setAccountSaving] = useState(false);
  const [diagnoseResult, setDiagnoseResult] = useState<Record<string, unknown> | null>(null);
  const [templates, setTemplates] = useState<WhatsAppTemplate[]>([]);
  const [templateStatusFilter, setTemplateStatusFilter] = useState("");
  const [templateSaving, setTemplateSaving] = useState(false);
  const [defaultTemplates, setDefaultTemplates] = useState<Record<string, string>>({});
  const [automationDrafts, setAutomationDrafts] = useState<AutomationDraft[]>(defaultAutomationDrafts);
  const [savedAutomationDrafts, setSavedAutomationDrafts] = useState<AutomationDraft[]>(defaultAutomationDrafts);
  const [automationDraftsTouched, setAutomationDraftsTouched] = useState(false);
  const [automationValidationError, setAutomationValidationError] = useState<string | null>(null);
  const [confirmationFlowDrafts, setConfirmationFlowDrafts] = useState<CommerceFlowDraft[]>([]);
  const [savedConfirmationFlowDrafts, setSavedConfirmationFlowDrafts] = useState<CommerceFlowDraft[]>([]);
  const [orderFlowDrafts, setOrderFlowDrafts] = useState<CommerceFlowDraft[]>([]);
  const [savedOrderFlowDrafts, setSavedOrderFlowDrafts] = useState<CommerceFlowDraft[]>([]);
  const [recoveryFlowDrafts, setRecoveryFlowDrafts] = useState<CartRecoveryFlowDraft[]>([]);
  const [savedRecoveryFlowDrafts, setSavedRecoveryFlowDrafts] = useState<CartRecoveryFlowDraft[]>([]);
  const [salesActionsSettings, setSalesActionsSettings] = useState<SalesActionsSettings>(defaultSalesActionsSettings());
  const [savedSalesActionsSettings, setSavedSalesActionsSettings] = useState<SalesActionsSettings>(defaultSalesActionsSettings());
  const [commerceFlowValidationError, setCommerceFlowValidationError] = useState<string | null>(null);
  const [websiteSyncDraftsById, setWebsiteSyncDraftsById] = useState<Record<string, WebsiteSyncDraftState>>({});
  const [websiteSyncSaving, setWebsiteSyncSaving] = useState(false);
  const settingsModalOpenRef = useRef(false);
  const automationDraftsDirtyRef = useRef(false);
  const automationDraftsTouchedRef = useRef(false);
  const confirmationFlowsDirtyRef = useRef(false);
  const orderFlowsDirtyRef = useRef(false);
  const settingsFormDirtyRef = useRef(false);
  const mediaResourcesDirtyRef = useRef(false);
  const settingsTabLoadSeqRef = useRef({
    settings: 0,
    confirmations: 0,
    orders: 0,
  });
  const [testPhone, setTestPhone] = useState("");
  const [testMessage, setTestMessage] = useState("Mensaje de prueba del asistente.");
  const [testSending, setTestSending] = useState(false);
  const [registerPin, setRegisterPin] = useState("");
  const [whatsappSetupAction, setWhatsappSetupAction] = useState<string | null>(null);

  const [settingsForm, setSettingsForm] = useState<SupportSettingsPayload>({});
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [pendingSettingsPanelChange, setPendingSettingsPanelChange] = useState<{
    tab: SettingsPanelTab;
    integrationDetail: IntegrationDetail | null;
    knowledgeView: "main" | "test" | "editor" | "source" | "media";
  } | null>(null);
  const [pendingSettingsModalClose, setPendingSettingsModalClose] = useState(false);
  const [pendingSettingsModalCloseAction, setPendingSettingsModalCloseAction] = useState<(() => void) | null>(null);
  // Sub-pantalla "Configurar imágenes" abierta dentro de Acciones: se oculta el Guardar global.
  const [actionsImagesScreenOpen, setActionsImagesScreenOpen] = useState(false);

  const [guardianSettings, setGuardianSettings] = useState<GuardianSettings | null>(null);
  const [guardianState, setGuardianState] = useState<GuardianState | null>(null);
  const [guardianRuns, setGuardianRuns] = useState<GuardianRun[]>([]);
  const [guardianMemory, setGuardianMemory] = useState<GuardianMemoryItem[]>([]);
  const [guardianTabLoaded, setGuardianTabLoaded] = useState(false);
  const [guardianTabLoading, setGuardianTabLoading] = useState(false);
  const [guardianProposals, setGuardianProposals] = useState<GuardianProposal[]>([]);
  const [guardianConversations, setGuardianConversations] = useState<GuardianConversation[]>([]);
  const [guardianSuggestionFocusId, setGuardianSuggestionFocusId] = useState<string | null>(null);
  const [guardianSaving, setGuardianSaving] = useState(false);

  const [knowledgeSources, setKnowledgeSources] = useState<KnowledgeSource[]>([]);
  const [knowledgeTabLoading, setKnowledgeTabLoading] = useState(false);
  const [knowledgeTabLoadedOnce, setKnowledgeTabLoadedOnce] = useState(false);
  const [knowledgeForm, setKnowledgeForm] = useState<KnowledgeFormState>(EMPTY_KNOWLEDGE_FORM);
  const [editingKnowledgeSourceId, setEditingKnowledgeSourceId] = useState<number | null>(null);
  const [knowledgeSaving, setKnowledgeSaving] = useState(false);
  const [knowledgeTestMessage, setKnowledgeTestMessage] = useState("");
  const [knowledgeTestContact, setKnowledgeTestContact] = useState("");
  const [knowledgeTestResponse, setKnowledgeTestResponse] = useState<SupportTestChatResponse | null>(null);
  const [knowledgeTestMessages, setKnowledgeTestMessages] = useState<SupportTestChatMessage[]>([]);
  const [knowledgeTestLoading, setKnowledgeTestLoading] = useState(false);
  const [mediaResourceDrafts, setMediaResourceDrafts] = useState<MediaResourceDraft[]>(defaultMediaResourceDrafts);
  const [savedMediaResourceDrafts, setSavedMediaResourceDrafts] = useState<MediaResourceDraft[]>(defaultMediaResourceDrafts);

  // Modo aprendiz: aprobación de respuesta activa + recientes de la conversación abierta.
  const [replyApproval, setReplyApproval] = useState<ReplyApproval | null>(null);
  const [replyApprovalRecent, setReplyApprovalRecent] = useState<ReplyApproval[]>([]);
  const [approvalActionLoading, setApprovalActionLoading] = useState<null | "approve" | "feedback" | "reject">(null);
  const [approvalAwaitingVersion, setApprovalAwaitingVersion] = useState(false);
  const [approvalSendError, setApprovalSendError] = useState<string | null>(null);
  const [approvalForceConfirm, setApprovalForceConfirm] = useState<{ approvalId: number; version: number } | null>(null);

  const [integrationProviders, setIntegrationProviders] = useState<IntegrationProvider[]>([]);
  const [integrationConnections, setIntegrationConnections] = useState<IntegrationConnection[]>([]);
  const [ecommerceDropiActive, setEcommerceDropiActive] = useState(false);
  const [selectedIntegrationConnectionId, setSelectedIntegrationConnectionId] = useState<number | null>(null);
  const [integrationToolsByConnectionId, setIntegrationToolsByConnectionId] = useState<Record<number, IntegrationTool[]>>({});
  const [savedIntegrationToolsByConnectionId, setSavedIntegrationToolsByConnectionId] = useState<Record<number, IntegrationTool[]>>({});
  const [hotmartMappings, setHotmartMappings] = useState<HotmartProductMapping[]>([]);
  const [hotmartAccessCases, setHotmartAccessCases] = useState<HotmartAccessCase[]>([]);
  const [hotmartAccessCasesLoading, setHotmartAccessCasesLoading] = useState(false);
  const [hotmartForm, setHotmartForm] = useState<HotmartFormState>(EMPTY_HOTMART_FORM);
  const [dropiForm, setDropiForm] = useState<DropiFormState>(EMPTY_DROPI_FORM);
  const [integrationLoading, setIntegrationLoading] = useState(false);
  const [integrationSaving, setIntegrationSaving] = useState(false);
  const [integrationActionLoading, setIntegrationActionLoading] = useState<string | null>(null);

  const [confirmationsOverview, setConfirmationsOverview] = useState<ConfirmationsOverview | null>(null);
  const [statesDomain, setStatesDomain] = useState<StateDomain>(initialStatesDomain);
  const [statesView, setStatesView] = useState<StateView>("kanban");
  const [orderStateBoard, setOrderStateBoard] = useState<StateBoard<SupportConfirmation> | null>(null);
  const [contactStateBoard, setContactStateBoard] = useState<StateBoard<ContactStateItem> | null>(null);
  const [statesLoading, setStatesLoading] = useState(false);
  const [metricsSurface, setMetricsSurface] = useState<MetricsSurface>("dashboard");
  const [metricsRangeMode, setMetricsRangeMode] = useState<MetricsRangeMode>("preset");
  const [metricsRangeDays, setMetricsRangeDays] = useState<number>(30);
  const [metricsCustomFrom, setMetricsCustomFrom] = useState("");
  const [metricsCustomTo, setMetricsCustomTo] = useState("");
  const [metricsHealth, setMetricsHealth] = useState<OperationsHealth | null>(null);
  const [metricsOverview, setMetricsOverview] = useState<SupportOverview | null>(null);
  const [metricsLoading, setMetricsLoading] = useState(false);
  const [activeWorkspace, setActiveWorkspace] = useState<MainWorkspace>("chat");

  const selectedConversationFromList = useMemo(() => {
    if (!selectedConversationId) return null;
    const listConversation = conversations.find((conversation) => conversation.id === selectedConversationId);
    if (!listConversation) return selectedConversation?.id === selectedConversationId ? selectedConversation : null;
    if (!selectedConversation || selectedConversation.id !== listConversation.id) return listConversation;
    return mergeConversationRecord(listConversation, selectedConversation);
  }, [conversations, selectedConversation, selectedConversationId]);

  const approvedTemplates = useMemo(
    () => templates.filter((template) => template.status === "APPROVED"),
    [templates]
  );

  // Pickers de acciones (spec Julian): TODOS los estados visibles — PENDING elegible
  // (queda configurado y arranca solo al aprobarse), rechazadas/no-disponibles se ven
  // deshabilitadas con su motivo. Las del Guardián no aplican a acciones.
  const pickerTemplates = useMemo(
    () => templates.filter((template) => templateCategoryTab(template) !== "guardian"),
    [templates]
  );

  const selectedIntegrationConnection = useMemo(
    () => integrationConnections.find((connection) => connection.id === selectedIntegrationConnectionId) || null,
    [integrationConnections, selectedIntegrationConnectionId]
  );
  const integrationTools = useMemo(
    () => (selectedIntegrationConnectionId ? integrationToolsByConnectionId[selectedIntegrationConnectionId] || [] : []),
    [integrationToolsByConnectionId, selectedIntegrationConnectionId]
  );

  const hotmartConnection = useMemo(
    () => integrationConnections.find((connection) => connection.provider_key === "hotmart") || null,
    [integrationConnections]
  );

  const dropiConnection = useMemo(
    () => integrationConnections.find((connection) => connection.provider_key === "dropi") || null,
    [integrationConnections]
  );

  const apiStatus = useMemo(() => whatsappApiStatus(account, health), [account, health]);
  const unreadNotificationsCount = useMemo(
    () => notifications.filter((notification) => notification.status === "unread").length,
    [notifications]
  );
  const activeSettingsTab = useMemo(
    () => settingsTabs.find((tab) => tab.id === settingsPanelTab) || settingsTabs[0],
    [settingsPanelTab]
  );
  const settingsModalTitle = (() => {
    if (settingsPanelTab === "knowledge" && knowledgePanelView === "source") return "Conocimiento";
    if (settingsPanelTab === "knowledge" && knowledgePanelView === "media") return "Recurso multimedia";
    if (settingsPanelTab === "knowledge" && knowledgePanelView === "editor") return "Editando conocimiento";
    if (settingsPanelTab === "knowledge" && knowledgePanelView === "test") return "Probar chat";
    if (settingsPanelTab === "actions") {
      const actionLabels: Record<ActionsConfigSubTab, string> = {
        ventas: "Ventas",
        confirmaciones: "Confirmaciones",
        logistica: "Logística",
        agendamientos: "Agendamientos",
        recuperacion: "Recuperación",
        hotmart: "Hotmart",
        otras: "Otras acciones",
      };
      return `Acciones · ${actionLabels[actionsConfigSubTab]}`;
    }
    if (settingsPanelTab === "integrations" && integrationDetail) {
      if (integrationDetail === "whatsapp") return "WhatsApp";
      if (integrationDetail === "messenger") return "Messenger";
      if (integrationDetail === "instagram") return "Instagram";
      if (integrationDetail === "hotmart") return "Hotmart";
      return "Dropi";
    }
    return activeSettingsTab.label;
  })();

  const openWhatsAppSettings = useCallback(() => {
    setSettingsModalOpen(true);
    setSettingsPanelTab("integrations");
    setIntegrationDetail("whatsapp");
  }, []);

  const openAssistantSettings = useCallback(() => {
    setKnowledgeTabLoading(true);
    setSettingsModalOpen(true);
    setSettingsPanelTab("knowledge");
    setIntegrationDetail(null);
    setKnowledgePanelView("main");
  }, []);

  const handleOpenNotification = useCallback(async (notification: SupportNotification) => {
    const previousNotifications = notifications;
    setNotifications((rows) =>
      rows.map((row) =>
        row.id === notification.id
          ? { ...row, status: "read", read_at: row.read_at || new Date().toISOString() }
          : row
      )
    );
    try {
      if (notification.status === "unread") {
        await chatbotV2Service.markNotificationRead(notification.id);
      }
    } catch (error) {
      console.warn("[ChatbotPage] notification read failed", error);
      setNotifications(previousNotifications);
      toast.error("No se pudo marcar la notificación como leída");
    }

    if (notification.target_type === "conversation" && notification.target_id) {
      const conversationId = Number(notification.target_id);
      if (Number.isFinite(conversationId)) {
        setActiveWorkspace("chat");
        setSelectedConversationId(conversationId);
        setNotificationsOpen(false);
        return;
      }
    }

    if (notification.target_type === "guardian_suggestion") {
      const proposalId =
        notification.target_id ||
        (typeof notification.target_payload?.proposal_id === "string" ? notification.target_payload.proposal_id : null);
      if (proposalId) setGuardianSuggestionFocusId(proposalId);
      setActiveWorkspace("guardian");
      setNotificationsOpen(false);
      return;
    }

    if (notification.target_type === "guardian" || notification.type.includes("guardian")) {
      setActiveWorkspace("guardian");
      setNotificationsOpen(false);
      return;
    }

    setNotificationsOpen(false);
  }, [notifications]);

  const markNotificationsReadBatch = useCallback(
    async (notificationIds: number[]) => {
      if (!notificationIds.length) return;
      const previousNotifications = notifications;
      const readAt = new Date().toISOString();
      setNotifications((rows) =>
        rows.map((row) =>
          notificationIds.includes(row.id)
            ? { ...row, status: "read", read_at: row.read_at || readAt }
            : row
        )
      );
      try {
        await Promise.all(notificationIds.map((id) => chatbotV2Service.markNotificationRead(id)));
      } catch (error) {
        console.warn("[ChatbotPage] batch notification read failed", error);
        setNotifications(previousNotifications);
        toast.error("No se pudieron marcar las notificaciones como leídas");
      }
    },
    [notifications]
  );

  const handleOpenSuggestionGroup = useCallback(
    async (proposalId: string | null, notificationIds: number[]) => {
      await markNotificationsReadBatch(notificationIds);
      if (proposalId) setGuardianSuggestionFocusId(proposalId);
      setActiveWorkspace("guardian");
      setNotificationsOpen(false);
    },
    [markNotificationsReadBatch]
  );

  const handleOpenHumanHelpGroup = useCallback(
    async (notificationIds: number[]) => {
      await markNotificationsReadBatch(notificationIds);
      setActiveWorkspace("chat");
      setConversationHumanHelpFilter("with");
      setNotificationsOpen(false);
    },
    [markNotificationsReadBatch]
  );

  const handleOpenUnansweredGroup = useCallback(() => {
    setActiveWorkspace("chat");
    setConversationUnansweredFilter(true);
    setNotificationsOpen(false);
  }, []);

  const handleOpenReplyApprovals = useCallback(
    async (notificationIds: number[], singleTargetId: string | null) => {
      await markNotificationsReadBatch(notificationIds);
      setActiveWorkspace("chat");
      if (singleTargetId) {
        const conversationId = Number(singleTargetId);
        if (Number.isFinite(conversationId)) {
          setSelectedConversationId(conversationId);
          setNotificationsOpen(false);
          return;
        }
      }
      setConversationPendingApprovalFilter(true);
      setNotificationsOpen(false);
    },
    [markNotificationsReadBatch]
  );

  const loadTemplates = useCallback(async (status = templateStatusFilter) => {
    const rows = await chatbotV2Service.listTemplates(status || undefined);
    setTemplates(Array.isArray(rows) ? rows : []);
  }, [templateStatusFilter]);

  const loadNotifications = useCallback(async () => {
    try {
      const [rows, summary] = await Promise.all([
        chatbotV2Service.listNotifications({ limit: 80 }),
        // El summary trae los counts por estado real. Si falla, conservamos el valor previo.
        chatbotV2Service.getNotificationsSummary().catch((error) => {
          console.warn("[ChatbotPage] notifications summary load failed", error);
          return null;
        }),
      ]);
      setNotifications(rows);
      if (summary) setNotificationsSummary(summary);
    } catch (error) {
      console.warn("[ChatbotPage] notifications load failed", error);
    }
  }, []);

  const loadConversations = useCallback(async () => {
    invalidateConversations();
    try {
      const { overviewResult, healthResult } = await refreshConversationsOverview();
      setOverview(overviewResult);
      setHealth(healthResult);
    } catch (error) {
      console.warn("[ChatbotPage] conversations overview refresh failed", error);
    }
  }, [invalidateConversations, refreshConversationsOverview]);

  const loadCore = useCallback(async (showSpinner = false) => {
    if (coreLoadInFlightRef.current) return; // dedup: evita tormentas de cargas concurrentes del core
    coreLoadInFlightRef.current = true;
    if (showSpinner) setCoreLoading(true);
    setPageError(null);
    try {
      const [accountResult, settingsResult, automationRows, mediaResourcesResult] =
        await Promise.all([
          chatbotV2Service.getWhatsAppAccount(),
          chatbotV2Service.getSettings(),
          chatbotV2Service.listSupportAutomations(),
          chatbotV2Service.listSupportMediaResources().catch(() => null),
        ]);

      setAccount(accountResult);
      setSettings(settingsResult);
      invalidateConversations();
      // Métricas secundarias (overview/operations-health): NO deben bloquear el render ni
      // tumbar toda la página si el backend está lento. Se cargan en segundo plano y degradan solo.
      void chatbotV2Service
        .getOverview()
        .then(setOverview)
        .catch((err) => console.warn("[ChatbotPage] overview load failed", err));
      void chatbotV2Service
        .getOperationsHealth()
        .then(setHealth)
        .catch((err) => console.warn("[ChatbotPage] operations-health load failed", err));
      const loadedAutomationDrafts = automationDraftsFromRows(automationRows, settingsResult);
      const loadedMediaDrafts = mediaResourceDraftsFromApi(mediaResourcesResult, settingsResult);
      if (!settingsModalOpenRef.current || !(automationDraftsDirtyRef.current || automationDraftsTouchedRef.current)) {
        setAutomationDrafts(loadedAutomationDrafts);
        setSavedAutomationDrafts(loadedAutomationDrafts);
        setAutomationDraftsTouched(false);
        automationDraftsTouchedRef.current = false;
      }
      if (!settingsModalOpenRef.current || !mediaResourcesDirtyRef.current) {
        setMediaResourceDrafts(loadedMediaDrafts);
        setSavedMediaResourceDrafts(loadedMediaDrafts);
      }
      setAccountForm({
        phone_number_id: accountResult.phone_number_id || "",
        waba_id: accountResult.waba_id || "",
        display_name: accountResult.display_name || "",
        access_token: "",
        app_secret: "",
        is_active: accountResult.is_active,
        rotate_verify_token: false,
      });
      if (!settingsModalOpenRef.current || !settingsFormDirtyRef.current) {
        setSettingsForm(settingsToForm(settingsResult));
      }
      setDefaultTemplates({
        confirmation_template_name: accountResult.default_templates?.confirmation_template_name || "",
        reminder_template_name: accountResult.default_templates?.reminder_template_name || "",
        fallback_template_name: accountResult.default_templates?.fallback_template_name || "",
        cart_recovery_template_name: accountResult.default_templates?.cart_recovery_template_name || "",
        order_confirmed_template_name: accountResult.default_templates?.order_confirmed_template_name || "",
      });
    } catch (error) {
      const message = getErrorMessage(error);
      setPageError(message);
      toast.error(message);
    } finally {
      coreLoadInFlightRef.current = false;
      setInitialLoading(false);
      setCoreLoading(false);
    }
  }, [invalidateConversations]);

  const loadConversationDetails = useCallback(async (
    conversationId: number,
    options: { background?: boolean } = {}
  ) => {
    const hasCurrentMessages = selectedConversationRef.current?.id === conversationId && messagesRef.current.length > 0;
    const showLoading = !options.background && !hasCurrentMessages;
    if (showLoading) setConversationLoading(true);
    try {
      const [conversation, messageRows, eventRows, deliveryRows, approvalResult] = await Promise.all([
        chatbotV2Service.getConversation(conversationId),
        chatbotV2Service.listMessages(conversationId, { limit: 80 }),
        chatbotV2Service.listEvents({ conversation_id: conversationId, limit: 25 }),
        chatbotV2Service.listDeliveryLogs({ conversation_id: conversationId, limit: 25 }),
        chatbotV2Service.getConversationReplyApproval(conversationId),
      ]);
      if (selectedConversationIdRef.current !== conversationId) return;
      const mergedConversation =
        selectedConversationRef.current?.id === conversation.id
          ? mergeConversationRecord(selectedConversationRef.current, conversation)
          : conversation;
      setSelectedConversation(mergedConversation);
      setReplyApproval(approvalResult.active);
      setReplyApprovalRecent(approvalResult.recent);
      // B-14: al abrir/refrescar una conversacion, la LISTA del sidebar tambien se refresca
      // (fila y detalle divergian cuando el realtime perdia eventos; conexion viva-pero-sorda).
      if (!options.background) {
        void loadConversations();
      }
      if (shouldClearRetryingTraceForConversation(mergedConversation)) {
        setRetryingTraceId((prevTrace) => {
          const runtime = getConversationAiRuntime(mergedConversation);
          if (prevTrace && runtime?.trace_id && runtime.trace_id !== prevTrace) return prevTrace;
          return null;
        });
        setRetryingConversationId((prevId) => (prevId === conversation.id ? null : prevId));
      }
      setContactForm(contactToForm(conversation.contact));
      setMessages((current) => {
        const next = mergeConversationMessageRows(current, messageRows, conversation.id);
        return rowsEqualByJson(current, next) ? current : next;
      });
      if (!options.background) setMessagesHasMore(messageRows.length === 80);
      setRowsIfChanged(setConversationEvents, eventRows);
      setRowsIfChanged(setConversationDeliveryLogs, deliveryRows);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      if (showLoading) setConversationLoading(false);
    }
  }, [loadConversations]);

  const handleApproveReply = useCallback(
    async (version: number, force = false) => {
      const approvalId = replyApproval?.id;
      if (!approvalId) return;
      setApprovalActionLoading("approve");
      setApprovalSendError(null);
      try {
        const result = await chatbotV2Service.approveReplyApproval(approvalId, { version, force });
        setApprovalForceConfirm(null);
        if (result.status === "send_failed") {
          const sendError = (result as { error?: string | null }).error;
          setApprovalSendError(sendError ? String(sendError) : null);
          toast.error("No se pudo enviar la respuesta");
        }
      } catch (error) {
        const conflict = getReplyApprovalConflict(error);
        if (conflict === "customer_replied") {
          setApprovalForceConfirm({ approvalId, version });
        } else if (conflict === "window_closed") {
          toast.error("La ventana de 24h se cerró");
        } else if (conflict === "invalid_status" || conflict === "version_not_found") {
          // Refetch silencioso: el estado real llega abajo en el finally.
        } else {
          toast.error(getErrorMessage(error));
        }
      } finally {
        setApprovalActionLoading(null);
        const selectedId = selectedConversationIdRef.current;
        if (selectedId) void loadConversationDetails(selectedId, { background: true });
      }
    },
    [loadConversationDetails, replyApproval?.id]
  );

  const handleSendReplyFeedback = useCallback(
    async (text: string): Promise<boolean> => {
      const approvalId = replyApproval?.id;
      if (!approvalId) return false;
      setApprovalActionLoading("feedback");
      try {
        await chatbotV2Service.sendReplyApprovalFeedback(approvalId, text);
        setApprovalAwaitingVersion(true);
        toast.success("Preparando la nueva versión…");
        const selectedId = selectedConversationIdRef.current;
        if (selectedId) void loadConversationDetails(selectedId, { background: true });
        return true;
      } catch (error) {
        toast.error(getErrorMessage(error));
        return false;
      } finally {
        setApprovalActionLoading(null);
      }
    },
    [loadConversationDetails, replyApproval?.id]
  );

  const handleRejectReply = useCallback(
    async (reason: string): Promise<boolean> => {
      const approvalId = replyApproval?.id;
      if (!approvalId) return false;
      setApprovalActionLoading("reject");
      try {
        const result = await chatbotV2Service.rejectReplyApproval(approvalId, reason || undefined);
        toast.success(
          result.reversal === "started"
            ? "Rechazada — la IA está revirtiendo las acciones"
            : "Rechazada"
        );
        const selectedId = selectedConversationIdRef.current;
        if (selectedId) void loadConversationDetails(selectedId, { background: true });
        return true;
      } catch (error) {
        toast.error(getErrorMessage(error));
        return false;
      } finally {
        setApprovalActionLoading(null);
      }
    },
    [loadConversationDetails, replyApproval?.id]
  );

  const loadOlderConversationMessages = useCallback(async () => {
    const conversationId = selectedConversationIdRef.current;
    if (!conversationId || messagesLoadingOlder || !messagesHasMore) return;
    const oldest = [...messagesRef.current].sort((a, b) => {
      const timeDiff = Date.parse(a.created_at || "") - Date.parse(b.created_at || "");
      return timeDiff || a.id - b.id;
    })[0];
    if (!oldest?.created_at) {
      setMessagesHasMore(false);
      return;
    }
    setMessagesLoadingOlder(true);
    try {
      const olderRows = await chatbotV2Service.listMessages(conversationId, {
        limit: 80,
        before: oldest.created_at,
        before_id: oldest.id,
      });
      if (selectedConversationIdRef.current !== conversationId) return;
      setMessages((current) => mergeConversationMessageRows(current, olderRows, conversationId));
      setMessagesHasMore(olderRows.length === 80);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setMessagesLoadingOlder(false);
    }
  }, [messagesHasMore, messagesLoadingOlder]);

  const handleOpenContactConversationFromStates = useCallback((conversationId: number | null | undefined) => {
    if (!conversationId) {
      toast.error("Este contacto todavía no tiene una conversación para abrir.");
      return;
    }
    setConversationBucketFilters(["open", "resolved"]);
    setActiveWorkspace("chat");
    setSelectedConversationId(conversationId);
    void loadConversationDetails(conversationId);
  }, [loadConversationDetails]);

  const loadSettingsTab = useCallback(async (options?: { force?: boolean }) => {
    const seq = ++settingsTabLoadSeqRef.current.settings;
    try {
      const [settingsResult, automationRows, mediaResourcesResult] = await Promise.all([
        chatbotV2Service.getSettings(),
        chatbotV2Service.listSupportAutomations(),
        chatbotV2Service.listSupportMediaResources().catch(() => null),
      ]);
      if (seq !== settingsTabLoadSeqRef.current.settings) return;
      const settingsFormResult = settingsToForm(settingsResult);
      const loadedAutomationDrafts = automationDraftsFromRows(automationRows, settingsResult);
      const loadedMediaDrafts = mediaResourceDraftsFromApi(mediaResourcesResult, settingsResult);
      setSettings(settingsResult);
      if (options?.force || !settingsFormDirtyRef.current) {
        setSettingsForm(settingsFormResult);
      }
      if (options?.force || !(automationDraftsDirtyRef.current || automationDraftsTouchedRef.current)) {
        setAutomationDrafts(loadedAutomationDrafts);
        setSavedAutomationDrafts(loadedAutomationDrafts);
        setAutomationDraftsTouched(false);
        automationDraftsTouchedRef.current = false;
      }
      if (options?.force || !mediaResourcesDirtyRef.current) {
        setMediaResourceDrafts(loadedMediaDrafts);
        setSavedMediaResourceDrafts(loadedMediaDrafts);
      }
      const metadata = isRecord(settingsFormResult.metadata_json) ? settingsFormResult.metadata_json : {};
      const loadedRecoveryDrafts = cartRecoveryFlowDraftsFromMetadata(metadata);
      const loadedSalesSettings = salesActionsSettingsFromMetadata(metadata);
      if (options?.force || serializeCartRecoveryFlowDrafts(recoveryFlowDrafts) === serializeCartRecoveryFlowDrafts(savedRecoveryFlowDrafts)) {
        setRecoveryFlowDrafts(loadedRecoveryDrafts);
        setSavedRecoveryFlowDrafts(loadedRecoveryDrafts);
      }
      if (options?.force || JSON.stringify(salesActionsSettings) === JSON.stringify(savedSalesActionsSettings)) {
        setSalesActionsSettings(loadedSalesSettings);
        setSavedSalesActionsSettings(loadedSalesSettings);
      }
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  }, []);

  const loadGuardianTab = useCallback(async () => {
    const isFirstLoad = !guardianTabLoaded;
    if (isFirstLoad) setGuardianTabLoading(true);
    try {
      const [guardianStateResult, pendingProposalRows, recentProposalRows, conversationRows] = await Promise.all([
        chatbotV2Service.getGuardianState(),
        chatbotV2Service.listGuardianProposals({ status: "pending", limit: 200 }),
        chatbotV2Service.listGuardianProposals({ limit: 80 }),
        chatbotV2Service.listGuardianConversations(),
      ]);
      setGuardianState(guardianStateResult);
      setGuardianSettings(guardianStateResult.settings);
      setGuardianRuns(guardianStateResult.recent_runs);
      const proposalsById = new Map(
        [...pendingProposalRows, ...recentProposalRows].map((proposal) => [proposal.id, proposal])
      );
      setGuardianProposals([...proposalsById.values()]);
      setGuardianConversations(conversationRows);
      if (isFirstLoad) setGuardianTabLoaded(true);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      if (isFirstLoad) setGuardianTabLoading(false);
    }
  }, [guardianTabLoaded]);

  const loadKnowledgeTab = useCallback(async () => {
    setKnowledgeTabLoading(true);
    try {
      const sources = await chatbotV2Service.listKnowledgeSources({ active_only: false, limit: 200 });
      setKnowledgeSources(Array.isArray(sources) ? sources : []);
      setKnowledgeTabLoadedOnce(true);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setKnowledgeTabLoading(false);
    }
  }, []);

  const loadIntegrationsTab = useCallback(async (connectionId?: number | null) => {
    setIntegrationLoading(true);
    try {
      const [providers, connections] = await Promise.all([
        chatbotV2Service.listIntegrationProviders(),
        chatbotV2Service.listIntegrationConnections(),
      ]);
      setIntegrationProviders(providers);
      setIntegrationConnections(connections);
      // Dropi has a single source of truth: the ecommerce account integration. The chatbot tab
      // derives "connected" from it so an owner already connected in Integraciones is not asked to
      // connect again here (best-effort; on failure the previous value is kept).
      const { res: ecommerceDropi } = await handleApi(getDropiIntegrations());
      setEcommerceDropiActive(Boolean(ecommerceDropi?.is_active));
      const hotmartConnectionId = connections.find((connection) => connection.provider_key === "hotmart")?.id ?? null;
      const selectedId = connectionId ?? selectedIntegrationConnectionId ?? hotmartConnectionId ?? connections[0]?.id ?? null;
      setSelectedIntegrationConnectionId(selectedId);
      if (connections.length) {
        const [toolEntries, mappings] = await Promise.all([
          Promise.all(
            connections.map(async (connection) => [
              connection.id,
              await chatbotV2Service.listIntegrationTools(connection.id),
            ] as const)
          ),
          hotmartConnectionId
            ? chatbotV2Service.listHotmartProductMappings(hotmartConnectionId)
            : Promise.resolve([]),
        ]);
        const toolsByConnectionId = Object.fromEntries(toolEntries) as Record<number, IntegrationTool[]>;
        setIntegrationToolsByConnectionId(toolsByConnectionId);
        setSavedIntegrationToolsByConnectionId(toolsByConnectionId);
        setHotmartMappings(mappings);
      } else {
        setIntegrationToolsByConnectionId({});
        setSavedIntegrationToolsByConnectionId({});
        setHotmartMappings([]);
      }
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIntegrationLoading(false);
    }
  }, [selectedIntegrationConnectionId]);

  const loadHotmartAccessCases = useCallback(async () => {
    if (!hotmartConnection?.id) {
      setHotmartAccessCases([]);
      return;
    }
    setHotmartAccessCasesLoading(true);
    try {
      const rows = await chatbotV2Service.listHotmartAccessCases({ limit: 80 });
      setHotmartAccessCases(rows);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setHotmartAccessCasesLoading(false);
    }
  }, [hotmartConnection?.id]);

  const loadStatesWorkspace = useCallback(async (domain: StateDomain = statesDomain) => {
    setStatesLoading(true);
    try {
      const pageSize = domain === "orders" ? 500 : 200;
      const pages: StateBoard<SupportConfirmation | ContactStateItem>[] = [];
      for (let offset = 0; ; offset += pageSize) {
        const board = domain === "orders"
          ? await chatbotV2Service.getOrderStates({ limit: pageSize, offset })
          : await chatbotV2Service.getContactStates({ limit: pageSize, offset });
        pages.push(board as StateBoard<SupportConfirmation | ContactStateItem>);
        if (board.rows.length < pageSize) break;
      }
      const first = pages[0];
      if (!first) return;
      const mergedRows = pages.flatMap((page) => page.rows);
      const mergedColumns = first.columns.map((column) => {
        const items = pages.flatMap((page) => page.columns.find((candidate) => candidate.id === column.id)?.items || []);
        return { ...column, items, count: items.length };
      });
      const merged = { ...first, rows: mergedRows, columns: mergedColumns };
      if (domain === "orders") setOrderStateBoard(merged as StateBoard<SupportConfirmation>);
      else setContactStateBoard(merged as StateBoard<ContactStateItem>);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setStatesLoading(false);
    }
  }, [statesDomain]);

  const loadConfirmationsTab = useCallback(async (options?: { force?: boolean }) => {
    const seq = ++settingsTabLoadSeqRef.current.confirmations;
    try {
      const [summary, flows] = await Promise.all([
        chatbotV2Service.getConfirmationsOverview(),
        chatbotV2Service.listCommerceFlows("confirmation"),
      ]);
      if (seq !== settingsTabLoadSeqRef.current.confirmations) return;
      if (!options?.force && confirmationFlowsDirtyRef.current) return;
      setConfirmationsOverview(summary);
      const loaded = normalizeCommerceFlowDrafts(flows);
      setConfirmationFlowDrafts(loaded);
      setSavedConfirmationFlowDrafts(loaded);
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  }, []);

  const loadOrdersTab = useCallback(async (options?: { force?: boolean }) => {
    const seq = ++settingsTabLoadSeqRef.current.orders;
    try {
      const flows = await chatbotV2Service.listCommerceFlows("order_capture");
      if (seq !== settingsTabLoadSeqRef.current.orders) return;
      if (!options?.force && orderFlowsDirtyRef.current) return;
      const loaded = normalizeCommerceFlowDrafts(flows);
      setOrderFlowDrafts(loaded);
      setSavedOrderFlowDrafts(loaded);
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  }, []);

  const loadMetricsDashboard = useCallback(async (options?: { days?: number; customFrom?: string; customTo?: string }) => {
    setMetricsLoading(true);
    try {
      const since = options?.customFrom
        ? metricsSinceIsoFromDate(options.customFrom)
        : metricsSinceIso(options?.days ?? metricsRangeDays);
      const until = options?.customTo ? metricsUntilIsoFromDate(options.customTo) : undefined;
      // Cada métrica degrada por separado: un endpoint lento/caído no debe tumbar todo el panel.
      const [healthResult, overviewResult, confirmationsSummary] = await Promise.all([
        chatbotV2Service.getOperationsHealth(since, until).catch(() => null),
        chatbotV2Service.getOverview(since, until).catch(() => null),
        chatbotV2Service.getConfirmationsOverview().catch(() => null),
      ]);
      if (healthResult) setMetricsHealth(healthResult);
      if (overviewResult) setMetricsOverview(overviewResult);
      if (confirmationsSummary) setConfirmationsOverview(confirmationsSummary);
      if (!healthResult && !overviewResult) {
        toast.error("No pudimos cargar las métricas ahora. Reintenta en un momento.");
      }
    } finally {
      setMetricsLoading(false);
    }
  }, [metricsRangeDays]);

  const loadContactOrders = useCallback(async (contactId: number | null | undefined) => {
    if (!contactId) {
      setContactOrders([]);
      return;
    }
    setContactOrdersLoading(true);
    try {
      const rows = await chatbotV2Service.listContactOrders(contactId, 12);
      setContactOrders(rows);
    } catch (error) {
      toast.error(getErrorMessage(error));
      setContactOrders([]);
    } finally {
      setContactOrdersLoading(false);
    }
  }, []);

  const updateOrderState = useCallback(async (confirmationId: number, payload: { status?: string | null; tags?: string[] }) => {
    try {
      await chatbotV2Service.updateOrderState(confirmationId, payload);
      await loadStatesWorkspace("orders");
      const contactId = selectedConversationFromList?.contact?.id;
      if (contactId) void loadContactOrders(contactId);
      toast.success("Orden actualizada");
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  }, [loadContactOrders, loadStatesWorkspace, selectedConversationFromList?.contact?.id]);

  const updateContactState = useCallback(async (item: ContactStateItem, state: string) => {
    try {
      const existingTags = item.contact.tags || [];
      const nextTags = [
        ...existingTags.filter((tag) => !customerStateTagSet.has(tag)),
        state,
      ];
      const updatedContact = await chatbotV2Service.updateContact(item.id, { tags: nextTags });
      await loadStatesWorkspace("contacts");
      const currentConversation = selectedConversationFromList;
      if (currentConversation?.contact?.id === item.id) {
        const updatedConversation = { ...currentConversation, contact: updatedContact };
        patchConversation(updatedConversation);
        setSelectedConversation(updatedConversation);
        setContactForm(contactToForm(updatedContact));
      }
      toast.success("Estado de contacto actualizado");
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  }, [loadStatesWorkspace, patchConversation, selectedConversationFromList]);

  useEffect(() => {
    selectedConversationIdRef.current = selectedConversationId;
  }, [selectedConversationId]);

  useEffect(() => {
    if (selectedConversationId) return;
    setSelectedConversation(null);
    setMessages([]);
    setMessagesHasMore(false);
    setConversationEvents([]);
    setConversationDeliveryLogs([]);
    setContactOrders([]);
    setContactForm(EMPTY_CONTACT_FORM);
    setPrivateNote("");
    setRetryInstruction("");
  }, [selectedConversationId]);

  // Modo aprendiz: al cambiar de conversación limpiamos la aprobación y su UI transitoria;
  // loadConversationDetails la vuelve a poblar para la conversación abierta.
  useEffect(() => {
    setReplyApproval(null);
    setReplyApprovalRecent([]);
    setApprovalAwaitingVersion(false);
    setApprovalSendError(null);
    setApprovalForceConfirm(null);
  }, [selectedConversationId]);

  // La bandera "preparando versión" (optimista tras el 202 del feedback) se limpia cuando
  // llega una versión nueva por SSE/refetch (o al cambiar de aprobación).
  useEffect(() => {
    setApprovalAwaitingVersion(false);
  }, [replyApproval?.id, replyApproval?.versions?.length]);

  useEffect(() => {
    selectedConversationRef.current = selectedConversation;
  }, [selectedConversation]);

  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  useEffect(() => {
    activeWorkspaceRef.current = activeWorkspace;
  }, [activeWorkspace]);

  useEffect(() => {
    statesDomainRef.current = statesDomain;
    try {
      window.localStorage.setItem("fluxi.chatbot.statesDomain", statesDomain);
    } catch {
      // Local storage can fail in private contexts; the current session state still works.
    }
  }, [statesDomain]);

  useEffect(() => {
    metricsSurfaceRef.current = metricsSurface;
  }, [metricsSurface]);

  useEffect(() => {
    if (coreLoadedRef.current) return;
    coreLoadedRef.current = true;
    void loadCore();
  }, [loadCore]);

  useEffect(() => {
    if (initialLoading) return;
    void loadNotifications();
  }, [initialLoading, loadNotifications]);

  useEffect(() => {
    if (initialLoading || conversationsLoading) return;
    setSelectedConversationId((current) => {
      if (current && (conversations.some((row) => row.id === current) || selectedConversationRef.current?.id === current)) {
        return current;
      }
      return conversations[0]?.id ?? null;
    });
  }, [conversations, conversationsLoading, initialLoading]);

  useEffect(() => {
    if (!selectedConversationId) return;
    void loadConversationDetails(selectedConversationId);
  }, [loadConversationDetails, selectedConversationId]);

  useEffect(() => {
    void loadContactOrders(selectedConversationFromList?.contact?.id);
  }, [loadContactOrders, selectedConversationFromList?.contact?.id]);

  useEffect(() => {
    const contactId = selectedConversationFromList?.contact?.id;
    if (!contactId) {
      setPersonView(null);
      return;
    }
    let cancelled = false;
    void chatbotV2Service
      .getContactPersonView(contactId)
      .then((view) => {
        if (!cancelled) setPersonView(view);
      })
      .catch(() => {
        if (!cancelled) setPersonView(null);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedConversationFromList?.contact?.id, personViewRefreshKey]);

  useEffect(() => {
    if (activeWorkspace !== "states") return;
    void loadOrdersTab();
    void loadConfirmationsTab();
  }, [activeWorkspace, loadConfirmationsTab, loadOrdersTab]);

  useEffect(() => {
    if (activeWorkspace !== "states") return;
    void loadOrdersTab();
    void loadConfirmationsTab();
  }, [activeWorkspace, loadConfirmationsTab, loadOrdersTab]);

  useEffect(() => {
    if (activeWorkspace !== "states") return;
    if (metricsSurface === "dashboard") {
      if (metricsRangeMode === "custom" && metricsCustomFrom) {
        void loadMetricsDashboard({ customFrom: metricsCustomFrom });
      } else {
        void loadMetricsDashboard({ days: metricsRangeDays });
      }
      return;
    }
    void loadStatesWorkspace(statesDomain);
  }, [
    activeWorkspace,
    loadMetricsDashboard,
    loadStatesWorkspace,
    metricsCustomFrom,
    metricsRangeDays,
    metricsRangeMode,
    metricsSurface,
    statesDomain,
  ]);

  useEffect(() => {
    if (activeWorkspace === "states") {
      setMetricsSurface("dashboard");
    }
  }, [activeWorkspace]);

  useEffect(() => {
    if (activeWorkspace !== "guardian") return;
    void loadGuardianTab();
  }, [activeWorkspace, loadGuardianTab]);

  useEffect(() => {
    activeChatbotRealtimeAbortController?.abort();
    const abortController = new AbortController();
    activeChatbotRealtimeAbortController = abortController;
    let fallbackInterval: number | null = null;

    const scheduleConversationListRefresh = (delay = 350) => {
      if (realtimeListTimerRef.current) {
        window.clearTimeout(realtimeListTimerRef.current);
      }
      realtimeListTimerRef.current = window.setTimeout(() => {
        void loadConversations();
      }, delay);
    };

    const scheduleConversationDetailsRefresh = (conversationId: number, delay = 650) => {
      if (realtimeDetailsTimerRef.current) {
        window.clearTimeout(realtimeDetailsTimerRef.current);
      }
      realtimeDetailsTimerRef.current = window.setTimeout(() => {
        void loadConversationDetails(conversationId, { background: true });
      }, delay);
    };
    const scheduleStatesRefresh = (delay = 350) => {
      if (realtimeStatesTimerRef.current) {
        window.clearTimeout(realtimeStatesTimerRef.current);
      }
      realtimeStatesTimerRef.current = window.setTimeout(() => {
        void loadStatesWorkspace(statesDomainRef.current);
      }, delay);
    };

    const scheduleNotificationsRefresh = (delay = 250) => {
      if (realtimeNotificationsTimerRef.current) {
        window.clearTimeout(realtimeNotificationsTimerRef.current);
      }
      realtimeNotificationsTimerRef.current = window.setTimeout(() => {
        void loadNotifications();
      }, delay);
    };

    const scheduleGuardianRefresh = (delay = 350) => {
      if (realtimeGuardianTimerRef.current) {
        window.clearTimeout(realtimeGuardianTimerRef.current);
      }
      realtimeGuardianTimerRef.current = window.setTimeout(() => {
        void loadGuardianTab();
      }, delay);
    };

    const refreshFromRealtime = (event: ChatbotRealtimeEvent) => {
      if (shouldIgnoreRealtimeEvent(event, realtimeSeenEventsRef.current)) return;

      const selectedId = selectedConversationIdRef.current;
      const eventConversationId = event.conversation_id || null;
      const affectsSelectedConversation = Boolean(
        selectedId && eventConversationId && eventConversationId === selectedId
      );

      if (inboxRealtimeEvents.has(event.type)) {
        scheduleConversationListRefresh(affectsSelectedConversation ? 500 : 250);
      }

      if (selectedId && affectsSelectedConversation && conversationDetailRealtimeEvents.has(event.type)) {
        if (
          event.type === "ai.response_part_sent" ||
          event.type === "message.created" ||
          event.type === "ai.response_failed" ||
          event.type === "ai.response_cancelled"
        ) {
          setRetryingTraceId(null);
          setRetryingConversationId(null);
        }
        if (event.type === "message.created" || event.type === "message.received") {
          scheduleConversationDetailsRefresh(selectedId, 650);
        } else {
          scheduleConversationDetailsRefresh(selectedId, event.type === "ai.response_started" ? 500 : 650);
        }
      }

      if (statesRealtimeEvents.has(event.type) && activeWorkspaceRef.current === "states" && metricsSurfaceRef.current !== "dashboard") {
        scheduleStatesRefresh();
      }

      if (notificationsRealtimeEvents.has(event.type)) {
        scheduleNotificationsRefresh();
      }

      if (guardianRealtimeEvents.has(event.type) && activeWorkspaceRef.current === "guardian") {
        scheduleGuardianRefresh();
      }
    };

    const startFallbackPolling = () => {
      if (fallbackInterval) return;
      fallbackInterval = window.setInterval(() => {
        void loadConversations();
        const selectedId = selectedConversationIdRef.current;
        if (selectedId) {
          void loadConversationDetails(selectedId, { background: true });
        }
      }, 8000);
    };

    const connectRealtimeWithRetry = async () => {
      let retryDelayMs = 1000;
      let consecutiveFailures = 0;
      while (!abortController.signal.aborted) {
        try {
          await connectChatbotRealtime(abortController.signal, refreshFromRealtime);
          if (abortController.signal.aborted) return;
          retryDelayMs = 1000;
          consecutiveFailures = 0;
        } catch (error) {
          if (abortController.signal.aborted) return;
          consecutiveFailures += 1;
          if (consecutiveFailures >= 4) {
            console.warn("[ChatbotPage] realtime stream unavailable, using polling fallback", error);
            startFallbackPolling();
            return;
          }
        }
        await new Promise<void>((resolve) => {
          window.setTimeout(resolve, retryDelayMs);
        });
        retryDelayMs = Math.min(retryDelayMs * 2, 15000);
      }
    };

    void connectRealtimeWithRetry();

    // B-14: refresco periodico SUAVE de la lista mientras el tab esta visible. El fallback de
    // arriba solo cubre fallos de CONEXION del stream; una conexion viva-pero-sorda (keepalives
    // fluyen, eventos no — p.ej. el breaker del publish descartando por rafagas) dejaba el
    // sidebar congelado indefinidamente. Costo: 1 request liviano cada 75s con el tab abierto.
    const softRefreshInterval = window.setInterval(() => {
      if (document.visibilityState === "visible") {
        void loadConversations();
        // I-3: tambien la conversacion ABIERTA (estados de entrega); un delivery.updated
        // perdido dejaba el chulito gris hasta que llegara otro mensaje.
        const selectedId = selectedConversationIdRef.current;
        if (selectedId) void loadConversationDetails(selectedId, { background: true });
      }
    }, 75000);

    return () => {
      abortController.abort();
      if (activeChatbotRealtimeAbortController === abortController) {
        activeChatbotRealtimeAbortController = null;
      }
      window.clearInterval(softRefreshInterval);
      if (fallbackInterval) window.clearInterval(fallbackInterval);
      if (realtimeListTimerRef.current) window.clearTimeout(realtimeListTimerRef.current);
      if (realtimeDetailsTimerRef.current) window.clearTimeout(realtimeDetailsTimerRef.current);
      if (realtimeStatesTimerRef.current) window.clearTimeout(realtimeStatesTimerRef.current);
      if (realtimeNotificationsTimerRef.current) window.clearTimeout(realtimeNotificationsTimerRef.current);
      if (realtimeGuardianTimerRef.current) window.clearTimeout(realtimeGuardianTimerRef.current);
    };
  }, [loadConversations, loadConversationDetails, loadGuardianTab, loadNotifications, loadStatesWorkspace]);

  useEffect(() => {
    if (!settingsModalOpen) return;
    if (settingsPanelTab === "knowledge") {
      void loadSettingsTab();
      void loadKnowledgeTab();
    }
    if (settingsPanelTab === "templates") {
      void loadTemplates();
    }
    if (settingsPanelTab === "integrations") {
      void loadIntegrationsTab();
    }
    if (settingsPanelTab === "actions") {
      void loadSettingsTab();
      void loadIntegrationsTab();
      void loadTemplates("APPROVED");
      void loadConfirmationsTab();
      void loadOrdersTab();
      void useWebsitesStore.getState().initialize();
    }
  }, [
    settingsModalOpen,
    settingsPanelTab,
    loadConfirmationsTab,
    loadIntegrationsTab,
    loadKnowledgeTab,
    loadOrdersTab,
    loadSettingsTab,
    loadTemplates,
  ]);

  useEffect(() => {
    if (!settingsModalOpen || settingsPanelTab !== "actions" || actionsConfigSubTab !== "hotmart") return;
    void loadHotmartAccessCases();
  }, [settingsModalOpen, settingsPanelTab, actionsConfigSubTab, loadHotmartAccessCases]);

  const handleCopy = async (value: string | null | undefined, label: string) => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      toast.success(`${label} copiado`);
    } catch {
      toast.error("No se pudo copiar");
    }
  };

  const handleSaveAccount = async (formOverride?: AccountFormState) => {
    setAccountSaving(true);
    try {
      const form = formOverride || accountForm;
      const payload = {
        phone_number_id: form.phone_number_id.trim(),
        waba_id: form.waba_id.trim(),
        display_name: form.display_name.trim() || undefined,
        is_active: form.is_active,
        rotate_verify_token: form.rotate_verify_token,
        ...(form.access_token.trim() ? { access_token: form.access_token.trim() } : {}),
        ...(form.app_secret.trim() ? { app_secret: form.app_secret.trim() } : {}),
      };
      const saved = await chatbotV2Service.saveWhatsAppAccount(payload);
      setAccount(saved);
      setAccountForm({
        phone_number_id: saved.phone_number_id || "",
        waba_id: saved.waba_id || "",
        display_name: saved.display_name || "",
        access_token: "",
        app_secret: "",
        is_active: saved.is_active,
        rotate_verify_token: false,
      });
      toast.success("WhatsApp guardado");
      void loadCore(true);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setAccountSaving(false);
    }
  };

  const hasUnsavedModalDrafts = () => settingsModalDirty;

  const requestIntegrationSave = (target: "whatsapp" | "hotmart") => {
    if (hasUnsavedModalDrafts()) {
      setPendingIntegrationSave(target);
      return;
    }
    if (target === "whatsapp") void handleSaveAccount();
    else void handleSaveHotmartConnection();
  };

  const confirmPendingIntegrationSave = () => {
    const target = pendingIntegrationSave;
    setPendingIntegrationSave(null);
    if (target === "whatsapp") void handleSaveAccount();
    else if (target === "hotmart") void handleSaveHotmartConnection();
  };

  const handleRotateVerifyToken = async () => {
    setAccountSaving(true);
    try {
      const saved = await chatbotV2Service.rotateVerifyToken();
      setAccount(saved);
      toast.success("Verify token rotado");
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setAccountSaving(false);
    }
  };

  const handleDiagnose = async () => {
    setAccountSaving(true);
    try {
      const result = await chatbotV2Service.diagnoseWhatsApp();
      setDiagnoseResult(result);
      toast.success("Diagnóstico actualizado");
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setAccountSaving(false);
    }
  };

  const handleSyncTemplates = async () => {
    setTemplateSaving(true);
    try {
      await chatbotV2Service.syncTemplates();
      await loadTemplates();
      toast.success("Templates sincronizados");
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setTemplateSaving(false);
    }
  };

  const handleRegisterWhatsAppNumber = async () => {
    const pin = registerPin.trim();
    if (!pin) {
      toast.error("Ingresa el PIN de 6 dígitos para registrar el número");
      return;
    }
    setWhatsappSetupAction("register");
    try {
      await chatbotV2Service.registerWhatsAppNumber({ pin });
      setRegisterPin("");
      toast.success("Número registrado en Cloud API");
      await loadCore(true);
      await handleDiagnose();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setWhatsappSetupAction(null);
    }
  };

  const handleSubscribeWhatsAppWebhooks = async () => {
    setWhatsappSetupAction("subscribe");
    try {
      await chatbotV2Service.subscribeWhatsAppWebhooks();
      toast.success("WABA suscrito a webhooks");
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setWhatsappSetupAction(null);
    }
  };

  const handleSaveDefaultTemplates = async (next?: Record<string, string>) => {
    const payload = next ?? defaultTemplates;
    setTemplateSaving(true);
    try {
      const saved = await chatbotV2Service.setDefaultTemplates({
        confirmation_template_name: payload.confirmation_template_name || null,
        reminder_template_name: payload.reminder_template_name || null,
        fallback_template_name: payload.fallback_template_name || null,
        cart_recovery_template_name: payload.cart_recovery_template_name || null,
        order_confirmed_template_name: payload.order_confirmed_template_name || null,
      });
      setAccount(saved);
      toast.success("Plantillas por defecto guardadas");
      return true;
    } catch (error) {
      toast.error(getErrorMessage(error));
      return false;
    } finally {
      setTemplateSaving(false);
    }
  };

  const handleTestSend = async () => {
    setTestSending(true);
    try {
      await chatbotV2Service.sendTestMessage({
        phone: testPhone.trim(),
        text: testMessage.trim(),
      });
      toast.success("Mensaje de prueba enviado");
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setTestSending(false);
    }
  };

  const handleResolveGuardianProposal = async (
    proposalId: string,
    status: "approved" | "rejected",
    userInput?: string | null,
    note?: string | null
  ) => {
    setGuardianSaving(true);
    try {
      if (status === "approved") {
        await chatbotV2Service.approveGuardianProposal(proposalId, null, userInput);
      } else {
        // La razón del rechazo (opcional) viaja como note: el guardián aprende de esto.
        await chatbotV2Service.rejectGuardianProposal(proposalId, note);
      }
      await loadGuardianTab();
      toast.success(status === "approved" ? "Propuesta aprobada" : "Propuesta rechazada");
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setGuardianSaving(false);
    }
  };

  const handleCreateGuardianConversation = async () => {
    setGuardianSaving(true);
    try {
      const conversation = await chatbotV2Service.createGuardianConversation();
      setGuardianConversations((current) => [conversation, ...current.filter((item) => item.id !== conversation.id)]);
      return conversation;
    } catch (error) {
      toast.error(getErrorMessage(error));
      return null;
    } finally {
      setGuardianSaving(false);
    }
  };

  const handleSendGuardianMessage = async (conversationId: string, content: string) => {
    setGuardianSaving(true);
    try {
      const conversation = await chatbotV2Service.sendGuardianMessage(conversationId, content);
      setGuardianConversations((current) => [
        conversation,
        ...current.filter((item) => item.id !== conversation.id),
      ]);
      await loadGuardianTab();
      return conversation;
    } catch (error) {
      toast.error(getErrorMessage(error));
      return null;
    } finally {
      setGuardianSaving(false);
    }
  };

  const handleSaveKnowledge = async () => {
    setKnowledgeSaving(true);
    try {
      const payload = {
        name: knowledgeForm.name.trim(),
        category: knowledgeForm.category.trim() || null,
        content: knowledgeForm.content.trim(),
        source_type: knowledgeForm.source_type.trim() || "manual",
        is_active: knowledgeForm.is_active,
      };
      if (editingKnowledgeSourceId) {
        await chatbotV2Service.updateKnowledgeSource(editingKnowledgeSourceId, payload);
        toast.success("Fuente actualizada");
      } else {
        await chatbotV2Service.createKnowledgeSource(payload);
        toast.success("Fuente creada");
      }
      setEditingKnowledgeSourceId(null);
      setKnowledgeForm(EMPTY_KNOWLEDGE_FORM);
      await loadKnowledgeTab();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setKnowledgeSaving(false);
    }
  };

  // Base "Sugerencias aprobadas" (learning_log): guarda la lista completa final de cajitas.
  const handleSaveLearningEntries = async (sourceId: number, entries: LearningEntry[]) => {
    setKnowledgeSaving(true);
    try {
      await chatbotV2Service.updateKnowledgeSource(sourceId, { entries });
      await loadKnowledgeTab();
      toast.success("Aprendizajes guardados");
      return true;
    } catch (error) {
      toast.error(getErrorMessage(error));
      return false;
    } finally {
      setKnowledgeSaving(false);
    }
  };

  const handleEditKnowledge = (source: KnowledgeSource) => {
    setEditingKnowledgeSourceId(source.id);
    setKnowledgeForm({
      name: source.name,
      category: source.category || "",
      content: source.content,
      source_type: source.source_type,
      is_active: source.is_active,
    });
  };

  const handleDeleteKnowledge = async (sourceId: number) => {
    try {
      await chatbotV2Service.deleteKnowledgeSource(sourceId);
      await loadKnowledgeTab();
      toast.success("Fuente eliminada");
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleToggleKnowledgeSource = async (source: KnowledgeSource, isActive: boolean) => {
    setKnowledgeSaving(true);
    try {
      await chatbotV2Service.updateKnowledgeSource(source.id, { is_active: isActive });
      await loadKnowledgeTab();
      toast.success(isActive ? "Conocimiento activado" : "Conocimiento pausado");
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setKnowledgeSaving(false);
    }
  };

  const handleRunKnowledgeTest = async (overrideMessage?: string, options?: { retryExistingMessage?: boolean }) => {
    const text = (overrideMessage ?? knowledgeTestMessage).trim();
    if (!text) return;
    const now = new Date().toISOString();
    const history: SupportTestChatHistoryItem[] = knowledgeTestMessages
      .filter((message) => message.content.trim() && !message.pending && !message.failed)
      .slice(-30)
      .map((message) => ({
        sender: message.sender === "customer" ? "customer" : "assistant",
        content: message.content,
      }));
    const stamp = Date.now();
    const userMessage: SupportTestChatMessage = {
      id: `test-customer-${stamp}`,
      sender: "customer",
      content: text,
      time: now,
    };
    const pendingMessage: SupportTestChatMessage = {
      id: `test-assistant-${stamp}`,
      sender: "assistant",
      content: "",
      time: now,
      pending: true,
    };
    setKnowledgeTestMessages((current) => [
      ...current.filter((message) => !message.failed && !message.pending),
      ...(options?.retryExistingMessage ? [] : [userMessage]),
      pendingMessage,
    ]);
    setKnowledgeTestMessage("");
    setKnowledgeTestLoading(true);
    try {
      const response = await chatbotV2Service.runTestChat({
        message: text,
        contact_name: knowledgeTestContact.trim() || null,
        history,
      });
      setKnowledgeTestResponse(response);
      setKnowledgeTestMessages((current) =>
        current.map((message) =>
          message.id === pendingMessage.id
            ? {
                ...message,
                content: response.text,
                pending: false,
                usageModel: response.usage?.model || null,
                action: response.action,
              }
            : message
        )
      );
    } catch (error) {
      setKnowledgeTestMessages((current) =>
        current.map((message) =>
              message.id === pendingMessage.id
                ? {
                    ...message,
                    content: "La IA no pudo responder. Intenta de nuevo.",
                    pending: false,
                    failed: true,
                    retryContent: text,
                  }
            : message
        )
      );
      toast.error(getErrorMessage(error));
    } finally {
      setKnowledgeTestLoading(false);
    }
  };

  const handleResetKnowledgeTest = () => {
    setKnowledgeTestMessages([]);
    setKnowledgeTestResponse(null);
    setKnowledgeTestMessage("");
  };

  const handleSaveHotmartConnection = async () => {
    setIntegrationSaving(true);
    try {
      const secrets = {
        client_id: hotmartForm.client_id.trim() || null,
        client_secret: hotmartForm.client_secret.trim() || null,
        basic: hotmartForm.basic.trim() || null,
        hottok: hotmartForm.hottok.trim() || null,
      };
      let saved: IntegrationConnection;
      if (hotmartConnection) {
        saved = await chatbotV2Service.updateIntegrationConnection(hotmartConnection.id, {
          display_name: hotmartForm.display_name.trim() || "Hotmart",
          secrets,
        });
        toast.success("Hotmart actualizado");
      } else {
        saved = await chatbotV2Service.createIntegrationConnection({
          provider_key: "hotmart",
          display_name: hotmartForm.display_name.trim() || "Hotmart",
          secrets,
        });
        toast.success("Hotmart conectado");
      }
      const diagnosis = await chatbotV2Service.diagnoseIntegration(saved.id);
      if (String(diagnosis.status || "") === "ok") {
        toast.success("Diagnóstico Hotmart OK");
      } else {
        toast.error("Hotmart quedó pendiente de diagnóstico");
      }
      setHotmartForm(EMPTY_HOTMART_FORM);
      await loadIntegrationsTab(saved.id);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIntegrationSaving(false);
    }
  };

  const handleSaveDropiConnection = async () => {
    setIntegrationSaving(true);
    try {
      const secrets = {
        api_token: dropiForm.api_token.trim() || null,
        country_code: dropiForm.country_code.trim() || null,
        base_url: dropiForm.base_url.trim() || null,
        webhook_secret: dropiForm.webhook_secret.trim() || null,
      };
      const metadata_json = {
        country_code: dropiForm.country_code.trim() || "co",
        ...(dropiForm.base_url.trim() ? { base_url: dropiForm.base_url.trim() } : {}),
      };
      let saved: IntegrationConnection;
      if (dropiConnection) {
        saved = await chatbotV2Service.updateIntegrationConnection(dropiConnection.id, {
          display_name: dropiForm.display_name.trim() || "Dropi",
          secrets,
          metadata_json,
        });
        toast.success("Dropi actualizado");
      } else {
        saved = await chatbotV2Service.createIntegrationConnection({
          provider_key: "dropi",
          display_name: dropiForm.display_name.trim() || "Dropi",
          secrets,
          metadata_json,
        });
        toast.success("Dropi conectado");
      }
      const diagnosis = await chatbotV2Service.diagnoseIntegration(saved.id);
      if (String(diagnosis.status || "") === "ok") {
        toast.success("Diagnóstico Dropi OK");
      } else {
        toast.error("Dropi quedó pendiente de diagnóstico");
      }
      setDropiForm(EMPTY_DROPI_FORM);
      await loadIntegrationsTab(saved.id);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIntegrationSaving(false);
    }
  };

  const handleUpdateIntegrationTool = (
    connectionId: number,
    tool: IntegrationTool,
    patch: { enabled?: boolean; policy?: string | null; metadata_json?: Record<string, unknown> }
  ) => {
    setSelectedIntegrationConnectionId(connectionId);
    setIntegrationToolsByConnectionId((current) => ({
      ...current,
      [connectionId]: (current[connectionId] || []).map((row) =>
        row.tool_name === tool.tool_name
          ? {
              ...row,
              ...patch,
              policy: patch.policy ?? row.policy,
              metadata_json: patch.metadata_json ?? row.metadata_json,
            }
          : row
      ),
    }));
  };

  const handleUpdateHotmartMapping = async (
    mapping: HotmartProductMapping,
    patch: Partial<Pick<HotmartProductMapping, "aliases" | "club_subdomain" | "product_type" | "is_active">> & {
      club_public_url?: string | null;
      metadata_json?: Record<string, unknown>;
    }
  ) => {
    setIntegrationActionLoading(`mapping-${mapping.id}`);
    try {
      await chatbotV2Service.updateHotmartProductMapping(mapping.id, patch);
      await loadIntegrationsTab(mapping.connection_id);
      toast.success("Producto actualizado");
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIntegrationActionLoading(null);
    }
  };

  const handleSyncHotmartCatalog = async (connectionId: number) => {
    setIntegrationActionLoading("sync-catalog");
    try {
      await chatbotV2Service.syncIntegrationCatalog(connectionId);
      await loadIntegrationsTab(connectionId);
      toast.success("Catálogo Hotmart sincronizado");
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIntegrationActionLoading(null);
    }
  };

  const handleUpdateHotmartAccessCase = async (
    accessCase: HotmartAccessCase,
    patch: Partial<Pick<HotmartAccessCase, "case_status" | "resolution_note" | "recommended_action">>
  ) => {
    setIntegrationActionLoading(`access-case-${accessCase.id}`);
    try {
      const updated = await chatbotV2Service.updateHotmartAccessCase(accessCase.id, patch);
      setHotmartAccessCases((current) => current.map((row) => (row.id === updated.id ? updated : row)));
      toast.success("Caso actualizado");
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIntegrationActionLoading(null);
    }
  };

  const handleUpdateConversation = async (
    conversationId: number,
    payload: {
      status?: ConversationStatus | string;
      priority?: ConversationPriority | string;
      labels?: string[];
      ai_enabled?: boolean;
    }
  ) => {
    try {
      const updated = await chatbotV2Service.updateConversation(conversationId, payload);
      replaceConversation(updated);
      setSelectedConversation((current) =>
        current?.id === updated.id ? mergeConversationRecord(current, updated) : updated
      );
      toast.success("Conversación actualizada");
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleToggleConversationAI = async (conversationId: number, next: boolean) => {
    if (next && settings?.ai_enabled === false) {
      toast("Activa la IA global para prenderla en esta conversación.");
      openAssistantSettings();
      return;
    }
    try {
      const updated = await chatbotV2Service.toggleConversationAI(conversationId, next);
      if (selectedConversationIdRef.current !== conversationId) return;
      replaceConversation(updated);
      setSelectedConversation((current) =>
        current?.id === updated.id ? mergeConversationRecord(current, updated) : updated
      );
      toast.success(next ? "IA activada" : "IA pausada");
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleResolveConversation = async (conversationId: number) => {
    const snapshot =
      selectedConversationRef.current?.id === conversationId
        ? selectedConversationRef.current
        : conversations.find((row) => row.id === conversationId) || null;
    if (!snapshot) return;

    const optimisticUpdated: SupportConversation = { ...snapshot, status: "resolved" };
    patchConversation(optimisticUpdated);
    if (selectedConversationRef.current?.id === conversationId) {
      setSelectedConversation((current) =>
        current ? mergeConversationRecord(current, optimisticUpdated) : optimisticUpdated
      );
    }
    toast.success("Conversación resuelta");
    setConversationActionLoading({ action: "resolve", conversationId });

    try {
      const updated = await chatbotV2Service.resolveConversation(conversationId);
      patchConversation(updated);
      if (selectedConversationIdRef.current === conversationId) {
        setSelectedConversation((current) =>
          current?.id === updated.id ? mergeConversationRecord(current, updated) : updated
        );
      }
      void invalidateConversations();
    } catch (error) {
      patchConversation(snapshot);
      if (selectedConversationRef.current?.id === conversationId) {
        setSelectedConversation(snapshot);
      }
      toast.error(getErrorMessage(error));
    } finally {
      setConversationActionLoading(null);
    }
  };

  const handleReopenConversation = async (conversationId: number) => {
    const snapshot =
      selectedConversationRef.current?.id === conversationId
        ? selectedConversationRef.current
        : conversations.find((row) => row.id === conversationId) || null;
    if (!snapshot) return;

    const optimisticUpdated: SupportConversation = { ...snapshot, status: "open" };
    patchConversation(optimisticUpdated);
    if (selectedConversationRef.current?.id === conversationId) {
      setSelectedConversation((current) =>
        current ? mergeConversationRecord(current, optimisticUpdated) : optimisticUpdated
      );
    }
    toast.success("Conversación reabierta");
    setConversationActionLoading({ action: "reopen", conversationId });

    try {
      const updated = await chatbotV2Service.reopenConversation(conversationId);
      patchConversation(updated);
      if (selectedConversationIdRef.current === conversationId) {
        setSelectedConversation((current) =>
          current?.id === updated.id ? mergeConversationRecord(current, updated) : updated
        );
      }
      void invalidateConversations();
    } catch (error) {
      patchConversation(snapshot);
      if (selectedConversationRef.current?.id === conversationId) {
        setSelectedConversation(snapshot);
      }
      toast.error(getErrorMessage(error));
    } finally {
      setConversationActionLoading(null);
    }
  };

  const handleRetryMediaExtraction = async (messageId: number) => {
    const conversationId = selectedConversationRef.current?.id;
    if (!conversationId) return;
    try {
      await chatbotV2Service.retryInboundMediaExtraction(messageId);
      await loadConversationDetails(conversationId, { background: true });
      toast.success("Reintentando transcripción del archivo…");
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleRetryAI = async (conversationId: number, failureTraceId?: string | null) => {
    const traceKey = failureTraceId || null;
    setRetryingTraceId(traceKey);
    setRetryingConversationId(conversationId);
    setConversationActionLoading({ action: "retry-ai", conversationId });
    try {
      const result = await chatbotV2Service.retryConversationAI(conversationId, {
        instruction: retryInstruction.trim() || null,
      });
      setRetryInstruction("");
      if (selectedConversationIdRef.current !== conversationId) return;
      await loadConversationDetails(conversationId, { background: true });
      void loadConversations();
      toast.success(result.sent ? "Respuesta IA reenviada" : "La IA no envió respuesta; revisa eventos");
      const runtime = getConversationAiRuntime(selectedConversationRef.current);
      if (isLiveAiRuntimeStatus(runtime?.status)) {
        setRetryingTraceId(runtime?.trace_id || traceKey);
      } else {
        setRetryingTraceId(null);
        setRetryingConversationId(null);
      }
    } catch (error) {
      toast.error(getErrorMessage(error));
      setRetryingTraceId(null);
      setRetryingConversationId(null);
    } finally {
      setConversationActionLoading(null);
    }
  };

  const handleCancelConversationAI = async (conversationId: number) => {
    setConversationActionLoading({ action: "cancel-ai", conversationId });
    try {
      const result = await chatbotV2Service.cancelConversationAI(conversationId);
      if (!result?.cancelled) {
        toast.error("No había una generación IA activa para cancelar");
        void loadConversationDetails(conversationId, { background: true });
        void loadConversations();
        return;
      }
      setRetryingTraceId(null);
      setRetryingConversationId(null);
      toast.success("Generación IA cancelada");
      void loadConversationDetails(conversationId, { background: true });
      void loadConversations();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setConversationActionLoading(null);
    }
  };

  const handleSaveContact = async (formOverride?: ContactFormState, contactIdOverride?: number) => {
    const contactId = contactIdOverride ?? selectedConversation?.contact?.id;
    if (!contactId) return;
    const form = formOverride || contactForm;
    setContactSaving(true);
    try {
      const updatedContact = await chatbotV2Service.updateContact(contactId, {
        name: form.name.trim() || null,
        email: form.email.trim() || null,
        alias: form.alias.trim() || null,
        phone: form.phone.trim() || null,
        tags: parseCsvTags(form.tags),
        custom_attributes: safeParseJson(form.custom_attributes),
      });
      const selectedContactId = selectedConversation?.contact?.id;
      const updatedConversation = selectedConversation && selectedContactId === updatedContact.id
        ? { ...selectedConversation, contact: updatedContact }
        : null;
      if (updatedConversation) {
        replaceConversation(updatedConversation);
        setSelectedConversation(updatedConversation);
        setContactForm(contactToForm(updatedContact));
      }
      toast.success("Contacto actualizado");
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setContactSaving(false);
    }
  };

  const handleAddContactNote = async () => {
    const contactId = selectedConversation?.contact?.id;
    if (!contactId || !contactNote.trim()) return;
    setContactSaving(true);
    try {
      const note = await chatbotV2Service.addContactNote(contactId, {
        note: contactNote.trim(),
        category: "private",
        conversation_id: selectedConversation?.id || null,
      });
      setContactNote("");
      if (selectedConversation?.contact) {
        const updatedContact = {
          ...selectedConversation.contact,
          notes: [...(selectedConversation.contact.notes || []), note],
        };
        const updatedConversation = { ...selectedConversation, contact: updatedContact };
        replaceConversation(updatedConversation);
        setSelectedConversation(updatedConversation);
      }
      toast.success("Nota de contacto guardada");
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setContactSaving(false);
    }
  };

  const handleCreatePrivateNote = async (contentOverride?: string) => {
    const content = (contentOverride ?? privateNote).trim();
    if (!selectedConversationId || !content) return false;
    setConversationActionLoading({ action: "private-note", conversationId: selectedConversationId });
    try {
      await chatbotV2Service.createPrivateNote(selectedConversationId, content);
      setPrivateNote("");
      await loadConversationDetails(selectedConversationId);
      toast.success("Nota guardada");
      return true;
    } catch (error) {
      toast.error(getErrorMessage(error));
      return false;
    } finally {
      setConversationActionLoading(null);
    }
  };

  const replaceConversation = (updated: SupportConversation) => {
    patchConversation(updated);
  };

  const handleSendMessage = async () => {
    const content = composerValue.trim();
    if (!selectedConversationId || !selectedConversation || !content) return;
    if (selectedConversation.contact?.opted_out_at) {
      toast.error("Este contacto pidió no recibir mensajes por WhatsApp");
      return;
    }
    const clientMessageId = createClientMessageId(selectedConversationId);
    const optimisticMessage = makeOptimisticOutboundMessage({
      conversation: selectedConversation,
      content,
      clientMessageId,
    });
    setSendingMessage(true);
    setMessages((current) => mergeMessageRows(current, [optimisticMessage]));
    setComposerValue("");
    setForceScrollToBottomSignal((current) => current + 1);
    const requestPayload = {
      content,
      disable_ai: false,
      client_message_id: clientMessageId,
    };
    const submitSameClaim = () =>
      chatbotV2Service.sendConversationMessage(selectedConversationId, requestPayload);
    try {
      let sentMessage: SupportMessage;
      try {
        sentMessage = await submitSameClaim();
      } catch (firstError) {
        if (!shouldReconcileOutboundRequestError(firstError)) throw firstError;
        try {
          // A 5xx or a lost response may happen after Meta accepted the POST. Retrying
          // with the exact same durable key only reads/reconciles that claim; it must
          // never create a second outbound message.
          sentMessage = await submitSameClaim();
        } catch (reconciliationError) {
          console.warn("[ChatbotPage] outbound claim reconciliation pending", reconciliationError);
          setMessages((current) =>
            mergeMessageRows(current, [makeLocalUnknownOutboundMessage(optimisticMessage)])
          );
          void loadConversationDetails(selectedConversationId, { background: true });
          toast("Envío en verificación. No lo envíes de nuevo mientras confirmamos el estado.");
          return;
        }
      }
      setMessages((current) => mergeMessageRows(current, [sentMessage]));
      void loadConversationDetails(selectedConversationId, { background: true });
      void loadConversations();
      const feedback = getOutboundDeliveryFeedback(sentMessage.delivery_status);
      if (feedback === "failed") {
        toast.error("No se pudo enviar");
      } else if (feedback === "confirmed") {
        toast.success("Mensaje enviado");
      } else {
        toast("Envío en verificación. No lo envíes de nuevo mientras confirmamos el estado.");
      }
    } catch (error) {
      const message = getErrorMessage(error);
      const failed = makeLocalFailedMessage({
        conversation: selectedConversation,
        content,
        error: message,
        clientMessageId,
      });
      setMessages((current) => mergeMessageRows(current, [failed.message]));
      setConversationDeliveryLogs((current) => [
        failed.deliveryLog,
        ...current.filter((row) => row.id !== failed.deliveryLog.id),
      ]);
      toast.error("No se pudo enviar");
    } finally {
      setSendingMessage(false);
    }
  };

  const handleSendConversationMedia = async (
    file: File,
    kind: "image" | "video" | "voice",
    caption?: string
  ): Promise<boolean> => {
    if (!selectedConversationId) return false;
    try {
      await chatbotV2Service.sendConversationMedia(selectedConversationId, { file, kind, caption });
      void loadConversationDetails(selectedConversationId, { background: true });
      void loadConversations();
      return true;
    } catch (error) {
      const status = (error as { response?: { status?: number } })?.response?.status;
      if (status === 409) {
        toast.error("La ventana de 24 horas para responder se cerró.");
        void loadConversationDetails(selectedConversationId, { background: true });
      } else {
        toast.error(getErrorMessage(error));
      }
      return false;
    }
  };

  const handleSendConversationTemplate = async (payload: {
    template_name: string;
    language?: string | null;
    variables?: string[];
  }): Promise<boolean> => {
    if (!selectedConversationId) return false;
    try {
      await chatbotV2Service.sendConversationTemplate(selectedConversationId, {
        ...payload,
        client_message_id: createClientMessageId(selectedConversationId),
      });
      void loadConversationDetails(selectedConversationId, { background: true });
      void loadConversations();
      return true;
    } catch (error) {
      const status = (error as { response?: { status?: number } })?.response?.status;
      if (status === 429) {
        const retryAt = (error as { response?: { data?: { retry_at?: string } } })?.response?.data?.retry_at;
        toast.error(
          `Ya enviaste una plantilla de reapertura — espera la respuesta del cliente (disponible ${formatMessageTime(retryAt)})`
        );
      } else {
        toast.error(getErrorMessage(error));
      }
      return false;
    }
  };

  const settingsFormDirty = Boolean(
    settings &&
      JSON.stringify(settingsFormComparable(settingsToForm(settings))) !==
        JSON.stringify(settingsFormComparable(settingsForm))
  );
  const assistantKnowledgeFormDirty = Boolean(
    settings &&
      JSON.stringify(assistantKnowledgeSettingsComparable(settingsToForm(settings))) !==
        JSON.stringify(assistantKnowledgeSettingsComparable(settingsForm))
  );
  const currentDefaultTemplates = defaultTemplateSlots.reduce<Record<string, string>>((acc, slot) => {
    acc[slot.key] = defaultTemplates[slot.key] || "";
    return acc;
  }, {});
  const savedDefaultTemplates = defaultTemplateSlots.reduce<Record<string, string>>((acc, slot) => {
    acc[slot.key] = account?.default_templates?.[slot.key] || "";
    return acc;
  }, {});
  const defaultTemplatesDirty = Boolean(
    account && JSON.stringify(currentDefaultTemplates) !== JSON.stringify(savedDefaultTemplates)
  );
  const automationDraftsDirty = Boolean(
    automationDraftsTouched ||
      settings &&
      JSON.stringify(normalizeAutomationDrafts(committedAutomationDrafts(automationDrafts))) !==
        JSON.stringify(normalizeAutomationDrafts(savedAutomationDrafts))
  );
  const confirmationFlowsDirty =
    serializeCommerceFlowDrafts(confirmationFlowDrafts) !== serializeCommerceFlowDrafts(savedConfirmationFlowDrafts);
  const orderFlowsDirty =
    serializeCommerceFlowDrafts(orderFlowDrafts) !== serializeCommerceFlowDrafts(savedOrderFlowDrafts);
  const recoveryFlowsDirty =
    serializeCartRecoveryFlowDrafts(recoveryFlowDrafts) !== serializeCartRecoveryFlowDrafts(savedRecoveryFlowDrafts);
  const shouldPersistRecoveryFlows =
    settingsPanelTab === "actions" && actionsConfigSubTab === "recuperacion" && recoveryFlowsDirty;
  const salesActionsSettingsDirty =
    JSON.stringify(salesActionsSettings) !== JSON.stringify(savedSalesActionsSettings);
  const commerceFlowsDirty =
    confirmationFlowsDirty || orderFlowsDirty || recoveryFlowsDirty || salesActionsSettingsDirty;
  const mediaResourcesDirty = Boolean(
    JSON.stringify(normalizeMediaResourceDrafts(committedMediaResourceDrafts(mediaResourceDrafts, savedMediaResourceDrafts))) !==
      JSON.stringify(normalizeMediaResourceDrafts(savedMediaResourceDrafts))
  );
  const integrationToolsDirty = Boolean(
    JSON.stringify(normalizeIntegrationToolDraftMap(integrationToolsByConnectionId)) !==
      JSON.stringify(normalizeIntegrationToolDraftMap(savedIntegrationToolsByConnectionId))
  );
  const websiteSyncDraftsDirty = Object.values(websiteSyncDraftsById).some((draft) => draft.dirty);
  const supportSettingsDraftsDirty = settingsFormDirty || automationDraftsDirty || mediaResourcesDirty || commerceFlowsDirty;
  const settingsModalDirty =
    supportSettingsDraftsDirty || defaultTemplatesDirty || integrationToolsDirty || websiteSyncDraftsDirty;
  const settingsModalSaving = settingsSaving || templateSaving || integrationSaving || websiteSyncSaving;
  const automationDraftsForCurrentSave = automationDrafts;
  const settingsSaveBlockedReason =
    (settingsPanelTab === "actions" && actionsConfigSubTab === "confirmaciones" && confirmationFlowsDirty
      ? validateCommerceFlowDrafts(confirmationFlowDrafts, "confirmation", checkoutWebsitesForValidation)[0] || null
      : null) ||
    (settingsPanelTab === "actions" && actionsConfigSubTab === "ventas" && orderFlowsDirty
      ? validateCommerceFlowDrafts(orderFlowDrafts, "order_capture", checkoutWebsitesForValidation)[0] || null
      : null) ||
    (settingsPanelTab === "actions" && actionsConfigSubTab === "ventas" && websiteSyncDraftsDirty
      ? validateWebsiteSyncDrafts(websiteSyncDraftsById)[0] || null
      : null) ||
    (shouldPersistRecoveryFlows ? validateCartRecoveryFlowDrafts(recoveryFlowDrafts)[0] || null : null) ||
    (mediaResourcesDirty ? validateMediaResourceDrafts(mediaResourceDrafts)[0] || null : null);

  useEffect(() => {
    settingsModalOpenRef.current = settingsModalOpen;
  }, [settingsModalOpen]);

  useEffect(() => {
    automationDraftsDirtyRef.current = automationDraftsDirty;
    confirmationFlowsDirtyRef.current = confirmationFlowsDirty;
    orderFlowsDirtyRef.current = orderFlowsDirty;
    settingsFormDirtyRef.current = settingsFormDirty;
    mediaResourcesDirtyRef.current = mediaResourcesDirty;
  }, [
    automationDraftsDirty,
    confirmationFlowsDirty,
    orderFlowsDirty,
    settingsFormDirty,
    mediaResourcesDirty,
  ]);

  const isSettingsTabDirty = (tab: SettingsPanelTab) => {
    switch (tab) {
      case "knowledge":
        return assistantKnowledgeFormDirty || mediaResourcesDirty;
      case "actions":
        return commerceFlowsDirty || integrationToolsDirty || automationDraftsDirty || websiteSyncDraftsDirty;
      case "templates":
        return defaultTemplatesDirty;
      case "integrations":
        return integrationToolsDirty;
      default:
        return false;
    }
  };

  const settingsTabDirtyLabel = (tab: SettingsPanelTab) => {
    switch (tab) {
      case "knowledge":
        return "Asistente IA";
      case "actions":
        return "Acciones";
      case "templates":
        return "Plantillas";
      case "integrations":
        return "Integraciones";
      default:
        return "esta sección";
    }
  };

  const pruneAutomationDraftState = () => {
    setAutomationDrafts((current) => pruneEphemeralAutomations(current));
  };

  const pruneCommerceFlowDraftState = () => {
    setConfirmationFlowDrafts((current) => pruneEphemeralCommerceFlows(current));
    setOrderFlowDrafts((current) => pruneEphemeralCommerceFlows(current));
  };

  const handleSaveSupportSettingsDrafts = async () => {
    if (!supportSettingsDraftsDirty) return true;
    if (settingsPanelTab === "actions" && confirmationFlowsDirty) {
      const confirmationErrors = validateCommerceFlowDrafts(
        confirmationFlowDrafts,
        "confirmation",
        checkoutWebsitesForValidation
      );
      if (confirmationErrors.length) {
        const message = confirmationErrors[0] || "Completa las confirmaciones antes de guardar.";
        setCommerceFlowValidationError(message);
        toast.error(message);
        return false;
      }
    }
    if (settingsPanelTab === "actions" && orderFlowsDirty) {
      const orderErrors = validateCommerceFlowDrafts(
        orderFlowDrafts,
        "order_capture",
        checkoutWebsitesForValidation
      );
      if (orderErrors.length) {
        const message = orderErrors[0] || "Completa las tomas de orden antes de guardar.";
        setCommerceFlowValidationError(message);
        toast.error(message);
        return false;
      }
    }
    if (shouldPersistRecoveryFlows) {
      const ephemeralRecoveryDrafts = recoveryFlowDrafts.filter((flow) => flow.ephemeral);
      if (ephemeralRecoveryDrafts.length) {
        const label = ephemeralRecoveryDrafts[0].title.trim() || "Recuperación sin terminar";
        const message = `${label}: completa producto y plantilla antes de guardar, o elimínala.`;
        setCommerceFlowValidationError(message);
        toast.error(message);
        return false;
      }
      const recoveryErrors = validateCartRecoveryFlowDrafts(recoveryFlowDrafts);
      if (recoveryErrors.length) {
        const message = recoveryErrors[0] || "Completa las recuperaciones antes de guardar.";
        setCommerceFlowValidationError(message);
        toast.error(message);
        return false;
      }
    }
    if (automationDraftsDirty) {
      const automationErrors = validateAutomationDrafts(automationDraftsForCurrentSave);
      if (automationErrors.length) {
        const message = automationErrors[0] || "Completa las automatizaciones antes de guardar.";
        setAutomationValidationError(message);
        toast.error(message);
        return false;
      }
    }
    if (mediaResourcesDirty) {
      const mediaErrors = validateMediaResourceDrafts(mediaResourceDrafts);
      if (mediaErrors.length) {
        toast.error(mediaErrors[0] || "Completa los recursos multimedia antes de guardar.");
        return false;
      }
    }
    setSettingsSaving(true);
    try {
      let saved = settings;
      const shouldSaveSettingsMetadata =
        settingsFormDirty || shouldPersistRecoveryFlows || salesActionsSettingsDirty;
      if (shouldSaveSettingsMetadata) {
        const currentMetadata = isRecord(settingsForm.metadata_json) ? settingsForm.metadata_json : {};
        const nextMetadata = {
          ...currentMetadata,
          ...(shouldPersistRecoveryFlows
            ? { cart_recovery_flows: committedCartRecoveryFlowDrafts(recoveryFlowDrafts) }
            : {}),
          ...(salesActionsSettingsDirty
            ? { sales_actions_settings: salesActionsSettingsToMetadata(salesActionsSettings) }
            : {}),
        };
        saved = await chatbotV2Service.saveSettings({
          ...cleanSettingsPayload(settingsForm),
          metadata_json: buildSettingsSaveMetadata(settings, {
            ...settingsForm,
            metadata_json: nextMetadata,
          }),
        });
        setSettings(saved);
        const savedForm = settingsToForm(saved);
        setSettingsForm(savedForm);
        const savedRecovery = cartRecoveryFlowDraftsFromMetadata(
          isRecord(savedForm.metadata_json) ? savedForm.metadata_json : {}
        );
        if (shouldPersistRecoveryFlows) {
          setRecoveryFlowDrafts(savedRecovery);
          setSavedRecoveryFlowDrafts(savedRecovery);
        }
        const savedSales = salesActionsSettingsFromMetadata(
          isRecord(savedForm.metadata_json) ? savedForm.metadata_json : {}
        );
        setSalesActionsSettings(savedSales);
        setSavedSalesActionsSettings(savedSales);
      }
      if (mediaResourcesDirty) {
        const savedMediaResources = await chatbotV2Service.saveSupportMediaResources(
          normalizeMediaResourceDrafts(mediaResourceDrafts) as unknown as Record<string, unknown>[]
        );
        const savedMediaDrafts = mediaResourceDraftsFromApi(savedMediaResources, saved);
        setMediaResourceDrafts(savedMediaDrafts);
        setSavedMediaResourceDrafts(savedMediaDrafts);
      }
      if (automationDraftsDirty) {
        const automationRows = await chatbotV2Service.saveSupportAutomations(
          normalizeAutomationDrafts(committedAutomationDrafts(automationDraftsForCurrentSave)) as unknown as Record<string, unknown>[]
        );
        const savedDrafts = automationDraftsFromRows(automationRows, saved);
        setAutomationDrafts(savedDrafts);
        setSavedAutomationDrafts(savedDrafts);
        setAutomationDraftsTouched(false);
        automationDraftsTouchedRef.current = false;
        setAutomationValidationError(null);
      }
      let confirmationSync: ConfirmationSyncResult | null = null;
      if (confirmationFlowsDirty) {
        const confirmationSave = await chatbotV2Service.saveCommerceFlows(
          "confirmation",
          committedCommerceFlowDrafts(confirmationFlowDrafts).map((flow) => commerceFlowToApiPayload(flow))
        );
        const savedConfirmationDrafts = normalizeCommerceFlowDrafts(confirmationSave.flows);
        setConfirmationFlowDrafts(savedConfirmationDrafts);
        setSavedConfirmationFlowDrafts(savedConfirmationDrafts);
        setCommerceFlowValidationError(null);
        confirmationSync = confirmationSave.confirmation_sync;
      }
      if (orderFlowsDirty) {
        const orderSave = await chatbotV2Service.saveCommerceFlows(
          "order_capture",
          committedCommerceFlowDrafts(orderFlowDrafts).map((flow) => commerceFlowToApiPayload(flow, checkoutWebsitesForValidation))
        );
        const savedOrderDrafts = normalizeCommerceFlowDrafts(orderSave.flows);
        setOrderFlowDrafts(savedOrderDrafts);
        setSavedOrderFlowDrafts(savedOrderDrafts);
        setCommerceFlowValidationError(null);
        confirmationSync = orderSave.confirmation_sync ?? confirmationSync;
      }
      toast.success("Configuración guardada");
      // El alcance real de confirmaciones queda VISIBLE: qué embudos confirman
      // (o error claro) — nunca "guardado" con el gate apagado por debajo.
      notifyConfirmationSyncResult(confirmationSync, confirmationFlowsDirty || orderFlowsDirty);
      void loadCore(true);
      return true;
    } catch (error) {
      toast.error(getErrorMessage(error));
      return false;
    } finally {
      setSettingsSaving(false);
    }
  };

  const handleSaveIntegrationToolDrafts = async () => {
    if (!integrationToolsDirty) return true;
    setIntegrationSaving(true);
    try {
      const changedEntries: Array<{ connectionId: number; tool: IntegrationTool }> = [];
      for (const [connectionIdText, tools] of Object.entries(integrationToolsByConnectionId)) {
        const connectionId = Number(connectionIdText);
        const savedTools = savedIntegrationToolsByConnectionId[connectionId] || [];
        const savedByName = new Map(savedTools.map((tool) => [tool.tool_name, tool]));
        for (const tool of tools) {
          const savedTool = savedByName.get(tool.tool_name);
          if (
            !savedTool ||
            JSON.stringify(normalizeIntegrationToolDrafts([tool])) !==
              JSON.stringify(normalizeIntegrationToolDrafts([savedTool]))
          ) {
            changedEntries.push({ connectionId, tool });
          }
        }
      }
      for (const { connectionId, tool } of changedEntries) {
        setIntegrationActionLoading(`tool-${connectionId}-${tool.tool_name}`);
        await chatbotV2Service.updateIntegrationTool(connectionId, tool.tool_name, {
          enabled: tool.enabled,
          policy: tool.policy,
          metadata_json: isRecord(tool.metadata_json) ? tool.metadata_json : {},
        });
      }
      await loadIntegrationsTab(selectedIntegrationConnectionId);
      toast.success("Configuración guardada");
      return true;
    } catch (error) {
      toast.error(getErrorMessage(error));
      return false;
    } finally {
      setIntegrationActionLoading(null);
      setIntegrationSaving(false);
    }
  };

  const handleSaveWebsiteSyncDrafts = async () => {
    const dirtyDrafts = Object.values(websiteSyncDraftsById).filter((draft) => draft.dirty);
    if (!dirtyDrafts.length) return true;
    const validationErrors = validateWebsiteSyncDrafts(websiteSyncDraftsById);
    if (validationErrors.length) {
      const message = validationErrors[0] || "Completa la sincronización de órdenes antes de guardar.";
      setCommerceFlowValidationError(message);
      toast.error(message);
      return false;
    }
    setWebsiteSyncSaving(true);
    try {
      for (const draft of dirtyDrafts) {
        await saveWebsiteSyncDraft(draft);
      }
      setWebsiteSyncDraftsById((current) => {
        const next = { ...current };
        dirtyDrafts.forEach((draft) => {
          if (next[draft.websiteId]) next[draft.websiteId] = { ...next[draft.websiteId], dirty: false };
        });
        return next;
      });
      await refreshCheckoutWebsites();
      toast.success("Sincronización de órdenes guardada");
      return true;
    } catch (error) {
      toast.error(getErrorMessage(error));
      return false;
    } finally {
      setWebsiteSyncSaving(false);
    }
  };

  const handleSaveSettingsModal = async () => {
    if (!(await handleSaveSupportSettingsDrafts())) return false;
    if (websiteSyncDraftsDirty) {
      if (!(await handleSaveWebsiteSyncDrafts())) return false;
    }
    if (defaultTemplatesDirty) {
      if (!(await handleSaveDefaultTemplates())) return false;
    }
    if (integrationToolsDirty) {
      if (!(await handleSaveIntegrationToolDrafts())) return false;
    }
    return true;
  };

  const resetSettingsModalDrafts = () => {
    if (settings) {
      setSettingsForm(settingsToForm(settings));
      setAutomationDrafts(savedAutomationDrafts);
      setAutomationDraftsTouched(false);
      automationDraftsTouchedRef.current = false;
      setConfirmationFlowDrafts(savedConfirmationFlowDrafts);
      setOrderFlowDrafts(savedOrderFlowDrafts);
      setRecoveryFlowDrafts(savedRecoveryFlowDrafts);
      setSalesActionsSettings(savedSalesActionsSettings);
      setMediaResourceDrafts(savedMediaResourceDrafts);
    }
    if (account) {
      setDefaultTemplates({
        confirmation_template_name: account.default_templates?.confirmation_template_name || "",
        reminder_template_name: account.default_templates?.reminder_template_name || "",
        fallback_template_name: account.default_templates?.fallback_template_name || "",
        cart_recovery_template_name: account.default_templates?.cart_recovery_template_name || "",
        order_confirmed_template_name: account.default_templates?.order_confirmed_template_name || "",
      });
    }
    setIntegrationToolsByConnectionId(savedIntegrationToolsByConnectionId);
    setWebsiteSyncDraftsById({});
  };

  const closeSettingsModal = () => {
    pruneAutomationDraftState();
    pruneCommerceFlowDraftState();
    resetSettingsModalDrafts();
    setSettingsModalOpen(false);
    setIntegrationDetail(null);
    setPendingSettingsPanelChange(null);
    setPendingSettingsModalClose(false);
    setPendingSettingsModalCloseAction(null);
  };

  const requestCloseSettingsModal = (afterClose?: () => void) => {
    if (settingsModalDirty) {
      setPendingSettingsModalCloseAction(() => afterClose ?? null);
      setPendingSettingsModalClose(true);
      return;
    }
    closeSettingsModal();
    afterClose?.();
  };

  const continuePendingSettingsModalClose = () => {
    const afterClose = pendingSettingsModalCloseAction;
    closeSettingsModal();
    afterClose?.();
  };

  const settingsModalWasOpenRef = useRef(false);
  useEffect(() => {
    if (settingsModalOpen && !settingsModalWasOpenRef.current) {
      resetSettingsModalDrafts();
      settingsTabLoadSeqRef.current = { settings: 0, confirmations: 0, orders: 0 };
      if (settingsPanelTab === "knowledge") {
        setKnowledgeTabLoading(true);
      }
    }
    settingsModalWasOpenRef.current = settingsModalOpen;
  }, [settingsModalOpen, settingsPanelTab]);

  const handleKnowledgePanelViewChange = (
    view: "main" | "test" | "editor" | "source" | "media"
  ) => {
    if (knowledgePanelView === "media" && view === "main") {
      setMediaResourceDrafts((current) => pruneEphemeralMediaResourceDrafts(current, savedMediaResourceDrafts));
    }
    setKnowledgePanelView(view);
  };

  const applySettingsPanelChange = (
    tab: SettingsPanelTab,
    options: { integrationDetail?: IntegrationDetail | null; knowledgeView?: "main" | "test" | "editor" | "source" | "media" } = {}
  ) => {
    const nextIntegrationDetail = options.integrationDetail ?? null;
    const nextKnowledgeView = options.knowledgeView ?? "main";
    if (tab === "knowledge" && nextKnowledgeView === "main") {
      setKnowledgeTabLoading(true);
    }
    setSettingsPanelTab(tab);
    setIntegrationDetail(nextIntegrationDetail);
    setKnowledgePanelView(nextKnowledgeView);
  };

  const handleSettingsPanelChange = (
    tab: SettingsPanelTab,
    options: { integrationDetail?: IntegrationDetail | null; knowledgeView?: "main" | "test" | "editor" | "source" | "media" } = {}
  ) => {
    const nextIntegrationDetail = options.integrationDetail ?? null;
    const nextKnowledgeView = options.knowledgeView ?? "main";
    const samePanel =
      settingsPanelTab === tab &&
      integrationDetail === nextIntegrationDetail &&
      (tab !== "knowledge" || knowledgePanelView === nextKnowledgeView);
    if (samePanel) return;

    if (isSettingsTabDirty(settingsPanelTab)) {
      setPendingSettingsPanelChange({
        tab,
        integrationDetail: nextIntegrationDetail,
        knowledgeView: nextKnowledgeView,
      });
      return;
    }

    applySettingsPanelChange(tab, options);
  };

  const continuePendingSettingsPanelChange = () => {
    if (!pendingSettingsPanelChange) return;
    const next = pendingSettingsPanelChange;
    const leavingTab = settingsPanelTab;
    switch (leavingTab) {
      case "knowledge":
        if (settings) setSettingsForm(settingsToForm(settings));
        setMediaResourceDrafts(savedMediaResourceDrafts);
        break;
      case "actions":
        setConfirmationFlowDrafts(savedConfirmationFlowDrafts);
        setOrderFlowDrafts(savedOrderFlowDrafts);
        setRecoveryFlowDrafts(savedRecoveryFlowDrafts);
        setSalesActionsSettings(savedSalesActionsSettings);
        setCommerceFlowValidationError(null);
        setIntegrationToolsByConnectionId(savedIntegrationToolsByConnectionId);
        // La fuga del dirty fantasma (bug reportado): Logística vive en Acciones y sus
        // drafts (automations + sync de embudos) no se descartaban al continuar.
        setAutomationDrafts(savedAutomationDrafts);
        setAutomationDraftsTouched(false);
        automationDraftsTouchedRef.current = false;
        setAutomationValidationError(null);
        setWebsiteSyncDraftsById({});
        break;
      case "templates":
        if (account) {
          setDefaultTemplates({
            confirmation_template_name: account.default_templates?.confirmation_template_name || "",
            reminder_template_name: account.default_templates?.reminder_template_name || "",
            fallback_template_name: account.default_templates?.fallback_template_name || "",
            cart_recovery_template_name: account.default_templates?.cart_recovery_template_name || "",
            order_confirmed_template_name: account.default_templates?.order_confirmed_template_name || "",
          });
        }
        break;
      case "integrations":
        setIntegrationToolsByConnectionId(savedIntegrationToolsByConnectionId);
        break;
      default:
        break;
    }
    setPendingSettingsPanelChange(null);
    applySettingsPanelChange(next.tab, {
      integrationDetail: next.integrationDetail,
      knowledgeView: next.knowledgeView,
    });
  };

  const renderSettingsPanel = (): React.ReactNode => {
    if (settingsPanelTab === "templates") {
      return (
        <TemplatesTab
          templates={templates}
          templateStatusFilter={templateStatusFilter}
          templateSaving={templateSaving}
          defaultTemplates={defaultTemplates}
          onDefaultTemplatesChange={setDefaultTemplates}
          onSaveDefaultTemplates={handleSaveDefaultTemplates}
          onTemplateStatusFilterChange={(status) => {
            setTemplateStatusFilter(status);
            void loadTemplates(status);
          }}
          onSyncTemplates={() => void handleSyncTemplates()}
          onTemplateCreated={() => void loadTemplates(templateStatusFilter)}
        />
      );
    }

    if (settingsPanelTab === "knowledge") {
      return (
        <KnowledgeTab
          settingsForm={settingsForm}
          detailView={knowledgePanelView}
          sources={knowledgeSources}
          loading={knowledgeTabLoading}
          loadedOnce={knowledgeTabLoadedOnce}
          form={knowledgeForm}
          editingSourceId={editingKnowledgeSourceId}
          saving={knowledgeSaving}
          testMessage={knowledgeTestMessage}
          testContact={knowledgeTestContact}
          testResponse={knowledgeTestResponse}
          testMessages={knowledgeTestMessages}
          testLoading={knowledgeTestLoading}
          mediaResources={mediaResourceDrafts}
          onSettingsFormChange={setSettingsForm}
          onDetailViewChange={handleKnowledgePanelViewChange}
          onFormChange={setKnowledgeForm}
          onSave={handleSaveKnowledge}
          onSaveLearningEntries={handleSaveLearningEntries}
          onEdit={handleEditKnowledge}
          onDelete={handleDeleteKnowledge}
          onToggle={(source, isActive) => void handleToggleKnowledgeSource(source, isActive)}
          onCancelEdit={() => {
            setEditingKnowledgeSourceId(null);
            setKnowledgeForm(EMPTY_KNOWLEDGE_FORM);
          }}
          onTestMessageChange={setKnowledgeTestMessage}
          onTestContactChange={setKnowledgeTestContact}
          onRunTest={(message, options) => void handleRunKnowledgeTest(message, options)}
          onResetTest={handleResetKnowledgeTest}
          onMediaResourcesChange={setMediaResourceDrafts}
        />
      );
    }

    if (settingsPanelTab === "integrations") {
      return (
        <IntegrationsTab
          providers={integrationProviders}
          connections={integrationConnections}
          ecommerceDropiActive={ecommerceDropiActive}
          selectedConnection={selectedIntegrationConnection}
          tools={integrationTools}
          hotmartMappings={hotmartMappings}
          form={hotmartForm}
          dropiForm={dropiForm}
          loading={integrationLoading}
          saving={integrationSaving}
          actionLoading={integrationActionLoading}
          account={account}
          accountForm={accountForm}
          accountSaving={accountSaving}
          diagnoseResult={diagnoseResult}
          testPhone={testPhone}
          testMessage={testMessage}
          testSending={testSending}
          registerPin={registerPin}
          whatsappSetupAction={whatsappSetupAction}
          integrationDetail={integrationDetail}
          onFormChange={setHotmartForm}
          onDropiFormChange={setDropiForm}
          onAccountFormChange={setAccountForm}
          onSaveAccount={() => requestIntegrationSave("whatsapp")}
          onQuickOAuthComplete={() => loadIntegrationsTab()}
          onTemplatesProvisioned={() => void loadTemplates(templateStatusFilter)}
          onRotateVerifyToken={() => void handleRotateVerifyToken()}
          onDiagnoseWhatsApp={() => void handleDiagnose()}
          onCopy={handleCopy}
          onTestPhoneChange={setTestPhone}
          onTestMessageChange={setTestMessage}
          onTestSend={() => void handleTestSend()}
          onRegisterPinChange={setRegisterPin}
          onRegisterNumber={() => void handleRegisterWhatsAppNumber()}
          onSubscribeWebhooks={() => void handleSubscribeWhatsAppWebhooks()}
          onOpenDetail={setIntegrationDetail}
          onCloseDetail={() => setIntegrationDetail(null)}
          onSelectConnection={(connectionId) => {
            setSelectedIntegrationConnectionId(connectionId);
            void loadIntegrationsTab(connectionId);
          }}
          onSaveHotmart={() => requestIntegrationSave("hotmart")}
          onSaveDropi={() => void handleSaveDropiConnection()}
          onUpdateMapping={(mapping, patch) => void handleUpdateHotmartMapping(mapping, patch)}
          onSyncCatalog={(connectionId) => void handleSyncHotmartCatalog(connectionId)}
          onRefresh={() => void loadIntegrationsTab()}
        />
      );
    }

    if (settingsPanelTab === "actions") {
      return (
        <ChatbotActionsConfigTab
          activeSubTab={actionsConfigSubTab}
          onSubTabChange={setActionsConfigSubTab}
          account={account}
          settingsForm={settingsForm}
          orderFlows={orderFlowDrafts}
          confirmationFlows={confirmationFlowDrafts}
          recoveryFlows={recoveryFlowDrafts}
          automations={automationDrafts}
          automationValidationError={automationValidationError}
          approvedTemplates={approvedTemplates}
          pickerTemplates={pickerTemplates}
          commerceFlowValidationError={commerceFlowValidationError}
          websiteSyncDraftsById={websiteSyncDraftsById}
          connections={integrationConnections}
          toolsByConnectionId={integrationToolsByConnectionId}
          onOrderFlowsChange={(rows) => {
            setCommerceFlowValidationError(null);
            setOrderFlowDrafts(
              rows.map((row) => ({
                ...row,
                ephemeral:
                  row.ephemeral && !isCommerceFlowDraftCommitted(row, checkoutWebsitesForValidation) ? true : false,
              }))
            );
          }}
          onConfirmationFlowsChange={(rows) => {
            setCommerceFlowValidationError(null);
            setConfirmationFlowDrafts(
              rows.map((row) => ({
                ...row,
                ephemeral: row.ephemeral && !isCommerceFlowDraftCommitted(row) ? true : false,
              }))
            );
          }}
          onRecoveryFlowsChange={setRecoveryFlowDrafts}
          onAutomationsChange={(rows) => {
            setAutomationValidationError(null);
            automationDraftsTouchedRef.current = true;
            setAutomationDraftsTouched(true);
            setAutomationDrafts(rows);
          }}
          onOpenIntegrationDetail={(detail) =>
            void handleSettingsPanelChange("integrations", { integrationDetail: detail })
          }
          onOpenTemplates={() => void handleSettingsPanelChange("templates")}
          onUpdateTool={(connectionId, tool, patch) => void handleUpdateIntegrationTool(connectionId, tool, patch)}
          onSettingsFormChange={setSettingsForm}
          onWebsiteSyncDraftChange={(websiteId, draft) => {
            setCommerceFlowValidationError(null);
            setWebsiteSyncDraftsById((current) => {
              const previous = current[websiteId];
              return {
                ...current,
                [websiteId]: {
                  ...draft,
                  dirty: Boolean(previous?.dirty || draft.dirty),
                },
              };
            });
          }}
          onImagesScreenChange={setActionsImagesScreenOpen}
          hotmartAccessCases={hotmartAccessCases}
          hotmartAccessCasesLoading={hotmartAccessCasesLoading}
          hotmartAccessCaseActionLoading={integrationActionLoading}
          onRefreshHotmartAccessCases={() => void loadHotmartAccessCases()}
          onUpdateHotmartAccessCase={(accessCase, patch) => void handleUpdateHotmartAccessCase(accessCase, patch)}
        />
      );
    }

    return null;
  };

  if (initialLoading) {
    return <ChatbotPageInitialSkeleton />;
  }

  return (
    <div className="h-[100dvh] overflow-hidden bg-[#F7F8FA] text-gray-900">
      <main className="flex h-full min-h-0 flex-col">
        <header className="shrink-0 border-b border-gray-200 bg-white px-4 py-3 md:px-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="flex items-center">
                <button
                  onClick={() => navigate("/home")}
                  className="group relative mr-[26px] inline-flex h-10 w-[65px] shrink-0 items-center justify-start text-gray-700"
                  title="Volver al home"
                >
                  <ArrowLeft className="absolute left-0 size-4 opacity-0 transition group-hover:opacity-100" />
                  <img
                    src={FluxiLogo}
                    alt="Fluxi"
                    className="h-8 w-auto origin-left -translate-y-0.5 transition duration-200 group-hover:-translate-y-px group-hover:translate-x-6 group-hover:scale-[0.78]"
                  />
                </button>
                <div className="mr-[22px] h-8 w-px shrink-0 bg-gray-200" aria-hidden="true" />
                <MainWorkspaceTabs value={activeWorkspace} onChange={setActiveWorkspace} />
                {coreLoading && <Loader2 className="size-4 animate-spin text-gray-400" />}
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <button
                onClick={() => setNotificationsOpen((current) => !current)}
                className="relative inline-flex size-11 items-center justify-center rounded-full bg-transparent text-gray-700 transition hover:bg-gray-100"
                title="Notificaciones"
              >
                <Bell className="size-6" />
                {unreadNotificationsCount ? (
                  <span className="absolute right-1.5 top-1.5 inline-flex min-w-5 items-center justify-center rounded-full bg-red-500 px-1 text-[11px] font-bold leading-5 text-white">
                    {unreadNotificationsCount > 99 ? "99+" : unreadNotificationsCount}
                  </span>
                ) : null}
                <span className="sr-only">Notificaciones</span>
              </button>
              <button
                onClick={() => {
                  if (activeWorkspace === "appointments") {
                    setSettingsPanelTab("actions");
                    setActionsConfigSubTab("agendamientos");
                    setIntegrationDetail(null);
                  } else if (activeWorkspace === "states") {
                    setSettingsPanelTab("actions");
                    setActionsConfigSubTab("confirmaciones");
                    setIntegrationDetail(null);
                  } else {
                    setKnowledgeTabLoading(true);
                    setSettingsPanelTab("knowledge");
                    setIntegrationDetail(null);
                    setKnowledgePanelView("main");
                  }
                  setSettingsModalOpen(true);
                }}
                className="inline-flex size-11 items-center justify-center rounded-full bg-transparent text-gray-700 transition hover:bg-gray-100"
                title="Configuración del chatbot"
              >
                <Settings className="size-6" />
                <span className="sr-only">Configuración</span>
              </button>
            </div>
          </div>
        </header>

        {pageError ? (
          <div className="m-6 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
            {pageError}
          </div>
        ) : null}

        <section className="min-h-0 flex-1 overflow-hidden">
          {activeWorkspace === "appointments" ? (
            <Suspense
              fallback={
                <div className="flex h-full items-center justify-center bg-[#F7F8FA] text-sm font-medium text-gray-500">
                  <Loader2 className="mr-2 size-4 animate-spin text-violet-600" />
                  Cargando agendamientos…
                </div>
              }
            >
              <AppointmentSchedulingWorkspace />
            </Suspense>
          ) : activeWorkspace === "states" ? (
            metricsSurface === "dashboard" ? (
              <MetricsDashboard
                loading={metricsLoading}
                rangeMode={metricsRangeMode}
                rangeDays={metricsRangeDays}
                customFrom={metricsCustomFrom}
                customTo={metricsCustomTo}
                health={metricsHealth}
                overview={metricsOverview}
                confirmationsOverview={confirmationsOverview}
                orderFlows={orderFlowDrafts}
                onRangeChange={(days) => {
                  setMetricsRangeMode("preset");
                  setMetricsRangeDays(days);
                  void loadMetricsDashboard({ days });
                }}
                onApplyCustomRange={(from, to) => {
                  if (!from) return;
                  setMetricsRangeMode("custom");
                  setMetricsCustomFrom(from);
                  setMetricsCustomTo(to);
                  void loadMetricsDashboard({ customFrom: from, customTo: to || undefined });
                }}
                onClearCustomRange={() => {
                  setMetricsRangeMode("preset");
                  setMetricsCustomFrom("");
                  setMetricsCustomTo("");
                  void loadMetricsDashboard({ days: metricsRangeDays });
                }}
                onRefresh={() => {
                  if (metricsRangeMode === "custom" && metricsCustomFrom) {
                    void loadMetricsDashboard({
                      customFrom: metricsCustomFrom,
                      customTo: metricsCustomTo || undefined,
                    });
                    return;
                  }
                  void loadMetricsDashboard({ days: metricsRangeDays });
                }}
                onOpenKanban={(domain) => {
                  setStatesDomain(domain);
                  setMetricsSurface("kanban");
                  void loadStatesWorkspace(domain);
                }}
              />
            ) : (
              <StatesWorkspace
                domain={statesDomain}
                view={statesView}
                orderBoard={orderStateBoard}
                contactBoard={contactStateBoard}
                loading={statesLoading}
                onDomainChange={setStatesDomain}
                onViewChange={setStatesView}
                onUpdateOrder={updateOrderState}
                onUpdateContactState={updateContactState}
                onOpenContactChat={handleOpenContactConversationFromStates}
                onRefreshContactStates={() => void loadStatesWorkspace("contacts")}
                onBackToDashboard={() => setMetricsSurface("dashboard")}
              />
            )
          ) : activeWorkspace === "guardian" ? (
            <GuardianWorkspace
              guardianSettings={guardianSettings}
              guardianState={guardianState}
              guardianRuns={guardianRuns}
              guardianMemory={guardianMemory}
              guardianProposals={guardianProposals}
              guardianConversations={guardianConversations}
              focusSuggestionId={guardianSuggestionFocusId}
              saving={guardianSaving}
              loading={guardianTabLoading && !guardianTabLoaded}
              onFocusSuggestionHandled={() => setGuardianSuggestionFocusId(null)}
              onResolveProposal={(proposalId, status, userInput, note) =>
                void handleResolveGuardianProposal(proposalId, status, userInput, note)
              }
              onCreateConversation={handleCreateGuardianConversation}
              onSendMessage={handleSendGuardianMessage}
            />
          ) : (
            <InboxTabV2
              overview={overview}
              apiStatus={apiStatus}
              supportAiEnabled={settings?.ai_enabled ?? true}
              conversations={conversations}
              selectedConversation={selectedConversationFromList}
              messages={messages}
              messagesHasMore={messagesHasMore}
              messagesLoadingOlder={messagesLoadingOlder}
              onLoadOlderMessages={() => void loadOlderConversationMessages()}
              events={conversationEvents}
              deliveryLogs={conversationDeliveryLogs}
              contactOrders={contactOrders}
              contactOrdersLoading={contactOrdersLoading}
              personView={personView}
              onPersonViewRefresh={() => setPersonViewRefreshKey((key) => key + 1)}
              loading={conversationLoading || conversationsLoading}
              conversationsFetching={conversationsFetching || conversationsSearchPending}
              hasMoreConversations={conversationsHasNextPage}
              onLoadMoreConversations={fetchNextConversationsPage}
              bucketFilters={conversationBucketFilters}
              search={conversationSearch}
              priorityFilter={conversationPriorityFilter}
              customerStateFilter={conversationCustomerStateFilter}
              labelFilters={conversationLabelFilters}
              humanHelpFilter={conversationHumanHelpFilter}
              channelFilter={conversationChannelFilter}
              unansweredFilter={conversationUnansweredFilter}
              pendingApprovalFilter={conversationPendingApprovalFilter}
              deliveryErrorFilter={conversationDeliveryErrorFilter}
              filterSummary={notificationsSummary}
              learningModeEnabled={Boolean(settingsForm.learning_mode_enabled)}
              replyApproval={replyApproval}
              replyApprovalRecent={replyApprovalRecent}
              approvalActionLoading={approvalActionLoading}
              approvalAwaitingVersion={approvalAwaitingVersion}
              approvalSendError={approvalSendError}
              mediaResources={savedMediaResourceDrafts}
              composerValue={composerValue}
              forceScrollToBottomSignal={forceScrollToBottomSignal}
              sendingMessage={sendingMessage}
              actionLoading={conversationActionLoading}
              contactForm={contactForm}
              retryInstruction={retryInstruction}
              onBucketFiltersChange={setConversationBucketFilters}
              onSearchChange={setConversationSearch}
              onPriorityFilterChange={setConversationPriorityFilter}
              onCustomerStateFilterChange={setConversationCustomerStateFilter}
              onLabelFiltersChange={setConversationLabelFilters}
              onHumanHelpFilterChange={setConversationHumanHelpFilter}
              onChannelFilterChange={setConversationChannelFilter}
              onUnansweredFilterChange={setConversationUnansweredFilter}
              onDeliveryErrorFilterChange={setConversationDeliveryErrorFilter}
              onPendingApprovalFilterChange={setConversationPendingApprovalFilter}
              onApproveReply={(version, force) => void handleApproveReply(version, force)}
              onSendReplyFeedback={handleSendReplyFeedback}
              onRejectReply={handleRejectReply}
              onOpenWhatsAppSettings={openWhatsAppSettings}
              onRefreshConversations={() => void loadConversations()}
              onSelectConversation={setSelectedConversationId}
              onToggleAI={handleToggleConversationAI}
              onUpdateConversation={handleUpdateConversation}
              onResolveConversation={(conversationId) => void handleResolveConversation(conversationId)}
              onReopenConversation={(conversationId) => void handleReopenConversation(conversationId)}
              onRetryAI={(conversationId, traceId) => void handleRetryAI(conversationId, traceId)}
              onRetryMediaExtraction={(messageId) => void handleRetryMediaExtraction(messageId)}
              onCancelAI={(conversationId) => void handleCancelConversationAI(conversationId)}
              retryingTraceId={retryingTraceId}
              retryingConversationId={retryingConversationId}
              onSaveContact={(form, contactId) => void handleSaveContact(form, contactId)}
              onPrivateNoteChange={setPrivateNote}
              onCreatePrivateNote={handleCreatePrivateNote}
              onRetryInstructionChange={setRetryInstruction}
              onComposerChange={setComposerValue}
              onSendMessage={() => void handleSendMessage()}
              approvedTemplates={approvedTemplates}
              onLoadTemplates={async () => {
                await loadTemplates("APPROVED");
              }}
              onSendMedia={handleSendConversationMedia}
              onSendTemplate={handleSendConversationTemplate}
            />
          )}
        </section>
      </main>

      {notificationsOpen ? (
        <NotificationsSidebar
          notifications={notifications}
          summary={notificationsSummary}
          onClose={() => setNotificationsOpen(false)}
          onOpen={handleOpenNotification}
          onOpenSuggestionGroup={handleOpenSuggestionGroup}
          onOpenHumanHelpGroup={handleOpenHumanHelpGroup}
          onOpenUnanswered={handleOpenUnansweredGroup}
          onOpenReplyApprovals={handleOpenReplyApprovals}
        />
      ) : null}

      <ConfirmModal
        open={!!approvalForceConfirm}
        positionClass="fixed"
        zIndexClass="z-[80]"
        title="El cliente escribió después de esta respuesta."
        description="¿Enviarla igual?"
        actions={[
          {
            label: "Cancelar",
            variant: "secondary",
            onClick: () => setApprovalForceConfirm(null),
            disabled: approvalActionLoading === "approve",
          },
          {
            label: "Enviar igual",
            variant: "primary",
            onClick: () => {
              if (approvalForceConfirm) void handleApproveReply(approvalForceConfirm.version, true);
            },
            loading: approvalActionLoading === "approve",
            disabled: approvalActionLoading === "approve",
          },
        ]}
      />

      {settingsModalOpen ? (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center bg-white/70 p-4 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          aria-labelledby="chatbot-settings-modal-title"
        >
          <div className="flex h-[min(84vh,820px)] w-[min(1120px,calc(100vw-48px))] overflow-hidden rounded-[30px] border border-gray-200 bg-white shadow-[0_24px_70px_rgba(15,23,42,0.18)]">
            <aside className="hidden w-[232px] shrink-0 border-r border-gray-200 bg-white px-4 py-7 md:flex md:flex-col">
              <button
                onClick={() => void requestCloseSettingsModal()}
                className="mb-8 inline-flex size-10 shrink-0 items-center justify-center rounded-full text-gray-900 transition hover:bg-gray-100"
                title="Cerrar configuración"
              >
                <X className="size-6" strokeWidth={2.1} />
              </button>

              <nav className="space-y-1.5">
                {settingsTabs.map((tab) => {
                  const Icon = tab.icon;
                  const active = settingsPanelTab === tab.id;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => void handleSettingsPanelChange(tab.id)}
                      className={`flex h-11 w-full items-center gap-3 rounded-[14px] px-3 text-left text-[15px] font-medium transition ${
                        active
                          ? "bg-gray-100 text-gray-950"
                          : "text-gray-600 hover:bg-gray-50 hover:text-gray-950"
                      }`}
                    >
                      <Icon className="size-4 shrink-0" strokeWidth={1.8} />
                      <span className="truncate">{tab.label}</span>
                    </button>
                  );
                })}
              </nav>
            </aside>

            <div className="flex min-w-0 flex-1 flex-col bg-white">
              <div className="shrink-0 border-b border-gray-200 bg-white px-4 py-3 md:hidden">
                <div className="mb-3 flex items-center justify-between gap-3">
                  <button
                    onClick={() => void requestCloseSettingsModal()}
                    className="inline-flex size-10 shrink-0 items-center justify-center rounded-full text-gray-900 hover:bg-gray-100"
                    title="Cerrar configuración"
                  >
                    <X className="size-6" />
                  </button>
                  {settingsPanelTab !== "actions" ? (
                    <h2 className="min-w-0 truncate text-lg font-semibold text-gray-950">{settingsModalTitle}</h2>
                  ) : (
                    <span className="min-w-0 flex-1" />
                  )}
                </div>
                <div className="flex gap-2 overflow-x-auto">
                  {settingsTabs.map((tab) => {
                    const Icon = tab.icon;
                    const active = settingsPanelTab === tab.id;
                    return (
                      <button
                        key={tab.id}
                        onClick={() => void handleSettingsPanelChange(tab.id)}
                        className={`inline-flex h-10 shrink-0 items-center gap-2 rounded-full border px-3 text-sm font-medium ${
                          active
                            ? "border-gray-200 bg-gray-100 text-gray-950"
                            : "border-gray-200 bg-white text-gray-600"
                        }`}
                      >
                        <Icon className="size-4" />
                        {tab.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {settingsPanelTab === "actions" ? (
                <ActionsConfigSubTabBar
                  activeSubTab={actionsConfigSubTab}
                  onSubTabChange={setActionsConfigSubTab}
                  className="sticky top-0 z-30 shrink-0 bg-white px-6 pb-0 pt-7 md:px-9 md:pt-8"
                />
              ) : (
                <div className="shrink-0 bg-white px-6 pb-4 pt-7 md:px-9 md:pb-5 md:pt-8">
                  <div className="flex items-center justify-between gap-4">
                    <h2
                      id="chatbot-settings-modal-title"
                      className="min-w-0 truncate text-[20px] font-semibold leading-tight text-gray-950 md:text-[24px]"
                    >
                      {settingsModalTitle}
                    </h2>
                    <div className="flex shrink-0 items-center gap-2">
                      {settingsPanelTab === "knowledge" && knowledgePanelView === "main" ? (
                        <button
                          type="button"
                          onClick={() => setKnowledgePanelView("test")}
                          className="inline-flex h-9 shrink-0 items-center justify-center whitespace-nowrap rounded-full bg-gray-100 px-4 text-sm font-semibold text-gray-800 transition hover:bg-gray-200"
                        >
                          Probar chat
                        </button>
                      ) : null}
                    </div>
                  </div>
                  <div className="mt-5 border-t border-gray-200" />
                </div>
              )}

              <div className={`min-h-0 flex-1 overflow-y-auto overflow-x-hidden bg-white md:px-9 ${settingsPanelTab === "actions" ? "px-4 pb-12" : "px-6 pb-12"}`}>
                <div>
                  {renderSettingsPanel()}
                </div>
              </div>

              {/* El botón Guardar solo aparece si el TAB ACTIVO tiene cambios (spec Julian).
                  Dentro de la sub-pantalla Configurar imágenes NO se muestra: allá solo queda
                  "← Volver" y se guarda desde el guardar de la acción al volver. */}
              {isSettingsTabDirty(settingsPanelTab) && !actionsImagesScreenOpen ? (
                <div className="flex shrink-0 justify-end border-t border-gray-200 bg-white px-6 py-4 md:px-10">
                          <button
                            onClick={() => void handleSaveSettingsModal()}
                            disabled={settingsModalSaving || Boolean(settingsSaveBlockedReason)}
                            className={`inline-flex h-10 items-center gap-2 rounded-full px-5 text-sm font-semibold transition disabled:cursor-not-allowed disabled:opacity-50 ${
                              settingsSaveBlockedReason
                                ? "bg-gray-200 text-gray-600"
                                : "bg-gray-950 text-white hover:bg-gray-800"
                            }`}
                          >
                    {settingsModalSaving ? <Loader2 className="size-4 animate-spin" /> : <Save className="size-4" />}
                    Guardar cambios
                  </button>
                </div>
              ) : null}
            </div>
          </div>

          <ConfirmModal
            open={Boolean(pendingSettingsPanelChange)}
            title="Cambios sin guardar"
            description={`Tienes cambios sin guardar en ${settingsTabDirtyLabel(settingsPanelTab)}. Si continúas, se descartan.`}
            actions={[
              {
                label: "Continuar y descartar cambios",
                onClick: continuePendingSettingsPanelChange,
                variant: "secondary",
              },
              {
                label: "Quedarme acá",
                onClick: () => setPendingSettingsPanelChange(null),
                variant: "primary",
              },
            ]}
          />

          <ConfirmModal
            open={pendingSettingsModalClose}
            title="Cambios sin guardar"
            description="Si cierras ahora, se descartan los cambios que no guardaste."
            actions={[
              {
                label: "Continuar y descartar cambios",
                onClick: continuePendingSettingsModalClose,
                variant: "secondary",
              },
              {
                label: "Quedarme acá",
                onClick: () => {
                  setPendingSettingsModalClose(false);
                  setPendingSettingsModalCloseAction(null);
                },
                variant: "primary",
              },
            ]}
          />
        </div>
      ) : null}

      <ConfirmModal
        open={Boolean(pendingIntegrationSave)}
        title="Cambios sin guardar"
        description="Tienes cambios sin guardar en la configuración del chatbot. ¿Guardar la integración igual?"
        positionClass="fixed"
        zIndexClass="z-[80]"
        actions={[
          {
            label: "Cancelar",
            onClick: () => setPendingIntegrationSave(null),
            variant: "secondary",
          },
          {
            label: "Ir a guardar cambios",
            onClick: () => {
              setPendingIntegrationSave(null);
              setSettingsPanelTab("knowledge");
            },
            variant: "secondary",
          },
          {
            label: "Guardar integración",
            onClick: confirmPendingIntegrationSave,
            variant: "primary",
          },
        ]}
      />
    </div>
  );
};

const NotificationsSidebar = ({
  notifications,
  summary,
  onClose,
  onOpen,
  onOpenSuggestionGroup,
  onOpenHumanHelpGroup,
  onOpenUnanswered,
  onOpenReplyApprovals,
}: {
  notifications: SupportNotification[];
  summary: NotificationsSummary | null;
  onClose: () => void;
  onOpen: (notification: SupportNotification) => void;
  onOpenSuggestionGroup: (proposalId: string | null, notificationIds: number[]) => void;
  onOpenHumanHelpGroup: (notificationIds: number[]) => void;
  onOpenUnanswered: () => void;
  onOpenReplyApprovals: (notificationIds: number[], singleTargetId: string | null) => void;
}) => {
  const unread = notifications.filter((item) => item.status === "unread");
  const suggestionGroup = unread.filter((item) => item.type === "guardian_suggestion");
  const humanHelpGroup = unread.filter((item) => item.type === "human_help_requested");
  const approvalGroup = unread.filter((item) => item.type === "reply_approval_pending");
  const otherNotifications = notifications.filter(
    (item) =>
      item.type !== "guardian_suggestion" &&
      item.type !== "human_help_requested" &&
      item.type !== "reply_approval_pending"
  );
  // Los números grandes salen del summary (estado REAL: propuestas pendientes, ayuda humana
  // activa, sin responder). Mientras el summary no carga, caemos al conteo de notificaciones.
  const suggestionsPendingCount = summary ? summary.suggestions_pending : suggestionGroup.length;
  const humanHelpActiveCount = summary ? summary.human_help_active : humanHelpGroup.length;
  const unansweredCount = summary ? summary.unanswered : null;
  const pendingApprovalsCount = summary?.pending_approvals ?? approvalGroup.length;
  const singleApprovalTargetId =
    pendingApprovalsCount === 1 ? approvalGroup[0]?.target_id ?? null : null;
  const latestSuggestion = [...suggestionGroup].sort(
    (a, b) => Date.parse(b.created_at || "") - Date.parse(a.created_at || "")
  )[0];
  const latestSuggestionProposalId =
    latestSuggestion?.target_id ||
    (typeof latestSuggestion?.target_payload?.proposal_id === "string"
      ? latestSuggestion.target_payload.proposal_id
      : null) ||
    (typeof latestSuggestion?.target_payload?.proposalId === "string"
      ? latestSuggestion.target_payload.proposalId
      : null);

  return (
  <aside className="fixed bottom-0 right-0 top-0 z-[60] flex w-[min(390px,calc(100vw-24px))] flex-col border-l border-gray-200 bg-white shadow-[0_18px_60px_rgba(15,23,42,0.16)]">
    <div className="flex h-[73px] shrink-0 items-center justify-between border-b border-gray-200 px-5">
      <div>
        <h2 className="text-lg font-semibold text-gray-950">Notificaciones</h2>
        <p className="mt-0.5 text-xs text-gray-500">
          {unread.length} sin leer
        </p>
      </div>
      <button
        type="button"
        onClick={onClose}
        className="inline-flex size-9 items-center justify-center rounded-full text-gray-500 transition hover:bg-gray-100 hover:text-gray-950"
        title="Cerrar notificaciones"
      >
        <X className="size-5" />
      </button>
    </div>
    <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4">
      {notifications.length || summary !== null ? (
        <div className="space-y-2.5">
          {suggestionsPendingCount > 0 ? (
            <div className="rounded-2xl bg-gray-100 px-4 py-4">
              <p className="text-3xl font-bold leading-none text-gray-950">{suggestionsPendingCount}</p>
              <p className="mt-2 text-sm font-semibold text-gray-900">
                {suggestionsPendingCount === 1 ? "Sugerencia sin revisar" : "Sugerencias sin revisar"}
              </p>
              <button
                type="button"
                onClick={() => {
                  void onOpenSuggestionGroup(
                    latestSuggestionProposalId || null,
                    suggestionGroup.map((item) => item.id)
                  );
                }}
                className="mt-3 inline-flex h-9 items-center rounded-full bg-gray-950 px-4 text-sm font-semibold text-white hover:bg-gray-800"
              >
                Ver
              </button>
            </div>
          ) : null}
          {pendingApprovalsCount > 0 ? (
            <div className="rounded-2xl bg-gray-100 px-4 py-4">
              <p className="text-3xl font-bold leading-none text-gray-950">{pendingApprovalsCount}</p>
              <p className="mt-2 text-sm font-semibold text-gray-900">
                {pendingApprovalsCount === 1
                  ? "1 respuesta esperando tu aprobación"
                  : `${pendingApprovalsCount} respuestas esperando tu aprobación`}
              </p>
              <button
                type="button"
                onClick={() =>
                  onOpenReplyApprovals(approvalGroup.map((item) => item.id), singleApprovalTargetId)
                }
                className="mt-3 inline-flex h-9 items-center rounded-full bg-gray-950 px-4 text-sm font-semibold text-white hover:bg-gray-800"
              >
                Ver
              </button>
            </div>
          ) : null}
          {humanHelpActiveCount > 0 ? (
            <div className="rounded-2xl bg-gray-100 px-4 py-4">
              <p className="text-3xl font-bold leading-none text-gray-950">{humanHelpActiveCount}</p>
              <p className="mt-2 text-sm font-semibold text-gray-900">
                {humanHelpActiveCount === 1
                  ? "Conversación pidió ayuda humana"
                  : "Conversaciones pidieron ayuda humana"}
              </p>
              <button
                type="button"
                onClick={() => void onOpenHumanHelpGroup(humanHelpGroup.map((item) => item.id))}
                className="mt-3 inline-flex h-9 items-center rounded-full bg-gray-950 px-4 text-sm font-semibold text-white hover:bg-gray-800"
              >
                Ver
              </button>
            </div>
          ) : null}
          {unansweredCount !== null ? (
            <div className="rounded-2xl bg-gray-100 px-4 py-4">
              <p className="text-3xl font-bold leading-none text-gray-950">{unansweredCount}</p>
              <p className="mt-2 text-sm font-semibold text-gray-900">
                {unansweredCount === 1 ? "Conversación sin responder" : "Conversaciones sin responder"}
              </p>
              <button
                type="button"
                onClick={onOpenUnanswered}
                className="mt-3 inline-flex h-9 items-center rounded-full bg-gray-950 px-4 text-sm font-semibold text-white hover:bg-gray-800"
              >
                Ver
              </button>
            </div>
          ) : null}
          {otherNotifications.map((notification) => {
            const isUnread = notification.status === "unread";
            return (
              <button
                key={notification.id}
                type="button"
                onClick={() => onOpen(notification)}
                className={`w-full rounded-2xl px-4 py-3 text-left transition ${
                  isUnread ? "bg-gray-100 hover:bg-gray-200" : "bg-gray-50 hover:bg-gray-100"
                }`}
              >
                <div className="flex items-start gap-3">
                  <span
                    className={`mt-1.5 size-2 shrink-0 rounded-full ${
                      isUnread ? "bg-red-500" : "bg-gray-300"
                    }`}
                  />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold leading-5 text-gray-950">{notification.title}</p>
                    {notification.body ? (
                      <p className="mt-1 line-clamp-3 text-xs leading-5 text-gray-600">{notification.body}</p>
                    ) : null}
                    <div className="mt-2 flex items-center justify-between gap-3 text-xs text-gray-400">
                      <span>{notification.created_at ? formatMessageTime(notification.created_at) : "Reciente"}</span>
                      <span className="font-semibold text-gray-700">Ver</span>
                    </div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      ) : (
        <div className="rounded-2xl bg-gray-50 px-4 py-8 text-center text-sm leading-6 text-gray-500">
          No hay notificaciones por ahora.
        </div>
      )}
    </div>
  </aside>
  );
};

type InboxTabProps = {
  overview: SupportOverview | null;
  apiStatus: ReturnType<typeof whatsappApiStatus>;
  supportAiEnabled: boolean;
  conversations: SupportConversation[];
  selectedConversation: SupportConversation | null;
  messages: SupportMessage[];
  messagesHasMore?: boolean;
  messagesLoadingOlder?: boolean;
  onLoadOlderMessages?: () => void;
  events: ChatbotEvent[];
  deliveryLogs: ChatbotDeliveryLog[];
  contactOrders: SupportConfirmation[];
  contactOrdersLoading: boolean;
  personView?: PersonView | null;
  onPersonViewRefresh?: () => void;
  loading: boolean;
  conversationsFetching?: boolean;
  hasMoreConversations?: boolean;
  onLoadMoreConversations?: () => void;
  bucketFilters: ConversationBucket[];
  search: string;
  priorityFilter: string;
  customerStateFilter: string;
  labelFilters: string[];
  humanHelpFilter: HumanHelpFilter;
  channelFilter: string;
  unansweredFilter: boolean;
  pendingApprovalFilter: boolean;
  deliveryErrorFilter: boolean;
  filterSummary: NotificationsSummary | null;
  learningModeEnabled: boolean;
  replyApproval: ReplyApproval | null;
  replyApprovalRecent: ReplyApproval[];
  approvalActionLoading: null | "approve" | "feedback" | "reject";
  approvalAwaitingVersion: boolean;
  approvalSendError: string | null;
  mediaResources: MediaResourceDraft[];
  composerValue: string;
  forceScrollToBottomSignal: number;
  sendingMessage: boolean;
  actionLoading: ConversationActionLoading;
  retryingTraceId: string | null;
  retryingConversationId: number | null;
  contactForm: ContactFormState;
  retryInstruction: string;
  onBucketFiltersChange: (value: ConversationBucket[]) => void;
  onSearchChange: (value: string) => void;
  onPriorityFilterChange: (value: string) => void;
  onCustomerStateFilterChange: (value: string) => void;
  onLabelFiltersChange: (value: string[]) => void;
  onHumanHelpFilterChange: (value: HumanHelpFilter) => void;
  onChannelFilterChange: (value: string) => void;
  onUnansweredFilterChange: (value: boolean) => void;
  onDeliveryErrorFilterChange: (value: boolean) => void;
  onPendingApprovalFilterChange: (value: boolean) => void;
  onApproveReply: (version: number, force?: boolean) => void;
  onSendReplyFeedback: (text: string) => Promise<boolean>;
  onRejectReply: (reason: string) => Promise<boolean>;
  onOpenWhatsAppSettings: () => void;
  onRefreshConversations: () => void;
  onSelectConversation: (conversationId: number | null) => void;
  onToggleAI: (conversationId: number, enabled: boolean) => void;
  onUpdateConversation: (conversationId: number, patch: SupportConversationUpdate) => void;
  onResolveConversation: (conversationId: number) => void;
  onReopenConversation: (conversationId: number) => void;
  onRetryAI: (conversationId: number, failureTraceId?: string | null) => void;
  onRetryMediaExtraction: (messageId: number) => void | Promise<void>;
  onCancelAI: (conversationId: number) => void;
  onSaveContact: (form: ContactFormState, contactId?: number) => void;
  onPrivateNoteChange: (value: string) => void;
  onCreatePrivateNote: (content?: string) => boolean | Promise<boolean>;
  onRetryInstructionChange: (value: string) => void;
  onComposerChange: (value: string) => void;
  onSendMessage: () => void;
  approvedTemplates: WhatsAppTemplate[];
  onLoadTemplates: () => Promise<void>;
  onSendMedia: (file: File, kind: "image" | "video" | "voice", caption?: string) => Promise<boolean>;
  onSendTemplate: (payload: {
    template_name: string;
    language?: string | null;
    variables?: string[];
  }) => Promise<boolean>;
};

type ConversationDetailsDraft = {
  status: ConversationStatus | string;
  priority: ConversationPriority | string;
  customerState: string;
  name: string;
  email: string;
  labels: string[];
  note: string;
};

const detailsDraftFromConversation = (conversation: SupportConversation | null): ConversationDetailsDraft => ({
  status: conversation?.status || "open",
  priority: conversation?.priority || "normal",
  customerState: getCustomerStateTag(conversation?.contact?.tags),
  name: conversation?.contact?.name || "",
  email: conversation?.contact?.email || "",
  labels: normalizeLabels(conversation?.labels || []),
  note: "",
});

const CompactSelect = ({
  value,
  options,
  formatOption,
  optionIcon,
  onChange,
  ariaLabel,
  className = "",
}: {
  value: string;
  options: string[];
  formatOption: (value: string) => string;
  optionIcon?: (value: string) => React.ReactNode;
  onChange: (value: string) => void;
  ariaLabel: string;
  className?: string;
}) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const closeOnOutside = (event: MouseEvent) => {
      const target = event.target;
      if (target instanceof Node && containerRef.current?.contains(target)) return;
      setOpen(false);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", closeOnOutside);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("mousedown", closeOnOutside);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [open]);

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => setOpen((current) => !current)}
        aria-label={ariaLabel}
        className={`flex h-10 w-full items-center rounded-full border bg-white pl-3 pr-2 text-left text-sm font-semibold outline-none transition ${
          open ? "border-gray-300 bg-gray-50" : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
        }`}
      >
        <span className="min-w-0 flex-1 truncate text-gray-800">{formatOption(value)}</span>
        <span className="ml-2 inline-flex size-6 shrink-0 items-center justify-center rounded-full text-gray-400">
          <ChevronDown className={`size-4 transition ${open ? "rotate-180" : ""}`} />
        </span>
      </button>
      {open ? (
        <div className="absolute left-0 right-0 top-11 z-30 max-h-64 overflow-auto rounded-2xl border border-gray-200 bg-white p-1.5 shadow-[0px_14px_36px_rgba(15,23,42,0.14)]">
          {options.map((option) => {
            const selected = option === value;
            return (
              <button
                key={option}
                type="button"
                onClick={() => {
                  onChange(option);
                  setOpen(false);
                }}
                className={`flex min-h-9 w-full items-center justify-between gap-3 rounded-xl px-3 py-2 text-left text-sm transition ${
                  selected ? "bg-gray-100 font-semibold text-gray-950" : "text-gray-700 hover:bg-gray-50"
                }`}
              >
                <span className="flex min-w-0 items-center gap-2">
                  {optionIcon ? optionIcon(option) : null}
                  <span className="truncate">{formatOption(option)}</span>
                </span>
                {selected ? <CheckCircle2 className="size-4 shrink-0 text-gray-500" /> : null}
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
};

const InlineSelectRow = ({
  label,
  value,
  options,
  formatOption,
  optionIcon,
  onChange,
}: {
  label: string;
  value: string;
  options: string[];
  formatOption: (value: string) => string;
  optionIcon?: (value: string) => React.ReactNode;
  onChange: (value: string) => void;
}) => (
  <div className="flex items-center justify-between gap-3 py-3 text-sm">
    <span className="text-gray-500">{label}</span>
    <CompactSelect
      value={value}
      options={options}
      formatOption={formatOption}
      optionIcon={optionIcon}
      onChange={onChange}
      ariaLabel={label}
      className="w-[210px] max-w-[65%] shrink-0"
    />
  </div>
);

const DetailInputRow = ({
  label,
  value,
  placeholder,
  readOnly = false,
  onChange,
}: {
  label: string;
  value: string;
  placeholder?: string;
  readOnly?: boolean;
  onChange?: (value: string) => void;
}) => (
  <label className="flex items-center justify-between gap-3 py-3 text-sm">
    <span className="text-gray-500">{label}</span>
    <input
      value={value}
      readOnly={readOnly}
      placeholder={placeholder}
      onChange={(event) => onChange?.(event.target.value)}
      className={`h-10 w-[210px] max-w-[65%] rounded-full border border-gray-200 px-3 text-right text-sm font-semibold outline-none transition placeholder:text-gray-400 focus:border-gray-300 focus:ring-2 focus:ring-gray-200 ${
        readOnly ? "bg-gray-50 text-gray-500" : "bg-white text-gray-900"
      }`}
    />
  </label>
);

const InboxTabV2 = ({
  overview,
  apiStatus,
  supportAiEnabled,
  conversations,
  selectedConversation,
  messages,
  messagesHasMore = false,
  messagesLoadingOlder = false,
  onLoadOlderMessages,
  events,
  deliveryLogs,
  contactOrders,
  contactOrdersLoading,
  personView,
  onPersonViewRefresh,
  loading,
  conversationsFetching = false,
  hasMoreConversations = false,
  onLoadMoreConversations,
  bucketFilters,
  search,
  priorityFilter,
  customerStateFilter,
  labelFilters,
  humanHelpFilter,
  channelFilter,
  unansweredFilter,
  pendingApprovalFilter,
  deliveryErrorFilter,
  filterSummary,
  learningModeEnabled,
  replyApproval,
  replyApprovalRecent,
  approvalActionLoading,
  approvalAwaitingVersion,
  approvalSendError,
  mediaResources,
  composerValue,
  forceScrollToBottomSignal,
  sendingMessage,
  actionLoading,
  retryingTraceId,
  retryingConversationId,
  contactForm,
  retryInstruction,
  onBucketFiltersChange,
  onSearchChange,
  onPriorityFilterChange,
  onCustomerStateFilterChange,
  onLabelFiltersChange,
  onHumanHelpFilterChange,
  onChannelFilterChange,
  onUnansweredFilterChange,
  onPendingApprovalFilterChange,
  onDeliveryErrorFilterChange,
  onApproveReply,
  onSendReplyFeedback,
  onRejectReply,
  onOpenWhatsAppSettings,
  onRefreshConversations,
  onSelectConversation,
  onToggleAI,
  onUpdateConversation,
  onResolveConversation,
  onReopenConversation,
  onRetryAI,
  onRetryMediaExtraction,
  onCancelAI,
  onSaveContact,
  onPrivateNoteChange,
  onCreatePrivateNote,
  onRetryInstructionChange,
  onComposerChange,
  onSendMessage,
  approvedTemplates,
  onLoadTemplates,
  onSendMedia,
  onSendTemplate,
}: InboxTabProps) => {
  const [mediaExtractionRetryMessageId, setMediaExtractionRetryMessageId] = useState<number | null>(null);
  const simulateAiProcessingDot = useMemo(() => {
    return getLocalChatbotDebugParams()?.get("simulate_ai_processing") === "1";
  }, []);
  const simulatedWorkIndicator = useMemo(() => {
    return getLocalChatbotDebugParams()?.get("simulate_work_indicator") || "";
  }, []);
  const orderedMessages = useMemo(() => [...messages].reverse(), [messages]);
  const activeAiFailure = useMemo(
    () => getActiveAiFailure(selectedConversation, events),
    [selectedConversation, events]
  );
  const aiPipelineActive = Boolean(
    selectedConversation && (
      isAiPipelineActive(selectedConversation, retryingTraceId, retryingConversationId, actionLoading, supportAiEnabled) ||
      (simulateAiProcessingDot && conversations[0]?.id === selectedConversation.id)
    )
  );
  const aiGenerationCancellable = Boolean(
    selectedConversation && isAiGenerationCancellable(selectedConversation)
  );
  const aiUiPhase = useMemo<AiUiPhase>(() => {
    if (simulateAiProcessingDot && selectedConversation && conversations[0]?.id === selectedConversation.id) return "preparing";
    return getConversationAiUiPhase(
      selectedConversation,
      messages,
      events,
      retryingTraceId,
      retryingConversationId,
      actionLoading,
      supportAiEnabled
    );
  }, [
    actionLoading,
    conversations,
    events,
    messages,
    retryingConversationId,
    retryingTraceId,
    selectedConversation,
    simulateAiProcessingDot,
    supportAiEnabled,
  ]);
  const timelineItems = useMemo(() => {
    const items: ChatTimelineItem[] = orderedMessages.map((message) => ({
      type: "message",
      key: `message-${getMessageStableKey(message)}`,
      createdAt: message.created_at,
      message,
    }));
    if (activeAiFailure) {
      items.push({
        type: "ai_failure",
        key: `ai-failure-${activeAiFailure.trace_id || activeAiFailure.created_at || "active"}`,
        createdAt: activeAiFailure.created_at,
        failure: activeAiFailure,
      });
    }
    const activeFailureTraceId = activeAiFailure?.trace_id || null;
    const messageIds = new Set(orderedMessages.map((message) => message.id));
    deliveryLogs.forEach((log) => {
      if (typeof log.message_id === "number" && messageIds.has(log.message_id)) return;
      if (activeFailureTraceId && log.trace_id === activeFailureTraceId) return;
      const failure = getDeliveryFailureTimelineItem(log);
      if (!failure) return;
      items.push({
        type: "ai_failure",
        key: `delivery-failure-${log.id}-${log.trace_id || "trace"}`,
        createdAt: failure.created_at,
        failure,
      });
    });
    events.forEach((event) => {
      const notice = getActionNotice(event);
      if (!notice) return;
      items.push({
        type: "action_notice",
        key: `action-notice-${event.id}-${event.event_type}`,
        createdAt: notice.created_at,
        notice,
      });
    });
    const sortedItems = items.sort((a, b) => getTimelineItemUiOrder(a) - getTimelineItemUiOrder(b));
    const timeline: ChatTimelineItem[] = [];
    let currentDateKey: string | null = null;

    sortedItems.forEach((item) => {
      const dateKey = timelineDateKey(item.createdAt);
      if (dateKey !== currentDateKey) {
        timeline.push({
          type: "date_separator",
          key: `date-${dateKey}-${item.key}`,
          createdAt: item.createdAt,
          label: formatTimelineDateLabel(item.createdAt),
        });
        currentDateKey = dateKey;
      }
      timeline.push(item);
    });

    // Modo aprendiz: la respuesta esperando aprobación (o el aviso de expirada/rechazada
    // reciente) se ancla al final del hilo, después del último mensaje.
    const lastMessageAt = orderedMessages.length
      ? timestampMs(orderedMessages[orderedMessages.length - 1].created_at)
      : 0;
    const activeApproval =
      replyApproval &&
      (replyApproval.status === "pending" ||
        replyApproval.status === "approving" ||
        replyApproval.status === "send_failed")
        ? replyApproval
        : null;
    if (activeApproval) {
      timeline.push({
        type: "reply_approval",
        key: `reply-approval-${activeApproval.id}`,
        createdAt: activeApproval.updated_at,
        approval: activeApproval,
      });
    } else {
      const recentApproval = replyApprovalRecent[0];
      if (recentApproval && timestampMs(recentApproval.updated_at) > lastMessageAt) {
        if (recentApproval.status === "expired") {
          timeline.push({
            type: "reply_approval_expired",
            key: `reply-approval-expired-${recentApproval.id}`,
            createdAt: recentApproval.updated_at,
            approval: recentApproval,
          });
        } else if (recentApproval.status === "rejected") {
          timeline.push({
            type: "reply_approval_rejected",
            key: `reply-approval-rejected-${recentApproval.id}`,
            createdAt: recentApproval.updated_at,
            approval: recentApproval,
          });
        }
      }
    }

    return timeline;
  }, [activeAiFailure, deliveryLogs, events, orderedMessages, replyApproval, replyApprovalRecent]);
  const timelineEndKey = timelineItems.length ? timelineItems[timelineItems.length - 1]?.key || "" : "";
  const privateNotes = useMemo(
    () => orderedMessages.filter((message) => (message.private || message.direction === "internal") && !isRetryInstructionMessage(message)),
    [orderedMessages]
  );
  const deliveryLogByMessageId = useMemo(() => {
    const byId = new Map<number, ChatbotDeliveryLog>();
    deliveryLogs.forEach((log) => {
      if (typeof log.message_id === "number") byId.set(log.message_id, log);
    });
    return byId;
  }, [deliveryLogs]);
  const effectiveDeliveryStatusByMessageId = useMemo(() => {
    const statusByMessageId = new Map<number, string>();
    deliveryLogs.forEach((log) => {
      if (log.direction !== "outbound" || typeof log.message_id !== "number") return;
      const logStatus = String(log.status || "").toLowerCase();
      statusByMessageId.set(
        log.message_id,
        highestOutboundDeliveryStatus(statusByMessageId.get(log.message_id), logStatus)
      );
    });
    return statusByMessageId;
  }, [deliveryLogs]);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [showAdvancedLogs, setShowAdvancedLogs] = useState(false);
  const [retryOpen, setRetryOpen] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const hasActiveConversationFilters =
    humanHelpFilter !== "all" ||
    unansweredFilter ||
    pendingApprovalFilter ||
    Boolean(priorityFilter) ||
    Boolean(customerStateFilter) ||
    Boolean(channelFilter) ||
    labelFilters.length > 0 ||
    Boolean(search.trim()) ||
    !(bucketFilters.length === 1 && bucketFilters[0] === "open");
  useEffect(() => {
    if (humanHelpFilter === "with") setFiltersOpen(true);
  }, [humanHelpFilter]);
  useEffect(() => {
    if (unansweredFilter) setFiltersOpen(true);
  }, [unansweredFilter]);
  useEffect(() => {
    if (pendingApprovalFilter) setFiltersOpen(true);
  }, [pendingApprovalFilter]);
  const [tagPickerOpen, setTagPickerOpen] = useState(false);
  const [tagInput, setTagInput] = useState("");
  const [filterTagPickerOpen, setFilterTagPickerOpen] = useState(false);
  const [filterTagInput, setFilterTagInput] = useState("");
  const [infoAdvancedOpen, setInfoAdvancedOpen] = useState(false);
  const [showAiSummaries, setShowAiSummaries] = useState(() =>
    getStoredBoolean(CHATBOT_SHOW_AI_SUMMARIES_STORAGE_KEY)
  );
  const [draft, setDraft] = useState<ConversationDetailsDraft>(() => detailsDraftFromConversation(selectedConversation));
  const noteRef = useRef<HTMLTextAreaElement | null>(null);
  const composerRef = useRef<HTMLTextAreaElement | null>(null);
  const messageScrollRef = useRef<HTMLDivElement | null>(null);
  const contactAutosaveTimerRef = useRef<number | null>(null);
  const shouldStickToBottomRef = useRef(true);
  const threadMetricsRef = useRef<{ conversationId: number | null; endKey: string; length: number } | null>(null);
  const threadScrollSnapshotRef = useRef<{ conversationId: number | null; scrollHeight: number; scrollTop: number } | null>(null);
  const [composerExpanded, setComposerExpanded] = useState(false);
  const [composerHeight, setComposerHeight] = useState(40);
  const conversationListRef = useRef<HTMLDivElement | null>(null);
  // Filtro "Por aprobar" (modo aprendiz): se aplica sobre las conversaciones ya cargadas.
  const visibleConversations = useMemo(
    () =>
      pendingApprovalFilter
        ? conversations.filter(
            (conversation) => getConversationAiRuntime(conversation)?.status === "pending_approval"
          )
        : conversations,
    [conversations, pendingApprovalFilter]
  );
  const conversationVirtualizer = useVirtualizer({
    count: visibleConversations.length,
    getScrollElement: () => conversationListRef.current,
    estimateSize: () => 72,
    overscan: 8,
  });
  useEffect(() => {
    const element = conversationListRef.current;
    if (!element || !hasMoreConversations || !onLoadMoreConversations) return;
    const handleScroll = () => {
      if (element.scrollTop + element.clientHeight >= element.scrollHeight - 200) {
        onLoadMoreConversations();
      }
    };
    element.addEventListener("scroll", handleScroll);
    return () => element.removeEventListener("scroll", handleScroll);
  }, [visibleConversations.length, hasMoreConversations, onLoadMoreConversations]);
  const conversationAiChecked = supportAiEnabled && Boolean(selectedConversation?.ai_enabled);
  const conversationAiLabel = !supportAiEnabled
    ? "IA global apagada"
    : selectedConversation?.ai_enabled
      ? "IA activa"
      : "IA pausada";
  const contactOptedOut = Boolean(selectedConversation?.contact?.opted_out_at);

  useEffect(() => {
    setDraft(detailsDraftFromConversation(selectedConversation));
    setTagInput("");
    setShowAdvancedLogs(false);
    shouldStickToBottomRef.current = true;
  }, [selectedConversation?.id]);

  useEffect(() => () => {
    if (contactAutosaveTimerRef.current) window.clearTimeout(contactAutosaveTimerRef.current);
  }, []);

  useEffect(() => {
    window.localStorage.setItem(CHATBOT_SHOW_AI_SUMMARIES_STORAGE_KEY, showAiSummaries ? "true" : "false");
  }, [showAiSummaries]);

  useEffect(() => {
    const composer = composerRef.current;
    if (!composer) return;
    composer.style.height = "auto";
    const nextHeight = Math.min(composer.scrollHeight, CHAT_COMPOSER_MAX_HEIGHT);
    composer.style.height = `${nextHeight}px`;
    setComposerHeight(nextHeight);
    if (composer.scrollHeight > CHAT_COMPOSER_MAX_HEIGHT) {
      composer.scrollTop = composer.scrollHeight;
    }
    setComposerExpanded(nextHeight > 44);
  }, [composerValue, selectedConversation?.id]);

  const composerOverlayOffset = Math.max(128, composerHeight + 56);
  const threadBottomPadding = composerOverlayOffset + 64;

  useLayoutEffect(() => {
    const scroller = messageScrollRef.current;
    if (!scroller) return;
    const conversationId = selectedConversation?.id ?? null;
    const previous = threadMetricsRef.current;
    const previousScroll = threadScrollSnapshotRef.current;
    const conversationChanged = previous?.conversationId !== conversationId;
    threadMetricsRef.current = {
      conversationId,
      endKey: timelineEndKey,
      length: timelineItems.length,
    };
    if (!shouldStickToBottomRef.current && !conversationChanged) {
      if (previousScroll?.conversationId === conversationId) {
        const heightDelta = scroller.scrollHeight - previousScroll.scrollHeight;
        scroller.scrollTop = Math.max(0, previousScroll.scrollTop + heightDelta);
      }
    } else {
      window.requestAnimationFrame(() => {
        scroller.scrollTop = scroller.scrollHeight;
      });
    }
    return () => {
      const currentScroller = messageScrollRef.current;
      if (!currentScroller) return;
      threadScrollSnapshotRef.current = {
        conversationId,
        scrollHeight: currentScroller.scrollHeight,
        scrollTop: currentScroller.scrollTop,
      };
    };
  }, [timelineItems.length, timelineEndKey, selectedConversation?.id, detailsOpen, composerHeight]);

  useEffect(() => {
    if (!forceScrollToBottomSignal) return;
    const scroller = messageScrollRef.current;
    if (!scroller) return;
    shouldStickToBottomRef.current = true;
    window.requestAnimationFrame(() => {
      scroller.scrollTop = scroller.scrollHeight;
    });
  }, [forceScrollToBottomSignal]);

  // === Compositor "+" (APR-7F): adjuntos, nota de voz, plantillas de reapertura y ventana 24h ===
  type ComposerDraft =
    | { kind: "image"; file: File; url: string; name: string }
    | { kind: "video"; file: File; name: string }
    | { kind: "voice"; blob: Blob; url: string }
    | { kind: "template"; template: WhatsAppTemplate; variables: string[] };

  const composerPillRef = useRef<HTMLDivElement | null>(null);
  const imageInputRef = useRef<HTMLInputElement | null>(null);
  const videoInputRef = useRef<HTMLInputElement | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const recordedChunksRef = useRef<Blob[]>([]);
  const recordingTimerRef = useRef<number | null>(null);
  const composerDraftRef = useRef<ComposerDraft | null>(null);

  const [composerMenuOpen, setComposerMenuOpen] = useState(false);
  const [templatePickerOpen, setTemplatePickerOpen] = useState(false);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [composerDraft, setComposerDraft] = useState<ComposerDraft | null>(null);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingSeconds, setRecordingSeconds] = useState(0);
  const [sendingAttachment, setSendingAttachment] = useState(false);
  const [nowMs, setNowMs] = useState(() => Date.now());

  const sessionWindow = selectedConversation?.session_window ?? null;
  const windowState: "none" | "open" | "closed" =
    !sessionWindow || sessionWindow.open === null
      ? "none"
      : sessionWindow.open
        ? "open"
        : "closed";
  const composerEnhanced = windowState !== "none";
  const sessionExpiresAt = sessionWindow?.expires_at ?? null;
  // Canales sociales (Pieza 3): la ventana es una ESTIMACION informativa (postbacks,
  // reacciones y links m.me la reabren sin que siempre nos llegue el evento), asi que
  // NO bloquea el compositor; y en IG/MS no existen plantillas ni notas de voz.
  const conversationChannel = String(selectedConversation?.channel || "whatsapp").toLowerCase();
  const isSocialChannel = conversationChannel === "instagram" || conversationChannel === "messenger";
  const socialChannelLabel = conversationChannel === "instagram" ? "Instagram" : "Messenger";

  const lastInboundAt = messages.reduce(
    (max, message) => (message.direction === "inbound" ? Math.max(max, timestampMs(message.created_at)) : max),
    0
  );
  const reopenTemplateSent = messages.some(
    (message) =>
      message.direction === "outbound" &&
      String(message.content_type || "").toLowerCase() === "template" &&
      timestampMs(message.created_at) > lastInboundAt
  );
  const reopenTemplatePending = windowState === "closed" && reopenTemplateSent && !isSocialChannel;

  const closedInfoText = isSocialChannel
    ? `La ventana estimada de 24 horas se cerró. En ${socialChannelLabel} no existen plantillas: se reabre cuando el cliente escriba o interactúe (botón o reacción). Puedes intentar enviar; si Meta lo rechaza, verás el motivo en el mensaje.`
    : reopenTemplatePending
      ? "Plantilla enviada — esperando la respuesta del cliente para reabrir la conversación."
      : "La ventana de 24 horas para responder se cerró. Envía una plantilla para reabrir la conversación; el envío libre se reactiva cuando el cliente responda.";

  const windowCountdown = (() => {
    if (windowState !== "open" || !sessionExpiresAt) return null;
    const remaining = timestampMs(sessionExpiresAt) - nowMs;
    if (remaining <= 0 || remaining >= 3 * 60 * 60 * 1000) return null;
    const totalMinutes = Math.floor(remaining / 60000);
    return `La ventana ${isSocialChannel ? "estimada " : ""}de 24h se cierra en ${Math.floor(totalMinutes / 60)}h ${totalMinutes % 60}m`;
  })();

  const reopenTemplates = approvedTemplates.filter((template) =>
    template.purpose?.startsWith("conversation_reopen")
  );
  const otherApprovedTemplates = approvedTemplates.filter(
    (template) => !template.purpose?.startsWith("conversation_reopen")
  );

  const composerDraftSendable = !composerDraft
    ? false
    : composerDraft.kind === "template"
      ? composerDraft.variables.every((value) => value.trim().length > 0)
      : true;
  const sendButtonDisabled =
    isRecording ||
    sendingAttachment ||
    (composerDraft
      ? !composerDraftSendable
      : !selectedConversation || !composerValue.trim() || sendingMessage || contactOptedOut || (windowState === "closed" && !isSocialChannel));

  const formatRecordingTime = (seconds: number) =>
    `${String(Math.floor(seconds / 60)).padStart(2, "0")}:${String(seconds % 60).padStart(2, "0")}`;

  const countTemplateVariables = (body: string | null) => {
    const matches = body ? body.match(/\{\{\s*(\d+)\s*\}\}/g) : null;
    if (!matches) return 0;
    return Math.max(0, ...matches.map((token) => Number(token.replace(/[^\d]/g, ""))));
  };

  const revokeDraftUrl = (item: ComposerDraft | null) => {
    if (item && (item.kind === "image" || item.kind === "voice") && item.url) URL.revokeObjectURL(item.url);
  };

  const clearDraft = () => {
    revokeDraftUrl(composerDraft);
    setComposerDraft(null);
  };

  const stopMediaStream = () => {
    mediaStreamRef.current?.getTracks().forEach((track) => track.stop());
    mediaStreamRef.current = null;
  };

  useEffect(() => {
    composerDraftRef.current = composerDraft;
  }, [composerDraft]);

  useEffect(() => {
    if (!composerMenuOpen && !templatePickerOpen) return;
    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target;
      if (target instanceof Node && composerPillRef.current?.contains(target)) return;
      setComposerMenuOpen(false);
      setTemplatePickerOpen(false);
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setComposerMenuOpen(false);
        setTemplatePickerOpen(false);
      }
    };
    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [composerMenuOpen, templatePickerOpen]);

  useEffect(() => {
    if (windowState !== "open" || !sessionExpiresAt) return;
    const intervalId = window.setInterval(() => setNowMs(Date.now()), 60000);
    return () => window.clearInterval(intervalId);
  }, [windowState, sessionExpiresAt]);

  useEffect(() => {
    // Al cambiar de conversación (o desmontar) descarta cualquier borrador/grabación del compositor.
    return () => {
      setComposerMenuOpen(false);
      setTemplatePickerOpen(false);
      const recorder = mediaRecorderRef.current;
      if (recorder && recorder.state !== "inactive") {
        recorder.onstop = null;
        recorder.stop();
      }
      mediaStreamRef.current?.getTracks().forEach((track) => track.stop());
      mediaStreamRef.current = null;
      if (recordingTimerRef.current) {
        window.clearInterval(recordingTimerRef.current);
        recordingTimerRef.current = null;
      }
      setIsRecording(false);
      setRecordingSeconds(0);
      const activeDraft = composerDraftRef.current;
      if (activeDraft && (activeDraft.kind === "image" || activeDraft.kind === "voice") && activeDraft.url) {
        URL.revokeObjectURL(activeDraft.url);
      }
      setComposerDraft(null);
    };
  }, [selectedConversation?.id]);

  const handlePickImage = () => {
    setComposerMenuOpen(false);
    imageInputRef.current?.click();
  };

  const handlePickVideo = () => {
    setComposerMenuOpen(false);
    videoInputRef.current?.click();
  };

  const handleImageSelected = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    if (file.type !== "image/jpeg" && file.type !== "image/png") {
      toast.error("Formato no soportado (usa JPG o PNG)");
      return;
    }
    const imageMaxBytes = isSocialChannel ? 8 * 1024 * 1024 : 5 * 1024 * 1024;
    if (file.size > imageMaxBytes) {
      toast.error(`La imagen supera ${isSocialChannel ? "8MB" : "5MB"}`);
      return;
    }
    revokeDraftUrl(composerDraft);
    setComposerDraft({ kind: "image", file, url: URL.createObjectURL(file), name: file.name });
  };

  const handleVideoSelected = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    if (file.type !== "video/mp4" && (isSocialChannel || file.type !== "video/3gpp")) {
      toast.error("Formato no soportado (usa MP4)");
      return;
    }
    const videoMaxBytes = isSocialChannel ? 25 * 1024 * 1024 : 16 * 1024 * 1024;
    if (file.size > videoMaxBytes) {
      toast.error(`El video supera ${isSocialChannel ? "25MB" : "16MB"}`);
      return;
    }
    revokeDraftUrl(composerDraft);
    setComposerDraft({ kind: "video", file, name: file.name });
  };

  const handleStartRecording = async () => {
    setComposerMenuOpen(false);
    const supportedMimeType =
      typeof MediaRecorder !== "undefined"
        ? ["audio/ogg;codecs=opus", "audio/webm;codecs=opus"].find((type) => MediaRecorder.isTypeSupported(type))
        : undefined;
    if (!supportedMimeType) {
      toast.error("Tu navegador no soporta grabar notas de voz");
      return;
    }
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      toast.error("No pudimos acceder al micrófono — revisa los permisos del navegador");
      return;
    }
    revokeDraftUrl(composerDraft);
    setComposerDraft(null);
    mediaStreamRef.current = stream;
    recordedChunksRef.current = [];
    const recorder = new MediaRecorder(stream, { mimeType: supportedMimeType });
    mediaRecorderRef.current = recorder;
    recorder.ondataavailable = (event) => {
      if (event.data.size > 0) recordedChunksRef.current.push(event.data);
    };
    recorder.onstop = () => {
      const blob = new Blob(recordedChunksRef.current, { type: supportedMimeType });
      stopMediaStream();
      if (recordingTimerRef.current) {
        window.clearInterval(recordingTimerRef.current);
        recordingTimerRef.current = null;
      }
      setIsRecording(false);
      setRecordingSeconds(0);
      if (blob.size > 16 * 1024 * 1024) {
        toast.error("La nota de voz supera 16MB");
        return;
      }
      setComposerDraft({ kind: "voice", blob, url: URL.createObjectURL(blob) });
    };
    recorder.start();
    setIsRecording(true);
    setRecordingSeconds(0);
    recordingTimerRef.current = window.setInterval(() => setRecordingSeconds((seconds) => seconds + 1), 1000);
  };

  const handleStopRecording = () => {
    const recorder = mediaRecorderRef.current;
    if (recorder && recorder.state !== "inactive") recorder.stop();
  };

  const handleReRecord = () => {
    clearDraft();
    void handleStartRecording();
  };

  const handleOpenTemplatePicker = () => {
    setComposerMenuOpen(false);
    setTemplatePickerOpen(true);
    if (!approvedTemplates.length && !templatesLoading) {
      setTemplatesLoading(true);
      void onLoadTemplates().finally(() => setTemplatesLoading(false));
    }
  };

  const handleSelectTemplate = (template: WhatsAppTemplate) => {
    revokeDraftUrl(composerDraft);
    setComposerDraft({
      kind: "template",
      template,
      variables: Array(countTemplateVariables(template.body_text)).fill(""),
    });
    setTemplatePickerOpen(false);
  };

  const handleTemplateVariableChange = (index: number, value: string) => {
    setComposerDraft((current) => {
      if (!current || current.kind !== "template") return current;
      const nextVariables = [...current.variables];
      nextVariables[index] = value;
      return { ...current, variables: nextVariables };
    });
  };

  const handleSendDraft = async () => {
    if (!composerDraft || sendingAttachment) return;
    const draftToSend = composerDraft;
    const caption = composerValue.trim();
    setSendingAttachment(true);
    let sent = false;
    try {
      if (draftToSend.kind === "image" || draftToSend.kind === "video") {
        sent = await onSendMedia(draftToSend.file, draftToSend.kind, caption || undefined);
      } else if (draftToSend.kind === "voice") {
        const extension = draftToSend.blob.type.includes("ogg") ? "ogg" : "webm";
        const file = new File([draftToSend.blob], `nota-de-voz.${extension}`, { type: draftToSend.blob.type });
        sent = await onSendMedia(file, "voice");
      } else {
        sent = await onSendTemplate({
          template_name: draftToSend.template.name,
          language: draftToSend.template.language,
          variables: draftToSend.variables.length ? draftToSend.variables : undefined,
        });
      }
    } finally {
      setSendingAttachment(false);
    }
    if (sent) {
      clearDraft();
      if (draftToSend.kind === "image" || draftToSend.kind === "video") onComposerChange("");
      setComposerMenuOpen(false);
      setTemplatePickerOpen(false);
    }
  };

  const handleMessageScroll = () => {
    const scroller = messageScrollRef.current;
    if (!scroller) return;
    const distanceFromBottom = scroller.scrollHeight - scroller.scrollTop - scroller.clientHeight;
    shouldStickToBottomRef.current = distanceFromBottom < 120;
    if (scroller.scrollTop < 120 && messagesHasMore && !messagesLoadingOlder) {
      onLoadOlderMessages?.();
    }
  };

  const labelOptions = useMemo(() => {
    const selected = new Set(draft.labels.map((label) => label.toLowerCase()));
    return normalizeLabels(conversations.flatMap((conversation) => conversation.labels || [])).filter(
      (label) => !selected.has(label.toLowerCase())
    );
  }, [conversations, draft.labels]);
  const conversationLabelOptions = useMemo(
    () => normalizeLabels(conversations.flatMap((conversation) => conversation.labels || [])),
    [conversations]
  );
  const visibleFilterLabelOptions = useMemo(() => {
    const selected = new Set(labelFilters.map((label) => label.toLowerCase()));
    const query = filterTagInput.trim().toLowerCase();
    return conversationLabelOptions
      .filter((label) => label !== HUMAN_HELP_REQUESTED_LABEL)
      .filter((label) => !selected.has(label.toLowerCase()))
      .filter((label) => !query || label.toLowerCase().includes(query))
      .slice(0, 8);
  }, [conversationLabelOptions, filterTagInput, labelFilters]);
  const visibleLabelOptions = useMemo(() => {
    const query = tagInput.trim().toLowerCase();
    return labelOptions.filter((label) => !query || label.toLowerCase().includes(query)).slice(0, 8);
  }, [labelOptions, tagInput]);

  const initialCustomerState = getCustomerStateTag(selectedConversation?.contact?.tags);
  const noteDirty = Boolean(draft.note.trim());

  const contactFormFromDraft = (nextDraft: ConversationDetailsDraft): ContactFormState | null => {
    if (!selectedConversation?.contact) return null;
    const nextTags =
      nextDraft.customerState !== initialCustomerState
        ? mergeCustomerStateTag(contactForm.tags, nextDraft.customerState)
        : contactForm.tags;
    return {
      ...contactForm,
      name: nextDraft.name,
      email: nextDraft.email,
      tags: nextTags,
      phone: selectedConversation.contact.phone || contactForm.phone,
    };
  };

  const persistContactDraft = (nextDraft: ConversationDetailsDraft) => {
    const contactId = selectedConversation?.contact?.id;
    const form = contactFormFromDraft(nextDraft);
    if (!contactId || !form) return;
    onSaveContact(form, contactId);
  };

  const scheduleContactAutosave = (nextDraft: ConversationDetailsDraft) => {
    const contactId = selectedConversation?.contact?.id;
    const form = contactFormFromDraft(nextDraft);
    if (!contactId || !form) return;
    if (contactAutosaveTimerRef.current) window.clearTimeout(contactAutosaveTimerRef.current);
    contactAutosaveTimerRef.current = window.setTimeout(() => {
      onSaveContact(form, contactId);
      contactAutosaveTimerRef.current = null;
    }, 450);
  };

  const updateConversationStatus = (value: string) => {
    setDraft((current) => ({ ...current, status: value }));
    if (!selectedConversation || value === primaryConversationStatus(selectedConversation.status)) return;
    onUpdateConversation(selectedConversation.id, { status: value });
  };

  const updateConversationPriority = (value: string) => {
    setDraft((current) => ({ ...current, priority: value }));
    if (!selectedConversation || value === selectedConversation.priority) return;
    onUpdateConversation(selectedConversation.id, { priority: value });
  };

  const updateCustomerState = (value: string) => {
    const nextDraft = { ...draft, customerState: value };
    setDraft(nextDraft);
    if (value === initialCustomerState) return;
    persistContactDraft(nextDraft);
  };

  const updateContactDraft = (field: "name" | "email", value: string) => {
    const nextDraft = { ...draft, [field]: value };
    setDraft(nextDraft);
    scheduleContactAutosave(nextDraft);
  };

  const persistLabels = (nextLabels: string[]) => {
    setDraft((current) => ({ ...current, labels: nextLabels }));
    if (!selectedConversation || labelsEqual(nextLabels, selectedConversation.labels || [])) return;
    onUpdateConversation(selectedConversation.id, { labels: nextLabels });
  };

  const addLabel = (label: string) => {
    const next = label.trim();
    if (!next) return;
    persistLabels(normalizeLabels([...draft.labels, next]));
    setTagInput("");
    setTagPickerOpen(false);
  };

  const removeLabel = (label: string) => {
    persistLabels(draft.labels.filter((item) => item.toLowerCase() !== label.toLowerCase()));
  };

  const acceptDraftNote = async () => {
    const content = draft.note.trim();
    if (!selectedConversation || !content || isConversationActionLoading(actionLoading, "private-note", selectedConversation.id)) return;
    const saved = await onCreatePrivateNote(content);
    if (!saved) return;
    setDraft((current) => ({ ...current, note: "" }));
    onPrivateNoteChange("");
  };
  const statusFilterOptions: ConversationBucket[] = ["open", "resolved"];
  const bucketCounts = useMemo(() => {
    const byStatus = overview?.conversations.by_status || {};
    return {
      open: overview?.conversations.active ?? byStatus.open ?? 0,
      resolved: byStatus.resolved ?? 0,
    } satisfies Record<ConversationBucket, number>;
  }, [overview]);
  const selectedBucketSet = useMemo(() => new Set(bucketFilters), [bucketFilters]);
  const toggleBucketFilter = (bucket: ConversationBucket) => {
    if (selectedBucketSet.has(bucket) && bucketFilters.length === 1) return;
    onBucketFiltersChange([bucket]);
  };

  useEffect(() => {
    if (!selectedConversation) return;
    const status = primaryConversationStatus(selectedConversation.status);
    const selectedBuckets = bucketFilters.length ? bucketFilters : (["open"] as ConversationBucket[]);
    if (!selectedBuckets.includes(status as ConversationBucket)) {
      onSelectConversation(null);
    }
  }, [bucketFilters, conversations, onSelectConversation, selectedConversation]);
  const addFilterLabel = (label: string) => {
    const next = label.trim();
    if (!next) return;
    onLabelFiltersChange(normalizeLabels([...labelFilters, next]));
    setFilterTagInput("");
    setFilterTagPickerOpen(false);
  };
  const removeFilterLabel = (label: string) => {
    onLabelFiltersChange(labelFilters.filter((item) => item.toLowerCase() !== label.toLowerCase()));
  };

  const selectedConversationInfoItem = selectedConversation ? conversationToContactStateItem(selectedConversation) : null;
  const detailsPanel = selectedConversation && detailsOpen && selectedConversationInfoItem ? (
    <ContactStateSidebar
      item={selectedConversationInfoItem}
      onClose={() => setDetailsOpen(false)}
      onOpenChat={() => {}}
      onRefreshContactStates={onRefreshConversations}
      initialConversation={selectedConversation}
      initialOrders={contactOrders}
      initialNotes={privateNotes}
      personView={personView}
      onPersonViewRefresh={onPersonViewRefresh}
      onJumpToConversation={(id) => {
        onSelectConversation(id);
        setDetailsOpen(false);
      }}
      showOpenChatButton={false}
      showAiSummaries={showAiSummaries}
      onShowAiSummariesChange={setShowAiSummaries}
      onDebugResetComplete={() => {
        setDetailsOpen(false);
        onSelectConversation(null);
        onRefreshConversations();
      }}
      className="fixed inset-y-0 right-0 z-40 shadow-2xl xl:relative xl:inset-auto xl:z-auto xl:shadow-none"
    />
  ) : null;

  return (
    <div
      className={`grid h-full min-h-0 grid-cols-1 lg:grid-cols-[420px_minmax(0,1fr)] ${
        selectedConversation && detailsOpen ? "xl:grid-cols-[420px_minmax(0,1fr)_391px]" : ""
      }`}
    >
      <aside className="flex min-h-0 flex-col border-r border-gray-200 bg-white">
        <div className="p-3">
          <div className="flex items-center gap-2">
            <div className="relative min-w-0 flex-1">
              <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-gray-400" />
              <input
                value={search}
                onChange={(event) => onSearchChange(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") onRefreshConversations();
                }}
                placeholder="Buscar chat"
                className="h-10 w-full rounded-full border border-gray-200 bg-white pl-9 pr-3 text-sm outline-none transition focus:border-[#4368E5] focus:ring-2 focus:ring-[#4368E5]/10"
              />
            </div>
            <button
              type="button"
              onClick={() => setFiltersOpen((open) => !open)}
              className={`relative inline-flex size-10 shrink-0 items-center justify-center rounded-full border transition ${
                filtersOpen || hasActiveConversationFilters
                  ? "border-gray-200 bg-[#F6F7F9] text-gray-800"
                  : "border-gray-200 bg-white text-gray-600 hover:bg-gray-50"
              }`}
              title="Filtrar conversaciones"
            >
              <Filter className="size-4" />
              {hasActiveConversationFilters && !filtersOpen ? (
                <span className="absolute right-1.5 top-1.5 size-2 rounded-full bg-gray-950" />
              ) : null}
            </button>
          </div>
          {filtersOpen ? (
            <div className="mt-3 space-y-3 rounded-2xl bg-gray-50 p-3">
              <div>
                <span className="mb-2 block text-xs font-medium text-gray-500">Estado</span>
                <div className="flex flex-wrap gap-2">
                  {statusFilterOptions.map((bucket) => {
                    const active = bucketFilters.includes(bucket);
                    return (
                      <button
                        key={bucket}
                        type="button"
                        onClick={() => toggleBucketFilter(bucket)}
                        className={`inline-flex h-8 items-center gap-2 rounded-full px-3 text-sm font-semibold transition ${
                          active ? "bg-gray-900 text-white" : "bg-white text-gray-600 hover:bg-gray-100"
                        }`}
                      >
                        {formatConversationBucket(bucket)}
                        <span className={`text-xs ${active ? "text-white/70" : "text-gray-400"}`}>
                          {bucketCounts[bucket]}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="flex items-center justify-between gap-3">
                <span className="text-xs font-medium text-gray-500">Canal</span>
                <CompactSelect
                  value={channelFilter || "all"}
                  options={["all", "whatsapp", "messenger", "instagram"]}
                  formatOption={(value) => (value === "all" ? "Todos" : formatChannel(value))}
                  onChange={(value) => onChannelFilterChange(value === "all" ? "" : value)}
                  ariaLabel="Filtrar por canal"
                  className="w-[210px] max-w-[70%]"
                />
              </div>
              <div className="flex items-center justify-between gap-3">
                <span className="text-xs font-medium text-gray-500">Prioridad</span>
                <CompactSelect
                  value={priorityFilter || "all"}
                  options={["all", ...priorityOptions]}
                  formatOption={(value) => (value === "all" ? "Todas" : formatConversationPriority(value))}
                  optionIcon={(value) => (value === "all" ? null : <PriorityAvatarMark priority={value} />)}
                  onChange={(value) => onPriorityFilterChange(value === "all" ? "" : value)}
                  ariaLabel="Filtrar por prioridad"
                  className="w-[210px] max-w-[70%]"
                />
              </div>
              <div className="flex items-center justify-between gap-3">
                <span className="text-xs font-medium text-gray-500">Estado del cliente</span>
                <CompactSelect
                  value={customerStateFilter || "all"}
                  options={["all", ...customerStateOptions]}
                  formatOption={(value) => (value === "all" ? "Todos" : formatContactState(value))}
                  onChange={(value) => onCustomerStateFilterChange(value === "all" ? "" : value)}
                  ariaLabel="Filtrar por estado cliente"
                  className="w-[210px] max-w-[70%]"
                />
              </div>
              {/* Fila 2: señales, estandarizadas como chips con contador (Julián).
                  Visibles aun en 0 — "es sano confirmar que hay cero errores". */}
              <div>
                <span className="mb-2 block text-xs font-medium text-gray-500">Señales</span>
                <div className="flex flex-wrap gap-2">
                  {(
                    [
                      {
                        key: "human_help",
                        label: "Ayuda humana",
                        count: filterSummary?.human_help_active ?? 0,
                        active: humanHelpFilter === "with",
                        onToggle: () => onHumanHelpFilterChange(humanHelpFilter === "with" ? "all" : "with"),
                        activeClass: "bg-emerald-600 text-white",
                      },
                      {
                        key: "unanswered",
                        label: "Sin responder",
                        count: filterSummary?.unanswered ?? 0,
                        active: unansweredFilter,
                        onToggle: () => onUnansweredFilterChange(!unansweredFilter),
                        activeClass: "bg-gray-900 text-white",
                      },
                      {
                        key: "delivery_error",
                        label: "Error",
                        count: filterSummary?.delivery_error ?? 0,
                        active: deliveryErrorFilter,
                        onToggle: () => onDeliveryErrorFilterChange(!deliveryErrorFilter),
                        activeClass: "bg-red-600 text-white",
                      },
                      {
                        key: "pending_approval",
                        label: "Por aprobar",
                        count: filterSummary?.pending_approvals ?? 0,
                        active: pendingApprovalFilter,
                        onToggle: () => onPendingApprovalFilterChange(!pendingApprovalFilter),
                        activeClass: "bg-gray-900 text-white",
                      },
                    ] as const
                  ).map((chip) => (
                    <button
                      key={chip.key}
                      type="button"
                      onClick={chip.onToggle}
                      className={`inline-flex h-8 items-center gap-2 rounded-full px-3 text-sm font-semibold transition ${
                        chip.active ? chip.activeClass : "bg-white text-gray-600 hover:bg-gray-100"
                      }`}
                    >
                      {chip.label}
                      <span className={`text-xs ${chip.active ? "text-white/70" : "text-gray-400"}`}>{chip.count}</span>
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex items-start justify-between gap-3">
                <span className="pt-2.5 text-xs font-medium text-gray-500">Etiquetas</span>
                <div
                  className="relative w-[210px] max-w-[70%] shrink-0"
                  onBlur={(event) => {
                    if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
                      setFilterTagPickerOpen(false);
                    }
                  }}
                >
                  <input
                    value={filterTagInput}
                    onChange={(event) => setFilterTagInput(event.target.value)}
                    onFocus={() => setFilterTagPickerOpen(true)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === ",") {
                        event.preventDefault();
                        addFilterLabel(filterTagInput);
                      }
                    }}
                    placeholder="Agregar etiqueta"
                    className="h-10 w-full rounded-full border border-gray-200 bg-white px-3 text-sm font-semibold text-gray-800 outline-none transition placeholder:font-medium placeholder:text-gray-400 focus:border-gray-300 focus:ring-2 focus:ring-gray-200"
                  />
                  {filterTagPickerOpen && visibleFilterLabelOptions.length > 0 ? (
                    <div className="absolute left-0 right-0 top-11 z-30 rounded-2xl border border-gray-200 bg-white p-2 shadow-[0px_2px_8px_0px_rgba(99,99,99,0.2)]">
                      {visibleFilterLabelOptions.map((label) => (
                        <button
                          key={label}
                          type="button"
                          onMouseDown={(event) => event.preventDefault()}
                          onClick={() => addFilterLabel(label)}
                          className="flex h-8 w-full items-center rounded-full px-3 text-left text-xs font-medium text-gray-600 hover:bg-gray-50"
                        >
                          {formatConversationLabel(label)}
                        </button>
                      ))}
                    </div>
                  ) : null}
                </div>
              </div>
              {labelFilters.length > 0 ? (
                <div className="flex flex-wrap justify-end gap-2">
                  {labelFilters.filter((label) => label !== HUMAN_HELP_REQUESTED_LABEL).map((label) => (
                    <button
                      key={label}
                      type="button"
                      onClick={() => removeFilterLabel(label)}
                      className="inline-flex h-7 items-center gap-1 rounded-full border border-gray-200 bg-white px-2.5 text-xs font-semibold text-gray-600 hover:border-gray-300"
                      title="Quitar filtro"
                    >
                      {formatConversationLabel(label)}
                      <XCircle className="size-3.5" />
                    </button>
                  ))}
                </div>
              ) : null}
              <button
                type="button"
                onClick={() => setFiltersOpen(false)}
                className="flex w-full items-center justify-center gap-1 py-2 text-xs text-gray-500 transition hover:text-gray-700"
              >
                Ocultar menú de filtros
                <ChevronUp className="size-3.5" />
              </button>
            </div>
          ) : null}
        </div>

        <div ref={conversationListRef} className="min-h-0 flex-1 overflow-y-auto p-2">
          {visibleConversations.length === 0 && (conversationsFetching || loading) ? (
            <div className="flex items-center justify-center py-10 text-sm font-medium text-gray-400">
              <Loader2 className="mr-2 size-4 animate-spin" />
              Actualizando conversaciones
            </div>
          ) : visibleConversations.length === 0 ? (
            <EmptyState icon={MessagesSquare} title="Sin conversaciones" body="Cuando entren mensajes de WhatsApp, Messenger o Instagram aparecerán aquí." />
          ) : (
            <div
              style={{
                height: `${conversationVirtualizer.getTotalSize()}px`,
                width: "100%",
                position: "relative",
              }}
            >
              {conversationVirtualizer.getVirtualItems().map((virtualRow) => {
                const conversation = visibleConversations[virtualRow.index];
                if (!conversation) return null;
                const index = virtualRow.index;
                const simulatedState =
                  index === 0 && (simulatedWorkIndicator === "needs_action" || simulatedWorkIndicator === "failed")
                    ? simulatedWorkIndicator
                    : null;
                const workState = simulatedState || (simulateAiProcessingDot && index === 0
                  ? "working"
                  : getConversationWorkState(conversation, supportAiEnabled, retryingTraceId, retryingConversationId, actionLoading));
                const pendingCustomerMessages =
                  simulatedState === "needs_action" ? Math.max(1, conversation.unread_count || 0) : getPendingCustomerMessageCount(conversation);
                // B-25 (spec Julian): hora y preview del ULTIMO mensaje del hilo, sea de quien sea.
                const customerTime = formatCustomerReplyAge(conversation.last_message_at || conversation.last_customer_message_at);
                const simulationMeta = getConversationSimulationMeta(conversation);
                return (
                  <button
                    key={conversation.id}
                    onClick={() => {
                      shouldStickToBottomRef.current = true;
                      onSelectConversation(conversation.id);
                    }}
                    className={`absolute left-2 right-2 flex w-[calc(100%-1rem)] items-center gap-3 rounded-xl border px-3 py-3 text-left transition ${
                      selectedConversation?.id === conversation.id
                        ? "border-[#E4E8F0] bg-[#F6F7F9]"
                        : "border-transparent bg-white hover:bg-[#F6F7F9]"
                    }`}
                    style={{
                      transform: `translateY(${virtualRow.start}px)`,
                    }}
                  >
                    <ConversationAvatar priority={conversation.priority} channel={conversation.channel} />
                    <div className="min-w-0 flex-1">
                      <div className="flex min-w-0 items-baseline justify-between gap-3">
                        <div className="flex min-w-0 items-center gap-2">
                          <p className="truncate text-sm font-semibold text-gray-950">{contactName(conversation)}</p>
                          {simulationMeta ? (
                            <span className="shrink-0 rounded-full bg-violet-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-violet-700">
                              Simulación
                            </span>
                          ) : null}
                        </div>
                        {customerTime ? <span className="shrink-0 text-[11px] font-medium text-gray-400">{customerTime}</span> : null}
                      </div>
                      <p className="mt-1 truncate text-sm leading-5 text-gray-500">
                        {conversation.last_message_preview || "Sin vista previa"}
                      </p>
                    </div>
                    <ConversationWorkIndicator state={workState} pendingCount={pendingCustomerMessages} />
                  </button>
                );
              })}
            </div>
          )}
          {conversationsFetching ? (
            <div className="flex items-center justify-center py-2 text-xs font-medium text-gray-400">
              <Loader2 className="mr-2 size-3.5 animate-spin" />
              Actualizando conversaciones
            </div>
          ) : null}
        </div>
        <div className="border-t border-gray-100 p-3">
          <button
            type="button"
            onClick={onOpenWhatsAppSettings}
            className="flex w-full items-center justify-between gap-3 rounded-2xl bg-white px-3 py-2.5 text-left transition hover:bg-gray-100"
            title="Ver estado de WhatsApp Cloud API"
          >
            <span className="flex min-w-0 items-center gap-2">
              <span className="inline-flex size-8 shrink-0 items-center justify-center rounded-full bg-white text-gray-600 ring-1 ring-gray-200">
                <RiWhatsappLine className="size-4" />
              </span>
              <span className="min-w-0">
                <span className="block truncate text-sm font-semibold text-gray-800">{apiStatus.label}</span>
                <span className="block truncate text-xs text-gray-500">WhatsApp Cloud API</span>
              </span>
            </span>
          </button>
        </div>
      </aside>

      <section className="flex min-h-0 flex-col" style={CHAT_THREAD_BACKGROUND_STYLE}>
        <div className="border-b border-gray-100 px-4 py-3 backdrop-blur" style={CHAT_THREAD_TOPBAR_STYLE}>
          <div className="flex min-h-10 items-center justify-between gap-3">
            {selectedConversation ? (
              <div className="min-w-0 flex-1">
                <button
                  type="button"
                  onClick={() => setDetailsOpen(true)}
                  className="inline-flex max-w-full items-center gap-2.5 rounded-full py-1 pl-1 pr-3 text-left transition hover:bg-gray-100/80 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-300 focus-visible:ring-offset-2"
                  title="Abrir detalles de la conversación"
                  aria-label="Abrir detalles de la conversación"
                >
                  <ConversationAvatar priority={selectedConversation.priority} channel={selectedConversation.channel} />
                  <span className="truncate text-sm font-semibold text-gray-900">
                    {conversationIdentityLabel(selectedConversation)}
                  </span>
                  {getConversationSimulationMeta(selectedConversation) ? (
                    <span className="shrink-0 rounded-full bg-violet-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-violet-700">
                      Simulación
                    </span>
                  ) : null}
                </button>
              </div>
            ) : (
              <div className="min-w-0 flex-1" />
            )}
            {selectedConversation ? (
              <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
                {selectedConversation.human_help_requested || selectedConversation.labels?.includes(HUMAN_HELP_REQUESTED_LABEL) ? (
                  <>
                    <span
                      role="status"
                      className="inline-flex h-9 items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3 text-sm font-semibold text-emerald-800"
                      title="IA solicitó ayuda de humano"
                    >
                      <span className="size-2 rounded-full bg-emerald-500" />
                      IA Solicitó ayuda de humano
                    </span>
                    <button
                      type="button"
                      onClick={() => {
                        const labels = (selectedConversation.labels || []).filter(
                          (label) => label.toLowerCase() !== HUMAN_HELP_REQUESTED_LABEL
                        );
                        onUpdateConversation(selectedConversation.id, {
                          labels,
                          human_help_requested: false,
                        });
                        toast.success("Ayuda humana resuelta");
                      }}
                      className="inline-flex h-9 items-center rounded-full border border-gray-200 bg-white px-3 text-sm font-semibold text-gray-700 shadow-sm transition hover:bg-gray-50"
                      title="Ayuda humana resuelta"
                    >
                      Ayuda humana resuelta
                    </button>
                  </>
                ) : null}
                {learningModeEnabled ? (
                  <span className="inline-flex items-center gap-2 rounded-full border border-dashed border-[#4368E5]/40 px-3 h-9">
                    <ToggleSwitch
                      checked={conversationAiChecked}
                      label="IA · Aprendiz"
                      onChange={(next) => onToggleAI(selectedConversation.id, next)}
                    />
                  </span>
                ) : (
                  <ToggleSwitch
                    checked={conversationAiChecked}
                    label={conversationAiLabel}
                    onChange={(next) => onToggleAI(selectedConversation.id, next)}
                  />
                )}
                <button
                  onClick={() => setRetryOpen(true)}
                  disabled={isConversationActionLoading(actionLoading, "retry-ai", selectedConversation.id)}
                  className="inline-flex h-9 items-center gap-2 rounded-full border border-gray-200 bg-white px-3 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  {isConversationActionLoading(actionLoading, "retry-ai", selectedConversation.id) ? (
                    <Loader2 className="size-4 animate-spin" />
                  ) : (
                    <RotateCcw className="size-4" />
                  )}
                  IA
                </button>
                {selectedConversation.status === "resolved" ? (
                  <button
                    onClick={() => onReopenConversation(selectedConversation.id)}
                    disabled={isConversationActionLoading(actionLoading, "reopen", selectedConversation.id)}
                    className="inline-flex h-9 items-center gap-2 rounded-full border border-amber-200 bg-amber-50 px-3 text-sm font-medium text-amber-800 hover:bg-amber-100 disabled:opacity-50"
                  >
                    {isConversationActionLoading(actionLoading, "reopen", selectedConversation.id) ? <Loader2 className="size-4 animate-spin" /> : <RefreshCw className="size-4" />}
                    Reabrir
                  </button>
                        ) : (
                          <button
                            onClick={() => onResolveConversation(selectedConversation.id)}
                            disabled={isConversationActionLoading(actionLoading, "resolve", selectedConversation.id)}
                            className="inline-flex h-9 items-center gap-2 rounded-full border border-gray-200 bg-white px-3 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                  >
                    {isConversationActionLoading(actionLoading, "resolve", selectedConversation.id) ? <Loader2 className="size-4 animate-spin" /> : <CheckCircle2 className="size-4" />}
                            Resolver
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => setDetailsOpen(true)}
                          className="inline-flex size-10 items-center justify-center rounded-full bg-transparent text-gray-600 transition hover:bg-gray-100 hover:text-gray-900"
                          title="Abrir información"
                          aria-label="Abrir información de la conversación"
                        >
                          <MoreVertical className="size-5" />
                        </button>
                      </div>
                    ) : null}
          </div>
        </div>

        <div
          ref={messageScrollRef}
          onScroll={handleMessageScroll}
          className="min-h-0 flex-1 overflow-y-auto px-5 pt-4"
          style={{ paddingBottom: threadBottomPadding }}
        >
          {loading ? (
            <MessageThreadSkeleton detailsOpen={detailsOpen} />
          ) : !selectedConversation ? (
            <EmptyState icon={MessageCircle} title="Selecciona una conversación" body="El historial y acciones aparecerán aquí." />
          ) : timelineItems.length === 0 ? (
            <EmptyState icon={MessageCircle} title="Sin mensajes" body="Aún no hay mensajes persistidos para esta conversación." />
          ) : (
            <div className={`${detailsOpen ? "mx-auto max-w-3xl" : "w-full"} flex flex-col gap-3`}>
              {messagesLoadingOlder ? (
                <div className="flex items-center justify-center gap-2 py-2 text-xs text-gray-500">
                  <Loader2 className="size-3.5 animate-spin" />
                  Cargando mensajes anteriores
                </div>
              ) : messagesHasMore ? (
                <button
                  type="button"
                  onClick={onLoadOlderMessages}
                  className="mx-auto rounded-full border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50"
                >
                  Cargar mensajes anteriores
                </button>
              ) : null}
              {timelineItems.map((item) => {
                if (item.type === "date_separator") {
                  return <DateSeparator key={item.key} label={item.label} />;
                }
                if (item.type === "ai_failure") {
                  const failureKey = item.failure.trace_id || item.failure.created_at || item.key;
                  const isRetrying = Boolean(
                    selectedConversation &&
                    isConversationActionLoading(actionLoading, "retry-ai", selectedConversation.id) &&
                    (!retryingTraceId || retryingTraceId === failureKey)
                  );
                  return (
                    <AiFailureBubble
                      key={item.key}
                      failure={item.failure}
                      loading={isRetrying}
                      onRetry={() => {
                        if (!selectedConversation) return;
                        onRetryAI(selectedConversation.id, failureKey);
                      }}
                    />
                  );
                }
                if (item.type === "action_notice") {
                  return <ActionNoticeBubble key={item.key} notice={item.notice} />;
                }
                if (item.type === "reply_approval") {
                  return (
                    <PendingReplyBubble
                      key={item.key}
                      approval={item.approval}
                      mediaResources={mediaResources}
                      actionLoading={approvalActionLoading}
                      awaitingVersion={approvalAwaitingVersion}
                      sendError={approvalSendError}
                      onApprove={onApproveReply}
                      onFeedback={onSendReplyFeedback}
                      onReject={onRejectReply}
                    />
                  );
                }
                if (item.type === "reply_approval_expired") {
                  return <ExpiredReplyBubble key={item.key} approval={item.approval} />;
                }
                if (item.type === "reply_approval_rejected") {
                  const rejectedVersion =
                    item.approval.approved_version ??
                    item.approval.versions[item.approval.versions.length - 1]?.version;
                  return (
                    <div key={item.key} className="flex justify-center">
                      <div className="max-w-[min(520px,88%)] rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-[13px] text-gray-600">
                        Respuesta rechazada · v{rejectedVersion} — {item.approval.reject_reason ?? "sin motivo"}
                      </div>
                    </div>
                  );
                }
                const message = item.message;
                const outbound = message.direction === "outbound";
                const internal = message.private || message.direction === "internal";
                const retryInstruction = isRetryInstructionMessage(message);
                const outboundHuman = outbound && isHumanSender(message.sender_type);
                // Los mensajes de plantilla se etiquetan como tal (no como "IA") en el pie de la burbuja.
                const outboundSenderLabel = outbound
                  ? String(message.content_type || "").toLowerCase() === "template"
                    ? "Plantilla"
                    : formatSenderType(message.sender_type)
                  : null;
                const messageTime = formatMessageTime(message.created_at);
                const deliveryFailure = getMessageDeliveryFailure(message, deliveryLogByMessageId.get(message.id));
                const aiSummary = showAiSummaries && outbound && !outboundHuman ? getMessageMemorySummaryUsed(message) : "";
                const inboundMedia = isInboundMediaMessage(message);
                const inboundMediaExtracted = inboundMedia ? getInboundMediaExtractedText(message) : "";
                const inboundMediaVisual = inboundMedia
                  ? getInboundMediaChipLabel(message.content_type, Boolean(inboundMediaExtracted))
                  : null;
                const InboundMediaIcon = inboundMediaVisual?.icon;
                const inboundMediaCaption = inboundMedia ? getInboundMediaCaption(message) : "";
                const inboundMediaExtractionPending = inboundMedia ? isInboundMediaExtractionPending(message) : false;
                const inboundMediaExtractionError = inboundMedia
                  ? getInboundMediaExtractionError(message) ||
                    (!inboundMediaExtracted &&
                    !inboundMediaExtractionPending &&
                    (getInboundMediaExtractionStatus(message) === "pending" ||
                      getInboundMediaExtractionStatus(message) === "processing")
                      ? "La transcripción tardó demasiado. Reintenta."
                      : "")
                  : "";
                const inboundMediaExtractionRetryable = inboundMedia
                  ? isInboundMediaExtractionRetryable(message)
                  : false;
                const outboundMediaResource = outbound && !internal ? getOutboundMediaResource(message) : null;
                // Media de plantillas (header de imagen de confirmaciones y afines):
                // sin esto el owner ve la plantilla como solo-texto y cree que la
                // foto no se envió (caso berrylove 2026-07-17).
                const templateOutboundMedia = (() => {
                  if (!outbound || internal) return null;
                  const raw = message.metadata_json?.outbound_media;
                  if (!raw || typeof raw !== "object") return null;
                  const url = String((raw as Record<string, unknown>).url || "").trim();
                  if (!url.startsWith("http")) return null;
                  return { url, media_type: String((raw as Record<string, unknown>).media_type || "image") };
                })();
                const outboundMediaResourceUrl = outboundMediaResource
                  ? String(outboundMediaResource.link || outboundMediaResource.image_url || "").trim() || null
                  : null;
                const outboundMediaResourceLabel = outboundMediaResource
                  ? String(
                      outboundMediaResource.title ||
                        (outboundMediaResource.source === "catalog_product_photo"
                          ? "Foto del producto"
                          : "Recurso multimedia")
                    )
                  : "";
                const outboundMediaResourceCaption = outboundMediaResource
                  ? (() => {
                      const content = String(message.content || "").trim();
                      if (!content) return "";
                      if (/^\[(recurso multimedia|foto de producto)\]/i.test(content)) return "";
                      return content;
                    })()
                  : "";
                return (
                  <div key={item.key} className="contents">
                    {aiSummary ? (
                      <div className="flex justify-center">
                        <div className="max-w-[min(620px,86%)] rounded-xl bg-gray-900/90 px-3 py-2 text-white shadow-[0px_1px_4px_0px_rgba(99,99,99,0.18)]">
                          <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-gray-300">
                            Resumen IA
                          </div>
                          <p className="whitespace-pre-wrap text-xs leading-5 text-gray-100">{aiSummary}</p>
                        </div>
                      </div>
                    ) : null}
                    <div className={`flex ${internal ? "justify-center" : outbound ? "justify-end" : "justify-start"}`}>
                      <div
                        className={`max-w-[min(560px,78%)] rounded-xl px-3 py-2 ${
                          internal
                            ? retryInstruction
                              ? "border border-blue-200 bg-blue-50 text-blue-950"
                              : "border border-amber-200 bg-amber-50 text-amber-950"
                            : outbound
                              ? outboundHuman
                                ? "bg-[#F6F7F8] text-gray-900 shadow-[0px_1px_4px_0px_rgba(99,99,99,0.18)]"
                                : "bg-[#E8EEFF] text-gray-900 shadow-[0px_1px_4px_0px_rgba(99,99,99,0.18)]"
                              : "bg-white text-gray-900 shadow-[0px_1px_4px_0px_rgba(99,99,99,0.18)]"
                        }`}
                      >
                        {internal ? (
                          <div className={`mb-1 text-[11px] font-semibold uppercase ${retryInstruction ? "text-blue-700" : "text-amber-700"}`}>
                            {retryInstruction ? "Instrucciones de reintentar IA" : "Nota"}
                          </div>
                        ) : null}
                        {inboundMedia && inboundMediaVisual ? (
                          <div className="space-y-2">
                            {String(message.content_type || "").toLowerCase() !== "image" ||
                            !getInboundMediaId(message) ? (
                              <div className="inline-flex items-center gap-2 rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-700">
                                {InboundMediaIcon ? <InboundMediaIcon className="size-3.5" /> : null}
                                <span>{inboundMediaVisual.label}</span>
                              </div>
                            ) : null}
                            {getInboundMediaId(message) ? <InboundMediaViewer message={message} /> : null}
                            {inboundMediaCaption ? (
                              <div className="whitespace-pre-wrap text-sm leading-relaxed text-gray-800">{inboundMediaCaption}</div>
                            ) : null}
                            {inboundMediaExtracted ? (
                              <p className="whitespace-pre-wrap text-sm italic leading-relaxed text-gray-600">
                                &ldquo;{inboundMediaExtracted}&rdquo;
                              </p>
                            ) : inboundMediaExtractionPending ? (
                              <p className="text-xs italic text-gray-400">Transcribiendo contenido…</p>
                            ) : inboundMediaExtractionError ? (
                              <div className="space-y-2">
                                <p className="text-xs text-red-600">{inboundMediaExtractionError}</p>
                                {inboundMediaExtractionRetryable ? (
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setMediaExtractionRetryMessageId(message.id);
                                      void Promise.resolve(onRetryMediaExtraction(message.id)).finally(() => {
                                        setMediaExtractionRetryMessageId((current) =>
                                          current === message.id ? null : current
                                        );
                                      });
                                    }}
                                    disabled={mediaExtractionRetryMessageId === message.id}
                                    className="inline-flex h-7 items-center gap-1.5 rounded-full border border-red-200 bg-white px-2.5 text-[11px] font-semibold text-red-700 transition hover:bg-red-50 disabled:opacity-50"
                                  >
                                    {mediaExtractionRetryMessageId === message.id ? (
                                      <Loader2 className="size-3 animate-spin" />
                                    ) : (
                                      <RotateCcw className="size-3" />
                                    )}
                                    Reintentar transcripción
                                  </button>
                                ) : null}
                              </div>
                            ) : null}
                          </div>
                        ) : isOutboundVoiceReplyMessage(message) ? (
                          <div className="space-y-2">
                            <div className="inline-flex items-center gap-2 rounded-full bg-white/70 px-2.5 py-1 text-xs font-medium text-gray-700">
                              <Mic className="size-3.5" />
                              <span>Nota de voz</span>
                            </div>
                            {message.content ? (
                              <div className="whitespace-pre-wrap text-sm leading-relaxed text-gray-800">{message.content}</div>
                            ) : null}
                          </div>
                        ) : outboundMediaResource ? (
                          <div className="space-y-2">
                            {outboundMediaResourceUrl ? (
                              <ChatMediaContent
                                url={outboundMediaResourceUrl}
                                mediaType={String(outboundMediaResource.media_type || "document")}
                                filename={outboundMediaResource.file_name || null}
                              />
                            ) : null}
                            {outboundMediaResourceCaption ? (
                              <div className="whitespace-pre-wrap text-sm leading-relaxed">{outboundMediaResourceCaption}</div>
                            ) : null}
                            <div className="flex items-center gap-1.5 text-[11px] italic text-gray-500">
                              <Paperclip className="size-3 shrink-0" />
                              <span className="truncate">{outboundMediaResourceLabel}</span>
                            </div>
                          </div>
                        ) : templateOutboundMedia ? (
                          <div className="space-y-2">
                            <ChatMediaContent
                              url={templateOutboundMedia.url}
                              mediaType={templateOutboundMedia.media_type}
                              filename={null}
                            />
                            {message.content ? (
                              <div className="whitespace-pre-wrap text-sm leading-relaxed">{message.content}</div>
                            ) : null}
                          </div>
                        ) : (
                          <div className="space-y-1">
                            <div className="whitespace-pre-wrap text-sm leading-relaxed">{message.content}</div>
                            {messageVoiceReplyFailed(message) ? (
                              <p className="text-xs text-gray-500">La nota de voz falló — se envió como texto</p>
                            ) : null}
                          </div>
                        )}
                        <div className="mt-1 flex items-center justify-end gap-2 text-[11px] text-gray-500">
                          {internal ? (
                            <>
                              <span>{noteSenderLabel(message)}</span>
                              {messageTime ? <span aria-hidden="true">•</span> : null}
                            </>
                          ) : null}
                          {!internal && outboundSenderLabel ? (
                            <>
                              <span>{outboundSenderLabel}</span>
                              <span aria-hidden="true">•</span>
                            </>
                          ) : null}
                          {messageTime ? <span>{messageTime}</span> : null}
                          {!internal && outbound && message.delivery_status ? (
                            <>
                              <span aria-hidden="true">•</span>
                              <DeliveryStatusMarker
                                status={effectiveDeliveryStatusByMessageId.get(message.id) || message.delivery_status}
                                channel={message.channel}
                                failure={deliveryFailure}
                              />
                            </>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div
          className="pointer-events-none relative z-10 px-5 pb-6 pt-4"
          style={{ marginTop: -composerOverlayOffset }}
        >
          <div className={`${detailsOpen ? "mx-auto max-w-3xl" : "mx-auto max-w-[760px]"}`}>
            {aiPipelineActive && selectedConversation ? (
              <AiPipelineStatusBar
                phase={aiUiPhase}
                cancelling={selectedConversation ? isConversationActionLoading(actionLoading, "cancel-ai", selectedConversation.id) : false}
                cancelEnabled={aiGenerationCancellable}
                onCancel={() => onCancelAI(selectedConversation.id)}
              />
            ) : null}
            {composerEnhanced ? (
              windowState === "closed" ? (
                <p className="mb-2 px-1 text-[11px] text-gray-500">{closedInfoText}</p>
              ) : windowCountdown ? (
                <p className="mb-2 px-1 text-[11px] text-gray-500">{windowCountdown}</p>
              ) : null
            ) : null}
            <div
              ref={composerPillRef}
              className={`pointer-events-auto relative flex items-end gap-2 border border-gray-200 bg-white p-2 shadow-[0px_2px_8px_0px_rgba(99,99,99,0.2)] transition-[border-radius] ${
                composerExpanded ? "rounded-[24px]" : "rounded-full"
              }`}
            >
              {composerEnhanced ? (
                <>
                  {windowState === "closed" && !isSocialChannel ? (
                    <button
                      type="button"
                      onClick={() => {
                        setTemplatePickerOpen(false);
                        setComposerMenuOpen((open) => !open);
                      }}
                      disabled={reopenTemplatePending}
                      title={
                        reopenTemplatePending
                          ? "Enviar varias plantillas seguidas sin respuesta afecta la calidad de tu número ante WhatsApp."
                          : undefined
                      }
                      className="inline-flex h-9 shrink-0 items-center gap-1.5 rounded-full bg-[#E8EEFF] px-3 text-sm font-semibold text-[#3151C9] transition disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      <Plus className="size-4" />
                      Enviar plantilla
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => {
                        setTemplatePickerOpen(false);
                        setComposerMenuOpen((open) => !open);
                      }}
                      className="inline-flex size-10 shrink-0 items-center justify-center rounded-full bg-transparent text-gray-600 transition hover:bg-gray-100"
                      title={isSocialChannel ? "Adjuntar imagen o video" : "Adjuntar o enviar plantilla"}
                      aria-label={isSocialChannel ? "Adjuntar imagen o video" : "Adjuntar o enviar plantilla"}
                    >
                      <Plus className="size-5" />
                    </button>
                  )}
                  {composerMenuOpen ? (
                    <div className="absolute bottom-full left-0 z-50 mb-2 rounded-xl border border-gray-200 bg-white p-1 shadow-[0px_2px_8px_0px_rgba(99,99,99,0.2)]">
                      <button
                        type="button"
                        onClick={handlePickImage}
                        disabled={windowState === "closed" && !isSocialChannel}
                        title={windowState === "closed" ? "Disponible cuando el cliente responda (ventana de 24h)" : undefined}
                        className={`flex h-9 w-full items-center gap-2 whitespace-nowrap rounded-lg px-3 text-sm hover:bg-gray-50 ${
                          windowState === "closed" ? "cursor-not-allowed opacity-40" : ""
                        }`}
                      >
                        <ImageIcon className="size-4" />
                        Enviar imagen
                      </button>
                      <button
                        type="button"
                        onClick={handlePickVideo}
                        disabled={windowState === "closed" && !isSocialChannel}
                        title={windowState === "closed" ? "Disponible cuando el cliente responda (ventana de 24h)" : undefined}
                        className={`flex h-9 w-full items-center gap-2 whitespace-nowrap rounded-lg px-3 text-sm hover:bg-gray-50 ${
                          windowState === "closed" ? "cursor-not-allowed opacity-40" : ""
                        }`}
                      >
                        <Film className="size-4" />
                        Enviar video
                      </button>
                      {!isSocialChannel ? (
                        <button
                          type="button"
                          onClick={() => void handleStartRecording()}
                          disabled={windowState === "closed"}
                          title={windowState === "closed" ? "Disponible cuando el cliente responda (ventana de 24h)" : undefined}
                          className={`flex h-9 w-full items-center gap-2 whitespace-nowrap rounded-lg px-3 text-sm hover:bg-gray-50 ${
                            windowState === "closed" ? "cursor-not-allowed opacity-40" : ""
                          }`}
                        >
                          <Mic className="size-4" />
                          Enviar nota de voz
                        </button>
                      ) : null}
                      {!isSocialChannel ? (
                        <button
                          type="button"
                          onClick={handleOpenTemplatePicker}
                          className="flex h-9 w-full items-center gap-2 whitespace-nowrap rounded-lg px-3 text-sm hover:bg-gray-50"
                        >
                          <FileText className="size-4" />
                          Enviar plantilla
                        </button>
                      ) : null}
                    </div>
                  ) : null}
                  {templatePickerOpen ? (
                    <div className="absolute bottom-full left-0 z-50 mb-2 w-[min(420px,90%)] rounded-xl border border-gray-200 bg-white p-1 shadow-[0px_2px_8px_0px_rgba(99,99,99,0.2)]">
                      <div className="max-h-72 overflow-auto">
                        {templatesLoading ? (
                          <div className="flex items-center gap-2 px-3 py-2 text-sm text-gray-500">
                            <Loader2 className="size-4 animate-spin" />
                            Cargando plantillas…
                          </div>
                        ) : !approvedTemplates.length ? (
                          <div className="px-3 py-2 text-sm text-gray-400">Sin plantillas aprobadas</div>
                        ) : (
                          <>
                            {reopenTemplates.length ? (
                              <>
                                <div className="px-3 pb-1 pt-2.5 text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                                  Para reabrir la conversación
                                </div>
                                {reopenTemplates.map((template) => (
                                  <button
                                    key={template.id}
                                    type="button"
                                    onClick={() => handleSelectTemplate(template)}
                                    className="flex w-full flex-col gap-0.5 rounded-lg px-3 py-2 text-left transition hover:bg-gray-50"
                                  >
                                    <span className="truncate text-sm font-medium text-gray-800">
                                      {templateDisplayTitle(template)}
                                    </span>
                                    {template.body_text ? (
                                      <span className="line-clamp-2 text-xs leading-5 text-gray-500">{template.body_text}</span>
                                    ) : null}
                                  </button>
                                ))}
                              </>
                            ) : null}
                            {otherApprovedTemplates.length ? (
                              <>
                                <div className="px-3 pb-1 pt-2.5 text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                                  Todas las aprobadas
                                </div>
                                {otherApprovedTemplates.map((template) => (
                                  <button
                                    key={template.id}
                                    type="button"
                                    onClick={() => handleSelectTemplate(template)}
                                    className="flex w-full flex-col gap-0.5 rounded-lg px-3 py-2 text-left transition hover:bg-gray-50"
                                  >
                                    <span className="truncate text-sm font-medium text-gray-800">
                                      {templateDisplayTitle(template)}
                                    </span>
                                    {template.body_text ? (
                                      <span className="line-clamp-2 text-xs leading-5 text-gray-500">{template.body_text}</span>
                                    ) : null}
                                  </button>
                                ))}
                              </>
                            ) : null}
                          </>
                        )}
                      </div>
                    </div>
                  ) : null}
                </>
              ) : null}
              <div className="flex min-w-0 flex-1 flex-col gap-2">
                {composerEnhanced && composerDraft ? (
                  composerDraft.kind === "image" ? (
                    <div className="flex items-center gap-2 rounded-xl border border-gray-200 bg-gray-50 p-2">
                      <img src={composerDraft.url} alt="" className="size-10 shrink-0 rounded-lg object-cover" />
                      <span className="min-w-0 flex-1 truncate text-sm text-gray-700">{composerDraft.name}</span>
                      <button
                        type="button"
                        onClick={clearDraft}
                        className="inline-flex size-7 shrink-0 items-center justify-center rounded-full text-gray-500 transition hover:bg-gray-200"
                        title="Quitar"
                        aria-label="Quitar"
                      >
                        <X className="size-4" />
                      </button>
                    </div>
                  ) : composerDraft.kind === "video" ? (
                    <div className="flex items-center gap-2 rounded-xl border border-gray-200 bg-gray-50 p-2">
                      <span className="inline-flex size-10 shrink-0 items-center justify-center rounded-lg bg-gray-200 text-gray-600">
                        <Film className="size-5" />
                      </span>
                      <span className="min-w-0 flex-1 truncate text-sm text-gray-700">{composerDraft.name}</span>
                      <button
                        type="button"
                        onClick={clearDraft}
                        className="inline-flex size-7 shrink-0 items-center justify-center rounded-full text-gray-500 transition hover:bg-gray-200"
                        title="Quitar"
                        aria-label="Quitar"
                      >
                        <X className="size-4" />
                      </button>
                    </div>
                  ) : composerDraft.kind === "voice" ? (
                    <div className="flex items-center gap-2 rounded-xl border border-gray-200 bg-gray-50 p-2">
                      <audio controls src={composerDraft.url} className="h-9 min-w-0 flex-1" />
                      <button
                        type="button"
                        onClick={handleReRecord}
                        className="inline-flex h-9 shrink-0 items-center gap-1.5 rounded-full border border-gray-200 bg-white px-3 text-xs font-semibold text-gray-700 transition hover:bg-gray-50"
                        title="Regrabar"
                      >
                        <Mic className="size-3.5" />
                        Regrabar
                      </button>
                      <button
                        type="button"
                        onClick={clearDraft}
                        className="inline-flex size-7 shrink-0 items-center justify-center rounded-full text-gray-500 transition hover:bg-gray-200"
                        title="Quitar"
                        aria-label="Quitar"
                      >
                        <X className="size-4" />
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <div className="whitespace-pre-wrap rounded-xl border border-gray-200 bg-white p-2 text-sm">
                        {composerDraft.template.body_text}
                        <div className="mt-2 flex items-center justify-between gap-2">
                          <span className="text-[11px] text-gray-500">Plantilla · {composerDraft.template.name}</span>
                          <button
                            type="button"
                            onClick={clearDraft}
                            className="inline-flex size-6 shrink-0 items-center justify-center rounded-full text-gray-500 transition hover:bg-gray-100"
                            title="Quitar"
                            aria-label="Quitar"
                          >
                            <X className="size-3.5" />
                          </button>
                        </div>
                      </div>
                      {composerDraft.variables.length ? (
                        <div className="space-y-1.5">
                          {composerDraft.variables.map((value, index) => (
                            <label key={index} className="block">
                              <span className="mb-1 block text-[11px] font-semibold text-gray-600">Variable {index + 1}</span>
                              <input
                                value={value}
                                onChange={(event) => handleTemplateVariableChange(index, event.target.value)}
                                className="h-8 w-full rounded-lg border border-gray-200 bg-white px-2.5 text-sm text-gray-900 outline-none transition focus:border-gray-400"
                              />
                            </label>
                          ))}
                        </div>
                      ) : null}
                    </div>
                  )
                ) : null}
                {composerEnhanced && isRecording ? (
                  <div className="flex items-center gap-2 rounded-xl border border-gray-200 bg-gray-50 p-2">
                    <span className="size-2 shrink-0 animate-pulse rounded-full bg-red-500" />
                    <span className="min-w-0 flex-1 text-sm text-gray-700">
                      Grabando… {formatRecordingTime(recordingSeconds)}
                    </span>
                    <button
                      type="button"
                      onClick={handleStopRecording}
                      className="inline-flex h-9 shrink-0 items-center rounded-full bg-gray-950 px-3 text-xs font-semibold text-white transition hover:bg-gray-800"
                      title="Detener"
                    >
                      Detener
                    </button>
                  </div>
                ) : null}
                <textarea
                  ref={composerRef}
                  value={composerValue}
                  onChange={(event) => onComposerChange(event.target.value)}
                  onKeyDown={(event) => submitTextareaOnEnter(event, onSendMessage)}
                  placeholder={
                    windowState === "closed"
                      ? isSocialChannel
                        ? "La ventana estimada de 24h se cerró — puedes intentar enviar; si Meta lo rechaza verás el motivo."
                        : "La ventana de 24h se cerró — envía una plantilla para reabrir."
                      : manualReplyPlaceholder(selectedConversation?.channel, contactOptedOut)
                  }
                  rows={1}
                  className="max-h-96 min-h-10 w-full resize-none overflow-y-auto rounded-[18px] border-0 bg-transparent px-3 py-2 text-sm leading-5 outline-none placeholder:text-gray-400"
                  disabled={!selectedConversation || sendingMessage || contactOptedOut || (windowState === "closed" && !isSocialChannel)}
                />
              </div>
              <button
                onClick={composerDraft ? () => void handleSendDraft() : onSendMessage}
                disabled={sendButtonDisabled}
                className="inline-flex size-10 shrink-0 items-center justify-center rounded-full bg-gray-950 text-white transition hover:bg-gray-800 disabled:cursor-not-allowed disabled:bg-gray-200 disabled:text-gray-400"
                title="Enviar"
              >
                {sendingMessage || sendingAttachment ? <Loader2 className="size-4 animate-spin" /> : <Send className="size-4" />}
              </button>
            </div>
            {composerEnhanced ? (
              <>
                <input
                  ref={imageInputRef}
                  type="file"
                  accept="image/jpeg,image/png"
                  className="hidden"
                  onChange={handleImageSelected}
                />
                <input
                  ref={videoInputRef}
                  type="file"
                  accept="video/mp4,video/3gpp"
                  className="hidden"
                  onChange={handleVideoSelected}
                />
              </>
            ) : null}
          </div>
        </div>
      </section>

      {detailsPanel}

      {retryOpen && selectedConversation ? (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/40 p-4">
          <div className="w-[min(440px,100%)] rounded-lg bg-white p-4 shadow-xl">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className="text-base font-semibold text-gray-950">
                  Solicitar que la IA envíe un mensaje siguiendo la conversación
                </h3>
                <p className="mt-1 text-sm text-gray-500">Puedes agregar una instrucción puntual para este intento.</p>
              </div>
              <button
                onClick={() => setRetryOpen(false)}
                className="inline-flex size-8 items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100"
              >
                <XCircle className="size-5" />
              </button>
            </div>
            <textarea
              value={retryInstruction}
              onChange={(event) => onRetryInstructionChange(event.target.value)}
              rows={4}
              placeholder="Ej. Responde más breve y pregunta el correo de compra."
              className="mt-4 w-full resize-none rounded-lg border border-gray-200 p-3 text-sm outline-none focus:border-[#4368E5]"
            />
            <div className="mt-4 flex justify-end gap-2">
              <button
                onClick={() => setRetryOpen(false)}
                className="inline-flex h-9 items-center rounded-lg border border-gray-200 px-3 text-sm font-semibold text-gray-700 hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                onClick={() => {
                  if (!selectedConversation) return;
                  onRetryAI(selectedConversation.id, retryingTraceId);
                  setRetryOpen(false);
                }}
                disabled={selectedConversation ? isConversationActionLoading(actionLoading, "retry-ai", selectedConversation.id) : false}
                className="inline-flex h-9 items-center gap-2 rounded-lg bg-gray-950 px-3 text-sm font-semibold text-white hover:bg-gray-800 disabled:opacity-50"
              >
                {selectedConversation && isConversationActionLoading(actionLoading, "retry-ai", selectedConversation.id) ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  <RotateCcw className="size-4" />
                )}
                Solicitar respuesta IA
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

const WhatsAppTab = ({
  account,
  accountForm,
  accountSaving,
  diagnoseResult,
  testPhone,
  testMessage,
  testSending,
  registerPin,
  whatsappSetupAction,
  onAccountFormChange,
  onSaveAccount,
  onQuickOAuthComplete,
  onTemplatesProvisioned,
  onRotateVerifyToken,
  onDiagnose,
  onCopy,
  onTestPhoneChange,
  onTestMessageChange,
  onTestSend,
  onRegisterPinChange,
  onRegisterNumber,
  onSubscribeWebhooks,
}: {
  account: WhatsAppConnectionStatus | null;
  accountForm: AccountFormState;
  accountSaving: boolean;
  diagnoseResult: Record<string, unknown> | null;
  testPhone: string;
  testMessage: string;
  testSending: boolean;
  registerPin: string;
  whatsappSetupAction: string | null;
  onAccountFormChange: (form: AccountFormState) => void;
  onSaveAccount: () => void;
  onQuickOAuthComplete: () => void | Promise<void>;
  onTemplatesProvisioned?: () => void;
  onRotateVerifyToken: () => void;
  onDiagnose: () => void;
  onCopy: (value: string | null | undefined, label: string) => void;
  onTestPhoneChange: (value: string) => void;
  onTestMessageChange: (value: string) => void;
  onTestSend: () => void;
  onRegisterPinChange: (value: string) => void;
  onRegisterNumber: () => void;
  onSubscribeWebhooks: () => void;
}) => {
  const { sdkReady, launchEmbeddedSignup } = useFacebookSDK();
  const [setupTab, setSetupTab] = useState<WhatsAppSetupTab>(account?.setup_mode === "manual_cloud_api" && account?.exists ? "manual" : "quick");
  const [credentialEditorOpen, setCredentialEditorOpen] = useState(false);
  const [quickConnecting, setQuickConnecting] = useState(false);
  const [quickSignupResult, setQuickSignupResult] = useState<{ phone_number_id: string; waba_id: string } | null>(null);
  const [postConnectOpen, setPostConnectOpen] = useState(false);
  const [provisioningPackage, setProvisioningPackage] = useState(false);
  const currentAccountForm: AccountFormState = {
    phone_number_id: account?.phone_number_id || "",
    waba_id: account?.waba_id || "",
    display_name: account?.display_name || "",
    access_token: "",
    app_secret: "",
    is_active: account?.exists ? account.is_active : true,
    rotate_verify_token: false,
  };
  const accountFormDirty =
    accountForm.phone_number_id !== currentAccountForm.phone_number_id ||
    accountForm.waba_id !== currentAccountForm.waba_id ||
    accountForm.display_name !== currentAccountForm.display_name ||
    accountForm.is_active !== currentAccountForm.is_active ||
    accountForm.rotate_verify_token ||
    Boolean(accountForm.access_token.trim() || accountForm.app_secret.trim());
  const credentialsStatus = whatsappCredentialStatus(account);
  const whatsappIntegrationComplete = Boolean(
    account?.exists &&
      account.is_active &&
      account.phone_number_id &&
      account.waba_id &&
      account.access_token_configured &&
      account.app_secret_configured &&
      credentialsStatus.ready
  );
  const setupModeLabel = account?.setup_mode === "manual_cloud_api" ? "Manual Cloud API" : account?.setup_mode ? "Integración rápida" : "Sin conexión";
  const editableStatusRows = [
    { label: "Cuenta activa", ready: Boolean(account?.exists && account.is_active), value: account?.exists && account.is_active ? "Listo" : "Pendiente" },
    { label: "phone_number_id", ready: Boolean(account?.phone_number_id), value: account?.phone_number_id || "Pendiente" },
    { label: "waba_id", ready: Boolean(account?.waba_id), value: account?.waba_id || "Pendiente" },
    { label: "access_token", ready: Boolean(account?.access_token_configured), value: account?.access_token_configured ? "••••••••" : "Pendiente" },
    { label: "app_secret", ready: Boolean(account?.app_secret_configured), value: account?.app_secret_configured ? "••••••••" : "Pendiente" },
  ];
  const indicatorRows = [
    { label: "Modo de conexión", ready: Boolean(account?.exists), value: setupModeLabel },
    { label: "Estado Cloud API", ready: account?.status === "connected", value: formatWhatsAppStatus(account?.status) },
    { label: "Credenciales", ready: credentialsStatus.ready, value: credentialsStatus.value },
    {
      label: "Estado del token",
      ready: isWhatsAppTokenHealthy(account?.health?.token_status, account?.access_token_configured),
      value: formatWhatsAppTokenStatus(account?.health?.token_status, account?.access_token_configured),
    },
    {
      label: "Vence token",
      ready: Boolean(account?.health?.token_expires_at),
      value: account?.health?.token_expires_at ? formatDate(account.health.token_expires_at) : "Sin dato",
    },
    {
      label: "Calidad del número",
      ready: isWhatsAppQualityHealthy(account?.health?.quality_rating),
      value: formatWhatsAppQuality(account?.health?.quality_rating),
    },
    {
      label: "Límite de mensajes",
      ready: Boolean(account?.health?.messaging_limit_tier),
      value: formatWhatsAppMessagingLimit(account?.health?.messaging_limit_tier),
    },
    {
      label: "Última revisión Meta",
      ready: Boolean(account?.health?.last_health_check_at),
      value: account?.health?.last_health_check_at ? formatDate(account.health.last_health_check_at) : "Sin dato",
    },
    {
      label: "Webhook Fluxi",
      ready: Boolean(account?.recommended_webhook_url && account?.verify_token),
      value: account?.recommended_webhook_url && account?.verify_token ? "Listo" : "Pendiente",
    },
  ];
  const resetAccountDraft = () => onAccountFormChange(currentAccountForm);
  const testingSectionClass = whatsappIntegrationComplete ? "space-y-3" : "space-y-3 opacity-45 grayscale";
  const rowClass = "flex items-center justify-between gap-3 border-b border-gray-100 px-5 py-3 text-sm md:[&:nth-last-child(-n+2)]:border-b-0";
  const renderStatusRows = (rows: Array<{ label: string; ready: boolean; value: string | number }>) =>
    rows.map((row) => (
      <div key={row.label} className={rowClass}>
        <span className="text-gray-600">{row.label}</span>
        <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${
          row.ready ? "bg-gray-100 text-gray-800" : "bg-gray-50 text-gray-400"
        }`}>
          {row.ready ? <CheckCircle2 className="size-3.5" /> : null}
          {row.value}
        </span>
      </div>
    ));
  const handleQuickConnect = async () => {
    setQuickConnecting(true);
    try {
      const result = await launchEmbeddedSignup();
      await completeWhatsAppOAuth({
        code: result.code,
        phone_number_id: result.phone_number_id,
        waba_id: result.waba_id,
      });
      setQuickSignupResult({
        phone_number_id: result.phone_number_id,
        waba_id: result.waba_id,
      });
      toast.success("WhatsApp conectado con Meta");
      setPostConnectOpen(true);
      await onQuickOAuthComplete();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "No se pudo conectar con Meta.");
    } finally {
      setQuickConnecting(false);
    }
  };

  const handleProvisionRecommendedPackage = async () => {
    setProvisioningPackage(true);
    try {
      await chatbotV2Service.provisionTemplatePackage();
      toast.success("Plantillas recomendadas en camino: Meta las está aprobando");
      onTemplatesProvisioned?.();
      setPostConnectOpen(false);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setProvisioningPackage(false);
    }
  };

  return (
    <div>
      <div className="mb-5 inline-flex rounded-full bg-gray-100 p-1">
        {[
          { id: "quick" as const, label: "Integración rápida" },
          { id: "manual" as const, label: "Integración manual" },
        ].map((tab) => {
          const active = setupTab === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => setSetupTab(tab.id)}
              className={`inline-flex h-8 items-center rounded-full px-4 text-sm font-semibold transition ${
                active ? "bg-white text-gray-950 shadow-sm" : "text-gray-500 hover:text-gray-800"
              }`}
            >
              {tab.label}
            </button>
          );
        })}
      </div>

      {setupTab === "quick" ? (
        <section className="rounded-2xl border border-gray-200 bg-white">
          <div className="flex flex-wrap items-center justify-between gap-4 border-b border-gray-100 px-5 py-4">
            <div>
              <h3 className="text-base font-semibold text-gray-950">Conectar con Meta</h3>
              <p className="mt-1 text-sm text-gray-500">
                El usuario inicia sesión en Meta y Fluxi recibe el número y la WABA sin pedir credenciales manuales.
              </p>
            </div>
            <button
              type="button"
              onClick={() => void handleQuickConnect()}
              disabled={!sdkReady || quickConnecting}
              className="inline-flex h-10 items-center gap-2 rounded-full bg-gray-950 px-4 text-sm font-semibold text-white hover:bg-gray-800 disabled:bg-gray-200 disabled:text-gray-400"
            >
              {quickConnecting ? <Loader2 className="size-4 animate-spin" /> : <RiWhatsappLine className="size-4" />}
              Conectar con Meta
            </button>
          </div>
          <div className="grid gap-0 md:grid-cols-3">
            <div className={rowClass}>
              <span className="text-gray-600">SDK Meta</span>
              <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${
                sdkReady ? "bg-gray-100 text-gray-800" : "bg-gray-50 text-gray-400"
              }`}>
                {sdkReady ? <CheckCircle2 className="size-3.5" /> : null}
                {sdkReady ? "Listo" : "Cargando"}
              </span>
            </div>
            <div className={rowClass}>
              <span className="text-gray-600">Aprobación Meta</span>
              <span className="inline-flex rounded-full bg-gray-50 px-2.5 py-1 text-xs font-semibold text-gray-400">
                En preparación
              </span>
            </div>
            <div className={rowClass}>
              <span className="text-gray-600">Guardado automático</span>
              <span className="inline-flex rounded-full bg-gray-50 px-2.5 py-1 text-xs font-semibold text-gray-400">
                Pendiente
              </span>
            </div>
          </div>
          <div className="border-t border-gray-100 px-5 py-4 text-sm leading-6 text-gray-500">
            Esta ruta será la principal para usuarios finales cuando Meta apruebe los permisos de Fluxi. Por ahora la integración manual sigue siendo la ruta operativa.
            {quickSignupResult ? (
              <div className="mt-4 grid gap-3 rounded-xl bg-gray-50 p-4 md:grid-cols-2">
                <CopyBox label="phone_number_id recibido" value={quickSignupResult.phone_number_id} onCopy={onCopy} />
                <CopyBox label="waba_id recibido" value={quickSignupResult.waba_id} onCopy={onCopy} />
              </div>
            ) : null}
          </div>
        </section>
      ) : (
        <section className="rounded-2xl border border-gray-200 bg-white">
          <div className="flex flex-wrap items-center justify-between gap-4 border-b border-gray-100 px-5 py-4">
            <div className="flex min-w-0 flex-wrap items-center gap-2">
              <h3 className="text-base font-semibold text-gray-950">Estado de la integración</h3>
              <span
                className={`inline-flex h-7 items-center rounded-full px-3 text-xs font-semibold ${
                  whatsappIntegrationComplete ? "bg-[#EEF3FF] text-[#3151C9]" : "bg-gray-100 text-gray-600"
                }`}
              >
                {whatsappIntegrationComplete ? "Activa" : "Pendiente"}
              </span>
            </div>
            <div className="flex flex-wrap items-center justify-end gap-2">
              {credentialEditorOpen && accountFormDirty ? (
                <>
                  <button
                    type="button"
                    onClick={resetAccountDraft}
                    disabled={accountSaving}
                    className="inline-flex h-9 items-center rounded-full bg-gray-100 px-3 text-sm font-semibold text-gray-700 transition hover:bg-gray-200 hover:text-gray-950 disabled:opacity-50"
                  >
                    Deshacer cambios
                  </button>
                  <button
                    type="button"
                    onClick={onSaveAccount}
                    disabled={accountSaving}
                    className="inline-flex h-9 items-center gap-2 rounded-full bg-gray-950 px-3 text-sm font-semibold text-white hover:bg-gray-800 disabled:opacity-50"
                  >
                    {accountSaving ? <Loader2 className="size-4 animate-spin" /> : <Save className="size-4" />}
                    Guardar cambios
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  onClick={() => setCredentialEditorOpen((current) => !current)}
                  className={`inline-flex h-9 items-center gap-2 rounded-full px-3 text-sm font-semibold transition ${
                    credentialEditorOpen || whatsappIntegrationComplete
                      ? "bg-gray-100 text-gray-700 hover:bg-gray-200 hover:text-gray-950"
                      : "bg-gray-950 text-white hover:bg-gray-800"
                  }`}
                >
                  <Settings2 className="size-4" />
                  {credentialEditorOpen
                    ? "Cerrar edición"
                    : whatsappIntegrationComplete
                      ? "Editar conexión"
                      : "Conectar integración"}
                </button>
              )}
            </div>
          </div>
          <div className="grid gap-0 md:grid-cols-2">
            {credentialEditorOpen ? (
              <>
                <div className="flex flex-col gap-2 border-b border-gray-100 px-5 py-3 text-sm">
                  <span className="text-gray-600">phone_number_id</span>
                  <input
                    value={accountForm.phone_number_id}
                    onChange={(event) => onAccountFormChange({ ...accountForm, phone_number_id: event.target.value })}
                    className="h-10 rounded-xl border border-gray-200 bg-white px-3 text-sm text-gray-900 outline-none transition focus:border-gray-400"
                    placeholder="123456789"
                  />
                </div>
                <div className="flex flex-col gap-2 border-b border-gray-100 px-5 py-3 text-sm">
                  <span className="text-gray-600">waba_id</span>
                  <input
                    value={accountForm.waba_id}
                    onChange={(event) => onAccountFormChange({ ...accountForm, waba_id: event.target.value })}
                    className="h-10 rounded-xl border border-gray-200 bg-white px-3 text-sm text-gray-900 outline-none transition focus:border-gray-400"
                    placeholder="123456789"
                  />
                </div>
                <div className="flex flex-col gap-2 border-b border-gray-100 px-5 py-3 text-sm">
                  <span className="text-gray-600">Nombre visible</span>
                  <input
                    value={accountForm.display_name}
                    onChange={(event) => onAccountFormChange({ ...accountForm, display_name: event.target.value })}
                    className="h-10 rounded-xl border border-gray-200 bg-white px-3 text-sm text-gray-900 outline-none transition focus:border-gray-400"
                    placeholder="Marca / tienda"
                  />
                </div>
                <div className="flex items-center justify-between gap-3 border-b border-gray-100 px-5 py-3 text-sm">
                  <span className="text-gray-600">Cuenta activa</span>
                  <ToggleSwitch
                    checked={accountForm.is_active}
                    label={accountForm.is_active ? "Activa" : "Pausada"}
                    onChange={(value) => onAccountFormChange({ ...accountForm, is_active: value })}
                  />
                </div>
                <div className="flex flex-col gap-2 border-b border-gray-100 px-5 py-3 text-sm md:border-b-0">
                  <span className="text-gray-600">{account?.access_token_configured ? "access_token nuevo" : "access_token"}</span>
                  <input
                    type="password"
                    value={accountForm.access_token}
                    onChange={(event) => onAccountFormChange({ ...accountForm, access_token: event.target.value })}
                    className="h-10 rounded-xl border border-gray-200 bg-white px-3 text-sm text-gray-900 outline-none transition focus:border-gray-400"
                    placeholder={account?.access_token_configured ? "••••••••" : "Pegar token permanente"}
                  />
                </div>
                <div className="flex flex-col gap-2 border-b border-gray-100 px-5 py-3 text-sm md:border-b-0">
                  <span className="text-gray-600">{account?.app_secret_configured ? "app_secret nuevo" : "app_secret"}</span>
                  <input
                    type="password"
                    value={accountForm.app_secret}
                    onChange={(event) => onAccountFormChange({ ...accountForm, app_secret: event.target.value })}
                    className="h-10 rounded-xl border border-gray-200 bg-white px-3 text-sm text-gray-900 outline-none transition focus:border-gray-400"
                    placeholder={account?.app_secret_configured ? "••••••••" : "Pegar app secret"}
                  />
                </div>
              </>
            ) : (
              renderStatusRows(editableStatusRows)
            )}
          </div>
        </section>
      )}

      <section className="mt-5 rounded-2xl border border-gray-200 bg-white">
        <div className="border-b border-gray-100 px-5 py-4">
          <h3 className="text-base font-semibold text-gray-950">Indicadores de la integración</h3>
          <p className="mt-1 text-sm text-gray-500">Estado operativo que Fluxi consulta desde Meta o calcula sobre la conexión guardada.</p>
        </div>
        <div className="grid gap-0 md:grid-cols-2">{renderStatusRows(indicatorRows)}</div>
      </section>

      <div className="mt-8 space-y-5">
        {setupTab === "manual" ? (
          <section className="space-y-3">
            <details open className="rounded-xl border border-gray-200 bg-white">
              <summary className="flex cursor-pointer list-none items-center justify-between gap-4 px-4 py-3 hover:bg-gray-50">
                <div>
                  <p className="text-sm font-semibold text-gray-950">Webhook manual en Meta</p>
                  <p className="mt-1 text-xs text-gray-500">Estos datos se copian en Meta para que Fluxi reciba mensajes, estados y errores.</p>
                </div>
                <ChevronDown className="size-4 text-gray-500" />
              </summary>
              <div className="border-t border-gray-100 px-4 py-4">
                <div className="grid gap-3 md:grid-cols-2">
                  <CopyBox label="Webhook recomendado" value={account?.recommended_webhook_url} onCopy={onCopy} />
                  <CopyBox label="Verify token" value={account?.verify_token} onCopy={onCopy} />
                  <CopyBox label="Webhook por owner" value={account?.owner_webhook_url} onCopy={onCopy} />
                  <CopyBox label="Campos a suscribir" value={account?.subscribe_fields?.join(", ")} onCopy={onCopy} />
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  <button
                    onClick={onRotateVerifyToken}
                    disabled={accountSaving || !account?.exists}
                    className="inline-flex h-9 items-center gap-2 rounded-full bg-gray-100 px-3 text-sm font-semibold text-gray-700 hover:bg-gray-200 disabled:opacity-50"
                  >
                    <RefreshCw className="size-4" />
                    Rotar verify token
                  </button>
                </div>
                <p className="mt-3 text-xs leading-5 text-gray-500">
                  En integración rápida este paso se configura desde Fluxi. En manual, la URL debe ser HTTPS pública; en local usa un túnel apuntando a SpaceC antes de verificarlo en Meta.
                </p>
              </div>
            </details>

            <details className="rounded-xl border border-gray-200 bg-white">
              <summary className="flex cursor-pointer list-none items-center justify-between gap-4 px-4 py-3 hover:bg-gray-50">
                <div>
                  <p className="text-sm font-semibold text-gray-950">Registro Cloud API</p>
                  <p className="mt-1 text-xs text-gray-500">Solo se usa si Meta dejó el número agregado pero todavía no registrado para Cloud API.</p>
                </div>
                <ChevronDown className="size-4 text-gray-500" />
              </summary>
              <div className="border-t border-gray-100 px-4 py-4">
                <div className="flex gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
                  <AlertTriangle className="mt-0.5 size-4 shrink-0" />
                  <p>
                    Suscribe webhooks solo si esta WABA contiene exclusivamente el número que estás conectando. Si comparte números con otro sistema, Meta puede enviar eventos de todos ellos al mismo webhook.
                  </p>
                </div>
                <div className="mt-4 grid gap-4 md:grid-cols-[220px_auto_auto] md:items-end">
                  <TextField
                    label="PIN Cloud API"
                    type="password"
                    value={registerPin}
                    onChange={onRegisterPinChange}
                    placeholder="6 dígitos"
                  />
                  <button
                    onClick={onRegisterNumber}
                    disabled={!whatsappIntegrationComplete || whatsappSetupAction === "register" || !account?.exists || !registerPin.trim()}
                    className="inline-flex h-10 items-center justify-center gap-2 rounded-full bg-gray-100 px-3 text-sm font-semibold text-gray-700 hover:bg-gray-200 disabled:opacity-50"
                  >
                    {whatsappSetupAction === "register" ? <Loader2 className="size-4 animate-spin" /> : <CheckCircle2 className="size-4" />}
                    Registrar número
                  </button>
                  <button
                    onClick={onSubscribeWebhooks}
                    disabled={!whatsappIntegrationComplete || whatsappSetupAction === "subscribe" || !account?.exists}
                    className="inline-flex h-10 items-center justify-center gap-2 rounded-full bg-gray-100 px-3 text-sm font-semibold text-gray-700 hover:bg-gray-200 disabled:opacity-50"
                  >
                    {whatsappSetupAction === "subscribe" ? <Loader2 className="size-4 animate-spin" /> : <Plug className="size-4" />}
                    Suscribir webhooks
                  </button>
                </div>
              </div>
            </details>
          </section>
        ) : null}

        <section className={testingSectionClass}>
          <details open className="rounded-xl border border-gray-200 bg-white">
            <summary className="flex cursor-pointer list-none items-center justify-between gap-4 px-4 py-3 hover:bg-gray-50">
              <div>
                <p className="text-sm font-semibold text-gray-950">Validación y prueba</p>
                <p className="mt-1 text-xs text-gray-500">Diagnostica la conexión y envía un mensaje controlado para validar delivery.</p>
              </div>
              <ChevronDown className="size-4 text-gray-500" />
            </summary>
            <div className="space-y-4 border-t border-gray-100 px-4 py-4">
              <button
                onClick={onDiagnose}
                disabled={!whatsappIntegrationComplete || accountSaving || !account?.exists}
                className="inline-flex h-9 items-center gap-2 rounded-full bg-gray-100 px-3 text-sm font-semibold text-gray-700 hover:bg-gray-200 disabled:opacity-50"
              >
                <Gauge className="size-4" />
                Diagnóstico
              </button>
              <div className="grid gap-4 md:grid-cols-[240px_minmax(0,1fr)_auto] md:items-end">
                <TextField label="Teléfono" value={testPhone} onChange={onTestPhoneChange} placeholder="+573001234567" />
                <TextareaField label="Mensaje" value={testMessage} onChange={onTestMessageChange} rows={3} />
                <button
                  onClick={onTestSend}
                  disabled={!whatsappIntegrationComplete || testSending || !testPhone.trim() || !testMessage.trim()}
                  className="inline-flex h-10 items-center justify-center gap-2 rounded-full bg-gray-950 px-4 text-sm font-semibold text-white hover:bg-gray-800 disabled:bg-gray-200 disabled:text-gray-400"
                >
                  {testSending ? <Loader2 className="size-4 animate-spin" /> : <Send className="size-4" />}
                  Probar
                </button>
              </div>
            </div>
          </details>

          {diagnoseResult ? (
            <pre className="max-h-72 overflow-auto rounded-lg bg-gray-950 p-3 text-xs text-gray-100">
              {stringifySmall(diagnoseResult)}
            </pre>
          ) : null}
        </section>
      </div>

      {postConnectOpen ? (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/40 p-4">
          <div className="w-[min(440px,100%)] rounded-[22px] border border-gray-200 bg-white p-5 shadow-xl">
            <h3 className="text-base font-semibold text-gray-950">¡Tu WhatsApp quedó conectado! 🎉</h3>
            <p className="mt-2 text-sm leading-6 text-gray-500">
              Para que tu chatbot funcione completo, te sugerimos instalar las plantillas recomendadas de Fluxi:
              confirmación de pedidos, avisos de envío, recuperación de carrito y seguimientos. Se envían a Meta para
              aprobación (tarda unos minutos) y las verás en la sección Plantillas.
            </p>
            <div className="mt-5 space-y-2">
              <button
                type="button"
                onClick={() => void handleProvisionRecommendedPackage()}
                disabled={provisioningPackage}
                className="inline-flex h-10 w-full items-center justify-center gap-2 rounded-full bg-gray-950 px-4 text-sm font-semibold text-white transition hover:bg-gray-800 disabled:opacity-50"
              >
                {provisioningPackage ? <Loader2 className="size-4 animate-spin" /> : null}
                Instalar plantillas sugeridas (recomendado)
              </button>
              <button
                type="button"
                onClick={() => setPostConnectOpen(false)}
                disabled={provisioningPackage}
                className="inline-flex h-9 w-full items-center justify-center rounded-full px-4 text-xs font-semibold text-gray-500 transition hover:text-gray-700 disabled:opacity-50"
              >
                Ahora no
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

// Tab bar subrayado genérico (mismo idioma visual de ActionsConfigSubTabBar).
// compact: versión reducida para tabs dentro de cards (px-3 py-2 text-xs).
const SimpleSubTabBar = <T extends string>({
  tabs,
  active,
  compact = false,
  onChange,
}: {
  tabs: { id: T; label: string }[];
  active: T;
  compact?: boolean;
  onChange: (tab: T) => void;
}) => (
  <div className="flex items-end gap-3 border-b border-gray-200">
    {tabs.map((tab) => {
      const isActive = active === tab.id;
      return (
        <button
          key={tab.id}
          type="button"
          onClick={() => onChange(tab.id)}
          className={`-mb-px shrink-0 border-b-[3px] font-semibold transition ${
            compact ? "px-3 py-2 text-xs" : "px-4 py-3 text-sm"
          } ${isActive ? "border-gray-950 text-gray-950" : "border-transparent text-gray-500 hover:text-gray-900"}`}
        >
          {tab.label}
        </button>
      );
    })}
  </div>
);

// Sin tab "Guardián" (Julian): las plantillas del Guardián quedan fuera del UI de plantillas.
const TEMPLATE_CATEGORY_TABS: { id: TemplateCategoryTabId; label: string }[] = [
  { id: "all", label: "Todas" },
  { id: "logistics", label: "Logística" },
  { id: "marketing", label: "Marketing" },
  { id: "reopen", label: "Reapertura" },
  { id: "other", label: "Personalizadas" },
];

// Slots de plantillas por defecto de la cuenta (default_templates del account).
const DEFAULT_TEMPLATE_SLOTS: { key: string; label: string }[] = [
  { key: "confirmation_template_name", label: "Confirmación de pedido (inicia conversación)" },
  { key: "reminder_template_name", label: "Recordatorio de confirmación" },
  { key: "order_confirmed_template_name", label: "Pedido confirmado (aviso final)" },
  { key: "cart_recovery_template_name", label: "Recuperación de carrito" },
  { key: "fallback_template_name", label: "Respaldo (en desuso)" },
];

const TemplatesTab = ({
  templates,
  templateStatusFilter,
  templateSaving,
  defaultTemplates,
  onDefaultTemplatesChange,
  onSaveDefaultTemplates,
  onTemplateStatusFilterChange,
  onSyncTemplates,
  onTemplateCreated,
}: {
  templates: WhatsAppTemplate[];
  templateStatusFilter: string;
  templateSaving: boolean;
  defaultTemplates: Record<string, string>;
  onDefaultTemplatesChange: (next: Record<string, string>) => void;
  onSaveDefaultTemplates: (next: Record<string, string>) => Promise<boolean>;
  onTemplateStatusFilterChange: (status: string) => void;
  onSyncTemplates: () => void;
  onTemplateCreated: () => void;
}) => {
  const [creatorOpen, setCreatorOpen] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [creatingTemplate, setCreatingTemplate] = useState(false);
  const [categoryTab, setCategoryTab] = useState<TemplateCategoryTabId>("all");
  const [provisioningPackage, setProvisioningPackage] = useState(false);
  // "Plantillas por defecto" vive detrás de "Más opciones" (patrón de "Más instrucciones").
  const [moreOptionsOpen, setMoreOptionsOpen] = useState(false);
  const [creatorForm, setCreatorForm] = useState({
    name: "",
    language: "es",
    category: "UTILITY",
    body_text: "",
  });

  // Nombres del paquete recomendado que aún no existen en la cuenta (por nombre exacto).
  // Las del Guardián salieron del paquete (y del UI), así que no cuentan como faltantes.
  const missingRecommended = useMemo(
    () =>
      RECOMMENDED_TEMPLATE_PACKAGE_NAMES.filter(
        (name) => !name.startsWith("chatbot_guardian_") && !templates.some((template) => template.name === name)
      ),
    [templates]
  );

  // Las plantillas del Guardián se ocultan de TODO el listado (incluida "Todas").
  const listableTemplates = useMemo(
    () => templates.filter((template) => templateCategoryTab(template) !== "guardian"),
    [templates]
  );

  const visibleTemplates = useMemo(() => {
    if (categoryTab === "all") return listableTemplates;
    const filtered = listableTemplates.filter((template) => templateCategoryTab(template) === categoryTab);
    if (categoryTab !== "logistics") return filtered;
    // Logística en orden cronológico fijo del flujo de una orden.
    const orderIndex = (template: WhatsAppTemplate) => {
      const index = LOGISTICS_PURPOSE_ORDER.indexOf(templateEffectivePurpose(template) || "");
      return index === -1 ? LOGISTICS_PURPOSE_ORDER.length : index;
    };
    return [...filtered].sort((a, b) => orderIndex(a) - orderIndex(b));
  }, [categoryTab, listableTemplates]);

  // Defaults elegibles aunque estén EN APROBACIÓN (spec Julian): queda configurado y
  // arranca solo cuando Meta apruebe; los estados muertos salen deshabilitados.
  const selectableForDefaults = useMemo(
    () => listableTemplates.filter((template) => templateSelectableInPicker(template)),
    [listableTemplates]
  );
  const defaultTemplateOptions = useMemo(
    () => [
      { value: "", label: "— Sin asignar —", description: "El chatbot no usará plantilla para este caso" },
      ...buildTemplatePickerOptions(selectableForDefaults),
    ],
    [selectableForDefaults]
  );
  const updateDefaultTemplate = (key: string, value: string) => {
    const next = { ...defaultTemplates, [key]: value };
    onDefaultTemplatesChange(next);
    void onSaveDefaultTemplates(next);
  };

  const handleProvisionPackage = async () => {
    setProvisioningPackage(true);
    try {
      const outcome = await chatbotV2Service.provisionTemplatePackage();
      const failed = Array.isArray(outcome?.failed) ? outcome.failed : [];
      if (failed.length) {
        // El error de instalación se VE con su causa (antes fallaba en silencio y el
        // banner de "faltan plantillas" quedaba para siempre sin explicación).
        toast.error(
          `${failed.length} plantilla${failed.length === 1 ? "" : "s"} no se pudieron instalar: ${failed
            .join(" · ")
            .slice(0, 220)}`
        );
      } else {
        toast.success("Plantillas recomendadas en camino: Meta las está aprobando");
      }
      onTemplateCreated();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setProvisioningPackage(false);
    }
  };

  const handleCreateTemplate = async () => {
    if (!creatorForm.name.trim() || !creatorForm.body_text.trim()) return;
    setCreatingTemplate(true);
    try {
      await chatbotV2Service.createTemplate({
        name: creatorForm.name.trim(),
        language: creatorForm.language,
        category: creatorForm.category,
        body_text: creatorForm.body_text.trim(),
      });
      toast.success("Plantilla enviada a Meta");
      setCreatorForm({ name: "", language: "es", category: "UTILITY", body_text: "" });
      setCreatorOpen(false);
      onTemplateCreated();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setCreatingTemplate(false);
    }
  };
  const templateStatusFilters = [
    "APPROVED",
    "PENDING",
    "REJECTED",
    "PAUSED",
    "DISABLED",
    "IN_APPEAL",
    "PENDING_DELETION",
    "DELETED",
    "DRAFT",
    "UNKNOWN",
  ];

  // Sub-pantalla de creación (Julian): ocupa toda la zona del tab, con "Volver" arriba
  // (mismo patrón de configurar imágenes de embudo). Al volver o crear, regresa al listado.
  if (creatorOpen) {
    return (
      <ScrollSurface>
        <div className="mx-auto max-w-5xl">
          <IntegrationBackButton label="Volver a plantillas" onBack={() => setCreatorOpen(false)} />
          <section className="rounded-xl border border-gray-200 bg-white p-4">
            <div>
              <h4 className="text-sm font-semibold text-gray-950">Nueva plantilla</h4>
              <p className="mt-1 text-xs leading-5 text-gray-500">
                Se envía a Meta para revisión. Cuando quede aprobada, sincronízala y asígnala a un uso.
              </p>
            </div>
            <div className="mt-4 grid gap-4 md:grid-cols-[minmax(0,1fr)_160px_190px]">
              <TextField
                label="Nombre"
                value={creatorForm.name}
                onChange={(value) => setCreatorForm({ ...creatorForm, name: value.toLowerCase().replace(/[^a-z0-9_]/g, "_") })}
                placeholder="confirmacion_orden"
              />
              <SelectField
                label="Idioma"
                value={creatorForm.language}
                options={["es", "en", "pt_BR"]}
                onChange={(value) => setCreatorForm({ ...creatorForm, language: value })}
              />
              <SelectField
                label="Categoría"
                value={creatorForm.category}
                options={["UTILITY", "MARKETING", "AUTHENTICATION"]}
                onChange={(value) => setCreatorForm({ ...creatorForm, category: value })}
              />
              <div className="md:col-span-3">
                <TextareaField
                  label="Contenido"
                  value={creatorForm.body_text}
                  onChange={(value) => setCreatorForm({ ...creatorForm, body_text: value })}
                  rows={5}
                  placeholder="Hola {{1}}, necesitamos confirmar tu pedido {{2}}. Responde Sí para confirmarlo o No para cancelarlo."
                  helperText="Puedes usar variables como {{1}}, {{2}}. Fluxi enviará ejemplos automáticos a Meta."
                />
              </div>
            </div>
            <div className="mt-4 flex justify-end">
              <button
                onClick={() => void handleCreateTemplate()}
                disabled={creatingTemplate || !creatorForm.name.trim() || !creatorForm.body_text.trim()}
                className="inline-flex h-10 items-center justify-center rounded-full bg-gray-950 px-5 text-sm font-semibold text-white transition hover:bg-gray-800 disabled:opacity-50"
              >
                {creatingTemplate ? <Loader2 className="mr-2 size-4 animate-spin" /> : null}
                Crear plantilla
              </button>
            </div>
          </section>
        </div>
      </ScrollSurface>
    );
  }

  return (
    <ScrollSurface>
      <div className="mx-auto max-w-5xl">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => setFiltersOpen((open) => !open)}
              className={`inline-flex size-9 items-center justify-center rounded-full transition ${
                filtersOpen || templateStatusFilter ? "bg-gray-200 text-gray-950" : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
              title="Filtrar plantillas"
            >
              <Filter className="size-4" />
            </button>
            <button
              onClick={onSyncTemplates}
              disabled={templateSaving}
              className="inline-flex size-9 items-center justify-center rounded-full bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:opacity-50"
              title="Sincronizar plantillas"
            >
              {templateSaving ? <Loader2 className="size-4 animate-spin" /> : <RefreshCw className="size-4" />}
            </button>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={() => setCreatorOpen((open) => !open)}
              className="inline-flex h-9 items-center justify-center rounded-full bg-gray-100 px-4 text-sm font-semibold text-gray-800 transition hover:bg-gray-200"
            >
              + Agregar plantilla
            </button>
          </div>
        </div>
        {missingRecommended.length > 0 && !templateStatusFilter ? (
          <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-gray-200 bg-gray-50 px-4 py-3">
            <p className="text-sm text-gray-700">Te faltan plantillas recomendadas de Fluxi</p>
            <button
              type="button"
              onClick={() => void handleProvisionPackage()}
              disabled={provisioningPackage}
              className="inline-flex h-9 shrink-0 items-center gap-2 rounded-full bg-gray-950 px-4 text-sm font-semibold text-white transition hover:bg-gray-800 disabled:opacity-50"
            >
              {provisioningPackage ? <Loader2 className="size-4 animate-spin" /> : null}
              Instalarlas
            </button>
          </div>
        ) : null}
        {filtersOpen ? (
          <div className="mt-3 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => onTemplateStatusFilterChange("")}
              className={`rounded-full px-3 py-1.5 text-xs font-semibold transition ${
                !templateStatusFilter ? "bg-gray-200 text-gray-950" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
            >
              Todos
            </button>
            {templateStatusFilters.map((status) => (
              <button
                key={status}
                type="button"
                onClick={() => onTemplateStatusFilterChange(templateStatusFilter === status ? "" : status)}
                className={`rounded-full px-3 py-1.5 text-xs font-semibold transition ${
                  templateStatusFilter === status ? "bg-gray-200 text-gray-950" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
              >
                {formatTemplateStatus(status)}
              </button>
            ))}
          </div>
        ) : null}

        <div className="mt-5">
          <SimpleSubTabBar tabs={TEMPLATE_CATEGORY_TABS} active={categoryTab} onChange={setCategoryTab} />
        </div>

        <div className="mt-4 space-y-3">
          {visibleTemplates.map((template) => {
            const visibleIssue = templateVisibleIssue(template);
            return (
            <details key={template.id} className="group rounded-lg border border-gray-200 bg-white">
              <summary className="flex cursor-pointer list-none items-center justify-between gap-4 px-4 py-3 hover:bg-gray-50">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="truncate font-semibold text-gray-950">{formatTemplateDisplayName(template.name)}</p>
                    <span className="inline-flex rounded-full bg-gray-100 px-2.5 py-1 text-xs font-semibold text-gray-600">
                      {formatTemplateStatus(template.status)}
                    </span>
                  </div>
                  <p className="mt-1 truncate text-xs text-gray-500">{template.name || "Sin nombre técnico"}</p>
                  <p className="mt-1 line-clamp-1 text-xs text-gray-500 group-open:hidden">
                    {template.body_text || "Sin cuerpo sincronizado desde Meta."}
                  </p>
                </div>
                <div className="flex shrink-0 items-center gap-3 text-xs text-gray-500">
                  <ChevronDown className="size-4 transition group-open:rotate-180" />
                </div>
              </summary>
              <div className="px-4 pb-4 pt-2">
                <div>
                  <div className="mt-3 rounded-lg bg-gray-50 p-3">
                    {templateHasImageHeader(template) ? <TemplateImageHeaderPlaceholder /> : null}
                    <p className="whitespace-pre-wrap text-sm leading-6 text-gray-700">
                      {template.body_text || "Meta no devolvió el texto del body en la última sincronización."}
                    </p>
                  </div>
                  <p className="mt-2 text-xs text-gray-400">Actualizado: {formatDate(template.last_synced_at || template.created_at)}</p>
                  {visibleIssue ? (
                    <p className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                      {visibleIssue}
                    </p>
                  ) : null}
                </div>
                <details className="mt-4">
                  <summary className="flex cursor-pointer list-none items-center gap-1.5 py-1 text-xs font-medium text-gray-500 hover:text-gray-900">
                    Información avanzada
                    <ChevronDown className="size-3.5 transition group-open:rotate-180" />
                  </summary>
                  <div className="mt-2 rounded-xl bg-gray-50 px-4 py-4">
                    <div className="grid gap-x-8 gap-y-3 text-sm md:grid-cols-2">
                      <InfoRow label="Idioma" value={template.language} />
                      <InfoRow label="Categoría" value={template.category || "Sin dato"} />
                      <InfoRow label="Calidad" value={formatWhatsAppQuality(template.quality_score || template.quality_rating)} />
                      <InfoRow label="Variables" value={template.variable_count} />
                      <InfoRow label="Meta ID" value={template.meta_template_id || "Sin dato"} />
                      <InfoRow label="Sync" value={formatDate(template.last_synced_at)} />
                    </div>
                    {template.components?.length ? (
                      <pre className="mt-4 max-h-56 overflow-auto rounded-lg bg-white p-3 text-xs text-gray-700">
                        {JSON.stringify(template.components, null, 2)}
                      </pre>
                    ) : null}
                  </div>
                </details>
              </div>
            </details>
          );
          })}
          {templates.length === 0 ? (
            <EmptyState icon={FileText} title="Sin plantillas sincronizadas" body="Sincroniza con Meta para cargar el catálogo de templates disponibles." />
          ) : visibleTemplates.length === 0 ? (
            <div className="rounded-xl bg-gray-50 px-4 py-5 text-sm leading-6 text-gray-500">
              No hay plantillas en esta categoría.
            </div>
          ) : null}
        </div>

        <div className="mt-8 flex justify-end border-t border-gray-100 pt-4">
          <button
            type="button"
            onClick={() => setMoreOptionsOpen((open) => !open)}
            className="inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs font-semibold text-gray-500 transition hover:bg-gray-50 hover:text-gray-950"
          >
            Más opciones
            <ChevronDown className={`size-3.5 transition ${moreOptionsOpen ? "rotate-180" : ""}`} />
          </button>
        </div>
        {moreOptionsOpen ? (
          <section className="mt-3">
            <h3 className="text-sm font-semibold text-gray-950">Plantillas por defecto</h3>
            <p className="mt-1 text-xs leading-5 text-gray-500">
              Las que el chatbot usa si una acción no tiene plantilla propia.
            </p>
            <div className="mt-4 grid gap-4 md:grid-cols-2">
              {DEFAULT_TEMPLATE_SLOTS.map((slot) => (
                <div key={slot.key} className="block">
                  <span className="mb-1 block text-xs font-semibold text-gray-600">{slot.label}</span>
                  <ModalSelect
                    value={defaultTemplates[slot.key] || ""}
                    placeholder="— Sin asignar —"
                    options={defaultTemplateOptions}
                    dividers
                    emphasizeLabels
                    disabled={templateSaving}
                    onChange={(value) => updateDefaultTemplate(slot.key, value)}
                  />
                </div>
              ))}
            </div>
          </section>
        ) : null}
      </div>
    </ScrollSurface>
  );
};

const GuardianWorkspace = ({
  guardianSettings,
  guardianRuns,
  guardianProposals,
  guardianConversations,
  focusSuggestionId,
  saving,
  loading = false,
  onFocusSuggestionHandled,
  onResolveProposal,
  onCreateConversation,
  onSendMessage,
}: {
  guardianSettings: GuardianSettings | null;
  guardianState: GuardianState | null;
  guardianRuns: GuardianRun[];
  guardianMemory: GuardianMemoryItem[];
  guardianProposals: GuardianProposal[];
  guardianConversations: GuardianConversation[];
  focusSuggestionId: string | null;
  saving: boolean;
  loading?: boolean;
  onFocusSuggestionHandled: () => void;
  onResolveProposal: (proposalId: string, status: "approved" | "rejected", userInput?: string, note?: string) => void;
  onCreateConversation: () => Promise<GuardianConversation | null>;
  onSendMessage: (conversationId: string, content: string) => Promise<GuardianConversation | null>;
}) => {
  const openProposals = guardianProposals.filter((proposal) => proposal.status === "pending" || proposal.status === "open");
  const guardianEnabled = guardianSettings?.enabled !== false;
  const [guardianDraft, setGuardianDraft] = useState("");
  const [expandedSuggestionId, setExpandedSuggestionId] = useState<string | null>(null);
  const [expandedProcessedSuggestionId, setExpandedProcessedSuggestionId] = useState<string | null>(null);
  const [selectedGuardianConversationId, setSelectedGuardianConversationId] = useState<string | null>(null);
  const [localGuardianConversations, setLocalGuardianConversations] = useState<GuardianChatHistoryItem[]>([]);
  const [suggestionsOpen, setSuggestionsOpen] = useState(false);
  const [conversationsCollapsed, setConversationsCollapsed] = useState(false);
  const [showProcessedSuggestions, setShowProcessedSuggestions] = useState(false);
  const [exampleSuggestionStatuses, setExampleSuggestionStatuses] = useState<Record<string, "approved" | "rejected">>({});
  const guardianTextareaRef = useRef<HTMLTextAreaElement | null>(null);
  const pendingGuardianCaretRef = useRef<number | null>(null);
  const [guardianComposerHeight, setGuardianComposerHeight] = useState(40);
  const guardianComposerExpanded = guardianDraft.includes("\n") || guardianDraft.length > 80;
  useEffect(() => {
    const textarea = guardianTextareaRef.current;
    if (!textarea) return;
    textarea.style.height = "auto";
    const nextHeight = Math.min(textarea.scrollHeight, CHAT_COMPOSER_MAX_HEIGHT);
    textarea.style.height = `${nextHeight}px`;
    setGuardianComposerHeight(nextHeight);
    if (textarea.scrollHeight > CHAT_COMPOSER_MAX_HEIGHT) {
      textarea.scrollTop = textarea.scrollHeight;
    }
  }, [guardianDraft]);
  const guardianThreadBottomPadding = Math.max(128, guardianComposerHeight + 72);
  const guardianComposerOverlayOffset = Math.max(128, guardianComposerHeight + 56);
  useEffect(() => {
    if (pendingGuardianCaretRef.current === null) return;
    const position = pendingGuardianCaretRef.current;
    pendingGuardianCaretRef.current = null;
    requestAnimationFrame(() => {
      const textarea = guardianTextareaRef.current;
      if (!textarea) return;
      textarea.focus({ preventScroll: true });
      textarea.setSelectionRange(position, position);
      textarea.scrollTop = textarea.scrollHeight;
    });
  }, [guardianDraft]);
  const showGuardianSuggestionExamples = useMemo(() => {
    return getLocalChatbotDebugParams()?.has("simulate_work_indicator") || false;
  }, []);
  const exampleSuggestionCards = showGuardianSuggestionExamples
    ? GUARDIAN_SUGGESTION_EXAMPLES.filter((suggestion) => !exampleSuggestionStatuses[suggestion.id])
    : [];
  const proposalSuggestionCards = openProposals.map(proposalToSuggestionCard);
  const suggestionCards = showGuardianSuggestionExamples
    ? exampleSuggestionCards
    : proposalSuggestionCards;
  const suggestionCount = suggestionCards.length;
  const processedProposalCards: ProcessedGuardianSuggestionCardData[] = guardianProposals
    .filter((proposal) => proposal.status === "approved" || proposal.status === "rejected")
    .map((proposal) => ({
      ...proposalToSuggestionCard(proposal),
      processedStatus: proposal.status === "approved" ? "approved" : "rejected",
      actionResult: guardianProposalActionResult(proposal),
    }));
  const processedExampleCards: ProcessedGuardianSuggestionCardData[] = showGuardianSuggestionExamples
    ? GUARDIAN_SUGGESTION_EXAMPLES
        .filter((suggestion) => exampleSuggestionStatuses[suggestion.id])
        .map((suggestion) => ({
          ...suggestion,
          processedStatus: exampleSuggestionStatuses[suggestion.id],
        }))
    : [];
  const processedSuggestionCards = [...processedProposalCards, ...processedExampleCards];
  useEffect(() => {
    if (!focusSuggestionId) return;
    setSuggestionsOpen(true);
    if (suggestionCards.some((suggestion) => suggestion.id === focusSuggestionId)) {
      setExpandedSuggestionId(focusSuggestionId);
      setExpandedProcessedSuggestionId(null);
      onFocusSuggestionHandled();
      return;
    }
    if (processedSuggestionCards.some((suggestion) => suggestion.id === focusSuggestionId)) {
      setShowProcessedSuggestions(true);
      setExpandedProcessedSuggestionId(focusSuggestionId);
      setExpandedSuggestionId(null);
      onFocusSuggestionHandled();
      return;
    }
    onFocusSuggestionHandled();
  }, [focusSuggestionId, onFocusSuggestionHandled, processedSuggestionCards, suggestionCards]);
  const serverGuardianConversations = useMemo(() => guardianConversations.map((conversation): GuardianChatHistoryItem => ({
    id: conversation.id,
    title: conversation.title || "Conversación",
    preview: conversation.preview || "Sin mensajes todavía",
    time: formatMessageTime(conversation.updated_at) || "Reciente",
    messages: (conversation.messages || []).map((message): GuardianChatMessage => ({
      id: message.id,
      sender: message.sender === "owner" ? "owner" : "guardian",
      content: message.content,
      time: formatMessageTime(message.created_at) || "Reciente",
    })),
  })), [guardianConversations]);
  const localGuardianConversationIds = new Set(localGuardianConversations.map((conversation) => conversation.id));
  const chatHistory = [
    ...localGuardianConversations,
    ...serverGuardianConversations.filter((conversation) => !localGuardianConversationIds.has(conversation.id)),
  ];
  const selectedGuardianConversation =
    chatHistory.find((item) => item.id === selectedGuardianConversationId) || chatHistory[0] || null;
  // El sidebar de sugerencias usa el MISMO ancho que el de conversaciones (Julian).
  const guardianGridTemplateColumns = `${conversationsCollapsed ? "0px" : "420px"} minmax(0,1fr)${
    suggestionsOpen ? " 420px" : ""
  }`;
  // Autoscroll del chat (Julian): al enviar un mensaje y al llegar la respuesta, el contenedor
  // baja al fondo para que el mensaje nuevo no quede oculto debajo de la caja de texto.
  const guardianThreadRef = useRef<HTMLDivElement | null>(null);
  const selectedGuardianMessages = selectedGuardianConversation?.messages;
  useEffect(() => {
    const container = guardianThreadRef.current;
    if (!container) return;
    container.scrollTop = container.scrollHeight;
  }, [selectedGuardianConversation?.id, selectedGuardianMessages]);
  const handleNewGuardianConversation = async () => {
    const conversation = await onCreateConversation();
    if (!conversation) return;
    setLocalGuardianConversations([]);
    setSelectedGuardianConversationId(conversation.id);
    setGuardianDraft("");
  };
  const sendGuardianText = async (rawText: string, options?: { retryExistingMessage?: boolean }) => {
    const text = rawText.trim();
    if (!text || saving || !guardianEnabled) return;
    let targetConversationId = selectedGuardianConversation?.id || null;
    let baseMessages = (selectedGuardianConversation?.messages || []).filter((message) => !message.pending && !message.failed);
    if (!targetConversationId || !guardianConversations.some((item) => item.id === targetConversationId)) {
      const conversation = await onCreateConversation();
      if (!conversation) return;
      targetConversationId = conversation.id;
      baseMessages = [];
      setSelectedGuardianConversationId(conversation.id);
    }
    const now = new Date().toISOString();
    const ownerMessage: GuardianChatMessage = {
      id: `guardian-owner-${Date.now()}`,
      sender: "owner",
      content: text,
      time: formatMessageTime(now) || "Ahora",
    };
    const pendingMessage: GuardianChatMessage = {
      id: `guardian-pending-${Date.now()}`,
      sender: "guardian",
      content: "",
      time: "Ahora",
      pending: true,
    };
    const optimisticConversation: GuardianChatHistoryItem = {
      id: targetConversationId,
      title: selectedGuardianConversation?.title || "Conversación",
      preview: text,
      time: "Ahora",
      messages: [...baseMessages, ...(options?.retryExistingMessage ? [] : [ownerMessage]), pendingMessage],
    };
    setLocalGuardianConversations((current) => [
      optimisticConversation,
      ...current.filter((item) => item.id !== targetConversationId),
    ]);
    setSelectedGuardianConversationId(targetConversationId);
    setGuardianDraft("");
    const conversation = await onSendMessage(targetConversationId, text);
    if (conversation) {
      setLocalGuardianConversations([]);
      setSelectedGuardianConversationId(conversation.id);
    } else {
      setLocalGuardianConversations((current) =>
        current.map((item) =>
          item.id === targetConversationId
            ? {
                ...item,
                messages: item.messages.map((message) =>
                  message.id === pendingMessage.id
                    ? {
                        ...message,
                        pending: false,
                        failed: true,
                        retryContent: text,
                        content: "Guardian no pudo responder. Intenta de nuevo.",
                      }
                    : message
                ),
              }
            : item
        )
      );
    }
  };
  const handleSendGuardianDraft = async () => {
    const text = guardianDraft.trim();
    if (!text || saving || !guardianEnabled) return;
    await sendGuardianText(text);
  };
  const handleDiscussGuardianSuggestion = (suggestionNumber: number) => {
    const discussionDraft = `Sobre la sugerencia #${suggestionNumber}, quiero discutir lo siguiente: \n\n`;
    pendingGuardianCaretRef.current = discussionDraft.length;
    setGuardianDraft(discussionDraft);
  };
  const suggestionsContent = (
    <div className="space-y-2">
      {loading ? (
        <div className="space-y-2 px-1 py-2">
          {Array.from({ length: 3 }).map((_, index) => (
            <div key={index} className="animate-pulse rounded-xl bg-gray-100 px-3 py-4">
              <div className="h-4 w-3/4 rounded-full bg-gray-200" />
              <div className="mt-2 h-3 w-full rounded-full bg-gray-200/80" />
            </div>
          ))}
        </div>
      ) : suggestionCards.length > 0 ? (
        suggestionCards.map((suggestion, index) => (
          <GuardianSuggestionCard
            key={suggestion.id}
            suggestion={suggestion}
            index={suggestion.number ?? index + 1}
            expanded={expandedSuggestionId === suggestion.id}
            saving={saving}
            onToggle={() => setExpandedSuggestionId((current) => current === suggestion.id ? null : suggestion.id)}
            onDiscuss={() => handleDiscussGuardianSuggestion(suggestion.number ?? index + 1)}
            onResolve={(status, userInput, note) => {
              if (!suggestion.proposal) {
                setExampleSuggestionStatuses((current) => ({ ...current, [suggestion.id]: status }));
                setExpandedSuggestionId(null);
                setShowProcessedSuggestions(true);
                toast(status === "approved" ? "Sugerencia aprobada" : "Sugerencia rechazada");
                return;
              }
              setShowProcessedSuggestions(true);
              onResolveProposal(suggestion.proposal.id, status, userInput, note);
            }}
          />
        ))
      ) : (
        <p className="px-3 py-2 text-xs leading-5 text-gray-400">Sin sugerencias activas.</p>
      )}
      {processedSuggestionCards.length > 0 ? (
        <div className="pt-1">
          <button
            type="button"
            onClick={() => setShowProcessedSuggestions((current) => !current)}
            className="mx-auto flex items-center gap-1 rounded-full px-2 py-1 text-xs font-medium text-gray-500 transition hover:text-gray-900"
            aria-expanded={showProcessedSuggestions}
          >
            Sugerencias procesadas
            <span className="text-gray-400">{processedSuggestionCards.length}</span>
            <ChevronDown className={`size-3.5 transition ${showProcessedSuggestions ? "rotate-180" : ""}`} />
          </button>
          {showProcessedSuggestions ? (
            <div className="mt-2 space-y-2">
              {processedSuggestionCards.map((suggestion, index) => (
                <ProcessedGuardianSuggestionCard
                  key={`${suggestion.id}-${suggestion.processedStatus}`}
                  suggestion={suggestion}
                  index={suggestion.number ?? index + 1}
                  expanded={expandedProcessedSuggestionId === suggestion.id}
                  onToggle={() => setExpandedProcessedSuggestionId((current) => current === suggestion.id ? null : suggestion.id)}
                  onDiscuss={() => handleDiscussGuardianSuggestion(suggestion.number ?? index + 1)}
                />
              ))}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );

  return (
    <div className="relative h-full min-h-0 overflow-hidden" style={GUARDIAN_WORKSPACE_BACKGROUND_STYLE}>
      <div
        className="grid h-full min-h-0 w-full transition-[grid-template-columns] duration-200"
        style={{ gridTemplateColumns: guardianGridTemplateColumns }}
      >
        {conversationsCollapsed ? (
          <div aria-hidden="true" />
        ) : (
        <aside className="flex min-h-0 flex-col overflow-hidden border-r border-gray-200 bg-white">
          <div className="flex min-h-0 flex-1 flex-col">
            <div className="flex h-14 shrink-0 items-center gap-2 px-4">
              <MessageCircle className="size-4 text-gray-500" />
              <h3 className="min-w-0 flex-1 text-sm font-semibold text-gray-950">Conversaciones</h3>
              <button
                type="button"
                onClick={() => void handleNewGuardianConversation()}
                className="inline-flex h-8 shrink-0 items-center gap-1 rounded-full px-2.5 text-xs font-semibold text-gray-700 transition hover:bg-gray-100"
              >
                <Plus className="size-3.5" />
                Nueva
              </button>
            </div>
            <div className="min-h-0 flex-1 space-y-2 overflow-y-auto px-3 pb-3">
              {loading ? (
                <div className="space-y-2">
                  {Array.from({ length: 5 }).map((_, index) => (
                    <div key={index} className="animate-pulse rounded-xl bg-gray-100 px-3 py-4">
                      <div className="h-4 w-2/3 rounded-full bg-gray-200" />
                      <div className="mt-2 h-3 w-full rounded-full bg-gray-200/80" />
                      <div className="mt-1 h-3 w-4/5 rounded-full bg-gray-200/70" />
                    </div>
                  ))}
                </div>
              ) : chatHistory.length === 0 ? (
                <div className="rounded-xl bg-gray-50 px-3 py-8 text-center">
                  <p className="text-sm font-semibold text-gray-700">Sin conversaciones todavía</p>
                  <p className="mt-1 text-xs leading-5 text-gray-500">Crea una nueva conversación para hablar con Guardian.</p>
                  <button
                    type="button"
                    onClick={() => void handleNewGuardianConversation()}
                    className="mt-4 inline-flex h-9 items-center gap-2 rounded-full bg-gray-950 px-4 text-sm font-semibold text-white hover:bg-gray-800"
                  >
                    <Plus className="size-4" />
                    Nueva conversación
                  </button>
                </div>
              ) : (
              chatHistory.map((item) => {
                const active = selectedGuardianConversation?.id === item.id;
                return (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => setSelectedGuardianConversationId(item.id)}
                    className={`w-full rounded-xl px-3 py-3 text-left transition hover:bg-gray-50 ${
                      active ? "bg-gray-50" : "bg-white"
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <p className="min-w-0 truncate text-sm font-semibold text-gray-950">{item.title}</p>
                      <span className="shrink-0 text-[11px] font-medium text-gray-400">{item.time}</span>
                    </div>
                    <p className="mt-1 line-clamp-2 text-xs leading-5 text-gray-500">{item.preview}</p>
                  </button>
                );
              })
              )}
            </div>
          </div>

          <div className="shrink-0 border-t border-gray-100 p-3">
            <button
              type="button"
              onClick={() => setConversationsCollapsed(true)}
              className="inline-flex h-9 items-center gap-1.5 rounded-full px-2.5 text-xs font-semibold text-gray-600 transition hover:bg-gray-100 hover:text-gray-950"
            >
              <ChevronLeft className="size-4" />
              Ocultar
            </button>
          </div>
        </aside>
        )}
        <section className="relative m-4 flex min-h-0 flex-col overflow-hidden rounded-[22px] border border-gray-200 bg-white shadow-[0px_18px_45px_rgba(37,99,235,0.12)] md:m-6">
          <div className="relative flex h-24 items-center justify-between gap-3 overflow-hidden border-b border-gray-100 px-5">
            <div className="flex min-w-0 items-center gap-4">
              <span
                className={`relative -mb-px h-24 w-[106px] shrink-0 overflow-hidden ${
                  guardianEnabled ? "" : "grayscale opacity-60"
                }`}
              >
                <img
                  src={GuardianAvatar}
                  alt="Guardian"
                  className="absolute bottom-[-8px] left-1/2 h-[104px] max-w-none -translate-x-1/2 object-contain"
                />
              </span>
              <div className="min-w-0">
                <span className="mb-1 inline-flex h-[18px] items-center gap-1 rounded-full bg-gray-100 px-[7px] text-[10px] font-semibold text-gray-500">
                  <span className={`size-[5px] rounded-full ${guardianEnabled ? "bg-blue-400" : "bg-gray-400"}`} />
                  {guardianEnabled ? "Disponible" : "Desactivado"}
                </span>
                <h3 className="truncate text-xl font-semibold text-gray-950">Guardian de soporte</h3>
              </div>
            </div>
            {!suggestionsOpen ? (
              <button
                type="button"
                onClick={() => setSuggestionsOpen(true)}
                className="inline-flex h-10 shrink-0 items-center gap-2 rounded-full bg-gray-100 px-3 text-sm font-semibold text-gray-700 transition hover:bg-gray-200/80"
              >
                Sugerencias
                <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-white px-1.5 text-xs text-gray-700">
                  {suggestionCount}
                </span>
              </button>
            ) : null}
          </div>

          <div
            ref={guardianThreadRef}
            className="min-h-0 flex-1 overflow-y-auto px-5 pt-5"
            style={{ ...CHAT_THREAD_BACKGROUND_STYLE, paddingBottom: guardianThreadBottomPadding }}
          >
            <div className="mx-auto flex max-w-3xl flex-col gap-3">
              {selectedGuardianConversation?.messages.length === 0 ? (
                <div className="mx-auto mt-12 max-w-sm rounded-2xl bg-white/90 px-4 py-3 text-center text-sm text-gray-500 shadow-[0px_1px_4px_0px_rgba(99,99,99,0.16)]">
                  Escribe a Guardian para iniciar esta conversación.
                </div>
              ) : null}
              {selectedGuardianConversation?.messages.map((message) => {
                const outbound = message.sender === "owner";
                return (
                  <div key={message.id} className={`flex ${outbound ? "justify-end" : "justify-start"}`}>
                    <div
                      className={`max-w-[min(560px,82%)] rounded-xl px-4 py-3 text-sm leading-relaxed shadow-[0px_1px_4px_0px_rgba(99,99,99,0.18)] ${
                        message.failed
                          ? "border border-red-200 bg-red-50 text-red-900"
                          : outbound
                            ? "bg-[#F6F7F8] text-gray-900"
                            : "bg-white text-gray-800"
                      }`}
                    >
                      {message.pending ? (
                        <TypingDots label="Guardian está pensando" />
                      ) : (
                        <>
                          <p className="whitespace-pre-wrap">{message.content}</p>
                          {message.failed && message.retryContent ? (
                            <button
                              type="button"
                              onClick={() => void sendGuardianText(message.retryContent || "", { retryExistingMessage: true })}
                              disabled={saving || !guardianEnabled}
                              className="mt-3 inline-flex h-8 items-center gap-1.5 rounded-full border border-red-200 bg-white px-3 text-xs font-semibold text-red-700 transition hover:bg-red-100 disabled:opacity-50"
                            >
                              {saving ? <Loader2 className="size-3.5 animate-spin" /> : <RotateCcw className="size-3.5" />}
                              Reintentar
                            </button>
                          ) : null}
                        </>
                      )}
                      {!message.pending ? (
                        <div className="mt-2 flex items-center justify-end gap-2 text-[11px] text-gray-500">
                          <span>{outbound ? "Tú" : "Guardian"}</span>
                          <span aria-hidden="true">•</span>
                          <span>{message.time}</span>
                        </div>
                      ) : null}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div
            className="pointer-events-none relative z-10 px-5 pb-6 pt-4"
            style={{ marginTop: -guardianComposerOverlayOffset }}
          >
            <div className="mx-auto max-w-3xl">
              <div
                className={`pointer-events-auto flex items-end gap-2 border border-gray-200 bg-white p-2 text-sm shadow-[0px_2px_8px_0px_rgba(99,99,99,0.2)] transition-[border-radius,opacity] ${
                  guardianComposerExpanded ? "rounded-[24px]" : "rounded-full"
                } ${guardianEnabled ? "text-gray-700" : "opacity-70 grayscale"}`}
              >
                <textarea
                  ref={guardianTextareaRef}
                  rows={guardianComposerExpanded ? 3 : 1}
                  value={guardianDraft}
                  onChange={(event) => setGuardianDraft(event.target.value)}
                  onKeyDown={(event) => submitTextareaOnEnter(event, () => void handleSendGuardianDraft())}
                  disabled={!guardianEnabled}
                  placeholder={guardianEnabled ? "Escribe a Guardian" : "Guardian está desactivado"}
                  className={`max-h-96 flex-1 resize-none overflow-y-auto rounded-[18px] border-0 bg-transparent px-3 py-2 text-sm leading-5 outline-none placeholder:text-gray-400 disabled:cursor-not-allowed ${
                    guardianComposerExpanded ? "min-h-[76px]" : "min-h-10"
                  }`}
                />
                <button
                  type="button"
                  onClick={() => void handleSendGuardianDraft()}
                  disabled={!guardianEnabled || !guardianDraft.trim() || saving}
                  className="inline-flex size-10 shrink-0 items-center justify-center rounded-full bg-gray-950 text-white transition hover:bg-gray-800 disabled:cursor-not-allowed disabled:bg-gray-200 disabled:text-gray-400"
                  title="Enviar a Guardian"
                >
                  <Send className="size-4" />
                </button>
              </div>
            </div>
          </div>
          {conversationsCollapsed ? (
            <button
              type="button"
              onClick={() => setConversationsCollapsed(false)}
              aria-label="Abrir conversaciones"
              className={`absolute bottom-7 left-5 z-20 inline-flex h-10 items-center rounded-full bg-white text-xs font-semibold text-gray-700 shadow-[0px_8px_24px_rgba(15,23,42,0.12)] transition hover:bg-gray-50 hover:text-gray-950 ${
                suggestionsOpen ? "gap-1.5 pl-2.5 pr-3" : "gap-2 pl-3 pr-3.5"
              }`}
            >
              <ChevronRight className="size-4" />
              <span>{suggestionsOpen ? "💬" : "Conversaciones"}</span>
            </button>
          ) : null}
        </section>
        {suggestionsOpen ? (
          <aside className="flex min-h-0 flex-col overflow-hidden border-l border-gray-200 bg-white">
            <div className="flex shrink-0 items-center justify-between px-4 py-3">
              <div className="min-w-0">
                <h3 className="text-[21px] font-semibold text-gray-950">Sugerencias</h3>
              </div>
              <button
                type="button"
                onClick={() => setSuggestionsOpen(false)}
                className="inline-flex size-8 items-center justify-center text-gray-600 transition hover:text-gray-950"
                title="Ocultar sugerencias"
              >
                <X className="size-5" />
              </button>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto px-3 pb-6 pt-px">{suggestionsContent}</div>
          </aside>
        ) : null}
      </div>
    </div>
  );
};

const ProcessedGuardianSuggestionCard = ({
  suggestion,
  index,
  expanded,
  onToggle,
  onDiscuss,
}: {
  suggestion: ProcessedGuardianSuggestionCardData;
  index: number;
  expanded: boolean;
  onToggle: () => void;
  onDiscuss: () => void;
}) => {
  const approved = suggestion.processedStatus === "approved";
  return (
    <div className={`overflow-hidden rounded-xl bg-gray-50 ${expanded ? "ring-1 ring-gray-500" : ""}`}>
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-start justify-between gap-3 px-3 py-3 text-left transition hover:bg-gray-100/70"
        aria-expanded={expanded}
      >
        <span className="min-w-0 flex-1">
          <span className="mb-1 flex items-center gap-2">
            <span className="inline-flex rounded-full bg-white px-2 py-0.5 text-[11px] font-semibold text-gray-500">#{index}</span>
            {suggestion.detectedAt ? (
              <span className="text-[11px] font-medium text-gray-400">{formatDate(suggestion.detectedAt)}</span>
            ) : null}
          </span>
          <span className="line-clamp-2 text-sm font-semibold leading-5 text-gray-950">{suggestion.title}</span>
          {suggestion.productName ? (
            <span className="mt-1 inline-flex max-w-full items-center rounded-full bg-white px-2.5 py-1 text-xs font-semibold text-gray-500">
              <span className="truncate">Producto: {suggestion.productName}</span>
            </span>
          ) : null}
          {suggestion.kind === "chatbot_info_gap" ? (
            <span className="mt-1 block text-[11px] font-medium text-gray-400">
              Sugerida por tu asistente desde una conversación
            </span>
          ) : null}
          {!expanded ? <span className="mt-1 line-clamp-2 text-xs leading-5 text-gray-500">{suggestion.problem}</span> : null}
        </span>
        <span className="flex shrink-0 items-center gap-1.5">
          <span
            className={`inline-flex size-5 items-center justify-center rounded-full ${
              approved ? "bg-emerald-50 text-emerald-600" : "bg-rose-50 text-rose-500"
            }`}
            title={approved ? "Aprobada" : "Rechazada"}
          >
            {approved ? <CheckCircle2 className="size-3.5" /> : <XCircle className="size-3.5" />}
          </span>
          <ChevronDown className={`size-4 text-gray-400 transition ${expanded ? "rotate-180" : ""}`} />
        </span>
      </button>
      {expanded ? (
        <div className="px-3 pb-3 pt-1">
          <div className="space-y-3 text-xs leading-5">
            <div className="flex flex-wrap items-center gap-1.5">
              <span
                className={`inline-flex items-center gap-1.5 rounded-full bg-white px-2.5 py-1 text-xs font-semibold ${
                  approved ? "text-emerald-600" : "text-rose-500"
                }`}
              >
                {approved ? <CheckCircle2 className="size-3.5" /> : <XCircle className="size-3.5" />}
                {approved ? "Aprobada" : "Rechazada"}
              </span>
              {suggestion.actionResult ? (
                <StatusPill label={suggestion.actionResult.label} tone={suggestion.actionResult.tone} />
              ) : null}
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">Problema</p>
              <p className="mt-1 text-gray-600">{suggestion.problem}</p>
            </div>
            {suggestion.kind === "chatbot_info_gap" ? null : (
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">Solución</p>
                <p className="mt-1 text-gray-600">{suggestion.solution}</p>
              </div>
            )}
            <GuardianProposedQuoteBlock proposal={suggestion.proposal} />
          </div>
          <div className="mt-4">
            <button
              type="button"
              onClick={onDiscuss}
              className="inline-flex h-8 w-full items-center justify-center gap-1.5 rounded-full bg-white px-3 text-xs font-semibold text-gray-700 transition hover:bg-gray-100"
            >
              <MessageCircle className="size-3.5" />
              Discutir
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
};

const GuardianSuggestionCard = ({
  suggestion,
  index,
  expanded,
  saving,
  onToggle,
  onDiscuss,
  onResolve,
}: {
  suggestion: GuardianSuggestionCardData;
  index: number;
  expanded: boolean;
  saving: boolean;
  onToggle: () => void;
  onDiscuss: () => void;
  onResolve: (status: "approved" | "rejected", userInput?: string, note?: string) => void;
}) => {
  const [userInput, setUserInput] = useState("");
  // Rechazo en dos pasos: al pulsar "Rechazar" se abre una caja para contar el porqué
  // (opcional, el guardián aprende de esto) antes de confirmar.
  const [rejectBoxOpen, setRejectBoxOpen] = useState(false);
  const [rejectNote, setRejectNote] = useState("");
  const requiresUserInput = Boolean(suggestion.needsUserInput);
  // Aprendizaje del modo aprendiz: la lección queda tal cual el dueño la apruebe, editable en un textarea.
  const isLearning = suggestion.kind === "learning";
  const [learningText, setLearningText] = useState(suggestion.learningEntryText ?? "");
  useEffect(() => {
    setLearningText(suggestion.learningEntryText ?? "");
  }, [suggestion.id, suggestion.learningEntryText]);
  const learningCategory = isLearning ? learningCategoryLabel(suggestion.learningCategory) : "";
  const approveDisabled = isLearning
    ? saving || rejectBoxOpen || !learningText.trim()
    : saving || rejectBoxOpen || (requiresUserInput && !userInput.trim());

  return (
  <div className={`overflow-hidden rounded-xl ${expanded ? "bg-white ring-1 ring-gray-500" : "bg-gray-50"}`}>
    <button
      type="button"
      onClick={onToggle}
      className={`flex w-full items-start justify-between gap-3 px-3 py-3 text-left transition ${expanded ? "hover:bg-gray-50" : "hover:bg-gray-100/70"}`}
      aria-expanded={expanded}
    >
      <span className="min-w-0 flex-1">
        {/* Fila #N + fecha (Julian: la fecha a la derecha del número gana espacio vertical). */}
        <span className="mb-1 flex items-center gap-2">
          <span className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold text-gray-500 ${expanded ? "bg-gray-100" : "bg-white"}`}>#{index}</span>
          {isLearning ? (
            <span className="inline-flex rounded-full bg-[#E8EEFF] px-2 text-[11px] font-semibold text-[#3151C9]">Aprendizaje del modo aprendiz</span>
          ) : null}
          {isLearning && learningCategory ? (
            <span className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold text-gray-500 ${expanded ? "bg-gray-100" : "bg-white"}`}>{learningCategory}</span>
          ) : null}
          {suggestion.detectedAt ? (
            <span className="text-[11px] font-medium text-gray-400">{formatDate(suggestion.detectedAt)}</span>
          ) : null}
        </span>
        <span className="line-clamp-2 text-sm font-semibold leading-5 text-gray-950">{suggestion.title}</span>
        {suggestion.productName ? (
          /* Chip gris del producto, debajo del título (mismo estilo del viejo "Usuarios afectados"). */
          <span className={`mt-1 inline-flex max-w-full items-center rounded-full px-2.5 py-1 text-xs font-semibold text-gray-500 ${expanded ? "bg-gray-100" : "bg-white"}`}>
            <span className="truncate">Producto: {suggestion.productName}</span>
          </span>
        ) : null}
        {suggestion.kind === "chatbot_info_gap" ? (
          <span className="mt-1 block text-[11px] font-medium text-gray-400">
            Sugerida por tu asistente desde una conversación
          </span>
        ) : null}
        {!expanded ? <span className="mt-1 line-clamp-2 text-xs leading-5 text-gray-500">{suggestion.problem}</span> : null}
      </span>
      <ChevronDown className={`mt-0.5 size-4 shrink-0 text-gray-400 transition ${expanded ? "rotate-180" : ""}`} />
    </button>
    {expanded ? (
      <div className="px-3 pb-3 pt-1">
        <div className="space-y-3 text-xs leading-5">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">Problema</p>
            <p className="mt-1 text-gray-600">{suggestion.problem}</p>
          </div>
          {/* En sugerencias del asistente (chatbot_info_gap) la "solución" no aporta: no se muestra. */}
          {suggestion.kind === "chatbot_info_gap" ? null : (
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">Solución</p>
              <p className="mt-1 text-gray-600">{suggestion.solution}</p>
            </div>
          )}
          {isLearning ? (
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">Así quedará en "Sugerencias aprobadas" — puedes editarlo:</p>
              <textarea
                value={learningText}
                onChange={(event) => setLearningText(event.target.value)}
                rows={3}
                className="mt-2 w-full rounded-xl border border-gray-200 p-2 text-sm"
              />
              {suggestion.learningExampleSummary ? (
                <p className="mt-1 text-[11px] italic text-gray-500">Ej.: {suggestion.learningExampleSummary}</p>
              ) : null}
            </div>
          ) : (
            <GuardianProposedQuoteBlock proposal={suggestion.proposal} />
          )}
          {requiresUserInput ? (
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">Instrucciones</p>
              <p className="mt-1 text-gray-600">{suggestion.inputRequest}</p>
              <textarea
                value={userInput}
                onChange={(event) => setUserInput(event.target.value)}
                rows={3}
                placeholder="Escribe la información que falta"
                className="mt-2 w-full resize-y rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm leading-5 text-gray-800 outline-none transition placeholder:text-gray-400 focus:border-gray-300 focus:ring-2 focus:ring-gray-200"
              />
            </div>
          ) : null}
        </div>
        {rejectBoxOpen ? (
          <div className="mt-4 rounded-xl border border-red-100 bg-red-50/60 p-3">
            <textarea
              value={rejectNote}
              onChange={(event) => setRejectNote(event.target.value)}
              rows={2}
              placeholder="¿Por qué decides rechazarla? (opcional — el guardián aprende de esto)"
              className="w-full resize-y rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs leading-5 text-gray-800 outline-none transition placeholder:text-gray-400 focus:border-red-200"
            />
            <div className="mt-2 flex flex-wrap justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setRejectBoxOpen(false);
                  setRejectNote("");
                }}
                disabled={saving}
                className="inline-flex h-8 items-center justify-center rounded-full bg-white px-3 text-xs font-semibold text-gray-700 transition hover:bg-gray-100 disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={() => onResolve("rejected", undefined, rejectNote.trim() || undefined)}
                disabled={saving}
                className="inline-flex h-8 items-center justify-center gap-1.5 rounded-full border border-red-200 bg-white px-3 text-xs font-semibold text-red-700 transition hover:bg-red-50 disabled:opacity-50"
              >
                <XCircle className="size-3.5" />
                Confirmar rechazo
              </button>
            </div>
          </div>
        ) : null}
        <div className="mt-4 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => onResolve("approved", isLearning ? learningText.trim() : userInput.trim() || undefined)}
            disabled={approveDisabled}
            className="inline-flex h-8 min-w-[96px] flex-1 items-center justify-center gap-1.5 rounded-full bg-gray-950 px-3 text-xs font-semibold text-white transition hover:bg-gray-800 disabled:cursor-not-allowed disabled:bg-gray-200 disabled:text-gray-400"
          >
            <CheckCircle2 className="size-3.5" />
            Aprobar
          </button>
          <button
            type="button"
            onClick={onDiscuss}
            disabled={saving || rejectBoxOpen}
            className="inline-flex h-8 min-w-[96px] flex-1 items-center justify-center gap-1.5 rounded-full border border-gray-200 bg-white px-3 text-xs font-semibold text-gray-700 transition hover:bg-gray-50 disabled:opacity-50"
          >
            <MessageCircle className="size-3.5" />
            Discutir
          </button>
          <button
            type="button"
            onClick={() => setRejectBoxOpen(true)}
            disabled={saving || rejectBoxOpen}
            className="inline-flex h-8 min-w-[96px] flex-1 items-center justify-center gap-1.5 rounded-full border border-gray-200 bg-white px-3 text-xs font-semibold text-gray-700 transition hover:bg-gray-50 disabled:opacity-50"
          >
            <XCircle className="size-3.5" />
            Rechazar
          </button>
        </div>
      </div>
    ) : null}
  </div>
  );
};

const KnowledgeTab = ({
  settingsForm,
  detailView,
  sources,
  loading = false,
  loadedOnce = false,
  form,
  editingSourceId,
  saving,
  testMessage,
  testContact,
  testResponse,
  testMessages,
  testLoading,
  mediaResources,
  onSettingsFormChange,
  onDetailViewChange,
  onFormChange,
  onSave,
  onSaveLearningEntries,
  onEdit,
  onDelete,
  onToggle,
  onCancelEdit,
  onTestMessageChange,
  onTestContactChange,
  onRunTest,
  onResetTest,
  onMediaResourcesChange,
}: {
  settingsForm: SupportSettingsPayload;
  detailView: "main" | "test" | "editor" | "source" | "media";
  sources: KnowledgeSource[];
  loading?: boolean;
  loadedOnce?: boolean;
  form: KnowledgeFormState;
  editingSourceId: number | null;
  saving: boolean;
  testMessage: string;
  testContact: string;
  testResponse: SupportTestChatResponse | null;
  testMessages: SupportTestChatMessage[];
  testLoading: boolean;
  mediaResources: MediaResourceDraft[];
  onSettingsFormChange: (form: SupportSettingsPayload) => void;
  onDetailViewChange: (view: "main" | "test" | "editor" | "source" | "media") => void;
  onFormChange: (form: KnowledgeFormState) => void;
  onSave: () => Promise<void> | void;
  onSaveLearningEntries: (sourceId: number, entries: LearningEntry[]) => Promise<boolean> | boolean;
  onEdit: (source: KnowledgeSource) => void;
  onDelete: (sourceId: number) => Promise<void> | void;
  onToggle: (source: KnowledgeSource, isActive: boolean) => void;
  onCancelEdit: () => void;
  onTestMessageChange: (value: string) => void;
  onTestContactChange: (value: string) => void;
  onRunTest: (message?: string, options?: { retryExistingMessage?: boolean }) => void;
  onResetTest: () => void;
  onMediaResourcesChange: (rows: MediaResourceDraft[]) => void;
}) => {
  const [selectedSourceId, setSelectedSourceId] = useState<number | null>(null);
  const [selectedMediaResourceId, setSelectedMediaResourceId] = useState<string | null>(null);
  const [uploadingMediaResourceId, setUploadingMediaResourceId] = useState<string | null>(null);
  const [uploadedFiles, setUploadedFiles] = useState<Array<{ id: string; name: string; type: string; size: number }>>([]);
  const [moreInstructionsOpen, setMoreInstructionsOpen] = useState(false);
  // Config de seguimientos dinámicos: se expande con el chevron, independiente del toggle.
  const [followupConfigOpen, setFollowupConfigOpen] = useState(false);
  const selectedSource = sources.find((source) => source.id === selectedSourceId) || null;
  const selectedMediaResource = mediaResources.find((resource) => resource.id === selectedMediaResourceId) || null;
  const activeKnowledgeChars = sources.reduce((total, source) => {
    if (!source.is_active) return total;
    return total + resolveKnowledgeCharCount(source);
  }, 0);
  const activeKnowledgePercent = Math.min(100, Math.round((activeKnowledgeChars / KNOWLEDGE_ACTIVE_CHAR_LIMIT) * 100));
  const formatKnowledgeChars = (value: number) => value.toLocaleString("es-CO");
  const toggleKnowledgeSource = (source: KnowledgeSource, isActive: boolean) => {
    if (isActive && !source.is_active) {
      const sourceChars = resolveKnowledgeCharCount(source);
      const nextUsage = activeKnowledgeChars + sourceChars;
      if (nextUsage > KNOWLEDGE_ACTIVE_CHAR_LIMIT) {
        toast.error("Este conocimiento supera el espacio activo disponible. Resume o pausa otra fuente antes de activarlo.");
        return;
      }
    }
    onToggle(source, isActive);
  };
  const settingsMetadata = isRecord(settingsForm.metadata_json) ? settingsForm.metadata_json : {};
  const closingInstructions =
    typeof settingsMetadata.conversation_closing_instructions === "string"
      ? settingsMetadata.conversation_closing_instructions
      : "";
  const priorityMediumInstructions =
    typeof settingsMetadata.priority_medium_instructions === "string"
      ? settingsMetadata.priority_medium_instructions
      : "";
  const priorityHighInstructions =
    typeof settingsMetadata.priority_high_instructions === "string"
      ? settingsMetadata.priority_high_instructions
      : "";
  const priorityNormalInstructions =
    typeof settingsMetadata.priority_normal_instructions === "string"
      ? settingsMetadata.priority_normal_instructions
      : "";
  const humanHelpRequestInstructions =
    typeof settingsMetadata.human_help_request_instructions === "string"
      ? settingsMetadata.human_help_request_instructions
      : "";
  const chatNoteUsageInstructions =
    typeof settingsMetadata.chat_note_usage_instructions === "string"
      ? settingsMetadata.chat_note_usage_instructions
      : "";
  const refundPolicyInstructions =
    typeof settingsMetadata.refund_policy_instructions === "string"
      ? settingsMetadata.refund_policy_instructions
      : "";
  const updateSettingsMetadata = (patch: Record<string, unknown>) => {
    onSettingsFormChange({
      ...settingsForm,
      metadata_json: {
        ...settingsMetadata,
        ...patch,
      },
    });
  };

  useEffect(() => {
    if (selectedSourceId && !sources.some((source) => source.id === selectedSourceId)) {
      setSelectedSourceId(null);
      onDetailViewChange("main");
    }
  }, [onDetailViewChange, selectedSourceId, sources]);

  useEffect(() => {
    if (selectedMediaResourceId && !mediaResources.some((resource) => resource.id === selectedMediaResourceId)) {
      setSelectedMediaResourceId(null);
      onDetailViewChange("main");
    }
  }, [mediaResources, onDetailViewChange, selectedMediaResourceId]);

  const startNew = () => {
    onCancelEdit();
    onFormChange({ ...EMPTY_KNOWLEDGE_FORM });
    setUploadedFiles([]);
    onDetailViewChange("editor");
  };

  const openSource = (source: KnowledgeSource) => {
    setSelectedSourceId(source.id);
    setUploadedFiles([]);
    onDetailViewChange("source");
  };

  const openSourceEditor = (source: KnowledgeSource) => {
    setSelectedSourceId(source.id);
    setUploadedFiles([]);
    onEdit(source);
    onDetailViewChange("editor");
  };

  const openMediaResource = (resource: MediaResourceDraft) => {
    setSelectedMediaResourceId(resource.id);
    onDetailViewChange("media");
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;
    let nextForm = form;

    try {
      for (const file of files) {
        const baseName = file.name.replace(/\.[^.]+$/, "");
        const extension = file.name.split(".").pop()?.toLowerCase() || "file";
        const textLike =
          file.type.startsWith("text/") || ["txt", "md", "markdown", "csv", "json", "html", "htm", "xml"].includes(extension);
        const documentLike = ["pdf", "docx"].includes(extension);

        setUploadedFiles((current) => [
          ...current,
          {
            id: `${file.name}-${file.size}-${Date.now()}-${Math.random().toString(36).slice(2)}`,
            name: file.name,
            type: extension.toUpperCase(),
            size: file.size,
          },
        ]);

        if (textLike) {
          const content = await file.text();
          nextForm = {
            ...nextForm,
            name: nextForm.name || baseName,
            content: nextForm.content ? `${nextForm.content}\n\n${content}` : content,
            source_type: extension,
            is_active: true,
          };
        } else if (documentLike) {
          const extraction = await chatbotV2Service.extractKnowledgeDocument(file);
          const content = String(extraction.text || "").trim();
          if (!content) {
            toast.error("No se pudo extraer texto del documento. Usa un PDF con texto seleccionable o un DOCX.");
            continue;
          }
          nextForm = {
            ...nextForm,
            name: nextForm.name || baseName,
            content: nextForm.content ? `${nextForm.content}\n\n${content}` : content,
            source_type: extension,
            is_active: true,
          };
        } else {
          nextForm = {
            ...nextForm,
            name: nextForm.name || baseName,
            source_type: extension,
            is_active: true,
          };
        }
      }
      onFormChange(nextForm);
      if (files.some((file) => {
        const extension = file.name.split(".").pop()?.toLowerCase() || "";
        return file.type.startsWith("text/") || ["txt", "md", "markdown", "csv", "json", "html", "htm", "xml", "pdf", "docx"].includes(extension);
      })) {
        toast.success(files.length === 1 ? "Archivo cargado" : "Archivos cargados");
      }
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      event.target.value = "";
    }
  };

  const handleSaveAndReturn = async () => {
    await Promise.resolve(onSave());
    onDetailViewChange("main");
  };

  const handleDeleteAndReturn = async (sourceId: number) => {
    await Promise.resolve(onDelete(sourceId));
    setSelectedSourceId(null);
    onDetailViewChange("main");
  };

  const mediaKindOptions: Array<{ value: MediaResourceDraft["kind"]; label: string }> = [
    { value: "image", label: "Imágenes" },
    { value: "video", label: "Video" },
    { value: "audio", label: "Audio" },
    { value: "document", label: "Documento" },
    { value: "sticker", label: "Sticker" },
    { value: "gif", label: "GIF" },
    { value: "mixed", label: "Mixto" },
  ];

  const mediaKindLabel = (kind: MediaResourceDraft["kind"]) =>
    mediaKindOptions.find((option) => option.value === kind)?.label || "Mixto";

  const updateMediaResource = (id: string, patch: Partial<MediaResourceDraft>) => {
    onMediaResourcesChange(mediaResources.map((resource) => (resource.id === id ? { ...resource, ...patch } : resource)));
  };

  const commitMediaResource = (id: string, patch: Partial<MediaResourceDraft>) => {
    let changed = false;
    const next = mediaResources.map((resource) => {
      if (resource.id !== id) return resource;
      const updated = { ...resource, ...patch };
      if (JSON.stringify(updated) === JSON.stringify(resource)) return resource;
      changed = true;
      return updated;
    });
    if (changed) onMediaResourcesChange(next);
  };

  const addMediaResource = () => {
    const id = `media-resource-${Date.now()}`;
    const next: MediaResourceDraft[] = [
      ...mediaResources,
      {
        id,
        title: "Nuevo recurso multimedia",
        kind: "mixed",
        summary: "",
        instructions: "",
        files: [],
        enabled: false,
      },
    ];
    onMediaResourcesChange(next);
    setSelectedMediaResourceId(id);
    onDetailViewChange("media");
  };

  const deleteMediaResource = (id: string) => {
    const next = mediaResources.filter((resource) => resource.id !== id);
    onMediaResourcesChange(next);
  };

  const setMediaResourceEnabled = (id: string, enabled: boolean) => {
    const resource = mediaResources.find((item) => item.id === id);
    if (!resource) return;
    if (enabled && !resource.summary.trim()) {
      toast.error("Agrega un resumen del recurso antes de activarlo.");
      return;
    }
    if (enabled && !resource.files.some((file) => mediaFileWhatsAppStatus(resource.kind, file).sendable)) {
      const firstReason = resource.files[0] ? mediaFileWhatsAppStatus(resource.kind, resource.files[0]).blockedReason : "missing_url_or_media_id";
      toast.error(`Sube al menos un archivo enviable por WhatsApp (${mediaBlockedReasonLabel(firstReason)}).`);
      return;
    }
    commitMediaResource(id, { enabled });
  };

  const addMediaFiles = async (id: string, event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;
    const resource = mediaResources.find((item) => item.id === id);
    if (!resource) return;
    setUploadingMediaResourceId(id);
    try {
      const uploaded: MediaResourceDraft["files"] = [];
      for (const file of files) {
        const extension = file.name.split(".").pop()?.toUpperCase() || file.type || "FILE";
        const base64File = await toBase64(file);
        const filename = `${Date.now()}-${Math.random().toString(36).slice(2)}-${sanitizeUploadFilename(file.name)}`;
        const response = await uploadImagesToS3({
          file: base64File,
          folder: `chatbot-v2/media-resources/${id}`,
          filename,
        });
        const url = typeof response?.s3_url === "string" ? response.s3_url : typeof response?.url === "string" ? response.url : "";
        if (!url) throw new Error("No se recibió URL del archivo subido.");
        const mediaType = inferWhatsAppMediaTypeFromFile(file);
        const uploadedFile: MediaResourceDraft["files"][number] = {
          id: `${file.name}-${file.size}-${Date.now()}-${Math.random().toString(36).slice(2)}`,
          name: file.name,
          type: extension,
          size: file.size,
          url,
          mime_type: file.type || undefined,
          media_type: mediaType,
        };
        const status = mediaFileWhatsAppStatus(resource.kind, uploadedFile);
        uploaded.push({
          ...uploadedFile,
          whatsapp_sendable: status.sendable,
          whatsapp_blocked_reason: status.blockedReason,
        });
      }
      commitMediaResource(id, { files: [...resource.files, ...uploaded] });
      toast.success(files.length === 1 ? "Archivo subido" : "Archivos subidos");
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setUploadingMediaResourceId(null);
      event.target.value = "";
    }
  };

  const removeMediaFile = (resourceId: string, fileId: string) => {
    const resource = mediaResources.find((item) => item.id === resourceId);
    if (!resource) return;
    commitMediaResource(resourceId, { files: resource.files.filter((file) => file.id !== fileId) });
  };

  const mediaResourceSummary = (resource: MediaResourceDraft) => {
    if (resource.summary.trim()) return resource.summary.trim();
    return "Resumen pendiente. Abre el recurso y explica qué contiene antes de activarlo.";
  };

  const mediaResourceThumb = (resource: MediaResourceDraft, variant: "large" | "compact" = "large") => {
    const firstFile = resource.files[0];
    const canPreviewImage = Boolean(firstFile?.url && (firstFile.media_type === "image" || ["image", "gif"].includes(resource.kind)));
    const icon =
      resource.kind === "video"
        ? PlayCircle
        : resource.kind === "audio"
          ? Mic
        : resource.kind === "document"
          ? FileText
          : resource.kind === "image" || resource.kind === "gif" || resource.kind === "sticker"
            ? ImageIcon
            : UploadCloud;
    const Icon = icon;
    if (variant === "compact") {
      return (
        <div className="flex size-16 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-gray-50 text-gray-500">
          {canPreviewImage ? (
            <img src={firstFile?.url} alt={firstFile?.name || resource.title} className="h-full w-full object-cover" />
          ) : (
            <Icon className="size-6" />
          )}
        </div>
      );
    }
    return (
      <div className="flex h-28 items-center justify-center overflow-hidden rounded-lg bg-gray-50 text-gray-500">
        {canPreviewImage ? (
          <img src={firstFile?.url} alt={firstFile?.name || resource.title} className="h-full w-full object-cover" />
        ) : (
          <div className="text-center">
            <Icon className="mx-auto size-7" />
            <p className="mt-2 max-w-[180px] truncate px-3 text-xs font-semibold text-gray-600">
              {firstFile?.name || mediaKindLabel(resource.kind)}
            </p>
            <p className="mt-0.5 text-[11px] text-gray-400">
              {resource.files.length ? `${resource.files.length} archivo${resource.files.length === 1 ? "" : "s"}` : "Sin archivos"}
            </p>
          </div>
        )}
      </div>
    );
  };

  const dynamicFollowupSettings = dynamicFollowupSettingsFromMetadata(
    isRecord(settingsForm.metadata_json) ? settingsForm.metadata_json : {}
  );
  const updateDynamicFollowupSettings = (patch: Partial<DynamicFollowupSettings>) => {
    const next: DynamicFollowupSettings = {
      ...dynamicFollowupSettings,
      ...patch,
      flows: {
        ...dynamicFollowupSettings.flows,
        ...(patch.flows || {}),
      },
    };
    updateSettingsMetadata({
      dynamic_followup_settings: dynamicFollowupSettingsToMetadata(next),
    });
  };

  const assistantExtraInstructionFields = [
    {
      key: "human_help_request_instructions",
      label: "Instrucciones para solicitar ayuda humana",
      value: humanHelpRequestInstructions,
      placeholder:
        "Ej. Solicita ayuda humana solo si el cliente la pide, si hay un reclamo serio o si no puedes resolver con la información disponible.",
      icon: UserRound,
      iconClass: "bg-emerald-50 text-emerald-600",
    },
    {
      key: "chat_note_usage_instructions",
      label: "Instrucciones sobre el uso de notas en el chat",
      value: chatNoteUsageInstructions,
      placeholder: "Ej. Guarda notas internas cuando haya acuerdos, contexto importante o pasos pendientes para el equipo.",
      icon: StickyNote,
      iconClass: "bg-sky-50 text-sky-600",
    },
    {
      key: "refund_policy_instructions",
      label: "Política de devoluciones",
      value: refundPolicyInstructions,
      placeholder: "Ej. No ofrezcas devoluciones a menos que haya una instrucción precisa de que sí tenemos devoluciones.",
      icon: RotateCcw,
      iconClass: "bg-violet-50 text-violet-600",
    },
    {
      key: "conversation_closing_instructions",
      label: "Instrucciones para cerrar conversaciones",
      value: closingInstructions,
      placeholder:
        "Ej. Cierra la conversación cuando el cliente confirme que pudo ingresar, cuando el pedido quede resuelto o cuando no haya una acción pendiente clara.",
      icon: CheckCircle2,
      iconClass: "bg-gray-100 text-gray-600",
    },
    {
      key: "priority_medium_instructions",
      label: "Instrucciones para calificar conversación como prioridad media",
      value: priorityMediumInstructions,
      placeholder: "Ej. Marca como media cuando el cliente espera una respuesta humana, hay una duda comercial importante o falta validar una orden.",
      icon: UserRound,
      iconClass: "bg-amber-50 text-amber-500",
    },
    {
      key: "priority_high_instructions",
      label: "Instrucciones para calificar conversación como prioridad alta",
      value: priorityHighInstructions,
      placeholder: "Ej. Marca como alta si hay pago en riesgo, cliente molesto, bloqueo de acceso crítico o posible pérdida de venta.",
      icon: UserRound,
      iconClass: "bg-red-50 text-red-500",
    },
    {
      key: "customer_state_support_active_instructions",
      settingsField: true,
      label: "Instrucciones para soporte activo",
      value: String(settingsForm.customer_state_support_active_instructions || ""),
      placeholder: "Ej. Marcar soporte activo cuando el cliente reporte un problema o pida ayuda post-compra.",
      icon: UserRound,
      iconClass: "bg-orange-50 text-orange-600",
    },
    {
      key: "customer_state_support_resolved_instructions",
      settingsField: true,
      label: "Instrucciones para soporte resuelto",
      value: String(settingsForm.customer_state_support_resolved_instructions || ""),
      placeholder: "Ej. Marcar soporte resuelto cuando el cliente confirme que la solución funcionó.",
      icon: CheckCircle2,
      iconClass: "bg-teal-50 text-teal-600",
    },
  ];

  if (detailView === "test") {
    return (
      <ScrollSurface>
        <div className="mx-auto max-w-5xl">
          <IntegrationBackButton label="Volver a Asistente IA" onBack={() => onDetailViewChange("main")} />
          <div className="mt-5 flex items-center justify-between gap-3">
            <div>
              <p className="text-sm font-semibold text-gray-950">Chat de prueba</p>
              <p className="mt-1 text-xs leading-5 text-gray-500">
                Usa las instrucciones, conocimiento, recursos multimedia y acciones disponibles sin enviar WhatsApp real.
              </p>
            </div>
            <button
              type="button"
              onClick={onResetTest}
              disabled={testLoading || testMessages.length === 0}
              className="inline-flex h-9 shrink-0 items-center gap-2 rounded-full bg-gray-100 px-3 text-sm font-semibold text-gray-700 transition hover:bg-gray-200 disabled:opacity-50"
            >
              <RotateCcw className="size-4" />
              Reiniciar
            </button>
          </div>
          <div className="mt-5 overflow-hidden rounded-[22px] border border-gray-200 bg-white">
            <div className="flex flex-wrap items-end justify-between gap-3 border-b border-gray-100 bg-white px-4 py-3">
              <div className="w-full max-w-sm">
                <TextField
                  label="Cliente de prueba"
                  value={testContact}
                  onChange={onTestContactChange}
                  placeholder="Ana Cliente"
                />
              </div>
              <div className="flex flex-wrap items-center gap-2 pb-1">
                <StatusPill label={testResponse?.usage?.model || "Modelo real"} tone="neutral" />
                {testResponse?.action ? <StatusPill label={`Acción: ${testResponse.action}`} tone="warning" /> : null}
                {testResponse?.tool_name ? <StatusPill label={`Herramienta: ${testResponse.tool_name}`} tone="success" /> : null}
                {testResponse?.degraded_mode ? <StatusPill label="Modo degradado" tone="warning" /> : null}
              </div>
            </div>
            <div className="flex min-h-[460px] flex-col" style={CHAT_THREAD_BACKGROUND_STYLE}>
              <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5">
                {testMessages.length === 0 ? (
                  <div className="mx-auto mt-16 max-w-sm rounded-2xl bg-white/90 px-4 py-3 text-center text-sm text-gray-500 shadow-[0px_1px_4px_0px_rgba(99,99,99,0.16)]">
                    Escribe como cliente para probar la respuesta real del asistente.
                  </div>
                ) : (
                  <div className="mx-auto flex max-w-3xl flex-col gap-3">
                    {testMessages.map((message) => {
                      const outbound = message.sender === "assistant";
                      return (
                        <div key={message.id} className={`flex ${outbound ? "justify-end" : "justify-start"}`}>
                          <div
                            className={`max-w-[min(560px,82%)] rounded-xl px-4 py-3 text-sm leading-relaxed shadow-[0px_1px_4px_0px_rgba(99,99,99,0.18)] ${
                              message.failed
                                ? "border border-red-200 bg-red-50 text-red-900"
                                : outbound
                                  ? "bg-[#E8EEFF] text-gray-900"
                                  : "bg-white text-gray-900"
                            }`}
                          >
                            {message.pending ? (
                              <TypingDots label="La IA está pensando" />
                            ) : (
                              <>
                                <p className="whitespace-pre-wrap">{message.content}</p>
                                {message.failed && message.retryContent ? (
                                  <button
                                    type="button"
                                    onClick={() => onRunTest(message.retryContent, { retryExistingMessage: true })}
                                    disabled={testLoading}
                                    className="mt-3 inline-flex h-8 items-center gap-1.5 rounded-full border border-red-200 bg-white px-3 text-xs font-semibold text-red-700 transition hover:bg-red-100 disabled:opacity-50"
                                  >
                                    {testLoading ? <Loader2 className="size-3.5 animate-spin" /> : <RotateCcw className="size-3.5" />}
                                    Reintentar
                                  </button>
                                ) : null}
                              </>
                            )}
                            {!message.pending ? (
                              <div className="mt-2 flex items-center justify-end gap-2 text-[11px] text-gray-500">
                                <span>{outbound ? "IA" : "Cliente"}</span>
                                <span aria-hidden="true">•</span>
                                <span>{formatMessageTime(message.time) || "Ahora"}</span>
                                {message.usageModel ? (
                                  <>
                                    <span aria-hidden="true">•</span>
                                    <span>{message.usageModel}</span>
                                  </>
                                ) : null}
                              </div>
                            ) : null}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
              <div className="px-5 pb-5">
                <div className="mx-auto flex max-w-3xl items-end gap-2 rounded-[24px] border border-gray-200 bg-white p-2 shadow-[0px_2px_8px_0px_rgba(99,99,99,0.2)]">
                  <textarea
                    value={testMessage}
                    onChange={(event) => onTestMessageChange(event.target.value)}
                    onKeyDown={(event) => submitTextareaOnEnter(event, onRunTest)}
                    disabled={testLoading}
                    rows={3}
                    placeholder="Escribe una pregunta real que debería usar este conocimiento."
                    className="max-h-40 min-h-[76px] flex-1 resize-none overflow-y-auto rounded-[18px] border-0 bg-transparent px-3 py-2 text-sm leading-5 outline-none placeholder:text-gray-400"
                  />
                  <button
                    type="button"
                    onClick={() => onRunTest()}
                    disabled={testLoading || !testMessage.trim()}
                    className="inline-flex size-10 shrink-0 items-center justify-center rounded-full bg-gray-950 text-white transition hover:bg-gray-800 disabled:cursor-not-allowed disabled:bg-gray-200 disabled:text-gray-400"
                    title="Enviar prueba"
                  >
                    {testLoading ? <Loader2 className="size-4 animate-spin" /> : <Send className="size-4" />}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </ScrollSurface>
    );
  }

  if (detailView === "editor") {
    return (
      <ScrollSurface>
        <div className="mx-auto max-w-5xl">
          <IntegrationBackButton
            label="Volver a Asistente IA"
            onBack={() => {
              onDetailViewChange("main");
              onCancelEdit();
            }}
          />
          <div className="mt-5 grid gap-6 lg:grid-cols-[minmax(0,3fr)_minmax(220px,1fr)]">
            <section className="space-y-5">
              <div className="grid gap-3 md:grid-cols-[minmax(0,2fr)_minmax(180px,1fr)] md:items-end">
                <TextField
                  label="Título"
                  value={form.name}
                  onChange={(value) => onFormChange({ ...form, name: value })}
                />
                <div>
                  <span className="mb-1 block text-xs font-semibold text-gray-600">Estado</span>
                  <ToggleSwitch
                    checked={form.is_active}
                    label={form.is_active ? "Activa" : "Pausada"}
                    onChange={(is_active) => onFormChange({ ...form, is_active })}
                  />
                </div>
              </div>
              <TextareaField
                label="Contenido"
                value={form.content}
                onChange={(value) => onFormChange({ ...form, content: value, source_type: form.source_type || "manual" })}
                rows={14}
                placeholder="Pega aquí políticas, pasos internos, preguntas frecuentes, links, horarios o cualquier contexto que el asistente de IA deba conocer."
              />
            </section>
            <aside className="space-y-3">
              <label className="flex min-h-[170px] cursor-pointer flex-col items-center justify-center rounded-xl border border-dashed border-gray-300 bg-gray-50 px-4 py-5 text-center transition hover:bg-gray-100">
                <UploadCloud className="size-7 text-gray-500" />
                <span className="mt-3 text-sm font-semibold text-gray-900">Subir archivo</span>
                <span className="mt-1 text-xs leading-5 text-gray-500">PDF, DOC, TXT o MD.</span>
                <input
                  type="file"
                  multiple
                  accept=".txt,.md,.markdown,.pdf,.doc,.docx,.csv,.json"
                  onChange={(event) => void handleFileSelect(event)}
                  className="sr-only"
                />
              </label>
              {uploadedFiles.length ? (
                <div className="space-y-2">
                  {uploadedFiles.map((file) => (
                    <div key={file.id} className="flex items-center justify-between gap-2 rounded-lg bg-gray-50 px-3 py-2">
                      <div className="min-w-0">
                        <p className="truncate text-xs font-semibold text-gray-900">{file.name}</p>
                        <p className="mt-0.5 text-[11px] text-gray-400">
                          {file.type} · {Math.max(1, Math.round(file.size / 1024))} KB
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setUploadedFiles((current) => current.filter((item) => item.id !== file.id))}
                        className="inline-flex size-7 shrink-0 items-center justify-center rounded-full text-gray-500 hover:bg-gray-200 hover:text-gray-900"
                        title="Quitar archivo"
                      >
                        <X className="size-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              ) : null}
            </aside>
          </div>
          <div className="mt-6 flex flex-wrap justify-end gap-2">
            {editingSourceId ? (
              <button
                onClick={() => void handleDeleteAndReturn(editingSourceId)}
                className="inline-flex h-10 items-center justify-center rounded-full bg-red-50 px-5 text-sm font-semibold text-red-700 transition hover:bg-red-100"
              >
                Eliminar conocimiento
              </button>
            ) : null}
            <button
              onClick={handleSaveAndReturn}
              disabled={saving || !form.name.trim() || !form.content.trim()}
              className="inline-flex h-10 items-center justify-center rounded-full bg-gray-950 px-5 text-sm font-semibold text-white transition hover:bg-gray-800 disabled:opacity-50"
            >
              {saving ? <Loader2 className="mr-2 size-4 animate-spin" /> : null}
              Guardar fuente
            </button>
          </div>
        </div>
      </ScrollSurface>
    );
  }

  if (detailView === "source" && selectedSource && selectedSource.source_type === "learning_log") {
    return (
      <LearningLogDetail
        source={selectedSource}
        saving={saving}
        onBack={() => onDetailViewChange("main")}
        onToggle={(value) => toggleKnowledgeSource(selectedSource, value)}
        onSave={(entries) => onSaveLearningEntries(selectedSource.id, entries)}
      />
    );
  }

  if (detailView === "source" && selectedSource) {
    return (
      <ScrollSurface>
        <div className="mx-auto max-w-5xl">
          <IntegrationBackButton label="Volver a Asistente IA" onBack={() => onDetailViewChange("main")} />
          <SettingsPageHeader
            icon={FileText}
            title={selectedSource.name}
            description={`${resolveKnowledgeCharCount(selectedSource).toLocaleString("es-CO")} caracteres · ${knowledgeSourceAuthorLabel(selectedSource)} · Actualizada ${formatDate(selectedSource.updated_at)}`}
            action={
                      <ToggleSwitch
                        checked={selectedSource.is_active}
                        label={selectedSource.is_active ? "Activa" : "Pausada"}
                        onChange={(value) => toggleKnowledgeSource(selectedSource, value)}
                      />
            }
          />
          <section className="mt-6 rounded-xl bg-gray-50 p-5">
            <p className="whitespace-pre-wrap text-sm leading-7 text-gray-800">{selectedSource.content}</p>
          </section>
          <div className="mt-5 flex justify-end gap-2">
            <button
              onClick={() => {
                setUploadedFiles([]);
                onEdit(selectedSource);
                onDetailViewChange("editor");
              }}
              className="inline-flex h-9 items-center justify-center rounded-full bg-gray-100 px-4 text-sm font-semibold text-gray-800 transition hover:bg-gray-200"
            >
              Editar
            </button>
            <button
              onClick={() => void handleDeleteAndReturn(selectedSource.id)}
              className="inline-flex h-9 items-center justify-center rounded-full bg-red-50 px-4 text-sm font-semibold text-red-700 transition hover:bg-red-100"
            >
              Eliminar
            </button>
          </div>
        </div>
      </ScrollSurface>
    );
  }

  if (detailView === "media" && selectedMediaResource) {
    return (
      <ScrollSurface>
        <div className="mx-auto max-w-5xl">
          <IntegrationBackButton label="Volver a Asistente IA" onBack={() => onDetailViewChange("main")} />

          <div className="mt-5 grid gap-6 lg:grid-cols-[minmax(0,3fr)_minmax(240px,1fr)]">
            <section className="space-y-5">
              <div className="grid gap-3 md:grid-cols-[minmax(0,2fr)_minmax(170px,1fr)_minmax(150px,1fr)] md:items-end">
                <label className="block">
                  <span className="mb-1 block text-xs font-semibold text-gray-600">Nombre del recurso</span>
                  <input
                    value={selectedMediaResource.title}
                    onChange={(event) => updateMediaResource(selectedMediaResource.id, { title: event.target.value })}
                    onBlur={(event) =>
                      commitMediaResource(selectedMediaResource.id, {
                        title: event.target.value.trim() || "Recurso multimedia",
                      })
                    }
                    className="h-10 w-full rounded-lg border border-gray-200 bg-white px-3 text-sm font-semibold text-gray-950 outline-none focus:border-[#4368E5]"
                  />
                </label>
                <SelectField
                  label="Tipo"
                  value={selectedMediaResource.kind}
                  options={mediaKindOptions.map((option) => option.value)}
                  formatOption={(value) => mediaKindLabel(value as MediaResourceDraft["kind"])}
                  onChange={(value) =>
                    commitMediaResource(selectedMediaResource.id, {
                      kind: value as MediaResourceDraft["kind"],
                    })
                  }
                />
                <div>
                  <span className="mb-1 block text-xs font-semibold text-gray-600">Estado</span>
                          <ToggleSwitch
                            checked={selectedMediaResource.enabled}
                            label={selectedMediaResource.enabled ? "Activo" : "Pausado"}
                            onChange={(enabled) => setMediaResourceEnabled(selectedMediaResource.id, enabled)}
                          />
                        </div>
                      </div>
                      <label className="block">
                        <span className="mb-1 block text-xs font-semibold text-gray-600">Resumen obligatorio del recurso</span>
                        <textarea
                          value={selectedMediaResource.summary}
                          onChange={(event) => updateMediaResource(selectedMediaResource.id, { summary: event.target.value })}
                          onBlur={(event) => commitMediaResource(selectedMediaResource.id, { summary: event.target.value.trim() })}
                          rows={3}
                          placeholder="Explica qué contiene este recurso para que la IA sepa cuándo tendría sentido enviarlo."
                          className="w-full resize-y rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm leading-6 text-gray-800 outline-none focus:border-[#4368E5]"
                        />
                        {!selectedMediaResource.summary.trim() && selectedMediaResource.enabled ? (
                          <span className="mt-1 block text-xs text-amber-600">
                            El recurso se pausará al guardar hasta que tenga resumen.
                          </span>
                        ) : null}
                      </label>
                      <label className="block">
                        <span className="mb-1 block text-xs font-semibold text-gray-600">Instrucciones para la IA</span>
                        <textarea
                  value={selectedMediaResource.instructions}
                  onChange={(event) => updateMediaResource(selectedMediaResource.id, { instructions: event.target.value })}
                  onBlur={(event) => commitMediaResource(selectedMediaResource.id, { instructions: event.target.value.trim() })}
                  rows={8}
                  placeholder="Cuándo usar este recurso, con qué tipo de mensaje enviarlo y cuándo evitarlo."
                  className="w-full resize-y rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm leading-6 text-gray-800 outline-none focus:border-[#4368E5]"
                />
              </label>
            </section>

            <aside className="space-y-3">
              {mediaResourceThumb(selectedMediaResource)}
              <label
                className={`flex min-h-[150px] flex-col items-center justify-center rounded-xl border border-dashed border-gray-300 bg-gray-50 px-4 py-5 text-center transition ${
                  uploadingMediaResourceId === selectedMediaResource.id ? "cursor-wait opacity-70" : "cursor-pointer hover:bg-gray-100"
                }`}
              >
                {uploadingMediaResourceId === selectedMediaResource.id ? (
                  <Loader2 className="size-7 animate-spin text-gray-500" />
                ) : (
                  <UploadCloud className="size-7 text-gray-500" />
                )}
                <span className="mt-3 text-sm font-semibold text-gray-900">
                  {uploadingMediaResourceId === selectedMediaResource.id ? "Subiendo..." : "Subir archivos"}
                </span>
                <span className="mt-1 text-xs leading-5 text-gray-500">Imágenes JPG/PNG, stickers WebP, videos, audios, PDFs o documentos.</span>
                <input
                  type="file"
                  multiple
                  accept={WHATSAPP_MEDIA_RESOURCE_ACCEPT}
                  disabled={uploadingMediaResourceId === selectedMediaResource.id}
                  onChange={(event) => void addMediaFiles(selectedMediaResource.id, event)}
                  className="sr-only"
                />
              </label>
              {selectedMediaResource.files.length ? (
                <div className="space-y-2">
                  {selectedMediaResource.files.map((file) => {
                    const status = mediaFileWhatsAppStatus(selectedMediaResource.kind, file);
                    return (
                      <div key={file.id} className="flex items-center justify-between gap-2 rounded-lg bg-gray-50 px-3 py-2">
                        <div className="min-w-0">
                          <p className="truncate text-xs font-semibold text-gray-900">{file.name}</p>
                          <p className="mt-0.5 text-[11px] text-gray-400">
                            {file.type} · {file.size ? `${Math.max(1, Math.round(file.size / 1024))} KB` : "pendiente"}
                          </p>
                          <p className={`mt-1 text-[11px] font-semibold ${status.sendable ? "text-emerald-600" : "text-amber-600"}`}>
                            {status.sendable ? `Enviable como ${mediaKindLabel(status.mediaType as MediaResourceDraft["kind"]).toLowerCase()}` : mediaBlockedReasonLabel(status.blockedReason)}
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeMediaFile(selectedMediaResource.id, file.id)}
                          className="inline-flex size-7 shrink-0 items-center justify-center rounded-full text-gray-500 hover:bg-gray-200 hover:text-gray-900"
                          title="Quitar archivo"
                        >
                          <X className="size-3.5" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              ) : null}
            </aside>
          </div>

          <div className="mt-6 flex justify-end">
            <button
              type="button"
              onClick={() => {
                deleteMediaResource(selectedMediaResource.id);
                setSelectedMediaResourceId(null);
                onDetailViewChange("main");
              }}
              className="inline-flex h-9 items-center justify-center rounded-full bg-red-50 px-4 text-sm font-semibold text-red-700 transition hover:bg-red-100"
            >
              Eliminar recurso
            </button>
          </div>
        </div>
      </ScrollSurface>
    );
  }

  return (
    <ScrollSurface>
      <div className="mx-auto max-w-5xl">
        <section>
          <div className="grid gap-4 md:grid-cols-2">
            <TextField
              label="Nombre del negocio"
              value={String(settingsForm.business_name || "")}
              onChange={(value) => onSettingsFormChange({ ...settingsForm, business_name: value })}
              placeholder="Ej. Academia SpaceC"
            />
            <TextField
              label="Nombre del asistente"
              value={String(settingsForm.bot_name || "")}
              onChange={(value) => onSettingsFormChange({ ...settingsForm, bot_name: value })}
              placeholder="Asistente IA"
            />
          </div>
          <div className="mt-5">
            <TextareaField
              label="Instrucciones generales para el asistente de IA"
              value={String(settingsForm.custom_instructions ?? "")}
              onChange={(value) => onSettingsFormChange({ ...settingsForm, custom_instructions: value })}
              rows={4}
              placeholder="Ej. Si el cliente pregunta por acceso a cursos, pide el email de compra y revisa Hotmart antes de escalar."
            />
          </div>

          <div className="mt-6 space-y-4">
            <div className="rounded-xl bg-gray-50 p-4">
              <div className="flex items-center gap-4">
                <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-[#EEF3FF] text-[#3151C9]">
                  <Brain className="size-4" />
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="text-sm font-semibold text-gray-950">Activar IA en conversaciones nuevas</h3>
                  <p className="mt-1 text-xs leading-5 text-gray-500">
                    Si está apagada, las conversaciones nuevas quedan para atención manual.
                  </p>
                </div>
                <div className="flex shrink-0 items-center gap-2 self-center">
                  <ToggleSwitch
                    checked={Boolean(settingsForm.ai_enabled)}
                    label={settingsForm.ai_enabled ? "Activa" : "Pausada"}
                    accent="blue"
                    onChange={(value) =>
                      onSettingsFormChange({
                        ...settingsForm,
                        ai_enabled: value,
                        status: value ? "active" : "paused",
                      })
                    }
                  />
                  {/* Placeholder invisible del ancho del chevron de la card 3: los 3 toggles quedan alineados. */}
                  <span aria-hidden="true" className="inline-flex size-7 shrink-0" />
                </div>
              </div>
            </div>
            <div className="rounded-xl bg-gray-50 p-4">
              <div className="flex items-center gap-4">
                <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-[#EEF3FF] text-[#3151C9]">
                  <Mic className="size-4" />
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="text-sm font-semibold text-gray-950">Responder notas de voz con voz</h3>
                  <p className="mt-1 text-xs leading-5 text-gray-500">
                    Responde con una nota de voz; si falla, responde en texto.
                  </p>
                </div>
                <div className="flex shrink-0 items-center gap-2 self-center">
                  <ToggleSwitch
                    checked={Boolean(settingsForm.voice_replies_enabled)}
                    label={settingsForm.voice_replies_enabled ? "Activa" : "Apagada"}
                    accent="blue"
                    onChange={(value) =>
                      onSettingsFormChange({
                        ...settingsForm,
                        voice_replies_enabled: value,
                      })
                    }
                  />
                  <span aria-hidden="true" className="inline-flex size-7 shrink-0" />
                </div>
              </div>
            </div>
            <div className="rounded-xl bg-gray-50 p-4">
              <div className="flex items-center gap-4">
                <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-[#EEF3FF] text-[#3151C9]">
                  <GraduationCap className="size-4" />
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="text-sm font-semibold text-gray-950">Modo aprendiz</h3>
                  <p className="mt-1 text-xs leading-5 text-gray-500">
                    Apruebas cada respuesta de la IA antes de que se envíe. Sus correcciones se convierten en sugerencias de aprendizaje.
                  </p>
                </div>
                <div className="flex shrink-0 items-center gap-2 self-center">
                  <ToggleSwitch
                    checked={Boolean(settingsForm.learning_mode_enabled)}
                    label={settingsForm.learning_mode_enabled ? "Activo" : "Apagado"}
                    accent="blue"
                    onChange={(value) =>
                      onSettingsFormChange({
                        ...settingsForm,
                        learning_mode_enabled: value,
                      })
                    }
                  />
                  <span aria-hidden="true" className="inline-flex size-7 shrink-0" />
                </div>
              </div>
            </div>

            {/* Card ÚNICA de seguimientos (fusión Julian): dinámicos por inactividad, con
              tiempo y flujos (venta/confirmación/soporte). Vive en el nivel principal; el
              contacto futuro pedido por el cliente quedó siempre-on en el backend. */}
                <div className="rounded-xl bg-gray-50 p-4">
                  <div className="flex items-center gap-4">
                    <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-[#EEF3FF] text-[#3151C9]">
                      <CalendarClock className="size-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <h3 className="text-sm font-semibold text-gray-950">
                        Permitir seguimientos dinámicos por inactividad
                      </h3>
                      <p className="mt-1 text-xs leading-5 text-gray-500">
                        Si el cliente deja de responder y el último turno fue positivo, la IA puede enviar un mensaje amable dentro de 7am-7pm.
                      </p>
                    </div>
                    <div className="flex shrink-0 items-center gap-2 self-center">
                      <ToggleSwitch
                        checked={dynamicFollowupSettings.enabled}
                        label={dynamicFollowupSettings.enabled ? "Activo" : "Apagado"}
                        accent="blue"
                        onChange={(enabled) => updateDynamicFollowupSettings({ enabled })}
                      />
                      <button
                        type="button"
                        onClick={() => setFollowupConfigOpen((open) => !open)}
                        className="inline-flex items-center rounded-full p-1.5 text-gray-500 transition hover:bg-gray-100 hover:text-gray-950"
                        aria-expanded={followupConfigOpen}
                        title="Configurar seguimientos"
                      >
                        <ChevronDown className={`size-4 transition ${followupConfigOpen ? "rotate-180" : ""}`} />
                      </button>
                    </div>
                  </div>
                  {followupConfigOpen ? (
                    <div className="mt-4 flex flex-wrap items-start gap-x-10 gap-y-3 border-t border-gray-200 pt-4">
                      <div>
                        <span className="mb-1 block text-xs font-semibold text-gray-600">Esperar</span>
                        <div className="flex items-center gap-2">
                          <input
                            type="number"
                            min={dynamicFollowupSettings.delayMinutes !== null ? 2 : 1}
                            max={dynamicFollowupSettings.delayMinutes !== null ? 360 : 6}
                            value={
                              dynamicFollowupSettings.delayMinutes !== null
                                ? dynamicFollowupSettings.delayMinutes
                                : dynamicFollowupSettings.delayHours
                            }
                            onChange={(event) => {
                              const nextValue = Number(event.target.value);
                              if (dynamicFollowupSettings.delayMinutes !== null) {
                                updateDynamicFollowupSettings({ delayMinutes: nextValue });
                              } else {
                                updateDynamicFollowupSettings({ delayHours: nextValue });
                              }
                            }}
                            className="h-10 w-20 rounded-lg border border-gray-200 px-3 text-sm"
                          />
                          <select
                            value={dynamicFollowupSettings.delayMinutes !== null ? "minutes" : "hours"}
                            onChange={(event) => {
                              if (event.target.value === "minutes") {
                                updateDynamicFollowupSettings({
                                  delayMinutes: Math.min(360, Math.max(2, dynamicFollowupSettings.delayHours * 60)),
                                });
                              } else {
                                updateDynamicFollowupSettings({ delayMinutes: null });
                              }
                            }}
                            className="h-10 rounded-lg border border-gray-200 px-2 text-sm"
                          >
                            <option value="hours">horas</option>
                            <option value="minutes">minutos</option>
                          </select>
                        </div>
                      </div>
                      <div>
                        <span className="mb-1 block text-xs font-semibold text-gray-600">Aplicar en</span>
                        <div className="flex flex-wrap gap-2">
                          {([
                            ["sale", "Venta"],
                            ["confirmation", "Confirmación"],
                            ["support", "Soporte"],
                          ] as const).map(([key, label]) => {
                            const checked = dynamicFollowupSettings.flows[key];
                            return (
                              <button
                                key={key}
                                type="button"
                                onClick={() =>
                                  updateDynamicFollowupSettings({
                                    flows: { ...dynamicFollowupSettings.flows, [key]: !checked },
                                  })
                                }
                                className={`inline-flex h-10 items-center gap-2 rounded-full border px-3 text-sm font-semibold transition ${
                                  checked
                                    ? "border-[#C8D7FF] bg-[#EEF3FF] text-[#3151C9]"
                                    : "border-gray-200 bg-white text-gray-600 hover:bg-gray-50"
                                }`}
                              >
                                {checked ? <CheckCircle2 className="size-4" /> : <span className="size-4 rounded-full border border-gray-300" />}
                                {label}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  ) : null}
                </div>
          </div>

          <div className="mt-2 flex justify-end">
            <button
              type="button"
              onClick={() => setMoreInstructionsOpen((open) => !open)}
              className="inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs font-semibold text-gray-500 transition hover:bg-gray-50 hover:text-gray-950"
            >
              Más instrucciones
              <ChevronDown className={`size-3.5 transition ${moreInstructionsOpen ? "rotate-180" : ""}`} />
            </button>
          </div>
          {moreInstructionsOpen ? (
            <div className="mt-3 grid gap-3">
              {assistantExtraInstructionFields.map((field) => {
                const Icon = field.icon;
                return (
                  <label key={field.key} className="block rounded-xl bg-gray-50 p-3">
                    <span className="mb-2 flex items-center gap-2 text-xs font-semibold text-gray-700">
                      <span className={`inline-flex size-7 items-center justify-center rounded-full ${field.iconClass}`}>
                        <Icon className="size-3.5" />
                      </span>
                      {field.label}
                    </span>
                    <textarea
                      value={field.value}
                      onChange={(event) =>
                        "settingsField" in field && field.settingsField
                          ? onSettingsFormChange({ ...settingsForm, [field.key]: event.target.value })
                          : updateSettingsMetadata({ [field.key]: event.target.value })
                      }
                      rows={3}
                      placeholder={field.placeholder}
                      className="w-full resize-y rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm leading-6 text-gray-800 outline-none transition focus:border-[#4368E5]"
                    />
                  </label>
                );
              })}
              <div className="rounded-xl bg-gray-50 p-3">
                <div className="flex items-center gap-4">
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-semibold text-gray-800">
                      Dividir la respuesta en varios mensajes y esperar tiempo de escritura como humano
                    </p>
                    <p className="mt-1 text-xs leading-5 text-gray-500">
                      Al apagarlo, la IA responde cada mensaje de inmediato en una sola burbuja para pruebas rápidas.
                    </p>
                  </div>
                  <div className="flex shrink-0 self-center">
                    <ToggleSwitch
                      checked={settingsForm.humanized_replies_enabled !== false}
                      label={settingsForm.humanized_replies_enabled !== false ? "Activa" : "Apagada"}
                      accent="blue"
                      onChange={(value) =>
                        onSettingsFormChange({
                          ...settingsForm,
                          humanized_replies_enabled: value,
                        })
                      }
                    />
                  </div>
                </div>
              </div>
            </div>
          ) : null}
        </section>

        <section className="mt-8">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <div className="flex flex-wrap items-baseline gap-2">
                        <h3 className="text-base font-semibold text-gray-950">Fuentes de conocimiento</h3>
                        <span className="text-xs font-medium text-gray-500">
                          {formatKnowledgeChars(activeKnowledgeChars)} / {formatKnowledgeChars(KNOWLEDGE_ACTIVE_CHAR_LIMIT)} caracteres
                        </span>
                      </div>
                      <p className="mt-0.5 text-xs leading-5 text-gray-500">
                        Agrega documentos o textos específicos. Toca una fuente para ver su contenido completo.
                      </p>
                    </div>
                    <button
                      onClick={startNew}
              className="inline-flex h-9 items-center justify-center rounded-full bg-gray-100 px-4 text-sm font-semibold text-gray-800 transition hover:bg-gray-200"
            >
                      + Agregar fuente
                    </button>
                  </div>
                  <div className="mt-3 hidden rounded-xl bg-gray-50 px-4 py-3">
                    <div className="flex flex-wrap items-center justify-between gap-2 text-xs">
                      <span className="font-semibold text-gray-700">Conocimiento activo</span>
                      <span className="font-medium text-gray-500">
                        {formatKnowledgeChars(activeKnowledgeChars)} / {formatKnowledgeChars(KNOWLEDGE_ACTIVE_CHAR_LIMIT)} caracteres
                      </span>
                    </div>
                    <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-white">
                      <div
                        className={`h-full rounded-full transition-all ${
                          activeKnowledgePercent >= 90 ? "bg-red-300" : activeKnowledgePercent >= 75 ? "bg-amber-300" : "bg-gray-400"
                        }`}
                        style={{ width: `${activeKnowledgePercent}%` }}
                      />
                    </div>
                    <p className="mt-2 text-xs leading-5 text-gray-500">
                      Mantén el conocimiento activo dentro de este espacio para que la IA responda rápido y con contexto claro.
                    </p>
                  </div>

                  <div className="mt-4 grid gap-3 md:grid-cols-2">
            {sources.map((source) => (
              <article
                key={source.id}
                role="button"
                tabIndex={0}
                onClick={() => openSource(source)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") openSource(source);
                }}
                className="cursor-pointer rounded-xl border border-gray-200 bg-white p-4 transition hover:bg-gray-50"
              >
                <div className="mb-3" onClick={(event) => event.stopPropagation()}>
                          <ToggleSwitch
                            checked={source.is_active}
                            label={source.is_active ? "Activa" : "Pausada"}
                            onChange={(value) => toggleKnowledgeSource(source, value)}
                          />
                </div>
                <h4 className="line-clamp-2 text-sm font-semibold leading-5 text-gray-950">
                  {formatKnowledgeSourceDisplayName(source.name)}
                </h4>
                {knowledgeSourceTechnicalName(source.name) ? (
                  <p className="mt-0.5 line-clamp-1 text-[11px] text-gray-400">{knowledgeSourceTechnicalName(source.name)}</p>
                ) : null}
                {source.source_type === "learning_log" ? (
                  <>
                    <p className="mt-2 line-clamp-2 text-xs leading-5 text-gray-600">«{learningLogLatestText(source)}»</p>
                    <p className="mt-1 text-[11px] text-gray-400">
                      {(source.entries?.length ?? 0)} aprendizaje{(source.entries?.length ?? 0) === 1 ? "" : "s"}
                    </p>
                  </>
                ) : (
                  <p className="mt-2 line-clamp-4 text-xs leading-5 text-gray-600">{source.content}</p>
                )}
              </article>
            ))}
            {loading ? (
              <div className="col-span-full flex items-center justify-center rounded-xl border border-dashed border-gray-200 bg-gray-50 px-4 py-10 text-sm text-gray-500">
                <Loader2 className="mr-2 size-4 animate-spin" />
                Cargando fuentes de conocimiento...
              </div>
            ) : null}
            {!loading && loadedOnce && sources.length === 0 ? (
              <EmptyState icon={BrainCircuit} title="Sin conocimiento" body="Agrega la primera fuente para que el asistente tenga contexto del negocio." />
            ) : null}
          </div>
        </section>

        <section className="mt-8 border-t border-gray-100 pt-7">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h3 className="text-base font-semibold text-gray-950">Recursos multimedia</h3>
              <p className="mt-0.5 max-w-2xl text-xs leading-5 text-gray-500">
                Imágenes, stickers, videos, audios y documentos que el asistente de IA puede enviar cuando el contexto lo pida.
              </p>
            </div>
            <button
              type="button"
              onClick={addMediaResource}
              className="inline-flex h-9 items-center justify-center rounded-full bg-gray-100 px-4 text-sm font-semibold text-gray-800 transition hover:bg-gray-200"
            >
              + Agregar recurso
            </button>
          </div>

          <div className="mt-4 grid gap-3 md:grid-cols-2">
            {mediaResources.map((resource) => (
              <article
                key={resource.id}
                role="button"
                tabIndex={0}
                onClick={() => openMediaResource(resource)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") openMediaResource(resource);
                }}
                className="cursor-pointer rounded-xl border border-gray-200 bg-white p-4 transition hover:bg-gray-50"
              >
                <div className="mb-3" onClick={(event) => event.stopPropagation()}>
                          <ToggleSwitch
                            checked={resource.enabled}
                            label={resource.enabled ? "Activo" : "Pausado"}
                            onChange={(enabled) => setMediaResourceEnabled(resource.id, enabled)}
                          />
                </div>
                <div>
                  <div className="flex items-start gap-3">
                    {mediaResourceThumb(resource, "compact")}
                    <div className="min-w-0 flex-1">
                    <p className="text-xs text-gray-400">
                      {mediaKindLabel(resource.kind)} · {resource.files.length} archivo{resource.files.length === 1 ? "" : "s"}
                    </p>
                    <h4 className="mt-1 line-clamp-2 text-sm font-semibold leading-5 text-gray-950">{resource.title}</h4>
                    </div>
                  </div>
                  <p className="mt-3 text-sm font-semibold leading-none text-gray-400">...</p>
                  {/* Mismo tamaño pequeño que la descripción de fuentes de conocimiento (Julian). */}
                  <p className="mt-2 line-clamp-3 text-xs leading-5 text-gray-600">{mediaResourceSummary(resource)}</p>
                </div>
              </article>
            ))}
            {mediaResources.length === 0 ? (
              <EmptyState icon={UploadCloud} title="Sin recursos multimedia" body="Agrega el primer recurso para que la IA pueda usarlo cuando el contexto lo pida." />
            ) : null}
          </div>
        </section>
      </div>
    </ScrollSurface>
  );
};

type LearningEntryDraft = LearningEntry & { key: string };

const learningEntriesToDrafts = (entries?: LearningEntry[]): LearningEntryDraft[] =>
  learningEntriesSortedDesc(entries).map((entry) => ({
    ...entry,
    key: entry.id ? `id-${entry.id}` : `new-${Math.random().toString(36).slice(2)}`,
  }));

// Vista detalle de la base "Sugerencias aprobadas" (learning_log): pila de cajitas editables.
const LearningLogDetail = ({
  source,
  saving,
  onBack,
  onToggle,
  onSave,
}: {
  source: KnowledgeSource;
  saving: boolean;
  onBack: () => void;
  onToggle: (isActive: boolean) => void;
  onSave: (entries: LearningEntry[]) => Promise<boolean> | boolean;
}) => {
  const [entries, setEntries] = useState<LearningEntryDraft[]>(() => learningEntriesToDrafts(source.entries));
  const [search, setSearch] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const sourceEntriesRef = useRef(source.entries);
  sourceEntriesRef.current = source.entries;

  useEffect(() => {
    setEntries(learningEntriesToDrafts(sourceEntriesRef.current));
  }, [source.id]);

  const searchTerm = search.trim().toLowerCase();
  // Los filtros son solo de visualización: al guardar entran todas las cajitas.
  const visibleEntries = entries.filter((entry) => {
    if (searchTerm && !entry.text.toLowerCase().includes(searchTerm)) return false;
    const dayKey = entry.date ? timelineDateKey(entry.date) : "";
    if (dayKey && dayKey !== "sin-fecha") {
      if (dateFrom && dayKey < dateFrom) return false;
      if (dateTo && dayKey > dateTo) return false;
    }
    return true;
  });

  const addEntry = () => {
    setEntries((current) => [
      { key: `new-${Math.random().toString(36).slice(2)}`, date: new Date().toISOString(), text: "" },
      ...current,
    ]);
  };

  const updateEntryText = (key: string, value: string) => {
    setEntries((current) => current.map((entry) => (entry.key === key ? { ...entry, text: value } : entry)));
  };

  const removeEntry = (key: string) => {
    setEntries((current) => current.filter((entry) => entry.key !== key));
  };

  const handleSave = async () => {
    const finalEntries: LearningEntry[] = entries
      .filter((entry) => entry.text.trim())
      .map((entry) => ({
        id: entry.id,
        date: entry.date,
        suggestion_uid: entry.suggestion_uid,
        suggestion_number: entry.suggestion_number,
        text: entry.text.trim(),
        example_summary: entry.example_summary,
        category: entry.category,
      }));
    const ok = await Promise.resolve(onSave(finalEntries));
    if (ok) setEntries((current) => current.filter((entry) => entry.text.trim()));
  };

  return (
    <ScrollSurface>
      <div className="mx-auto max-w-5xl">
        <IntegrationBackButton label="Volver a Asistente IA" onBack={onBack} />
        <SettingsPageHeader
          icon={FileText}
          title={source.name}
          description={`${entries.length} aprendizaje${entries.length === 1 ? "" : "s"} · Actualizada ${formatDate(source.updated_at)}`}
          action={
            <ToggleSwitch
              checked={source.is_active}
              label={source.is_active ? "Activa" : "Pausada"}
              onChange={(value) => onToggle(value)}
            />
          }
        />
        <div className="mt-6 flex flex-wrap items-end gap-3">
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Buscar aprendizaje"
            className="h-10 min-w-[200px] flex-1 rounded-lg border border-gray-200 bg-white px-3 text-sm text-gray-900 outline-none focus:border-[#4368E5]"
          />
          <label className="block">
            <span className="mb-1 block text-xs font-semibold text-gray-600">Desde</span>
            <input
              type="date"
              value={dateFrom}
              onChange={(event) => setDateFrom(event.target.value)}
              className="h-10 rounded-lg border border-gray-200 bg-white px-3 text-sm text-gray-900 outline-none focus:border-[#4368E5]"
            />
          </label>
          <label className="block">
            <span className="mb-1 block text-xs font-semibold text-gray-600">Hasta</span>
            <input
              type="date"
              value={dateTo}
              min={dateFrom || undefined}
              onChange={(event) => setDateTo(event.target.value)}
              className="h-10 rounded-lg border border-gray-200 bg-white px-3 text-sm text-gray-900 outline-none focus:border-[#4368E5]"
            />
          </label>
        </div>
        <div className="mt-4 flex justify-end">
          <button
            type="button"
            onClick={addEntry}
            className="inline-flex h-9 items-center justify-center rounded-full border border-gray-200 bg-white px-4 text-sm font-semibold text-gray-700 transition hover:bg-gray-50"
          >
            + Agregar aprendizaje
          </button>
        </div>
        {entries.length === 0 ? (
          <p className="mt-6 rounded-xl border border-dashed border-gray-200 bg-gray-50 px-4 py-10 text-center text-sm text-gray-500">
            Aún no hay aprendizajes aprobados. Se crean desde las sugerencias del modo aprendiz.
          </p>
        ) : (
          <div className="mt-4 space-y-3">
            {visibleEntries.map((entry) => (
              <article key={entry.key} className="rounded-xl border border-gray-200 bg-white p-3">
                <div className="flex items-center justify-between gap-2 text-[11px] text-gray-500">
                  <span className="flex items-center gap-2">
                    <span>{formatLearningDate(entry.date)}</span>
                    {entry.suggestion_number ? <span>Sug. #{entry.suggestion_number}</span> : null}
                  </span>
                  <button
                    type="button"
                    onClick={() => removeEntry(entry.key)}
                    className="inline-flex size-6 items-center justify-center rounded-full text-gray-400 transition hover:bg-red-50 hover:text-red-600"
                    title="Eliminar aprendizaje"
                  >
                    <Trash2 className="size-3.5" />
                  </button>
                </div>
                <textarea
                  value={entry.text}
                  onChange={(event) => updateEntryText(entry.key, event.target.value)}
                  rows={Math.max(2, entry.text.split("\n").length)}
                  className="mt-2 w-full resize-y rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm leading-6 text-gray-800 outline-none focus:border-[#4368E5]"
                />
                {entry.example_summary ? (
                  <p className="mt-1 text-[11px] italic text-gray-500">Ej.: {entry.example_summary}</p>
                ) : null}
              </article>
            ))}
          </div>
        )}
        <div className="mt-6 flex justify-end">
          <button
            type="button"
            onClick={() => void handleSave()}
            disabled={saving}
            className="inline-flex h-10 items-center justify-center rounded-full bg-gray-950 px-5 text-sm font-semibold text-white transition hover:bg-gray-800 disabled:opacity-50"
          >
            {saving ? <Loader2 className="mr-2 size-4 animate-spin" /> : null}
            Guardar cambios
          </button>
        </div>
      </div>
    </ScrollSurface>
  );
};

const HotmartLineMark = ({ className = "size-5" }: { className?: string }) => (
  <svg viewBox="300 200 1150 1520" aria-hidden="true" className={className} fill="currentColor">
    <path d="M1241.91 645.024C1350.75 818.66 1395.28 1031 1388.6 1160.84C1388.6 1161.62 1388.63 1162.41 1388.66 1163.2C1388.7 1163.99 1388.73 1164.78 1388.73 1165.57C1386.31 1456.54 1148.48 1690.47 857.504 1688.05C566.531 1685.64 332.606 1447.8 335.018 1156.83C335.018 1149.79 335.383 1140.81 335.747 1135.35C335.869 1133.52 336.046 1131.6 336.227 1129.64C336.585 1125.77 336.96 1121.71 336.96 1117.76C337.819 1107.45 339.286 1094.22 339.855 1089.1C340.089 1086.99 340.171 1086.25 339.993 1087.67C359.893 896.074 464.972 689.798 580.851 568.459C581.923 566.917 584.042 566.537 585.583 567.609C587.125 568.682 587.505 570.8 586.433 572.342C572.236 602.798 542.751 690.283 583.763 693.074C597.475 693.074 604.391 685.065 612.521 668.806C622.266 649.068 631.887 622.38 642.924 591.762C686.168 471.805 751.163 291.509 930.671 232.35C934.19 231.137 938.437 233.078 935.282 238.174C882.406 321.096 881.042 426.808 931.763 511.065C968.408 571.735 1006.63 576.224 1027.5 558.145C1053.34 535.455 1046.67 468.84 1035.51 447.484C1033.69 443.965 1037.57 441.053 1040.85 443.237C1136.55 507.892 1150 589.239 1159.5 646.694C1166.25 687.578 1171.01 716.365 1201.98 718.434C1244.09 721.346 1242.39 673.538 1235.11 646.965C1234.02 642.233 1238.99 640.534 1241.91 645.024ZM617.493 1159.27C616.421 1294.18 724.882 1404.44 859.794 1405.58C924.67 1406.13 987.101 1380.86 1033.33 1335.34C1079.56 1289.81 1105.78 1227.78 1106.23 1162.9C1107.17 1027.99 998.591 917.844 863.678 916.841C728.766 915.839 618.566 1024.36 617.493 1159.27Z" />
  </svg>
);

const IntegrationBrandMark = ({ provider }: { provider: "whatsapp" | "messenger" | "instagram" | "hotmart" | "dropi" | string }) => {
  if (provider === "dropi") {
    return (
      <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-sky-50">
        <img src={DropiLogo} alt="" className="h-4 w-7 object-contain" />
      </div>
    );
  }

  if (provider === "messenger") {
    return (
      <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-blue-50">
        <img
          src="https://upload.wikimedia.org/wikipedia/commons/b/be/Facebook_Messenger_logo_2020.svg"
          alt=""
          className="h-5 w-5 object-contain"
        />
      </div>
    );
  }

  if (provider === "instagram") {
    return (
      <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-pink-50">
        <img
          src="https://upload.wikimedia.org/wikipedia/commons/e/e7/Instagram_logo_2016.svg"
          alt=""
          className="h-5 w-5 object-contain"
        />
      </div>
    );
  }

  if (provider === "shopify") {
    return (
      <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-lime-50">
        <img
          src="https://cdn.shopify.com/assets/images/logos/shopify-bag.png"
          alt=""
          className="h-5 w-5 object-contain"
        />
      </div>
    );
  }

  const iconClass = "size-5";
  const Icon =
    provider === "whatsapp"
      ? RiWhatsappLine
      : provider === "hotmart"
        ? HotmartLineMark
        : Plug;
  const colorClass =
    provider === "whatsapp"
      ? "bg-emerald-50 text-emerald-600"
      : provider === "hotmart"
        ? "bg-orange-50 text-orange-600"
        : "bg-gray-50 text-gray-700";
  return (
    <div className={`flex size-10 shrink-0 items-center justify-center rounded-lg ${colorClass}`}>
      <Icon className={iconClass} />
    </div>
  );
};

const integrationReadyForTools = (connection: IntegrationConnection | null | undefined) =>
  Boolean(
    connection &&
      (connection.ready_for_tools ??
        (connection.is_active && connection.status === "connected" && connection.diagnostic_status === "ok"))
  );

const IntegrationsTab = ({
  providers,
  connections,
  ecommerceDropiActive,
  selectedConnection,
  tools,
  hotmartMappings,
  form,
  dropiForm,
  saving,
  account,
  accountForm,
  accountSaving,
  diagnoseResult,
  testPhone,
  testMessage,
  testSending,
  registerPin,
  whatsappSetupAction,
  integrationDetail,
  onFormChange,
  onDropiFormChange,
  onAccountFormChange,
  onSaveAccount,
  onQuickOAuthComplete,
  onTemplatesProvisioned,
  onRotateVerifyToken,
  onDiagnoseWhatsApp,
  onCopy,
  onTestPhoneChange,
  onTestMessageChange,
  onTestSend,
  onRegisterPinChange,
  onRegisterNumber,
  onSubscribeWebhooks,
  onOpenDetail,
  onCloseDetail,
  onSelectConnection,
  onSaveHotmart,
  onSaveDropi,
  onUpdateMapping,
  onSyncCatalog,
  actionLoading,
  onRefresh,
}: {
  providers: IntegrationProvider[];
  connections: IntegrationConnection[];
  ecommerceDropiActive: boolean;
  selectedConnection: IntegrationConnection | null;
  tools: IntegrationTool[];
  hotmartMappings: HotmartProductMapping[];
  form: HotmartFormState;
  dropiForm: DropiFormState;
  loading: boolean;
  saving: boolean;
  actionLoading: string | null;
  account: WhatsAppConnectionStatus | null;
  accountForm: AccountFormState;
  accountSaving: boolean;
  diagnoseResult: Record<string, unknown> | null;
  testPhone: string;
  testMessage: string;
  testSending: boolean;
  registerPin: string;
  whatsappSetupAction: string | null;
  integrationDetail: IntegrationDetail | null;
  onFormChange: (form: HotmartFormState) => void;
  onDropiFormChange: (form: DropiFormState) => void;
  onAccountFormChange: (form: AccountFormState) => void;
  onSaveAccount: () => void;
  onQuickOAuthComplete: () => void | Promise<void>;
  onTemplatesProvisioned?: () => void;
  onRotateVerifyToken: () => void;
  onDiagnoseWhatsApp: () => void;
  onCopy: (value: string | null | undefined, label: string) => void;
  onTestPhoneChange: (value: string) => void;
  onTestMessageChange: (value: string) => void;
  onTestSend: () => void;
  onRegisterPinChange: (value: string) => void;
  onRegisterNumber: () => void;
  onSubscribeWebhooks: () => void;
  onOpenDetail: (detail: IntegrationDetail) => void;
  onCloseDetail: () => void;
  onSelectConnection: (connectionId: number) => void;
  onSaveHotmart: () => void;
  onSaveDropi: () => void;
  onUpdateMapping: (
    mapping: HotmartProductMapping,
    patch: Partial<Pick<HotmartProductMapping, "aliases" | "club_subdomain" | "product_type" | "is_active">> & {
      club_public_url?: string | null;
      metadata_json?: Record<string, unknown>;
    }
  ) => void;
  onSyncCatalog: (connectionId: number) => void;
  onRefresh: () => void;
}) => {
  const hotmartProvider = providers.find((provider) => provider.provider_key === "hotmart");
	  const hotmartConnection = connections.find((connection) => connection.provider_key === "hotmart") || null;
	  const hotmartTools = selectedConnection?.provider_key === "hotmart" ? tools : [];
	  const dropiProvider = providers.find((provider) => provider.provider_key === "dropi");
	  const dropiConnection = connections.find((connection) => connection.provider_key === "dropi") || null;
	  const dropiTools = selectedConnection?.provider_key === "dropi" ? tools : [];
	  const { websites: integrationWebsites, initialize: initializeIntegrationWebsites } = useWebsitesStore();
	  const hotmartConnectionReady = integrationReadyForTools(hotmartConnection);
	  const dropiConnectionActive = ecommerceDropiActive || integrationReadyForTools(dropiConnection);
	  const shopifyFunnelRows = integrationWebsites.filter((website) => {
	    const fulfillment = checkoutWebsiteFulfillmentContext(website);
	    return fulfillment.shopify_automatic_order || fulfillment.shopify_checkout_enabled || website.shopifyIntegrationStatus;
	  });
	  const shopifyConnectionActive = shopifyFunnelRows.some((website) => {
	    const fulfillment = checkoutWebsiteFulfillmentContext(website);
	    return fulfillment.shopify_automatic_order || fulfillment.shopify_checkout_enabled;
	  });
  const whatsappMessagingAvailable = Boolean(account?.exists && account.is_active && account.phone_number_id && account.waba_id);
  const {
    statuses: channelStatusByKey,
    connectedMap: channelStatuses,
    actionLoading: channelActionLoading,
    refresh: refreshChannelConnections,
    disconnect: disconnectChannelConnection,
    diagnose: diagnoseChannelConnection,
    testSend: testChannelConnectionSend,
  } = useChatChannelConnections();
  const [hotmartCredentialEditorOpen, setHotmartCredentialEditorOpen] = useState(false);
  const [hotmartProductToAdd, setHotmartProductToAdd] = useState("");
	  const [dropiCredentialEditorOpen, setDropiCredentialEditorOpen] = useState(false);
	  useEffect(() => {
	    void initializeIntegrationWebsites();
	  }, [initializeIntegrationWebsites]);
	  const integrationConnectionChip = (connected: boolean) => (
    <span
      className={`inline-flex h-8 items-center gap-1.5 rounded-full px-3 text-sm font-semibold ${
        connected ? "bg-gray-100 text-gray-800" : "bg-gray-50 text-gray-500"
      }`}
    >
      {connected ? <CheckCircle2 className="size-3.5" /> : null}
      {connected ? "Conectado" : "Conectar"}
    </span>
  );

  if (integrationDetail === "whatsapp") {
    return (
      <ScrollSurface>
        <div className="mx-auto max-w-5xl">
          <IntegrationBackButton onBack={onCloseDetail} />
          <WhatsAppTab
            account={account}
            accountForm={accountForm}
            accountSaving={accountSaving}
            diagnoseResult={diagnoseResult}
            testPhone={testPhone}
            testMessage={testMessage}
            testSending={testSending}
            registerPin={registerPin}
            whatsappSetupAction={whatsappSetupAction}
            onAccountFormChange={onAccountFormChange}
            onSaveAccount={onSaveAccount}
            onQuickOAuthComplete={onQuickOAuthComplete}
            onTemplatesProvisioned={onTemplatesProvisioned}
            onRotateVerifyToken={onRotateVerifyToken}
            onDiagnose={onDiagnoseWhatsApp}
            onCopy={onCopy}
            onTestPhoneChange={onTestPhoneChange}
            onTestMessageChange={onTestMessageChange}
            onTestSend={onTestSend}
            onRegisterPinChange={onRegisterPinChange}
            onRegisterNumber={onRegisterNumber}
            onSubscribeWebhooks={onSubscribeWebhooks}
          />
        </div>
      </ScrollSurface>
    );
  }

  if (integrationDetail === "messenger" || integrationDetail === "instagram") {
    const channel = integrationDetail;
    return (
      <ScrollSurface>
        <div className="mx-auto max-w-5xl">
          <IntegrationBackButton onBack={onCloseDetail} />
          <ChannelConnectionDetail
            channel={channel}
            account={channelStatusByKey[channel] || null}
            surface="chatbot"
            actionLoading={channelActionLoading}
            onRefresh={async () => {
              await refreshChannelConnections();
            }}
            onDisconnect={() => disconnectChannelConnection(channel)}
            onDiagnose={() => diagnoseChannelConnection(channel)}
            onTestSend={(recipient, text) => testChannelConnectionSend(channel, recipient, text)}
          />
        </div>
      </ScrollSurface>
    );
  }

	  if (integrationDetail === "hotmart") {
    const hotmartSecretStatus = hotmartConnection?.secret_status || {};
    const hotmartConnectionCreated = Boolean(hotmartConnection);
    const hotmartDiagnosticOk = hotmartConnection?.diagnostic_status === "ok";
    const hotmartReadyForTools = integrationReadyForTools(hotmartConnection);
    const hotmartApiReady = Boolean(
      hotmartConnectionCreated &&
      hotmartSecretStatus.client_id &&
      hotmartSecretStatus.client_secret &&
      hotmartSecretStatus.basic &&
      hotmartDiagnosticOk &&
      hotmartReadyForTools
    );
    const hotmartStatusRows = [
      { label: "Conexión creada", ready: hotmartConnectionCreated, value: hotmartConnectionCreated ? "Listo" : "Pendiente" },
      { label: "Client ID", ready: Boolean(hotmartSecretStatus.client_id), value: hotmartSecretStatus.client_id ? "••••••••" : "Pendiente" },
      { label: "Client secret", ready: Boolean(hotmartSecretStatus.client_secret), value: hotmartSecretStatus.client_secret ? "••••••••" : "Pendiente" },
      { label: "Basic auth", ready: Boolean(hotmartSecretStatus.basic), value: hotmartSecretStatus.basic ? "••••••••" : "Pendiente" },
      { label: "Diagnóstico Hotmart", ready: hotmartDiagnosticOk, value: hotmartDiagnosticOk ? "OK" : "Pendiente" },
      { label: "Hottok webhooks", ready: Boolean(hotmartSecretStatus.hottok), value: hotmartSecretStatus.hottok ? "••••••••" : "Obligatorio" },
    ];
    const hotmartIntegrationComplete = hotmartApiReady;
    const currentHotmartName = hotmartConnection?.display_name || "Hotmart";
    const hotmartNameValue = form.display_name.trim() || "Hotmart";
    const hotmartNameDirty = hotmartNameValue !== "Hotmart" && hotmartNameValue !== currentHotmartName;
    const hotmartFormDirty =
      hotmartNameDirty ||
      Boolean(form.client_id.trim() || form.client_secret.trim() || form.basic.trim() || form.hottok.trim());
    const resetHotmartCredentialDraft = () => {
      onFormChange({
        display_name: currentHotmartName,
        client_id: "",
        client_secret: "",
        basic: "",
        hottok: "",
      });
    };
    const displayedHotmartTools = hotmartTools.length ? hotmartTools : hotmartProvider?.tools ?? [];
    const activeHotmartMappings = hotmartIntegrationComplete ? hotmartMappings.filter((mapping) => mapping.is_active) : [];
    const inactiveHotmartMappings = hotmartIntegrationComplete ? hotmartMappings.filter((mapping) => !mapping.is_active) : [];
    const hotmartProductOptions = inactiveHotmartMappings.map((mapping) => ({
      value: String(mapping.id),
      label: mapping.product_name,
    }));
    const activateHotmartProduct = (mappingId: string) => {
      setHotmartProductToAdd(mappingId);
      const mapping = inactiveHotmartMappings.find((item) => String(item.id) === mappingId);
      if (!mapping) return;
      onUpdateMapping(mapping, { is_active: true });
      setHotmartProductToAdd("");
    };
    return (
      <ScrollSurface>
        <div className="mx-auto max-w-5xl">
          <IntegrationBackButton onBack={onCloseDetail} />

          <section className="rounded-2xl border border-gray-200 bg-white">
            <div className="flex flex-wrap items-center justify-between gap-4 border-b border-gray-100 px-5 py-4">
              <div className="flex min-w-0 items-center gap-3">
                <div className="flex min-w-0 flex-wrap items-center gap-2">
                  <h3 className="text-base font-semibold text-gray-950">Estado de la integración</h3>
                  <span
                    className={`inline-flex h-7 items-center rounded-full px-3 text-xs font-semibold ${
                      hotmartIntegrationComplete ? "bg-[#EEF3FF] text-[#3151C9]" : "bg-gray-100 text-gray-600"
                    }`}
                  >
                    {hotmartIntegrationComplete ? "Activa" : "Pendiente"}
                  </span>
                </div>
              </div>
              <div className="flex flex-wrap items-center justify-end gap-2">
                {hotmartCredentialEditorOpen && hotmartFormDirty ? (
                  <>
                    <button
                      type="button"
                      onClick={resetHotmartCredentialDraft}
                      disabled={saving}
                      className="inline-flex h-9 items-center rounded-full bg-gray-100 px-3 text-sm font-semibold text-gray-700 transition hover:bg-gray-200 hover:text-gray-950 disabled:opacity-50"
                    >
                      Deshacer cambios
                    </button>
                    <button
                      type="button"
                      onClick={onSaveHotmart}
                      disabled={saving || (!hotmartConnection && (!form.client_id || !form.client_secret || !form.basic))}
                      className="inline-flex h-9 items-center gap-2 rounded-full bg-gray-950 px-3 text-sm font-semibold text-white hover:bg-gray-800 disabled:opacity-50"
                    >
                      {saving ? <Loader2 className="size-4 animate-spin" /> : <Save className="size-4" />}
                      Guardar cambios
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    onClick={() => setHotmartCredentialEditorOpen((current) => !current)}
                    className={`inline-flex h-9 items-center gap-2 rounded-full px-3 text-sm font-semibold transition ${
                      hotmartCredentialEditorOpen || hotmartIntegrationComplete
                        ? "bg-gray-100 text-gray-700 hover:bg-gray-200 hover:text-gray-950"
                        : "bg-gray-950 text-white hover:bg-gray-800"
                    }`}
                  >
                    <Settings2 className="size-4" />
                    {hotmartCredentialEditorOpen
                      ? "Cerrar edición"
                      : hotmartIntegrationComplete
                        ? "Editar conexión"
                        : "Conectar integración"}
                  </button>
                )}
              </div>
            </div>
            <div className="grid gap-0 md:grid-cols-2">
              {hotmartCredentialEditorOpen ? (
                <>
                  <div className="flex flex-col gap-2 border-b border-gray-100 px-5 py-3 text-sm">
                    <span className="text-gray-600">Nombre</span>
                    <input
                      value={form.display_name}
                      onChange={(event) => onFormChange({ ...form, display_name: event.target.value })}
                      className="h-10 rounded-xl border border-gray-200 bg-white px-3 text-sm text-gray-900 outline-none transition focus:border-gray-400"
                      placeholder="Hotmart"
                    />
                  </div>
                  <div className="flex flex-col gap-2 border-b border-gray-100 px-5 py-3 text-sm">
                    <span className="text-gray-600">Client ID</span>
                    <input
                      value={form.client_id}
                      onChange={(event) => onFormChange({ ...form, client_id: event.target.value })}
                      className="h-10 rounded-xl border border-gray-200 bg-white px-3 text-sm text-gray-900 outline-none transition focus:border-gray-400"
                      placeholder={hotmartSecretStatus.client_id ? "••••••••" : ""}
                    />
                  </div>
                  <div className="flex flex-col gap-2 border-b border-gray-100 px-5 py-3 text-sm md:border-b-0">
                    <span className="text-gray-600">Client secret</span>
                    <input
                      type="password"
                      value={form.client_secret}
                      onChange={(event) => onFormChange({ ...form, client_secret: event.target.value })}
                      className="h-10 rounded-xl border border-gray-200 bg-white px-3 text-sm text-gray-900 outline-none transition focus:border-gray-400"
                      placeholder={hotmartSecretStatus.client_secret ? "••••••••" : ""}
                    />
                  </div>
                  <div className="flex flex-col gap-2 border-b border-gray-100 px-5 py-3 text-sm md:border-b-0">
                    <span className="text-gray-600">Basic auth</span>
                    <input
                      type="password"
                      value={form.basic}
                      onChange={(event) => onFormChange({ ...form, basic: event.target.value })}
                      className="h-10 rounded-xl border border-gray-200 bg-white px-3 text-sm text-gray-900 outline-none transition focus:border-gray-400"
                      placeholder={hotmartSecretStatus.basic ? "••••••••" : ""}
                    />
                  </div>
                  <div className="flex flex-col gap-2 px-5 py-3 text-sm md:col-span-2">
                    <span className="text-gray-600">Hottok webhooks</span>
                    <input
                      type="password"
                      value={form.hottok}
                      onChange={(event) => onFormChange({ ...form, hottok: event.target.value })}
                      className="h-10 rounded-xl border border-gray-200 bg-white px-3 text-sm text-gray-900 outline-none transition focus:border-gray-400"
                      placeholder={hotmartSecretStatus.hottok ? "••••••••" : ""}
                    />
                  </div>
                </>
              ) : (
                hotmartStatusRows.map((row) => (
                  <div
                    key={row.label}
                    className="flex items-center justify-between gap-3 border-b border-gray-100 px-5 py-3 text-sm md:[&:nth-last-child(-n+2)]:border-b-0"
                  >
                    <span className="text-gray-600">{row.label}</span>
                    <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${
                      row.ready ? "bg-gray-100 text-gray-800" : "bg-gray-50 text-gray-400"
                    }`}>
                      {row.ready ? <CheckCircle2 className="size-3.5" /> : null}
                      {row.value}
                    </span>
                  </div>
                ))
              )}
            </div>
          </section>

          {hotmartConnection && hotmartIntegrationComplete ? (
            <HotmartWorkspace
              connection={hotmartConnection}
              tools={displayedHotmartTools}
              initialMappings={hotmartMappings}
              syncing={actionLoading === "sync-catalog"}
              onSyncCatalog={() => onSyncCatalog(hotmartConnection.id)}
            />
          ) : null}

          <div className="hidden">
            <section className={hotmartIntegrationComplete ? "" : "opacity-45 grayscale"}>
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div>
                  <h3 className="text-sm font-semibold text-gray-950">Productos y Clubs</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Activa productos y configura la URL pública del Club y el subdomain de API por curso.
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  {hotmartConnection ? (
                    <button
                      type="button"
                      disabled={!hotmartIntegrationComplete || actionLoading === "sync-catalog"}
                      onClick={() => onSyncCatalog(hotmartConnection.id)}
                      className="inline-flex h-9 items-center gap-2 rounded-full bg-gray-100 px-3 text-sm font-semibold text-gray-800 hover:bg-gray-200 disabled:opacity-50"
                    >
                      {actionLoading === "sync-catalog" ? (
                        <Loader2 className="size-4 animate-spin" />
                      ) : (
                        <RefreshCw className="size-4" />
                      )}
                      Sincronizar catálogo
                    </button>
                  ) : null}
                  <a
                    href={HOTMART_MEMBERS_IMPORT_URL}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex h-9 items-center gap-2 rounded-full bg-gray-950 px-3 text-sm font-semibold text-white hover:bg-gray-800"
                  >
                    Importar alumnos
                    <ExternalLink className="size-3.5" />
                  </a>
                </div>
              </div>
              <div className="mt-4 flex flex-wrap items-center justify-end gap-3">
                <div className="w-full sm:w-72">
                  <ModalSelect
                    value={hotmartProductToAdd}
                    placeholder={
                      hotmartIntegrationComplete
                        ? inactiveHotmartMappings.length
                          ? "Agregar producto"
                          : "Sin productos pendientes"
                        : "Integración pendiente"
                    }
                    disabled={!hotmartIntegrationComplete || !hotmartConnection || inactiveHotmartMappings.length === 0}
                    options={hotmartProductOptions}
                    onChange={activateHotmartProduct}
                  />
                </div>
              </div>
              <div className="mt-4 overflow-hidden rounded-xl border border-gray-200">
                <table className="w-full text-left text-sm">
                  <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                    <tr>
                      <th className="px-4 py-3">Producto</th>
                      <th className="px-4 py-3">URL Club (alumno)</th>
                      <th className="px-4 py-3">Subdomain API</th>
                      <th className="px-4 py-3 text-right">Estado</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 bg-white">
                    {activeHotmartMappings.map((mapping) => {
                      const clubPublicUrl = clubPublicUrlFromMapping(mapping);
                      const subdomainPreview = mapping.club_subdomain || parseClubSubdomainFromUrl(clubPublicUrl) || "";
                      const mappingBusy = actionLoading === `mapping-${mapping.id}`;
                      return (
                        <tr key={mapping.id}>
                          <td className="px-4 py-3 align-top">
                            <p className="font-semibold text-gray-950">{mapping.product_name}</p>
                            <p className="mt-1 text-xs text-gray-500">{mapping.product_id || mapping.ucode || "Sin ID"}</p>
                          </td>
                          <td className="px-4 py-3 align-top">
                            <input
                              key={`${mapping.id}-${clubPublicUrl}`}
                              defaultValue={clubPublicUrl}
                              disabled={mappingBusy}
                              placeholder="https://acceso.tudominio.com/.../club/mi-curso"
                              onBlur={(event) => {
                                const next = event.target.value.trim();
                                if (next === clubPublicUrl) return;
                                onUpdateMapping(mapping, buildHotmartClubMappingPatch(mapping, { club_public_url: next }));
                              }}
                              className="w-full min-w-[220px] rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs text-gray-800 outline-none transition focus:border-gray-400 disabled:opacity-50"
                            />
                          </td>
                          <td className="px-4 py-3 align-top">
                            <input
                              key={`${mapping.id}-sub-${mapping.club_subdomain || ""}`}
                              defaultValue={mapping.club_subdomain || ""}
                              disabled={mappingBusy}
                              placeholder={subdomainPreview || "protocoloia"}
                              onBlur={(event) => {
                                const next = event.target.value.trim();
                                if (next === (mapping.club_subdomain || "")) return;
                                onUpdateMapping(
                                  mapping,
                                  buildHotmartClubMappingPatch(mapping, { club_subdomain: next })
                                );
                              }}
                              className="w-full min-w-[140px] rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs text-gray-800 outline-none transition focus:border-gray-400 disabled:opacity-50"
                            />
                            {subdomainPreview && subdomainPreview !== mapping.club_subdomain ? (
                              <p className="mt-1 text-[11px] text-gray-500">Sugerido desde URL: {subdomainPreview}</p>
                            ) : null}
                          </td>
                          <td className="px-4 py-3 align-top">
                            <div className="flex justify-end">
                              <ToggleSwitch
                                checked={mapping.is_active}
                                label={mapping.is_active ? "Activo" : "Inactivo"}
                                onChange={(is_active) => onUpdateMapping(mapping, { is_active })}
                              />
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                    {activeHotmartMappings.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="px-4 py-8 text-center text-sm text-gray-500">
                          {!hotmartIntegrationComplete
                            ? "Completa la integración para activar productos."
                            : hotmartMappings.length
                              ? "Agrega productos desde el selector para activarlos."
                              : "Sin productos sincronizados todavía. Usa Sincronizar catálogo."}
                        </td>
                      </tr>
                    ) : null}
                  </tbody>
                </table>
              </div>
            </section>

            <section className={hotmartIntegrationComplete ? "" : "opacity-45 grayscale"}>
              <h3 className="text-sm font-semibold text-gray-950">Acciones</h3>
              <p className="mt-1 text-sm text-gray-500">
                Esta tabla muestra qué puede consultar o ejecutar la IA en Hotmart. La activación se gestiona en el tab Acciones.
              </p>
              <div className="mt-4 overflow-hidden rounded-xl border border-gray-200">
                <table className="w-full text-left text-sm">
                    <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                      <tr>
                        <th className="px-4 py-3">Acción</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 bg-white">
                      {displayedHotmartTools.map((tool) => (
                        <tr key={tool.tool_name}>
                          <td className="px-4 py-3">
                            <p className="font-semibold text-gray-900">{tool.display_name}</p>
                            <p className="mt-1 text-xs text-gray-500">{tool.description}</p>
                          </td>
                        </tr>
                      ))}
                      {displayedHotmartTools.length === 0 ? (
                        <tr>
                          <td className="px-4 py-8 text-center text-sm text-gray-500">
                            Sin acciones disponibles para Hotmart.
                          </td>
                        </tr>
                      ) : null}
                    </tbody>
                  </table>
                </div>
            </section>
          </div>
        </div>
      </ScrollSurface>
    );
	  }

	  if (integrationDetail === "shopify") {
	    return (
	      <ScrollSurface>
	        <div className="mx-auto max-w-5xl">
	          <IntegrationBackButton onBack={onCloseDetail} />
	          <section className="rounded-2xl border border-gray-200 bg-white">
	            <div className="flex flex-wrap items-center justify-between gap-4 border-b border-gray-100 px-5 py-4">
	              <div className="flex min-w-0 items-center gap-3">
	                <IntegrationBrandMark provider="shopify" />
	                <div className="min-w-0">
	                  <h3 className="text-base font-semibold text-gray-950">Shopify</h3>
	                  <p className="mt-1 text-sm leading-5 text-gray-500">
	                    La sincronización se controla por embudo desde Acciones &gt; Ventas, dentro de cada producto enlazado.
	                  </p>
	                </div>
	              </div>
	              {integrationConnectionChip(shopifyConnectionActive)}
	            </div>
	            <div className="divide-y divide-gray-100">
	              {shopifyFunnelRows.length ? (
	                shopifyFunnelRows.map((website) => {
	                  const fulfillment = checkoutWebsiteFulfillmentContext(website);
	                  return (
	                    <div key={website.websiteId} className="flex flex-wrap items-center justify-between gap-3 px-5 py-3">
	                      <div className="min-w-0">
	                        <p className="truncate text-sm font-semibold text-gray-950">
	                          {checkoutWebsiteSequenceLabel(website)} {checkoutWebsiteName(website)}
	                        </p>
	                        <p className="mt-1 truncate text-xs text-gray-500">{checkoutWebsiteSubtitle(website)}</p>
	                      </div>
	                      <div className="flex flex-wrap items-center gap-2">
	                        {fulfillment.shopify_checkout_enabled ? (
	                          <span className="inline-flex rounded-full bg-[#EEF3FF] px-2.5 py-1 text-xs font-semibold text-[#3151C9]">
	                            Checkout Shopify
	                          </span>
	                        ) : null}
	                        {fulfillment.shopify_automatic_order ? (
	                          <span className="inline-flex rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700">
	                            Sync órdenes activo
	                          </span>
	                        ) : null}
	                        {!fulfillment.shopify_checkout_enabled && !fulfillment.shopify_automatic_order ? (
	                          <span className="inline-flex rounded-full bg-gray-100 px-2.5 py-1 text-xs font-semibold text-gray-600">
	                            Configurado
	                          </span>
	                        ) : null}
	                      </div>
	                    </div>
	                  );
	                })
	              ) : (
	                <p className="px-5 py-8 text-sm leading-6 text-gray-500">
	                  No hay embudos con Shopify activo o configurado todavía. Cuando selecciones Shopify como sincronización en una toma de orden, el estado aparecerá aquí.
	                </p>
	              )}
	            </div>
	          </section>
	        </div>
	      </ScrollSurface>
	    );
	  }

	  if (integrationDetail === "dropi") {
    const dropiSecretStatus = dropiConnection?.secret_status || {};
    const dropiCredentialsSaved = Boolean(dropiConnection?.is_active);
    const dropiDiagnosticOk = dropiConnection?.diagnostic_status === "ok";
    const dropiConnectionReady = integrationReadyForTools(dropiConnection);
    const dropiIntegrationComplete = Boolean(
      dropiConnectionReady && dropiSecretStatus.api_token && dropiSecretStatus.country_code
    );
    const currentDropiName = dropiConnection?.display_name || "Dropi";
    const currentDropiCountry = String(dropiConnection?.metadata_json?.country_code || "co");
    const currentDropiBaseUrl = String(dropiConnection?.metadata_json?.base_url || "");
    const dropiNameValue = dropiForm.display_name.trim() || "Dropi";
    const dropiNameDirty = dropiNameValue !== "Dropi" && dropiNameValue !== currentDropiName;
    const dropiFormDirty =
      dropiNameDirty ||
      (dropiForm.country_code.trim() || "co") !== currentDropiCountry ||
      dropiForm.base_url.trim() !== currentDropiBaseUrl ||
      Boolean(dropiForm.api_token.trim() || dropiForm.webhook_secret.trim());
    const dropiStatusRows = [
      { label: "Credenciales guardadas", ready: dropiCredentialsSaved, value: dropiCredentialsSaved ? "Listo" : "Pendiente" },
      { label: "API token", ready: Boolean(dropiSecretStatus.api_token), value: dropiSecretStatus.api_token ? "••••••••" : "Pendiente" },
      { label: "País / host", ready: Boolean(dropiSecretStatus.country_code), value: dropiSecretStatus.country_code ? currentDropiCountry.toUpperCase() : "Pendiente" },
      { label: "Diagnóstico Dropi", ready: dropiDiagnosticOk, value: dropiDiagnosticOk ? "OK" : "Pendiente" },
      { label: "Base API", ready: true, value: currentDropiBaseUrl || "Por defecto" },
    ];
    const resetDropiCredentialDraft = () =>
      onDropiFormChange({
        display_name: currentDropiName,
        country_code: currentDropiCountry,
        base_url: currentDropiBaseUrl,
        api_token: "",
        webhook_secret: "",
      });
    const dropiGatedClass = dropiIntegrationComplete ? "" : "opacity-45 grayscale";
    const displayedDropiTools = dropiTools.length ? dropiTools : dropiProvider?.tools ?? [];

    return (
      <ScrollSurface>
        <div className="mx-auto max-w-5xl">
          <IntegrationBackButton onBack={onCloseDetail} />

          <section className="rounded-2xl border border-gray-200 bg-white">
            <div className="flex flex-wrap items-center justify-between gap-4 border-b border-gray-100 px-5 py-4">
              <div className="flex min-w-0 flex-wrap items-center gap-2">
                <h3 className="text-base font-semibold text-gray-950">Estado de la integración</h3>
                <span
                  className={`inline-flex h-7 items-center rounded-full px-3 text-xs font-semibold ${
                    dropiIntegrationComplete ? "bg-[#EEF3FF] text-[#3151C9]" : "bg-gray-100 text-gray-600"
                  }`}
                >
                  {dropiIntegrationComplete ? "Activa" : "Pendiente"}
                </span>
              </div>
              <div className="flex flex-wrap items-center justify-end gap-2">
                {dropiCredentialEditorOpen && dropiFormDirty ? (
                  <>
                    <button
                      type="button"
                      onClick={resetDropiCredentialDraft}
                      className="inline-flex h-9 items-center rounded-full bg-gray-100 px-3 text-sm font-semibold text-gray-700 transition hover:bg-gray-200 hover:text-gray-950"
                    >
                      Deshacer cambios
                    </button>
                    <button
                      type="button"
                      onClick={onSaveDropi}
                      disabled={saving || (!dropiConnection && (!dropiForm.api_token.trim() || !dropiForm.country_code.trim()))}
                      className="inline-flex h-9 items-center gap-2 rounded-full bg-gray-950 px-3 text-sm font-semibold text-white hover:bg-gray-800 disabled:opacity-50"
                    >
                      {saving ? <Loader2 className="size-4 animate-spin" /> : <Save className="size-4" />}
                      Guardar cambios
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    onClick={() => {
                      const opening = !dropiCredentialEditorOpen;
                      if (opening) resetDropiCredentialDraft();
                      setDropiCredentialEditorOpen(opening);
                    }}
                    className={`inline-flex h-9 items-center gap-2 rounded-full px-3 text-sm font-semibold transition ${
                      dropiCredentialEditorOpen || dropiIntegrationComplete
                        ? "bg-gray-100 text-gray-700 hover:bg-gray-200 hover:text-gray-950"
                        : "bg-gray-950 text-white hover:bg-gray-800"
                    }`}
                  >
                    <Settings2 className="size-4" />
                    {dropiCredentialEditorOpen
                      ? "Cerrar edición"
                      : dropiIntegrationComplete
                        ? "Editar conexión"
                        : "Conectar integración"}
                  </button>
                )}
              </div>
            </div>
            <div className="grid gap-0 md:grid-cols-2">
              {dropiCredentialEditorOpen ? (
                <>
                  <div className="flex flex-col gap-2 border-b border-gray-100 px-5 py-3 text-sm">
                    <span className="text-gray-600">Nombre visible</span>
                    <input
                      value={dropiForm.display_name}
                      onChange={(event) => onDropiFormChange({ ...dropiForm, display_name: event.target.value })}
                      className="h-10 rounded-xl border border-gray-200 bg-white px-3 text-sm text-gray-900 outline-none transition focus:border-gray-400"
                      placeholder="Dropi"
                    />
                  </div>
                  <div className="flex flex-col gap-2 border-b border-gray-100 px-5 py-3 text-sm">
                    <span className="text-gray-600">País / host</span>
                    <input
                      value={dropiForm.country_code}
                      onChange={(event) => onDropiFormChange({ ...dropiForm, country_code: event.target.value })}
                      className="h-10 rounded-xl border border-gray-200 bg-white px-3 text-sm text-gray-900 outline-none transition focus:border-gray-400"
                      placeholder="co"
                    />
                  </div>
                  <div className="flex flex-col gap-2 border-b border-gray-100 px-5 py-3 text-sm md:border-b-0">
                    <span className="text-gray-600">Base API opcional</span>
                    <input
                      value={dropiForm.base_url}
                      onChange={(event) => onDropiFormChange({ ...dropiForm, base_url: event.target.value })}
                      className="h-10 rounded-xl border border-gray-200 bg-white px-3 text-sm text-gray-900 outline-none transition focus:border-gray-400"
                      placeholder={currentDropiBaseUrl || "https://api.dropi.co/api"}
                    />
                  </div>
                  <div className="flex flex-col gap-2 border-b border-gray-100 px-5 py-3 text-sm md:border-b-0">
                    <span className="text-gray-600">API token</span>
                    <input
                      type="password"
                      value={dropiForm.api_token}
                      onChange={(event) => onDropiFormChange({ ...dropiForm, api_token: event.target.value })}
                      className="h-10 rounded-xl border border-gray-200 bg-white px-3 text-sm text-gray-900 outline-none transition focus:border-gray-400"
                      placeholder={dropiSecretStatus.api_token ? "••••••••" : ""}
                    />
                  </div>
                  <div className="flex flex-col gap-2 px-5 py-3 text-sm md:col-span-2">
                    <span className="text-gray-600">Webhook secret opcional</span>
                    <input
                      type="password"
                      value={dropiForm.webhook_secret}
                      onChange={(event) => onDropiFormChange({ ...dropiForm, webhook_secret: event.target.value })}
                      className="h-10 rounded-xl border border-gray-200 bg-white px-3 text-sm text-gray-900 outline-none transition focus:border-gray-400"
                      placeholder="Secret para eventos si Dropi los entrega"
                    />
                  </div>
                </>
              ) : (
                dropiStatusRows.map((row) => (
                  <div
                    key={row.label}
                    className="flex items-center justify-between gap-3 border-b border-gray-100 px-5 py-3 text-sm md:[&:nth-last-child(-n+2)]:border-b-0"
                  >
                    <span className="text-gray-600">{row.label}</span>
                    <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${
                      row.ready ? "bg-gray-100 text-gray-800" : "bg-gray-50 text-gray-400"
                    }`}>
                      {row.ready ? <CheckCircle2 className="size-3.5" /> : null}
                      {row.value}
                    </span>
                  </div>
                ))
              )}
            </div>
          </section>

          <div className={`mt-8 ${dropiGatedClass}`}>
            <section>
              <h3 className="text-sm font-semibold text-gray-950">Acciones</h3>
              <p className="mt-1 text-sm text-gray-500">
                Esta tabla muestra qué puede consultar la IA en Dropi. La activación se gestiona en el tab Acciones.
              </p>
              <div className="mt-4 overflow-hidden rounded-xl border border-gray-200">
                <table className="w-full text-left text-sm">
                  <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                    <tr>
                      <th className="px-4 py-3">Acción</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 bg-white">
                    {displayedDropiTools.map((tool) => (
                      <tr key={tool.tool_name}>
                        <td className="px-4 py-3">
                          <p className="font-semibold text-gray-900">{tool.display_name}</p>
                          <p className="mt-1 text-xs text-gray-500">{tool.description}</p>
                        </td>
                      </tr>
                    ))}
                    {displayedDropiTools.length === 0 ? (
                      <tr>
                        <td className="px-4 py-8 text-center text-sm text-gray-500">
                          Sin acciones disponibles para Dropi.
                        </td>
                      </tr>
                    ) : null}
                  </tbody>
                </table>
              </div>
            </section>
          </div>
        </div>
      </ScrollSurface>
    );
  }

  return (
    <ScrollSurface>
      <div className="mx-auto max-w-5xl">
        <div className="border-b border-gray-100">
          <div className="border-t border-gray-100 first:border-t-0">
            <button
              onClick={() => onOpenDetail("whatsapp")}
              className="flex w-full items-center justify-between gap-4 rounded-2xl px-3 py-4 text-left transition hover:bg-gray-50"
            >
              <div className="flex min-w-0 items-center gap-3">
                <IntegrationBrandMark provider="whatsapp" />
                <div className="min-w-0">
                  <p className="text-base font-semibold text-gray-950">WhatsApp Cloud API</p>
                </div>
              </div>
              {integrationConnectionChip(whatsappMessagingAvailable)}
            </button>
          </div>

          <div className="border-t border-gray-100 first:border-t-0">
            <button
              onClick={() => onOpenDetail("messenger")}
              className="flex w-full items-center justify-between gap-4 rounded-2xl px-3 py-4 text-left transition hover:bg-gray-50"
            >
              <div className="flex min-w-0 items-center gap-3">
                <IntegrationBrandMark provider="messenger" />
                <div className="min-w-0">
                  <p className="text-base font-semibold text-gray-950">Messenger</p>
                </div>
              </div>
              {integrationConnectionChip(Boolean(channelStatuses.messenger))}
            </button>
          </div>

          <div className="border-t border-gray-100 first:border-t-0">
            <button
              onClick={() => onOpenDetail("instagram")}
              className="flex w-full items-center justify-between gap-4 rounded-2xl px-3 py-4 text-left transition hover:bg-gray-50"
            >
              <div className="flex min-w-0 items-center gap-3">
                <IntegrationBrandMark provider="instagram" />
                <div className="min-w-0">
                  <p className="text-base font-semibold text-gray-950">Instagram</p>
                </div>
              </div>
              {integrationConnectionChip(Boolean(channelStatuses.instagram))}
            </button>
          </div>

          <div className="border-t border-gray-100 first:border-t-0">
            <button
              onClick={() => {
                if (hotmartConnection) onSelectConnection(hotmartConnection.id);
                onOpenDetail("hotmart");
              }}
              className="flex w-full items-center justify-between gap-4 rounded-2xl px-3 py-4 text-left transition hover:bg-gray-50"
            >
              <div className="flex min-w-0 items-center gap-3">
                <IntegrationBrandMark provider="hotmart" />
                <div className="min-w-0">
                  <p className="text-base font-semibold text-gray-950">Hotmart</p>
                </div>
              </div>
              {integrationConnectionChip(hotmartConnectionReady)}
            </button>
          </div>

	          <div className="border-t border-gray-100 first:border-t-0">
	            <button
	              onClick={() => {
	                if (dropiConnection) onSelectConnection(dropiConnection.id);
	                onOpenDetail("dropi");
              }}
              className="flex w-full items-center justify-between gap-4 rounded-2xl px-3 py-4 text-left transition hover:bg-gray-50"
            >
              <div className="flex min-w-0 items-center gap-3">
                <IntegrationBrandMark provider="dropi" />
                <div className="min-w-0">
                  <p className="text-base font-semibold text-gray-950">Dropi</p>
                </div>
              </div>
	              {integrationConnectionChip(dropiConnectionActive)}
	            </button>
	          </div>

	          <div className="border-t border-gray-100 first:border-t-0">
	            <button
	              onClick={() => onOpenDetail("shopify")}
	              className="flex w-full items-center justify-between gap-4 rounded-2xl px-3 py-4 text-left transition hover:bg-gray-50"
	            >
	              <div className="flex min-w-0 items-center gap-3">
	                <IntegrationBrandMark provider="shopify" />
	                <div className="min-w-0">
	                  <p className="text-base font-semibold text-gray-950">Shopify</p>
	                </div>
	              </div>
	              {integrationConnectionChip(shopifyConnectionActive)}
	            </button>
	          </div>

	          {(providers.length ? providers : []).filter((provider) => !["hotmart", "dropi", "shopify"].includes(provider.provider_key)).map((provider) => {
            const connection = connections.find((item) => item.provider_key === provider.provider_key);
            return (
              <div key={provider.provider_key} className="border-t border-gray-100 first:border-t-0">
                <div className="flex items-center justify-between gap-4 rounded-2xl px-3 py-4 transition hover:bg-gray-50">
                  <div className="flex min-w-0 items-center gap-3">
                    <IntegrationBrandMark provider={provider.provider_key} />
                    <div className="min-w-0">
                      <p className="text-base font-semibold text-gray-950">{provider.display_name}</p>
                    </div>
                  </div>
                  {integrationConnectionChip(integrationReadyForTools(connection))}
                </div>
              </div>
            );
          })}
        </div>

      </div>
    </ScrollSurface>
  );
};

const ActionsTab = ({
  account,
  settingsForm,
  connections,
  toolsByConnectionId,
  integrationScope = "all",
  onOpenIntegrationDetail,
  onUpdateTool,
  onSettingsFormChange,
}: {
  account: WhatsAppConnectionStatus | null;
  settingsForm: SupportSettingsPayload;
  connections: IntegrationConnection[];
  toolsByConnectionId: Record<number, IntegrationTool[]>;
  integrationScope?: "all" | "hotmart" | "otras";
  onOpenIntegrationDetail: (detail: IntegrationDetail) => void;
  onUpdateTool: (
    connectionId: number,
    tool: IntegrationTool,
    patch: { enabled?: boolean; policy?: string | null; metadata_json?: Record<string, unknown> }
  ) => void;
  onSettingsFormChange: (form: SupportSettingsPayload) => void;
}) => {
  const hotmartConnection = connections.find((connection) => connection.provider_key === "hotmart") || null;
  const hotmartTools = hotmartConnection ? toolsByConnectionId[hotmartConnection.id] || [] : [];
  const dropiConnection = connections.find((connection) => connection.provider_key === "dropi") || null;
  const dropiTools = dropiConnection ? toolsByConnectionId[dropiConnection.id] || [] : [];
  const whatsappMessagingAvailable = Boolean(account?.exists && account.is_active && account.phone_number_id && account.waba_id);
  const hotmartConnectionActive = integrationReadyForTools(hotmartConnection);
  const permittedHotmartTools = hotmartConnectionActive ? hotmartTools : [];
  const dropiConnectionActive = integrationReadyForTools(dropiConnection);
  const permittedDropiTools = dropiConnectionActive ? dropiTools : [];
  const [activeIntegrationFilters, setActiveIntegrationFilters] = useState<Set<string>>(new Set());
  const supportMetadata = isRecord(settingsForm.metadata_json) ? settingsForm.metadata_json : {};
  const supportToolSettings = isRecord(supportMetadata.support_tool_settings)
    ? supportMetadata.support_tool_settings
    : {};
  const guardianToolSettings = isRecord(supportMetadata.guardian_tool_settings)
    ? supportMetadata.guardian_tool_settings
    : {};
  const whatsappActionInstructions =
    typeof supportMetadata.whatsapp_action_instructions === "string"
      ? supportMetadata.whatsapp_action_instructions
      : "";
  const [expandedActionInstructionKeys, setExpandedActionInstructionKeys] = useState<Set<string>>(new Set());
  const [showFluxiGuardianActions, setShowFluxiGuardianActions] = useState(false);
  const toggleActionInstructions = (key: string) => {
    setExpandedActionInstructionKeys((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };
  const getToolInstructions = (tool: IntegrationTool) =>
    typeof tool.metadata_json?.instructions === "string" ? tool.metadata_json.instructions : "";
  const getSupportToolSettings = (toolName: string) => {
    const value = supportToolSettings[toolName];
    return isRecord(value) ? value : {};
  };
  const getGuardianToolSettings = (toolName: string) => {
    const value = guardianToolSettings[toolName];
    return isRecord(value) ? value : {};
  };
  const supportActionEnabled = (toolName: string) => getSupportToolSettings(toolName).enabled !== false;
  const guardianActionEnabled = (toolName: string) => getGuardianToolSettings(toolName).enabled !== false;
  const supportActionInstructions = (toolName: string) => {
    const value = getSupportToolSettings(toolName).instructions;
    return typeof value === "string" ? value : "";
  };
  const guardianActionInstructions = (toolName: string) => {
    const value = getGuardianToolSettings(toolName).instructions;
    return typeof value === "string" ? value : "";
  };
  const saveSupportToolSetting = (toolName: string, patch: Record<string, unknown>) => {
    const currentToolSettings = getSupportToolSettings(toolName);
    onSettingsFormChange({
      ...settingsForm,
      metadata_json: {
        ...supportMetadata,
        support_tool_settings: {
          ...supportToolSettings,
          [toolName]: {
            ...currentToolSettings,
            ...patch,
          },
        },
      },
    });
  };
  const saveGuardianToolSetting = (toolName: string, patch: Record<string, unknown>) => {
    const currentToolSettings = getGuardianToolSettings(toolName);
    onSettingsFormChange({
      ...settingsForm,
      metadata_json: {
        ...supportMetadata,
        guardian_tool_settings: {
          ...guardianToolSettings,
          [toolName]: {
            ...currentToolSettings,
            ...patch,
          },
        },
      },
    });
  };
  const saveWhatsAppInstructions = (value: string) => {
    const next = value;
    if (next === whatsappActionInstructions) return;
    onSettingsFormChange({
      ...settingsForm,
      metadata_json: {
        ...supportMetadata,
        whatsapp_action_instructions: next,
      },
    });
  };
  const saveIntegrationToolInstructions = (connection: IntegrationConnection | null, tool: IntegrationTool, value: string) => {
    if (!connection) return;
    const next = value;
    if (next === getToolInstructions(tool)) return;
    onUpdateTool(connection.id, tool, {
      metadata_json: {
        ...(isRecord(tool.metadata_json) ? tool.metadata_json : {}),
        instructions: next,
      },
    });
  };
  const actionIntegrationFilters = [
    { key: "whatsapp", label: "WhatsApp", count: whatsappMessagingAvailable ? 1 : 0 },
    { key: "hotmart", label: "Hotmart", count: permittedHotmartTools.length },
    { key: "dropi", label: "Dropi", count: permittedDropiTools.length },
  ];
  const integrationActionCount = actionIntegrationFilters.reduce((sum, filter) => sum + filter.count, 0);
  const totalActionCount = integrationActionCount + supportToolActions.length + guardianToolActions.length;
  const filterMatches = (key: string) => activeIntegrationFilters.size === 0 || activeIntegrationFilters.has(key);
  const toggleIntegrationFilter = (key: string) => {
    setActiveIntegrationFilters((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };
  const showWhatsAppAction =
    integrationScope === "all" && whatsappMessagingAvailable && filterMatches("whatsapp");
  const filteredHotmartTools =
    integrationScope === "otras" ? [] : filterMatches("hotmart") ? permittedHotmartTools : [];
  const filteredDropiTools =
    integrationScope === "hotmart" ? [] : filterMatches("dropi") ? permittedDropiTools : [];
  const visibleIntegrationActionCount =
    (showWhatsAppAction ? 1 : 0) + filteredHotmartTools.length + filteredDropiTools.length;
  const visibleActionCount =
    visibleIntegrationActionCount +
    (integrationScope === "all" && showFluxiGuardianActions ? supportToolActions.length + guardianToolActions.length : 0);
  const scopedTitle =
    integrationScope === "hotmart"
      ? "Acciones de Hotmart"
      : integrationScope === "otras"
        ? "Otras integraciones"
        : "Acciones habilitadas para la IA";
  const scopedDescription =
    integrationScope === "hotmart"
      ? "Accesos y compras de Hotmart resueltos por chat"
      : integrationScope === "otras"
        ? "Acciones disponibles según otras integraciones conectadas."
        : "Estas son las acciones que el asistente de IA puede hacer según las integraciones conectadas.";

  return (
    <ScrollSurface>
      <div className={integrationScope === "all" ? "mx-auto max-w-5xl" : "mx-auto max-w-5xl pb-2"}>
        {integrationScope === "all" ? (
          <>
        <h3 className="text-sm font-semibold text-gray-950">{scopedTitle}</h3>
        <p className="mt-1 text-sm leading-6 text-gray-500">{scopedDescription}</p>
        <div className="mt-4 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setActiveIntegrationFilters(new Set())}
            className={`inline-flex h-8 items-center gap-2 rounded-full px-3 text-sm font-semibold transition ${
              activeIntegrationFilters.size === 0
                ? "bg-gray-900 text-white"
                : "bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-900"
            }`}
          >
            Todas
            <span className={`text-xs ${activeIntegrationFilters.size === 0 ? "text-white/70" : "text-gray-400"}`}>
              {integrationActionCount}
            </span>
          </button>
          {actionIntegrationFilters.map((filter) => {
            const active = activeIntegrationFilters.has(filter.key);
            return (
              <button
                key={filter.key}
                type="button"
                disabled={filter.count === 0}
                onClick={() => toggleIntegrationFilter(filter.key)}
                className={`inline-flex h-8 items-center gap-2 rounded-full px-3 text-sm font-semibold transition disabled:cursor-not-allowed disabled:opacity-45 ${
                  active
                    ? "bg-gray-900 text-white"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-900"
                }`}
              >
                {filter.label}
                <span className={`text-xs ${active ? "text-white/70" : "text-gray-400"}`}>{filter.count}</span>
              </button>
            );
          })}
        </div>
          </>
        ) : (
          <>
            <h3 className="text-sm font-semibold text-gray-950">{scopedTitle}</h3>
            <p className="mt-1 text-sm leading-6 text-gray-500">{scopedDescription}</p>
          </>
        )}
        <div className="mt-4 grid gap-3">
          {showWhatsAppAction ? (
            <article
              role="button"
              tabIndex={0}
              aria-expanded={expandedActionInstructionKeys.has("whatsapp")}
              onClick={() => toggleActionInstructions("whatsapp")}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  toggleActionInstructions("whatsapp");
                }
              }}
              className="cursor-pointer rounded-xl bg-gray-50 p-4 transition hover:bg-gray-100"
            >
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-gray-950">Enviar mensajes por WhatsApp</p>
                  <p className="mt-1 text-sm leading-5 text-gray-500">
                    WhatsApp · Permite que la IA responda conversaciones y envíe mensajes dentro de la ventana disponible.
                  </p>
                </div>
                <div className="grid w-[180px] shrink-0 grid-cols-[minmax(0,1fr)_32px] items-center gap-2 justify-items-end">
                  <div onClick={(event) => event.stopPropagation()} onKeyDown={(event) => event.stopPropagation()}>
                    <span
                      className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ${
                        account?.status === "connected" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"
                      }`}
                    >
                      {formatWhatsAppStatus(account?.status)}
                    </span>
                  </div>
                  <ChevronDown
                    className={`size-4 text-gray-500 transition ${
                      expandedActionInstructionKeys.has("whatsapp") ? "rotate-180 text-gray-950" : ""
                    }`}
                  />
                </div>
              </div>
              {expandedActionInstructionKeys.has("whatsapp") ? (
                <div className="mt-3 space-y-3" onClick={(event) => event.stopPropagation()}>
                  <button
                    type="button"
                    onClick={() => onOpenIntegrationDetail("whatsapp")}
                    className="inline-flex h-9 items-center gap-2 rounded-full border border-gray-200 bg-white px-3 text-sm font-semibold text-gray-800 hover:bg-gray-50"
                  >
                    Configurar en Integraciones
                    <ChevronRight className="size-4" />
                  </button>
                  <label className="block">
                    <span className="mb-1 block text-xs font-semibold text-gray-600">Instrucciones para la IA</span>
                    <textarea
                      value={whatsappActionInstructions}
                      onChange={(event) => saveWhatsAppInstructions(event.target.value)}
                      rows={2}
                      placeholder="Cuándo usar WhatsApp, cuándo pausar, qué evitar al responder..."
                      className="w-full resize-y rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm outline-none focus:border-[#4368E5]"
                    />
                  </label>
                </div>
              ) : null}
            </article>
          ) : null}

          {filteredHotmartTools.map((tool) => (
            (() => {
              const instructionKey = `hotmart:${tool.tool_name}`;
              const expanded = expandedActionInstructionKeys.has(instructionKey);
              return (
            <article
              key={`allowed-${tool.tool_name}`}
              role="button"
              tabIndex={0}
              aria-expanded={expanded}
              onClick={() => toggleActionInstructions(instructionKey)}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  toggleActionInstructions(instructionKey);
                }
              }}
              className="cursor-pointer rounded-xl bg-gray-50 p-4 transition hover:bg-gray-100"
            >
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-gray-950">{tool.display_name}</p>
                  <p className="mt-1 text-sm leading-5 text-gray-500">Hotmart · {tool.description}</p>
                </div>
                <div className="grid w-[150px] shrink-0 grid-cols-[minmax(0,1fr)_32px] items-center gap-2 justify-items-end">
                  <div onClick={(event) => event.stopPropagation()} onKeyDown={(event) => event.stopPropagation()}>
                    <ToggleSwitch
                      checked={tool.enabled}
                      label={tool.enabled ? "Activa" : "Pausada"}
                      onChange={(enabled) => hotmartConnection && onUpdateTool(hotmartConnection.id, tool, { enabled })}
                    />
                  </div>
                  <ChevronDown
                    className={`size-4 text-gray-500 transition ${
                      expanded ? "rotate-180 text-gray-950" : ""
                    }`}
                  />
                </div>
              </div>
              {expanded ? (
                <label className="mt-3 block" onClick={(event) => event.stopPropagation()}>
                  <span className="mb-1 block text-xs font-semibold text-gray-600">Instrucciones para la IA</span>
                  <textarea
                    value={getToolInstructions(tool)}
                    onChange={(event) => saveIntegrationToolInstructions(hotmartConnection, tool, event.target.value)}
                    rows={2}
                    placeholder="Cuándo usar esta acción, cuándo pedir aprobación, qué condiciones revisar..."
                    className="w-full resize-y rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm outline-none focus:border-[#4368E5]"
                  />
                </label>
              ) : null}
            </article>
              );
            })()
          ))}

          {filteredDropiTools.map((tool) => {
            const instructionKey = `dropi:${tool.tool_name}`;
            const expanded = expandedActionInstructionKeys.has(instructionKey);
            return (
              <article
                key={`allowed-dropi-${tool.tool_name}`}
                role="button"
                tabIndex={0}
                aria-expanded={expanded}
                onClick={() => toggleActionInstructions(instructionKey)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    toggleActionInstructions(instructionKey);
                  }
                }}
                className="cursor-pointer rounded-xl bg-gray-50 p-4 transition hover:bg-gray-100"
              >
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-gray-950">{tool.display_name}</p>
                    <p className="mt-1 text-sm leading-5 text-gray-500">Dropi · {tool.description}</p>
                  </div>
                  <div className="grid w-[150px] shrink-0 grid-cols-[minmax(0,1fr)_32px] items-center gap-2 justify-items-end">
                    <div onClick={(event) => event.stopPropagation()} onKeyDown={(event) => event.stopPropagation()}>
                      <ToggleSwitch
                        checked={tool.enabled}
                        label={tool.enabled ? "Activa" : "Pausada"}
                        onChange={(enabled) => dropiConnection && onUpdateTool(dropiConnection.id, tool, { enabled })}
                      />
                    </div>
                    <ChevronDown className={`size-4 text-gray-500 transition ${expanded ? "rotate-180 text-gray-950" : ""}`} />
                  </div>
                </div>
                {expanded ? (
                  <label className="mt-3 block" onClick={(event) => event.stopPropagation()}>
                    <span className="mb-1 block text-xs font-semibold text-gray-600">Instrucciones para la IA</span>
                    <textarea
                      value={getToolInstructions(tool)}
                      onChange={(event) => saveIntegrationToolInstructions(dropiConnection, tool, event.target.value)}
                      rows={2}
                      placeholder="Cuándo consultar la calificación, cuándo escalar a humano, qué evitar..."
                      className="w-full resize-y rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm outline-none focus:border-[#4368E5]"
                    />
                  </label>
                ) : null}
              </article>
            );
          })}

          {integrationScope === "all" ? (
          <button
            type="button"
            onClick={() => setShowFluxiGuardianActions((open) => !open)}
            className="flex items-center gap-1 text-sm text-gray-500 transition hover:text-gray-700"
          >
            Ver acciones de Fluxi y guardián
            <ChevronDown className={`size-4 transition-transform ${showFluxiGuardianActions ? "rotate-180" : ""}`} />
          </button>
          ) : null}

          {integrationScope === "all" && showFluxiGuardianActions
            ? supportToolActions.map((action) => {
                const instructionKey = `support:${action.key}`;
                const expanded = expandedActionInstructionKeys.has(instructionKey);
                const enabled = supportActionEnabled(action.key);
                return (
                  <article
                    key={`support-action-${action.key}`}
                    role="button"
                    tabIndex={0}
                    aria-expanded={expanded}
                    onClick={() => toggleActionInstructions(instructionKey)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        toggleActionInstructions(instructionKey);
                      }
                    }}
                    className="cursor-pointer rounded-xl bg-gray-50 p-4 transition hover:bg-gray-100"
                  >
                    <div className="flex flex-wrap items-center justify-between gap-4">
                      <div className="min-w-0 flex-1">
                        <p className="font-semibold text-gray-950">{action.title}</p>
                        <p className="mt-1 text-sm leading-5 text-gray-500">{action.description}</p>
                      </div>
                      <div className="grid w-[150px] shrink-0 grid-cols-[minmax(0,1fr)_32px] items-center gap-2 justify-items-end">
                        <div onClick={(event) => event.stopPropagation()} onKeyDown={(event) => event.stopPropagation()}>
                          <ToggleSwitch
                            checked={enabled}
                            label={enabled ? "Activa" : "Pausada"}
                            onChange={(nextEnabled) => saveSupportToolSetting(action.key, { enabled: nextEnabled })}
                          />
                        </div>
                        <ChevronDown className={`size-4 text-gray-500 transition ${expanded ? "rotate-180 text-gray-950" : ""}`} />
                      </div>
                    </div>
                    {expanded ? (
                      <label className="mt-3 block" onClick={(event) => event.stopPropagation()}>
                        <span className="mb-1 block text-xs font-semibold text-gray-600">Instrucciones para la IA</span>
                        <textarea
                          value={supportActionInstructions(action.key)}
                          onChange={(event) =>
                            saveSupportToolSetting(action.key, { instructions: event.target.value })
                          }
                          rows={2}
                          placeholder={action.placeholder}
                          className="w-full resize-y rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm outline-none focus:border-[#4368E5]"
                        />
                      </label>
                    ) : null}
                  </article>
                );
              })
            : null}

          {integrationScope === "all" && showFluxiGuardianActions
            ? guardianToolActions.map((action) => {
                const instructionKey = `guardian:${action.key}`;
                const expanded = expandedActionInstructionKeys.has(instructionKey);
                const enabled = guardianActionEnabled(action.key);
                return (
                  <article
                    key={`guardian-action-${action.key}`}
                    role="button"
                    tabIndex={0}
                    aria-expanded={expanded}
                    onClick={() => toggleActionInstructions(instructionKey)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        toggleActionInstructions(instructionKey);
                      }
                    }}
                    className="cursor-pointer rounded-xl bg-gray-50 p-4 transition hover:bg-gray-100"
                  >
                    <div className="flex flex-wrap items-center justify-between gap-4">
                      <div className="min-w-0 flex-1">
                        <p className="font-semibold text-gray-950">{action.title}</p>
                        <p className="mt-1 text-sm leading-5 text-gray-500">{action.description}</p>
                      </div>
                      <div className="grid w-[150px] shrink-0 grid-cols-[minmax(0,1fr)_32px] items-center gap-2 justify-items-end">
                        <div onClick={(event) => event.stopPropagation()} onKeyDown={(event) => event.stopPropagation()}>
                          <ToggleSwitch
                            checked={enabled}
                            label={enabled ? "Activa" : "Pausada"}
                            onChange={(nextEnabled) => saveGuardianToolSetting(action.key, { enabled: nextEnabled })}
                          />
                        </div>
                        <ChevronDown className={`size-4 text-gray-500 transition ${expanded ? "rotate-180 text-gray-950" : ""}`} />
                      </div>
                    </div>
                    {expanded ? (
                      <label className="mt-3 block" onClick={(event) => event.stopPropagation()}>
                        <span className="mb-1 block text-xs font-semibold text-gray-600">Instrucciones para la IA</span>
                        <textarea
                          value={guardianActionInstructions(action.key)}
                          onChange={(event) =>
                            saveGuardianToolSetting(action.key, { instructions: event.target.value })
                          }
                          rows={2}
                          placeholder={action.placeholder}
                          className="w-full resize-y rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm outline-none focus:border-[#4368E5]"
                        />
                      </label>
                    ) : null}
                  </article>
                );
              })
            : null}

          {integrationActionCount === 0 ? (
            <div className="rounded-xl bg-gray-50 p-4 text-sm text-gray-500">
              Conecta WhatsApp, Hotmart o Dropi para habilitar acciones del asistente.
            </div>
          ) : null}
          {integrationActionCount > 0 && visibleIntegrationActionCount === 0 ? (
            <div className="rounded-xl bg-gray-50 p-4 text-sm text-gray-500">
              No hay acciones para los filtros seleccionados.
            </div>
          ) : null}
        </div>
      </div>
    </ScrollSurface>
  );
};

const AutomationsTab = ({
  automations,
  validationError,
  approvedTemplates,
  pickerTemplates,
  onChange,
  mode = "general",
}: {
  automations: AutomationDraft[];
  validationError?: string | null;
  approvedTemplates: WhatsAppTemplate[];
  /** Todas las plantillas no-guardian (incluye PENDING), para mostrar su estado en el picker. */
  pickerTemplates: WhatsAppTemplate[];
  onChange: (rows: AutomationDraft[]) => void;
  mode?: "general" | "logistics";
}) => {
  const { catalogProducts, catalogLoading } = useFluxiProductsCatalog();
  const activeProducts = catalogProducts;
  const allProductsOption = "__all_products__";
  const [expandedAutomationId, setExpandedAutomationId] = useState<string | null>(null);
  const [expandedGroupKey, setExpandedGroupKey] = useState<string | null>(null);
  const [automationRuns, setAutomationRuns] = useState<SupportAutomationRun[]>([]);
  const [runsLoading, setRunsLoading] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const loadAutomationRuns = useCallback(async () => {
    setRunsLoading(true);
    try {
      const rows = await chatbotV2Service.listSupportAutomationRuns({ limit: 20 });
      setAutomationRuns(rows);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setRunsLoading(false);
    }
  }, []);
  useEffect(() => {
    if (mode === "logistics") return;
    void loadAutomationRuns();
  }, [loadAutomationRuns, mode]);
  const productKey = (product: Product) => String(product.id);
  const toggleAutomationExpansion = (id: string) => {
    setExpandedAutomationId((current) => (current === id ? null : id));
  };
	  const updateAutomation = (id: string, patch: Partial<AutomationDraft>) => {
	    onChange(automations.map((automation) => (automation.id === id ? { ...automation, ...patch } : automation)));
	  };
	  const setAutomationEnabled = (automation: AutomationDraft, enabled: boolean) => {
	    if (!enabled) {
	      updateAutomation(automation.id, { enabled: false });
	      return;
	    }
	    const issue = automationActivationIssues(automation)[0];
	    if (issue) {
	      toast.error(issue);
	      return;
	    }
	    updateAutomation(automation.id, { enabled: true, ephemeral: false });
	  };
  const commitAutomation = (id: string, patch: Partial<AutomationDraft>) => {
    const next = automations.map((automation) => {
      if (automation.id !== id) return automation;
      const merged = { ...automation, ...patch };
      return isAutomationDraftCommitted(merged) ? { ...merged, ephemeral: false } : merged;
    });
    onChange(next);
  };
  const selectProduct = (automation: AutomationDraft, value: string) => {
    if (!value) return;
    if (value === allProductsOption) {
      commitAutomation(automation.id, { productIds: [], productScope: "all" });
      return;
    }
    const selected = new Set(automation.productIds || []);
    selected.add(value);
    commitAutomation(automation.id, { productIds: Array.from(selected), productScope: "selected" });
  };
  const removeSelectedProduct = (automation: AutomationDraft, productId: string) => {
    if (productId === allProductsOption) {
      commitAutomation(automation.id, { productIds: [], productScope: "selected" });
      return;
    }
    const productIds = (automation.productIds || []).filter((id) => id !== productId);
    commitAutomation(automation.id, { productIds, productScope: "selected" });
  };
  const productLabel = (productId: string) =>
    activeProducts.find((product) => productKey(product) === productId)?.name || productId;
  const formatAutomationRunReason = (run: SupportAutomationRun) => {
    const reason = run.result_json?.reason || run.result_json?.skip_reason || run.error_message_sanitized;
    return typeof reason === "string" && reason.trim() ? reason : run.status;
  };
  const stateLabel = (value: string) => automationStateTargets.find((item) => item.value === value)?.label || humanize(value);
  const logisticsStatusLabel = (value: string) =>
    automationLogisticsStatusTargets.find((item) => item.value === value)?.label || humanize(value);
  const automationActionValue = (automation: AutomationDraft) =>
    automation.action === "set_ai_responses"
      ? automation.aiStateTarget === "paused"
        ? "pause_ai_responses"
        : "activate_ai_responses"
      : automation.action;
  const followupActionValue = (automation: AutomationDraft) =>
    automation.followupAction === "set_ai_responses"
      ? automation.followupAiStateTarget === "paused"
        ? "pause_ai_responses"
        : "activate_ai_responses"
      : automation.followupAction;
  const automationActionLabel = (value: string) =>
    automationActions.find((item) => item.value === value)?.label || humanize(value);
  const applyActionValue = (id: string, value: string, field: "action" | "followupAction") => {
    if (value === "activate_ai_responses" || value === "pause_ai_responses") {
      const stateTarget = value === "pause_ai_responses" ? "paused" : "active";
      commitAutomation(id, field === "action"
        ? { action: "set_ai_responses", aiStateTarget: stateTarget }
        : { followupAction: "set_ai_responses", followupAiStateTarget: stateTarget });
      return;
    }
    commitAutomation(id, { [field]: value } as Partial<AutomationDraft>);
  };
  const addAutomation = () => {
    const id = `automation-${Date.now()}`;
    const next: AutomationDraft[] = [
      {
        id,
        title: "",
        trigger: mode === "logistics" ? "logistics_event" : "",
        triggerStateTarget: "",
        triggerAiStateTarget: "active",
        logisticsStatusTarget: mode === "logistics" ? "guide_generated" : "",
        action: mode === "logistics" ? "send_template" : "",
        templateName: "",
        stateTarget: "",
        aiStateTarget: "active",
        // En Logística cada card es un grupo por productos: la nueva arranca con su propio
        // segmento (el grupo "Todos los productos" ya existe con los avisos default).
        productScope: "selected",
        productIds: [],
        enabled: false,
        ephemeral: true,
        followupEnabled: false,
        followupDelayValue: 30,
        followupDelayUnit: "minutes",
        followupAction: "",
        followupTemplateName: "",
        followupStateTarget: "",
        followupAiStateTarget: "active",
      },
      ...automations,
    ];
    onChange(next);
    setExpandedAutomationId(id);
  };

  const deleteAutomation = (id: string) => {
    if (id.startsWith("group:")) {
      const key = id.slice(6);
      onChange(automations.filter((automation) => logisticsGroupKey(automation) !== key));
      setDeleteConfirmId(null);
      if (expandedGroupKey === key) setExpandedGroupKey(null);
      return;
    }
    onChange(automations.filter((automation) => automation.id !== id));
    setDeleteConfirmId(null);
    if (expandedAutomationId === id) setExpandedAutomationId(null);
  };

  // ---- Logística en cajas (Julian): cada caja agrupa varias reglas. Las cajas se identifican
  // por groupKey persistido en el frontend_payload de cada regla (hermano de title); las reglas
  // viejas sin groupKey se agrupan como antes por su segmento de productos.
  const logisticsSegmentKey = (automation: AutomationDraft) =>
    (automation.productScope || "selected") === "all"
      ? "__all__"
      : [...(automation.productIds || [])].sort().join(",") || "__none__";

  const logisticsGroupKey = (automation: AutomationDraft) =>
    automation.groupKey?.trim() || `legacy:${logisticsSegmentKey(automation)}`;

  type LogisticsGroup = { key: string; items: AutomationDraft[]; title: string };

  // Cajas derivadas del listado + título mostrado: groupTitle persistido, o secuencial
  // "Avisos de envío N" para las cajas viejas sin groupKey.
  const buildLogisticsGroups = (): LogisticsGroup[] => {
    const groups: LogisticsGroup[] = [];
    automations.forEach((automation) => {
      const key = logisticsGroupKey(automation);
      const found = groups.find((group) => group.key === key);
      if (found) {
        found.items.push(automation);
        return;
      }
      groups.push({ key, items: [automation], title: "" });
    });
    let legacyNumber = 0;
    groups.forEach((group) => {
      const explicit = group.items.map((item) => item.groupTitle?.trim()).find(Boolean);
      if (explicit) {
        group.title = explicit;
        return;
      }
      legacyNumber += 1;
      group.title = `Avisos de envío ${legacyNumber}`;
    });
    return groups;
  };

  // Identidad persistida de la caja: si es una caja vieja sin groupKey, se le asigna uno
  // (con su título mostrado) en el primer cambio estructural, junto con el cambio.
  const ensureGroupIdentity = (group: LogisticsGroup) => {
    const existingKey = group.items.map((item) => item.groupKey?.trim()).find(Boolean);
    if (existingKey) {
      return {
        groupKey: existingKey,
        groupTitle: group.items.map((item) => item.groupTitle?.trim()).find(Boolean) || group.title,
        patch: null as Partial<AutomationDraft> | null,
      };
    }
    const groupKey = `logistics-group-${Date.now()}`;
    return { groupKey, groupTitle: group.title, patch: { groupKey, groupTitle: group.title } };
  };

  const blankLogisticsRule = (): AutomationDraft => ({
    id: `automation-${Date.now()}`,
    title: "",
    trigger: "logistics_event",
    triggerStateTarget: "",
    triggerAiStateTarget: "active",
    logisticsStatusTarget: "",
    action: "send_template",
    templateName: "",
    stateTarget: "",
    aiStateTarget: "active",
    productScope: "selected",
    productIds: [],
    enabled: false,
    ephemeral: true,
    followupEnabled: false,
    followupDelayValue: 30,
    followupDelayUnit: "minutes",
    followupAction: "",
    followupTemplateName: "",
    followupStateTarget: "",
    followupAiStateTarget: "active",
  });

  // "+ Agregar avisos logísticos" (Julian): crea una CAJA nueva (no una regla en la caja
  // anterior) con groupKey único y título secuencial persistido.
  const addLogisticsGroup = () => {
    const groups = buildLogisticsGroups();
    const groupKey = `logistics-group-${Date.now()}`;
    const groupTitle = nextSequentialFlowTitle(
      "Avisos de envío",
      groups.map((group) => group.title)
    );
    onChange([{ ...blankLogisticsRule(), groupKey, groupTitle }, ...automations]);
    setExpandedGroupKey(groupKey);
  };

  const addRuleToGroup = (group: LogisticsGroup) => {
    const sample = group.items[0];
    const identity = ensureGroupIdentity(group);
    const ids = new Set(group.items.map((item) => item.id));
    const newRule: AutomationDraft = {
      ...sample,
      id: `automation-${Date.now()}`,
      title: "",
      groupKey: identity.groupKey,
      groupTitle: identity.groupTitle,
      logisticsStatusTarget: "",
      templateName: "",
      enabled: false,
      ephemeral: true,
    };
    onChange([
      ...automations.map((automation) =>
        identity.patch && ids.has(automation.id) ? { ...automation, ...identity.patch } : automation
      ),
      newRule,
    ]);
    if (identity.patch) setExpandedGroupKey(identity.groupKey);
  };

  // Crea las reglas recomendadas del paquete en el orden del dropdown de estados
  // (guía generada → en camino → entregado → devuelto → cancelado), activas y con la
  // plantilla recomendada. Los estados que ya existen en la caja se saltan.
  const addRecommendedRulesToGroup = (group: LogisticsGroup) => {
    const sample = group.items[0];
    const identity = ensureGroupIdentity(group);
    const ids = new Set(group.items.map((item) => item.id));
    const existingStates = new Set(group.items.map((item) => item.logisticsStatusTarget).filter(Boolean));
    const stamp = Date.now();
    const newRules: AutomationDraft[] = automationLogisticsStatusTargets
      .filter((state) => LOGISTICS_STATE_RECOMMENDED_TEMPLATE[state.value] && !existingStates.has(state.value))
      .map((state, index) => ({
        ...sample,
        id: `automation-${stamp + index}`,
        title: `Aviso de envío: ${state.label}`,
        groupKey: identity.groupKey,
        groupTitle: identity.groupTitle,
        logisticsStatusTarget: state.value,
        templateName: LOGISTICS_STATE_RECOMMENDED_TEMPLATE[state.value],
        action: "send_template",
        enabled: true,
        ephemeral: false,
      }));
    if (!newRules.length) {
      toast("Esta caja ya tiene todos los avisos recomendados.");
      return;
    }
    // Quita las reglas VACÍAS del grupo (sin estado): la caja nace con una en blanco
    // y "Agregar recomendados" no debe dejarla colgando (Julián). Las de otros grupos
    // y las ya configuradas se conservan.
    const isBlankRule = (a: AutomationDraft) => !a.logisticsStatusTarget && !a.templateName;
    onChange([
      ...automations
        .filter((automation) => !(ids.has(automation.id) && isBlankRule(automation)))
        .map((automation) =>
          identity.patch && ids.has(automation.id) ? { ...automation, ...identity.patch } : automation
        ),
      ...newRules,
    ]);
    if (identity.patch) setExpandedGroupKey(identity.groupKey);
  };

  const updateGroupProducts = (group: LogisticsGroup, patch: Partial<AutomationDraft>) => {
    const identity = ensureGroupIdentity(group);
    const ids = new Set(group.items.map((item) => item.id));
    onChange(
      automations.map((automation) =>
        ids.has(automation.id) ? { ...automation, ...(identity.patch || {}), ...patch } : automation
      )
    );
    // Con groupKey la caja no se re-identifica al cambiar productos; queda abierta.
    setExpandedGroupKey(identity.groupKey);
  };

  // En Logística no hay campo de título por regla: al activar autotitulamos con el estado
  // para que la validación de guardado (que exige título en reglas activas) no bloquee.
  const logisticsAutoTitle = (automation: AutomationDraft) => {
    const status = logisticsStatusLabel(automation.logisticsStatusTarget || "").trim();
    return status ? `Aviso de envío: ${status}` : "Aviso de envío";
  };

  // Toggle a NIVEL del grupo (Julian): apagar pausa TODAS las reglas del grupo;
  // prender las activa todas.
  const setGroupEnabled = (groupItems: AutomationDraft[], enabled: boolean) => {
    const ids = new Set(groupItems.map((item) => item.id));
    onChange(
      automations.map((automation) => {
        if (!ids.has(automation.id)) return automation;
        if (!enabled) return { ...automation, enabled: false };
        return {
          ...automation,
          title: automation.title.trim() ? automation.title : logisticsAutoTitle(automation),
          enabled: true,
          ephemeral: false,
        };
      })
    );
  };

  // Sin toggle por regla, una regla nueva en un grupo activo quedaría pausada para siempre.
  // Al completarla (estado + plantilla) hereda la activación del grupo.
  const commitLogisticsRule = (rule: AutomationDraft, patch: Partial<AutomationDraft>) => {
    const merged = { ...rule, ...patch };
    const groupOn = automations.some(
      (automation) =>
        automation.id !== rule.id &&
        logisticsGroupKey(automation) === logisticsGroupKey(rule) &&
        automation.enabled
    );
    const complete = Boolean(merged.logisticsStatusTarget && merged.templateName.trim());
    if (groupOn && complete && !merged.enabled) {
      commitAutomation(rule.id, {
        ...patch,
        title: merged.title.trim() ? merged.title : logisticsAutoTitle(merged),
        enabled: true,
      });
      return;
    }
    commitAutomation(rule.id, patch);
  };

  const renderLogisticsGroups = () => {
    const groups = buildLogisticsGroups();
    // Un producto no puede estar en dos CAJAS de avisos logísticos (Julian): las opciones
    // usadas por otra caja habilitada salen gray-out con "Ya está en '<caja>'".
    const groupProductUsage = (currentKey: string): Record<string, string> => {
      const usage: Record<string, string> = {};
      groups.forEach((other) => {
        if (other.key === currentKey) return;
        if (!other.items.some((item) => item.enabled)) return;
        const sample = other.items[0];
        if ((sample.productScope || "selected") === "all") return;
        (sample.productIds || []).forEach((productId) => {
          if (!usage[productId]) usage[productId] = other.title;
        });
      });
      return usage;
    };
    return groups.map((group) => {
      const first = group.items[0];
      const scopeAll = (first.productScope || "selected") === "all";
      const groupProductIds = scopeAll ? [] : first.productIds || [];
      const isOpen = expandedGroupKey === group.key;
      const enabledCount = group.items.filter((item) => item.enabled).length;
      const groupEnabled = enabledCount > 0;
      // El botón "Agregar todos los recomendados" solo sale si FALTA alguno (Julián):
      // si el grupo ya tiene todos los estados recomendados, no tiene sentido mostrarlo.
      const groupStates = new Set(group.items.map((item) => item.logisticsStatusTarget).filter(Boolean));
      const missingRecommendedCount = automationLogisticsStatusTargets.filter(
        (state) => LOGISTICS_STATE_RECOMMENDED_TEMPLATE[state.value] && !groupStates.has(state.value)
      ).length;
      return (
        <article
          key={group.key}
          className={`relative rounded-xl border bg-gray-50 ${isOpen ? "border-gray-950" : "border-transparent"}`}
        >
          {/* Header estilo EditableActionCardHeader; el título es el groupTitle de la caja. */}
          <div
            role="button"
            tabIndex={0}
            aria-expanded={isOpen}
            onClick={() => setExpandedGroupKey((current) => (current === group.key ? null : group.key))}
            onKeyDown={(event) => {
              if (event.key !== "Enter" && event.key !== " ") return;
              event.preventDefault();
              setExpandedGroupKey((current) => (current === group.key ? null : group.key));
            }}
            className="flex min-h-[68px] cursor-pointer items-center justify-between gap-3 px-4 py-3"
          >
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold text-gray-950">{group.title}</p>
            </div>
            <div className="flex shrink-0 items-center gap-2" onClick={(event) => event.stopPropagation()}>
              {/* Envíos fallidos NO se muestran en config (Julián): viven en la
                  conversación (nota ❌ + puntico rojo + filtro Error). */}
              <ToggleSwitch
                checked={groupEnabled}
                label={groupEnabled ? "Activo" : "Pausado"}
                onChange={(enabled) => setGroupEnabled(group.items, enabled)}
              />
              <button
                type="button"
                onClick={() => setDeleteConfirmId(`group:${group.key}`)}
                className="inline-flex size-9 items-center justify-center rounded-full text-gray-400 transition hover:bg-white hover:text-red-600"
                title="Eliminar grupo"
              >
                <Trash2 className="size-4" />
              </button>
              <button
                type="button"
                onClick={() => setExpandedGroupKey((current) => (current === group.key ? null : group.key))}
                className="inline-flex size-9 items-center justify-center rounded-full text-gray-500 transition hover:bg-white hover:text-gray-900"
                title={isOpen ? "Contraer" : "Expandir"}
              >
                <ChevronDown className={`size-4 transition ${isOpen ? "rotate-180" : ""}`} />
              </button>
            </div>
          </div>
          {isOpen ? (
            <div className="border-t border-gray-100 px-4 pb-4">
              {/* Patrón estándar de "Productos enlazados" (Julian): dropdown arriba +
                  cards horizontales a ancho completo. Misma semántica scopeAll/groupProductIds. */}
              <div className="mt-5">
                <p className="text-sm font-semibold text-gray-950">Productos donde aplica</p>
                <p className="mt-1 text-xs leading-5 text-gray-500">Aplica a todas las acciones de este grupo.</p>
                <div className="mt-3">
                  <span className="mb-1 block text-xs font-semibold text-gray-600">Agregar producto</span>
                  <ProductPickerSelect
                    includeAllOption
                    allOptionLabel="Todos los productos"
                    excludeIds={scopeAll ? [] : groupProductIds}
                    disabledReasonById={groupProductUsage(group.key)}
                    placeholder="Seleccionar producto"
                    onSelect={(productId) => {
                      if (productId === allProductsOption) {
                        updateGroupProducts(group, { productIds: [], productScope: "all" });
                        return;
                      }
                      const selected = new Set(groupProductIds);
                      selected.add(productId);
                      const nextIds = Array.from(selected);
                      updateGroupProducts(group, { productIds: nextIds, productScope: "selected" });
                    }}
                  />
                </div>
                <div className="mt-3 grid gap-3">
                  {(scopeAll ? [allProductsOption] : groupProductIds).map((productId) => {
                    const catalogProduct =
                      productId === allProductsOption
                        ? null
                        : activeProducts.find((product) => productKey(product) === productId);
                    const chip = catalogProduct ? productPickerChipContent(catalogProduct) : null;
                    const label = productId === allProductsOption ? "Todos los productos" : productLabel(productId);
                    return (
                      <div
                        key={`${group.key}-product-${productId}`}
                        className="rounded-lg border border-gray-200 bg-white p-3"
                      >
                        <div className="flex items-center gap-3">
                          {chip?.thumbnail ? (
                            <img src={chip.thumbnail} alt="" className="size-9 shrink-0 rounded object-cover" />
                          ) : (
                            <div className="flex size-9 shrink-0 items-center justify-center rounded bg-gray-100 text-xs font-semibold text-gray-500">
                              {label.slice(0, 1).toUpperCase()}
                            </div>
                          )}
                          <div className="min-w-0 flex-1">
                            <p className="truncate text-sm font-semibold text-gray-950">{label}</p>
                            {chip?.shortCode ? (
                              <p className="truncate font-mono text-[11px] text-gray-500">{chip.shortCode}</p>
                            ) : null}
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              if (productId === allProductsOption) {
                                updateGroupProducts(group, { productIds: [], productScope: "selected" });
                                return;
                              }
                              const nextIds = groupProductIds.filter((id) => id !== productId);
                              updateGroupProducts(group, { productIds: nextIds, productScope: "selected" });
                            }}
                            className="inline-flex size-8 shrink-0 items-center justify-center rounded-full text-gray-400 transition hover:bg-gray-100 hover:text-red-600"
                            title="Quitar producto"
                          >
                            <X className="size-4" />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                  {!scopeAll && groupProductIds.length === 0 ? (
                    <p className="text-xs leading-5 text-gray-500">
                      Sin productos elegidos. Puedes agregar productos o elegir Todos los productos.
                    </p>
                  ) : null}
                </div>
              </div>

              <div className="mt-4 grid gap-3">
                {group.items.map((rule) => {
                  const recommendedForState = LOGISTICS_STATE_RECOMMENDED_TEMPLATE[rule.logisticsStatusTarget || ""];
                  const ruleTemplate = pickerTemplates.find((template) => template.name === rule.templateName);
                  const ruleTemplateBadge = ruleTemplate ? templateStateBadge(ruleTemplate) : "";
                  const ruleTemplateMissing = Boolean(rule.templateName) && !ruleTemplate;
                  return (
                  <div key={rule.id} className="rounded-lg border border-gray-200 bg-white p-3">
                    {/* La activación es del grupo (Julian): sin toggle por regla, solo caneca. */}
                    <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto]">
                      <div className="block">
                        <span className="mb-1 block text-xs font-semibold text-gray-600">Cada vez que la orden queda en</span>
                        <ModalSelect
                          value={rule.logisticsStatusTarget || ""}
                          placeholder="Elegir estado"
                          options={automationLogisticsStatusTargets.map((item) => ({ value: item.value, label: item.label }))}
                          onChange={(logisticsStatusTarget) => commitLogisticsRule(rule, { logisticsStatusTarget })}
                        />
                      </div>
                      <TemplatePickerField
                        label="Enviar plantilla"
                        value={rule.templateName}
                        templates={pickerTemplates}
                        recommendedNames={recommendedForState ? [recommendedForState] : undefined}
                        hidePreview
                        onChange={(templateName) =>
                          commitLogisticsRule(rule, { templateName, action: "send_template" })
                        }
                      />
                      <div className="flex items-start justify-end pt-6">
                        <button
                          type="button"
                          onClick={() => setDeleteConfirmId(rule.id)}
                          className="inline-flex size-8 items-center justify-center rounded-full text-gray-400 transition hover:bg-gray-100 hover:text-red-600"
                          title="Eliminar acción"
                        >
                          <Trash2 className="size-4" />
                        </button>
                      </div>
                    </div>
                    {/* Estados de plantilla visibles (spec Julian): en aprobación = informativo
                        y arranca solo; rechazada/inexistente = error claro sin adivinar. */}
                    {ruleTemplateMissing ? (
                      <p className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs leading-5 text-red-700">
                        La plantilla "{rule.templateName}" ya no existe en tu WhatsApp — elige otra.
                      </p>
                    ) : ruleTemplateBadge ? (
                      <p
                        className={`mt-3 rounded-lg border px-3 py-2 text-xs leading-5 ${
                          String(ruleTemplate?.status || "").toUpperCase() === "PENDING"
                            ? "border-amber-200 bg-amber-50 text-amber-800"
                            : "border-red-200 bg-red-50 text-red-700"
                        }`}
                      >
                        {String(ruleTemplate?.status || "").toUpperCase() === "PENDING"
                          ? "Plantilla en aprobación por Meta — los envíos arrancan solos al aprobarse."
                          : `Plantilla ${ruleTemplateBadge.toLowerCase()} — elige otra plantilla.`}
                      </p>
                    ) : null}
                    {/* Los envíos fallidos NO se muestran aquí (Julián): viven en la
                        conversación (nota ❌ + puntico rojo). En config solo alertas de
                        config, como la de plantilla no disponible de arriba. */}
                    {/* Previsualización a ancho completo de la sub-caja (Julian). */}
                    {ruleTemplate?.body_text ? (
                      <div className="mt-3">
                        <span className="mb-1 block text-xs font-semibold text-gray-600">Previsualización de la plantilla</span>
                        <div className="rounded-lg border border-dashed border-gray-200 bg-gray-50 px-3 py-2">
                          {templateHasImageHeader(ruleTemplate) ? <TemplateImageHeaderPlaceholder /> : null}
                          <p className="whitespace-pre-line text-xs leading-5 text-gray-600">{ruleTemplate.body_text}</p>
                        </div>
                      </div>
                    ) : null}
                  </div>
                  );
                })}
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={() => addRuleToGroup(group)}
                  className="inline-flex h-9 items-center gap-2 rounded-full bg-gray-100 px-4 text-sm font-semibold text-gray-800 transition hover:bg-gray-200"
                >
                  + Agregar otra acción
                </button>
                {missingRecommendedCount > 0 ? (
                  <button
                    type="button"
                    onClick={() => addRecommendedRulesToGroup(group)}
                    className="inline-flex h-9 items-center gap-2 rounded-full border border-gray-200 bg-white px-4 text-sm font-semibold text-gray-800 transition hover:bg-gray-50"
                  >
                    Agregar todos los recomendados
                  </button>
                ) : null}
              </div>
            </div>
          ) : null}
        </article>
      );
    });
  };

  return (
    <ScrollSurface>
      <div className="mx-auto max-w-5xl">
        {mode === "logistics" ? (
          <FlowListHeader
            title="Avisos logísticos"
            subtitle="Avisos automáticos al cliente según avanza su envío"
            addLabel="+ Agregar avisos logísticos"
            onAdd={addLogisticsGroup}
          />
        ) : (
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="max-w-2xl text-sm leading-6 text-gray-500">
              Crea reglas simples para que Fluxi reaccione cuando ocurra algo: enviar un mensaje, cambiar un estado o
              activar y pausar respuestas IA.
            </p>
            <button
              type="button"
              onClick={addAutomation}
              className="inline-flex h-9 shrink-0 items-center justify-center rounded-full bg-gray-100 px-4 text-sm font-semibold text-gray-800 transition hover:bg-gray-200"
            >
              + Agregar
            </button>
          </div>
        )}
        {validationError ? (
          <div className="mt-4 rounded-xl bg-red-50 px-4 py-3 text-sm leading-5 text-red-700">
            {validationError}
          </div>
        ) : null}

        <div className="mt-5 grid gap-3">
          {automations.length === 0 ? (
            <div className="rounded-xl bg-gray-50 px-4 py-5 text-sm leading-6 text-gray-500">
              {mode === "logistics"
                ? "No hay avisos logísticos creados. Agrega una caja de avisos para enviar plantillas cuando Dropi reporte un avance."
                : "No hay automatizaciones creadas. Agrega una regla para definir qué pasa cuando llega un evento."}
            </div>
          ) : null}
          {mode === "logistics"
            ? renderLogisticsGroups()
            : automations.map((automation) => {
            const productScope = automation.productScope || "selected";
            const stateTarget = automation.stateTarget || "";
            const triggerStateTarget = automation.triggerStateTarget || "";
            const followupAction = automation.followupAction || "";
            const followupStateTarget = automation.followupStateTarget || "";
            const followupDelayUnit = automation.followupDelayUnit || "minutes";
            const isExpanded = expandedAutomationId === automation.id;
            const productsApply = automationRequiresProducts(automation);
            const selectedProductIds = productScope === "all" ? [allProductsOption] : automation.productIds || [];
            const statusBadge =
              automation.requestedEnabled && automation.configured === false
                ? { label: "Incompleta", className: "border-amber-200 bg-amber-50 text-amber-800" }
                : !automation.enabled && automationActivationIssues(automation).length
                  ? { label: "Borrador", className: "border-gray-200 bg-white text-gray-600" }
                  : null;
            // Los envíos fallidos NO se muestran en config (Julián): viven en la
            // conversación (nota ❌ + puntico rojo). Aquí solo alertas de config.
	            return (
              <article
                key={automation.id}
                className={`relative rounded-xl border bg-gray-50 ${
                  isExpanded ? "border-gray-950" : "border-transparent"
                } ${isExpanded ? "overflow-visible" : "overflow-hidden"}`}
              >
	                <EditableActionCardHeader
	                  title={automation.title || ""}
	                  fallbackTitle="Automatización"
	                  isExpanded={isExpanded}
	                  enabled={automation.enabled}
	                  onToggleExpanded={() => toggleAutomationExpansion(automation.id)}
	                  onToggleEnabled={(enabled) => setAutomationEnabled(automation, enabled)}
	                  onDelete={() => setDeleteConfirmId(automation.id)}
	                  onTitleChange={(title) => updateAutomation(automation.id, { title })}
	                  deleteTitle="Eliminar automatización"
	                  extra={
	                    <>
	                      {statusBadge ? (
	                        <span className={`inline-flex shrink-0 items-center rounded-full border px-2.5 py-1 text-[11px] font-semibold ${statusBadge.className}`}>
	                          {statusBadge.label}
	                        </span>
	                      ) : null}
	                    </>
	                  }
	                />

	                {isExpanded ? (
	                  <div className="border-t border-gray-100 px-4 pb-4">
	                <div className="mt-4 grid gap-3 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)]">
                  <SelectField
                    label="Cada vez que"
                    value={automation.trigger}
                    options={automationTriggers.map((item) => item.value)}
                    placeholder="Elegir evento"
                    formatOption={(value) => automationTriggers.find((item) => item.value === value)?.label || humanize(value)}
                    onChange={(triggerValue) => commitAutomation(automation.id, { trigger: triggerValue })}
                  />
                  {automation.trigger === "state_changed" ? (
                    <SelectField
                      label="Estado que activa"
                      value={triggerStateTarget}
                      options={automationStateTargets.map((item) => item.value)}
                      placeholder="Elegir estado"
                      formatOption={(value) => automationStateTargets.find((item) => item.value === value)?.label || humanize(value)}
                      onChange={(triggerStateTarget) => commitAutomation(automation.id, { triggerStateTarget })}
                    />
                  ) : null}
                  <SelectField
                    label="Pasa"
                    value={automationActionValue(automation)}
                    options={automationActions.map((item) => item.value)}
                    placeholder="Elegir acción"
                    formatOption={automationActionLabel}
                    onChange={(actionValue) => applyActionValue(automation.id, actionValue, "action")}
                  />
                  {automation.action === "change_state" ? (
                    <SelectField
                      label="Estado destino"
                      value={stateTarget}
                      options={automationStateTargets.map((item) => item.value)}
                      placeholder="Elegir estado"
                      formatOption={(value) => automationStateTargets.find((item) => item.value === value)?.label || humanize(value)}
                      onChange={(stateTarget) => commitAutomation(automation.id, { stateTarget })}
                    />
                  ) : automation.action === "send_template" ? (
                    <div className="block">
                      <span className="mb-1 block text-xs font-semibold text-gray-600">Plantilla</span>
                      <ModalSelect
                        value={automation.templateName}
                        placeholder="Elegir plantilla"
                        options={approvedTemplates.map((template) => ({
                          value: template.name,
                          label: `${template.name} (${template.language})`,
                        }))}
                        onChange={(templateName) => commitAutomation(automation.id, { templateName })}
                      />
                    </div>
                  ) : null}
                </div>

                  {automation.action === "send_template" ? (
                    <div className="mt-4 rounded-xl bg-white p-4">
                      {automation.trigger === "purchase_created" ? (
                        <p className="mb-3 text-xs leading-5 text-gray-500">
                          Si ya se envió la confirmación automática de la orden, Fluxi puede omitir esta plantilla para evitar duplicados. El mensaje puede no aparecer en el hilo del chat aunque el envío sea exitoso.
                        </p>
                      ) : null}
                      <div className="flex flex-wrap items-center justify-between gap-3">
                      <div>
                        <p className="text-sm font-semibold text-gray-950">Seguimiento opcional</p>
                        <p className="mt-1 text-xs leading-5 text-gray-500">
                          Si el usuario no responde después del tiempo elegido, Fluxi ejecuta otra acción.
                        </p>
                      </div>
                      <ToggleSwitch
                        checked={automation.followupEnabled}
                        label={automation.followupEnabled ? "Activo" : "Pausado"}
                        onChange={(followupEnabled) => commitAutomation(automation.id, { followupEnabled })}
                      />
                    </div>

                    {automation.followupEnabled ? (
                      <div className="mt-4 grid gap-3 md:grid-cols-[110px_140px_minmax(0,1fr)_minmax(0,1fr)]">
                        <NumberField
                          label="Luego de"
                          value={automation.followupDelayValue || 30}
                          min={1}
                          step={1}
                          onChange={(value) =>
                            commitAutomation(automation.id, {
                              followupDelayValue: Number.isFinite(value) && value > 0 ? Math.round(value) : 1,
                            })
                          }
                        />
                        <SelectField
                          label="Unidad"
                          value={followupDelayUnit}
                          options={["minutes", "hours"]}
                          formatOption={(value) => (value === "hours" ? "Horas" : "Minutos")}
                          onChange={(followupDelayUnit) =>
                            commitAutomation(automation.id, {
                              followupDelayUnit: followupDelayUnit as AutomationDraft["followupDelayUnit"],
                            })
                          }
                        />
                        <SelectField
                          label="Pasa"
                          value={followupActionValue(automation)}
                          options={automationActions.map((item) => item.value)}
                          placeholder="Elegir acción"
                          formatOption={automationActionLabel}
                          onChange={(followupAction) => applyActionValue(automation.id, followupAction, "followupAction")}
                        />
                        {followupAction === "change_state" ? (
                          <SelectField
                            label="Estado destino"
                            value={followupStateTarget}
                            options={automationStateTargets.map((item) => item.value)}
                            placeholder="Elegir estado"
                            formatOption={(value) =>
                              automationStateTargets.find((item) => item.value === value)?.label || humanize(value)
                            }
                            onChange={(followupStateTarget) => commitAutomation(automation.id, { followupStateTarget })}
                          />
                        ) : followupAction === "send_template" ? (
                          <div className="block">
                            <span className="mb-1 block text-xs font-semibold text-gray-600">Plantilla</span>
                            <ModalSelect
                              value={automation.followupTemplateName}
                              placeholder="Elegir plantilla"
                              options={approvedTemplates.map((template) => ({
                                value: template.name,
                                label: `${template.name} (${template.language})`,
                              }))}
                              onChange={(followupTemplateName) =>
                                commitAutomation(automation.id, { followupTemplateName })
                              }
                            />
                          </div>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                ) : null}

                {productsApply ? (
                  <div className="mt-4 rounded-xl bg-white p-4">
                    <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_minmax(280px,2fr)] md:items-start">
                      <div>
                        <p className="text-sm font-semibold text-gray-950">Productos donde aplica</p>
                      </div>
                      <div className="block">
                        <span className="mb-1 block text-xs font-semibold text-gray-600">Agregar producto</span>
                        <ProductPickerSelect
                          includeAllOption
                          allOptionLabel="Todos"
                          excludeIds={selectedProductIds.filter((id) => id !== allProductsOption)}
                          placeholder="Seleccionar"
                          onSelect={(productId) => selectProduct(automation, productId)}
                        />
                      </div>
                    </div>

                    <div className="mt-3 flex flex-wrap gap-2">
                      {selectedProductIds.map((productId) => {
                        const catalogProduct =
                          productId === allProductsOption
                            ? null
                            : activeProducts.find((product) => productKey(product) === productId);
                        const chip = catalogProduct ? productPickerChipContent(catalogProduct) : null;
                        return (
                        <span
                          key={`${automation.id}-selected-product-${productId}`}
                          className="inline-flex items-center gap-2 rounded-full bg-gray-100 px-3 py-1.5 text-xs font-semibold text-gray-800"
                        >
                          {chip?.thumbnail ? (
                            <img src={chip.thumbnail} alt="" className="size-5 rounded object-cover" />
                          ) : null}
                          {productId === allProductsOption ? "Todos" : productLabel(productId)}
                          {chip?.shortCode ? <span className="font-mono text-[10px] text-gray-500">{chip.shortCode}</span> : null}
                          <button
                            type="button"
                            onClick={() => removeSelectedProduct(automation, productId)}
                            className="inline-flex size-4 items-center justify-center rounded-full text-gray-500 hover:bg-gray-200 hover:text-gray-900"
                            title="Quitar"
                          >
                            <X className="size-3" />
                          </button>
                        </span>
                        );
                      })}
                      {selectedProductIds.length === 0 ? (
                        <p className="text-xs leading-5 text-gray-500">
                          Sin productos elegidos. Puedes agregar productos o elegir Todos.
                        </p>
                      ) : null}
                      {activeProducts.length === 0 ? (
                        <p className="text-xs leading-5 text-gray-500">
                          {catalogLoading ? "Cargando productos Fluxi..." : "Sin productos Fluxi disponibles. Crea productos en tu tienda para elegirlos aquí."}
                        </p>
                      ) : null}
                    </div>
                  </div>
                ) : null}
                  </div>
                ) : null}
              </article>
            );
          })}
        </div>

        {/* Julian: en Logística "Últimas ejecuciones" queda OCULTO (no es visible al usuario;
            no estaba siendo relevante). El endpoint y el registro siguen vivos por si volvemos
            a necesitarlo — solo se muestra en el modo general de automatizaciones. */}
        {mode === "logistics" ? null : (
        <section className="mt-8 rounded-xl bg-gray-50 p-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h3 className="text-sm font-semibold text-gray-950">Últimas ejecuciones</h3>
              <p className="mt-1 text-xs leading-5 text-gray-500">Revisa si tus reglas corrieron, se omitieron o fallaron.</p>
            </div>
            <button
              type="button"
              onClick={() => void loadAutomationRuns()}
              disabled={runsLoading}
              className="inline-flex h-9 items-center gap-2 rounded-full bg-white px-3 text-sm font-semibold text-gray-800 transition hover:bg-gray-100 disabled:opacity-50"
            >
              {runsLoading ? <Loader2 className="size-4 animate-spin" /> : <RefreshCw className="size-4" />}
              Actualizar
            </button>
          </div>
          <div className="mt-4 space-y-2">
            {automationRuns.length === 0 ? (
              <p className="text-sm text-gray-500">Sin ejecuciones registradas todavía.</p>
            ) : (
              automationRuns.slice(0, 10).map((run) => (
                <article key={run.id} className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <p className="font-semibold text-gray-950">{run.automation_key || `Regla #${run.automation_id}`}</p>
                    <span className="text-xs text-gray-400">{formatDate(run.finished_at || run.created_at)}</span>
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    {run.event_type} · {run.phase} · {run.status}
                  </p>
                  <p className="mt-1 text-xs leading-5 text-gray-600">{formatAutomationRunReason(run)}</p>
                </article>
              ))
            )}
          </div>
        </section>
        )}
      </div>

      {deleteConfirmId ? (
        <ConfirmModal
          open
          title={
            deleteConfirmId.startsWith("group:")
              ? "Eliminar avisos logísticos"
              : mode === "logistics"
                ? "Eliminar acción"
                : "Eliminar automatización"
          }
          description={
            deleteConfirmId.startsWith("group:")
              ? "Esta caja y todas sus acciones se quitarán al guardar los cambios."
              : "Esta regla se quitará al guardar los cambios."
          }
          positionClass="fixed"
          zIndexClass="z-[90]"
          actions={[
            {
              label: "Cancelar",
              onClick: () => setDeleteConfirmId(null),
              variant: "secondary",
            },
            {
              label: "Eliminar",
              onClick: () => deleteAutomation(deleteConfirmId),
              variant: "destructive",
            },
          ]}
        />
      ) : null}
    </ScrollSurface>
  );
};

type CommerceFlowSyncMirror = {
  dropiCount: number;
  shopifyCount: number;
  totalCount: number;
};

const CommerceLogisticsPolicyEditor = ({
  policy,
  onChange,
  approvedTemplates = [],
  showConfirmationInstructions = false,
  syncMirror,
}: {
  policy: CommerceLogisticsPolicy;
  onChange: (policy: CommerceLogisticsPolicy) => void;
  approvedTemplates?: WhatsAppTemplate[];
  showConfirmationInstructions?: boolean;
  syncMirror?: CommerceFlowSyncMirror;
}) => {
  const [expandedRows, setExpandedRows] = useState<string[]>([]);
  const updatePolicy = (patch: Partial<CommerceLogisticsPolicy>) => onChange({ ...policy, ...patch });
  const toggleRow = (rowId: string) => {
    setExpandedRows((current) =>
      current.includes(rowId) ? current.filter((id) => id !== rowId) : [...current, rowId]
    );
  };
  const isExpanded = (rowId: string) => expandedRows.includes(rowId);

	  const logisticsRows: Array<{
	    id: string;
	    title: string;
	    subtitle?: string;
	    nested?: boolean;
	    enabled: boolean;
	    onToggle: (enabled: boolean) => void;
	    expandable?: boolean;
	    details?: React.ReactNode;
	  }> = [
    {
	      id: "dropi-confirmation",
	      title: "Confirmar la orden",
	      enabled: policy.dropiConfirmationEnabled !== false,
	      onToggle: (dropiConfirmationEnabled) => updatePolicy({ dropiConfirmationEnabled }),
	      expandable: true,
	      details: (
	        <div className="border-t border-gray-100 px-4 py-3">
          <p className="text-sm leading-6 text-gray-700">
            Antes de despachar, la IA confirma el pedido con el cliente: le lee el resumen de su orden y espera su
            sí. Al confirmarlo, la orden se cotiza y despacha en Dropi. Si lo apagas, la orden se registra y
            despacha sin ese paso de confirmación.
          </p>
        </div>
	      ),
	    },
	    {
	      id: "small-office",
	      title: "Oficina en municipios pequeños",
	      enabled: Boolean(policy.smallMunicipalityOffice),
	      onToggle: (smallMunicipalityOffice) => updatePolicy({ smallMunicipalityOffice }),
	      expandable: true,
	      details: (
	        <div className="space-y-3 border-t border-gray-100 px-4 py-3">
          <p className="text-sm leading-6 text-gray-700">
            En municipios pequeños o veredas, la entrega puerta a puerta suele fallar. Con esto, la IA ofrece que
            el pedido llegue a la oficina de la transportadora en el municipio y el cliente lo recoge allá.
          </p>
          <div className="block">
            <span className="mb-1 block text-xs font-semibold text-gray-600">Cuando aplique la regla</span>
            <ModalSelect
              value={policy.smallMunicipalityOfficeMode || "suggest"}
              wrapDescriptions
              options={[
                {
                  value: "suggest",
                  label: "Sugerir la oficina (el cliente decide)",
                  description:
                    "La IA la propone; si el cliente prefiere su domicilio, se respeta y queda registrado.",
                },
                {
                  value: "require_acceptance",
                  label: "Solo con oficina aceptada",
                  description:
                    "Si aplica oficina y el cliente no acepta recoger allá, la IA no crea el pedido.",
                },
              ]}
              onChange={(smallMunicipalityOfficeMode) =>
                updatePolicy({ smallMunicipalityOfficeMode: smallMunicipalityOfficeMode as "suggest" | "require_acceptance" })
              }
            />
          </div>
          <TextareaField
            label="Instrucciones de oficina"
            value={String(policy.smallMunicipalityOfficeInstructions || "")}
            onChange={(smallMunicipalityOfficeInstructions) => updatePolicy({ smallMunicipalityOfficeInstructions })}
            rows={2}
            placeholder="Ej. si el municipio tiene baja cobertura, explicar que el pedido se enviará por Interrapidísimo a la oficina principal del municipio."
          />
        </div>
	      ),
	    },
	    {
	      id: "preferred-carrier",
	      title: "Preferencia de transportadora",
	      enabled: Boolean(policy.askPreferredCarrier),
	      onToggle: (askPreferredCarrier) => updatePolicy({ askPreferredCarrier }),
	      expandable: true,
	      details: (
	        <div className="space-y-3 border-t border-gray-100 px-4 py-3">
          <p className="text-sm leading-6 text-gray-700">
            Define con qué transportadora sale cada pedido: la IA puede preguntarle al cliente cuál prefiere o
            aplicar reglas fijas de tu negocio al momento de cotizar el envío.
          </p>
          <SelectField
            label="Cómo elegir transportadora"
            value={policy.preferredCarrierMode || "ask_customer"}
            options={["ask_customer", "rules"]}
            formatOption={(value) =>
              value === "rules" ? "Aplicar reglas del negocio" : "Preguntar al cliente"
            }
            onChange={(preferredCarrierMode) =>
              updatePolicy({ preferredCarrierMode: preferredCarrierMode as "ask_customer" | "rules" })
            }
          />
          {policy.preferredCarrierMode === "rules" ? (
            <TextareaField
              label="Reglas de transportadora"
              value={String(policy.preferredCarrierRules || "")}
              onChange={(preferredCarrierRules) => updatePolicy({ preferredCarrierRules })}
              rows={3}
              placeholder="Ej. si vive en el sur de Bogotá usar Envía; si vive en el norte usar Delivery/Entregado."
            />
          ) : null}
        </div>
	      ),
	    },
	    {
	      id: "maps-validation",
	      title: "Validar dirección con Maps",
	      enabled: Boolean(policy.mapsValidationEnabled),
	      onToggle: (mapsValidationEnabled) => updatePolicy({ mapsValidationEnabled }),
	      expandable: true,
	      details: (
	        <div className="space-y-3 border-t border-gray-100 px-4 py-3">
          <p className="text-sm leading-6 text-gray-700">
            La IA valida la dirección de entrega con Google Maps antes de crear el pedido: confirma que exista,
            la corrige y detecta la ciudad correcta.
          </p>
          {/* Mismo patrón que Preferencia de transportadora (Julian): párrafo + dropdown.
              opcional = mapsRejectOrderIfInvalid:false / obligatoria = mapsRejectOrderIfInvalid:true. */}
          <div className="block">
            <span className="mb-1 block text-xs font-semibold text-gray-600">Qué tan estricta es la validación</span>
            <ModalSelect
              value={policy.mapsRejectOrderIfInvalid === false ? "opcional" : "obligatoria"}
              wrapDescriptions
              options={[
                {
                  value: "opcional",
                  label: "Opcional (recomendado)",
                  description:
                    "Si Maps no reconoce la dirección, la IA continúa con la que dio el cliente y deja registro de que no se pudo validar.",
                },
                {
                  value: "obligatoria",
                  label: "Obligatoria",
                  description:
                    "La IA no crea el pedido hasta validar la dirección con Maps; le pide precisiones al cliente si hace falta.",
                },
              ]}
              onChange={(mode) => updatePolicy({ mapsRejectOrderIfInvalid: mode === "obligatoria" })}
            />
          </div>
        </div>
	      ),
	    },
	    {
	      id: "shipping-surcharge",
	      title: "Recargo envío caro",
	      enabled: Boolean(policy.shippingSurchargeEnabled),
	      onToggle: (shippingSurchargeEnabled) => updatePolicy({ shippingSurchargeEnabled }),
	      expandable: true,
	      details: (
	        <div className="space-y-3 border-t border-gray-100 px-4 py-3">
          <p className="text-sm leading-6 text-gray-700">
            Cuando el envío cotizado supera el límite que definas, la IA le cobra al cliente un recargo (una
            cantidad fija o un porcentaje del excedente) para proteger tu margen en zonas de envío caro.
          </p>
          <NumberField
            label="Límite de envío (COP)"
            value={Number(policy.shippingSurchargeThreshold || 20000)}
            min={0}
            onChange={(shippingSurchargeThreshold) => updatePolicy({ shippingSurchargeThreshold })}
          />
          <SelectField
            label="Si el envío vale más que el límite"
            value={policy.shippingSurchargeMode || "percent"}
            options={["percent", "fixed"]}
            formatOption={(value) =>
              value === "fixed" ? "El cliente paga una cantidad fija extra" : "El cliente paga un porcentaje del excedente"
            }
            onChange={(shippingSurchargeMode) =>
              updatePolicy({ shippingSurchargeMode: shippingSurchargeMode as "percent" | "fixed" })
            }
          />
          {policy.shippingSurchargeMode === "fixed" ? (
            <NumberField
              label="Cantidad fija extra (COP)"
              value={Number(policy.shippingSurchargeFixedAmount || 5000)}
              min={0}
              onChange={(shippingSurchargeFixedAmount) => updatePolicy({ shippingSurchargeFixedAmount })}
            />
          ) : (
            <NumberField
              label="Porcentaje del excedente (%)"
              value={Number(policy.shippingSurchargePercent || 50)}
              min={0}
              max={100}
              onChange={(shippingSurchargePercent) => updatePolicy({ shippingSurchargePercent })}
            />
          )}
        </div>
	      ),
	    },
  ];

  // UI (Julian): la sincronización con Dropi se configura UNA sola vez, por producto, en el
  // bloque "Embudo fuente por producto" de arriba — aquí no se repite. En la toma de orden la
  // primera acción logística es "Confirmar la orden" y solo aparece cuando algún embudo de la
  // toma sincroniza con Dropi (confirmar = cotizar y despachar allá). En los flujos de
  // Confirmaciones no aparece: confirmar ES el punto entero de esa tab. El aviso de guía ya no
  // vive en los flujos: todas las automatizaciones de avances de Dropi están en la tab Logística.
  const dropiSyncActive = Boolean(syncMirror && syncMirror.dropiCount > 0);
  const visibleRows = logisticsRows.filter((row) =>
    row.id === "dropi-confirmation" ? Boolean(syncMirror) && dropiSyncActive : true
  );

  return (
    <div className="space-y-4">
      {showConfirmationInstructions ? (
        <div className="grid gap-4 md:grid-cols-2">
          <TextareaField
            label="Instrucciones para confirmar (opcional)"
            value={String(policy.confirmedInstructions || "")}
            onChange={(value) => updatePolicy({ confirmedInstructions: value })}
            rows={3}
            placeholder="Ej. Confirmar cuando el cliente diga claramente que acepta recibir la orden."
          />
          <TextareaField
            label="Instrucciones para rechazar (opcional)"
            value={String(policy.rejectedInstructions || "")}
            onChange={(value) => updatePolicy({ rejectedInstructions: value })}
            rows={3}
            placeholder="Ej. Rechazar cuando el cliente pida cancelar o no reconozca la compra."
          />
        </div>
      ) : null}

      {showConfirmationInstructions ? (
        /* Julian: auto-expirar va ARRIBA de las acciones logísticas (última opción estándar). */
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-gray-200 bg-white px-4 py-3">
          <div>
            <p className="text-sm font-semibold text-gray-950">Auto-expirar órdenes sin respuesta</p>
            <p className="mt-1 text-xs leading-5 text-gray-500">
              Si el cliente no responde la confirmación en este tiempo, la orden pasa a expirada y deja de quedar pendiente.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div className="w-28">
              <NumberField
                label="Horas"
                value={Number(policy.autoExpireHours || 48)}
                min={1}
                max={720}
                onChange={(autoExpireHours) => updatePolicy({ autoExpireHours })}
              />
            </div>
            <ToggleSwitch
              checked={Boolean(policy.autoExpireEnabled)}
              label={policy.autoExpireEnabled ? "Activa" : "Pausada"}
              onChange={(autoExpireEnabled) => updatePolicy({ autoExpireEnabled })}
            />
          </div>
        </div>
      ) : null}

      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
        {visibleRows.map((row, index) => (
          <div key={row.id} className={index > 0 ? "border-t border-gray-100" : ""}>
            <div
              className={`flex items-center justify-between gap-3 px-4 py-3 ${
                row.nested ? "ml-4 border-l-2 border-gray-200 bg-gray-50/60" : ""
              }`}
            >
	              <div className="min-w-0 flex-1">
	                <p className="text-sm font-semibold text-gray-950">{row.title}</p>
	                {row.subtitle ? <p className="mt-0.5 text-xs text-gray-500">{row.subtitle}</p> : null}
	              </div>
              <div className="flex shrink-0 items-center gap-2">
                <ToggleSwitch
                  checked={row.enabled}
                  label={row.enabled ? "Activa" : "Pausada"}
                  onChange={row.onToggle}
                />
                {row.expandable ? (
                  <button
                    type="button"
                    onClick={() => toggleRow(row.id)}
                    className="inline-flex size-8 items-center justify-center rounded-full text-gray-500 transition hover:bg-gray-100 hover:text-gray-900"
                    title="Configurar"
                  >
                    <ChevronDown className={`size-4 transition ${isExpanded(row.id) ? "rotate-180" : ""}`} />
                  </button>
                ) : null}
              </div>
            </div>
            {row.expandable && isExpanded(row.id) && row.details ? row.details : null}
          </div>
        ))}
      </div>

    </div>
  );
};

const CommerceFlowProductSection = ({
  flow,
  catalogProducts,
  catalogLoading,
  usedProducts = {},
  includeAllOption = true,
  showSelectedChips = true,
  selectedDisplay = "chips",
  onSelectProduct,
  onRemoveProduct,
  renderCardAction,
  renderAllCardAction,
}: {
  flow: CommerceFlowDraft;
  catalogProducts: Product[];
  catalogLoading: boolean;
  // productId → nombre de la otra acción del mismo tipo que ya lo usa (opción gray-out).
  usedProducts?: Record<string, string>;
  includeAllOption?: boolean;
  showSelectedChips?: boolean;
  selectedDisplay?: "chips" | "cards";
  onSelectProduct: (productId: string, product?: Product) => void;
  onRemoveProduct: (productId: string) => void;
  // Fila extra opcional dentro de cada card de producto (solo selectedDisplay="cards").
  renderCardAction?: (productId: string, product: Product | null) => React.ReactNode;
  // Fila extra opcional dentro de la card "Todos los productos" (solo selectedDisplay="cards").
  renderAllCardAction?: () => React.ReactNode;
}) => {
  const productScope = flow.productScope || "selected";
  const selectedProductIds = productScope === "all" ? [ALL_PRODUCTS_OPTION] : flow.productIds || [];
  const productLabel = (productId: string) =>
    flow.productLabels?.[productId] ||
    catalogProducts.find((product) => String(product.id) === productId)?.name ||
    productId;

  return (
    <div className="block">
      <span className="mb-1 block text-xs font-semibold text-gray-600">Productos enlazados</span>
      {catalogLoading && catalogProducts.length === 0 ? (
        <div className="flex h-10 w-full animate-pulse items-center gap-3 rounded-lg border border-gray-200 bg-white px-3">
          <span className="size-6 shrink-0 rounded-md bg-gray-100" />
          <span className="h-3 w-36 rounded bg-gray-100" />
          <span className="ml-auto size-4 rounded bg-gray-100" />
        </div>
      ) : (
        <ProductPickerSelect
          includeAllOption={includeAllOption}
          allOptionLabel="Todos los productos"
          excludeIds={selectedProductIds.filter((id) => id !== ALL_PRODUCTS_OPTION)}
          disabledReasonById={usedProducts}
          placeholder="Seleccionar producto"
          disabled={productScope === "all"}
          onSelect={onSelectProduct}
        />
      )}
      {showSelectedChips && selectedDisplay === "cards" ? (
        /* Card estándar de producto (Julian): misma caja que en la toma de orden, a 1 columna. */
        <div className="mt-3 grid gap-3">
          {selectedProductIds.map((productId) => {
            const catalogProduct =
              productId === ALL_PRODUCTS_OPTION
                ? null
                : catalogProducts.find((product) => String(product.id) === productId);
            const chip = catalogProduct ? productPickerChipContent(catalogProduct) : null;
            const label = productId === ALL_PRODUCTS_OPTION ? "Todos los productos" : productLabel(productId);
            const cardAction =
              productId === ALL_PRODUCTS_OPTION
                ? renderAllCardAction?.() ?? null
                : renderCardAction?.(productId, catalogProduct || null);
            return (
              <div
                key={`${flow.id}-product-card-${productId}`}
                className="rounded-lg border border-gray-200 bg-white p-3"
              >
                <div className="flex items-center gap-3">
                  {chip?.thumbnail ? (
                    <img src={chip.thumbnail} alt="" className="size-9 shrink-0 rounded object-cover" />
                  ) : (
                    <div className="flex size-9 shrink-0 items-center justify-center rounded bg-gray-100 text-xs font-semibold text-gray-500">
                      {label.slice(0, 1).toUpperCase()}
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold text-gray-950">{label}</p>
                    {chip?.shortCode ? (
                      <p className="truncate font-mono text-[11px] text-gray-500">{chip.shortCode}</p>
                    ) : null}
                  </div>
                  <button
                    type="button"
                    onClick={() => onRemoveProduct(productId)}
                    className="inline-flex size-8 shrink-0 items-center justify-center rounded-full text-gray-400 transition hover:bg-gray-100 hover:text-red-600"
                    title="Quitar producto"
                  >
                    <X className="size-4" />
                  </button>
                </div>
                {cardAction ? <div className="mt-3 border-t border-gray-100 pt-3">{cardAction}</div> : null}
              </div>
            );
          })}
          {selectedProductIds.length === 0 ? (
            <p className="text-xs leading-5 text-gray-500">Agrega productos desde el selector.</p>
          ) : null}
        </div>
      ) : showSelectedChips ? (
      <div className="mt-2 flex flex-wrap gap-2">
        {selectedProductIds.map((productId) => {
          const catalogProduct =
            productId === ALL_PRODUCTS_OPTION
              ? null
              : catalogProducts.find((product) => String(product.id) === productId);
          const chip = catalogProduct ? productPickerChipContent(catalogProduct) : null;
          return (
            <span
              key={`${flow.id}-product-${productId}`}
              className="inline-flex items-center gap-2 rounded-full bg-gray-100 px-3 py-1.5 text-xs font-semibold text-gray-800"
            >
              {chip?.thumbnail ? <img src={chip.thumbnail} alt="" className="size-5 rounded object-cover" /> : null}
              {productId === ALL_PRODUCTS_OPTION ? "Todos los productos" : productLabel(productId)}
              {chip?.shortCode ? <span className="font-mono text-[10px] text-gray-500">{chip.shortCode}</span> : null}
              <button
                type="button"
                onClick={() => onRemoveProduct(productId)}
                className="inline-flex size-4 items-center justify-center rounded-full text-gray-500 hover:bg-gray-200 hover:text-gray-900"
                title="Quitar"
              >
                <X className="size-3" />
              </button>
            </span>
          );
        })}
        {selectedProductIds.length === 0 ? (
          <p className="text-xs leading-5 text-gray-500">Agrega productos desde el selector.</p>
        ) : null}
      </div>
      ) : null}
    </div>
  );
};

const FunnelSourcePicker = ({
  value,
  websites,
  mode,
  disabled,
  onChange,
}: {
  value: string;
  websites: CheckoutWebsiteOption[];
  mode: CommerceOrderMode | null;
  disabled?: boolean;
  onChange: (websiteId: string) => void;
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const containerRef = useRef<HTMLDivElement | null>(null);
  const selectedWebsite = websites.find((website) => String(website.websiteId) === value) || null;
  const normalizedQuery = query.trim().toLowerCase();
  const visibleWebsites = normalizedQuery
    ? websites.filter((website) =>
        [
          checkoutWebsiteSequenceLabel(website),
          checkoutWebsiteName(website),
          checkoutWebsiteSubtitle(website),
          website.sales_angle_description,
        ]
          .join(" ")
          .toLowerCase()
          .includes(normalizedQuery)
      )
    : websites;

  useEffect(() => {
    if (!open) return;
    const handlePointerDown = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, [open]);

  return (
    <div ref={containerRef} className="relative min-w-0">
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((current) => !current)}
        className="flex min-h-10 w-full items-center justify-between gap-3 rounded-lg border border-gray-200 bg-white px-3 py-2 text-left text-sm text-gray-950 outline-none transition hover:border-gray-300 focus:border-[#4368E5] disabled:cursor-not-allowed disabled:opacity-60"
      >
        {selectedWebsite ? (
          <span className="min-w-0">
            <span className="flex min-w-0 items-center gap-2">
              <span className="shrink-0 rounded bg-gray-100 px-1.5 py-0.5 font-mono text-[11px] font-semibold text-gray-600">
                {checkoutWebsiteSequenceLabel(selectedWebsite)}
              </span>
              <span className="truncate font-semibold">{checkoutWebsiteName(selectedWebsite)}</span>
            </span>
            <span className="mt-0.5 block truncate text-xs text-gray-500">{checkoutWebsiteSubtitle(selectedWebsite)}</span>
          </span>
        ) : (
          <span className="text-gray-500">
            {mode === "fluxi_checkout_link" ? "Elegir checkout" : "Elegir embudo fuente"}
          </span>
        )}
        <ChevronDown className={`size-4 shrink-0 text-gray-400 transition ${open ? "rotate-180" : ""}`} />
      </button>

      {open ? (
        <div className="absolute left-0 right-0 z-40 mt-2 max-h-80 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-xl">
          <div className="border-b border-gray-100 p-2">
            <div className="flex h-9 items-center gap-2 rounded-lg bg-gray-50 px-2">
              <Search className="size-4 text-gray-400" />
              <input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Buscar embudo"
                className="min-w-0 flex-1 bg-transparent text-sm outline-none placeholder:text-gray-400"
                autoFocus
              />
            </div>
          </div>
          <div className="max-h-64 overflow-y-auto p-1">
            {visibleWebsites.length ? (
              visibleWebsites.map((website) => {
                const websiteId = String(website.websiteId);
                const openUrl = checkoutWebsiteOpenUrl(website);
                return (
                  <div
                    key={websiteId}
                    className={`flex items-stretch gap-1 rounded-lg ${
                      websiteId === value ? "bg-[#EEF2FF]" : "hover:bg-gray-50"
                    }`}
                  >
                    <button
                      type="button"
                      onClick={() => {
                        onChange(websiteId);
                        setOpen(false);
                        setQuery("");
                      }}
                      className="min-w-0 flex-1 px-2.5 py-2 text-left"
                    >
                      <span className="flex min-w-0 items-center gap-2">
                        <span className="shrink-0 rounded bg-white px-1.5 py-0.5 font-mono text-[11px] font-semibold text-gray-600">
                          {checkoutWebsiteSequenceLabel(website)}
                        </span>
                        <span className="truncate text-sm font-semibold text-gray-950">{checkoutWebsiteName(website)}</span>
                      </span>
                      <span className="mt-0.5 block truncate text-xs text-gray-500">{checkoutWebsiteSubtitle(website)}</span>
                    </button>
                    {openUrl ? (
                      <a
                        href={openUrl}
                        target="_blank"
                        rel="noreferrer"
                        onClick={(event) => event.stopPropagation()}
                        className="m-1 inline-flex shrink-0 items-center gap-1 rounded-md px-2 text-xs font-semibold text-[#4368E5] hover:bg-white"
                        title="Ver checkout"
                      >
                        Ver
                        <ExternalLink className="size-3.5" />
                      </a>
                    ) : null}
                  </div>
                );
              })
            ) : (
              <p className="px-3 py-4 text-sm text-gray-500">No hay embudos que coincidan.</p>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
};

const EditableActionCardHeader = ({
  title,
  fallbackTitle,
  isExpanded,
  enabled,
  enabledLabel,
  pausedLabel = "Pausada",
  onToggleExpanded,
  onToggleEnabled,
  onDelete,
  onTitleChange,
  extra,
  deleteTitle,
}: {
  title: string;
  fallbackTitle: string;
  isExpanded: boolean;
  enabled: boolean;
  enabledLabel?: string;
  pausedLabel?: string;
  onToggleExpanded: () => void;
  onToggleEnabled: (enabled: boolean) => void;
  onDelete: () => void;
  onTitleChange: (title: string) => void;
  extra?: React.ReactNode;
  deleteTitle: string;
}) => {
  const [editing, setEditing] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const displayTitle = title.trim() || fallbackTitle;

  useEffect(() => {
    if (!editing) return;
    inputRef.current?.focus();
    inputRef.current?.select();
  }, [editing]);

  return (
    <div
      role="button"
      tabIndex={0}
      aria-expanded={isExpanded}
      onClick={onToggleExpanded}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onToggleExpanded();
        }
      }}
      className="flex min-h-[68px] cursor-pointer items-center justify-between gap-3 px-4 py-3"
    >
      <div className="min-w-0 flex-1">
        {editing ? (
          <input
            ref={inputRef}
            value={title}
            onClick={(event) => event.stopPropagation()}
            onKeyDown={(event) => {
              if (event.key === "Escape") {
                setEditing(false);
                inputRef.current?.blur();
              }
              if (event.key === "Enter") {
                setEditing(false);
                inputRef.current?.blur();
              }
            }}
            onBlur={() => setEditing(false)}
            onChange={(event) => onTitleChange(event.target.value)}
            className="h-9 w-full max-w-md rounded-lg border border-gray-200 bg-white px-3 text-sm font-semibold text-gray-950 outline-none focus:border-[#4368E5]"
          />
        ) : (
          <div className="flex min-w-0 items-center gap-2">
            <p className="truncate text-sm font-semibold text-gray-950">{displayTitle}</p>
            {isExpanded ? (
              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  setEditing(true);
                }}
                className="inline-flex size-8 shrink-0 items-center justify-center rounded-full text-gray-400 transition hover:bg-white hover:text-gray-900"
                title="Editar nombre"
              >
                <Pencil className="size-3.5" />
              </button>
            ) : null}
            {extra}
          </div>
        )}
      </div>
      <div className="flex shrink-0 items-center gap-2" onClick={(event) => event.stopPropagation()}>
        <ToggleSwitch
          checked={enabled}
          label={enabled ? enabledLabel || "Activa" : pausedLabel}
          onChange={onToggleEnabled}
        />
        <button
          type="button"
          onClick={onDelete}
          className="inline-flex size-9 items-center justify-center rounded-full text-gray-400 transition hover:bg-white hover:text-red-600"
          title={deleteTitle}
        >
          <Trash2 className="size-4" />
        </button>
        <button
          type="button"
          onClick={onToggleExpanded}
          className="inline-flex size-9 items-center justify-center rounded-full text-gray-500 transition hover:bg-white hover:text-gray-900"
          title={isExpanded ? "Contraer" : "Expandir"}
        >
          <ChevronDown className={`size-4 transition ${isExpanded ? "rotate-180" : ""}`} />
        </button>
      </div>
    </div>
  );
};

type FunnelSyncSettingsProvider = "dropi" | "shopify";
type FunnelSyncProductRow = {
  id: string;
  name: string;
  type?: string | null;
  dropi_id: string;
  dropi_lookup?: DropiProductLookupResponse | null;
  dropi_verified_id?: string | null;
  dropi_verification_error?: string | null;
  dropi_needs_verification?: boolean;
  sku: string;
  id_shopify_product: string;
  variants?: FunnelShopifyVariantMapping[];
};

type WebsiteSyncDraftState = {
  websiteId: string;
  provider: FunnelSyncSettingsProvider | "none" | "";
  dirty: boolean;
  loaded?: boolean;
  loading?: boolean;
  error?: string | null;
  dropiCompatible?: boolean;
  dropiCompatibilityReason?: string | null;
  rows: FunnelSyncProductRow[];
  shopifyUrlStore?: string;
  shopifyToken?: string;
  shopifyCurrencyMode?: string;
  shopifyCheckoutProvider?: string;
  shopifyStorefrontToken?: string;
  shopifyClientId?: string;
};

const funnelProductTypeLabel = (type?: string | null) => {
  if (type === "PRINCIPAL") return "Principal";
  if (type === "UP_SELL") return "Upsell";
  if (type === "DOWN_SELL") return "Downsell";
  if (type === "BUMP_OFFER") return "Order bump";
  return "Producto";
};

const formatCheckoutAmount = (value: number) =>
  Number.isFinite(value)
    ? new Intl.NumberFormat("es-CO", { style: "currency", currency: "COP", maximumFractionDigits: 0 }).format(value)
    : "";

const syncRowsFromWebsiteProducts = (
  website: CheckoutWebsiteOption,
  catalogProducts: Product[],
  existingRows?: {
    dropi?: Array<{ id: string; dropi_id?: string; dropiId?: string }>;
    shopify?: FunnelShopifyProductMapping[];
  }
) => {
  const dropiById = new Map(
    (existingRows?.dropi || []).map((product) => [String(product.id || "").trim(), String(product.dropi_id || product.dropiId || "")])
  );
  const shopifyById = new Map(
    (existingRows?.shopify || []).map((product) => [
      String(product.product_id || product.productId || "").trim(),
      product,
    ])
  );
  const rows = (Array.isArray(website.products) ? website.products : [])
    .map((websiteProduct) => {
      const id = checkoutWebsiteProductRef(websiteProduct);
      if (!id) return null;
      const record = websiteProduct as unknown as Record<string, unknown>;
      const catalogProduct = catalogProducts.find((product) => String(product.id) === id);
      const shopify = shopifyById.get(id);
      return {
        id,
        name: catalogProduct?.name || funnelProductTypeLabel(String(record.type || "")),
        type: typeof record.type === "string" ? record.type : null,
        dropi_id: dropiById.get(id) || "",
        sku: String(shopify?.sku || ""),
        id_shopify_product: String(shopify?.id_shopify_product || shopify?.idShopifyProduct || ""),
        variants: Array.isArray(shopify?.variants) ? shopify?.variants : [],
      };
    })
    .filter(Boolean) as FunnelSyncProductRow[];
  return rows;
};

const FunnelSyncSettingsPanel = ({
  provider,
  website,
  catalogProducts,
  draft,
  onDraftChange,
}: {
  provider: FunnelSyncSettingsProvider;
  website: CheckoutWebsiteOption;
  catalogProducts: Product[];
  draft?: WebsiteSyncDraftState;
  onDraftChange: (draft: WebsiteSyncDraftState) => void;
}) => {
  const websiteId = String(website.websiteId || "").trim();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dropiCompatible, setDropiCompatible] = useState(true);
  const [dropiCompatibilityReason, setDropiCompatibilityReason] = useState<string | null>(null);
  const [shopifyUrlStore, setShopifyUrlStore] = useState("");
  const [shopifyToken, setShopifyToken] = useState("");
  const [shopifyCurrencyMode, setShopifyCurrencyMode] = useState("FUNNEL_CURRENCY");
  const [shopifyCheckoutProvider, setShopifyCheckoutProvider] = useState("fluxi");
  const [shopifyStorefrontToken, setShopifyStorefrontToken] = useState("");
  const [shopifyClientId, setShopifyClientId] = useState("");
  const [rows, setRows] = useState<FunnelSyncProductRow[]>([]);
  const [verifyingDropiRowId, setVerifyingDropiRowId] = useState<string | null>(null);
  const shopifyIncomplete =
    rows.length === 0 ||
    !shopifyUrlStore.trim() ||
    !shopifyToken.trim() ||
    rows.some((row) => !row.id_shopify_product.trim());

  const emitDraft = (patch: Partial<WebsiteSyncDraftState>, dirty = true) => {
    onDraftChange({
      ...draft,
      websiteId,
      provider,
      rows,
      dropiCompatible,
      dropiCompatibilityReason,
      shopifyUrlStore,
      shopifyToken,
      shopifyCurrencyMode,
      shopifyCheckoutProvider,
      shopifyStorefrontToken,
      shopifyClientId,
      loaded: !loading,
      loading,
      error,
      ...patch,
      dirty,
    });
  };

  useEffect(() => {
    if (!websiteId) return;
    let mounted = true;
    setLoading(true);
    setError(null);
    onDraftChange({
      ...(draft || { websiteId, provider, rows: [] }),
      websiteId,
      provider,
      rows: draft?.rows || [],
      loading: true,
      loaded: false,
      error: null,
      dirty: Boolean(draft?.dirty),
    });
    if (provider === "dropi") {
      getFunnelDropiIntegrationState(websiteId)
        .then((integration) => {
          if (!mounted) return;
          const nextRows = syncRowsFromWebsiteProducts(website, catalogProducts, { dropi: integration.products || [] });
          setDropiCompatible(integration.compatible_with_funnel !== false);
          setDropiCompatibilityReason(integration.compatibility_reason || null);
          setRows(nextRows);
          onDraftChange({
            websiteId,
            provider,
            rows: nextRows,
            dropiCompatible: integration.compatible_with_funnel !== false,
            dropiCompatibilityReason: integration.compatibility_reason || null,
            loaded: true,
            loading: false,
            error: null,
            dirty: Boolean(draft?.dirty),
          });
        })
        .catch((loadError) => {
          if (!mounted) return;
          const message = getErrorMessage(loadError) || "No pudimos cargar la configuración Dropi de este embudo.";
          const nextRows = syncRowsFromWebsiteProducts(website, catalogProducts);
          setError(message);
          setRows(nextRows);
          onDraftChange({
            websiteId,
            provider,
            rows: nextRows,
            loaded: true,
            loading: false,
            error: message,
            dirty: Boolean(draft?.dirty),
          });
        })
        .finally(() => {
          if (mounted) setLoading(false);
        });
      return () => {
        mounted = false;
      };
    }
    getFunnelShopifyIntegrationState(websiteId)
      .then((integration) => {
        if (!mounted) return;
        const nextRows = syncRowsFromWebsiteProducts(website, catalogProducts, { shopify: integration?.products || [] });
        setShopifyUrlStore(String(integration?.url_store || ""));
        setShopifyToken(String(integration?.token || ""));
        setShopifyCurrencyMode(String(integration?.currency_mode || "FUNNEL_CURRENCY"));
        setShopifyCheckoutProvider(String(integration?.checkout_provider || "fluxi"));
        setShopifyStorefrontToken(String(integration?.storefront_access_token || ""));
        setShopifyClientId(String(integration?.client_id || ""));
        setRows(nextRows);
        onDraftChange({
          websiteId,
          provider,
          rows: nextRows,
          shopifyUrlStore: String(integration?.url_store || ""),
          shopifyToken: String(integration?.token || ""),
          shopifyCurrencyMode: String(integration?.currency_mode || "FUNNEL_CURRENCY"),
          shopifyCheckoutProvider: String(integration?.checkout_provider || "fluxi"),
          shopifyStorefrontToken: String(integration?.storefront_access_token || ""),
          shopifyClientId: String(integration?.client_id || ""),
          loaded: true,
          loading: false,
          error: null,
          dirty: Boolean(draft?.dirty),
        });
      })
      .catch((loadError) => {
        if (!mounted) return;
        const message = getErrorMessage(loadError) || "No pudimos cargar la configuración Shopify de este embudo.";
        const nextRows = syncRowsFromWebsiteProducts(website, catalogProducts);
        setError(message);
        setRows(nextRows);
        onDraftChange({
          websiteId,
          provider,
          rows: nextRows,
          loaded: true,
          loading: false,
          error: message,
          dirty: Boolean(draft?.dirty),
        });
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [catalogProducts, provider, website, websiteId]);

  const updateRow = (productId: string, patch: Partial<FunnelSyncProductRow>) => {
    const nextRows = rows.map((row) => (row.id === productId ? { ...row, ...patch } : row));
    setRows(nextRows);
    emitDraft({ rows: nextRows, loaded: true, loading: false, error: null }, true);
  };

  const updateShopifySetting = (patch: Partial<WebsiteSyncDraftState>) => {
    if (typeof patch.shopifyUrlStore === "string") setShopifyUrlStore(patch.shopifyUrlStore);
    if (typeof patch.shopifyToken === "string") setShopifyToken(patch.shopifyToken);
    if (typeof patch.shopifyCurrencyMode === "string") setShopifyCurrencyMode(patch.shopifyCurrencyMode);
    if (typeof patch.shopifyCheckoutProvider === "string") setShopifyCheckoutProvider(patch.shopifyCheckoutProvider);
    if (typeof patch.shopifyStorefrontToken === "string") setShopifyStorefrontToken(patch.shopifyStorefrontToken);
    if (typeof patch.shopifyClientId === "string") setShopifyClientId(patch.shopifyClientId);
    emitDraft(patch, true);
  };

  const verifyDropiRow = async (row: FunnelSyncProductRow) => {
    const dropiId = row.dropi_id.trim();
    if (!dropiId || verifyingDropiRowId) return;
    setVerifyingDropiRowId(row.id);
    try {
      const result = await lookupDropiProduct(dropiId);
      updateRow(row.id, {
        dropi_lookup: result,
        dropi_verified_id: dropiId,
        dropi_verification_error: null,
        dropi_needs_verification: false,
      });
      toast.success("Producto Dropi verificado");
    } catch (lookupError) {
      const message = getErrorMessage(lookupError) || "No pudimos verificar el producto Dropi.";
      updateRow(row.id, {
        dropi_lookup: null,
        dropi_verified_id: null,
        dropi_verification_error: message,
        dropi_needs_verification: true,
      });
      toast.error(message);
    } finally {
      setVerifyingDropiRowId(null);
    }
  };

  return (
    <div className="mt-3 rounded-lg border border-gray-200 bg-gray-50 p-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-xs font-semibold text-gray-700">
            {provider === "dropi" ? "Match de productos Dropi" : "Settings Shopify del embudo"}
          </p>
          <p className="mt-1 text-xs leading-5 text-gray-500">
            {provider === "dropi"
              ? "Asocia los productos de este embudo con su ID en Dropi para activar la sincronización."
              : "Estos campos quedan como cambios pendientes y se guardan con el botón general del modal."}
          </p>
        </div>
      </div>
      {error ? (
        <p className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs leading-5 text-red-700">
          {error}
        </p>
      ) : null}
      {loading ? (
        <div className="mt-4 flex items-center gap-2 text-xs font-semibold text-gray-500">
          <Loader2 className="size-4 animate-spin" />
          Cargando settings...
        </div>
      ) : provider === "dropi" ? (
        <div className="mt-4 grid gap-3">
          {!dropiCompatible && dropiCompatibilityReason ? (
            <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs leading-5 text-amber-800">
              {dropiCompatibilityReason}
            </p>
          ) : null}
          <div className="grid gap-2">
            {rows.map((row) => (
              <div key={`${websiteId}-dropi-${row.id}`} className="grid gap-3 rounded-lg bg-white p-3">
                <div className="min-w-0">
                  <p className="truncate text-xs font-semibold text-gray-800">{row.name}</p>
                  <p className="mt-0.5 truncate font-mono text-[11px] text-gray-500">{row.id} · {funnelProductTypeLabel(row.type)}</p>
                </div>
                <div className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_108px]">
                  <input
                    value={row.dropi_id}
                    onChange={(event) =>
                      updateRow(row.id, {
                        dropi_id: event.target.value,
                        dropi_lookup: null,
                        dropi_verified_id: null,
                        dropi_verification_error: null,
                        dropi_needs_verification: true,
                      })
                    }
                    placeholder="ID producto Dropi"
                    className="h-9 rounded-lg border border-gray-200 bg-white px-3 text-sm outline-none focus:border-[#4368E5]"
                  />
                  <button
                    type="button"
                    onClick={() => void verifyDropiRow(row)}
                    disabled={!row.dropi_id.trim() || verifyingDropiRowId === row.id}
                    className="inline-flex h-9 items-center justify-center gap-2 rounded-lg border border-gray-200 bg-white px-3 text-sm font-semibold text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {verifyingDropiRowId === row.id ? <Loader2 className="size-4 animate-spin" /> : <Search className="size-4" />}
                    Buscar
                  </button>
                </div>
                {row.dropi_lookup ? (
                  <div className="flex items-center gap-3 rounded-lg border border-emerald-100 bg-emerald-50 px-3 py-2">
                    {row.dropi_lookup.image ? (
                      <img src={row.dropi_lookup.image} alt="" className="size-9 shrink-0 rounded object-cover" />
                    ) : (
                      <div className="flex size-9 shrink-0 items-center justify-center rounded bg-white text-emerald-700">
                        <CheckCircle2 className="size-4" />
                      </div>
                    )}
                    <div className="min-w-0">
                      <p className="truncate text-xs font-semibold text-emerald-950">{row.dropi_lookup.name || `Dropi ${row.dropi_lookup.id}`}</p>
                      <p className="truncate text-[11px] text-emerald-700">
                        ID {row.dropi_lookup.id}
                        {row.dropi_lookup.sku ? ` · SKU ${row.dropi_lookup.sku}` : ""}
                        {row.dropi_lookup.price ? ` · ${formatCheckoutAmount(Number(row.dropi_lookup.price))}` : ""}
                        {row.dropi_lookup.variants?.length ? ` · ${row.dropi_lookup.variants.length} variantes` : ""}
                      </p>
                    </div>
                  </div>
                ) : row.dropi_verification_error ? (
                  <p className="rounded-lg border border-red-100 bg-red-50 px-3 py-2 text-xs leading-5 text-red-700">
                    {row.dropi_verification_error}
                  </p>
                ) : null}
              </div>
            ))}
          </div>
          {rows.some((row) => !row.dropi_id.trim() || row.dropi_needs_verification || row.dropi_verification_error) ? (
            <p className="text-xs leading-5 text-gray-500">
              Busca y verifica el ID de Dropi de cada producto nuevo; después usa Guardar cambios del modal.
            </p>
          ) : null}
        </div>
      ) : (
        <div className="mt-4 grid gap-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block">
              <span className="mb-1 block text-xs font-semibold text-gray-600">URL tienda Shopify</span>
              <input
                value={shopifyUrlStore}
                onChange={(event) => updateShopifySetting({ shopifyUrlStore: event.target.value })}
                placeholder="tienda.myshopify.com"
                className="h-9 w-full rounded-lg border border-gray-200 bg-white px-3 text-sm outline-none focus:border-[#4368E5]"
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-xs font-semibold text-gray-600">Admin token</span>
              <input
                value={shopifyToken}
                onChange={(event) => updateShopifySetting({ shopifyToken: event.target.value })}
                placeholder="shpat_..."
                className="h-9 w-full rounded-lg border border-gray-200 bg-white px-3 text-sm outline-none focus:border-[#4368E5]"
              />
            </label>
            <SelectField
              label="Moneda"
              value={shopifyCurrencyMode}
              options={["FUNNEL_CURRENCY", "USD_CONVERSION"]}
              formatOption={(value) => (value === "USD_CONVERSION" ? "Convertir a USD" : "Moneda del embudo")}
              onChange={(shopifyCurrencyMode) => updateShopifySetting({ shopifyCurrencyMode })}
            />
            <SelectField
              label="Tipo de integración"
              value={shopifyCheckoutProvider}
              options={["fluxi", "shopify"]}
              formatOption={(value) => (value === "shopify" ? "Checkout Shopify" : "Sincronizar órdenes")}
              onChange={(shopifyCheckoutProvider) => updateShopifySetting({ shopifyCheckoutProvider })}
            />
            <label className="block">
              <span className="mb-1 block text-xs font-semibold text-gray-600">Storefront token</span>
              <input
                value={shopifyStorefrontToken}
                onChange={(event) => updateShopifySetting({ shopifyStorefrontToken: event.target.value })}
                className="h-9 w-full rounded-lg border border-gray-200 bg-white px-3 text-sm outline-none focus:border-[#4368E5]"
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-xs font-semibold text-gray-600">Client ID</span>
              <input
                value={shopifyClientId}
                onChange={(event) => updateShopifySetting({ shopifyClientId: event.target.value })}
                className="h-9 w-full rounded-lg border border-gray-200 bg-white px-3 text-sm outline-none focus:border-[#4368E5]"
              />
            </label>
          </div>
          <div className="grid gap-2">
            {rows.map((row) => (
              <div key={`${websiteId}-shopify-${row.id}`} className="grid gap-2 rounded-lg bg-white p-3 sm:grid-cols-[minmax(0,1fr)_160px_210px] sm:items-center">
                <div className="min-w-0">
                  <p className="truncate text-xs font-semibold text-gray-800">{row.name}</p>
                  <p className="mt-0.5 truncate font-mono text-[11px] text-gray-500">{row.id} · {funnelProductTypeLabel(row.type)}</p>
                </div>
                <input
                  value={row.sku}
                  onChange={(event) => updateRow(row.id, { sku: event.target.value })}
                  placeholder="SKU"
                  className="h-9 rounded-lg border border-gray-200 bg-white px-3 text-sm outline-none focus:border-[#4368E5]"
                />
                <input
                  value={row.id_shopify_product}
                  onChange={(event) => updateRow(row.id, { id_shopify_product: event.target.value })}
                  placeholder="ID producto Shopify"
                  className="h-9 rounded-lg border border-gray-200 bg-white px-3 text-sm outline-none focus:border-[#4368E5]"
                />
              </div>
            ))}
          </div>
          {shopifyIncomplete ? (
            <p className="text-xs leading-5 text-gray-500">
              Completa tienda, token y el ID de Shopify de cada producto antes de guardar la sincronización.
            </p>
          ) : null}
        </div>
      )}
    </div>
  );
};

// ——— Sub-pantalla "Configurar imágenes": candidatas del embudo para el chatbot ———

const FUNNEL_IMAGE_SOURCE_LABELS: Record<string, string> = {
  portada: "Portada",
  galeria_producto: "Galería del producto",
  pagina: "Página",
  custom_upload: "Subida manual",
};

const funnelImageSourceLabel = (source: string) =>
  FUNNEL_IMAGE_SOURCE_LABELS[String(source || "").trim()] || humanize(source) || "Imagen";

type FunnelImageCandidateDraft = {
  key: string;
  url: string;
  source: string;
  sectionName: string;
  description: string;
};

const FunnelImageLightbox = ({ url, alt, onClose }: { url: string; alt: string; onClose: () => void }) => {
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onClose]);
  return createPortal(
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center bg-black/85 p-6"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <img src={url} alt={alt} className="max-h-full max-w-full rounded-lg object-contain" />
      <button
        type="button"
        onClick={onClose}
        className="absolute right-4 top-4 inline-flex size-9 items-center justify-center rounded-full bg-white/10 text-white transition hover:bg-white/20"
        aria-label="Cerrar"
      >
        <X className="size-5" />
      </button>
    </div>,
    document.body
  );
};

const FunnelChatbotImagesScreen = ({
  mode,
  websiteId,
  websiteName,
  productId,
  productLabel,
  backLabel,
  salesConfig,
  confirmationImage,
  onBack,
  onSalesConfigChange,
  onConfirmationImageChange,
}: {
  mode: "sales" | "confirmation";
  websiteId: string;
  websiteName: string;
  productId: string;
  productLabel: string;
  backLabel: string;
  salesConfig?: CommerceChatbotImagesConfig | null;
  confirmationImage?: CommerceConfirmationImage | null;
  onBack: () => void;
  onSalesConfigChange?: (config: CommerceChatbotImagesConfig | null) => void;
  onConfirmationImageChange?: (image: CommerceConfirmationImage) => void;
}) => {
  const [candidates, setCandidates] = useState<FunnelImageCandidateDraft[] | null>(null);
  const [uploadingCustomImage, setUploadingCustomImage] = useState(false);
  const [customImageError, setCustomImageError] = useState<string | null>(null);
  const [loadingCandidates, setLoadingCandidates] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [reloadNonce, setReloadNonce] = useState(0);
  const [selected, setSelected] = useState<CommerceChatbotSelectedImage[]>(() =>
    mode === "sales"
      ? (salesConfig?.selected || []).map((entry) => ({ ...entry }))
      : confirmationImage
        ? [{ key: confirmationImage.key, url: confirmationImage.url, description: confirmationImage.description, source: "" }]
        : []
  );
  const [instructions, setInstructions] = useState(salesConfig?.instructions || "");
  const [instructionsOpen, setInstructionsOpen] = useState(Boolean((salesConfig?.instructions || "").trim()));
  const [describingKeys, setDescribingKeys] = useState<Record<string, boolean>>({});
  const [lightbox, setLightbox] = useState<{ url: string; alt: string } | null>(null);

  // Refs espejo para que los callbacks async (describe, carga) lean el estado más reciente.
  const selectedRef = useRef(selected);
  const instructionsRef = useRef(instructions);
  const describingRef = useRef<Record<string, boolean>>({});
  // Sin config previa aplicamos los defaults (portada + primera de galería) al abrir.
  const hasExistingConfigRef = useRef(mode === "sales" ? salesConfig != null : confirmationImage != null);
  const implicitDefaultKeyRef = useRef<string | null>(null);

  const emitSelection = (nextSelected: CommerceChatbotSelectedImage[], nextInstructions: string) => {
    if (mode === "sales") {
      if (
        implicitDefaultKeyRef.current &&
        nextSelected.length === 1 &&
        nextSelected[0]?.key === implicitDefaultKeyRef.current &&
        !nextInstructions.trim()
      ) {
        onSalesConfigChange?.(null);
        return;
      }
      onSalesConfigChange?.({
        selected: nextSelected.slice(0, MAX_CHATBOT_IMAGES_SELECTED),
        instructions: nextInstructions,
      });
      return;
    }
    const image = nextSelected[0];
    if (image) {
      onConfirmationImageChange?.({ key: image.key, url: image.url, description: image.description });
    }
  };

  const commitSelected = (next: CommerceChatbotSelectedImage[]) => {
    selectedRef.current = next;
    setSelected(next);
    emitSelection(next, instructionsRef.current);
  };

  const commitInstructions = (next: string) => {
    instructionsRef.current = next;
    setInstructions(next);
    emitSelection(selectedRef.current, next);
  };

  const describeImage = (key: string, url: string) => {
    if (!url || describingRef.current[key]) return;
    describingRef.current = { ...describingRef.current, [key]: true };
    setDescribingKeys(describingRef.current);
    void chatbotV2Service
      .describeFunnelImage(url)
      .then((result) => {
        const description = String(result?.description || "").trim();
        if (!description) return;
        // Solo llenamos si el campo sigue vacío: lo que escriba el usuario manda.
        setCandidates((current) =>
          current
            ? current.map((candidate) =>
                candidate.key === key && !candidate.description.trim() ? { ...candidate, description } : candidate
              )
            : current
        );
        const entry = selectedRef.current.find((item) => item.key === key);
        if (entry && !entry.description.trim()) {
          commitSelected(
            selectedRef.current.map((item) => (item.key === key ? { ...item, description } : item))
          );
        }
      })
      .catch(() => {
        // Silencioso: el campo queda editable para escribir la descripción a mano.
      })
      .finally(() => {
        describingRef.current = Object.fromEntries(
          Object.entries(describingRef.current).filter(([describingKey]) => describingKey !== key)
        );
        setDescribingKeys(describingRef.current);
      });
  };

  useEffect(() => {
    let active = true;
    setLoadingCandidates(true);
    setLoadError(null);
    void Promise.resolve(getFunnelChatbotImages(websiteId, productId))
      .then((data) => {
        if (!active) return;
        const images = isRecord(data) && Array.isArray(data.images) ? data.images : [];
        const seen = new Set<string>();
        const drafts: FunnelImageCandidateDraft[] = [];
        images.forEach((image, index) => {
          if (!isRecord(image)) return;
          const url = String(image.url || "").trim();
          if (!url) return;
          const key = String(image.key || "").trim() || `imagen-${index}`;
          if (seen.has(key)) return;
          seen.add(key);
          drafts.push({
            key,
            url,
            source: String(image.source || "").trim(),
            sectionName: String(image.sectionName || "").trim(),
            description: String(image.description || "").trim(),
          });
        });
        // Las descripciones ya guardadas mandan sobre las que trae el endpoint.
        const savedByKey = new Map(selectedRef.current.map((entry) => [entry.key, entry]));
        const merged = drafts.map((draft) => {
          const saved = savedByKey.get(draft.key);
          return saved && saved.description.trim() ? { ...draft, description: saved.description } : draft;
        });
        setCandidates(merged);
        if (!hasExistingConfigRef.current && merged.length) {
          hasExistingConfigRef.current = true;
          // Espejo del default nuevo del backend (Julian): si nunca se configuró, la selección
          // es LA PRIMERA imagen del embudo (1 sola). En confirmación sigue siendo la portada.
          const defaults: FunnelImageCandidateDraft[] =
            mode === "confirmation"
              ? [merged.find((item) => item.source === "portada") || merged[0]]
              : [merged[0]];
          if (defaults.length) {
            if (mode === "sales") implicitDefaultKeyRef.current = defaults[0].key;
            commitSelected(
              defaults.map((item) => ({ key: item.key, url: item.url, description: item.description, source: item.source }))
            );
          }
        }
        // Auto-describe: toda chuleada sin descripción se describe una sola vez con visión.
        selectedRef.current.forEach((entry) => {
          if (entry.description.trim()) return;
          const candidate = merged.find((item) => item.key === entry.key);
          if (candidate) describeImage(entry.key, candidate.url);
        });
      })
      .catch((error) => {
        if (active) setLoadError(getErrorMessage(error));
      })
      .finally(() => {
        if (active) setLoadingCandidates(false);
      });
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [websiteId, productId, reloadNonce]);

  const isSelectedKey = (key: string) => selected.some((entry) => entry.key === key);
  const selectionLimitReached = mode === "sales" && selected.length >= MAX_CHATBOT_IMAGES_SELECTED;

  const toggleCandidate = (candidate: FunnelImageCandidateDraft) => {
    const already = selectedRef.current.some((entry) => entry.key === candidate.key);
    if (mode === "confirmation") {
      if (already) return; // Comportamiento radio: siempre queda una elegida.
      commitSelected([
        { key: candidate.key, url: candidate.url, description: candidate.description, source: candidate.source },
      ]);
      if (!candidate.description.trim()) describeImage(candidate.key, candidate.url);
      return;
    }
    if (already) {
      commitSelected(selectedRef.current.filter((entry) => entry.key !== candidate.key));
      return;
    }
    if (selectedRef.current.length >= MAX_CHATBOT_IMAGES_SELECTED) {
      toast.error(`Máximo ${MAX_CHATBOT_IMAGES_SELECTED} imágenes por producto.`);
      return;
    }
    commitSelected([
      ...selectedRef.current,
      { key: candidate.key, url: candidate.url, description: candidate.description, source: candidate.source },
    ]);
    if (!candidate.description.trim()) describeImage(candidate.key, candidate.url);
  };

  // Subida manual: la galería del embudo no siempre tiene la foto que el owner
  // quiere enviar en la confirmación — puede subir la suya y queda seleccionada.
  const addCustomImage = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setCustomImageError("Solo se permiten imágenes.");
      return;
    }
    setCustomImageError(null);
    setUploadingCustomImage(true);
    try {
      const base64File = await toBase64(file);
      const filename = `${Date.now()}-${sanitizeUploadFilename(file.name)}`;
      const response = await uploadImagesToS3({
        file: base64File,
        folder: "whatsapp-confirmation-images",
        filename,
      });
      const url =
        typeof response?.s3_url === "string" ? response.s3_url : typeof response?.url === "string" ? response.url : "";
      if (!url) throw new Error("No se recibió URL de la imagen subida.");
      const candidate: FunnelImageCandidateDraft = {
        key: `custom-${Date.now()}`,
        url,
        source: "custom_upload",
        sectionName: "Subida manual",
        description: "",
      };
      setCandidates((current) => [candidate, ...(current || [])]);
      toggleCandidate(candidate);
    } catch (error) {
      console.error("Error subiendo imagen personalizada:", error);
      setCustomImageError("No pudimos subir la imagen. Intenta de nuevo.");
    } finally {
      setUploadingCustomImage(false);
    }
  };

  const setCandidateDescription = (key: string, description: string) => {
    setCandidates((current) =>
      current ? current.map((candidate) => (candidate.key === key ? { ...candidate, description } : candidate)) : current
    );
    if (selectedRef.current.some((entry) => entry.key === key)) {
      commitSelected(selectedRef.current.map((entry) => (entry.key === key ? { ...entry, description } : entry)));
    }
  };

  return (
    <div className="mx-auto max-w-5xl space-y-5 overflow-x-hidden">
      <div>
        <IntegrationBackButton label={backLabel} onBack={onBack} />
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="text-base font-semibold text-gray-950">Imágenes de {websiteName}</h3>
            <p className="mt-1 max-w-2xl text-sm leading-6 text-gray-500">
              {mode === "sales"
                ? `Elige hasta ${MAX_CHATBOT_IMAGES_SELECTED} imágenes de este embudo para que la IA pueda enviarlas en el chat cuando hable de ${productLabel}.`
                : `Elige la foto que acompañará el mensaje de confirmación de ${productLabel}.`}
            </p>
          </div>
          {mode === "sales" ? (
            <span
              className={`inline-flex h-8 shrink-0 items-center rounded-full border px-3 text-xs font-semibold ${
                selectionLimitReached ? "border-gray-950 bg-gray-950 text-white" : "border-gray-200 bg-white text-gray-700"
              }`}
            >
              {selected.length}/{MAX_CHATBOT_IMAGES_SELECTED}
            </span>
          ) : null}
        </div>
      </div>

      {mode === "sales" ? (
        <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-sm leading-6 text-gray-600">
              Fluxi elegirá dinámicamente cuándo enviarlas según la conversación.
            </p>
            <button
              type="button"
              onClick={() => setInstructionsOpen((open) => !open)}
              className="inline-flex h-8 shrink-0 items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 text-xs font-semibold text-gray-800 transition hover:bg-gray-100"
            >
              Agregar instrucciones
              <ChevronDown className={`size-3.5 transition ${instructionsOpen ? "rotate-180" : ""}`} />
            </button>
          </div>
          {instructionsOpen ? (
            <textarea
              value={instructions}
              onChange={(event) => commitInstructions(event.target.value)}
              rows={3}
              placeholder="Ej: envía la foto del empaque cuando pregunten cómo llega el pedido."
              className="mt-3 w-full resize-y rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm leading-6 text-gray-800 outline-none focus:border-[#4368E5]"
            />
          ) : null}
        </div>
      ) : (
        <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm leading-6 text-gray-600">
          La plantilla de confirmación lleva una sola foto. Por defecto usamos la portada del embudo; aquí puedes elegir otra.
        </div>
      )}

      {!loadingCandidates ? (
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <p className="text-xs text-gray-500">¿No está la foto que quieres? Súbela tú mismo.</p>
          <label
            className={`inline-flex h-8 cursor-pointer items-center gap-1.5 rounded-full border border-gray-200 bg-white px-3 text-xs font-semibold text-gray-800 transition hover:bg-gray-50 ${
              uploadingCustomImage ? "pointer-events-none opacity-60" : ""
            }`}
          >
            {uploadingCustomImage ? <Loader2 className="size-3.5 animate-spin" /> : <Plus className="size-3.5" />}
            Agregar imagen
            <input
              type="file"
              accept="image/*"
              className="hidden"
              disabled={uploadingCustomImage}
              onChange={(event) => void addCustomImage(event)}
            />
          </label>
          {customImageError ? <p className="w-full text-xs text-red-600">{customImageError}</p> : null}
        </div>
      ) : null}
      {loadingCandidates ? (
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3" aria-live="polite" aria-busy="true">
          {Array.from({ length: 6 }).map((_, index) => (
            <div key={`funnel-image-skeleton-${index}`} className="overflow-hidden rounded-xl border border-gray-100 bg-white">
              <div className="h-44 w-full animate-pulse bg-gray-100" />
              <div className="space-y-2 p-3">
                <div className="h-3 w-24 animate-pulse rounded bg-gray-100" />
                <div className="h-9 w-full animate-pulse rounded-lg bg-gray-100" />
              </div>
            </div>
          ))}
          <span className="sr-only">Cargando imágenes del embudo...</span>
        </div>
      ) : loadError ? (
        <div className="rounded-xl bg-red-50 px-4 py-3 text-sm leading-6 text-red-700">
          <p>{loadError}</p>
          <button
            type="button"
            onClick={() => setReloadNonce((nonce) => nonce + 1)}
            className="mt-2 inline-flex h-8 items-center gap-1.5 rounded-lg border border-red-200 bg-white px-3 text-xs font-semibold text-red-700 transition hover:bg-red-100"
          >
            <RefreshCw className="size-3.5" />
            Reintentar
          </button>
        </div>
      ) : !candidates || candidates.length === 0 ? (
        <div className="rounded-xl bg-gray-50 px-4 py-5 text-sm leading-6 text-gray-500">
          No encontramos imágenes en este embudo.
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {candidates.map((candidate) => {
            const checked = isSelectedKey(candidate.key);
            const disabled = !checked && selectionLimitReached;
            const describing = Boolean(describingKeys[candidate.key]);
            return (
              <div
                key={candidate.key}
                className={`overflow-hidden rounded-xl border bg-white transition ${
                  checked ? "border-gray-950" : "border-gray-200"
                } ${disabled ? "opacity-60" : ""}`}
              >
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setLightbox({ url: candidate.url, alt: candidate.description || "Imagen del embudo" })}
                    className="block aspect-square w-full cursor-zoom-in bg-gray-100"
                    title="Ver en grande"
                  >
                    <img
                      src={candidate.url}
                      alt={candidate.description || funnelImageSourceLabel(candidate.source)}
                      loading="lazy"
                      className="aspect-square w-full object-contain"
                    />
                  </button>
                  <span className="absolute left-2 top-2 inline-flex max-w-[70%] items-center truncate rounded-full bg-gray-950/70 px-2 py-0.5 text-[11px] font-semibold text-white">
                    {funnelImageSourceLabel(candidate.source)}
                  </span>
                  <button
                    type="button"
                    disabled={disabled}
                    onClick={() => toggleCandidate(candidate)}
                    aria-pressed={checked}
                    title={
                      checked
                        ? mode === "confirmation"
                          ? "Foto elegida"
                          : "Quitar del chat"
                        : disabled
                          ? `Máximo ${MAX_CHATBOT_IMAGES_SELECTED} imágenes`
                          : mode === "confirmation"
                            ? "Elegir esta foto"
                            : "Enviar en el chat"
                    }
                    className={`absolute right-2 top-2 inline-flex size-7 items-center justify-center border shadow-sm transition ${
                      mode === "confirmation" ? "rounded-full" : "rounded-md"
                    } ${
                      checked
                        ? "border-gray-950 bg-gray-950 text-white"
                        : "border-gray-300 bg-white text-transparent hover:border-gray-500"
                    } disabled:cursor-not-allowed`}
                  >
                    <Check className="size-4" />
                  </button>
                </div>
                <div className="space-y-1.5 p-3">
                  {candidate.sectionName ? (
                    <p className="truncate text-[11px] font-semibold text-gray-400">{candidate.sectionName}</p>
                  ) : null}
                  <div className="relative">
                    <input
                      value={candidate.description}
                      onChange={(event) => setCandidateDescription(candidate.key, event.target.value)}
                      placeholder={describing ? "Describiendo imagen..." : "Describe la imagen para la IA"}
                      className="h-9 w-full rounded-lg border border-gray-200 bg-white px-3 pr-8 text-xs text-gray-800 outline-none focus:border-[#4368E5]"
                    />
                    {describing ? (
                      <Loader2 className="absolute right-2.5 top-1/2 size-4 -translate-y-1/2 animate-spin text-gray-400" />
                    ) : null}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {lightbox ? <FunnelImageLightbox url={lightbox.url} alt={lightbox.alt} onClose={() => setLightbox(null)} /> : null}
    </div>
  );
};

const ConfirmationsTab = ({
  flows,
  validationError,
  approvedTemplates,
  pickerTemplates,
  onChange,
  onOpenTemplates,
  onImagesScreenChange,
}: {
  flows: CommerceFlowDraft[];
  validationError?: string | null;
  approvedTemplates: WhatsAppTemplate[];
  /** Todas las plantillas no-guardian (incluye PENDING), para mostrar su estado en el picker. */
  pickerTemplates: WhatsAppTemplate[];
  onChange: (rows: CommerceFlowDraft[]) => void;
  onOpenTemplates: () => void;
  onImagesScreenChange?: (open: boolean) => void;
}) => {
  const { catalogProducts, catalogLoading } = useFluxiProductsCatalog();
  const { websites, initialize: initializeWebsites } = useWebsitesStore();
  const [expandedFlowId, setExpandedFlowId] = useState<string | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [imagesTarget, setImagesTarget] = useState<{ flowId: string; productId: string; websiteId: string } | null>(null);
  // Sub-pantalla catálogo (scope "Todos los productos"): elegir foto de confirmación por producto.
  const [catalogTargetFlowId, setCatalogTargetFlowId] = useState<string | null>(null);

  useEffect(() => {
    void initializeWebsites();
  }, [initializeWebsites]);

  const updateFlow = (id: string, patch: Partial<CommerceFlowDraft>) => {
    onChange(flows.map((flow) => (flow.id === id ? { ...flow, ...patch } : flow)));
  };
  const commitFlow = (id: string, patch: Partial<CommerceFlowDraft>) => {
    const next = flows.map((flow) => {
      if (flow.id !== id) return flow;
      const merged = { ...flow, ...patch };
      return isCommerceFlowDraftCommitted(merged) ? { ...merged, ephemeral: false } : merged;
    });
    onChange(next);
  };
	  const updateLogisticsPolicy = (id: string, policy: CommerceLogisticsPolicy) => {
	    commitFlow(id, { logisticsPolicy: policy });
	  };
	  const setFlowEnabled = (flow: CommerceFlowDraft, enabled: boolean) => {
	    if (!enabled) {
	      updateFlow(flow.id, { enabled: false });
	      return;
	    }
	    const issue = commerceFlowActivationIssues(flow)[0];
	    if (issue) {
	      toast.error(issue);
	      return;
	    }
	    updateFlow(flow.id, { enabled: true, ephemeral: false });
	  };
  const selectProduct = (flow: CommerceFlowDraft, value: string, product?: Product) => {
    if (!value) return;
    if (value === ALL_PRODUCTS_OPTION) {
      commitFlow(flow.id, { productIds: [], productScope: "all", productLabels: {} });
      return;
    }
    const selected = new Set(flow.productIds || []);
    selected.add(value);
    const productLabels = { ...(flow.productLabels || {}) };
    if (product) {
      const label = commerceFlowProductLabelSnapshot(product);
      if (label) productLabels[value] = label;
    }
    commitFlow(flow.id, { productIds: Array.from(selected), productScope: "selected", productLabels });
  };
  const removeProduct = (flow: CommerceFlowDraft, productId: string) => {
    if (productId === ALL_PRODUCTS_OPTION) {
      commitFlow(flow.id, { productIds: [], productScope: "selected", productLabels: {}, confirmationImageByProductId: {} });
      return;
    }
    const productLabels = { ...(flow.productLabels || {}) };
    delete productLabels[productId];
    const confirmationImageByProductId = { ...(flow.confirmationImageByProductId || {}) };
    delete confirmationImageByProductId[productId];
    const checkoutWebsiteByProductId = { ...(flow.checkoutWebsiteByProductId || {}) };
    delete checkoutWebsiteByProductId[productId];
    commitFlow(flow.id, {
      productIds: (flow.productIds || []).filter((id) => id !== productId),
      productScope: "selected",
      productLabels,
      confirmationImageByProductId,
      checkoutWebsiteByProductId,
    });
  };
  const addFlow = () => {
    const id = `confirmation-${Date.now()}`;
    const title = nextSequentialFlowTitle(
      "Confirmación",
      flows.map((flow) => flow.title)
    );
    onChange([
      {
        id,
        title,
	        enabled: false,
        flowType: "confirmation",
        mode: null,
        templateName: "chatbot_confirmacion_inicio_v1",
                templateLanguage: "es",
                instructions: "",
                productScope: "selected",
                productIds: [],
                productLabels: {},
                requiredFields: [],
                checkoutWebsiteIds: [],
                checkoutWebsiteByProductId: {},
                logisticsPolicy: defaultCommerceLogisticsPolicy(),
                ephemeral: true,
              },
      ...flows,
    ]);
    setExpandedFlowId(id);
  };
  const deleteFlow = (id: string) => {
    onChange(flows.filter((flow) => flow.id !== id));
    setDeleteConfirmId(null);
    if (expandedFlowId === id) setExpandedFlowId(null);
  };

  const imagesTargetFlow = imagesTarget ? flows.find((flow) => flow.id === imagesTarget.flowId) : undefined;
  const catalogTargetFlow = catalogTargetFlowId ? flows.find((flow) => flow.id === catalogTargetFlowId) : undefined;
  const imagesScreenOpen = Boolean(imagesTarget && imagesTargetFlow) || Boolean(catalogTargetFlow);
  useEffect(() => {
    onImagesScreenChange?.(imagesScreenOpen);
    return () => onImagesScreenChange?.(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imagesScreenOpen]);
  if (imagesTarget && imagesTargetFlow) {
    const targetWebsite = websites.find((website) => String(website.websiteId) === imagesTarget.websiteId);
    const targetProduct = catalogProducts.find((item) => String(item.id) === imagesTarget.productId) || null;
    const targetChip = targetProduct ? productPickerChipContent(targetProduct) : null;
    return (
      <FunnelChatbotImagesScreen
        mode="confirmation"
        websiteId={imagesTarget.websiteId}
        websiteName={targetWebsite ? checkoutWebsiteName(targetWebsite) : `Embudo ${imagesTarget.websiteId.slice(0, 6)}`}
        productId={imagesTarget.productId}
        productLabel={imagesTargetFlow.productLabels?.[imagesTarget.productId] || targetChip?.name || "este producto"}
        backLabel={catalogTargetFlow ? "Volver al catálogo" : "Volver a confirmaciones"}
        confirmationImage={imagesTargetFlow.confirmationImageByProductId?.[imagesTarget.productId] || null}
        onBack={() => setImagesTarget(null)}
        onConfirmationImageChange={(image) => {
          const current = flows.find((flow) => flow.id === imagesTarget.flowId);
          if (!current) return;
          // Snapshot del nombre: con scope "todos" el producto no está en productIds y el
          // mapping serializado necesita su product_name (mismo snapshot que al seleccionarlo).
          const productLabels = { ...(current.productLabels || {}) };
          if (targetProduct) {
            const label = commerceFlowProductLabelSnapshot(targetProduct);
            if (label) productLabels[imagesTarget.productId] = label;
          }
          commitFlow(current.id, {
            productLabels,
            confirmationImageByProductId: {
              ...(current.confirmationImageByProductId || {}),
              [imagesTarget.productId]: image,
            },
            // Registramos el embudo del que salió la foto: se serializa como website_id del mapping.
            checkoutWebsiteByProductId: {
              ...(current.checkoutWebsiteByProductId || {}),
              [imagesTarget.productId]: imagesTarget.websiteId,
            },
          });
        }}
      />
    );
  }
  if (catalogTargetFlow) {
    const chosenById = catalogTargetFlow.confirmationImageByProductId || {};
    const chosenCount = catalogProducts.filter((product) => chosenById[String(product.id)]).length;
    return (
      <div className="mx-auto max-w-5xl space-y-5 overflow-x-hidden">
        <div>
          <IntegrationBackButton label="Volver a confirmaciones" onBack={() => setCatalogTargetFlowId(null)} />
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <h3 className="text-base font-semibold text-gray-950">Imágenes de confirmación por producto</h3>
              <p className="mt-1 max-w-2xl text-sm leading-6 text-gray-500">
                La confirmación de cada producto sale con su foto principal. Aquí puedes elegir otra foto para los
                productos que quieras.
              </p>
            </div>
            {chosenCount ? (
              <span className="inline-flex h-8 shrink-0 items-center rounded-full border border-gray-200 bg-white px-3 text-xs font-semibold text-gray-700">
                {chosenCount} con foto elegida
              </span>
            ) : null}
          </div>
        </div>
        {catalogLoading && catalogProducts.length === 0 ? (
          <div className="grid gap-3">
            {[0, 1, 2].map((index) => (
              <div key={index} className="flex h-16 animate-pulse items-center gap-3 rounded-lg border border-gray-200 bg-white px-3">
                <span className="size-9 shrink-0 rounded bg-gray-100" />
                <span className="h-3 w-44 rounded bg-gray-100" />
                <span className="ml-auto h-8 w-32 rounded-lg bg-gray-100" />
              </div>
            ))}
          </div>
        ) : catalogProducts.length === 0 ? (
          <div className="rounded-xl bg-gray-50 px-4 py-5 text-sm leading-6 text-gray-500">
            No tienes productos en el catálogo todavía.
          </div>
        ) : (
          <div className="grid gap-3">
            {catalogProducts.map((product) => {
              const productId = String(product.id);
              const chip = productPickerChipContent(product);
              const image = chosenById[productId] || null;
              const recordedWebsiteId = String(catalogTargetFlow.checkoutWebsiteByProductId?.[productId] || "").trim();
              const recordedWebsite = recordedWebsiteId
                ? websites.find((website) => String(website.websiteId) === recordedWebsiteId)
                : undefined;
              const eligibleWebsite =
                recordedWebsite ||
                checkoutWebsitesForProduct(productId, websites, product)[0] ||
                websites.find((website) => checkoutWebsiteContainsProduct(website, productId, product)) ||
                null;
              return (
                <div key={`catalog-image-${productId}`} className="min-w-0 rounded-lg border border-gray-200 bg-white p-3">
                  <div className="flex items-center gap-3">
                    {chip?.thumbnail ? (
                      <img src={chip.thumbnail} alt="" className="size-9 shrink-0 rounded object-cover" />
                    ) : (
                      <div className="flex size-9 shrink-0 items-center justify-center rounded bg-gray-100 text-xs font-semibold text-gray-500">
                        {(chip?.name || productId).slice(0, 1).toUpperCase()}
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-semibold text-gray-950">{chip?.name || productId}</p>
                      <p className="truncate text-xs leading-5 text-gray-500">
                        {image ? "Foto de confirmación elegida" : "Usará la foto principal del producto"}
                      </p>
                    </div>
                    {image?.url ? (
                      <img src={image.url} alt="" className="size-8 shrink-0 rounded border border-gray-200 object-cover" />
                    ) : null}
                    {eligibleWebsite ? (
                      <button
                        type="button"
                        onClick={() =>
                          setImagesTarget({
                            flowId: catalogTargetFlow.id,
                            productId,
                            websiteId: String(eligibleWebsite.websiteId),
                          })
                        }
                        className="inline-flex h-8 shrink-0 items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 text-xs font-semibold text-gray-800 transition hover:bg-gray-50 hover:text-gray-950"
                      >
                        <ImageIcon className="size-3.5" />
                        Configurar imagen
                      </button>
                    ) : (
                      <p className="max-w-56 text-right text-xs leading-5 text-amber-700">
                        Publica un embudo con este producto para elegir su foto.
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl space-y-5 overflow-x-hidden">
      <FlowListHeader
        title="Tus confirmaciones"
        subtitle="Confirma pedidos por WhatsApp antes de despachar"
        addLabel="+ Agregar confirmación"
        onAdd={addFlow}
      />

      {validationError ? (
          <div className="rounded-xl bg-red-50 px-4 py-3 text-sm leading-5 text-red-700">{validationError}</div>
        ) : null}

        <div className="grid gap-3">
          {flows.length === 0 ? (
            <div className="rounded-xl bg-gray-50 px-4 py-5 text-sm leading-6 text-gray-500">
              No hay confirmaciones configuradas. Agrega una para definir cómo Fluxi pide confirmación por WhatsApp.
            </div>
          ) : null}
          {flows.map((flow) => {
            const isExpanded = expandedFlowId === flow.id;
            const usedProducts = collectActiveCommerceProductUsage(flows, "confirmation", flow.id);
            const draftBadge = !flow.enabled && commerceFlowActivationIssues(flow).length > 0;
	            return (
              <article
                key={flow.id}
                className={`relative rounded-xl border bg-gray-50 ${
                  isExpanded ? "border-gray-950" : "border-transparent"
                }`}
              >
		                <EditableActionCardHeader
		                  title={flow.title}
		                  fallbackTitle="Confirmación"
		                  isExpanded={isExpanded}
		                  enabled={flow.enabled}
		                  onToggleExpanded={() => setExpandedFlowId(isExpanded ? null : flow.id)}
		                  onToggleEnabled={(enabled) => setFlowEnabled(flow, enabled)}
		                  onDelete={() => setDeleteConfirmId(flow.id)}
		                  onTitleChange={(title) => updateFlow(flow.id, { title })}
		                  deleteTitle="Eliminar confirmación"
		                  extra={
		                    draftBadge ? (
		                      <span className="inline-flex shrink-0 items-center rounded-full border border-gray-200 bg-white px-2.5 py-1 text-[11px] font-semibold text-gray-600">
		                        Borrador
		                      </span>
		                    ) : null
		                  }
		                />

		                {isExpanded ? (
		                  <div className="border-t border-gray-100 px-4 pb-4">
	                    <div className="mt-4">
                      <CommerceFlowProductSection
                        flow={flow}
                                catalogProducts={catalogProducts}
                                catalogLoading={catalogLoading}
                                usedProducts={usedProducts}
                                includeAllOption={flow.mode !== "fluxi_checkout_link"}
                                selectedDisplay="cards"
                                onSelectProduct={(productId, product) => selectProduct(flow, productId, product)}
                                onRemoveProduct={(productId) => removeProduct(flow, productId)}
                                renderCardAction={
                                  flow.confirmationPhotosEnabled
                                    ? (productId, product) => {
                                        const image = flow.confirmationImageByProductId?.[productId] || null;
                                        const recordedWebsiteId = String(
                                          flow.checkoutWebsiteByProductId?.[productId] || ""
                                        ).trim();
                                        const recordedWebsite = recordedWebsiteId
                                          ? websites.find((website) => String(website.websiteId) === recordedWebsiteId)
                                          : undefined;
                                        const eligibleWebsite =
                                          recordedWebsite ||
                                          checkoutWebsitesForProduct(productId, websites, product)[0] ||
                                          websites.find((website) =>
                                            checkoutWebsiteContainsProduct(website, productId, product)
                                          ) ||
                                          null;
                                        return (
                                          <div className="flex flex-wrap items-center justify-between gap-2">
                                            <div className="flex min-w-0 items-center gap-2">
                                              {image?.url ? (
                                                <img
                                                  src={image.url}
                                                  alt=""
                                                  className="size-8 shrink-0 rounded border border-gray-200 object-cover"
                                                />
                                              ) : (
                                                <ImageIcon className="size-4 shrink-0 text-gray-400" />
                                              )}
                                              <p className="truncate text-xs leading-5 text-gray-500">
                                                {image
                                                  ? "Foto de confirmación elegida"
                                                  : "Usará la foto principal del producto"}
                                              </p>
                                            </div>
                                            {eligibleWebsite ? (
                                              <button
                                                type="button"
                                                onClick={() =>
                                                  setImagesTarget({
                                                    flowId: flow.id,
                                                    productId,
                                                    websiteId: String(eligibleWebsite.websiteId),
                                                  })
                                                }
                                                className="inline-flex h-8 shrink-0 items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 text-xs font-semibold text-gray-800 transition hover:bg-gray-50 hover:text-gray-950"
                                              >
                                                <ImageIcon className="size-3.5" />
                                                Configurar imagen
                                              </button>
                                            ) : (
                                              <p className="text-xs leading-5 text-amber-700">
                                                Publica un embudo con este producto para elegir su foto.
                                              </p>
                                            )}
                                          </div>
                                        );
                                      }
                                    : undefined
                                }
                                renderAllCardAction={
                                  flow.confirmationPhotosEnabled
                                    ? () => {
                                        const chosenCount = Object.keys(flow.confirmationImageByProductId || {}).length;
                                        return (
                                          <div className="flex flex-wrap items-center justify-between gap-2">
                                            <div className="flex min-w-0 items-center gap-2">
                                              <ImageIcon className="size-4 shrink-0 text-gray-400" />
                                              <p className="truncate text-xs leading-5 text-gray-500">
                                                {chosenCount
                                                  ? `${chosenCount} ${chosenCount === 1 ? "producto con foto elegida" : "productos con foto elegida"}; el resto usa su foto principal`
                                                  : "Cada producto usará su foto principal"}
                                              </p>
                                            </div>
                                            <button
                                              type="button"
                                              onClick={() => setCatalogTargetFlowId(flow.id)}
                                              className="inline-flex h-8 shrink-0 items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 text-xs font-semibold text-gray-800 transition hover:bg-gray-50 hover:text-gray-950"
                                            >
                                              <ImageIcon className="size-3.5" />
                                              Configurar imágenes
                                            </button>
                                          </div>
                                        );
                                      }
                                    : undefined
                                }
                              />
                    </div>

                    <div className="mt-4">
                      <TemplatePickerField
                        label="Plantilla de confirmación"
                        value={flow.templateName}
                        templates={pickerTemplates}
                        recommendedNames={["chatbot_confirmacion_inicio_v1", "chatbot_confirmacion_inicio_foto_v1"]}
                        onEditTemplates={onOpenTemplates}
                        onChange={(templateName, match) =>
                          commitFlow(flow.id, {
                            templateName,
                            templateLanguage: match?.language || flow.templateLanguage || "es",
                          })
                        }
                      />
                    </div>

                    <div className="mt-4 rounded-lg border border-gray-200 bg-white p-3">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-gray-950">
                            Habilitar fotos en el mensaje de confirmación
                          </p>
                          <p className="mt-0.5 text-xs leading-5 text-gray-500">
                            La confirmación llega con una foto del producto.
                          </p>
                        </div>
                        <ToggleSwitch
                          checked={flow.confirmationPhotosEnabled === true}
                          label={flow.confirmationPhotosEnabled ? "Activadas" : "Desactivadas"}
                          onChange={(enabled) => commitFlow(flow.id, { confirmationPhotosEnabled: enabled })}
                        />
                      </div>
                      {flow.confirmationPhotosEnabled ? (
                        <p className="mt-3 rounded-lg bg-gray-50 px-3 py-2 text-xs leading-5 text-gray-600">
                          Al activarlo creamos la plantilla de confirmación con foto (una sola) y Meta debe aprobarla;
                          mientras tanto seguimos usando la plantilla de texto, sin interrupciones. Los embudos usan por
                          defecto la foto principal del producto y aquí puedes elegir otra por embudo.
                        </p>
                      ) : null}
                    </div>

                    {/* Sin caja de fondo (Julian): título + tabla de acciones, directo. */}
                    <div className="mt-5">
                      <p className="text-sm font-semibold text-gray-950">Acciones logísticas</p>
                      <div className="mt-3">
                        <CommerceLogisticsPolicyEditor
                          policy={flow.logisticsPolicy}
                          approvedTemplates={approvedTemplates}
                          showConfirmationInstructions
                          onChange={(logisticsPolicy) => updateLogisticsPolicy(flow.id, logisticsPolicy)}
                        />
                      </div>
                    </div>
                  </div>
                ) : null}
              </article>
            );
          })}
        </div>

      {deleteConfirmId ? (
        <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/40 p-4">
          <div className="w-[min(380px,100%)] rounded-xl bg-white p-4 shadow-xl">
            <h3 className="text-base font-semibold text-gray-950">Eliminar confirmación</h3>
            <p className="mt-2 text-sm text-gray-500">Esta confirmación se quitará al guardar los cambios.</p>
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setDeleteConfirmId(null)}
                className="inline-flex h-9 items-center rounded-lg border border-gray-200 px-3 text-sm font-semibold text-gray-700 hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={() => deleteFlow(deleteConfirmId)}
                className="inline-flex h-9 items-center rounded-lg bg-red-600 px-3 text-sm font-semibold text-white hover:bg-red-700"
              >
                Eliminar
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

const CheckoutWebsiteLoadingSkeleton = ({ rows = 2 }: { rows?: number }) => (
  <div className="grid gap-3" aria-live="polite" aria-busy="true">
    {Array.from({ length: Math.max(1, rows) }).map((_, index) => (
      <div
        key={`checkout-website-loading-${index}`}
        className="grid gap-3 rounded-lg border border-gray-100 bg-gray-50 p-3 sm:grid-cols-[minmax(0,1fr)_minmax(260px,380px)] sm:items-start"
      >
        <div className="flex min-w-0 items-center gap-3">
          <div className="size-9 shrink-0 animate-pulse rounded bg-gray-200" />
          <div className="min-w-0 flex-1 space-y-2">
            <div className="h-3.5 w-3/4 animate-pulse rounded bg-gray-200" />
            <div className="h-2.5 w-24 animate-pulse rounded bg-gray-100" />
          </div>
        </div>
        <div className="min-w-0 space-y-2">
          <div className="h-10 animate-pulse rounded-lg bg-gray-200" />
          <div className="h-2.5 w-2/3 animate-pulse rounded bg-gray-100" />
        </div>
        <div className="sm:col-span-2 rounded-lg border border-gray-200 bg-white p-3">
          <div className="flex items-center justify-between gap-3">
            <div className="space-y-2">
              <div className="h-3 w-40 animate-pulse rounded bg-gray-200" />
              <div className="h-2.5 w-32 animate-pulse rounded bg-gray-100" />
            </div>
            <div className="h-9 w-28 animate-pulse rounded-full bg-gray-200" />
          </div>
        </div>
      </div>
    ))}
    <span className="sr-only">Cargando embudos de producto...</span>
  </div>
);

// Tabs internos de la card de producto en Toma de órdenes (Julian):
// Embudo fuente | Información | Oferta | Sincronizar órdenes.
type OrderProductCardTab = "embudo" | "info" | "oferta" | "sync";
const ORDER_PRODUCT_CARD_TABS: { id: OrderProductCardTab; label: string }[] = [
  { id: "embudo", label: "Embudo fuente" },
  { id: "info", label: "Información" },
  { id: "oferta", label: "Oferta" },
  { id: "sync", label: "Sincronizar órdenes" },
];

// Ángulos del embudo: el backend puede mandarlos como {titulo, descripcion} o como texto plano.
const normalizeFunnelProductAngle = (value: unknown): { titulo: string; descripcion: string } | null => {
  if (typeof value === "string") {
    const text = value.trim();
    return text ? { titulo: "", descripcion: text } : null;
  }
  if (!isRecord(value)) return null;
  const titulo = String(value.titulo || value.title || "").trim();
  const descripcion = String(value.descripcion || value.description || "").trim();
  if (!titulo && !descripcion) return null;
  return { titulo, descripcion };
};

type FlowProductContextState = {
  loading: boolean;
  error: string | null;
  data: CommerceFlowProductContext | null;
};

const OrderCaptureTab = ({
  flows,
  approvedTemplates,
  syncDraftsByWebsiteId,
  validationError,
  onSyncDraftChange,
  onImagesScreenChange,
  onChange,
}: {
  flows: CommerceFlowDraft[];
  approvedTemplates: WhatsAppTemplate[];
  syncDraftsByWebsiteId: Record<string, WebsiteSyncDraftState>;
  validationError?: string | null;
  onSyncDraftChange: (websiteId: string, draft: WebsiteSyncDraftState) => void;
  onImagesScreenChange?: (open: boolean) => void;
  onChange: (rows: CommerceFlowDraft[]) => void;
}) => {
  const { catalogProducts, catalogLoading } = useFluxiProductsCatalog();
	  const { websites, isLoading: websitesLoading, loadError: websitesLoadError, initialize } = useWebsitesStore();
	  const navigate = useNavigate();
		  const [expandedFlowId, setExpandedFlowId] = useState<string | null>(null);
		  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
		  const [imagesTarget, setImagesTarget] = useState<{ flowId: string; productId: string } | null>(null);
		  const [syncSelectorOpenByWebsiteId, setSyncSelectorOpenByWebsiteId] = useState<Record<string, boolean>>({});
		  const [syncProviderDraftByWebsiteId, setSyncProviderDraftByWebsiteId] = useState<Record<string, FunnelSyncSettingsProvider | "">>({});
		  // Card de producto (Julian): colapsada por defecto; expandida muestra tabs internos.
		  const [expandedProductCardKey, setExpandedProductCardKey] = useState<string | null>(null);
		  const [productCardTabByKey, setProductCardTabByKey] = useState<Record<string, OrderProductCardTab>>({});
		  // Cache simple del contexto del producto (tab Información): no re-fetch al cambiar de tab.
		  const [productContextByKey, setProductContextByKey] = useState<Record<string, FlowProductContextState>>({});

  useEffect(() => {
    void initialize();
  }, [initialize]);

  const productCardKey = (flowId: string, productId: string) => `${flowId}:${productId}`;
  const loadFlowProductContext = (flowId: string, productId: string, options?: { force?: boolean }) => {
    const key = productCardKey(flowId, productId);
    const existing = productContextByKey[key];
    if (!options?.force && existing && (existing.loading || existing.data)) return;
    setProductContextByKey((current) => ({ ...current, [key]: { loading: true, error: null, data: null } }));
    void chatbotV2Service
      .getFlowProductContext(flowId, productId)
      .then((data) => {
        setProductContextByKey((current) => ({ ...current, [key]: { loading: false, error: null, data } }));
      })
      .catch(() => {
        setProductContextByKey((current) => ({
          ...current,
          [key]: {
            loading: false,
            error: "No se pudo cargar la información (¿el producto tiene embudo conectado?)",
            data: null,
          },
        }));
      });
  };
  const setProductCardTab = (flowId: string, productId: string, tab: OrderProductCardTab) => {
    setProductCardTabByKey((current) => ({ ...current, [productCardKey(flowId, productId)]: tab }));
    if (tab === "info" || tab === "oferta") loadFlowProductContext(flowId, productId);
  };

  const updateFlow = (id: string, patch: Partial<CommerceFlowDraft>) => {
    onChange(flows.map((flow) => (flow.id === id ? { ...flow, ...patch } : flow)));
  };
  const commitFlow = (id: string, patch: Partial<CommerceFlowDraft>) => {
    const next = flows.map((flow) => {
      if (flow.id !== id) return flow;
      const merged = { ...flow, ...patch };
      return isCommerceFlowDraftCommitted(merged, websites) ? { ...merged, ephemeral: false } : merged;
    });
    onChange(next);
  };
  const selectProduct = (flow: CommerceFlowDraft, value: string, product?: Product) => {
    if (!value) return;
    if (value === ALL_PRODUCTS_OPTION) {
      commitFlow(flow.id, {
        productIds: [],
        productScope: "selected",
        productLabels: {},
        productDescriptions: {},
        checkoutWebsiteIds: [],
        checkoutWebsiteByProductId: {},
        chatbotImagesByProductId: {},
        disabledOfferUnitsByProductId: {},
      });
      return;
    }
    const selected = new Set(flow.productIds || []);
    selected.add(value);
    const productLabels = { ...(flow.productLabels || {}) };
    const productDescriptions = { ...(flow.productDescriptions || {}) };
    if (product) {
      const label = commerceFlowProductLabelSnapshot(product);
      if (label) productLabels[value] = label;
      // Snapshot de la descripción: el catálogo del chatbot la imprime por producto.
      const description = String(product.description || "").trim();
      if (description) productDescriptions[value] = description;
    }
    commitFlow(flow.id, {
      productIds: Array.from(selected),
      productScope: "selected",
      productLabels,
      productDescriptions,
    });
  };
  const removeProduct = (flow: CommerceFlowDraft, productId: string) => {
    if (productId === ALL_PRODUCTS_OPTION) {
      commitFlow(flow.id, { productIds: [], productScope: "selected", productLabels: {}, productDescriptions: {} });
      return;
    }
    const productLabels = { ...(flow.productLabels || {}) };
    delete productLabels[productId];
    const productDescriptions = { ...(flow.productDescriptions || {}) };
    delete productDescriptions[productId];
    const checkoutWebsiteByProductId = { ...(flow.checkoutWebsiteByProductId || {}) };
    delete checkoutWebsiteByProductId[productId];
    const chatbotImagesByProductId = { ...(flow.chatbotImagesByProductId || {}) };
    delete chatbotImagesByProductId[productId];
    const disabledOfferUnitsByProductId = { ...(flow.disabledOfferUnitsByProductId || {}) };
    delete disabledOfferUnitsByProductId[productId];
    commitFlow(flow.id, {
      productIds: (flow.productIds || []).filter((id) => id !== productId),
      productScope: "selected",
      productLabels,
      productDescriptions,
      checkoutWebsiteByProductId,
      checkoutWebsiteIds: uniqueCheckoutWebsiteIds(checkoutWebsiteByProductId),
      chatbotImagesByProductId,
      disabledOfferUnitsByProductId,
    });
  };
	  const setCheckoutWebsiteForProduct = (flow: CommerceFlowDraft, productId: string, websiteId: string) => {
	    const website = websites.find((item) => String(item.websiteId) === websiteId);
    const product = catalogProducts.find((item) => String(item.id) === productId) || null;
    if (websiteId && !checkoutWebsiteHasPublicUrl(website)) {
      toast.error("Este embudo no tiene URL pública. Publícalo antes de usarlo como fuente.");
      return;
    }
    if (websiteId && !checkoutWebsiteContainsProduct(website, productId, product)) {
      toast.error("Este embudo no contiene este producto. Elige un embudo vinculado al producto.");
      return;
    }
    const checkoutWebsiteByProductId = { ...(flow.checkoutWebsiteByProductId || {}) };
    const previousWebsiteId = String(checkoutWebsiteByProductId[productId] || "").trim();
    if (websiteId) checkoutWebsiteByProductId[productId] = websiteId;
    else delete checkoutWebsiteByProductId[productId];
    // Si cambia el embudo fuente, las imágenes elegidas y las ofertas por cantidad apagadas
    // (ambas salen de ese embudo) dejan de aplicar.
    const chatbotImagesByProductId = { ...(flow.chatbotImagesByProductId || {}) };
    if (previousWebsiteId && previousWebsiteId !== websiteId) delete chatbotImagesByProductId[productId];
    const disabledOfferUnitsByProductId = { ...(flow.disabledOfferUnitsByProductId || {}) };
    if (previousWebsiteId && previousWebsiteId !== websiteId) delete disabledOfferUnitsByProductId[productId];
    // El contexto cacheado del tab Información depende del embudo: se invalida al cambiarlo.
    if (previousWebsiteId !== websiteId) {
      setProductContextByKey((current) => {
        const key = productCardKey(flow.id, productId);
        if (!(key in current)) return current;
        const next = { ...current };
        delete next[key];
        return next;
      });
    }
    commitFlow(flow.id, {
      checkoutWebsiteByProductId,
	      checkoutWebsiteIds: uniqueCheckoutWebsiteIds(checkoutWebsiteByProductId),
	      chatbotImagesByProductId,
	      disabledOfferUnitsByProductId,
	    });
	  };
  // Apaga/prende una oferta por cantidad del embudo para el chat: escribe
  // disabled_offer_units en el mapping del producto (misma vía que chatbot_images,
  // se guarda con el flujo) y refleja el cambio al instante en el contexto cacheado.
  const setOfferUnitsEnabled = (flow: CommerceFlowDraft, productId: string, units: number, enabled: boolean) => {
    const current = normalizeDisabledOfferUnits(flow.disabledOfferUnitsByProductId?.[productId]);
    const nextUnits = enabled
      ? current.filter((item) => item !== units)
      : normalizeDisabledOfferUnits([...current, units]);
    const disabledOfferUnitsByProductId = { ...(flow.disabledOfferUnitsByProductId || {}) };
    if (nextUnits.length) disabledOfferUnitsByProductId[productId] = nextUnits;
    else delete disabledOfferUnitsByProductId[productId];
    commitFlow(flow.id, { disabledOfferUnitsByProductId });
    setProductContextByKey((currentState) => {
      const key = productCardKey(flow.id, productId);
      const entry = currentState[key];
      if (!entry?.data) return currentState;
      return {
        ...currentState,
        [key]: {
          ...entry,
          data: {
            ...entry.data,
            ofertas_cantidad: (entry.data.ofertas_cantidad || []).map((offer) =>
              offer.units === units ? { ...offer, habilitada: enabled } : offer
            ),
            ofertas_deshabilitadas: nextUnits,
          },
        },
      };
    });
  };
	  const setFlowEnabled = (flow: CommerceFlowDraft, enabled: boolean) => {
	    if (!enabled) {
	      updateFlow(flow.id, { enabled: false });
	      return;
	    }
	    const issue = commerceFlowActivationIssues(flow, websites)[0];
	    if (issue) {
	      toast.error(issue);
	      return;
	    }
	    updateFlow(flow.id, { enabled: true, ephemeral: false });
	  };
  const setFunnelFulfillmentDestinationDraft = (
    website: CheckoutWebsiteOption,
    destination: FunnelFulfillmentDestination
  ) => {
    const websiteId = String(website.websiteId || "").trim();
    if (!websiteId) return;
    onSyncDraftChange(websiteId, {
      ...(syncDraftsByWebsiteId[websiteId] || {
        websiteId,
        provider: destination,
        rows: [],
      }),
      websiteId,
      provider: destination,
      dirty: true,
      loaded: destination === "none",
      loading: destination !== "none",
      error: null,
    });
    setSyncSelectorOpenByWebsiteId((current) => ({ ...current, [websiteId]: destination !== "none" }));
  };
  // Espejo de sincronización Dropi a nivel de flujo (decisión #4): agrega el estado real de
  // los embudos cubiertos y aplica el cambio con la MISMA vía que el configurador por producto
  // (drafts que se guardan con el flujo). Los embudos con Shopify no se tocan desde aquí.
  const flowDropiSyncMirror = (flow: CommerceFlowDraft): CommerceFlowSyncMirror => {
    const websiteIds = uniqueCheckoutWebsiteIds(flow.checkoutWebsiteByProductId || {});
    const covered = websiteIds
      .map((id) => websites.find((website) => String(website.websiteId) === String(id)))
      .filter((website): website is (typeof websites)[number] => Boolean(website));
    const destinationOf = (website: (typeof websites)[number]): string => {
      const draftProvider = syncDraftsByWebsiteId[String(website.websiteId || "")]?.provider;
      if (draftProvider === "dropi" || draftProvider === "shopify" || draftProvider === "none") {
        return draftProvider;
      }
      return checkoutWebsiteFulfillmentContext(website).fulfillment_destination;
    };
    return {
      dropiCount: covered.filter((website) => destinationOf(website) === "dropi").length,
      shopifyCount: covered.filter((website) => destinationOf(website) === "shopify").length,
      totalCount: covered.length,
    };
  };
  const addRequiredField = (flow: CommerceFlowDraft, field: string) => {
    const value = canonicalOrderRequiredField(field);
    if (!value || flow.requiredFields.includes(value)) return;
    commitFlow(flow.id, { requiredFields: [...flow.requiredFields, value] });
  };
  const removeRequiredField = (flow: CommerceFlowDraft, field: string) => {
    const value = canonicalOrderRequiredField(field);
    commitFlow(flow.id, {
      requiredFields: flow.requiredFields.filter((item) => canonicalOrderRequiredField(item) !== value),
    });
  };
  const addFlow = () => {
    const id = `order-capture-${Date.now()}`;
    const title = nextSequentialFlowTitle(
      "Toma de orden",
      flows.map((flow) => flow.title)
    );
    onChange([
      {
        id,
        title,
	        enabled: false,
        flowType: "order_capture",
        mode: "chat_capture_cod",
        templateName: "",
        templateLanguage: "es",
        instructions: "",
        productScope: "selected",
        productIds: [],
                productLabels: {},
                requiredFields: [...DEFAULT_ORDER_REQUIRED_FIELDS],
                checkoutWebsiteIds: [],
                checkoutWebsiteByProductId: {},
                logisticsPolicy: defaultCommerceLogisticsPolicy(),
        ephemeral: true,
      },
      ...flows,
    ]);
    setExpandedFlowId(id);
  };
  const deleteFlow = (id: string) => {
    onChange(flows.filter((flow) => flow.id !== id));
    setDeleteConfirmId(null);
    if (expandedFlowId === id) setExpandedFlowId(null);
  };

  const imagesTargetFlow = imagesTarget ? flows.find((flow) => flow.id === imagesTarget.flowId) : undefined;
  const imagesTargetWebsiteId = imagesTargetFlow && imagesTarget
    ? String(imagesTargetFlow.checkoutWebsiteByProductId?.[imagesTarget.productId] || "").trim()
    : "";
  // Con la sub-pantalla abierta el modal esconde su botón global de Guardar (Julian):
  // los cambios quedan en el draft y se guardan al volver, desde el guardar de la acción.
  const imagesScreenOpen = Boolean(imagesTarget && imagesTargetFlow && imagesTargetWebsiteId);
  useEffect(() => {
    onImagesScreenChange?.(imagesScreenOpen);
    return () => onImagesScreenChange?.(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imagesScreenOpen]);
  if (imagesTarget && imagesTargetFlow && imagesTargetWebsiteId) {
    const targetWebsite = websites.find((website) => String(website.websiteId) === imagesTargetWebsiteId);
    const targetProduct = catalogProducts.find((item) => String(item.id) === imagesTarget.productId) || null;
    const targetChip = targetProduct ? productPickerChipContent(targetProduct) : null;
    return (
      <FunnelChatbotImagesScreen
        mode="sales"
        websiteId={imagesTargetWebsiteId}
        websiteName={targetWebsite ? checkoutWebsiteName(targetWebsite) : `Embudo ${imagesTargetWebsiteId.slice(0, 6)}`}
        productId={imagesTarget.productId}
        productLabel={imagesTargetFlow.productLabels?.[imagesTarget.productId] || targetChip?.name || "este producto"}
        backLabel="Volver a toma de órdenes"
        salesConfig={imagesTargetFlow.chatbotImagesByProductId?.[imagesTarget.productId] || null}
        onBack={() => {
          // Bug fix (Julian): al volver, el tab Información quedaba vacío hasta cambiar de tab.
          // Ahora re-fetchea el contexto de una vez; la preview usa el draft local sin esperar.
          loadFlowProductContext(imagesTarget.flowId, imagesTarget.productId, { force: true });
          setImagesTarget(null);
        }}
        onSalesConfigChange={(config) => {
          const current = flows.find((flow) => flow.id === imagesTarget.flowId);
          if (!current) return;
          const chatbotImagesByProductId = { ...(current.chatbotImagesByProductId || {}) };
          if (config) chatbotImagesByProductId[imagesTarget.productId] = config;
          else delete chatbotImagesByProductId[imagesTarget.productId];
          commitFlow(current.id, {
            chatbotImagesByProductId,
          });
        }}
      />
    );
  }

  return (
    <div className="mx-auto max-w-5xl space-y-5 overflow-x-hidden">
      <FlowListHeader
        title="Toma de órdenes"
        subtitle="Los productos que tu IA vende por chat"
        addLabel="+ Agregar toma de orden"
        onAdd={addFlow}
      />

      {validationError ? (
          <div className="rounded-xl bg-red-50 px-4 py-3 text-sm leading-5 text-red-700">{validationError}</div>
        ) : null}

        <div className="grid gap-3">
          {flows.length === 0 ? (
            <div className="rounded-xl bg-gray-50 px-4 py-5 text-sm leading-6 text-gray-500">
              No hay flujos de toma de orden. Agrega uno para que la IA pueda vender productos por WhatsApp.
            </div>
          ) : null}
          {flows.map((flow) => {
            const isExpanded = expandedFlowId === flow.id;
            const usedProducts = collectActiveCommerceProductUsage(flows, "order_capture", flow.id);
            const draftBadge = !flow.enabled && commerceFlowActivationIssues(flow, websites).length > 0;
            const selectedRequiredFields = new Set(flow.requiredFields.map(canonicalOrderRequiredField));
	            return (
              <article
                key={flow.id}
                className={`relative rounded-xl border bg-gray-50 ${
                  isExpanded ? "border-gray-950" : "border-transparent"
                }`}
              >
		                <EditableActionCardHeader
		                  title={flow.title}
		                  fallbackTitle="Toma de orden"
		                  isExpanded={isExpanded}
		                  enabled={flow.enabled}
		                  onToggleExpanded={() => setExpandedFlowId(isExpanded ? null : flow.id)}
		                  onToggleEnabled={(enabled) => setFlowEnabled(flow, enabled)}
		                  onDelete={() => setDeleteConfirmId(flow.id)}
		                  onTitleChange={(title) => updateFlow(flow.id, { title })}
		                  deleteTitle="Eliminar flujo"
		                  extra={
		                    draftBadge ? (
		                      <span className="inline-flex shrink-0 items-center rounded-full border border-gray-200 bg-white px-2.5 py-1 text-[11px] font-semibold text-gray-600">
		                        Borrador
		                      </span>
		                    ) : null
		                  }
		                />

		                {isExpanded ? (
		                  <div className="border-t border-gray-100 px-4 pb-4">
		                    <div className="mt-4">
	                      <SelectField
	                        label="Modo de cierre de venta"
	                        value={flow.mode || ""}
	                        options={ORDER_CAPTURE_MODES.map((item) => item.value)}
	                        placeholder="Elegir modo"
	                        formatOption={(value) =>
	                          ORDER_CAPTURE_MODES.find((item) => item.value === value)?.label || humanize(value)
	                        }
	                        onChange={(mode) =>
	                          commitFlow(flow.id, {
	                            mode: mode as CommerceOrderMode,
	                            productScope: "selected",
	                            checkoutWebsiteIds: uniqueCheckoutWebsiteIds(flow.checkoutWebsiteByProductId || {}),
	                            requiredFields:
	                              mode === "chat_capture_cod" && !flow.requiredFields.length
	                                ? [...DEFAULT_ORDER_REQUIRED_FIELDS]
	                                : flow.requiredFields,
	                          })
	                        }
	                      />
	                    </div>

	                    <div className="mt-4">
	                      <CommerceFlowProductSection
	                        flow={flow}
	                        catalogProducts={catalogProducts}
	                        catalogLoading={catalogLoading}
	                        usedProducts={usedProducts}
	                        includeAllOption={false}
	                        showSelectedChips={false}
	                        onSelectProduct={(productId, product) => selectProduct(flow, productId, product)}
	                        onRemoveProduct={(productId) => removeProduct(flow, productId)}
	                      />
	                    </div>

                    {flow.mode ? (
                      <div className="mt-3">
                        <p className="text-xs leading-5 text-gray-500">
                          {flow.mode === "fluxi_checkout_link"
                            ? "La IA enviará el link exacto del embudo seleccionado cuando el cliente quiera comprar ese producto."
                            : "El embudo fuente no se envía al cliente: es la fuente aprobada de precios, variaciones, combos y ángulos de cada producto."}
                        </p>
                        <div className="mt-3 grid gap-3">
                          {websitesLoading && websites.length === 0 ? (
                            <CheckoutWebsiteLoadingSkeleton rows={1} />
                          ) : websitesLoadError ? (
                            <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                              {websitesLoadError}
                            </p>
                          ) : flow.productScope === "all" ? (
                            <p className="text-sm text-amber-700">Elige productos específicos para asignar un embudo fuente exacto.</p>
                          ) : flow.productIds.length === 0 ? (
                            <p className="text-sm text-gray-500">Agrega productos enlazados para configurar su embudo y su sincronización.</p>
                          ) : (
                            flow.productIds.map((productId) => {
                              const product =
                                catalogProducts.find((item) => String(item.id) === productId) || null;
                              const chip = product ? productPickerChipContent(product) : null;
                              const productLabel = flow.productLabels?.[productId] || chip?.name || productId;
                              const selectedWebsiteId = String(flow.checkoutWebsiteByProductId?.[productId] || "");
                              const selectedWebsite = websites.find(
                                (website) => String(website.websiteId) === selectedWebsiteId
                              );
                              const eligibleWebsites = checkoutWebsitesForProduct(productId, websites, product);
                              const fulfillmentContext = checkoutWebsiteFulfillmentContext(selectedWebsite);
                              const syncDraft = selectedWebsiteId ? syncDraftsByWebsiteId[selectedWebsiteId] : undefined;
                              const draftProvider = syncDraft?.provider;
                              const effectiveSyncProvider =
                                draftProvider === "dropi" || draftProvider === "shopify" || draftProvider === "none"
                                  ? draftProvider
                                  : fulfillmentContext.fulfillment_destination;
                              const syncActive = effectiveSyncProvider !== "none";
                              const syncConfiguratorOpen = syncActive || Boolean(syncSelectorOpenByWebsiteId[selectedWebsiteId]);
                              const selectedSyncProvider =
                                effectiveSyncProvider === "shopify" ||
                                effectiveSyncProvider === "dropi"
                                  ? effectiveSyncProvider
                                  : syncProviderDraftByWebsiteId[selectedWebsiteId] || "";
                              const cardKey = productCardKey(flow.id, productId);
                              const isCardOpen = expandedProductCardKey === cardKey;
                              const cardTab = productCardTabByKey[cardKey] || "embudo";
                              const toggleCard = () =>
                                setExpandedProductCardKey((current) => (current === cardKey ? null : cardKey));
                              return (
                                <div
                                  key={`${flow.id}-checkout-product-${productId}`}
                                  className="min-w-0 rounded-lg border border-gray-200 bg-white p-3"
                                >
                                  {/* Card de producto (Julian): colapsada solo identidad; expandida con tabs
                                      internos Embudo fuente | Información | Sincronizar órdenes a ancho completo.
                                      min-w-0: sin esto el titulo nowrap fija el ancho intrinseco del grid item y
                                      desborda el modal en horizontal (visto en E2E con el titulo del ATORPOK). */}
                                  <div
                                    role="button"
                                    tabIndex={0}
                                    onClick={toggleCard}
                                    onKeyDown={(event) => {
                                      if (event.key !== "Enter" && event.key !== " ") return;
                                      event.preventDefault();
                                      toggleCard();
                                    }}
                                    className="flex cursor-pointer items-center gap-3"
                                  >
                                    {chip?.thumbnail ? (
                                      <img src={chip.thumbnail} alt="" className="size-9 shrink-0 rounded object-cover" />
                                    ) : (
                                      <div className="flex size-9 shrink-0 items-center justify-center rounded bg-gray-100 text-xs font-semibold text-gray-500">
                                        {productLabel.slice(0, 1).toUpperCase()}
                                      </div>
                                    )}
                                    <div className="min-w-0 flex-1">
                                      <p className="truncate text-sm font-semibold text-gray-950">{productLabel}</p>
                                      {chip?.shortCode ? (
                                        <p className="truncate font-mono text-[11px] text-gray-500">{chip.shortCode}</p>
                                      ) : null}
                                    </div>
                                    <div className="flex shrink-0 items-center gap-1" onClick={(event) => event.stopPropagation()}>
                                      <button
                                        type="button"
                                        onClick={() => removeProduct(flow, productId)}
                                        className="inline-flex size-8 items-center justify-center rounded-full text-gray-400 transition hover:bg-gray-100 hover:text-red-600"
                                        title="Quitar producto"
                                      >
                                        <Trash2 className="size-4" />
                                      </button>
                                      <button
                                        type="button"
                                        onClick={toggleCard}
                                        className="inline-flex size-8 items-center justify-center rounded-full text-gray-500 transition hover:bg-gray-100 hover:text-gray-900"
                                        title={isCardOpen ? "Contraer" : "Expandir"}
                                      >
                                        <ChevronDown className={`size-4 transition ${isCardOpen ? "rotate-180" : ""}`} />
                                      </button>
                                    </div>
                                  </div>

                                  {isCardOpen ? (
                                    <div className="mt-3 border-t border-gray-100 pt-3">
                                      <SimpleSubTabBar
                                        compact
                                        tabs={ORDER_PRODUCT_CARD_TABS}
                                        active={cardTab}
                                        onChange={(tab) => setProductCardTab(flow.id, productId, tab)}
                                      />
                                      <div className="mt-3">
                                        {cardTab === "embudo" ? (
                                          <div>
                                            <span className="mb-1 flex items-center gap-1.5 text-xs font-semibold text-gray-600">
                                              {flow.mode === "fluxi_checkout_link" ? "Embudo del checkout" : "Embudo fuente"}
                                              {selectedWebsite ? <CheckCircle2 className="size-3.5 text-emerald-600" /> : null}
                                            </span>
                                            {eligibleWebsites.length ? (
                                              <>
                                                <FunnelSourcePicker
                                                  value={selectedWebsiteId}
                                                  websites={eligibleWebsites}
                                                  mode={flow.mode}
                                                  onChange={(websiteId) =>
                                                    setCheckoutWebsiteForProduct(flow, productId, websiteId)
                                                  }
                                                />
                                                {selectedWebsite?.url ? (
                                                  <p className="mt-1 truncate text-xs text-gray-500">{checkoutWebsiteOpenUrl(selectedWebsite)}</p>
                                                ) : null}
                                              </>
                                            ) : (
                                              <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                                                No hay embudos públicos vinculados a este producto.
                                              </p>
                                            )}
                                          </div>
                                        ) : null}
                                        {cardTab === "info"
                                          ? (() => {
                                              const context = productContextByKey[cardKey];
                                              // Preview (Julian): manda el draft local de imágenes (lo que el
                                              // usuario va a guardar); si no hay draft, el preview_image_url del
                                              // backend; si tampoco, la miniatura del producto.
                                              const draftImagesConfig = flow.chatbotImagesByProductId?.[productId];
                                              const draftSelected = draftImagesConfig?.selected;
                                              // Solo afirmamos "0 imágenes" cuando el dato es real (draft o contexto cargado).
                                              const selectedImagesCountKnown = Boolean(draftSelected) || Boolean(context?.data);
                                              const selectedImagesCount = draftSelected
                                                ? draftSelected.length
                                                : context?.data?.imagenes_embudo?.length || 0;
                                              const previewImageUrl = draftSelected
                                                ? draftSelected[0]?.url || chip?.thumbnail || null
                                                : String(context?.data?.preview_image_url || "").trim() ||
                                                  chip?.thumbnail ||
                                                  null;
                                              const imagesInstructions = draftImagesConfig
                                                ? String(draftImagesConfig.instructions || "").trim()
                                                : String(context?.data?.instrucciones_imagenes || "").trim();
                                              const mainAngle = normalizeFunnelProductAngle(context?.data?.angulo_principal);
                                              const secondaryAngles = (context?.data?.angulos_secundarios || [])
                                                .map(normalizeFunnelProductAngle)
                                                .filter((angle): angle is { titulo: string; descripcion: string } => Boolean(angle));
                                              const aiDescription = String(
                                                context?.data?.descripcion_adicional || context?.data?.descripcion_catalogo || ""
                                              ).trim();
                                              const descriptionSource = String(context?.data?.descripcion_fuente || "").trim();
                                              const imagesPreviewBlock = (
                                                <div>
                                                  <p className="text-xs text-gray-500">Imágenes</p>
                                                  {previewImageUrl ? (
                                                    <img
                                                      src={previewImageUrl}
                                                      alt=""
                                                      className="mt-2 h-36 w-36 rounded-lg border border-gray-200 object-cover"
                                                    />
                                                  ) : null}
                                                  {selectedImagesCountKnown && selectedImagesCount === 0 ? (
                                                    <p className="mt-2 text-sm text-gray-500">0 imágenes del embudo elegidas</p>
                                                  ) : null}
                                                  {imagesInstructions ? (
                                                    <p className="mt-1 whitespace-pre-wrap text-xs leading-5 text-gray-500">{imagesInstructions}</p>
                                                  ) : null}
                                                  {selectedWebsite ? (
                                                    <button
                                                      type="button"
                                                      onClick={() => setImagesTarget({ flowId: flow.id, productId })}
                                                      className="mt-2 inline-flex h-8 items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 text-xs font-semibold text-gray-800 transition hover:bg-gray-50 hover:text-gray-950"
                                                    >
                                                      <ImageIcon className="size-3.5" />
                                                      Configurar imágenes
                                                    </button>
                                                  ) : null}
                                                </div>
                                              );
                                              return (
                                                <div className="space-y-4">
                                                  <p className="text-xs text-gray-500">
                                                    Esta es la información que tu chatbot recibe de este producto.
                                                  </p>
                                                  {imagesPreviewBlock}
                                                  {context?.loading ? (
                                                    <div className="flex items-center gap-2 py-3 text-sm text-gray-500">
                                                      <Loader2 className="size-4 animate-spin" />
                                                      Cargando información…
                                                    </div>
                                                  ) : context?.error ? (
                                                    <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                                                      {context.error}
                                                    </p>
                                                  ) : context?.data ? (
                                                    <>
                                                      <div>
                                                        <p className="text-xs text-gray-500">
                                                          Descripción
                                                          {descriptionSource ? (
                                                            <span className="text-gray-400"> · {descriptionSource}</span>
                                                          ) : null}
                                                        </p>
                                                        {context.data.descripcion ? (
                                                          <p className="mt-1 max-h-[200px] overflow-y-auto whitespace-pre-wrap px-3 text-sm leading-6 text-gray-800">
                                                            {context.data.descripcion}
                                                          </p>
                                                        ) : (
                                                          <p className="mt-1 px-3 text-sm text-gray-500">
                                                            Sin descripción — el chatbot no tiene detalles de este producto.
                                                          </p>
                                                        )}
                                                      </div>
                                                      {mainAngle ? (
                                                        <div>
                                                          <p className="text-xs text-gray-500">Ángulo principal</p>
                                                          {mainAngle.titulo ? (
                                                            <p className="mt-1 px-3 text-sm font-semibold leading-6 text-gray-900">{mainAngle.titulo}</p>
                                                          ) : null}
                                                          {mainAngle.descripcion ? (
                                                            <p className="mt-1 whitespace-pre-wrap px-3 text-sm leading-6 text-gray-800">
                                                              {mainAngle.descripcion}
                                                            </p>
                                                          ) : null}
                                                        </div>
                                                      ) : null}
                                                      {secondaryAngles.length ? (
                                                        <div>
                                                          <p className="text-xs text-gray-500">Ángulos secundarios</p>
                                                          <ul className="mt-1 space-y-2 px-3">
                                                            {secondaryAngles.map((angle, angleIndex) => (
                                                              <li key={`${cardKey}-angle-${angleIndex}`} className="text-sm leading-6 text-gray-800">
                                                                {angle.titulo ? (
                                                                  <span className="font-semibold text-gray-900">
                                                                    {angle.titulo}
                                                                    {angle.descripcion ? ": " : ""}
                                                                  </span>
                                                                ) : null}
                                                                {angle.descripcion}
                                                              </li>
                                                            ))}
                                                          </ul>
                                                        </div>
                                                      ) : null}
                                                      {aiDescription ? (
                                                        <div>
                                                          <p className="text-xs text-gray-500">Descripción adicional (agregada por la IA)</p>
                                                          <p className="mt-1 whitespace-pre-wrap px-3 text-sm leading-6 text-gray-800">{aiDescription}</p>
                                                        </div>
                                                      ) : null}
                                                      {context.data.contexto_exacto_chatbot?.length ? (
                                                        <details className="rounded-lg border border-gray-200 bg-gray-50">
                                                          <summary className="cursor-pointer px-3 py-2 text-xs font-semibold text-gray-600">
                                                            Ver contexto exacto del chatbot
                                                          </summary>
                                                          <pre className="max-h-[280px] overflow-y-auto whitespace-pre-wrap border-t border-gray-200 px-3 py-2 font-mono text-[11px] leading-4 text-gray-700">
                                                            {context.data.contexto_exacto_chatbot.join("\n\n")}
                                                          </pre>
                                                        </details>
                                                      ) : null}
                                                    </>
                                                  ) : null}
                                                  <div>
                                                    <button
                                                      type="button"
                                                      onClick={() => navigate("/products")}
                                                      className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 text-xs font-semibold text-gray-800 transition hover:bg-gray-50 hover:text-gray-950"
                                                    >
                                                      Editar producto en Fluxi
                                                      <ExternalLink className="size-3.5" />
                                                    </button>
                                                  </div>
                                                </div>
                                              );
                                            })()
                                          : null}
                                        {cardTab === "oferta"
                                          ? (() => {
                                              // Tab Oferta (Julian): precio humanizado + ofertas por cantidad
                                              // (antes vivían en Información). Mismo draft/guardado de la acción.
                                              const context = productContextByKey[cardKey];
                                              return (
                                                <div className="space-y-4">
                                                  {context?.loading ? (
                                                    <div className="flex items-center gap-2 py-3 text-sm text-gray-500">
                                                      <Loader2 className="size-4 animate-spin" />
                                                      Cargando oferta…
                                                    </div>
                                                  ) : context?.error ? (
                                                    <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                                                      {context.error}
                                                    </p>
                                                  ) : context?.data ? (
                                                    <>
                                                      <div>
                                                        <p className="text-xs text-gray-500">Oferta / precio del embudo</p>
                                                        {context.data.oferta ? (
                                                          <p className="mt-1 whitespace-pre-wrap px-3 text-sm leading-6 text-gray-800">
                                                            {formatFunnelOfferForHumans(context.data.oferta)}
                                                          </p>
                                                        ) : (
                                                          <p className="mt-1 px-3 text-sm text-gray-500">Sin datos de precio del embudo.</p>
                                                        )}
                                                      </div>
                                                      <div>
                                                        <p className="text-xs text-gray-500">Ofertas por cantidad del embudo</p>
                                                        {context.data.ofertas_cantidad?.length ? (
                                                          <>
                                                            <div className="mt-1 grid gap-1.5">
                                                              {context.data.ofertas_cantidad.map((offer) => (
                                                                <div
                                                                  key={`${cardKey}-offer-${offer.units}`}
                                                                  className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2"
                                                                >
                                                                  <div className="min-w-0">
                                                                    <p className="text-sm text-gray-800">
                                                                      <span className="font-semibold">{offer.titulo}</span>
                                                                      {offer.precio != null
                                                                        ? ` · ${formatQuantityOfferPrice(offer.precio, offer.moneda)}`
                                                                        : ""}
                                                                    </p>
                                                                    {offer.promocion ? (
                                                                      <span className="mt-0.5 inline-flex items-center rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">
                                                                        {offer.promocion}
                                                                      </span>
                                                                    ) : null}
                                                                  </div>
                                                                  <ToggleSwitch
                                                                    checked={offer.habilitada}
                                                                    label=""
                                                                    onChange={(enabled) =>
                                                                      setOfferUnitsEnabled(flow, productId, offer.units, enabled)
                                                                    }
                                                                  />
                                                                </div>
                                                              ))}
                                                            </div>
                                                            <p className="mt-1.5 text-xs leading-5 text-gray-500">
                                                              Las opciones apagadas no se ofrecen en el chat. La página web no cambia.
                                                            </p>
                                                          </>
                                                        ) : (
                                                          <p className="mt-1 px-3 text-sm text-gray-500">Este embudo no tiene ofertas por cantidad.</p>
                                                        )}
                                                      </div>
                                                    </>
                                                  ) : null}
                                                </div>
                                              );
                                            })()
                                          : null}
                                        {cardTab === "sync" ? (
                                          !selectedWebsite ? (
                                            <p className="text-sm text-gray-500">
                                              Elige primero el embudo fuente para configurar la sincronización.
                                            </p>
                                          ) : (
                                            <div className="grid gap-3">
                                              <div className="flex flex-wrap items-center justify-between gap-2">
                                                <div className="min-w-0">
                                                  <span className="text-xs font-semibold text-gray-600">Sincronización de órdenes</span>
                                                  <p className="mt-0.5 text-xs text-gray-500">
                                                    {selectedSyncProvider
                                                      ? `Sincroniza con ${selectedSyncProvider === "dropi" ? "Dropi" : "Shopify"}`
                                                      : syncConfiguratorOpen
                                                        ? "Elige la plataforma logística"
                                                        : "Sin sincronización automática"}
                                                  </p>
                                                </div>
                                                <ToggleSwitch
                                                  checked={syncConfiguratorOpen}
                                                  label=""
                                                  onChange={(enabled) => {
                                                    if (!enabled) {
                                                      setSyncSelectorOpenByWebsiteId((current) => ({ ...current, [selectedWebsiteId]: false }));
                                                      setSyncProviderDraftByWebsiteId((current) => ({ ...current, [selectedWebsiteId]: "" }));
                                                      setFunnelFulfillmentDestinationDraft(selectedWebsite, "none");
                                                      return;
                                                    }
                                                    setSyncSelectorOpenByWebsiteId((current) => ({ ...current, [selectedWebsiteId]: true }));
                                                  }}
                                                />
                                              </div>
                                              {syncConfiguratorOpen ? (
                                                <>
                                                  <ModalSelect
                                                    value={selectedSyncProvider}
                                                    placeholder="Sincronizar con"
                                                    options={[
                                                      { value: "shopify", label: "Shopify" },
                                                      { value: "dropi", label: "Dropi" },
                                                    ]}
                                                    onChange={(destination) => {
                                                      const provider = destination as FunnelSyncSettingsProvider;
                                                      setSyncProviderDraftByWebsiteId((current) => ({
                                                        ...current,
                                                        [selectedWebsiteId]: provider,
                                                      }));
                                                      setFunnelFulfillmentDestinationDraft(selectedWebsite, provider);
                                                    }}
                                                  />
                                                  {fulfillmentContext.shopify_checkout_enabled ? (
                                                    <p className="text-xs font-semibold text-[#3151C9]">Checkout Shopify activo en este embudo.</p>
                                                  ) : null}
                                                  {selectedSyncProvider ? (
                                                    <FunnelSyncSettingsPanel
                                                      provider={selectedSyncProvider}
                                                      website={selectedWebsite}
                                                      catalogProducts={catalogProducts}
                                                      draft={syncDraft}
                                                      onDraftChange={(draft) => onSyncDraftChange(selectedWebsiteId, draft)}
                                                    />
                                                  ) : null}
                                                </>
                                              ) : null}
                                            </div>
                                          )
                                        ) : null}
                                      </div>
                                    </div>
                                  ) : null}
                                </div>
                              );
                            })
                          )}
                        </div>
                      </div>
                    ) : null}

	                    {flow.mode === "chat_capture_cod" ? (
	                      <div className="mt-4">
	                        <SelectField
	                          label="Campos requeridos"
	                          value=""
	                          options={ORDER_REQUIRED_FIELD_OPTIONS.map((item) => item.value).filter(
	                            (field) => !selectedRequiredFields.has(canonicalOrderRequiredField(field))
	                          )}
	                          placeholder="Agregar campo requerido"
	                          formatOption={orderRequiredFieldLabel}
	                          onChange={(value) => {
	                            if (value) addRequiredField(flow, value);
	                          }}
	                        />
	                        <div className="mt-2 flex flex-wrap gap-2">
	                          {flow.requiredFields.map((field) => (
	                            <span
	                              key={`${flow.id}-field-${canonicalOrderRequiredField(field)}`}
	                              className="inline-flex items-center gap-2 rounded-full bg-gray-100 px-3 py-1.5 text-xs font-semibold text-gray-800"
	                            >
	                              {orderRequiredFieldLabel(field)}
	                              <button
	                                type="button"
	                                onClick={() => removeRequiredField(flow, field)}
	                                className="inline-flex size-4 items-center justify-center rounded-full text-gray-500 hover:bg-gray-200 hover:text-gray-900"
	                              >
	                                <X className="size-3" />
	                              </button>
	                            </span>
	                          ))}
	                        </div>
	                      </div>
	                    ) : null}

	                    {/* Sin caja de fondo (Julian): título + tabla de acciones, directo. */}
	                    <div className="mt-5">
                      <p className="text-sm font-semibold text-gray-950">Acciones logísticas</p>
                      <div className="mt-3">
                        <CommerceLogisticsPolicyEditor
                          policy={flow.logisticsPolicy}
                          approvedTemplates={approvedTemplates}
                          onChange={(logisticsPolicy) => commitFlow(flow.id, { logisticsPolicy })}
                          syncMirror={flowDropiSyncMirror(flow)}
                        />
                      </div>
                    </div>
                  </div>
                ) : null}
              </article>
            );
          })}
        </div>

      {deleteConfirmId ? (
        <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/40 p-4">
          <div className="w-[min(380px,100%)] rounded-xl bg-white p-4 shadow-xl">
            <h3 className="text-base font-semibold text-gray-950">Eliminar toma de orden</h3>
            <p className="mt-2 text-sm text-gray-500">Este flujo se quitará al guardar los cambios.</p>
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setDeleteConfirmId(null)}
                className="inline-flex h-9 items-center rounded-lg border border-gray-200 px-3 text-sm font-semibold text-gray-700 hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={() => deleteFlow(deleteConfirmId)}
                className="inline-flex h-9 items-center rounded-lg bg-red-600 px-3 text-sm font-semibold text-white hover:bg-red-700"
              >
                Eliminar
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

const CartRecoveryTab = ({
  flows,
  approvedTemplates,
  pickerTemplates,
  onChange,
}: {
  flows: CartRecoveryFlowDraft[];
  approvedTemplates: WhatsAppTemplate[];
  /** Todas las plantillas no-guardian (incluye PENDING), para mostrar su estado en el picker. */
  pickerTemplates: WhatsAppTemplate[];
  onChange: (rows: CartRecoveryFlowDraft[]) => void;
}) => {
  const { catalogProducts, catalogLoading } = useFluxiProductsCatalog();
  const [expandedFlowId, setExpandedFlowId] = useState<string | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  const updateFlow = (id: string, patch: Partial<CartRecoveryFlowDraft>) => {
    onChange(flows.map((flow) => (flow.id === id ? { ...flow, ...patch } : flow)));
  };
  const commitFlow = (id: string, patch: Partial<CartRecoveryFlowDraft>) => {
    const next = flows.map((flow) => {
      if (flow.id !== id) return flow;
      const merged = { ...flow, ...patch };
      return isCartRecoveryFlowDraftCommitted(merged) ? { ...merged, ephemeral: false } : merged;
    });
    onChange(next);
  };
  const selectProduct = (flow: CartRecoveryFlowDraft, value: string, product?: Product) => {
    if (!value) return;
    if (value === ALL_PRODUCTS_OPTION) {
      commitFlow(flow.id, { productIds: [], productScope: "all", productLabels: {} });
      return;
    }
    const selected = new Set(flow.productIds || []);
    selected.add(value);
    const productLabels = { ...(flow.productLabels || {}) };
    if (product) {
      const label = commerceFlowProductLabelSnapshot(product);
      if (label) productLabels[value] = label;
    }
    commitFlow(flow.id, { productIds: Array.from(selected), productScope: "selected", productLabels });
  };
	  const removeProduct = (flow: CartRecoveryFlowDraft, productId: string) => {
	    const productLabels = { ...(flow.productLabels || {}) };
    delete productLabels[productId];
    commitFlow(flow.id, {
      productIds: (flow.productIds || []).filter((id) => id !== productId),
      productScope: "selected",
	      productLabels,
	    });
	  };
	  const setFlowEnabled = (flow: CartRecoveryFlowDraft, enabled: boolean) => {
	    if (!enabled) {
	      updateFlow(flow.id, { enabled: false });
	      return;
	    }
	    const issue = cartRecoveryActivationIssues(flow)[0];
	    if (issue) {
	      toast.error(issue);
	      return;
	    }
	    updateFlow(flow.id, { enabled: true, ephemeral: false });
	  };
	  const addFlow = () => {
    const id = `cart-recovery-${Date.now()}`;
    const title = nextSequentialFlowTitle(
      "Recuperación",
      flows.map((flow) => flow.title)
    );
    onChange([
      {
        id,
        title,
	        enabled: false,
        templateName: "chatbot_carrito_recuperacion_v1",
        templateLanguage: "es",
        instructions: "",
        productScope: "selected",
        productIds: [],
        productLabels: {},
        delayValue: 30,
        delayUnit: "minutes",
        ephemeral: true,
      },
      ...flows,
    ]);
    setExpandedFlowId(id);
  };

  return (
    <div className="mx-auto max-w-5xl space-y-5 overflow-x-hidden">
      <FlowListHeader
        title="Recuperación de carritos"
        subtitle="Recupera por chat las compras abandonadas"
        addLabel="+ Agregar recuperación"
        onAdd={addFlow}
      />
      <div className="grid gap-3">
        {flows.length === 0 ? (
          <div className="rounded-xl bg-gray-50 px-4 py-5 text-sm leading-6 text-gray-500">
            No hay recuperaciones activas. Agrega una para reactivar carritos abandonados por producto.
          </div>
        ) : null}
        {flows.map((flow) => {
          const isExpanded = expandedFlowId === flow.id;
          const recoveryTemplate = approvedTemplates.find((template) => template.name === flow.templateName);
	          return (
            <article
              key={flow.id}
              className={`relative rounded-xl border bg-gray-50 ${isExpanded ? "border-gray-950" : "border-transparent"}`}
            >
		              <EditableActionCardHeader
		                title={flow.title}
		                fallbackTitle="Recuperación de carrito"
		                isExpanded={isExpanded}
		                enabled={flow.enabled}
		                onToggleExpanded={() => setExpandedFlowId(isExpanded ? null : flow.id)}
		                onToggleEnabled={(enabled) => setFlowEnabled(flow, enabled)}
		                onDelete={() => setDeleteConfirmId(flow.id)}
		                onTitleChange={(title) => updateFlow(flow.id, { title })}
		                deleteTitle="Eliminar recuperación"
		              />
	              {isExpanded ? (
	                <div className="border-t border-gray-100 px-4 pb-4">
                  {/* Copy espejo de Logística (Julian): "Cada vez que → Enviar plantilla" para que
                      la gente conecte la lógica. Aquí el "cada vez que" NO es configurable. */}
                  <div className="mt-4 grid gap-3 md:grid-cols-2">
                    <div className="block">
                      <span className="mb-1 block text-xs font-semibold text-gray-600">Cada vez que</span>
                      <div className="flex h-10 items-center rounded-lg border border-gray-200 bg-gray-50 px-3 text-sm text-gray-700">
                        🛒 Un usuario abandona la compra
                      </div>
                      <p className="mt-1 text-xs leading-5 text-gray-500">
                        Empezó el checkout de un producto y no terminó el pedido (carrito abandonado).
                      </p>
                    </div>
                    <TemplatePickerField
                      label="Enviar plantilla"
                      value={flow.templateName}
                      templates={pickerTemplates}
                      recommendedNames={["chatbot_carrito_recuperacion_v1"]}
                      hidePreview
                      onChange={(templateName, match) =>
                        commitFlow(flow.id, {
                          templateName,
                          templateLanguage: match?.language || flow.templateLanguage || "es",
                        })
                      }
                    />
                  </div>
                  {/* Previsualización a ancho completo, debajo del grid (Julian). */}
                  {recoveryTemplate?.body_text ? (
                    <div className="mt-3">
                      <span className="mb-1 block text-xs font-semibold text-gray-600">Previsualización de la plantilla</span>
                      <div className="rounded-lg border border-dashed border-gray-200 bg-gray-50 px-3 py-2">
                        {templateHasImageHeader(recoveryTemplate) ? <TemplateImageHeaderPlaceholder /> : null}
                        <p className="whitespace-pre-line text-xs leading-5 text-gray-600">{recoveryTemplate.body_text}</p>
                      </div>
                    </div>
                  ) : null}
	                  <div className="mt-4">
                    <CommerceFlowProductSection
                      flow={flow as unknown as CommerceFlowDraft}
                      catalogProducts={catalogProducts}
                      catalogLoading={catalogLoading}
                      usedProducts={collectActiveRecoveryProductUsage(flows, flow.id)}
                      selectedDisplay="cards"
                      onSelectProduct={(productId, product) => selectProduct(flow, productId, product)}
                      onRemoveProduct={(productId) => removeProduct(flow, productId)}
                    />
                  </div>
                  <div className="mt-4 grid gap-4 sm:grid-cols-[160px_160px]">
                    <NumberField
                      label="Esperar antes de enviar"
                      value={Number(flow.delayValue || 30)}
                      min={1}
                      onChange={(delayValue) => commitFlow(flow.id, { delayValue })}
                    />
                    <SelectField
                      label="Unidad"
                      value={flow.delayUnit || "minutes"}
                      options={["minutes", "hours"]}
                      formatOption={(value) => (value === "hours" ? "Horas" : "Minutos")}
                      onChange={(delayUnit) =>
                        commitFlow(flow.id, { delayUnit: delayUnit as CartRecoveryFlowDraft["delayUnit"] })
                      }
                    />
                  </div>
                </div>
              ) : null}
              {deleteConfirmId === flow.id ? (
                <div className="absolute inset-0 flex items-center justify-center rounded-xl bg-white/90 p-4">
                  <div className="text-center">
                    <p className="text-sm font-semibold text-gray-950">¿Eliminar esta recuperación?</p>
                    <div className="mt-3 flex justify-center gap-2">
                      <button type="button" onClick={() => setDeleteConfirmId(null)} className="rounded-full bg-gray-100 px-4 py-2 text-sm font-semibold">
                        Cancelar
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          onChange(flows.filter((item) => item.id !== flow.id));
                          setDeleteConfirmId(null);
                        }}
                        className="rounded-full bg-red-600 px-4 py-2 text-sm font-semibold text-white"
                      >
                        Eliminar
                      </button>
                    </div>
                  </div>
                </div>
              ) : null}
            </article>
          );
        })}
      </div>
    </div>
  );
};

const ActionsConfigSubTabBar = ({
  activeSubTab,
  onSubTabChange,
  className = "",
}: {
  activeSubTab: ActionsConfigSubTab;
  onSubTabChange: (tab: ActionsConfigSubTab) => void;
  className?: string;
}) => {
  const [moreMenuOpen, setMoreMenuOpen] = useState(false);
  const moreButtonRef = useRef<HTMLButtonElement | null>(null);
  const [moreMenuPosition, setMoreMenuPosition] = useState<{ top: number; left: number } | null>(null);
  const subTabs: { id: ActionsConfigSubTab; label: string }[] = [
    { id: "ventas", label: "Ventas" },
    { id: "confirmaciones", label: "Confirmaciones" },
    { id: "logistica", label: "Logística" },
    { id: "agendamientos", label: "Agendamientos" },
    { id: "recuperacion", label: "Recuperación" },
    { id: "hotmart", label: "Hotmart" },
  ];
  const upcomingSubTabs: { id: ActionsConfigSubTab; label: string }[] = [
    { id: "otras", label: "Otras" },
  ];

  useLayoutEffect(() => {
    if (!moreMenuOpen || !moreButtonRef.current) {
      setMoreMenuPosition(null);
      return;
    }
    const rect = moreButtonRef.current.getBoundingClientRect();
    setMoreMenuPosition({
      top: rect.bottom + 8,
      left: Math.max(12, rect.right - 224),
    });
  }, [moreMenuOpen]);

  useEffect(() => {
    if (!moreMenuOpen) return;
    const closeMenu = (event: MouseEvent) => {
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (moreButtonRef.current?.contains(target)) return;
      setMoreMenuOpen(false);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setMoreMenuOpen(false);
    };
    window.addEventListener("mousedown", closeMenu);
    window.addEventListener("keydown", closeOnEscape);
    return () => {
      window.removeEventListener("mousedown", closeMenu);
      window.removeEventListener("keydown", closeOnEscape);
    };
  }, [moreMenuOpen]);

  return (
    <div className={className}>
      <h2 id="chatbot-settings-modal-title" className="sr-only">
        Acciones
      </h2>
      <div className="flex items-end justify-between gap-3 border-b border-gray-200">
        <div className="flex min-w-0 flex-1 gap-0 overflow-x-auto">
          {subTabs.map((tab) => {
            const active = activeSubTab === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => onSubTabChange(tab.id)}
                className={`-mb-px shrink-0 border-b-[3px] px-4 py-3 text-sm font-semibold transition ${
                  active ? "border-gray-950 text-gray-950" : "border-transparent text-gray-500 hover:text-gray-900"
                }`}
              >
                {tab.label}
              </button>
            );
          })}
        </div>
        <div className="relative shrink-0 self-stretch pb-1">
          <button
            ref={moreButtonRef}
            type="button"
            onClick={() => setMoreMenuOpen((open) => !open)}
            className={`inline-flex size-9 items-center justify-center rounded-full transition ${
              moreMenuOpen ? "bg-gray-100 text-gray-950" : "text-gray-500 hover:bg-gray-100 hover:text-gray-900"
            }`}
            title="Más acciones"
            aria-expanded={moreMenuOpen}
          >
            <MoreHorizontal className="size-4" />
          </button>
        </div>
      </div>

      {moreMenuOpen && moreMenuPosition
        ? createPortal(
            <div
              className="fixed z-[120] w-56 rounded-xl border border-gray-200 bg-white py-1 shadow-xl"
              style={{ top: moreMenuPosition.top, left: moreMenuPosition.left }}
            >
              {upcomingSubTabs.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  disabled
                  className="flex w-full cursor-not-allowed items-center justify-between px-3 py-2.5 text-left text-sm text-gray-400"
                >
                  <span>{tab.label}</span>
                  <span className="text-xs font-semibold">Próximamente</span>
                </button>
              ))}
            </div>,
            document.body
          )
        : null}
    </div>
  );
};

// Header estándar de las tabs de acciones (Julian): título protagonista + subtítulo gris.
const FlowListHeader = ({
  title,
  subtitle,
  addLabel,
  onAdd,
}: {
  title: string;
  subtitle?: string;
  addLabel: string;
  onAdd: () => void;
}) => (
  <div className="flex flex-wrap items-center justify-between gap-3">
    <div className="min-w-0">
      <h3 className="text-sm font-semibold text-gray-950">{title}</h3>
      {subtitle ? <p className="mt-1 text-sm leading-6 text-gray-500">{subtitle}</p> : null}
    </div>
    <button
      type="button"
      onClick={onAdd}
      className="inline-flex h-9 shrink-0 items-center justify-center rounded-full bg-gray-100 px-4 text-sm font-semibold text-gray-800 transition hover:bg-gray-200"
    >
      {addLabel}
    </button>
  </div>
);

const TemplatePickerField = ({
  label,
  value,
  templates,
  placeholder = "Seleccionar plantilla",
  recommendedNames,
  hidePreview = false,
  onChange,
  onEditTemplates,
}: {
  label: string;
  value: string;
  templates: WhatsAppTemplate[];
  placeholder?: string;
  // Nombres de plantillas recomendadas para esta acción: si existen en el listado,
  // el dropdown las muestra primero en su propio grupo.
  recommendedNames?: string[];
  // Oculta la previsualización interna: el caller la renderiza aparte a ancho completo.
  hidePreview?: boolean;
  onChange: (templateName: string, template?: WhatsAppTemplate) => void;
  onEditTemplates?: () => void;
}) => {
  const selected = templates.find((template) => template.name === value);
  const buildGroupedOptions = (): ModalSelectOption[] => {
    if (!recommendedNames?.length) return buildTemplatePickerOptions(templates);
    const recommended = recommendedNames
      .map((name) => templates.find((template) => template.name === name))
      .filter((template): template is WhatsAppTemplate => Boolean(template));
    if (!recommended.length) return buildTemplatePickerOptions(templates);
    const recommendedSet = new Set(recommended.map((template) => template.name));
    const others = templates.filter((template) => !recommendedSet.has(template.name));
    return [
      { value: "__heading_recommended__", label: "Recomendadas para esta acción", heading: true },
      ...buildTemplatePickerOptions(recommended),
      ...(others.length
        ? [
            { value: "__heading_others__", label: "Otras plantillas", heading: true },
            ...buildTemplatePickerOptions(others),
          ]
        : []),
    ];
  };
  return (
    <div className="block">
      <span className="mb-1 block text-xs font-semibold text-gray-600">{label}</span>
      <ModalSelect
        value={value}
        placeholder={placeholder}
        options={buildGroupedOptions()}
        dividers
        emphasizeLabels
        headerAction={onEditTemplates ? { label: "Editar plantillas", onClick: onEditTemplates } : undefined}
        onChange={(templateName) => {
          const match = templates.find((template) => template.name === templateName);
          onChange(templateName, match);
        }}
      />
      {!hidePreview && selected?.body_text ? (
        <div className="mt-2">
          <span className="mb-1 block text-xs font-semibold text-gray-600">Previsualización de la plantilla</span>
          {/* Borde punteado + fondo gris: se lee como muestra, no como caja editable. */}
          <div className="rounded-lg border border-dashed border-gray-200 bg-gray-50 px-3 py-2">
            {templateHasImageHeader(selected) ? <TemplateImageHeaderPlaceholder /> : null}
            <p className="whitespace-pre-line text-xs leading-5 text-gray-600">{selected.body_text}</p>
          </div>
        </div>
      ) : null}
    </div>
  );
};

const ChatbotActionsConfigTab = ({
  activeSubTab,
  onSubTabChange,
  account,
  settingsForm,
  orderFlows,
  confirmationFlows,
  recoveryFlows,
  automations,
  automationValidationError,
  approvedTemplates,
  pickerTemplates,
  commerceFlowValidationError,
  websiteSyncDraftsById,
  connections,
  toolsByConnectionId,
  onOrderFlowsChange,
  onConfirmationFlowsChange,
  onRecoveryFlowsChange,
  onAutomationsChange,
  onOpenIntegrationDetail,
  onOpenTemplates,
  onUpdateTool,
  onSettingsFormChange,
  onWebsiteSyncDraftChange,
  onImagesScreenChange,
  hotmartAccessCases,
  hotmartAccessCasesLoading,
  hotmartAccessCaseActionLoading,
  onRefreshHotmartAccessCases,
  onUpdateHotmartAccessCase,
}: {
  activeSubTab: ActionsConfigSubTab;
  onSubTabChange: (tab: ActionsConfigSubTab) => void;
  account: WhatsAppConnectionStatus | null;
  settingsForm: SupportSettingsPayload;
  orderFlows: CommerceFlowDraft[];
  confirmationFlows: CommerceFlowDraft[];
  recoveryFlows: CartRecoveryFlowDraft[];
  automations: AutomationDraft[];
  automationValidationError?: string | null;
  approvedTemplates: WhatsAppTemplate[];
  /** Todas las plantillas no-guardian (incluye PENDING), para los pickers de acciones. */
  pickerTemplates: WhatsAppTemplate[];
  commerceFlowValidationError?: string | null;
  websiteSyncDraftsById: Record<string, WebsiteSyncDraftState>;
  connections: IntegrationConnection[];
  toolsByConnectionId: Record<number, IntegrationTool[]>;
  onOrderFlowsChange: (rows: CommerceFlowDraft[]) => void;
  onConfirmationFlowsChange: (rows: CommerceFlowDraft[]) => void;
  onRecoveryFlowsChange: (rows: CartRecoveryFlowDraft[]) => void;
  onAutomationsChange: (rows: AutomationDraft[]) => void;
  onOpenIntegrationDetail: (detail: IntegrationDetail) => void;
  onOpenTemplates: () => void;
  onUpdateTool: (
    connectionId: number,
    tool: IntegrationTool,
    patch: { enabled?: boolean; policy?: string | null; metadata_json?: Record<string, unknown> }
  ) => void;
  onSettingsFormChange: (form: SupportSettingsPayload) => void;
  onWebsiteSyncDraftChange: (websiteId: string, draft: WebsiteSyncDraftState) => void;
  // Sub-pantalla "Configurar imágenes" abierta (Julian): el footer global de Guardar se oculta.
  onImagesScreenChange?: (open: boolean) => void;
  hotmartAccessCases: HotmartAccessCase[];
  hotmartAccessCasesLoading: boolean;
  hotmartAccessCaseActionLoading: string | null;
  onRefreshHotmartAccessCases: () => void;
  onUpdateHotmartAccessCase: (
    accessCase: HotmartAccessCase,
    patch: Partial<Pick<HotmartAccessCase, "case_status" | "resolution_note" | "recommended_action">>
  ) => void;
}) => (
  <div className="mx-auto max-w-5xl overflow-x-hidden pt-8">
    {commerceFlowValidationError ? (
      <div className="mb-4 rounded-xl bg-red-50 px-4 py-3 text-sm leading-5 text-red-700">{commerceFlowValidationError}</div>
    ) : null}

    {activeSubTab === "ventas" ? (
      <div className="space-y-6">
        <OrderCaptureTab
          flows={orderFlows}
          approvedTemplates={approvedTemplates}
          syncDraftsByWebsiteId={websiteSyncDraftsById}
          onSyncDraftChange={onWebsiteSyncDraftChange}
          onImagesScreenChange={onImagesScreenChange}
          onChange={onOrderFlowsChange}
        />
      </div>
    ) : null}

    {activeSubTab === "confirmaciones" ? (
      <ConfirmationsTab
        flows={confirmationFlows}
        approvedTemplates={approvedTemplates}
        pickerTemplates={pickerTemplates}
        onChange={onConfirmationFlowsChange}
        onOpenTemplates={onOpenTemplates}
        onImagesScreenChange={onImagesScreenChange}
      />
    ) : null}

    {activeSubTab === "logistica" ? (
      <AutomationsTab
        mode="logistics"
        automations={automations.filter((automation) => automation.trigger === "logistics_event")}
        validationError={automationValidationError}
        approvedTemplates={approvedTemplates}
        pickerTemplates={pickerTemplates}
        onChange={(logisticsRows) => {
          const nonLogisticsRows = automations.filter((automation) => automation.trigger !== "logistics_event");
          onAutomationsChange([
            ...logisticsRows.map((automation) => ({
              ...automation,
              trigger: "logistics_event",
              productScope: automation.productScope || "all",
            })),
            ...nonLogisticsRows,
          ]);
        }}
      />
    ) : null}

    {activeSubTab === "agendamientos" ? (
      <AppointmentSchedulingSettings />
    ) : null}

    {activeSubTab === "recuperacion" ? (
      <CartRecoveryTab
        flows={recoveryFlows}
        approvedTemplates={approvedTemplates}
        pickerTemplates={pickerTemplates}
        onChange={onRecoveryFlowsChange}
      />
    ) : null}

    {activeSubTab === "hotmart" ? (
      <>
        <ActionsTab
          account={account}
          settingsForm={settingsForm}
          connections={connections}
          toolsByConnectionId={toolsByConnectionId}
          integrationScope="hotmart"
          onOpenIntegrationDetail={onOpenIntegrationDetail}
          onUpdateTool={onUpdateTool}
          onSettingsFormChange={onSettingsFormChange}
        />
        <HotmartAccessCasesPanel
          cases={hotmartAccessCases}
          loading={hotmartAccessCasesLoading}
          actionLoadingId={
            hotmartAccessCaseActionLoading?.startsWith("access-case-")
              ? Number(hotmartAccessCaseActionLoading.replace("access-case-", ""))
              : null
          }
          onRefresh={onRefreshHotmartAccessCases}
          onUpdateCase={onUpdateHotmartAccessCase}
        />
      </>
    ) : null}

    {activeSubTab === "otras" ? (
      <ActionsTab
        account={account}
        settingsForm={settingsForm}
        connections={connections}
        toolsByConnectionId={toolsByConnectionId}
        integrationScope="otras"
        onOpenIntegrationDetail={onOpenIntegrationDetail}
        onUpdateTool={onUpdateTool}
        onSettingsFormChange={onSettingsFormChange}
      />
    ) : null}
  </div>
);

const MetricsStatCard = ({
  label,
  value,
  hint,
  onClick,
}: {
  label: string;
  value: string | number;
  hint?: string;
  onClick?: () => void;
}) => {
  const clickable = Boolean(onClick);
  const className = `rounded-2xl border border-gray-200 bg-white p-5 text-left shadow-sm transition ${
    clickable ? "hover:-translate-y-0.5 hover:border-gray-300 hover:shadow-md" : ""
  }`;
  if (clickable) {
    return (
      <button type="button" onClick={onClick} className={className}>
        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">{label}</p>
        <p className="mt-2 text-3xl font-bold tracking-tight text-gray-950">{value}</p>
        {hint ? <p className="mt-2 text-xs leading-5 text-gray-500">{hint}</p> : null}
      </button>
    );
  }
  return (
    <div className={className}>
      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">{label}</p>
      <p className="mt-2 text-3xl font-bold tracking-tight text-gray-950">{value}</p>
      {hint ? <p className="mt-2 text-xs leading-5 text-gray-500">{hint}</p> : null}
    </div>
  );
};

type MetricsTopProductRow = {
  product_id: string;
  name: string;
  image_url?: string | null;
  units: number;
};

const buildMetricsProductCatalog = (orderFlows: CommerceFlowDraft[]) => {
  const labels: Record<string, string> = {};
  const images: Record<string, string> = {};
  orderFlows.forEach((flow) => {
    Object.entries(flow.productLabels || {}).forEach(([productId, label]) => {
      if (label) labels[productId] = label;
    });
    Object.entries(flow.productImages || {}).forEach(([productId, image]) => {
      if (image) images[productId] = image;
    });
  });
  return { labels, images };
};

const resolveMetricsProduct = (
  row: MetricsTopProductRow,
  catalog: { labels: Record<string, string>; images: Record<string, string> }
) => ({
  ...row,
  name: catalog.labels[row.product_id] || row.name || row.product_id,
  image_url: row.image_url || catalog.images[row.product_id] || null,
});

const MetricsProductTopList = ({
  title,
  products,
  emptyLabel,
}: {
  title: string;
  products: MetricsTopProductRow[];
  emptyLabel: string;
}) => (
  <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
    <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">{title}</p>
    {/* Exactamente 4 items visibles (item 56px + gap 8px = 248px) y scroll para el resto. */}
    <div className="mt-3 max-h-[248px] overflow-y-auto pr-1">
      {products.length ? (
        <ul className="space-y-2">
          {products.map((product) => (
            <li key={product.product_id} className="flex items-center gap-3 rounded-xl bg-gray-50 px-3 py-2">
              <div className="flex size-10 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-white ring-1 ring-gray-200">
                {product.image_url ? (
                  <img src={product.image_url} alt={product.name} className="size-full object-cover" />
                ) : (
                  <ShoppingBag className="size-4 text-gray-400" />
                )}
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-gray-900">{product.name}</p>
                <p className="text-xs text-gray-500">{product.units} unidades</p>
              </div>
              <span className="text-sm font-semibold text-gray-950">{product.units}</span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-gray-500">{emptyLabel}</p>
      )}
    </div>
  </div>
);

const MetricsDashboardSectionTitle = ({ children }: { children: React.ReactNode }) => (
  <h2 className="text-lg font-bold tracking-tight text-gray-950">{children}</h2>
);

const MetricsDashboard = ({
  loading,
  rangeMode,
  rangeDays,
  customFrom,
  customTo,
  health,
  overview,
  confirmationsOverview: _confirmationsOverview,
  orderFlows,
  onRangeChange,
  onApplyCustomRange,
  onClearCustomRange,
  onRefresh,
  onOpenKanban,
}: {
  loading: boolean;
  rangeMode: MetricsRangeMode;
  rangeDays: number;
  customFrom: string;
  customTo: string;
  health: OperationsHealth | null;
  overview: SupportOverview | null;
  confirmationsOverview: ConfirmationsOverview | null;
  orderFlows: CommerceFlowDraft[];
  onRangeChange: (days: number) => void;
  onApplyCustomRange: (from: string, to: string) => void;
  onClearCustomRange: () => void;
  onRefresh: () => void;
  onOpenKanban: (domain: StateDomain) => void;
}) => {
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [draftFrom, setDraftFrom] = useState(customFrom);
  const [draftTo, setDraftTo] = useState(customTo);
  const conversations = health?.support?.conversations;
  const priorityCounts = conversations?.active_by_priority || {};
  const productCatalog = buildMetricsProductCatalog(orderFlows);
  const chatOrders = overview?.chat_orders;
  const confirmationsPeriod = overview?.confirmations_period;
  const orderTopProducts = (chatOrders?.top_products || []).map((row) => resolveMetricsProduct(row, productCatalog));
  const confirmationTopProducts = (confirmationsPeriod?.top_products || []).map((row) =>
    resolveMetricsProduct(row, productCatalog)
  );

  useEffect(() => {
    setDraftFrom(customFrom);
    setDraftTo(customTo);
  }, [customFrom, customTo]);

  return (
    <div className="h-full overflow-y-auto bg-[#F7F8FA]">
      <div className="mx-auto max-w-6xl px-4 py-6 md:px-6">
        <section>
          <MetricsDashboardSectionTitle>
            <span className="mr-2 inline-block size-2 animate-pulse rounded-full bg-emerald-500" />
            Métricas actuales del chatbot
          </MetricsDashboardSectionTitle>
          {loading && !health ? (
            <div className="mt-6 grid gap-4 md:grid-cols-2">
              {Array.from({ length: 2 }).map((_, index) => (
                <div key={index} className="h-32 animate-pulse rounded-2xl bg-white/80" />
              ))}
            </div>
          ) : (
            <div className="mt-6 grid gap-4 md:grid-cols-[minmax(0,1fr)_minmax(0,1.15fr)]">
              <MetricsStatCard
                label="Conversaciones abiertas"
                value={conversations?.active ?? overview?.conversations?.active ?? 0}
              />
              <div className="rounded-2xl border border-gray-200 bg-white shadow-sm">
                <div className="divide-y divide-gray-100">
                  <div className="flex items-center justify-between px-4 py-3">
                    <div className="flex items-center gap-2">
                      <PriorityAvatarMark priority="normal" />
                      <span className="text-sm font-medium text-gray-700">Normal</span>
                    </div>
                    <span className="text-sm font-semibold text-gray-900">{priorityCounts.normal ?? 0}</span>
                  </div>
                  <div className="flex items-center justify-between px-4 py-3">
                    <div className="flex items-center gap-2">
                      <PriorityAvatarMark priority="medium" />
                      <span className="text-sm font-medium text-gray-700">Prioritario</span>
                    </div>
                    <span className="text-sm font-semibold text-gray-900">{priorityCounts.medium ?? 0}</span>
                  </div>
                  <div className="flex items-center justify-between px-4 py-3">
                    <div className="flex items-center gap-2">
                      <PriorityAvatarMark priority="critical" />
                      <span className="text-sm font-medium text-gray-700">Grave</span>
                    </div>
                    <span className="text-sm font-semibold text-gray-900">{priorityCounts.critical ?? 0}</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </section>

        <section className="mt-10">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <MetricsDashboardSectionTitle>Métricas del período seleccionado</MetricsDashboardSectionTitle>
            <div className="flex flex-wrap items-center gap-2">
            {METRICS_RANGE_PRESETS.map((preset) => (
              <button
                key={preset.id}
                type="button"
                onClick={() => onRangeChange(preset.days)}
                className={`inline-flex h-9 items-center rounded-full px-3 text-sm font-semibold transition ${
                  rangeMode === "preset" && rangeDays === preset.days
                    ? "bg-gray-900 text-white"
                    : "bg-white text-gray-600 ring-1 ring-gray-200 hover:text-gray-900"
                }`}
              >
                {preset.label}
              </button>
            ))}
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setCalendarOpen((open) => !open)}
                  title="Calendario"
                  aria-label="Calendario"
                  className={`inline-flex size-9 items-center justify-center rounded-full transition ${
                    rangeMode === "custom"
                      ? "bg-gray-900 text-white"
                      : "bg-white text-gray-600 ring-1 ring-gray-200 hover:text-gray-900"
                  }`}
                >
                  <Calendar className="size-4" />
                </button>
              {calendarOpen ? (
                <div className="absolute right-0 top-11 z-10 w-72 rounded-xl border border-gray-200 bg-white p-4 shadow-xl">
                  <div className="space-y-3">
                    <label className="block">
                      <span className="mb-1 block text-xs font-semibold text-gray-600">Desde</span>
                      <input
                        type="date"
                        value={draftFrom}
                        onChange={(event) => setDraftFrom(event.target.value)}
                        className="h-10 w-full rounded-lg border border-gray-200 bg-white px-3 text-sm text-gray-900 outline-none focus:border-[#4368E5]"
                      />
                    </label>
                    <label className="block">
                      <span className="mb-1 block text-xs font-semibold text-gray-600">Hasta</span>
                      <input
                        type="date"
                        value={draftTo}
                        min={draftFrom || undefined}
                        onChange={(event) => setDraftTo(event.target.value)}
                        className="h-10 w-full rounded-lg border border-gray-200 bg-white px-3 text-sm text-gray-900 outline-none focus:border-[#4368E5]"
                      />
                    </label>
                  </div>
                  <div className="mt-4 flex justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        setDraftFrom("");
                        setDraftTo("");
                        onClearCustomRange();
                        setCalendarOpen(false);
                      }}
                      className="inline-flex h-9 items-center justify-center rounded-full px-3 text-xs font-semibold text-gray-600 hover:bg-gray-100"
                    >
                      Limpiar
                    </button>
                    <button
                      type="button"
                      disabled={!draftFrom}
                      onClick={() => {
                        onApplyCustomRange(draftFrom, draftTo);
                        setCalendarOpen(false);
                      }}
                      className="inline-flex h-9 items-center justify-center rounded-full bg-gray-900 px-4 text-xs font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      Aplicar
                    </button>
                  </div>
                </div>
              ) : null}
            </div>
            <button
              type="button"
              onClick={onRefresh}
              className="inline-flex size-9 items-center justify-center rounded-full bg-white text-gray-600 ring-1 ring-gray-200 hover:text-gray-900"
              title="Actualizar"
            >
              <RefreshCw className={`size-4 ${loading ? "animate-spin" : ""}`} />
            </button>
            </div>
          </div>

          {loading && !overview ? (
            <div className="mt-6 grid gap-4 md:grid-cols-2">
              {Array.from({ length: 2 }).map((_, index) => (
                <div key={index} className="h-28 animate-pulse rounded-2xl bg-white/80" />
              ))}
            </div>
          ) : (
            <>
              <div className="mt-6 grid gap-4 md:grid-cols-2">
                <MetricsStatCard label="Conversaciones totales" value={overview?.period?.conversations_total ?? 0} />
                <MetricsStatCard
                  label="Conversaciones procesadas"
                  value={overview?.period?.conversations_processed ?? 0}
                />
              </div>

              <div className="mt-8 space-y-3">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <h3 className="text-sm font-semibold text-gray-950">Órdenes tomadas en el chatbot</h3>
                  <button
                    type="button"
                    onClick={() => onOpenKanban("contacts")}
                    className="text-xs font-semibold text-[#4368E5] hover:underline"
                  >
                    Ver detalle
                  </button>
                </div>
                <div className="grid gap-4 lg:grid-cols-2">
                  <section className="rounded-2xl border border-gray-200 bg-white shadow-sm">
                    <div className="border-b border-gray-100 px-4 py-3">
                      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Total de órdenes</p>
                      <p className="mt-2 text-2xl font-bold tracking-tight text-gray-950">{chatOrders?.total ?? 0}</p>
                    </div>
                    <div className="grid gap-4 px-4 py-3 sm:grid-cols-2">
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Contra entrega</p>
                        <p className="mt-2 text-2xl font-bold tracking-tight text-gray-950">
                          {chatOrders?.by_payment?.cod ?? 0}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Pago por adelantado</p>
                        <p className="mt-2 text-2xl font-bold tracking-tight text-gray-950">
                          {chatOrders?.by_payment?.prepaid ?? 0}
                        </p>
                      </div>
                    </div>
                  </section>
                  <MetricsProductTopList
                    title="Top productos vendidos"
                    products={orderTopProducts}
                    emptyLabel="Sin órdenes en el periodo seleccionado."
                  />
                </div>
              </div>

              <div className="mt-8 space-y-3">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <h3 className="text-sm font-semibold text-gray-950">Confirmaciones</h3>
                  <button
                    type="button"
                    onClick={() => onOpenKanban("orders")}
                    className="text-xs font-semibold text-[#4368E5] hover:underline"
                  >
                    Ver detalle
                  </button>
                </div>
                <div className="grid gap-4 lg:grid-cols-2">
                  <section className="rounded-2xl border border-gray-200 bg-white shadow-sm">
                    <div className="border-b border-gray-100 px-4 py-3">
                      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Confirmaciones exitosas</p>
                      <p className="mt-2 text-2xl font-bold tracking-tight text-gray-950">
                        {confirmationsPeriod?.confirmed ?? 0}
                      </p>
                    </div>
                    <div className="px-4 py-3">
                      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Confirmaciones rechazadas</p>
                      <p className="mt-2 text-2xl font-bold tracking-tight text-gray-950">
                        {confirmationsPeriod?.rejected ?? 0}
                      </p>
                    </div>
                  </section>
                  <MetricsProductTopList
                    title="Top productos confirmados"
                    products={confirmationTopProducts}
                    emptyLabel="Sin confirmaciones en el periodo seleccionado."
                  />
                </div>
              </div>
            </>
          )}
        </section>
      </div>
    </div>
  );
};

const StatesWorkspace = ({
  domain,
  view,
  orderBoard,
  contactBoard,
  loading,
  onDomainChange,
  onViewChange,
  onUpdateOrder,
  onUpdateContactState,
  onOpenContactChat,
  onRefreshContactStates,
  onBackToDashboard,
}: {
  domain: StateDomain;
  view: StateView;
  orderBoard: StateBoard<SupportConfirmation> | null;
  contactBoard: StateBoard<ContactStateItem> | null;
  loading: boolean;
  onDomainChange: (domain: StateDomain) => void;
  onViewChange: (view: StateView) => void;
  onUpdateOrder: (confirmationId: number, payload: { status?: string | null; tags?: string[] }) => void;
  onUpdateContactState: (item: ContactStateItem, state: string) => void;
  onOpenContactChat: (conversationId: number | null | undefined) => void;
  onRefreshContactStates: () => void;
  onBackToDashboard?: () => void;
}) => {
  const board = domain === "orders" ? orderBoard : contactBoard;
  const [detail, setDetail] = useState<
    | { type: "contact"; item: ContactStateItem }
    | { type: "order"; order: SupportConfirmation }
    | null
  >(null);
  const [contactSidebarPayload, setContactSidebarPayload] = useState<{
    conversation: SupportConversation | null;
    orders: SupportConfirmation[];
    notes: SupportMessage[];
  }>({ conversation: null, orders: [], notes: [] });
  const [contactSidebarLoading, setContactSidebarLoading] = useState(false);

  useEffect(() => {
    setDetail(null);
  }, [domain]);

  useEffect(() => {
    if (detail?.type !== "contact") {
      setContactSidebarPayload({ conversation: null, orders: [], notes: [] });
      return;
    }
    let cancelled = false;
    setContactSidebarLoading(true);
    void loadContactSidebarPayload(detail.item)
      .then((payload) => {
        if (!cancelled) setContactSidebarPayload(payload);
      })
      .catch((error) => {
        if (!cancelled) toast.error(getErrorMessage(error));
      })
      .finally(() => {
        if (!cancelled) setContactSidebarLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [detail?.type === "contact" ? detail.item.id : null]);

  return (
    <div className="flex h-full min-h-0 bg-[#F7F8FA]">
      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        <div className="px-4 py-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-2">
              {onBackToDashboard ? (
                <button
                  type="button"
                  onClick={onBackToDashboard}
                  className="inline-flex h-8 items-center gap-1 rounded-full bg-white px-3 text-xs font-semibold text-gray-700 ring-1 ring-gray-200 transition hover:bg-gray-50"
                >
                  <ChevronLeft className="size-3.5" />
                  Métricas
                </button>
              ) : null}
              <div className="inline-flex rounded-full bg-gray-200/80 p-0.5">
              {([
                { id: "contacts", label: "Contactos" },
                { id: "orders", label: "Confirmaciones" },
              ] as const).map((option) => (
                <button
                  key={option.id}
                  onClick={() => onDomainChange(option.id)}
                  className={`h-7 rounded-full px-3 text-xs font-semibold transition ${
                    domain === option.id ? "bg-white text-gray-950" : "text-gray-500 hover:text-gray-900"
                  }`}
                >
                  {option.label}
                </button>
              ))}
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <button
                onClick={() => onViewChange("kanban")}
                className={`inline-flex size-8 items-center justify-center rounded-full border ${
                  view === "kanban" ? "border-gray-300 bg-gray-100 text-gray-950" : "border-gray-200 bg-white text-gray-600 hover:bg-gray-50"
                }`}
                title="Ver kanban"
              >
                <Columns3 className="size-3.5" />
              </button>
              <button
                onClick={() => onViewChange("table")}
                className={`inline-flex size-8 items-center justify-center rounded-full border ${
                  view === "table" ? "border-gray-300 bg-gray-100 text-gray-950" : "border-gray-200 bg-white text-gray-600 hover:bg-gray-50"
                }`}
                title="Ver tabla"
              >
                <ListChecks className="size-3.5" />
              </button>
            </div>
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-hidden">
          {loading && !board ? (
            <StatesWorkspaceSkeleton />
          ) : view === "kanban" ? (
            <StatesKanbanBoard
              domain={domain}
              board={board}
              selectedContactId={detail?.type === "contact" ? detail.item.id : null}
              selectedOrderId={detail?.type === "order" ? detail.order.id : null}
              onSelectContact={(item) => setDetail({ type: "contact", item })}
              onSelectOrder={(order) => setDetail({ type: "order", order })}
              onUpdateOrder={onUpdateOrder}
              onUpdateContactState={onUpdateContactState}
            />
          ) : (
            <StatesTable
              domain={domain}
              board={board}
              selectedContactId={detail?.type === "contact" ? detail.item.id : null}
              selectedOrderId={detail?.type === "order" ? detail.order.id : null}
              onSelectContact={(item) => setDetail({ type: "contact", item })}
              onSelectOrder={(order) => setDetail({ type: "order", order })}
              onUpdateOrder={onUpdateOrder}
              onUpdateContactState={onUpdateContactState}
            />
          )}
        </div>
      </div>

      {detail?.type === "contact" ? (
        <ContactStateSidebar
          key={detail.item.id}
          item={detail.item}
          onClose={() => setDetail(null)}
          onOpenChat={() => onOpenContactChat(detail.item.latest_conversation_id)}
          onRefreshContactStates={onRefreshContactStates}
          initialConversation={contactSidebarPayload.conversation}
          initialOrders={contactSidebarPayload.orders}
          initialNotes={contactSidebarPayload.notes}
          loadingOverride={contactSidebarLoading}
        />
      ) : null}
      {detail?.type === "order" ? (
        <OrderStateSidebar
          order={detail.order}
          onClose={() => setDetail(null)}
          onUpdateOrder={onUpdateOrder}
          onOpenChat={() => {
            const contact = (contactBoard?.rows || []).find(
              (row) =>
                row.contact.phone &&
                detail.order.customer_phone &&
                row.contact.phone === detail.order.customer_phone
            );
            onOpenContactChat(contact?.latest_conversation_id);
          }}
        />
      ) : null}
    </div>
  );
};

const StatesWorkspaceSkeleton = () => (
  <div className="flex h-full gap-3 overflow-hidden p-4">
    {Array.from({ length: 4 }).map((_, columnIndex) => (
      <div key={columnIndex} className="flex min-h-0 w-[320px] shrink-0 flex-col rounded-xl border border-gray-200 bg-white">
        <div className="border-b border-gray-100 px-4 py-3">
          <div className="h-4 w-32 animate-pulse rounded-full bg-gray-100" />
          <div className="mt-2 h-3 w-16 animate-pulse rounded-full bg-gray-100" />
        </div>
        <div className="space-y-2 p-3">
          {Array.from({ length: 3 }).map((__, cardIndex) => (
            <div key={cardIndex} className="rounded-lg border border-gray-100 bg-gray-50 p-3">
              <div className="h-4 w-2/3 animate-pulse rounded-full bg-gray-200" />
              <div className="mt-2 h-3 w-full animate-pulse rounded-full bg-gray-200" />
              <div className="mt-2 h-3 w-1/2 animate-pulse rounded-full bg-gray-200" />
            </div>
          ))}
        </div>
      </div>
    ))}
  </div>
);

const StatesKanbanBoard = ({
  domain,
  board,
  selectedContactId,
  selectedOrderId,
  onSelectContact,
  onSelectOrder,
  onUpdateOrder,
  onUpdateContactState,
}: {
  domain: StateDomain;
  board: StateBoard<SupportConfirmation> | StateBoard<ContactStateItem> | null;
  selectedContactId: number | null;
  selectedOrderId: number | null;
  onSelectContact: (item: ContactStateItem) => void;
  onSelectOrder: (order: SupportConfirmation) => void;
  onUpdateOrder: (confirmationId: number, payload: { status?: string | null; tags?: string[] }) => void;
  onUpdateContactState: (item: ContactStateItem, state: string) => void;
}) => {
  const columns = board?.columns || [];
  const [dragOverColumnId, setDragOverColumnId] = useState<string | null>(null);
  const [pointerDragPayload, setPointerDragPayload] = useState<{ domain: StateDomain; id: number } | null>(null);
  const skipNextPointerDropRef = useRef(false);
  const applyStateMove = useCallback((payload: { domain: StateDomain; id: number }, columnId: string) => {
    if (payload.domain !== domain) return;
    if (domain === "orders") {
      const order = (board?.rows || []).find((row) => row.id === payload.id) as SupportConfirmation | undefined;
      const nextStatus = orderStatusForStateColumn(columnId);
      if (!order || !nextStatus || order.status === nextStatus) return;
      onUpdateOrder(order.id, { status: nextStatus, tags: order.tags || [] });
      return;
    }
    const contact = (board?.rows || []).find((row) => row.id === payload.id) as ContactStateItem | undefined;
    if (!contact || contact.state === columnId) return;
    onUpdateContactState(contact, columnId);
  }, [board?.rows, domain, onUpdateContactState, onUpdateOrder]);

  useEffect(() => {
    if (!pointerDragPayload) return undefined;
    const getColumnIdAtPoint = (event: PointerEvent) => {
      const element = document.elementFromPoint(event.clientX, event.clientY);
      return (element?.closest("[data-state-column-id]") as HTMLElement | null)?.dataset.stateColumnId || null;
    };
    const handlePointerMove = (event: PointerEvent) => {
      setDragOverColumnId(getColumnIdAtPoint(event));
    };
    const handlePointerUp = (event: PointerEvent) => {
      const columnId = getColumnIdAtPoint(event);
      setPointerDragPayload(null);
      setDragOverColumnId(null);
      if (skipNextPointerDropRef.current) {
        skipNextPointerDropRef.current = false;
        return;
      }
      if (columnId) applyStateMove(pointerDragPayload, columnId);
    };
    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", handlePointerUp, { once: true });
    return () => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerUp);
    };
  }, [applyStateMove, pointerDragPayload]);

  const handleDrop = (event: React.DragEvent<HTMLDivElement>, columnId: string) => {
    event.preventDefault();
    setDragOverColumnId(null);
    skipNextPointerDropRef.current = true;
    const rawPayload =
      event.dataTransfer.getData("application/x-fluxi-state-card") ||
      event.dataTransfer.getData("text/plain");
    if (!rawPayload) return;
    try {
      const payload = JSON.parse(rawPayload) as { domain: StateDomain; id: number };
      applyStateMove(payload, columnId);
    } catch {
      return;
    }
  };
  const handlePointerDragStart = (event: React.PointerEvent<HTMLDivElement>, payload: { domain: StateDomain; id: number }) => {
    if (event.button !== 0) return;
    setPointerDragPayload(payload);
  };

  if (columns.length === 0) {
    return (
      <div className="flex h-full items-center justify-center p-6">
        <EmptyState icon={ListChecks} title="Sin estados" body="Cuando haya datos conectados aparecerán en este tablero." />
      </div>
    );
  }
  return (
    <div className="flex h-full gap-3 overflow-x-auto p-4">
      {columns.map((column) => (
        <div
          key={column.id}
          data-state-column-id={column.id}
          className={`flex min-h-0 w-[320px] shrink-0 flex-col rounded-xl border bg-white transition ${
            dragOverColumnId === column.id ? "border-gray-400 ring-2 ring-gray-200" : "border-gray-200"
          }`}
          onDragOver={(event) => {
            event.preventDefault();
            event.dataTransfer.dropEffect = "move";
            setDragOverColumnId(column.id);
          }}
          onDragLeave={() => setDragOverColumnId((current) => (current === column.id ? null : current))}
          onDrop={(event) => handleDrop(event, column.id)}
        >
          <div className="flex shrink-0 items-center justify-between border-b border-gray-100 px-4 py-3">
            <div>
              <p className="text-sm font-semibold text-gray-950">{column.label}</p>
            </div>
            <span className="inline-flex size-6 items-center justify-center rounded-full bg-gray-100 text-xs font-semibold text-gray-600">{column.items.length}</span>
          </div>
          <div className="min-h-0 flex-1 space-y-2 overflow-y-auto p-3">
            {column.items.map((item) =>
              domain === "orders" ? (
                <OrderStateCard
                  key={`order-${(item as SupportConfirmation).id}`}
                  order={item as SupportConfirmation}
                  selected={selectedOrderId === (item as SupportConfirmation).id}
                  onSelect={() => onSelectOrder(item as SupportConfirmation)}
                  onPointerDragStart={(event) => handlePointerDragStart(event, { domain: "orders", id: (item as SupportConfirmation).id })}
                />
              ) : (
                <ContactStateCard
                  key={`contact-${(item as ContactStateItem).id}`}
                  item={item as ContactStateItem}
                  selected={selectedContactId === (item as ContactStateItem).id}
                  onSelect={() => onSelectContact(item as ContactStateItem)}
                  onPointerDragStart={(event) => handlePointerDragStart(event, { domain: "contacts", id: (item as ContactStateItem).id })}
                />
              )
            )}
            {column.items.length === 0 ? (
              <div className="rounded-lg border border-dashed border-gray-200 bg-gray-50 px-3 py-8 text-center text-sm text-gray-500">
                Sin elementos
              </div>
            ) : null}
          </div>
        </div>
      ))}
    </div>
  );
};

const StatesTable = ({
  domain,
  board,
  selectedContactId,
  selectedOrderId,
  onSelectContact,
  onSelectOrder,
}: {
  domain: StateDomain;
  board: StateBoard<SupportConfirmation> | StateBoard<ContactStateItem> | null;
  selectedContactId: number | null;
  selectedOrderId: number | null;
  onSelectContact: (item: ContactStateItem) => void;
  onSelectOrder: (order: SupportConfirmation) => void;
  onUpdateOrder: (confirmationId: number, payload: { status?: string | null; tags?: string[] }) => void;
  onUpdateContactState: (item: ContactStateItem, state: string) => void;
}) => {
  const rows = board?.rows || [];
  return (
    <div className="h-full overflow-auto p-4">
      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
        <table className="w-full text-left text-sm">
          <thead className="bg-gray-50 text-xs uppercase text-gray-500">
            <tr>
              {domain === "orders" ? (
                <>
                  <th className="px-3 py-2">Orden</th>
                  <th className="px-3 py-2">Cliente</th>
                  <th className="px-3 py-2">Estado</th>
                  <th className="px-3 py-2">Total</th>
                  <th className="px-3 py-2">Último error</th>
                </>
              ) : (
                <>
                  <th className="px-3 py-2">Contacto</th>
                  <th className="px-3 py-2">Estado</th>
                  <th className="px-3 py-2">Tags</th>
                  <th className="px-3 py-2">Órdenes</th>
                  <th className="px-3 py-2">Última orden</th>
                </>
              )}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 bg-white">
            {rows.map((row) =>
              domain === "orders" ? (
                <OrderStateRow
                  key={`order-row-${(row as SupportConfirmation).id}`}
                  order={row as SupportConfirmation}
                  selected={selectedOrderId === (row as SupportConfirmation).id}
                  onSelect={() => onSelectOrder(row as SupportConfirmation)}
                />
              ) : (
                <ContactStateRow
                  key={`contact-row-${(row as ContactStateItem).id}`}
                  item={row as ContactStateItem}
                  selected={selectedContactId === (row as ContactStateItem).id}
                  onSelect={() => onSelectContact(row as ContactStateItem)}
                />
              )
            )}
            {rows.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-3 py-10 text-center text-sm text-gray-500">
                  Sin elementos para mostrar.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const OrderProductsList = ({ order }: { order: SupportConfirmation }) => {
  const products = Array.isArray(order.products) ? order.products.filter((product) => product?.name) : [];
  if (!products.length) {
    return (
      <p className="line-clamp-2 text-sm font-semibold text-gray-950">
        {order.product_summary || formatOrderDisplayReference(order)}
      </p>
    );
  }
  return (
    <div className="space-y-1">
      {products.slice(0, 3).map((product, index) => (
        <div key={`${order.id}-${product.name}-${index}`} className="flex items-start justify-between gap-3 text-sm">
          <span className="min-w-0 flex-1 truncate font-semibold text-gray-950">{product.name}</span>
          <span className="shrink-0 text-xs font-semibold text-gray-500">x{product.quantity || 1}</span>
        </div>
      ))}
      {products.length > 3 ? (
        <p className="text-xs font-medium text-gray-500">+{products.length - 3} productos más</p>
      ) : null}
    </div>
  );
};

const OrderMiniCard = ({ order }: { order: SupportConfirmation }) => {
  // Un solo chip que PROGRESA: estado de confirmación en español y, cuando la
  // logística ya avanzó (fila ecommerce del mismo pedido), el estado logístico lo
  // reemplaza (implica la confirmación). Nunca mostramos buckets crudos del backend.
  const bucket = String(order.state_bucket || "").toLowerCase();
  const rawState = String(order.raw_status || order.status || bucket);
  const logisticsAdvance = orderLogisticsAdvanceLabel(order.ecommerce_status);
  const useLogistics = bucket === "confirmed" && Boolean(logisticsAdvance);
  const stateLabel = useLogistics ? (logisticsAdvance as string) : orderCardStateLabel(rawState);
  const stateTooltip = useLogistics
    ? `El cliente confirmó y la logística avanzó: ${logisticsAdvance}.`
    : orderCardStateTooltip(rawState);
  const stateTone = useLogistics ? "success" : orderStateTone(rawState || bucket);
  // Confirmada pero sin avance logístico conocido: dejar claro que el envío sigue pendiente.
  const showLogisticsPending = bucket === "confirmed" && !logisticsAdvance;
  // UNA sola etiqueta de origen (antes: bucket "Ecommerce" + tag "ecommerce" + tooltip = 3 veces).
  const originLabel = order.source === "ecommerce" ? "Tienda" : "Chatbot";
  const businessTags = (order.tags || []).filter((tag) => String(tag).toLowerCase() !== "ecommerce");
  return (
  <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0 flex-1">
        <OrderProductsList order={order} />
      </div>
      <span className="shrink-0 text-xs font-semibold text-gray-500">{formatOrderDisplayReference(order)}</span>
    </div>
    <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-gray-500">
      <span>{formatOrderTotal(order.total)}</span>
      <span>{formatDate(order.created_at)}</span>
      <span className="rounded-full bg-white px-2 py-0.5 text-[11px] font-semibold text-gray-500">{originLabel}</span>
    </div>
    {rawState && rawState !== "pending" ? (
      <div className="mt-2 flex flex-wrap items-center gap-1.5">
        <Tooltip content={stateTooltip}>
          <span className="inline-flex">
            <StatusPill label={stateLabel} tone={stateTone} />
          </span>
        </Tooltip>
        {showLogisticsPending ? (
          <span className="rounded-full bg-white px-2 py-0.5 text-[11px] font-semibold text-gray-500">
            Logística pendiente
          </span>
        ) : null}
      </div>
    ) : null}
    {businessTags.length ? (
      <div className="mt-2 flex flex-wrap gap-1.5">
        {businessTags.slice(0, 4).map((tag) => (
          <span key={`${order.id}-${tag}`} className="rounded-full bg-white px-2 py-0.5 text-[11px] font-semibold text-gray-600">
            {tag}
          </span>
        ))}
      </div>
    ) : null}
  </div>
  );
};

const OrderStateCard = ({
  order,
  selected,
  onSelect,
  onPointerDragStart,
}: {
  order: SupportConfirmation;
  selected: boolean;
  onSelect: () => void;
  onPointerDragStart: (event: React.PointerEvent<HTMLDivElement>) => void;
}) => {
  const errorSummary = formatOperationalErrorSummary(order.last_error);
  const orderState = String(order.state_bucket || order.status || "");
  const showError = Boolean(errorSummary) && !["pending", "awaiting_response", "waiting"].includes(orderState);
  const showDeliveryIssue = Boolean(order.delivery_issue || order.state_bucket === "send_failed");
  return (
    <div
      role="button"
      tabIndex={0}
      draggable
      onClick={onSelect}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onSelect();
        }
      }}
      onPointerDown={onPointerDragStart}
      onDragStart={(event) => {
        event.dataTransfer.effectAllowed = "move";
        const payload = JSON.stringify({ domain: "orders", id: order.id });
        event.dataTransfer.setData("application/x-fluxi-state-card", payload);
        event.dataTransfer.setData("text/plain", payload);
      }}
      className={`cursor-grab rounded-lg border bg-white p-3 transition hover:bg-gray-50 active:cursor-grabbing ${
        selected ? "border-gray-400 bg-gray-50" : "border-gray-200"
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <OrderProductsList order={order} />
          <p className="mt-1 truncate text-xs text-gray-500">{order.customer_name || compactPhone(order.customer_phone)}</p>
        </div>
        <StatusPill label={formatOrderTotal(order.total)} tone="neutral" />
      </div>
      {showDeliveryIssue ? (
        <div className="mt-2">
          <StatusPill label={order.delivery_issue_label || "No se pudo enviar"} tone="danger" />
        </div>
      ) : null}
      {showError ? (
        <p className="mt-2 line-clamp-2 text-xs font-medium text-red-600" title={order.last_error || errorSummary}>
          {errorSummary}
        </p>
      ) : null}
    </div>
  );
};

const ContactStateCard = ({
  item,
  selected,
  onSelect,
  onPointerDragStart,
}: {
  item: ContactStateItem;
  selected: boolean;
  onSelect: () => void;
  onPointerDragStart: (event: React.PointerEvent<HTMLDivElement>) => void;
}) => {
  const displayName = item.contact.alias || item.contact.name || formatPhoneForDisplay(item.contact.phone) || `Contacto #${item.id}`;
  const displayPhone = compactPhone(item.contact.phone);
  const showPhoneLine = Boolean(displayPhone && displayPhone !== displayName);
  const tags = visibleBusinessTags(item.tags);
  return (
  <div
    role="button"
    tabIndex={0}
    draggable
    onClick={onSelect}
    onKeyDown={(event) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        onSelect();
      }
    }}
    onPointerDown={onPointerDragStart}
    onDragStart={(event) => {
      event.dataTransfer.effectAllowed = "move";
      const payload = JSON.stringify({ domain: "contacts", id: item.id });
      event.dataTransfer.setData("application/x-fluxi-state-card", payload);
      event.dataTransfer.setData("text/plain", payload);
    }}
    className={`cursor-grab rounded-lg border bg-white p-3 transition hover:bg-gray-50 active:cursor-grabbing ${
      selected ? "border-gray-400 bg-gray-50" : "border-gray-200"
    }`}
  >
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <p className="truncate text-sm font-semibold text-gray-950">{displayName}</p>
        {showPhoneLine ? <p className="mt-1 truncate text-xs text-gray-500">{displayPhone}</p> : null}
      </div>
    </div>
    {tags.length ? (
    <div className="mt-3 flex flex-wrap gap-1.5">
      {tags.slice(0, 5).map((tag) => (
        <span key={`${item.id}-${tag}`} className="rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-semibold text-gray-600">
          {tag}
        </span>
      ))}
    </div>
    ) : null}
            {item.confirmed_orders > 0 ? (
              <div className="mt-3 flex items-center text-xs text-gray-500">
                <span>
                  {item.confirmed_orders} compra{item.confirmed_orders === 1 ? "" : "s"} confirmada{item.confirmed_orders === 1 ? "" : "s"}
                </span>
              </div>
            ) : null}
          </div>
  );
};

const OrderStateRow = ({
  order,
  selected,
  onSelect,
}: {
  order: SupportConfirmation;
  selected: boolean;
  onSelect: () => void;
}) => (
  <tr
    onClick={onSelect}
    className={`cursor-pointer transition hover:bg-gray-50 ${selected ? "bg-gray-50" : ""}`}
  >
    <td className="px-3 py-2 font-medium text-gray-900">{formatOrderDisplayReference(order)}</td>
    <td className="px-3 py-2">
      <p>{order.customer_name || "Sin nombre"}</p>
      <p className="text-xs text-gray-500">{compactPhone(order.customer_phone)}</p>
    </td>
    <td className="px-3 py-2">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-medium text-gray-700">{formatOrderState(order.status)}</span>
        {order.delivery_issue || order.state_bucket === "send_failed" ? (
          <span className="rounded-full bg-red-100 px-2 py-0.5 text-[11px] font-semibold text-red-700">
            {order.delivery_issue_label || "No se pudo enviar"}
          </span>
        ) : null}
      </div>
    </td>
    <td className="px-3 py-2 text-gray-600">{formatOrderTotal(order.total)}</td>
    <td className="max-w-[260px] px-3 py-2">
      <p className="line-clamp-2 text-xs text-gray-500" title={order.last_error || undefined}>
        {formatOperationalErrorSummary(order.last_error) || order.product_summary || "Sin error"}
      </p>
    </td>
  </tr>
);

const ContactStateRow = ({
  item,
  selected,
  onSelect,
}: {
  item: ContactStateItem;
  selected: boolean;
  onSelect: () => void;
}) => {
  const displayName = item.contact.alias || item.contact.name || formatPhoneForDisplay(item.contact.phone) || `Contacto #${item.id}`;
  const displayPhone = compactPhone(item.contact.phone);
  const showPhoneLine = Boolean(displayPhone && displayPhone !== displayName);
  const tags = visibleBusinessTags(item.tags);
  return (
  <tr
    onClick={onSelect}
    className={`cursor-pointer transition hover:bg-gray-50 ${selected ? "bg-gray-50" : ""}`}
  >
    <td className="px-3 py-2">
      <p className="font-medium text-gray-900">{displayName}</p>
      {showPhoneLine ? <p className="text-xs text-gray-500">{displayPhone}</p> : null}
    </td>
    <td className="px-3 py-2">
      <span className="text-sm font-medium text-gray-700">{item.state_label || formatContactState(item.state)}</span>
    </td>
    <td className="px-3 py-2">
      <div className="flex max-w-[260px] flex-wrap gap-1">
        {tags.length ? tags.slice(0, 5).map((tag) => (
          <span key={`${item.id}-${tag}`} className="rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-semibold text-gray-600">
            {tag}
          </span>
        )) : <span className="text-xs text-gray-400">Sin etiquetas</span>}
      </div>
    </td>
    <td className="px-3 py-2 text-gray-600">{item.confirmed_orders}</td>
    <td className="px-3 py-2 text-gray-600">{formatDate(item.latest_order_at)}</td>
  </tr>
  );
};

const StateDetailRow = ({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) => (
  <div className="flex items-start justify-between gap-4 py-2 text-sm">
    <span className="text-gray-500">{label}</span>
    <div className="max-w-[220px] text-right font-medium text-gray-900">{value}</div>
  </div>
);

const ContactStateSidebar = ({
  item,
  onClose,
  onOpenChat,
  onRefreshContactStates,
  initialConversation = null,
  initialOrders = [],
  initialNotes = [],
  personView = null,
  onPersonViewRefresh,
  onJumpToConversation,
  showOpenChatButton = true,
  showAiSummaries,
  onShowAiSummariesChange,
  onDebugResetComplete,
  loadingOverride,
  className = "relative",
}: {
  item: ContactStateItem;
  onClose: () => void;
  onOpenChat: () => void;
  onRefreshContactStates: () => void;
  initialConversation?: SupportConversation | null;
  initialOrders?: SupportConfirmation[];
  personView?: PersonView | null;
  onPersonViewRefresh?: () => void;
  onJumpToConversation?: (conversationId: number) => void;
  initialNotes?: SupportMessage[];
  showOpenChatButton?: boolean;
  showAiSummaries?: boolean;
  onShowAiSummariesChange?: (value: boolean) => void;
  onDebugResetComplete?: () => void;
  loadingOverride?: boolean;
  className?: string;
}) => {
  const [conversation, setConversation] = useState<SupportConversation | null>(initialConversation);
  const [orders, setOrders] = useState<SupportConfirmation[]>(initialOrders);
  const [notes, setNotes] = useState<SupportMessage[]>(initialNotes);
  const [loading, setLoading] = useState(false);
  const [draft, setDraft] = useState<ConversationDetailsDraft>(() => ({
    status: "open",
    priority: "normal",
    customerState: item.state || getCustomerStateTag(item.contact.tags),
    name: item.contact.name || item.contact.alias || "",
    email: item.contact.email || "",
    labels: [],
    note: "",
  }));
  const [tagPickerOpen, setTagPickerOpen] = useState(false);
  const [tagInput, setTagInput] = useState("");
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [infoAdvancedOpen, setInfoAdvancedOpen] = useState(false);
  const [debugResetConfirmOpen, setDebugResetConfirmOpen] = useState(false);
  const [debugResetting, setDebugResetting] = useState(false);
  const [unmergeConfirmOpen, setUnmergeConfirmOpen] = useState(false);
  const [unmerging, setUnmerging] = useState(false);
  const contactSaveTimerRef = useRef<number | null>(null);
  const conversationId = item.latest_conversation_id || null;
  const phone = compactPhone(conversation?.contact?.phone || item.contact.phone);
  const selectedContact = conversation?.contact || item.contact;
  const visibleLabelOptions = useMemo(() => {
    const selected = new Set(draft.labels.map((label) => label.toLowerCase()));
    return normalizeLabels(item.tags || [])
      .filter((label) => !selected.has(label.toLowerCase()))
      .slice(0, 8);
  }, [draft.labels, item.tags]);

  const loadStateSidebarDetails = useCallback(async () => {
    setLoading(true);
    try {
      const [conversationResult, orderRows, messageRows] = await Promise.all([
        conversationId ? chatbotV2Service.getConversation(conversationId) : Promise.resolve(null),
        chatbotV2Service.listContactOrders(item.id, 12),
        conversationId ? chatbotV2Service.listMessages(conversationId, { limit: 120 }) : Promise.resolve([]),
      ]);
      setConversation(conversationResult);
      setOrders(orderRows);
      setNotes(messageRows.filter((message) => (message.private || message.direction === "internal") && !isRetryInstructionMessage(message)));
      setDraft(conversationResult
        ? detailsDraftFromConversation(conversationResult)
        : {
            status: "open",
            priority: "normal",
            customerState: item.state || getCustomerStateTag(item.contact.tags),
            name: item.contact.name || item.contact.alias || "",
            email: item.contact.email || "",
            labels: [],
            note: "",
          });
      setTagInput("");
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  }, [conversationId, item.contact.alias, item.contact.email, item.contact.name, item.contact.tags, item.id, item.state]);

  useEffect(() => {
    setConversation(initialConversation);
    setOrders(initialOrders);
    setNotes(initialNotes);
    if (initialConversation) {
      setDraft(detailsDraftFromConversation(initialConversation));
    }
  }, [item.id, initialConversation, initialNotes, initialOrders]);

  useEffect(() => {
    if (initialConversation) return;
    void loadStateSidebarDetails();
    return () => {
      if (contactSaveTimerRef.current) window.clearTimeout(contactSaveTimerRef.current);
    };
  }, [initialConversation, item.id, loadStateSidebarDetails]);

  const updateLocalConversation = (patch: Partial<SupportConversation>) => {
    setConversation((current) => (current ? mergeConversationRecord(current, { ...current, ...patch }) : current));
  };

  const updateConversationPatch = async (patch: SupportConversationUpdate, successMessage?: string) => {
    if (!conversation) return;
    setSavingKey("conversation");
    try {
      const updated = await chatbotV2Service.updateConversation(conversation.id, patch);
      setConversation(updated);
      onRefreshContactStates();
      if (successMessage) toast.success(successMessage);
    } catch (error) {
      toast.error(getErrorMessage(error));
      void loadStateSidebarDetails();
    } finally {
      setSavingKey(null);
    }
  };

  const updateContactPatch = async (patch: SupportContactUpdate, successMessage?: string) => {
    setSavingKey("contact");
    try {
      const updatedContact = await chatbotV2Service.updateContact(item.id, patch);
      setConversation((current) => (current ? { ...current, contact: updatedContact } : current));
      onRefreshContactStates();
      if (successMessage) toast.success(successMessage);
    } catch (error) {
      toast.error(getErrorMessage(error));
      void loadStateSidebarDetails();
    } finally {
      setSavingKey(null);
    }
  };

  const scheduleContactSave = (nextDraft: ConversationDetailsDraft) => {
    if (contactSaveTimerRef.current) window.clearTimeout(contactSaveTimerRef.current);
    contactSaveTimerRef.current = window.setTimeout(() => {
      void updateContactPatch({
        name: nextDraft.name || null,
        email: nextDraft.email || null,
        tags: mergeCustomerStateTag((selectedContact.tags || []).join(", "), nextDraft.customerState).split(", ").filter(Boolean),
      });
      contactSaveTimerRef.current = null;
    }, 450);
  };

  const addLabel = (label: string) => {
    if (!conversation) return;
    const normalized = label.trim();
    if (!normalized) return;
    const labels = normalizeLabels([...draft.labels, normalized]);
    setDraft((current) => ({ ...current, labels }));
    setTagInput("");
    setTagPickerOpen(false);
    void updateConversationPatch({ labels });
  };

  const removeLabel = (label: string) => {
    if (!conversation) return;
    const labels = draft.labels.filter((itemLabel) => itemLabel !== label);
    setDraft((current) => ({ ...current, labels }));
    void updateConversationPatch({ labels });
  };

  const createNote = async () => {
    if (!conversation || !draft.note.trim()) return;
    setSavingKey("note");
    try {
      const note = await chatbotV2Service.createPrivateNote(conversation.id, draft.note.trim());
      setNotes((current) => [...current, note]);
      setDraft((current) => ({ ...current, note: "" }));
      toast.success("Nota agregada");
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setSavingKey(null);
    }
  };

  const resetConversationDebug = async () => {
    if (!conversation || debugResetting) return;
    setDebugResetting(true);
    try {
      const result = await chatbotV2Service.resetConversationForDebug(conversation.id);
      const backupLabel = result.backup_suffix ? `-${result.backup_suffix}` : "";
      const localOrders = result.chat_confirmation_orders_reassigned ?? 0;
      const ecommerceOrders = result.ecommerce_orders_reassigned ?? 0;
      const ecommerceStatus = String(result.ecommerce_reassign_result?.status || "").toLowerCase();
      if (result.ecommerce_reassign_error || ecommerceStatus === "skipped") {
        toast(
          `Historial local limpio. Backup ${backupLabel}: ${localOrders} órdenes locales. Ecommerce quedó pendiente de reasignar.`
        );
      } else {
        toast.success(
          `Historial limpio. Backup ${backupLabel}: ${localOrders + ecommerceOrders} órdenes reasignadas.`
        );
      }
      setDebugResetConfirmOpen(false);
      onRefreshContactStates();
      onDebugResetComplete?.();
      onClose();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setDebugResetting(false);
    }
  };

  // Separa el CONTACTO ACTUAL del chat abierto de su persona unificada (unmerge).
  const unmergeCurrentContact = async () => {
    const contactId = selectedContact?.id;
    if (!contactId || unmerging) return;
    setUnmerging(true);
    try {
      await chatbotV2Service.unmergeContact(contactId);
      toast.success("Chat separado: vuelve a ser un cliente independiente");
      setUnmergeConfirmOpen(false);
      onPersonViewRefresh?.();
      onRefreshContactStates();
      void loadStateSidebarDetails();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setUnmerging(false);
    }
  };

  return (
    <aside className={`${className} flex h-full min-h-0 w-[min(92vw,414px)] shrink-0 flex-col overflow-hidden border-l border-gray-200 bg-white xl:w-[391px]`}>
      <div className="flex shrink-0 items-center justify-between px-4 py-3">
        <div>
          <h3 className="text-[21px] font-semibold text-gray-950">Información</h3>
        </div>
        <button
          onClick={onClose}
          className="inline-flex size-8 items-center justify-center text-gray-600 transition hover:text-gray-950"
          title="Cerrar información"
        >
          <X className="size-5" />
        </button>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-6">
        {showOpenChatButton ? (
          <button
            type="button"
            onClick={onOpenChat}
            disabled={!item.latest_conversation_id}
            className="mb-4 inline-flex h-10 w-full items-center justify-center rounded-full bg-gray-950 px-4 text-sm font-semibold text-white transition hover:bg-gray-800 disabled:bg-gray-100 disabled:text-gray-400"
          >
            Ir al chat
          </button>
        ) : null}

        {loading || loadingOverride ? (
          <div className="space-y-3">
            <div className="h-10 animate-pulse rounded-full bg-gray-100" />
            <div className="h-10 animate-pulse rounded-full bg-gray-100" />
            <div className="h-24 animate-pulse rounded-xl bg-gray-100" />
          </div>
        ) : (
          <>
            <div className="divide-y-0">
              <InlineSelectRow
                label="Estado"
                value={draft.status}
                options={statusOptions}
                formatOption={formatConversationStatus}
                onChange={(value) => {
                  setDraft((current) => ({ ...current, status: value }));
                  void updateConversationPatch({ status: value });
                }}
              />
              <InlineSelectRow
                label="Prioridad"
                value={draft.priority}
                options={priorityOptions}
                formatOption={formatConversationPriority}
                optionIcon={(value) => <PriorityAvatarMark priority={value} />}
                onChange={(value) => {
                  setDraft((current) => ({ ...current, priority: value }));
                  updateLocalConversation({ priority: value });
                  void updateConversationPatch({ priority: value });
                }}
              />
              <InlineSelectRow
                label="Estado cliente"
                value={draft.customerState || item.state}
                options={customerStateOptions}
                formatOption={formatContactState}
                onChange={(value) => {
                  const nextDraft = { ...draft, customerState: value };
                  setDraft(nextDraft);
                  scheduleContactSave(nextDraft);
                }}
              />
              <DetailInputRow label="Teléfono" value={phone || "Sin teléfono"} readOnly />
              <DetailInputRow
                label="Nombre"
                value={draft.name}
                placeholder="Agregar nombre"
                onChange={(value) => {
                  const nextDraft = { ...draft, name: value };
                  setDraft(nextDraft);
                  scheduleContactSave(nextDraft);
                }}
              />
              <DetailInputRow
                label="Email"
                value={draft.email}
                placeholder="Agregar email"
                onChange={(value) => {
                  const nextDraft = { ...draft, email: value };
                  setDraft(nextDraft);
                  scheduleContactSave(nextDraft);
                }}
              />
              {item.confirmed_orders > 0 ? (
                <div className="flex items-start justify-between gap-3 py-3 text-sm">
                  <span className="text-gray-500">Compras confirmadas</span>
                  <span className="font-semibold text-gray-900">{item.confirmed_orders}</span>
                </div>
              ) : null}
              {item.latest_order_at ? (
                <div className="flex items-start justify-between gap-3 py-3 text-sm">
                  <span className="text-gray-500">Última orden</span>
                  <span className="font-semibold text-gray-900">{formatDate(item.latest_order_at)}</span>
                </div>
              ) : null}
              {!conversation ? (
                <p className="mt-2 rounded-lg bg-gray-50 px-3 py-2 text-sm text-gray-500">
                  Este contacto todavía no tiene conversación asociada; algunas acciones se activan cuando exista un chat.
                </p>
              ) : null}
            </div>

            {selectedContact.opted_out_at ? (
              <div className="mt-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                Este contacto pidió no recibir mensajes por WhatsApp.
              </div>
            ) : null}

            <section className="py-3 text-sm">
              <div className="flex items-start justify-between gap-3">
                <span className="pt-2 text-gray-500">Etiquetas</span>
                <div className="relative w-[210px] max-w-[65%] shrink-0">
                  <input
                    value={tagInput}
                    onChange={(event) => setTagInput(event.target.value)}
                    onFocus={() => setTagPickerOpen(true)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === ",") {
                        event.preventDefault();
                        addLabel(tagInput);
                      }
                    }}
                    disabled={!conversation}
                    placeholder="Agregar etiqueta"
                    className="h-10 w-full rounded-full border border-gray-200 px-3 text-sm font-semibold text-gray-800 outline-none transition placeholder:font-medium placeholder:text-gray-400 focus:border-gray-300 focus:ring-2 focus:ring-gray-200 disabled:bg-gray-50 disabled:text-gray-400"
                  />
                  {tagPickerOpen && visibleLabelOptions.length > 0 ? (
                    <div className="absolute left-0 right-0 top-11 z-20 rounded-2xl border border-gray-200 bg-white p-2 shadow-[0px_2px_8px_0px_rgba(99,99,99,0.2)]">
                      {visibleLabelOptions.map((label) => (
                        <button
                          key={label}
                          type="button"
                          onMouseDown={(event) => event.preventDefault()}
                          onClick={() => addLabel(label)}
                          className="flex h-8 w-full items-center rounded-full px-3 text-left text-xs font-medium text-gray-600 hover:bg-gray-50"
                        >
                          {formatConversationLabel(label)}
                        </button>
                      ))}
                    </div>
                  ) : null}
                </div>
              </div>
          {draft.labels.length > 0 ? (
            <div className="mt-3 flex flex-wrap gap-2">
              {draft.labels.map((label) => (
                <button
                  key={label}
                  onClick={() => removeLabel(label)}
                      className={`inline-flex h-7 items-center gap-1 rounded-full border px-2.5 text-xs font-semibold ${
                        label === HUMAN_HELP_REQUESTED_LABEL
                          ? "border-emerald-200 bg-emerald-50 text-emerald-700 hover:border-emerald-300"
                          : "border-[#DCE3FF] bg-[#F5F7FF] text-[#223AAE] hover:border-[#4368E5]"
                      }`}
                      title="Quitar etiqueta"
                    >
                      {formatConversationLabel(label)}
                      <XCircle className="size-3.5" />
                    </button>
                  ))}
                </div>
              ) : null}
            </section>

            {personView && (personView.conversations?.length || 0) > 1 ? (
              <section className="mt-6">
                <div className="flex items-center justify-between gap-3">
                  <p className="text-sm font-semibold text-gray-950">Conversaciones de este cliente</p>
                  <span className="text-xs text-gray-400">{personView.conversations.length}</span>
                </div>
                <p className="mt-1 text-xs text-gray-400">
                  Es la misma persona en varios chats. Toca uno para abrirlo. Sus órdenes y datos están unificados aquí.
                </p>
                <div className="mt-3 space-y-2">
                  {(personView.conversations || []).map((conv) => {
                    const isCurrent = conv.id === conversation?.id;
                    return (
                      <button
                        key={conv.id}
                        type="button"
                        disabled={isCurrent}
                        onClick={() => !isCurrent && onJumpToConversation?.(conv.id)}
                        className={`flex w-full items-center justify-between rounded-lg px-3 py-2 text-left text-sm transition ${
                          isCurrent
                            ? "border border-gray-300 bg-gray-100 text-gray-900"
                            : "bg-gray-50 text-gray-800 hover:bg-gray-100"
                        }`}
                      >
                        <span className="font-medium capitalize">
                          {conv.channel}
                          {isCurrent ? " · aquí" : ""}
                        </span>
                        {isCurrent ? null : <ChevronRight className="size-4 text-gray-400" />}
                      </button>
                    );
                  })}
                  <p className="text-xs text-gray-400">
                    {personView.orders?.length || 0} orden(es) en total para esta persona.
                  </p>
                  {selectedContact?.id ? (
                    <div className="flex justify-end">
                      <button
                        type="button"
                        onClick={() => setUnmergeConfirmOpen(true)}
                        className="text-xs text-gray-500 underline underline-offset-2 hover:text-gray-800"
                      >
                        Separar conversaciones
                      </button>
                    </div>
                  ) : null}
                </div>
              </section>
            ) : null}

            <section className="mt-6">
              <div className="flex items-center justify-between gap-3">
                <p className="text-sm font-semibold text-gray-950">Órdenes</p>
                {orders.length > 0 ? <span className="text-xs text-gray-400">{orders.length}</span> : null}
              </div>
              <div className="mt-3 space-y-2">
                {orders.map((order) => <OrderMiniCard key={order.id} order={order} />)}
                {orders.length === 0 ? (
                  <p className="rounded-lg bg-gray-50 px-3 py-2 text-sm text-gray-500">
                    Sin órdenes asociadas a este teléfono.
                  </p>
                ) : null}
              </div>
            </section>

            <section className="mt-6">
              <p className="text-sm font-semibold text-gray-950">Notas de conversación</p>
              <textarea
                value={draft.note}
                onChange={(event) => setDraft((current) => ({ ...current, note: event.target.value }))}
                rows={4}
                placeholder="Agregar nota interna"
                disabled={!conversation}
                className="mt-3 w-full resize-none rounded-lg border border-gray-200 p-3 text-sm outline-none focus:border-[#4368E5] disabled:bg-gray-50 disabled:text-gray-400"
              />
              {draft.note.trim() ? (
                <div className="mt-2 flex justify-end">
                  <button
                    type="button"
                    onClick={() => void createNote()}
                    disabled={savingKey === "note" || !conversation}
                    className="inline-flex h-8 items-center gap-1.5 rounded-full bg-gray-950 px-3 text-xs font-semibold text-white hover:bg-gray-800 disabled:opacity-50"
                  >
                    {savingKey === "note" ? <Loader2 className="size-3.5 animate-spin" /> : <CheckCircle2 className="size-3.5" />}
                    Agregar nota
                  </button>
                </div>
              ) : null}
              <div className="mt-3 space-y-2">
                {notes.filter((note) => !isRetryInstructionMessage(note)).slice(-5).map((note) => (
                  <div
                    key={note.id}
                    className={`rounded-lg border px-3 py-2 ${
                      isRetryInstructionMessage(note)
                        ? "border-blue-100 bg-blue-50"
                        : "border-amber-100 bg-amber-50"
                    }`}
                  >
                    <p className={`mb-1 text-[11px] font-semibold uppercase ${isRetryInstructionMessage(note) ? "text-blue-700" : "text-amber-700"}`}>
                      {isRetryInstructionMessage(note) ? "Instrucciones de reintentar IA" : "Nota"}
                    </p>
                    <p className={`whitespace-pre-wrap text-sm ${isRetryInstructionMessage(note) ? "text-blue-950" : "text-amber-950"}`}>{note.content}</p>
                    <div className="mt-1 flex items-center justify-end gap-2 text-[11px] text-gray-500">
                      <span>{noteSenderLabel(note)}</span>
                      <span aria-hidden="true">•</span>
                      <span>{formatDate(note.created_at)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {typeof showAiSummaries === "boolean" && onShowAiSummariesChange ? (
              <section className="mt-6 border-t border-gray-100 pt-4">
                <button
                  type="button"
                  onClick={() => setInfoAdvancedOpen((open) => !open)}
                  className="flex w-full items-center justify-between rounded-xl bg-gray-50 px-4 py-3 text-left transition hover:bg-gray-100"
                >
                  <span className="text-sm font-semibold text-gray-950">Ver información avanzada</span>
                  <ChevronDown className={`size-4 text-gray-500 transition ${infoAdvancedOpen ? "rotate-180" : ""}`} />
                </button>
                {infoAdvancedOpen ? (
                  <div className="mt-3 space-y-4 rounded-xl bg-gray-50 p-4">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <p className="text-sm font-semibold text-gray-950">Mostrar resúmenes de memoria en mensajes</p>
                        <p className="mt-1 text-xs leading-5 text-gray-500">
                          Cuando está activo, verás chips con el resumen de memoria que usó la IA bajo los mensajes del bot.
                        </p>
                      </div>
                      <ToggleSwitch
                        checked={showAiSummaries}
                        label={showAiSummaries ? "Activo" : "Pausado"}
                        onChange={onShowAiSummariesChange}
                      />
                    </div>
                    <div className="border-t border-gray-200 pt-4">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-gray-950">Eliminar historial y órdenes de prueba</p>
                          <p className="mt-1 text-xs leading-5 text-gray-500">
                            Mueve el historial a un backup sufijado y deja este teléfono listo para probar desde cero.
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() => setDebugResetConfirmOpen(true)}
                          disabled={!conversation || debugResetting}
                          className="inline-flex h-9 shrink-0 items-center gap-2 rounded-full border border-red-200 bg-white px-3 text-xs font-semibold text-red-700 transition hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          {debugResetting ? <Loader2 className="size-3.5 animate-spin" /> : <Trash2 className="size-3.5" />}
                          Eliminar
                        </button>
                      </div>
                    </div>
                  </div>
                ) : null}
              </section>
            ) : null}
          </>
        )}
        <ConfirmModal
          open={unmergeConfirmOpen}
          title="Separar este chat del cliente"
          description="Este chat volverá a ser un cliente independiente, con sus propios datos y órdenes. Las demás conversaciones del cliente unificado no cambian. Si más adelante se detecta que son la misma persona, el sistema volverá a proponer unirlos."
          positionClass="fixed"
          zIndexClass="z-[80]"
          actions={[
            {
              label: "Cancelar",
              onClick: () => setUnmergeConfirmOpen(false),
              variant: "secondary",
              disabled: unmerging,
            },
            {
              label: "Separar",
              onClick: () => void unmergeCurrentContact(),
              variant: "primary",
              disabled: unmerging,
              loading: unmerging,
            },
          ]}
        />
        <ConfirmModal
          open={debugResetConfirmOpen}
          title="Eliminar historial de prueba"
          description="Esto archivará la conversación actual, moverá sus mensajes y órdenes a un contacto backup con sufijo, y permitirá que el próximo mensaje de este teléfono cree una conversación limpia."
          positionClass="fixed"
          zIndexClass="z-[120]"
          actions={[
            {
              label: "Cancelar",
              onClick: () => setDebugResetConfirmOpen(false),
              variant: "secondary",
              disabled: debugResetting,
            },
            {
              label: "Eliminar historial",
              onClick: () => void resetConversationDebug(),
              variant: "destructive",
              disabled: debugResetting,
              loading: debugResetting,
            },
          ]}
        />
        {savingKey && savingKey !== "note" ? (
          <div className="pointer-events-none absolute bottom-4 right-4 inline-flex items-center gap-2 rounded-full bg-white px-3 py-2 text-xs font-semibold text-gray-600 shadow-[0px_2px_8px_0px_rgba(99,99,99,0.2)]">
            <Loader2 className="size-3.5 animate-spin" />
            Guardando
          </div>
        ) : null}
            </div>
    </aside>
  );
};

const OrderStateSidebar = ({
  order,
  onClose,
  onUpdateOrder,
  onOpenChat,
}: {
  order: SupportConfirmation;
  onClose: () => void;
  onUpdateOrder: (confirmationId: number, payload: { status?: string | null; tags?: string[] }) => void;
  onOpenChat: () => void;
}) => {
  const errorSummary = formatOperationalErrorSummary(order.last_error);
  const currentColumn =
    order.state_bucket ||
    ({
      awaiting_response: "waiting",
      pending: "waiting",
      confirmed: "confirmed",
      rejected: "rejected",
      expired: "expired",
      send_failed: "send_failed",
    }[String(order.status || "")] || "waiting");
  const [stateColumn, setStateColumn] = useState(String(currentColumn));
  useEffect(() => {
    setStateColumn(String(currentColumn));
  }, [order.id, currentColumn]);
  const canOpenChat = Boolean(order.customer_phone);
  return (
    <aside className="flex h-full min-h-0 w-[min(92vw,414px)] shrink-0 flex-col overflow-hidden border-l border-gray-200 bg-white xl:w-[391px]">
      <div className="flex shrink-0 items-center justify-between px-4 py-3">
        <div>
          <h3 className="text-[21px] font-semibold text-gray-950">Orden</h3>
        </div>
        <button
          onClick={onClose}
          className="inline-flex size-8 items-center justify-center text-gray-600 transition hover:text-gray-950"
          title="Cerrar información"
        >
          <X className="size-5" />
        </button>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-6">
        <div className="space-y-3">
          <label className="block">
            <span className="mb-1 block text-xs font-semibold text-gray-600">Estado</span>
            <CompactSelect
              value={stateColumn}
              options={orderStateColumnOptions}
              formatOption={orderStateColumnLabel}
              onChange={(columnId) => {
                setStateColumn(columnId);
                const nextStatus = orderStatusForStateColumn(columnId);
                if (!nextStatus || nextStatus === order.status) return;
                onUpdateOrder(order.id, { status: nextStatus, tags: order.tags || [] });
              }}
              ariaLabel="Cambiar estado de orden"
              className="w-full"
            />
          </label>
          <button
            type="button"
            onClick={onOpenChat}
            disabled={!canOpenChat}
            className="inline-flex h-10 w-full items-center justify-center rounded-full bg-gray-950 px-4 text-sm font-semibold text-white transition hover:bg-gray-800 disabled:bg-gray-100 disabled:text-gray-400"
          >
            Ir al chat
          </button>
        </div>

        <section className="mt-5 rounded-xl bg-gray-50 p-3">
          <StateDetailRow label="Orden" value={formatOrderDisplayReference(order)} />
          <StateDetailRow label="Cliente" value={order.customer_name || "Sin nombre"} />
          <StateDetailRow label="Teléfono" value={formatPhoneForDisplay(order.customer_phone) || "Sin teléfono"} />
          <StateDetailRow label="Total" value={formatOrderTotal(order.total)} />
          <StateDetailRow label="Producto" value={order.product_summary || "Sin producto sincronizado"} />
        </section>

        <section className="mt-5 rounded-xl bg-gray-50 p-3">
          <StateDetailRow label="Creada" value={formatDate(order.created_at)} />
          <StateDetailRow label="Actualizada" value={formatDate(order.updated_at)} />
          {order.confirmed_at ? <StateDetailRow label="Confirmada" value={formatDate(order.confirmed_at)} /> : null}
          {order.expired_at ? <StateDetailRow label="Expirada" value={formatDate(order.expired_at)} /> : null}
          {order.next_retry_at ? <StateDetailRow label="Próximo intento" value={formatDate(order.next_retry_at)} /> : null}
          <StateDetailRow label="Reintentos" value={order.retry_count ?? 0} />
        </section>

        {order.tags?.length ? (
          <section className="mt-5">
            <p className="text-sm font-semibold text-gray-950">Etiquetas</p>
            <div className="mt-3 flex flex-wrap gap-2">
              {order.tags.map((tag) => (
                <span key={`${order.id}-${tag}`} className="inline-flex h-7 items-center rounded-full bg-gray-100 px-2.5 text-xs font-semibold text-gray-600">
                  {formatConversationLabel(tag)}
                </span>
              ))}
            </div>
          </section>
        ) : null}

        {errorSummary ? (
          <section className="mt-5 rounded-xl bg-red-50 p-3">
            <p className="text-sm font-semibold text-red-800">Error al enviar</p>
            <p className="mt-2 text-sm leading-5 text-red-700">{errorSummary}</p>
            {order.last_error && order.last_error !== errorSummary ? (
              <p className="mt-2 text-xs leading-5 text-red-600">{order.last_error}</p>
            ) : null}
          </section>
        ) : null}
      </div>
    </aside>
  );
};

const orderStateTone = (state?: string | null): "neutral" | "success" | "warning" | "danger" => {
  const normalized = String(state || "");
  if (normalized === "confirmed") return "success";
  if (normalized === "rejected" || normalized === "expired" || normalized === "send_failed" || normalized === "canceled") return "danger";
  if (normalized === "awaiting_response" || normalized === "pending") return "warning";
  return "neutral";
};

const orderStatusForStateColumn = (state: string): string | null => ({
  waiting: "awaiting_response",
  confirmed: "confirmed",
  rejected: "rejected",
  expired: "expired",
  send_failed: "send_failed",
}[state] || null);

const orderStateColumnOptions = ["waiting", "confirmed", "rejected", "expired", "send_failed"];

const orderStateColumnLabel = (columnId: string) => {
  const labels: Record<string, string> = {
    waiting: "En espera",
    confirmed: "Confirmada",
    rejected: "Rechazada",
    expired: "Expirada",
    send_failed: "Error de envío",
  };
  return labels[columnId] || humanize(columnId);
};

const settingsToForm = (settings: SupportSettings): SupportSettingsPayload => ({
  status: settings.status,
  business_name: (settings.business_name || "").trim(),
  bot_name: (settings.bot_name || "").trim(),
  custom_instructions: settings.custom_instructions ?? "",
  ai_enabled: settings.ai_enabled,
  voice_replies_enabled: Boolean(settings.voice_replies_enabled),
  learning_mode_enabled: Boolean(settings.learning_mode_enabled),
  humanized_replies_enabled: settings.humanized_replies_enabled !== false,
  order_confirmations_enabled: Boolean(settings.order_confirmations_enabled),
  order_confirmation_product_scope: settings.order_confirmation_product_scope || "all_products",
  order_confirmation_selected_products: settings.order_confirmation_selected_products || [],
  order_state_confirmed_instructions: settings.order_state_confirmed_instructions || "",
  order_state_rejected_instructions: settings.order_state_rejected_instructions || "",
  order_state_auto_expire_enabled: Boolean(settings.order_state_auto_expire_enabled),
  order_state_auto_expire_hours: settings.order_state_auto_expire_hours || 24,
  customer_state_purchased_product_scope: settings.customer_state_purchased_product_scope || "all_products",
  customer_state_purchased_products: settings.customer_state_purchased_products || [],
  customer_state_support_active_instructions: settings.customer_state_support_active_instructions || "",
  customer_state_support_resolved_instructions: settings.customer_state_support_resolved_instructions || "",
  metadata_json: isRecord(settings.metadata_json) ? settings.metadata_json : {},
});

const SETTINGS_METADATA_KEYS_WITH_DEDICATED_SAVE = ["support_automations", "media_resources"] as const;

const ASSISTANT_KNOWLEDGE_METADATA_KEYS = [
  "conversation_closing_instructions",
  "priority_medium_instructions",
  "priority_high_instructions",
  "priority_normal_instructions",
  "human_help_request_instructions",
  "chat_note_usage_instructions",
  "refund_policy_instructions",
] as const;

const assistantKnowledgeSettingsComparable = (payload: SupportSettingsPayload) => {
  const cleaned = cleanSettingsPayload(payload);
  const metadata = isRecord(payload.metadata_json) ? payload.metadata_json : {};
  const comparableMetadata: Record<string, string | null> = {};
  for (const key of ASSISTANT_KNOWLEDGE_METADATA_KEYS) {
    const value = metadata[key];
    comparableMetadata[key] = typeof value === "string" ? value.trim() || null : null;
  }
  return {
    business_name: cleaned.business_name,
    bot_name: cleaned.bot_name,
    custom_instructions: cleaned.custom_instructions,
    ai_enabled: Boolean(payload.ai_enabled),
    voice_replies_enabled: Boolean(payload.voice_replies_enabled),
    learning_mode_enabled: Boolean(payload.learning_mode_enabled),
    humanized_replies_enabled: payload.humanized_replies_enabled !== false,
    customer_state_support_active_instructions:
      (payload.customer_state_support_active_instructions || "").trim() || null,
    customer_state_support_resolved_instructions:
      (payload.customer_state_support_resolved_instructions || "").trim() || null,
    metadata_json: comparableMetadata,
    // Los seguimientos dinámicos se editan en este tab: sin esto, cambiarlos no
    // marcaba el tab como dirty y el botón Guardar no aparecía.
    dynamic_followup_settings: dynamicFollowupSettingsFromMetadata(metadata),
  };
};

const settingsFormComparable = (payload: SupportSettingsPayload) => {
  const cleaned = cleanSettingsPayload(payload);
  const metadata = isRecord(payload.metadata_json) ? { ...payload.metadata_json } : {};
  for (const key of SETTINGS_METADATA_KEYS_WITH_DEDICATED_SAVE) {
    delete metadata[key];
  }
  return {
    ...cleaned,
    metadata_json: metadata,
  };
};

const buildSettingsSaveMetadata = (
  settings: SupportSettings | null | undefined,
  settingsForm: SupportSettingsPayload
): Record<string, unknown> => {
  const currentMetadata = isRecord(settings?.metadata_json) ? settings.metadata_json : {};
  const formMetadata = isRecord(settingsForm.metadata_json) ? settingsForm.metadata_json : {};
  const metadata: Record<string, unknown> = {
    ...currentMetadata,
    ...formMetadata,
    voice_replies_enabled: settingsForm.voice_replies_enabled,
    learning_mode_enabled: settingsForm.learning_mode_enabled,
    humanized_replies_enabled: settingsForm.humanized_replies_enabled !== false,
  };
  for (const key of SETTINGS_METADATA_KEYS_WITH_DEDICATED_SAVE) {
    delete metadata[key];
  }
  return metadata;
};

const cleanSettingsPayload = (payload: SupportSettingsPayload): SupportSettingsPayload => ({
  ...payload,
  business_name: (payload.business_name || "").trim() || null,
  bot_name: (payload.bot_name || "").trim() || null,
  custom_instructions: payload.custom_instructions === undefined ? null : (String(payload.custom_instructions).trim() || null),
  order_confirmation_selected_products: (payload.order_confirmation_selected_products || [])
    .map((product) => String(product).trim())
    .filter(Boolean),
  customer_state_purchased_products: (payload.customer_state_purchased_products || [])
    .map((product) => String(product).trim())
    .filter(Boolean),
});

const ScrollSurface = ({ children }: { children: React.ReactNode }) => (
  <div className="h-full overflow-y-auto pb-12">{children}</div>
);

const SectionSurface = ({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}) => (
  <section className={`rounded-lg border border-gray-200 bg-white p-4 shadow-sm ${className}`}>
    {children}
  </section>
);

const SectionHeader = ({
  icon: Icon,
  title,
  action,
}: {
  icon: typeof Inbox;
  title: string;
  action?: React.ReactNode;
}) => (
  <div className="flex flex-wrap items-center justify-between gap-3">
    <PanelTitle icon={Icon} title={title} />
    {action}
  </div>
);

const SettingsPageHeader = ({
  icon: Icon,
  title,
  description,
  action,
}: {
  icon: typeof Inbox;
  title: string;
  description: string;
  action?: React.ReactNode;
}) => (
  <div className="flex flex-wrap items-start justify-between gap-4 border-b border-gray-100 pb-5">
    <div className="flex min-w-0 items-start gap-3">
      <div className="flex size-9 shrink-0 items-center justify-center rounded-full bg-gray-100 text-gray-600">
        <Icon className="size-4" />
      </div>
      <div className="min-w-0">
        <h3 className="text-base font-semibold text-gray-950">{title}</h3>
        <p className="mt-1 max-w-2xl text-sm leading-5 text-gray-500">{description}</p>
      </div>
    </div>
    {action}
  </div>
);

const IntegrationBackButton = ({ label = "Volver a integraciones", onBack }: { label?: string; onBack: () => void }) => (
  <button
    type="button"
    onClick={onBack}
    aria-label={label}
    title={label}
    className="mb-5 inline-flex h-9 items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 text-sm font-semibold text-gray-800 transition hover:bg-gray-50 hover:text-gray-950 focus:outline-none focus:ring-2 focus:ring-[#4368E5]/30"
  >
    <ArrowLeft className="size-4" />
    <span>{label}</span>
  </button>
);

const PanelTitle = ({ icon: Icon, title }: { icon: typeof Inbox; title: string }) => (
  <div className="flex items-center gap-2">
    <Icon className="size-4 text-[#4368E5]" />
    <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
  </div>
);

const MetricMini = ({ label, value }: { label: string; value: string | number }) => (
  <div className="flex h-12 items-center justify-between rounded-lg border border-gray-200 bg-gray-50 px-3">
    <p className="truncate text-xs font-semibold text-gray-500">{label}</p>
    <p className="text-lg font-semibold text-gray-900">{value}</p>
  </div>
);

const StateDefinition = ({
  label,
  description,
  tone,
}: {
  label: string;
  description: string;
  tone: "neutral" | "success" | "warning" | "danger";
}) => (
  <div className="rounded-xl border border-gray-200 bg-gray-50 p-3">
    <StatusPill label={label} tone={tone} />
    <p className="mt-3 text-xs leading-5 text-gray-500">{description}</p>
  </div>
);

const AiPipelineStatusBar = ({
  phase,
  cancelling,
  cancelEnabled,
  onCancel,
}: {
  phase: AiUiPhase;
  cancelling: boolean;
  cancelEnabled: boolean;
  onCancel: () => void;
}) => {
  const isPreparing = phase !== "writing";
  return (
    <div className="mb-2 flex items-center justify-center gap-2">
      <span
        role="status"
        className="inline-flex items-center gap-2 rounded-full border border-blue-200 bg-blue-50 px-3 py-1.5 text-xs font-medium text-blue-700 shadow-sm"
      >
        <span className="size-3 animate-spin rounded-full border-2 border-blue-300 border-t-blue-600" aria-hidden="true" />
        {isPreparing ? "La IA está preparando una respuesta" : "La IA está escribiendo…"}
      </span>
      {cancelEnabled ? (
        <button
          type="button"
          onClick={onCancel}
          disabled={cancelling}
          className="inline-flex h-7 items-center rounded-full border border-gray-200 bg-gray-100 px-3 text-xs font-medium text-gray-700 transition hover:bg-gray-200 disabled:opacity-60"
        >
          {cancelling ? <Loader2 className="mr-1 size-3.5 animate-spin" /> : null}
          Cancelar
        </button>
      ) : null}
    </div>
  );
};

const ConversationWorkIndicator = ({
  state,
  pendingCount = 0,
}: {
  state: ConversationWorkState;
  pendingCount?: number;
}) => {
  if (state === "idle") {
    return <span className="size-4 shrink-0" />;
  }
  if (state === "failed") {
    return (
      <span
        className="flex size-5 shrink-0 items-center justify-center rounded-full bg-red-50 text-red-600 ring-1 ring-red-100"
        title="La IA falló al responder"
        aria-label="La IA falló al responder"
      >
        <AlertTriangle className="size-3.5" />
      </span>
    );
  }
  if (state === "needs_action") {
    const label = pendingCount > 9 ? "9+" : String(Math.max(1, pendingCount));
    return (
      <span
        className="flex size-5 shrink-0 items-center justify-center rounded-full bg-gray-900 text-[10px] font-bold leading-none text-white"
        title={`${label} mensaje${label === "1" ? "" : "s"} pendiente${label === "1" ? "" : "s"} de respuesta humana`}
        aria-label={`${label} mensaje${label === "1" ? "" : "s"} pendiente${label === "1" ? "" : "s"} de respuesta humana`}
      >
        {label}
      </span>
    );
  }
  if (state === "human_help") {
    return (
      <span
        className="size-2.5 shrink-0 rounded-full bg-emerald-500"
        title="Ayuda humana solicitada"
        aria-label="Ayuda humana solicitada"
      />
    );
  }
  if (state === "resolved_ping") {
    return (
      <span
        className="size-2 shrink-0 rounded-full bg-gray-300"
        title="Mensaje nuevo en conversación resuelta"
        aria-label="Mensaje nuevo en conversación resuelta"
      />
    );
  }
  return (
    <span
      className="relative flex size-4 shrink-0 items-center justify-center"
      title="Fluxi está procesando"
      aria-label="Fluxi está procesando"
    >
      <span className="absolute size-3.5 rounded-full bg-blue-400 opacity-70 animate-ping" />
      <span className="relative size-2.5 rounded-full bg-blue-500 animate-pulse" />
    </span>
  );
};

const MessageThreadSkeleton = ({ detailsOpen }: { detailsOpen: boolean }) => (
  <div className={`${detailsOpen ? "mx-auto max-w-3xl" : "w-full"} animate-pulse motion-reduce:animate-none space-y-3`}>
    <div className="h-14 max-w-[min(520px,58%)] rounded-2xl bg-gray-200/50" />
    <div className="ml-auto h-10 w-[min(520px,42%)] rounded-2xl bg-[#EEF3FF]/60" />
    <div className="h-20 max-w-[min(520px,64%)] rounded-2xl bg-gray-200/50" />
    <div className="ml-auto h-14 w-[min(520px,48%)] rounded-2xl bg-[#EEF3FF]/60" />
    <div className="h-12 max-w-[min(520px,36%)] rounded-2xl bg-gray-200/50" />
    <div className="ml-auto h-16 w-[min(520px,52%)] rounded-2xl bg-[#EEF3FF]/60" />
  </div>
);

const ChatbotPageInitialSkeleton = () => (
  <div className="h-[100dvh] overflow-hidden bg-[#F7F8FA] text-gray-900">
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex h-[65px] shrink-0 items-center justify-between border-b border-gray-200 bg-white px-6">
        <div className="flex items-center gap-3">
          <div className="size-9 animate-pulse rounded-full bg-gray-100" />
          <div className="size-9 animate-pulse rounded-full bg-gray-100" />
          <div className="h-5 w-28 animate-pulse rounded-full bg-gray-100" />
        </div>
        <div className="flex items-center gap-2">
          <div className="h-9 w-24 animate-pulse rounded-full bg-gray-100" />
          <div className="size-9 animate-pulse rounded-full bg-gray-100" />
        </div>
      </div>
      <div className="grid min-h-0 flex-1 grid-cols-[420px_minmax(0,1fr)]">
        <aside className="border-r border-gray-200 bg-white p-3">
          <div className="mb-3 grid grid-cols-2 gap-2">
            <div className="h-16 animate-pulse rounded-lg bg-gray-100" />
            <div className="h-16 animate-pulse rounded-lg bg-gray-100" />
          </div>
          <div className="mb-3 h-10 animate-pulse rounded-full bg-gray-100" />
          <div className="space-y-2">
            {Array.from({ length: 7 }).map((_, index) => (
              <div key={index} className="flex items-center gap-3 rounded-xl p-3">
                <div className="size-10 animate-pulse rounded-full bg-gray-100" />
                <div className="min-w-0 flex-1 space-y-2">
                  <div className="h-4 w-2/3 animate-pulse rounded-full bg-gray-100" />
                  <div className="h-3 w-full animate-pulse rounded-full bg-gray-100" />
                </div>
              </div>
            ))}
          </div>
        </aside>
        <section className="flex min-h-0 flex-col" style={CHAT_THREAD_BACKGROUND_STYLE}>
          <div className="h-[65px] border-b border-gray-100 bg-white/95" />
          <div className="min-h-0 flex-1 px-5 pb-32 pt-4">
            <MessageThreadSkeleton detailsOpen={false} />
          </div>
        </section>
      </div>
    </div>
  </div>
);

const DateSeparator = ({ label }: { label: string }) => (
  <div className="flex justify-center py-1">
    <span className="rounded-full border border-gray-200/70 bg-white/80 px-3 py-1 text-[11px] font-semibold text-gray-500 shadow-[0px_1px_4px_0px_rgba(99,99,99,0.12)] backdrop-blur">
      {label}
    </span>
  </div>
);

const TypingDots = ({ label }: { label: string }) => (
  <span className="inline-flex items-center gap-1" aria-label={label}>
    <span className="size-1.5 animate-bounce rounded-full bg-gray-400 [animation-delay:-0.2s]" />
    <span className="size-1.5 animate-bounce rounded-full bg-gray-400 [animation-delay:-0.1s]" />
    <span className="size-1.5 animate-bounce rounded-full bg-gray-400" />
    <span className="sr-only">{label}</span>
  </span>
);

const AiFailureBubble = ({
  failure,
  loading,
  onRetry,
}: {
  failure: ActiveAiFailure;
  loading: boolean;
  onRetry: () => void;
}) => {
  const canRetry = failure.retryable !== false;
  return (
    <div className="flex justify-center">
      <div className="max-w-[min(520px,88%)] rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-red-950">
        <div className="flex items-start gap-2">
          <AlertTriangle className="mt-0.5 size-4 shrink-0 text-red-600" />
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <p className="text-sm font-semibold">{failure.title}</p>
              <span className="rounded-full border border-red-200 bg-white/70 px-2 py-0.5 text-[11px] font-semibold text-red-700">
                {failure.shortReason}
              </span>
            </div>
            <p className="mt-1 text-xs leading-5 text-red-800">{failure.detail}</p>
            <div className="mt-2 flex flex-wrap items-center justify-between gap-2 text-[11px] text-red-700">
              <span>{formatMessageTime(failure.created_at)}</span>
              {canRetry ? (
                <button
                  type="button"
                  onClick={onRetry}
                  disabled={loading}
                  className="inline-flex h-7 items-center gap-1.5 rounded-full border border-red-200 bg-white px-2.5 font-semibold text-red-700 hover:bg-red-100 disabled:opacity-50"
                >
                  {loading ? <Loader2 className="size-3.5 animate-spin" /> : <RotateCcw className="size-3.5" />}
                  Reintentar
                </button>
              ) : failure.recommended_action ? (
                <span className="rounded-full bg-white/70 px-2 py-1 font-semibold">
                  {formatRecommendedAction(failure.recommended_action)}
                </span>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Modo aprendiz: globo de la respuesta esperando aprobación del owner. Muestra las versiones
// (navegables), cómo saldrá partida en globitos, las acciones ejecutadas/retenidas y las
// acciones de aprobar / sugerir cambios / rechazar.
const LearnerVoicePlayer = ({ approvalId, version }: { approvalId: number; version: number }) => {
  // La URL firmada de S3 expira, así que se pide al montar (y se puede reintentar); el audio
  // es el MISMO que se reenviará al aprobar, no una vista previa aproximada.
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setUrl(null);
    setFailed(false);
    chatbotV2Service
      .getReplyApprovalVersionVoice(approvalId, version)
      .then((data) => {
        if (!cancelled) setUrl(data.url);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [approvalId, version, attempt]);

  if (failed) {
    return (
      <button
        type="button"
        onClick={() => setAttempt((n) => n + 1)}
        className="mt-1.5 text-[11px] text-gray-500 underline underline-offset-2"
      >
        No se pudo cargar el audio — reintentar
      </button>
    );
  }
  if (!url) {
    return <div className="mt-1.5 text-[11px] text-gray-400">Cargando audio…</div>;
  }
  return <audio controls preload="metadata" src={url} className="mt-1.5 h-9 w-full max-w-[320px]" />;
};

const PendingReplyBubble = ({
  approval,
  mediaResources,
  actionLoading,
  awaitingVersion,
  sendError,
  onApprove,
  onFeedback,
  onReject,
}: {
  approval: ReplyApproval;
  mediaResources: MediaResourceDraft[];
  actionLoading: null | "approve" | "feedback" | "reject";
  awaitingVersion: boolean;
  sendError: string | null;
  onApprove: (version: number, force?: boolean) => void;
  onFeedback: (text: string) => Promise<boolean>;
  onReject: (reason: string) => Promise<boolean>;
}) => {
  const versions = approval.versions ?? [];
  const [versionIdx, setVersionIdx] = useState(Math.max(0, versions.length - 1));
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [feedbackText, setFeedbackText] = useState("");
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectText, setRejectText] = useState("");
  const [consultsExpanded, setConsultsExpanded] = useState(false);
  useEffect(() => {
    setVersionIdx(Math.max(0, versions.length - 1));
  }, [approval.id, versions.length]);

  const currentVersion = versions[Math.min(Math.max(versionIdx, 0), versions.length - 1)];
  if (!currentVersion) return null;
  const vigenteVersion = versions[versions.length - 1];
  const isLatest = versionIdx === versions.length - 1;
  const blocks = currentVersion.parts.length ? currentVersion.parts : [currentVersion.text];
  // `unmapped` es diagnostico (nombres tecnicos de tools sin frase en el catalogo, ya logueados
  // como warning en el backend). `executed` ya trae su fallback humano, asi que pintarlo aqui
  // duplicaba la linea en snake_case.
  const executedLines = currentVersion.actions.executed;
  const heldPreviews = currentVersion.actions.held
    .filter((phrase) => phrase.trim().startsWith("Enviará:"))
    .map((phrase) => {
      const query = phrase.slice(phrase.indexOf(":") + 1).toLowerCase();
      let match: { url: string; mediaType: string; name: string } | null = null;
      for (const draft of mediaResources) {
        const draftTitle = String(draft.title || "").toLowerCase();
        for (const file of draft.files) {
          const fileName = String(file.name || "").toLowerCase();
          if (file.url && ((fileName && query.includes(fileName)) || (draftTitle && query.includes(draftTitle)))) {
            match = { url: file.url, mediaType: file.media_type || "document", name: file.name };
            break;
          }
        }
        if (match) break;
      }
      return { phrase, match };
    });
  const busy = actionLoading !== null;

  return (
    <div className="flex justify-end">
      <div className="relative max-w-[min(560px,78%)] rounded-xl bg-gray-50 px-3 py-2 shadow-[0px_1px_4px_0px_rgba(99,99,99,0.18)]">
        <span
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 rounded-xl border border-dashed border-[#4368E5]/40 animate-pulse motion-reduce:animate-none"
        />
        <div className="relative">
          <div className="flex items-center justify-between text-[11px]">
            <div className="flex items-center gap-1">
              {versions.length >= 2 ? (
                <>
                  <button
                    type="button"
                    disabled={versionIdx === 0}
                    onClick={() => setVersionIdx((idx) => Math.max(0, idx - 1))}
                    className="inline-flex size-5 items-center justify-center rounded-full hover:bg-gray-100 disabled:opacity-40"
                    aria-label="Versión anterior"
                  >
                    <ChevronLeft className="size-3.5" />
                  </button>
                  <span className="text-gray-500">
                    Versión {versionIdx + 1} de {versions.length}
                  </span>
                  <button
                    type="button"
                    disabled={versionIdx === versions.length - 1}
                    onClick={() => setVersionIdx((idx) => Math.min(versions.length - 1, idx + 1))}
                    className="inline-flex size-5 items-center justify-center rounded-full hover:bg-gray-100 disabled:opacity-40"
                    aria-label="Versión siguiente"
                  >
                    <ChevronRight className="size-3.5" />
                  </button>
                </>
              ) : (
                <span />
              )}
            </div>
            {approval.status === "pending" ? (
              <span className="rounded-full bg-[#E8EEFF] px-2 py-0.5 text-[#3151C9]">Esperando tu aprobación</span>
            ) : approval.status === "approving" ? (
              <span className="inline-flex items-center gap-1 rounded-full bg-[#E8EEFF] px-2 py-0.5 text-[#3151C9]">
                <Loader2 className="size-3 animate-spin" />
                Enviando…
              </span>
            ) : approval.status === "send_failed" ? (
              <span className="rounded-full bg-red-50 px-2 py-0.5 text-red-600" title={sendError ?? undefined}>
                No se pudo enviar
              </span>
            ) : null}
          </div>

          {awaitingVersion ? (
            <div className="mt-1.5 flex items-center gap-2 text-sm text-gray-500">
              Preparando versión {versions.length + 1}…
              <TypingDots label="Preparando nueva versión" />
            </div>
          ) : (
            <>
              {!isLatest ? (
                <p className="mt-1 text-[11px] text-gray-500">Versión anterior — puedes aprobarla</p>
              ) : null}
              {currentVersion.superseded_by_customer ? (
                <p className="mt-1 text-[11px] text-gray-500">
                  El cliente escribió de nuevo — hay una versión más reciente
                </p>
              ) : null}

              <div className="mt-1.5">
                {blocks.map((block, idx) => (
                  <div key={idx}>
                    {idx > 0 ? <div className="border-t border-dashed border-gray-200 my-1.5" /> : null}
                    <div className="whitespace-pre-wrap text-sm text-gray-900">{block}</div>
                  </div>
                ))}
              </div>

              {currentVersion.voice_reply_expected ? (
                currentVersion.voice_media?.available ? (
                  <div className="mt-1.5">
                    <span className="rounded-full border border-gray-200 bg-white px-2 py-0.5 text-[11px] text-gray-600">
                      🎙️ Nota de voz — escúchala antes de aprobar
                    </span>
                    <LearnerVoicePlayer
                      key={`${approval.id}-${currentVersion.version}`}
                      approvalId={approval.id}
                      version={currentVersion.version}
                    />
                  </div>
                ) : (
                  <div className="mt-1.5 flex flex-wrap items-center gap-2">
                    <span className="rounded-full border border-gray-200 bg-white px-2 py-0.5 text-[11px] text-gray-600">
                      🎙️ Se enviará como nota de voz
                    </span>
                    {currentVersion.voice_synthesis_failed ? (
                      <span className="text-[11px] text-amber-600">
                        No se pudo generar el audio — se enviará como texto.
                      </span>
                    ) : null}
                  </div>
                )
              ) : null}

              {heldPreviews.map((preview, idx) => (
                <div key={idx} className="mt-1.5">
                  {preview.match ? (
                    <ChatMediaContent
                      url={preview.match.url}
                      mediaType={preview.match.mediaType}
                      filename={preview.match.name}
                    />
                  ) : (
                    <p className="text-[11px] text-gray-500">{preview.phrase}</p>
                  )}
                </div>
              ))}

              {executedLines.length || currentVersion.actions.held.length || currentVersion.actions.consults.length ? (
                <div className="mt-1.5 space-y-0.5 text-[11px] italic text-gray-500">
                  {executedLines.length ? (
                    <div>
                      <span className="font-semibold">Acciones realizadas:</span>
                      {executedLines.map((line, idx) => (
                        <div key={idx}>· {line}</div>
                      ))}
                    </div>
                  ) : null}
                  {currentVersion.actions.held.length ? (
                    <div>
                      <span className="font-semibold">Al aprobar hará:</span>
                      {currentVersion.actions.held.map((line, idx) => (
                        <div key={idx}>· {line}</div>
                      ))}
                    </div>
                  ) : null}
                  {currentVersion.actions.consults.length ? (
                    <div>
                      <button
                        type="button"
                        onClick={() => setConsultsExpanded((value) => !value)}
                        className="hover:underline"
                      >
                        y {currentVersion.actions.consults.length} consultas ▾
                      </button>
                      {consultsExpanded
                        ? currentVersion.actions.consults.map((consult, idx) => <div key={idx}>· {consult.label}</div>)
                        : null}
                    </div>
                  ) : null}
                </div>
              ) : null}

              <div className="mt-2 flex justify-end gap-2">
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => {
                    setRejectOpen((value) => !value);
                    setFeedbackOpen(false);
                  }}
                  className="inline-flex h-9 items-center gap-2 rounded-full border border-gray-200 bg-white px-3 text-sm font-semibold text-gray-700 disabled:opacity-50"
                >
                  Rechazar
                </button>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => {
                    setFeedbackOpen((value) => !value);
                    setRejectOpen(false);
                  }}
                  className="inline-flex h-9 items-center gap-2 rounded-full border border-gray-200 bg-white px-3 text-sm font-semibold text-gray-700 disabled:opacity-50"
                >
                  Sugerir cambios
                </button>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => onApprove(currentVersion.version)}
                  className="inline-flex h-9 items-center gap-2 rounded-full bg-gray-950 px-4 text-sm font-semibold text-white disabled:opacity-50"
                >
                  {actionLoading === "approve" ? <Loader2 className="size-4 animate-spin" /> : null}
                  {approval.status === "send_failed" ? "Reintentar envío" : "Aprobar y enviar"}
                </button>
              </div>

              {feedbackOpen ? (
                <div className="mt-2 rounded-xl border border-gray-200 bg-white p-2">
                  {approval.feedback_history.length ? (
                    <p className="mb-2 text-[11px] text-gray-500">
                      Correcciones anteriores:{" "}
                      {approval.feedback_history.map((item, idx) => `${idx + 1}) ${item.text}`).join(" ")}
                    </p>
                  ) : null}
                  <textarea
                    value={feedbackText}
                    onChange={(event) => setFeedbackText(event.target.value)}
                    rows={2}
                    placeholder="¿Qué debería cambiar? Ej.: no ofrezcas descuento todavía; primero confirma la ciudad."
                    className="w-full resize-y rounded-xl border border-gray-200 p-2 text-sm outline-none focus:border-gray-300"
                  />
                  <div className="mt-2 flex justify-end gap-2">
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => {
                        setFeedbackOpen(false);
                        setFeedbackText("");
                      }}
                      className="inline-flex h-9 items-center rounded-full px-3 text-sm font-semibold text-gray-600 transition hover:bg-gray-100 disabled:opacity-50"
                    >
                      Cancelar
                    </button>
                    <button
                      type="button"
                      disabled={busy || !feedbackText.trim()}
                      onClick={async () => {
                        const ok = await onFeedback(feedbackText.trim());
                        if (ok) {
                          setFeedbackOpen(false);
                          setFeedbackText("");
                        }
                      }}
                      className="inline-flex h-9 items-center gap-2 rounded-full bg-gray-950 px-4 text-sm font-semibold text-white disabled:opacity-50"
                    >
                      {actionLoading === "feedback" ? <Loader2 className="size-4 animate-spin" /> : null}
                      Procesar
                    </button>
                  </div>
                </div>
              ) : null}

              {rejectOpen ? (
                <div className="mt-2 rounded-xl border border-gray-200 bg-white p-2">
                  <textarea
                    value={rejectText}
                    onChange={(event) => setRejectText(event.target.value)}
                    rows={2}
                    placeholder="Motivo (opcional) — el asistente aprende de esto"
                    className="w-full resize-y rounded-xl border border-gray-200 p-2 text-sm outline-none focus:border-gray-300"
                  />
                  {approval.reversible.length ? (
                    <p className="mt-2 text-[11px] text-amber-700">
                      Estas acciones ya se ejecutaron y la IA intentará revertirlas:{" "}
                      {approval.reversible.join(" · ")}
                    </p>
                  ) : null}
                  <div className="mt-2 flex justify-end gap-2">
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => {
                        setRejectOpen(false);
                        setRejectText("");
                      }}
                      className="inline-flex h-9 items-center rounded-full px-3 text-sm font-semibold text-gray-600 transition hover:bg-gray-100 disabled:opacity-50"
                    >
                      Cancelar
                    </button>
                    <button
                      type="button"
                      disabled={busy}
                      onClick={async () => {
                        const ok = await onReject(rejectText.trim());
                        if (ok) {
                          setRejectOpen(false);
                          setRejectText("");
                        }
                      }}
                      className="inline-flex h-9 items-center gap-2 rounded-full border border-red-200 bg-red-50 px-4 text-sm font-semibold text-red-600 disabled:opacity-50"
                    >
                      {actionLoading === "reject" ? <Loader2 className="size-4 animate-spin" /> : null}
                      Rechazar mensaje
                    </button>
                  </div>
                </div>
              ) : null}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// Modo aprendiz: aviso colapsado cuando la ventana de 24h se cerró y la respuesta propuesta
// ya no se puede enviar. Mensaje distinto para messenger/instagram (sin plantillas).
const ExpiredReplyBubble = ({ approval }: { approval: ReplyApproval }) => {
  const [expanded, setExpanded] = useState(false);
  const lastVersion = approval.versions[approval.versions.length - 1];
  const channel = String(approval.channel || "").toLowerCase();
  const secondSentence =
    channel === "messenger" || channel === "instagram"
      ? "Podrás continuar cuando el cliente vuelva a escribir."
      : "Ahora puedes enviar solo plantillas para reabrir la conversación.";
  return (
    <div className="flex justify-end">
      <div className="max-w-[min(560px,78%)] rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 shadow-[0px_1px_4px_0px_rgba(99,99,99,0.18)]">
        <p className="text-sm text-gray-700">
          El mensaje propuesto ya no se puede enviar debido a que se cerró la ventana de 24 hrs para responder. {secondSentence}
        </p>
        {lastVersion ? (
          <div className="mt-1 text-[13px] text-gray-500">
            <p>Mensaje que estaba pensado enviarse:</p>
            <p className={expanded ? "whitespace-pre-wrap" : "line-clamp-4 whitespace-pre-wrap"}>{lastVersion.text}</p>
            <button
              type="button"
              onClick={() => setExpanded((value) => !value)}
              className="mt-0.5 font-semibold text-gray-600 hover:underline"
            >
              {expanded ? "Ver menos" : "Ver más"}
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
};

const ActionNoticeBubble = ({ notice }: { notice: ActionNotice }) => {
  const toneClass =
    notice.tone === "danger"
      ? "border-red-200 bg-red-50 text-red-900"
      : notice.tone === "warning"
        ? "border-amber-200 bg-amber-50 text-amber-950"
        : "border-gray-200 bg-gray-50 text-gray-800";
  const iconClass =
    notice.tone === "danger"
      ? "text-red-600"
      : notice.tone === "warning"
        ? "text-amber-600"
        : "text-gray-500";
  return (
    <div className="flex justify-center">
      <div className={`max-w-[min(520px,88%)] rounded-xl border px-3 py-2 ${toneClass}`}>
        <div className="flex items-start gap-2">
          <AlertTriangle className={`mt-0.5 size-4 shrink-0 ${iconClass}`} />
          <div className="min-w-0">
            <p className="text-sm font-semibold">{notice.title}</p>
            <p className="mt-1 text-xs leading-5 opacity-80">{notice.detail}</p>
            {notice.created_at ? (
              <p className="mt-1 text-[11px] opacity-60">{formatMessageTime(notice.created_at)}</p>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

const DeliveryStatusMarker = ({
  status,
  channel,
  failure,
}: {
  status?: string | null;
  channel?: string | null;
  failure?: { label: string; title: string } | null;
}) => {
  if (failure) {
    return (
      <span
        className="inline-flex items-center gap-1 rounded-full bg-red-50 px-1.5 py-0.5 font-medium text-red-700"
        title={failure.title}
      >
        <AlertTriangle className="size-3 shrink-0" />
        {failure.label}
      </span>
    );
  }

  const normalized = String(status || "").toLowerCase();
  if (!normalized) return null;
  const title = getDeliveryStatusTitle(normalized, channel);
  if (isOutboundDeliveryUncertain(normalized)) {
    if (normalized === "unknown") {
      return (
        <span title={title} aria-label={title}>
          <AlertTriangle className="size-3.5 text-amber-500" />
        </span>
      );
    }
    return (
      <span title={title} aria-label={title}>
        <Loader2 className="size-3.5 animate-spin text-gray-400" />
      </span>
    );
  }
  if (normalized === "recorded" || normalized === "simulated") {
    // B-15c: un turno simulado NO salio por WhatsApp — no puede verse igual que un chulito real.
    return (
      <span
        className="rounded-full bg-violet-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-violet-700"
        title="Simulado: registrado en Fluxi, no se envió por WhatsApp"
      >
        Sim
      </span>
    );
  }
  if (normalized === "read") {
    return (
      <span title={title} aria-label={title}>
        <CheckCheck className="size-3.5 text-blue-500" />
      </span>
    );
  }
  if (normalized === "delivered") {
    return (
      <span title={title} aria-label={title}>
        <CheckCheck className="size-3.5 text-gray-500" />
      </span>
    );
  }
  return (
    <span title={title} aria-label={title}>
      <Check className="size-3.5 text-gray-500" />
    </span>
  );
};

const StatusPill = ({
  label,
  tone = "neutral",
  icon: Icon,
}: {
  label: string | number;
  tone?: "neutral" | "success" | "warning" | "danger";
  icon?: typeof Inbox;
}) => (
  <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold ${toneClass(tone, "pill")}`}>
    {Icon ? <Icon className="size-3.5" /> : null}
    {label}
  </span>
);

const ToggleSwitch = ({
  checked,
  label,
  onChange,
  accent = "default",
  disabled = false,
}: {
  checked: boolean;
  label: string;
  onChange: (value: boolean) => void;
  accent?: "default" | "blue";
  disabled?: boolean;
}) => {
  const activeTrackClass = disabled ? "bg-gray-300" : accent === "blue" ? "bg-[#4368E5]" : "bg-gray-950";
  const activeTextClass = disabled
    ? "bg-gray-100 text-gray-400"
    : accent === "blue"
      ? "text-[#3151C9] hover:bg-[#EEF3FF]"
      : "text-gray-900 hover:bg-gray-100";
  return (
  <button
    type="button"
    disabled={disabled}
    onClick={() => {
      if (!disabled) onChange(!checked);
    }}
    className={`inline-flex h-9 items-center gap-2 rounded-full px-2.5 text-sm font-semibold transition focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-300 focus-visible:ring-offset-2 ${
      checked ? activeTextClass : "bg-gray-100 text-gray-600 hover:bg-gray-200"
    } disabled:cursor-not-allowed disabled:text-gray-400 disabled:hover:bg-gray-100`}
  >
    <span className={`relative inline-flex h-5 w-9 shrink-0 rounded-full transition ${checked ? activeTrackClass : "bg-gray-300"}`}>
      <span
        className={`absolute left-0.5 top-0.5 size-4 rounded-full bg-white transition-transform ${
          checked ? "translate-x-4" : "translate-x-0"
        }`}
      />
    </span>
    {label}
  </button>
  );
};

const ConversationAvatar = ({ priority, channel }: { priority?: string | null; channel?: string | null }) => {
  const normalized = String(priority || "normal");
  const normalizedPriority = normalizeConversationPriority(normalized);
  const classes =
    normalizedPriority === "critical"
      ? "bg-gray-50 text-red-500"
      : normalizedPriority === "medium"
        ? "bg-gray-50 text-amber-500"
        : "bg-gray-50 text-gray-500";
  return (
    <span className={`relative flex size-10 shrink-0 items-center justify-center rounded-full ring-1 ring-gray-200 ${classes}`}>
      <UserRound className="size-5" />
      <ChannelAvatarBadge channel={channel} />
    </span>
  );
};

const toneClass = (tone: string, target: "pill" | "soft" | "icon") => {
  if (target === "icon") {
    if (tone === "success") return "text-green-600";
    if (tone === "warning") return "text-amber-600";
    if (tone === "danger") return "text-red-600";
    return "text-[#4368E5]";
  }
  if (target === "soft") {
    if (tone === "success") return "border-green-200 bg-green-50 text-green-700";
    if (tone === "warning") return "border-amber-200 bg-amber-50 text-amber-700";
    if (tone === "danger") return "border-red-200 bg-red-50 text-red-700";
    return "border-gray-200 bg-gray-50 text-gray-700";
  }
  if (tone === "success") return "bg-green-100 text-green-700";
  if (tone === "warning") return "bg-amber-100 text-amber-700";
  if (tone === "danger") return "bg-red-100 text-red-700";
  return "bg-gray-100 text-gray-700";
};

const TextField = ({
  label,
  value,
  onChange,
  placeholder,
  type = "text",
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  type?: string;
}) => (
  <label className="block">
    <span className="mb-1 block text-xs font-semibold text-gray-600">{label}</span>
    <input
      type={type}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      placeholder={placeholder}
      className="h-10 w-full rounded-lg border border-gray-200 bg-white px-3 text-sm outline-none focus:border-[#4368E5]"
    />
  </label>
);

const NumberField = ({
  label,
  value,
  min,
  max,
  step = 1,
  onChange,
}: {
  label: string;
  value: number;
  min?: number;
  max?: number;
  step?: number;
  onChange: (value: number) => void;
}) => (
  <label className="block">
    <span className="mb-1 block text-xs font-semibold text-gray-600">{label}</span>
    <input
      type="number"
      value={Number.isFinite(value) ? value : 0}
      min={min}
      max={max}
      step={step}
      onChange={(event) => onChange(Number(event.target.value))}
      className="h-10 w-full rounded-lg border border-gray-200 bg-white px-3 text-sm outline-none focus:border-[#4368E5]"
    />
  </label>
);

const TextareaField = ({
  label,
  value,
  onChange,
  rows = 4,
  placeholder,
  helperText,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  rows?: number;
  placeholder?: string;
  helperText?: string;
}) => (
  <label className="block">
    <span className="mb-1 block text-xs font-semibold text-gray-600">{label}</span>
    <textarea
      value={value}
      onChange={(event) => onChange(event.target.value)}
      rows={rows}
      placeholder={placeholder}
      className="w-full resize-y rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm outline-none focus:border-[#4368E5]"
    />
    {helperText ? <span className="mt-1 block text-xs leading-5 text-gray-500">{helperText}</span> : null}
  </label>
);

// Modal de confirmación compartido (reemplaza los modales inline duplicados).
// La fila de botones usa flex-wrap: si los textos no caben en una fila, se apilan
// en vez de desbordarse (ese era el bug de los modales de "Cambios sin guardar").
const CONFIRM_MODAL_ACTION_CLASSES: Record<"primary" | "secondary" | "destructive", string> = {
  primary: "bg-gray-950 text-white hover:bg-gray-800",
  secondary: "bg-gray-100 text-gray-800 hover:bg-gray-200",
  destructive: "bg-red-600 text-white hover:bg-red-500",
};

const ConfirmModal = ({
  open,
  title,
  description,
  actions,
  zIndexClass = "z-40",
  widthClass = "w-[min(430px,100%)]",
  positionClass = "absolute",
}: {
  open: boolean;
  title: string;
  description: React.ReactNode;
  actions: Array<{
    label: string;
    onClick: () => void;
    variant: "primary" | "secondary" | "destructive";
    disabled?: boolean;
    loading?: boolean;
  }>;
  zIndexClass?: string;
  // "absolute" cubre el contenedor posicionado más cercano (p. ej. el modal de configuración);
  // "fixed" cubre el viewport completo (para los modales que viven fuera de un overlay).
  positionClass?: "absolute" | "fixed";
  widthClass?: string;
}) => {
  if (!open) return null;
  return (
    <div className={`${positionClass} inset-0 ${zIndexClass} flex items-center justify-center bg-white/60 p-4 backdrop-blur-sm`}>
      <div className={`${widthClass} rounded-[22px] border border-gray-200 bg-white p-5 shadow-[0_18px_50px_rgba(15,23,42,0.16)]`}>
        <h3 className="text-base font-semibold text-gray-950">{title}</h3>
        <div className="mt-2 text-sm leading-5 text-gray-500">{description}</div>
        <div className="mt-5 flex flex-wrap justify-end gap-2">
          {actions.map((action) => (
            <button
              key={action.label}
              type="button"
              onClick={action.onClick}
              disabled={action.disabled}
              className={`inline-flex h-10 items-center justify-center gap-2 rounded-full px-4 text-sm font-semibold transition disabled:cursor-not-allowed disabled:opacity-50 ${CONFIRM_MODAL_ACTION_CLASSES[action.variant]}`}
            >
              {action.loading ? <Loader2 className="size-4 animate-spin" /> : null}
              {action.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

type ModalSelectOption = {
  value: string;
  label: string;
  description?: string;
  preview?: string;
  disabled?: boolean;
  // Encabezado de grupo: fila no seleccionable que titula las opciones siguientes.
  heading?: boolean;
};

const ModalSelect = ({
  value,
  options,
  placeholder = "Seleccionar",
  disabled = false,
  onChange,
  dividers = false,
  emphasizeLabels = false,
  wrapDescriptions = false,
  headerAction,
}: {
  value: string;
  options: ModalSelectOption[];
  placeholder?: string;
  disabled?: boolean;
  onChange: (value: string) => void;
  dividers?: boolean;
  emphasizeLabels?: boolean;
  // Descripciones largas a varias líneas (Julian: opciones auto-explicativas en logística).
  wrapDescriptions?: boolean;
  headerAction?: { label: string; onClick: () => void };
}) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const [menuStyle, setMenuStyle] = useState<React.CSSProperties>({});
  const selectedOption = options.find((option) => option.value === value);

  const updateMenuPosition = useCallback(() => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    setMenuStyle({
      position: "fixed",
      left: rect.left,
      top: rect.bottom + 4,
      width: rect.width,
      zIndex: 120,
    });
  }, []);

  useLayoutEffect(() => {
    if (!open) return;
    updateMenuPosition();
    window.addEventListener("resize", updateMenuPosition);
    window.addEventListener("scroll", updateMenuPosition, true);
    return () => {
      window.removeEventListener("resize", updateMenuPosition);
      window.removeEventListener("scroll", updateMenuPosition, true);
    };
  }, [open, updateMenuPosition]);

  useEffect(() => {
    if (!open) return;

    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target;
      if (target instanceof Node && containerRef.current?.contains(target)) return;
      if (target instanceof Node && menuRef.current?.contains(target)) return;
      setOpen(false);
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((current) => !current)}
        className={`flex h-10 w-full items-center rounded-lg border bg-white pl-3 pr-2 text-left text-sm outline-none transition disabled:cursor-not-allowed disabled:opacity-50 ${
          open ? "border-gray-400" : "border-gray-200 hover:border-gray-300"
        }`}
      >
        <span className={`min-w-0 flex-1 truncate ${selectedOption ? "text-gray-900" : "text-gray-400"}`}>
          {selectedOption?.label || placeholder}
        </span>
        <span className="ml-3 inline-flex size-6 shrink-0 items-center justify-center rounded-full text-gray-400">
          <ChevronDown className={`size-4 transition ${open ? "rotate-180" : ""}`} />
        </span>
      </button>

      {open ? createPortal(
        <div
          ref={menuRef}
          style={menuStyle}
          className="max-h-72 overflow-auto rounded-xl border border-gray-200 bg-white p-1 shadow-[0px_14px_36px_rgba(15,23,42,0.14)]"
        >
          {headerAction ? (
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                headerAction.onClick();
              }}
              className="mb-1 flex w-full items-center gap-2 rounded-lg border-b border-gray-100 px-3 py-2.5 text-left text-sm font-semibold text-[#3151C9] transition hover:bg-gray-50"
            >
              <FileText className="size-4" />
              {headerAction.label}
            </button>
          ) : null}
          {options.length ? (
            options.map((option) => {
              if (option.heading) {
                return (
                  <div
                    key={option.value || option.label}
                    className="px-3 pb-1 pt-2.5 text-[11px] font-semibold uppercase tracking-wide text-gray-400"
                  >
                    {option.label}
                  </div>
                );
              }
              const selected = option.value === value;
              return (
                <button
                  key={option.value || "__empty__"}
                  type="button"
                  disabled={option.disabled}
                  onClick={() => {
                    if (option.disabled) return;
                    onChange(option.value);
                    setOpen(false);
                  }}
                  className={`flex min-h-9 w-full flex-col gap-1 px-3 py-2.5 text-left transition disabled:cursor-not-allowed disabled:opacity-50 ${
                    dividers ? "border-b border-gray-100 last:border-b-0 first:rounded-t-lg last:rounded-b-lg" : "rounded-lg"
                  } ${selected ? "bg-gray-100 text-gray-950" : "text-gray-700 hover:bg-gray-50"}`}
                >
                  <span className="flex items-start justify-between gap-3">
                    <span className="min-w-0 flex-1">
                      <span className={`block truncate text-sm ${selected || emphasizeLabels ? "font-semibold" : "font-medium"}`}>
                        {option.label}
                      </span>
                      {option.description ? (
                        <span
                          className={`mt-0.5 block text-xs text-gray-500 ${
                            wrapDescriptions ? "whitespace-normal leading-5" : "truncate"
                          }`}
                        >
                          {option.description}
                        </span>
                      ) : null}
                    </span>
                    {selected ? <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-gray-500" /> : null}
                  </span>
                  {option.preview ? (
                    <span className="line-clamp-2 text-xs leading-5 text-gray-500">{option.preview}</span>
                  ) : null}
                </button>
              );
            })
          ) : (
            <div className="px-3 py-2 text-sm text-gray-400">Sin opciones</div>
          )}
        </div>,
        document.body
      ) : null}
    </div>
  );
};

const SelectField = ({
  label,
  value,
  options,
  formatOption,
  placeholder,
  onChange,
}: {
  label: string;
  value: string;
  options: string[];
  formatOption?: (value: string) => string;
  placeholder?: string;
  onChange: (value: string) => void;
}) => (
  <div className="block">
    <span className="mb-1 block text-xs font-semibold text-gray-600">{label}</span>
    <ModalSelect
      value={value}
      options={options.map((option) => ({
        value: option,
        label: formatOption ? formatOption(option) : option,
      }))}
      placeholder={placeholder}
      onChange={onChange}
    />
  </div>
);

const CopyBox = ({
  label,
  value,
  onCopy,
}: {
  label: string;
  value?: string | null;
  onCopy: (value: string | null | undefined, label: string) => void;
}) => (
  <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
    <div className="flex items-center justify-between gap-2">
      <p className="text-xs font-semibold text-gray-600">{label}</p>
      <button
        onClick={() => onCopy(value, label)}
        disabled={!value}
        className="inline-flex size-7 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 disabled:opacity-50"
        title={`Copiar ${label}`}
      >
        <Clipboard className="size-3.5" />
      </button>
    </div>
    <p className="mt-2 break-all text-xs text-gray-700">{value || "Pendiente"}</p>
  </div>
);

const InfoRow = ({ label, value }: { label: string; value: string | number }) => (
  <div className="flex items-start justify-between gap-3 border-b border-gray-100 py-2 last:border-0">
    <span className="text-gray-500">{label}</span>
    <span className="text-right font-medium text-gray-900">{value}</span>
  </div>
);

const EmptyState = ({
  icon: Icon,
  title,
  body,
}: {
  icon: typeof Inbox;
  title: string;
  body: string;
}) => (
  <div className="flex h-full min-h-[180px] flex-col items-center justify-center rounded-lg border border-dashed border-gray-200 bg-gray-50 px-4 py-8 text-center">
    <Icon className="size-8 text-gray-400" />
    <p className="mt-3 text-sm font-semibold text-gray-700">{title}</p>
    <p className="mt-1 max-w-sm text-sm text-gray-500">{body}</p>
  </div>
);

const LogMini = ({ title, subtitle, tone }: { title: string; subtitle: string; tone: string }) => (
  <div className="rounded-lg border border-gray-200 bg-gray-50 p-2">
    <div className="flex items-center justify-between gap-2">
      <p className="truncate text-xs font-semibold text-gray-800">{title}</p>
      <span className={`size-2 shrink-0 rounded-full ${tone === "error" || tone === "critical" || tone === "failed" ? "bg-red-500" : "bg-blue-500"}`} />
    </div>
    <p className="mt-1 line-clamp-2 text-xs text-gray-500">{subtitle}</p>
  </div>
);

export default ChatbotPage;
